import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { ResearchReportPhotoStrip } from './ResearchReportPhotoStrip';
import {
  escapeHtml,
  linkifyEscapedHtml,
  linkifyPlainTextToHtml,
  looksLikeReportPhotoUrl,
  wrapOpenableImageHtml,
  scrubResearchBiographyText,
  collapseCompetingSocialCitations,
  collectIdentityPhotosFromReport,
  collectReportContactRows,
  collectReportCourtRows,
  collectReportFamilyRows,
  collectReportAffiliationRows,
  collectReportPhotoNotes,
  buildReportEvidence,
  reportContentKeepingBiography,
  splitResearchReportSections,
  isRichResearchReportSection,
} from './researchReportMedia';
import { mergeResearchReportKeyData, mergeResearchReportKnownAliases } from './researchRerunIntake';
import { researchIntakeShouldClearForQueue } from './researchIntakeWorkflow';
import { mergeResearchQueueFromGatewayJobs } from './researchQueueSync';
import { estimateQueueEtaSeconds, resolveResearchQueueTimerStart } from './researchQueueElapsed';
import { decideResearchWatchFailure } from './researchJobWatch';
import { releaseDocumentFocus, scrollElementWithinConsole } from './consoleScroll';
import {
  consoleStarStorageKey,
  isPermanentStarredSection,
  parseStarredSectionIds,
  starredSectionItems,
  toggleStarredSectionId,
  withPermanentStars,
} from './consoleStars';
import {
  filterStaffNavItems,
  isNextCompliAdmin,
  isStaffOnlyConsoleNavItem,
} from './platformAdminNav';
import { markFunnel, trackUxEvent } from './uxTelemetry';
import {
  normalizeAgeRangeForSubmit,
} from './researchAgeRange';
import {
  isTwoFactorEnabled,
  setTwoFactorEnabled,
} from './accountSecurity';
import { useAuth } from './AuthContext';
import { gatewayAuthorizationHeaders, isRealGatewayAccessToken } from './gatewayAuth';
import {
  isOfacCustomerDirectoryCompany,
  isOfacHirePathway,
  ofacWhitelistRolesForKind,
  ofacWhitelistAddressKinds,
  ofacWhitelistIdentifierTypes,
  applyOfacWhitelistIntakeKind,
  type OfacWhitelistIntakeKind,
  normalizeOfacReviewCycleDays,
  ofacCompanyNameRequiredMessage,
  ofacCompanyRequiredMessage,
  ofacCreateListNameRequiredMessage,
  ofacDirectoryCompanyRejectedMessage,
  ofacGatewaySessionRequiredMessage,
  ofacOwnerRequiredMessage,
  ofacWhitelistRecordkeepingCsv,
  emptyOfacWhitelistForm,
  buildOfacWhitelistIdentifiers,
  buildOfacFamilyMemberIdentifiers,
  emptyOfacIntakeAddress,
  emptyOfacIntakeFamilyMember,
  emptyOfacIntakeEmployer,
  appendOfacIntakeRow,
  removeOfacIntakeRow,
  patchOfacIntakeRow,
  firstOfacHireLocation,
  syncOfacIntakeLegalName,
  composeOfacIntakeLegalName,
  composeOfacIntakePlace,
  OFAC_REVIEW_CYCLE_DAYS,
  OFAC_FAMILY_RELATIONS,
  OFAC_INTAKE_MAX_ROWS,
  type OfacIntakeFamilyMember,
} from './ofacNamedWhitelistUi';
import { laneForVerifyStep } from './constants/complianceWorkflowGuardrails';
import { OfacLocationSelect } from './OfacLocationSelect';
import { OfacNameOrderIntake } from './OfacNameOrderIntake';
import { ResearchKeyDataIntake, chooseResearchIntakeKind } from './ResearchKeyDataIntakeForm';
import {
  EMPTY_RESEARCH_PERSON_FIELDS,
  composeResearchPersonLegalName,
  composeResearchPersonPlace,
  researchPersonOriginCountryCodes,
  isCountryOnlyResearchPlace,
  researchPersonAgeBasis,
  researchPersonAgePayload,
  researchFamilyMemberAliasSeeds,
  researchPersonNameStyle,
  researchPersonSearchAliases,
} from './researchPersonIntake';
import {
  ofacAddressFieldLabel,
  ofacAddressLayout,
  ofacCountryOptions,
  withOtherOption,
  type OfacAddressField,
} from './ofacIntakeCatalog';
import {
  CONSOLE_NAV_COMPACT_MQ,
  DEFAULT_WORK_SUBGROUP_LABELS_EN,
  DEFAULT_WORK_SUBGROUP_LABELS_ES,
  consoleNavGroupForItem,
  filterConsoleNavGroups,
  groupConsoleNavItems,
  initialOpenConsoleNavGroup,
  openGroupForNavSelection,
  openWorkSubgroupForNavSelection,
  navItemMatchesQuery,
  shouldExpandConsoleNavGroup,
  shouldExpandWorkSubgroup,
  type ConsoleNavGroupId,
  type ConsoleWorkSubgroupId,
} from './consoleNavGroups';
import {
  CONSOLE_INLET_PATH,
  CONSOLE_PURSUIT_PATH,
  CONSOLE_RAZZMATAZZ_PATH,
  isCrmNavHighlightPath,
  isInletOperatorPath,
  isPursuitOperatorPath,
  isRazzmatazzOperatorPath,
  pursuitViewBreadcrumbLabel,
  pursuitViewFromPath,
} from './consolePaths';
import { inferUserGroups, loadGroupScopes, matchesScopedResource, resolveSalesScope } from './iamModel';
import {
  canAccessCrmNav,
  canAccessEntitledProductNav,
  canAccessInletNav,
  canAccessRazzmatazzNav,
  canManageBillingNav,
  canWriteWorkspaceNav,
} from './subscriberNavAccess';
import AdminConsolePage from './AdminConsolePage';
import ConsoleAssistant, { type ConsoleHelpSignal } from './ConsoleAssistant';
import PursuitWorkspace from './PursuitWorkspace';
import InletWorkspace from './InletWorkspace';
import RazzmatazzWorkspace from './RazzmatazzWorkspace';
import CompliDeskWorkspace from './CompliDeskWorkspace';
import PostludePanel from './PostludePanel';
import AgentInstallPanel from './AgentInstallPanel';
import FeatureAgentGates from './components/FeatureAgentGates';
import { parseHomeLens, rowMatchesHomeLens } from './homeDashboardLens';
import { publishedDeadlinesFor } from './publishedDeadlines';
import ThemeDesignerPage from './ThemeDesignerPage';
import DashboardEmailAccounts from './DashboardEmailAccounts';
import SubscriptionsDashboard from './components/SubscriptionsDashboard';
import ComplianceConsolePanel from './components/ComplianceConsolePanel';
import CompliancePortfolioPage from './components/CompliancePortfolioPage';
import DashboardWidgetEditor from './components/DashboardWidgetEditor';
import { GlanceColumns, GlanceTimeline } from './components/GlanceVisuals';
import {
  HOME_DASHBOARD_WIDGETS,
  normalizeLayout,
  readAccountPreference,
  readLayout,
  writeAccountPreference,
  writeLayout,
  type DashboardLayout,
} from './dashboardWidgets';
import EvidenceExportPanel from './components/EvidenceExportPanel';
import CustomerBillingPanel from './components/CustomerBillingPanel';
import SubscriptionCatalogPanel from './components/SubscriptionCatalogPanel';
import SubscriptionHubPanel, { type SubscriptionHubTarget } from './components/SubscriptionHubPanel';
import SubscriptionTeamPanel from './components/SubscriptionTeamPanel';
import { getLocalPassword, getLocalUsername } from './accountSecurity';
import HelpBadge from './components/HelpBadge';
import GlossaryTerm from './components/GlossaryTerm';
import DodTermText from './DodTermText';
import {
  downloadOfacScreeningCsvTemplate,
  parseOfacScreeningCsv,
  type OfacImportRow,
} from './ofacScreeningImport';
import OfacDashboardsPanel, { OfacWorkspaceBar } from './OfacDashboardsPanel';
import { businessCreationPillars } from './constants/businessCreationInterview';
import { willsTrustsPillars } from './constants/willsTrustsInterview';
import { type OfacDashboardLive } from './ofacDashboards';
import { getFacebookPageUrl } from './socialLinks';
import {
  companySubjectFromWebsite,
  deriveLocalResearchTargetName,
  fetchResolvedResearchTargetName,
  formatResearchSubjectTitle,
  isSocialPlatformHost,
  socialProfileFromUrl,
} from './researchTargetName';
import { DEFAULT_PAYMENT_PLANS, type PaymentPlanRecord } from './constants/pricingTiers';
import {
  DOD_CONTRACTS_COMPLIANCE_CONSOLE,
  BUSINESS_CREATION_COMPLIANCE_CONSOLE,
  WILLS_TRUSTS_COMPLIANCE_CONSOLE,
  EDUCATION_COMPLIANCE_CONSOLE,
  AI_COMPLIANCE_CONSOLE,
  WEBSITE_COMPLIANCE_CONSOLE,
  CLOUD_COMPLIANCE_CONSOLE,
  OFAC_COMPLIANCE_CONSOLE,
  complianceProgressPercent,
  hasComplianceEntitlement,
  collectHomeworkRows,
  checklistDependencyDepth,
  checklistDependencyStates,
  findChecklistItem,
  isDodWaitingOnExternal,
  outsideCheckStatus,
  outsideWaitFor,
  recommendedWorkRank,
  homeworkStatusTally,
  workflowStatusLabel,
  loadComplianceConsoleState,
  emptyComplianceState,
  parseWillsTrustsAnswers,
  planFromFeatures,
  type ComplianceConsoleDefinition,
} from './constants/complianceChecklists';
import {
  APPEARANCE_UPDATED_EVENT,
  type AppearanceDensity,
  type AppearanceFontScale,
  type AppearancePreferences,
  type AppearanceReduceMotion,
  type AppearanceThemeMode,
  loadAppearancePreferences,
  resolveThemeMode,
  saveAppearancePreferences,
} from './appearanceRuntime';
import './Dashboard.css';

type NavIconName =
  | 'home'
  | 'dashboards'
  | 'crm'
  | 'inlet'
  | 'razzmatazz'
  | 'personal'
  | 'security'
  | 'privacy'
  | 'payments'
  | 'billing'
  | 'subscriptions'
  | 'research'
  | 'complidesk'
  | 'ofac'
  | 'ofacCompliance'
  | 'ofacDashboards'
  | 'ofacLiveFeed'
  | 'ofacScreening'
  | 'ofacInvestigations'
  | 'ofacWhitelist'
  | 'ofacCapta'
  | 'websiteCompliance'
  | 'cloudCompliance'
  | 'dodContracts'
  | 'educationCompliance'
  | 'aiCompliance'
  | 'businessFormation'
  | 'willsTrusts'
  | 'complianceCalendar'
  | 'alerts'
  | 'audits'
  | 'tips'
  | 'style'
  | 'admin'
  | 'costOptimization'
  | 'emailInbox'
  | 'voiceMessages'
  | 'keycloakAdmin'
  | 'socials';

type OfacScreeningRelationshipType =
  | 'customer'
  | 'vendor'
  | 'payee'
  | 'ubo_director'
  | 'employee'
  | 'contractor'
  | 'associate'
  | 'intermediary'
  | 'vessel'
  | 'aircraft';
type OfacInvestigationStatus = 'not_started' | 'in_progress' | 'escalated' | 'closed';
type OfacInvestigationDisposition =
  | 'pending'
  | 'cleared'
  | 'likely_blocked'
  | 'confirmed_blocked'
  | 'insufficient_data'
  | 'false_positive';
type OfacChecklistStepId =
  | 'subject_in_portfolio'
  | 'collect_ownership_sources'
  | 'verify_multi_tier_chart'
  | 'screen_owners_sdn'
  | 'aggregate_blocked_stake'
  | 'assess_control_indicators'
  | 'document_determination'
  | 'confirm_disposition'
  | 'schedule_monitoring';
type OfacHireChecklistStepId =
  | 'identity_on_file'
  | 'gov_affiliation_declared'
  | 'sdn_screened'
  | 'not_gov_or_licensed'
  | 'fifty_percent_if_owns_company'
  | 'payment_path'
  | 'us_reviewer_signoff'
  | 'schedule_hire_monitoring';
type OfacChecklistStepState = {
  done?: boolean;
  done_at?: string | null;
  source?: string | null;
  note?: string;
};
type OfacScreenMatch = {
  uid?: string;
  name?: string;
  entity_type?: string;
  programs?: string[];
  match_type?: string;
  similarity?: number;
  entry_hash?: string;
  list_source?: string;
  source?: string;
};
type OfacFalseHitPair = {
  id: number;
  subject_id: number;
  list_source?: string;
  list_entry_id: string;
  list_entry_name?: string | null;
  status: string;
  rationale?: string;
  maker_user_id?: string;
  checker_user_id?: string | null;
  four_eyes_waived?: boolean;
  expires_at?: string | null;
  approved_at?: string | null;
  recertified_at?: string | null;
  recertify_due?: boolean;
  invalidate_reason?: string | null;
};
type OfacNamedWhitelist = {
  id: number;
  company_id?: number | null;
  name: string;
  purpose?: string;
  is_default?: boolean;
  member_count?: number;
};
type OfacWorkspaceCompany = {
  company_id: number;
  name: string;
  group_type?: string;
};
type OfacScreeningSubject = {
  id: number;
  entity_key?: string;
  entity_uuid?: string;
  entity_name: string;
  entity_type: string;
  relationship_type: OfacScreeningRelationshipType | string;
  identifiers?: Record<string, unknown>;
  reference_id?: string | null;
  source: string;
  company_id?: number | null;
  current_status?: string | null;
  last_checked_at?: string | null;
  match_count?: number;
  risk_score?: number | null;
  match_rationale?: string | null;
  investigation_status?: OfacInvestigationStatus | string;
  investigation_disposition?: OfacInvestigationDisposition | string;
  investigation_notes?: string | null;
  investigation_updated_at?: string | null;
  investigation_checklist?: Partial<Record<OfacChecklistStepId, OfacChecklistStepState>>;
  investigation_checklist_progress?: { done?: number; total?: number; remaining?: number };
  fifty_percent_last_run?: {
    status?: string;
    blocked_under_rule?: boolean;
    aggregate_blocked_percent?: number;
    has_owners?: boolean;
    recommendations?: string[];
    generated_at?: string;
  } | null;
  capta_disposition?: string;
  capta_notes?: string | null;
  fact_database?: Record<string, unknown> | null;
  fact_summary?: {
    canonical_name?: string | null;
    address_count?: number;
    alias_count?: number;
    affiliation_count?: number;
    signal_count?: number;
    fact_record_count?: number;
    source_count?: number;
    sources?: string[];
    vantage?: {
      run_count?: number;
      last_job_id?: string | null;
      last_status?: string | null;
      last_queued_at?: string | null;
      last_completed_at?: string | null;
      last_error?: string | null;
    };
    last_updated?: string | null;
  } | null;
  last_screen_matches?: OfacScreenMatch[];
  last_suppressed_matches?: OfacScreenMatch[];
  last_screen_all_matches?: OfacScreenMatch[];
  named_whitelist_ids?: number[];
  false_hit_summary?: { active?: number; pending?: number; recertify_due?: number };
  hire_clearance?: {
    pathway?: string;
    country?: string | null;
    location?: string | null;
    role_split?: string;
    progress?: { done?: number; total?: number; remaining?: number };
    checklist?: Partial<Record<OfacHireChecklistStepId, OfacChecklistStepState & { role?: string }>>;
    current_step?: {
      id?: string;
      index?: number;
      status?: string;
      short_en?: string;
      short_es?: string;
      label_en?: string;
      label_es?: string;
    };
    can_clear?: boolean;
    blockers?: string[];
    reviewer_user_id?: string | null;
    last_validated_at?: string | null;
    last_validation_attempt_at?: string | null;
    last_validated_status?: string | null;
    recertify_days?: number;
    recertify_due?: boolean;
    validated?: boolean;
    owner?: string | null;
    owner_missing?: boolean;
  } | null;
  report_due_at?: string | null;
  report_clock_started_at?: string | null;
  vantage_action?: {
    label?: string;
    helper?: string;
    mode?: 'first' | 'again' | 'retry' | 'in_progress' | string;
  } | null;
};

function collectOfacScreenMatches(subject: OfacScreeningSubject): OfacScreenMatch[] {
  const identifiers = subject.identifiers || {};
  const fromIds = Array.isArray(identifiers.last_screen_all_matches)
    ? (identifiers.last_screen_all_matches as OfacScreenMatch[])
    : [];
  const pool = [
    ...fromIds,
    ...(subject.last_screen_all_matches || []),
    ...(subject.last_screen_matches || []),
    ...(subject.last_suppressed_matches || []),
  ];
  const seen = new Set<string>();
  const out: OfacScreenMatch[] = [];
  for (const match of pool) {
    const uid = String(match?.uid || '').trim();
    if (!uid || uid.toLowerCase().startsWith('name:') || seen.has(uid)) {
      continue;
    }
    seen.add(uid);
    out.push(match);
  }
  return out;
}

const OFAC_FIFTY_PERCENT_CHECKLIST: Array<{
  id: OfacChecklistStepId;
  automatable: boolean;
  labelEn: string;
  labelEs: string;
  detailEn: string;
  detailEs: string;
}> = [
  {
    id: 'subject_in_portfolio',
    automatable: true,
    labelEn: 'Subject is on the Screening portfolio',
    labelEs: 'El sujeto está en la cartera de Screening',
    detailEn: 'Case opened from the Screening list (auto).',
    detailEs: 'Caso abierto desde la lista de Screening (auto).',
  },
  {
    id: 'collect_ownership_sources',
    automatable: false,
    labelEn: 'Collect ownership sources',
    labelEs: 'Recopilar fuentes de propiedad',
    detailEn: 'Registry extract, certificate of incumbency, and/or shareholder declaration with dates.',
    detailEs: 'Extracto registral, certificado de incumbency y/o declaración de accionistas con fechas.',
  },
  {
    id: 'verify_multi_tier_chart',
    automatable: false,
    labelEn: 'Verify multi-tier ownership chart',
    labelEs: 'Verificar organigrama multi-nivel',
    detailEn: 'Trace beyond one level up — intermediaries can hide blocked ownership (FAQ 401).',
    detailEs: 'Rastrear más de un nivel — intermediarios pueden ocultar propiedad bloqueada (FAQ 401).',
  },
  {
    id: 'screen_owners_sdn',
    automatable: true,
    labelEn: 'Screen owners against SDN',
    labelEs: 'Evaluar dueños contra SDN',
    detailEn: 'Automated when Run 50% check has owner names.',
    detailEs: 'Automático al ejecutar la regla del 50% con nombres de dueños.',
  },
  {
    id: 'aggregate_blocked_stake',
    automatable: true,
    labelEn: 'Aggregate blocked ownership (direct + indirect)',
    labelEs: 'Agregar propiedad bloqueada (directa + indirecta)',
    detailEn: 'Compute aggregate blocked % per FAQ 401; automated by the 50% engine.',
    detailEs: 'Calcular % bloqueado agregado según FAQ 401; automático con el motor del 50%.',
  },
  {
    id: 'assess_control_indicators',
    automatable: false,
    labelEn: 'Assess control indicators',
    labelEs: 'Evaluar indicadores de control',
    detailEn: 'Board seats, veto rights, signatory authority, fund flows — separate from the ownership rule.',
    detailEs: 'Consejo, derechos de veto, firmas, flujos de fondos — aparte de la regla de propiedad.',
  },
  {
    id: 'document_determination',
    automatable: false,
    labelEn: 'Document the determination',
    labelEs: 'Documentar la determinación',
    detailEn: 'Sources, dates, calculation, control overlay, confidence, and decision-maker.',
    detailEs: 'Fuentes, fechas, cálculo, control, confianza y quien decide.',
  },
  {
    id: 'confirm_disposition',
    automatable: false,
    labelEn: 'Confirm likely outcome',
    labelEs: 'Confirmar resultado probable',
    detailEn: 'Analyst confirms engine suggestion (cleared / likely blocked / confirmed blocked / insufficient data).',
    detailEs: 'El analista confirma la sugerencia del motor.',
  },
  {
    id: 'schedule_monitoring',
    automatable: false,
    labelEn: 'Schedule ongoing re-check',
    labelEs: 'Programar re-chequeo continuo',
    detailEn: 'Re-run when SDN updates or ownership changes — 50% status is dynamic.',
    detailEs: 'Reejecutar ante cambios SDN o de propiedad — el estado del 50% es dinámico.',
  },
];

const OFAC_HIRE_RELATIONSHIPS = new Set<OfacScreeningRelationshipType>(['employee', 'contractor']);

const OFAC_HIRE_CHECKLIST: Array<{
  id: OfacHireChecklistStepId;
  role: 'collector' | 'reviewer';
  automatable: boolean;
  labelEn: string;
  labelEs: string;
  detailEn: string;
  detailEs: string;
}> = [
  {
    id: 'identity_on_file',
    role: 'collector',
    automatable: true,
    labelEn: 'Identity on file (name, DOB, national ID, address)',
    labelEs: 'Identidad en expediente (nombre, fecha de nacimiento, ID nacional, dirección)',
    detailEn: 'Local staff can collect. National ID + date of birth distinguish same-name SDN hits.',
    detailEs: 'El personal local puede recopilar. El ID nacional y la fecha de nacimiento distinguen homónimos en SDN.',
  },
  {
    id: 'gov_affiliation_declared',
    role: 'collector',
    automatable: false,
    labelEn: 'Government / PdVSA affiliation declared',
    labelEs: 'Afiliación gubernamental / PdVSA declarada',
    detailEn: 'Ask whether they work for a government, SOE, or blocked entity. Living in the work city is not an answer.',
    detailEs: 'Preguntar si trabajan para un gobierno, una empresa estatal o una entidad bloqueada. Vivir en la ciudad de trabajo no es la respuesta.',
  },
  {
    id: 'sdn_screened',
    role: 'collector',
    automatable: true,
    labelEn: 'SDN screen run (always — never skipped)',
    labelEs: 'Screening SDN ejecutado (siempre — nunca se omite)',
    detailEn: 'Marked when Screen now completes. A name on a hire list does not skip SDN.',
    detailEs: 'Se marca al completar Screen now. Un nombre en una lista de contratación no omite SDN.',
  },
  {
    id: 'not_gov_or_licensed',
    role: 'reviewer',
    automatable: false,
    labelEn: 'U.S. reviewer: not GoV/SDN/50%, or a current license covers it (FAQ 680)',
    labelEs: 'Revisor EE.UU.: no GoV/SDN/50%, o hay licencia vigente (FAQ 680)',
    detailEn: 'U.S. reviewer confirms they are a private person, or documents the license. GL 52C can cover certain PdVSA activity; it does not skip SDN or reporting.',
    detailEs: 'El revisor en EE.UU. confirma que es persona privada, o documenta la licencia. La GL 52C puede cubrir cierta actividad de PdVSA; no omite SDN ni reportes.',
  },
  {
    id: 'fifty_percent_if_owns_company',
    role: 'reviewer',
    automatable: false,
    labelEn: 'If they own a company, 50% ownership is documented',
    labelEs: 'Si poseen una empresa, la propiedad al 50% está documentada',
    detailEn: 'Optional unless they own or control a company. Then run the FAQ 401 checklist.',
    detailEs: 'Opcional salvo que posean o controlen una empresa. Entonces usar el checklist FAQ 401.',
  },
  {
    id: 'payment_path',
    role: 'reviewer',
    automatable: false,
    labelEn: 'Payment / bank path documented (not a blocked bank)',
    labelEs: 'Ruta de pago / banco documentada (no un banco bloqueado)',
    detailEn: 'How NextCompli will pay them. Each payroll can be a separate OFAC transaction. GL 52C still requires permitted payment routing, not a blocked bank.',
    detailEs: 'Cómo NextCompli les pagará. Cada nómina puede ser una transacción OFAC aparte. La GL 52C sigue exigiendo una ruta de pago permitida, no un banco bloqueado.',
  },
  {
    id: 'us_reviewer_signoff',
    role: 'reviewer',
    automatable: false,
    labelEn: 'U.S. reviewer sign-off (four-eyes) — cleared to hire',
    labelEs: 'Firma del revisor EE.UU. (cuatro ojos) — autorizado para contratar',
    detailEn: 'The mute button stays in the U.S. Local staff collect; a U.S. reviewer approves hire.',
    detailEs: 'El botón de silenciar queda en EE.UU. El personal local recopila; un revisor en EE.UU. aprueba la contratación.',
  },
  {
    id: 'schedule_hire_monitoring',
    role: 'reviewer',
    automatable: false,
    labelEn: 'Schedule re-screen before payroll / on SDN updates',
    labelEs: 'Programar re-screen antes de nómina / ante actualizaciones SDN',
    detailEn: 'Hire clearance is not forever. Re-screen on list updates and before each payment cycle.',
    detailEs: 'La autorización de contratación no es para siempre. Re-screen en actualizaciones de lista y antes de cada ciclo de pago.',
  },
];

const OFAC_HIRE_STEP_SHORT: Record<OfacHireChecklistStepId, { en: string; es: string }> = {
  identity_on_file: { en: 'Identity', es: 'Identidad' },
  gov_affiliation_declared: { en: 'Affiliation', es: 'Afiliación' },
  sdn_screened: { en: 'SDN', es: 'SDN' },
  not_gov_or_licensed: { en: 'FAQ 680', es: 'FAQ 680' },
  fifty_percent_if_owns_company: { en: '50%', es: '50%' },
  payment_path: { en: 'Payment', es: 'Pago' },
  us_reviewer_signoff: { en: 'Sign-off', es: 'Firma' },
  schedule_hire_monitoring: { en: 'Schedule', es: 'Programa' },
};

const OFAC_ACTIVE_WHITELIST_STORAGE_KEY = 'nextcompli.ofac.activeWhitelistId';
const OFAC_ACTIVE_COMPANY_STORAGE_KEY = 'nextcompli.ofac.activeCompanyId';

const isOfacHireRelationship = (relationshipType: string): boolean => (
  OFAC_HIRE_RELATIONSHIPS.has(relationshipType as OfacScreeningRelationshipType)
);

const ofacEntityTypeForRelationship = (relationshipType: OfacScreeningRelationshipType): string => {
  if (relationshipType === 'vessel' || relationshipType === 'aircraft') {
    return relationshipType;
  }
  if (relationshipType === 'ubo_director' || relationshipType === 'associate' || isOfacHireRelationship(relationshipType)) {
    return 'person';
  }
  return 'company';
};

const isOfacHireSubject = (subject: OfacScreeningSubject): boolean => {
  if (isOfacHireRelationship(String(subject.relationship_type || ''))) {
    return true;
  }
  return isOfacHirePathway(
    subject.hire_clearance?.pathway
    || (subject.identifiers || {}).hire_pathway,
  );
};

const formatOfacTimestamp = (value?: string | null, language: DashboardLanguage = 'en'): string => {
  const raw = String(value || '').trim();
  if (!raw) {
    return language === 'es' ? 'Nunca' : 'Never';
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  return parsed.toLocaleString(language === 'es' ? 'es' : 'en');
};

const nextOfacValidationDue = (lastValidatedAt?: string | null, recertifyDays = 365): Date | null => {
  const raw = String(lastValidatedAt || '').trim();
  if (!raw) {
    return null;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  const next = new Date(parsed.getTime());
  next.setUTCDate(next.getUTCDate() + recertifyDays);
  return next;
};

const parseOfacOwnersText = (text: string): Array<{ name: string; percent: number }> => {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const match = line.match(/^(.*?)(?:,\s*|:\s*|\s+)(\d+(?:\.\d+)?)\s*%?\s*$/);
      if (match) {
        return { name: match[1].trim(), percent: Number(match[2]) };
      }
      return { name: line, percent: 0, owner_id: `owner_${index + 1}` } as { name: string; percent: number };
    })
    .filter((row) => row.name);
};

type OfacComplianceKpi = {
  id?: string;
  label: string;
  value: string;
  tone: string;
  source?: string;
};

type OfacComplianceDashboardPayload = {
  data_source?: string;
  kpis?: OfacComplianceKpi[];
  list_coverage?: Array<{
    id?: string;
    nav_target?: DashboardNavItemId | string;
    name: string;
    detail: string;
    freshness: string;
    status: string;
    purpose?: string;
  }>;
  queue?: Array<{
    name: string;
    reason: string;
    age: string;
    status: string;
    assignee: string;
    disposition: string;
    program?: string | null;
  }>;
  triage?: Array<{
    id?: string;
    title: string;
    body: string;
    status?: 'open' | 'complete';
    objective?: string;
    nav_target?: string;
    action_label?: string;
  }>;
  monitoring?: Array<{ title: string; body: string; age: string }>;
  lanes?: Array<{ label: string; value: string; detail: string }>;
  trend?: { volume_points?: number[]; exception_points?: number[] };
  screening_import_sample?: string;
  notes?: string[];
  plan_gates?: {
    continuous_monitoring?: boolean;
    real_time_batch_screening?: boolean;
  };
  reporting_clocks?: {
    open?: number;
    overdue?: number;
    business_days?: number;
  };
  plan_coverage?: {
    tier_id?: string;
    plan_label?: string;
    rescreen_mode?: string;
    auto_rescreen_on_notice?: boolean;
    csv_batch_import?: boolean;
    notes?: { en?: string; es?: string };
  };
  rescreen_mode?: string;
  email_notices?: Array<{
    notice_id?: string;
    title: string;
    sent_at?: string | null;
    ingested_at?: string | null;
    source?: string;
    lists?: string[];
    general_licenses?: string[];
    recent_action_urls?: string[];
    refresh_targets?: string[];
  }>;
  email_notice_mailbox?: string;
};

/** Industry keyword definitions for OFAC rollover tooltips (hover + focus). */
const OFAC_GLOSSARY = {
  ofac: {
    en: 'U.S. Treasury Office of Foreign Assets Control — administers and enforces economic and trade sanctions.',
    es: 'Oficina de Control de Activos Extranjeros del Tesoro de EE. UU. — administra y aplica sanciones económicas y comerciales.',
  },
  sdn: {
    en: 'Specially Designated Nationals and Blocked Persons List — persons and entities whose property U.S. persons must block.',
    es: 'Lista de Nacionales Especialmente Designados y Personas Bloqueadas — personas y entidades cuyos bienes las personas de EE. UU. deben bloquear.',
  },
  capta: {
    en: 'Correspondent Account or Payable-Through Account Sanctions List — foreign financial institutions restricted from (or conditioned on) U.S. correspondent / payable-through accounts. Not an SDN blocking list.',
    es: 'Lista de sanciones de cuentas corresponsales o payable-through — instituciones financieras extranjeras restringidas (o condicionadas) respecto a cuentas corresponsales / payable-through en EE. UU. No es una lista de bloqueo SDN.',
  },
  fiftyPercent: {
    en: 'OFAC 50% rule (FAQ 401): an entity owned 50% or more (aggregate, direct or indirect) by blocked persons is itself blocked even if not named on the SDN.',
    es: 'Regla del 50% de OFAC (FAQ 401): una entidad poseída en un 50% o más (agregado, directo o indirecto) por personas bloqueadas queda bloqueada aunque no figure en SDN.',
  },
} as const;

const ofacSparklinePoints = (
  values: number[],
  x0: number,
  x1: number,
  yTop: number,
  yBottom: number,
): string => {
  if (!values.length) {
    return '';
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(1, max - min);
  return values
    .map((value, index) => {
      const x = x0 + (index / Math.max(1, values.length - 1)) * (x1 - x0);
      const y = yBottom - ((value - min) / span) * (yBottom - yTop);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
};

const OFAC_SCREENING_RELATIONSHIP_OPTIONS: { value: OfacScreeningRelationshipType; labelEn: string; labelEs: string }[] = [
  { value: 'customer', labelEn: 'Customer', labelEs: 'Cliente' },
  { value: 'vendor', labelEn: 'Vendor', labelEs: 'Proveedor' },
  { value: 'payee', labelEn: 'Payee', labelEs: 'Beneficiario de pago' },
  { value: 'ubo_director', labelEn: 'UBO / Director', labelEs: 'UBO / Director' },
  { value: 'employee', labelEn: 'Employee (hire)', labelEs: 'Empleado (contratación)' },
  { value: 'contractor', labelEn: 'Contractor (hire)', labelEs: 'Contratista (contratación)' },
  { value: 'associate', labelEn: 'Family / associate', labelEs: 'Familiar / asociado' },
  { value: 'intermediary', labelEn: 'Intermediary', labelEs: 'Intermediario' },
  { value: 'vessel', labelEn: 'Vessel', labelEs: 'Buque' },
  { value: 'aircraft', labelEn: 'Aircraft', labelEs: 'Aeronave' },
];

const NavStarIcon: React.FC<{ filled: boolean }> = ({ filled }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <path
      d="M12 3.2l2.2 4.7 5.1.7-3.7 3.6.9 5.1L12 15.8 7.5 17.3l.9-5.1L4.7 8.6l5.1-.7L12 3.2z"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

const NavIcon: React.FC<{ name: NavIconName }> = ({ name }) => {
  switch (name) {
    case 'home':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3.5 10.5 12 4l8.5 6.5" />
          <path d="M6.5 9.8V20h11V9.8" />
          <path d="M10 20v-5h4v5" />
        </svg>
      );
    case 'ofacDashboards':
    case 'dashboards':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3.8" y="4.8" width="16.4" height="14.4" rx="2.2" />
          <path d="M8.2 9.2h7.6" />
          <path d="M8.2 12h4.8" />
          <path d="M8.2 14.8h6" />
        </svg>
      );
    case 'crm':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="4" y="5" width="16" height="14" rx="2.4" />
          <path d="M8 9h8" />
          <path d="M8 13h5" />
          <path d="M15.5 13.5 17 15l2.5-3" />
        </svg>
      );
    case 'inlet':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 6h16l-3.5 6H7.5L4 6Z" />
          <path d="M8 12v6h8v-6" />
          <path d="M10 22h4" />
        </svg>
      );
    case 'razzmatazz':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="7.2" />
          <path d="M12 3.2v2.4M12 18.4v2.4M3.2 12h2.4M18.4 12h2.4" />
          <path d="m12 8.2 1 1.8 2 .4-1.4 1.4.4 2L12 12.8 9.6 14l.4-2L8.6 10.4l2-.4z" />
        </svg>
      );
    case 'personal':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="4" y="6" width="16" height="14" rx="2.5" />
          <circle cx="12" cy="10.3" r="2.2" />
          <path d="M8.6 17.2c.9-1.9 2-2.9 3.4-2.9s2.5 1 3.4 2.9" />
        </svg>
      );
    case 'security':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="6" y="10" width="12" height="10" rx="2" />
          <path d="M9 10V8a3 3 0 0 1 6 0v2" />
          <circle cx="12" cy="15" r="1.6" />
        </svg>
      );
    case 'privacy':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 4.5c3.6 0 6.5 1.4 6.5 3.1 0 4.6-2.1 8.4-6.5 11.2C7.6 16 5.5 12.2 5.5 7.6 5.5 5.9 8.4 4.5 12 4.5Z" />
          <path d="M9.8 12.2h4.4" />
          <path d="M12 10v4.4" />
        </svg>
      );
    case 'payments':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="4" y="7" width="16" height="10" rx="2" />
          <path d="M4.8 10.5h14.4" />
          <path d="M8.4 14h3.2" />
        </svg>
      );
    case 'admin':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 4.5 4.8 7.6v4.9c0 4 2.7 6.5 7.3 8.9 4.6-2.4 7.3-4.9 7.3-8.9V7.6L12 4.5Z" />
          <path d="M9 12.2h6" />
          <path d="M12 9.2v6" />
        </svg>
      );
    case 'costOptimization':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 19h16" />
          <path d="M7 16V9" />
          <path d="M12 16V5" />
          <path d="M17 16v-3" />
        </svg>
      );
    case 'emailInbox':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
        </svg>
      );
    case 'subscriptions':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="4" y="5" width="16" height="14" rx="2" />
          <path d="M8 9.5h8" />
          <path d="M8 13h5" />
          <circle cx="16.5" cy="13.5" r="2.5" />
        </svg>
      );
    case 'billing':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 10h18" />
          <path d="M7 15h3" />
          <path d="M14 15h3" />
        </svg>
      );
    case 'research':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="6" />
          <path d="m20 20-4.1-4.1" />
          <path d="M11 8.6v4.8" />
          <path d="M8.6 11h4.8" />
        </svg>
      );
    case 'complidesk':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3.5" y="5" width="17" height="14" rx="2" />
          <path d="M3.5 9h17" />
          <path d="M8 13h5" />
          <path d="M8 16h3" />
        </svg>
      );
    case 'ofacCompliance':
    case 'ofac':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 3.5 19 7v5.2c0 4.4-2.9 7.4-7 8.8-4.1-1.4-7-4.4-7-8.8V7l7-3.5Z" />
          <path d="M9.2 12.2h5.6" />
          <path d="M12 9.4v5.6" />
        </svg>
      );
    case 'ofacLiveFeed':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="6" cy="18" r="1.6" fill="currentColor" stroke="none" />
          <path d="M5.2 12.2a6.8 6.8 0 0 1 6.6 6.6" />
          <path d="M5.2 7.4A11.6 11.6 0 0 1 16.6 18.8" />
          <path d="M5.2 3.5A15.5 15.5 0 0 1 20.5 18.8" />
        </svg>
      );
    case 'ofacScreening':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M8 6h11" />
          <path d="M8 12h11" />
          <path d="M8 18h11" />
          <path d="m4.2 5.8 1.4 1.4 2.4-2.8" />
          <path d="m4.2 11.8 1.4 1.4 2.4-2.8" />
          <path d="M4.5 18.2h2.2" />
        </svg>
      );
    case 'ofacInvestigations':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M8 4.5h7.5L19 8v11.5a1.5 1.5 0 0 1-1.5 1.5H8A1.5 1.5 0 0 1 6.5 19.5v-13A1.5 1.5 0 0 1 8 4.5Z" />
          <path d="M15.5 4.5V8H19" />
          <path d="M9 12h6" />
          <path d="M9 15.5h4" />
          <circle cx="16.2" cy="15.8" r="2.2" />
          <path d="m17.7 17.3 1.6 1.6" />
        </svg>
      );
    case 'ofacWhitelist':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M8 4.5h7.5L19 8v11.5a1.5 1.5 0 0 1-1.5 1.5H8A1.5 1.5 0 0 1 6.5 19.5v-13A1.5 1.5 0 0 1 8 4.5Z" />
          <path d="M15.5 4.5V8H19" />
          <path d="m8.8 13.2 1.6 1.6 3.4-3.6" />
          <path d="M9 17.2h6" />
        </svg>
      );
    case 'ofacCapta':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3.5" y="6" width="17" height="12" rx="2" />
          <path d="M3.5 10h17" />
          <path d="M8 14h3" />
          <path d="M13.5 14h4" />
        </svg>
      );
    case 'cloudCompliance':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M7 18h10a4 4 0 0 0 .4-8 5.5 5.5 0 0 0-10.6-1.5A3.5 3.5 0 0 0 7 18Z" />
        </svg>
      );
    case 'websiteCompliance':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="4" y="5" width="16" height="14" rx="2.2" />
          <path d="M8 9h8" />
          <path d="M8 12.5h5" />
          <path d="M8 16h3.5" />
          <path d="m15.2 14.2 1.3 1.3 2.3-2.6" />
        </svg>
      );
    case 'dodContracts':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M7 4.5h7.5L19 9v10.5a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 19.5v-13A1.5 1.5 0 0 1 7 4.5Z" />
          <path d="M14.5 4.5V9H19" />
          <path d="M8.5 12.5h7" />
          <path d="M8.5 15.5h5" />
        </svg>
      );
    case 'educationCompliance':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4.5 10 12 6.5 19.5 10 12 13.5 4.5 10Z" />
          <path d="M7.5 11.5v4.2c0 .6 2 1.8 4.5 1.8s4.5-1.2 4.5-1.8v-4.2" />
          <path d="M19.5 10v5.5" />
        </svg>
      );
    case 'aiCompliance':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="5" y="6" width="14" height="12" rx="2" />
          <path d="M9 10h2" />
          <path d="M13 10h2" />
          <path d="M9 14h6" />
        </svg>
      );
    case 'complianceCalendar':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="4" y="5" width="16" height="15" rx="2" />
          <path d="M8 3.5v3" />
          <path d="M16 3.5v3" />
          <path d="M4 9.5h16" />
          <path d="M8 13h2" />
          <path d="M12 13h2" />
          <path d="M16 13h0.01" />
          <path d="M8 16.5h2" />
        </svg>
      );
    case 'businessFormation':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4.5 20.5h15" />
          <path d="M6.5 20.5V9.5L12 5.5l5.5 4v11" />
          <path d="M10 20.5v-4.5h4v4.5" />
          <path d="M9 12.5h2.2" />
          <path d="M12.8 12.5H15" />
        </svg>
      );
    case 'willsTrusts':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M7 3.5h7l4 4V20.5H7z" />
          <path d="M14 3.5v4h4" />
          <path d="M9.5 12h5" />
          <path d="M9.5 15.5h5" />
        </svg>
      );
    case 'alerts':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 3a6 6 0 0 0-6 6v3l-1.5 2.5h15L18 12V9a6 6 0 0 0-6-6z" />
          <path d="M10 18a2 2 0 0 0 4 0" />
        </svg>
      );
    case 'audits':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M8 4h8v16H8z" />
          <path d="M10 8h4" />
          <path d="M10 12h4" />
          <path d="M10 16h2.5" />
        </svg>
      );
    case 'tips':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 4.8a6.2 6.2 0 0 0-3.6 11.2c.8.6 1.3 1.4 1.5 2.3h4.2c.2-.9.7-1.7 1.5-2.3A6.2 6.2 0 0 0 12 4.8Z" />
          <path d="M9.8 20h4.4" />
        </svg>
      );
    case 'style':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4.5 15.5a3.5 3.5 0 1 0 0 7 2.5 2.5 0 0 0 2.5-2.5V19h1.5a4 4 0 0 0 0-8h-1.8l6.8-6.8a2.3 2.3 0 1 1 3.2 3.2L10 14.2" />
        </svg>
      );
    case 'voiceMessages':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M5.5 7.5a3 3 0 0 1 3-3h7a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3h-3.2l-3.5 2.8v-2.8H8.5a3 3 0 0 1-3-3v-5Z" />
          <path d="M8.2 9.4h7.6" />
          <path d="M8.2 12h5.2" />
        </svg>
      );
    case 'keycloakAdmin':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 4.5 4.8 7.6v4.9c0 4 2.7 6.5 7.3 8.9 4.6-2.4 7.3-4.9 7.3-8.9V7.6L12 4.5Z" />
          <circle cx="12" cy="11.5" r="2" />
          <path d="M12 13.5v2.5" />
        </svg>
      );
    case 'socials':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="18" cy="5" r="2.5" />
          <circle cx="6" cy="12" r="2.5" />
          <circle cx="18" cy="19" r="2.5" />
          <path d="M8.4 10.9 15.6 6.6" />
          <path d="M8.4 13.1 15.6 17.4" />
        </svg>
      );
    default:
      return null;
  }
};

const SettingsCogIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="2.9" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.03.03a2 2 0 0 1-2.83 2.83l-.03-.03a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.04 1.56V21a2 2 0 0 1-4 0v-.05a1.7 1.7 0 0 0-1.04-1.56 1.7 1.7 0 0 0-1.87.34l-.03.03a2 2 0 1 1-2.83-2.83l.03-.03A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.56-1.04H3a2 2 0 0 1 0-4h.04A1.7 1.7 0 0 0 4.6 8.92a1.7 1.7 0 0 0-.34-1.87l-.03-.03a2 2 0 1 1 2.83-2.83l.03.03a1.7 1.7 0 0 0 1.87.34H9a1.7 1.7 0 0 0 1-1.56V3a2 2 0 0 1 4 0v.04a1.7 1.7 0 0 0 1.04 1.56h.01a1.7 1.7 0 0 0 1.87-.34l.03-.03a2 2 0 1 1 2.83 2.83l-.03.03a1.7 1.7 0 0 0-.34 1.87v.01A1.7 1.7 0 0 0 20.96 10H21a2 2 0 0 1 0 4h-.04A1.7 1.7 0 0 0 19.4 15Z" />
  </svg>
);

const AccordionChevronIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const CollapseLeftIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const AdminSubmenuIcon: React.FC<{ name: 'users' | 'groups' | 'roles' | 'crm' | 'costOptimization' | 'observability' | 'experience' }> = ({ name }) => {
  if (name === 'users') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="9" cy="8.5" r="2.5" />
        <path d="M4.8 16.8c.9-2.2 2.4-3.4 4.2-3.4s3.3 1.2 4.2 3.4" />
        <circle cx="16.7" cy="9.5" r="2" />
        <path d="M14.8 16.5c.6-1.5 1.5-2.4 2.7-2.4 1.2 0 2.1.9 2.7 2.4" />
      </svg>
    );
  }

  if (name === 'groups') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3.8" y="5" width="16.4" height="14" rx="2.4" />
        <path d="M7.2 9.2h9.6" />
        <path d="M7.2 12h7.2" />
        <path d="M7.2 14.8h5" />
      </svg>
    );
  }

  if (name === 'costOptimization') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 19h16" />
        <path d="M7 16V9" />
        <path d="M12 16V5" />
        <path d="M17 16v-3" />
      </svg>
    );
  }

  if (name === 'crm') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="4" y="5" width="16" height="14" rx="2.4" />
        <path d="M8 9h8" />
        <path d="M8 13h5" />
        <path d="M15.5 13.5 17 15l2.5-3" />
      </svg>
    );
  }

  if (name === 'observability') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3.5 12h3l2-5 3 10 2.5-6.5 2 3.5h4" />
      </svg>
    );
  }

  if (name === 'experience') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="7.2" />
        <path d="M8.5 13.2c.8 1.4 2 2.1 3.5 2.1s2.7-.7 3.5-2.1" />
        <path d="M9 10h.01M15 10h.01" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 4 6.2 6.3v5.2c0 3.6 2.6 6.2 5.8 7.4 3.2-1.2 5.8-3.8 5.8-7.4V6.3L12 4Z" />
      <path d="M9.2 12.2h5.6" />
    </svg>
  );
};

const SecurityRowIcon: React.FC<{ name: 'tips' | 'activity' | 'shield' | 'passkey' | 'password' | 'skip' | 'prompt' | 'phone' | 'email' | 'backup' | 'device' | 'locate' }> = ({ name }) => {
  switch (name) {
    case 'tips':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 3.5 4.8 6.4V12c0 4.1 2.9 6.9 7.2 8.5 4.3-1.6 7.2-4.4 7.2-8.5V6.4L12 3.5Z" />
          <path d="m9.3 12.2 1.8 1.8 3.6-3.8" />
        </svg>
      );
    case 'activity':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="8.5" />
          <path d="M12 7.2V12l3 2" />
        </svg>
      );
    case 'shield':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 4 6.2 6.3v5.2c0 3.6 2.6 6.2 5.8 7.4 3.2-1.2 5.8-3.8 5.8-7.4V6.3L12 4Z" />
        </svg>
      );
    case 'passkey':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="8" cy="12" r="3" />
          <path d="M11 12h8" />
          <path d="M16 12v2" />
          <path d="M18.5 12v1.5" />
        </svg>
      );
    case 'password':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4.2 12h15.6" />
          <path d="M7.2 9.5h.01" />
          <path d="M10.7 9.5h.01" />
          <path d="M14.2 9.5h.01" />
        </svg>
      );
    case 'skip':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M5 5 19 19" />
          <path d="M9 9a3 3 0 1 1 4.2 4.2" />
          <path d="M4.5 12h3" />
          <path d="M16.5 12h3" />
        </svg>
      );
    case 'prompt':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="8" y="3.8" width="8" height="16.4" rx="1.8" />
          <path d="M11 16.5h2" />
        </svg>
      );
    case 'phone':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="8" y="3.8" width="8" height="16.4" rx="1.8" />
          <path d="M11 7.8h2" />
          <path d="M10.8 16.5h2.4" />
        </svg>
      );
    case 'email':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="4.5" y="6.5" width="15" height="11" rx="1.8" />
          <path d="m5.5 8 6.5 5 6.5-5" />
        </svg>
      );
    case 'backup':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="5" y="7.5" width="14" height="9" rx="1.5" />
          <path d="M9 10.8h6" />
          <path d="M9 13.2h2.8" />
        </svg>
      );
    case 'device':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="4.5" y="6.5" width="15" height="9.8" rx="1.6" />
          <path d="M10 18.8h4" />
        </svg>
      );
    case 'locate':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="6" />
          <circle cx="12" cy="12" r="1.8" />
          <path d="M12 4v2" />
          <path d="M12 18v2" />
          <path d="M4 12h2" />
          <path d="M18 12h2" />
        </svg>
      );
    default:
      return null;
  }
};

type MetricsDashboardService = {
  service_name: string;
  status?: string;
  response_time_ms?: number;
  requests_total?: number;
  requests_success?: number;
  error_rate?: number;
  risk_level?: string;
  compliance_percentage?: number;
  evidence_items?: string[];
  lacking_items?: string[];
  website_signal?: string;
  confluence_signal?: string;
  metrics_source_required?: string;
  website_scan_required?: boolean;
  confluence_required?: boolean;
  website_scan_coverage?: number;
  website_scan_status?: string;
};

type MetricsDashboardResponse = {
  overall_health?: string;
  healthy_services?: number;
  degraded_services?: number;
  unhealthy_services?: number;
  total_services?: number;
  compliance_risk_score?: number;
  services?: MetricsDashboardService[];
};

type MetricsServiceDataSummary = Record<string, {
  count?: number;
  live_count?: number;
  reliable_count?: number;
  percent_scraped?: number;
  status?: string;
}>;

type LegalExposureAreaInput = {
  service_name: string;
  status?: string;
  compliance_percentage?: number;
  response_time_ms?: number;
  data_freshness_seconds?: number;
  metrics_source_required?: string;
  website_signal?: string;
  evidence?: string[];
  lacking?: string[];
  requirements?: string[];
};

type TermsReplayVerificationResponse = {
  status?: string;
  replay_gate?: string;
  replay_gate_status?: string;
  verification_status?: string;
  verified?: boolean;
};

type AssentReplaySummaryResponse = {
  controls_satisfied?: boolean;
  recent_events?: number;
  minimum_recent_events_required?: number;
  version_hash_present?: boolean;
  immutable_reference_present?: boolean;
  latest_event_at?: string | null;
};

type ContractUpdateNoticeStatusResponse = {
  deployment_gate_status?: string;
  controls_satisfied?: boolean;
  missing_signals?: string[];
};

type TermsPageAccessAndLinkageStatusResponse = {
  deployment_gate_status?: string;
  controls_satisfied?: boolean;
  missing_signals?: string[];
};

type AuthenticatedEntrypointAssentStatusResponse = {
  deployment_gate_status?: string;
  controls_satisfied?: boolean;
  missing_signals?: string[];
};

type NegotiatedContractTermsStatusResponse = {
  deployment_gate_status?: string;
  controls_satisfied?: boolean;
  missing_signals?: string[];
};

type PublicTermsPageStatusResponse = {
  deployment_gate_status?: string;
  controls_satisfied?: boolean;
  missing_signals?: string[];
};

type AIServiceInsight = {
  service_name: string;
  predicted_risk_score: number;
  risk_band: string;
  confidence: number;
  rationale?: string[];
  evidence?: string[];
  recommended_actions?: string[];
};

type AIInsightsData = {
  model_version: string;
  overall_risk_score: number;
  top_risks: AIServiceInsight[];
};

type AIDeepLearningInferenceData = {
  service_name: string;
  model_version: string;
  predicted_risk_score: number;
  risk_band: string;
  confidence: number;
};

type AIPredictiveForecastPoint = {
  day_offset: number;
  predicted_risk_score: number;
  risk_band: string;
};

type AIPredictiveForecastData = {
  service_name: string;
  baseline_score: number;
  daily_trend: number;
  forecast: AIPredictiveForecastPoint[];
};

type AINlpSignal = {
  name: string;
  confidence: number;
  evidence?: string[];
};

type AINlpAnalysisData = {
  sentiment: string;
  urgency_score: number;
  key_phrases?: string[];
  signals?: AINlpSignal[];
  recommended_actions?: string[];
};

const getStoredDashboardLanguage = (): DashboardLanguage => (window.localStorage.getItem('nextcompli.website.language') === 'es' ? 'es' : 'en');


const normalizeContractUpdateNoticeGateStatus = (payload: ContractUpdateNoticeStatusResponse | null): string | null => {
  if (!payload) {
    return null;
  }

  const rawStatus = payload.deployment_gate_status
    ?? (payload.controls_satisfied ? 'pass' : 'pending');

  if (!rawStatus) {
    return null;
  }

  return String(rawStatus).replace(/_/g, ' ').trim().toUpperCase();
};

const normalizeTermsPageAccessGateStatus = (payload: TermsPageAccessAndLinkageStatusResponse | null): string | null => {
  if (!payload) {
    return null;
  }

  const rawStatus = payload.deployment_gate_status
    ?? (payload.controls_satisfied ? 'pass' : 'pending');

  if (!rawStatus) {
    return null;
  }

  return String(rawStatus).replace(/_/g, ' ').trim().toUpperCase();
};

const normalizeAuthenticatedEntrypointAssentGateStatus = (
  payload: AuthenticatedEntrypointAssentStatusResponse | null,
): string | null => {
  if (!payload) {
    return null;
  }

  const rawStatus = payload.deployment_gate_status
    ?? (payload.controls_satisfied ? 'pass' : 'pending');

  if (!rawStatus) {
    return null;
  }

  return String(rawStatus).replace(/_/g, ' ').trim().toUpperCase();
};

const normalizeNegotiatedContractTermsGateStatus = (
  payload: NegotiatedContractTermsStatusResponse | null,
): string | null => {
  if (!payload) {
    return null;
  }

  const rawStatus = payload.deployment_gate_status
    ?? (payload.controls_satisfied ? 'pass' : 'pending');

  if (!rawStatus) {
    return null;
  }

  return String(rawStatus).replace(/_/g, ' ').trim().toUpperCase();
};

const normalizePublicTermsPageGateStatus = (
  payload: PublicTermsPageStatusResponse | null,
): string | null => {
  if (!payload) {
    return null;
  }

  const rawStatus = payload.deployment_gate_status
    ?? (payload.controls_satisfied ? 'pass' : 'pending');

  if (!rawStatus) {
    return null;
  }

  return String(rawStatus).replace(/_/g, ' ').trim().toUpperCase();
};

type DashboardLanguage = 'en' | 'es';

type AccountProfile = {
  personUuid: string;
  displayName: string;
  email: string;
  contactNumber: string;
  language: string;
  timezone: string;
};

type AccountAvatarMap = Record<string, string>;

type DeviceSessionRecord = {
  id: string;
  label: string;
  browser: string;
  ipAddress: string;
  location: string;
  lastSeen: string;
};

type DeviceGroupKey = 'mac' | 'iphone' | 'windows';

type PaymentMethodRecord = {
  id: string;
  type: 'card' | 'bank';
  label: string;
  detail: string;
  isDefault: boolean;
};

type SubscriptionAdminRecord = {
  id: string;
  name: string;
  monthlyPrice: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled';
  renewalDate: string;
  ownerEmail: string;
};

type SecurityOptionKey =
  | 'two-step'
  | 'passkeys'
  | 'password'
  | 'two-step-phones'
  | 'recovery-phone'
  | 'recovery-email'
  | 'login-aliases'
  | 'backup-codes';

type DashboardServiceFilterKey = 'ofac' | 'websiteCompliance' | 'governmentalCompliance';
type DashboardNavItemId = 'home' | 'dashboards' | 'crm' | 'inlet' | 'razzmatazz' | 'personal' | 'security' | 'team' | 'privacy' | 'mySubscriptions' | 'payments' | 'billing' | 'subscriptions' | 'subscriptionHub' | 'subscriptionLevels' | 'payingSubscriptions' | 'expiredSubscriptions' | 'research' | 'complidesk' | 'postlude' | 'agent' | 'ofac' | 'ofacCompliance' | 'ofacDashboards' | 'ofacLiveFeed' | 'ofacScreening' | 'ofacInvestigations' | 'ofacWhitelist' | 'ofacCapta' | 'websiteCompliance' | 'cloudCompliance' | 'dodContracts' | 'educationCompliance' | 'aiCompliance' | 'businessFormation' | 'willsTrusts' | 'complianceCalendar' | 'audits' | 'tips' | 'style' | 'admin' | 'costOptimization' | 'emailInbox' | 'voiceMessages' | 'keycloakAdmin' | 'socials';
type AdminPrivilegeKey = 'users' | 'groups' | 'roles' | 'observability' | 'experience';

type TipOfDayDefinition = {
  id: string;
  audience: 'non_super_admin';
  title: {
    en: string;
    es: string;
  };
  body: {
    en: string;
    es: string;
  };
  action: {
    navItem: DashboardNavItemId;
    label: {
      en: string;
      es: string;
    };
  };
};

type TipOfDayState = {
  lastShownDate?: string;
  lastTipId?: string;
  dismissedUntilDate?: string;
};

type ResearchAffinityGraphNode = {
  id: string;
  label: string;
  entity_type: 'company' | 'person' | string;
  degree: number;
  is_root?: boolean;
};

type ResearchAffinityGraphEdge = {
  source: string;
  target: string;
  relationship: string;
  degree?: number;
  source_key?: string;
};

type ResearchAffinityGraph = {
  requested_depth?: number;
  nodes?: ResearchAffinityGraphNode[];
  edges?: ResearchAffinityGraphEdge[];
  mermaid?: string;
};

type KeyData = {
  names: string;
  addresses: string;
  phones: string;
  emails: string;
  birthdate: string;
  /** Approximate age range as `min-max` (people). Used when birthdate is unknown. */
  ageRange: string;
  ageBasis?: 'birthdate' | 'ageRange';
  ssn: string;
  dlNumber: string;
  companies: string;
  socialMediaHandles: string;
  website: string;
  knownAliases: string;
  searchCountry: string;
  nationality: string;
  nationalities: string;
  gender: '' | 'male' | 'female';
  includeMaidenName?: boolean;
  addressLine: string;
  addressLine2: string;
  postalCode: string;
  familyMembers?: { name: string; relation: string }[];
  nameStyle: '' | 'hispanic' | 'portuguese' | 'western';
  nameStyleLocked?: boolean;
  givenName: string;
  givenName2: string;
  surname: string;
  surname2: string;
  formerNameStyle: '' | 'hispanic' | 'portuguese' | 'western';
  formerNameStyleLocked?: boolean;
  formerGivenName: string;
  formerGivenName2: string;
  formerSurname: string;
  formerSurname2: string;
  nationalIdType: string;
  nationalId: string;
  locationCountry: string;
  locationOtherCountry: string;
  locationRegion: string;
  locationOtherRegion: string;
  locationCounty: string;
  locationOtherCounty: string;
  locationCity: string;
  locationOtherCity: string;
  includeMaidenName?: boolean;
  addressLine?: string;
  addressLine2?: string;
  postalCode?: string;
  familyMembers?: OfacIntakeFamilyMember[];
};

type FactListField = 'names' | 'knownAliases' | 'addresses' | 'companies' | 'socialMediaHandles';

type FactEntity = {
  canonical_name?: string;
  alt_names?: string[];
  addresses?: string[];
  birthdate?: string;
  age_range?: string;
  ssn?: string;
  dl_number?: string;
  company_affiliations?: string[];
  social_handles?: Record<string, string>;
  height?: string;
  weight?: string;
  eye_color?: string;
  hair_color?: string;
  realtor_name?: string;
  realtor_phone?: string;
  emergency_contact?: string;
  employment_verification_phone?: string;
  monthly_rent?: string;
  mobile_phone?: string;
  phones?: string[];
  emails?: string[];
};

const identifierLooksMasked = (value: string): boolean => {
  const text = String(value || '').trim();
  if (!text) {
    return true;
  }
  return /[*•]/.test(text) || /x{3,}/i.test(text);
};

const preferCompleteIdentifier = (parsedValue: string, formValue: string, kind: 'ssn' | 'dl'): string => {
  const parsed = String(parsedValue || '').trim();
  const form = String(formValue || '').trim();
  const isComplete = (value: string): boolean => {
    if (!value || identifierLooksMasked(value)) {
      return false;
    }
    if (kind === 'ssn') {
      return value.replace(/\D/g, '').length >= 9;
    }
    return value.replace(/[^A-Za-z0-9]/g, '').length >= 5;
  };
  if (isComplete(parsed)) {
    return parsed;
  }
  if (isComplete(form)) {
    return form;
  }
  return parsed || form;
};

type FactProvenance = {
  check?: string | null;
  run_id?: string | null;
  screening_id?: string | null;
  found_at?: string | null;
  affinity_depth?: number | null;
};

type FactRecord = FactProvenance & {
  value: string;
  field?: string | null;
  fact_type?: string | null;
  also_found_in?: FactProvenance[];
};

type FactDatabaseSignal = {
  source: string;
  fact_type: string;
  value: string | Record<string, any>;
  confidence?: number;
  match_probability?: number;
  decision?: 'included' | 'excluded' | 'review' | 'confirmed' | 'dismissed' | 'pending';
  prominence_noise?: boolean;
  review_reasons?: string[];
  decision_at?: string;
  timestamp?: string;
  check?: string | null;
  run_id?: string | null;
  screening_id?: string | null;
  found_at?: string | null;
  affinity_depth?: number | null;
};

type ResearchCheckTiming = {
  check?: string;
  phase?: string | null;
  started_at?: string;
  start_at?: string;
  ended_at?: string | null;
  end_at?: string | null;
  duration_ms?: number | null;
};

type ResearchLogEntry = {
  at?: string | null;
  level?: string | null;
  check?: string | null;
  phase?: string | null;
  status?: string | null;
  duration_ms?: number | null;
  text: string;
};

type ResearchTimeEfficiencyRecommendation = {
  id?: string;
  severity?: string;
  title?: string;
  detail?: string;
  cloud_hint?: string | null;
};

type ResearchTimeEfficiencyAnalysis = {
  version?: number;
  generated_at?: string;
  wall_elapsed_ms?: number;
  active_check_ms?: number;
  idle_or_unattributed_ms?: number;
  check_count?: number;
  max_concurrent_jobs?: number;
  efficiency_score?: number | null;
  theoretical_parallel_speedup_x?: number;
  top_checks?: Array<{ check?: string; duration_ms?: number; share_of_active?: number }>;
  time_by_class_ms?: Record<string, number>;
  recommendations?: ResearchTimeEfficiencyRecommendation[];
  summary?: string;
};

type FactDatabase = {
  entity: FactEntity;
  signals: FactDatabaseSignal[];
  relationships: Array<{ entity_id: string; relationship_type: string; confidence?: number }>;
  records?: {
    court_cases?: Array<{
      case_number?: string;
      case_name?: string;
      court?: string;
      filed_at?: string;
      case_type?: string;
      case_status?: string;
      subject_role?: string;
      litigation_direction?: string;
      civil?: boolean;
      criminal?: boolean;
    }>;
  };
  sourceCheckFacts?: string[];
  sourceCheckFactsByCheck?: Record<string, string[]>;
  sourceCheckFactCount?: number;
  factRecords?: FactRecord[];
  entityProvenance?: FactRecord[];
  metadata: {
    created_at: string;
    last_updated: string;
    affinity_depth?: number;
    source_checks?: string[];
    check_timings?: ResearchCheckTiming[];
    time_efficiency_analysis?: ResearchTimeEfficiencyAnalysis;
    last_run_id?: string;
    last_screening_id?: string;
    runs?: Array<{
      run_id?: string | null;
      screening_id?: string | null;
      affinity_depth?: number | null;
      at?: string | null;
    }>;
  };
};

type ResearchRerunOption = {
  label: string;
  requestOverrides?: {
    affinity_depth?: number;
  };
};

type ResearchGatewayTiming = {
  request_started_at?: string;
  request_completed_at?: string;
  request_elapsed_ms?: number;
  screening_elapsed_ms?: number;
};

type ResearchReportResponse = {
  report?: string;
  markdown?: string;
  content?: string;
  report_name?: string;
  status?: string;
  message?: string;
  screening_id?: string;
  risk_score?: number;
  match_count?: number;
  affinity_depth?: number;
  affinity_graph?: ResearchAffinityGraph;
  gateway_timing?: ResearchGatewayTiming;
  check_timings?: ResearchCheckTiming[];
  research_log?: ResearchLogEntry[];
  check_results?: Array<Record<string, unknown>>;
  source_checks_runs?: Array<Record<string, unknown>>;
  critical_facts?: ResearchReportRecord['criticalFacts'];
  time_efficiency_analysis?: ResearchTimeEfficiencyAnalysis;
  identifier_readiness?: IdentifierReadinessAssessment;
  email_delivery?: {
    sent?: number;
    failed?: number;
    unresolved_groups?: string[];
  };
  source_of_truth?: string;
  fact_database?: FactDatabase;
  rerun_options?: ResearchRerunOption[];
};

type IdentifierReadinessFact = {
  key: string;
  label: string;
  value: string;
  sensitive?: boolean;
  confidence?: number;
  status?: string;
  attested?: boolean;
};

type IdentifierReadinessOption = {
  id: string;
  label: string;
  description?: string;
  priority?: string;
  value?: string;
};

type DocumentAttestation = {
  document_type?: string;
  document_class?: string;
  signed?: boolean;
  treat_parsed_vitals_as_attested?: boolean;
  has_attestation_clause?: boolean;
  has_signature_block?: boolean;
  signature_executed?: boolean;
  summary?: string;
  confidence?: number;
};

type IdentifierReadinessAssessment = {
  ready?: boolean;
  strongly_ready?: boolean;
  blocked?: boolean;
  guidance?: string;
  warnings?: string[];
  confirmable_facts?: IdentifierReadinessFact[];
  missing_secondary_identifiers?: IdentifierReadinessOption[];
  acceptable_secondary_options?: IdentifierReadinessOption[];
  present_secondary_identifiers?: IdentifierReadinessOption[];
  primary_identifier?: { label: string; value: string } | null;
  identifier_count?: number;
  minimum_required?: number;
  recommended_count?: number;
  standards_reference?: string;
  name_commonality_score?: number;
  document_attestation?: DocumentAttestation | null;
};

type ResearchReportRecord = {
  id: string;
  reportName: string;
  targetType: 'company' | 'person';
  targetName: string;
  /** RBAC: creator user id */
  ownerUserId?: string;
  /** RBAC: creator email (display / legacy match) */
  ownerEmail?: string;
  /** RBAC: owning company id */
  companyId?: number | null;
  screeningId?: string;
  riskScore?: number;
  matchCount?: number;
  searchDurationMs?: number;
  searchStartedAt?: string;
  searchCompletedAt?: string;
  timeEfficiencyAnalysis?: ResearchTimeEfficiencyAnalysis;
  researchLog?: ResearchLogEntry[];
  sourceChecksRuns?: Array<Record<string, unknown>>;
  checkResults?: Array<Record<string, unknown>>;
  reportEvidence?: {
    photoUrls?: string[];
    photoNotes?: string[];
    courts?: Array<{ title: string; detail: string }>;
    contacts?: Array<{ label: string; value: string }>;
    affiliations?: Array<{ kind: string; value: string }>;
    family?: Array<{ name: string; relation: string }>;
    friendNote?: string;
  };
  criticalFacts?: {
    items?: Array<{
      id: string;
      label: string;
      tier?: number;
      status?: string;
      value?: string | null;
      required?: boolean;
    }>;
    summary?: Record<string, unknown>;
  };
  website?: string;
  jurisdiction: string;
  knownAliases: string[];
  notes: string;
  affinityDepth?: number;
  affinityGraph?: ResearchAffinityGraph;
  keyData?: KeyData;
  factDatabase?: FactDatabase;
  sourceOfTruth?: string;
  rerunOptions?: ResearchRerunOption[];
  documentParseReceipt?: Record<string, unknown>;
  emailAddresses?: string[];
  emailGroups?: string[];
  content: string;
  createdAt: string;
  updatedAt: string;
  isFallback?: boolean;
  fallbackReason?: string;
  gatewayJobId?: string;
  vantageJobId?: string;
};

type ResearchQueueStatus = 'queued' | 'screening' | 'completed' | 'failed' | 'cancelled';

type ResearchQueueItem = {
  id: string;
  reportName: string;
  targetType: 'company' | 'person' | 'unknown';
  targetName: string;
  website?: string;
  status: ResearchQueueStatus;
  progress: number;
  message: string;
  createdAt: string;
  updatedAt: string;
  runStartedAt?: string | null;
  etaSeconds?: number;
  reportId?: string;
  gatewayJobId?: string;
  researchLog?: ResearchLogEntry[];
};

type ResearchReportSubmitHandle = {
  kind: 'async_submitted';
  jobId: string;
  reportId: string;
  keyDataPayload: KeyData & { aliases?: string };
};

type ResearchIdentifierConfirmationHandle = {
  kind: 'identifier_confirmation_required';
};

type ComprehensiveBackgroundCheckSummary = {
  overall_status?: string;
  highest_risk_score?: number;
  review_required?: number;
  clear?: number;
  total_matches?: number;
  checks_run?: number;
};

type ComprehensiveBackgroundCheckHistoryRecord = {
  request_id: string;
  target_type: string;
  subject: string;
  countries: string[];
  summary?: ComprehensiveBackgroundCheckSummary;
  created_at: string;
};

const TIP_OF_DAY_STATE_STORAGE_KEY = 'nextcompli.tip_of_day.state.v1';
const TIP_OF_DAY_CATALOG_STORAGE_KEY = 'nextcompli.tip_of_day.catalog.v1';
const RESEARCH_REPORTS_STORAGE_KEY = 'nextcompli.research_reports.catalog.v1';
const RESEARCH_REPORTS_STORAGE_KEY_PREFIX = 'nextcompli.research_reports.catalog.v1.user.';

const researchReportsStorageKeyForUser = (userId?: string | null): string => {
  const normalized = String(userId || '').trim();
  if (!normalized) return RESEARCH_REPORTS_STORAGE_KEY;
  return `${RESEARCH_REPORTS_STORAGE_KEY_PREFIX}${encodeURIComponent(normalized)}`;
};

type ResearchAccessActor = {
  userId?: string | null;
  email?: string | null;
  companyId?: number | null;
  isPlatformAdmin?: boolean;
};

const canAccessResearchReportRecord = (
  report: ResearchReportRecord | null | undefined,
  actor: ResearchAccessActor,
): boolean => {
  if (!report) return false;
  if (actor.isPlatformAdmin) return true;
  const ownerUserId = String(report.ownerUserId || '').trim();
  const ownerEmail = String(report.ownerEmail || '').trim().toLowerCase();
  const actorUserId = String(actor.userId || '').trim();
  const actorEmail = String(actor.email || '').trim().toLowerCase();
  if (ownerUserId && actorUserId && ownerUserId === actorUserId) return true;
  if (ownerEmail && actorEmail && ownerEmail === actorEmail) return true;
  const reportCompanyId = report.companyId != null ? Number(report.companyId) : null;
  const actorCompanyId = actor.companyId != null ? Number(actor.companyId) : null;
  if (
    reportCompanyId != null
    && Number.isFinite(reportCompanyId)
    && actorCompanyId != null
    && Number.isFinite(actorCompanyId)
    && reportCompanyId === actorCompanyId
  ) {
    return true;
  }
  // Legacy unscoped reports: only platform admins (handled above) or no one else.
  return false;
};

const filterResearchReportsForActor = (
  reports: ResearchReportRecord[],
  actor: ResearchAccessActor,
): ResearchReportRecord[] => reports.filter((report) => canAccessResearchReportRecord(report, actor));
const RESEARCH_REPORTS_DISMISSED_STORAGE_KEY = 'nextcompli.research_reports.dismissed.v1';
const RESEARCH_QUEUE_STORAGE_KEY = 'nextcompli.research_queue.v1';
const RESEARCH_REPORT_PDF_WINDOW_NAME = 'NextCompliResearchReportPdf';

const toSafePdfFileName = (value: string): string => {
  const cleaned = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return cleaned || 'research-report';
};

const formatEtaText = (etaSeconds: number, language: 'en' | 'es'): string => {
  const total = Math.max(0, Math.round(etaSeconds || 0));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (language === 'es') {
    if (minutes > 0) return `Aproximadamente ${minutes}m ${seconds}s restantes`;
    return `Aproximadamente ${seconds}s restantes`;
  }
  if (minutes > 0) return `About ${minutes}m ${seconds}s left`;
  return `About ${seconds}s left`;
};

type GatewayResearchJobSummary = {
  job_id: string;
  status?: string;
  phase?: string;
  progress_percent?: number;
  target_type?: string;
  target_name?: string;
  created_at?: string;
  updated_at?: string;
  started_at?: string | null;
  completed_at?: string | null;
  error?: string | null;
  current_check?: string;
  current_check_elapsed_ms?: number;
  checks_completed?: number;
  checks_total?: number;
  vantage_job_id?: string;
  has_result?: boolean;
  result_complete?: boolean;
  created_by_user_id?: string;
  created_by_email?: string;
  company_id?: number | null;
  result?: ResearchReportResponse | null;
};

const researchJobResultIsComplete = (job: GatewayResearchJobSummary): boolean => {
  // Slim list payloads set result_complete: false — always hydrate via job detail.
  if (job.result_complete !== true) {
    return false;
  }
  const result = job.result;
  if (!result || typeof result !== 'object') {
    return false;
  }
  const reportText = String(result.report || result.markdown || result.content || '');
  return reportText.length >= 400 || Boolean(result.fact_database);
};

const normalizeResearchTargetKey = (name: string): string => (
  String(name || '').trim().toLowerCase().replace(/\s+/g, ' ')
);

const extractResearchScreeningId = (value: unknown): string => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (value && typeof value === 'object') {
    const record = value as { screening_id?: unknown; screeningId?: unknown; content?: unknown };
    const direct = String(record.screening_id || record.screeningId || '').trim();
    if (direct) {
      return direct;
    }
    const content = String(record.content || '');
    const match = content.match(/(?:Vantage research run ID|Research run ID|Screening ID):\s*`?([0-9a-f-]{8,})`?/i);
    if (match?.[1]) {
      return match[1].trim();
    }
  }
  return '';
};

const dedupeResearchReports = (reports: ResearchReportRecord[]): ResearchReportRecord[] => {
  const byKey = new Map<string, ResearchReportRecord>();
  for (const report of reports) {
    const screeningId = extractResearchScreeningId(report.screeningId || report);
    const targetKey = normalizeResearchTargetKey(report.targetName || report.reportName);
    const key = screeningId
      ? `screening:${screeningId}`
      : `target:${targetKey}|created:${String(report.createdAt || '').slice(0, 19)}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, report);
      continue;
    }
    const existingUpdated = Date.parse(existing.updatedAt || existing.createdAt || '');
    const candidateUpdated = Date.parse(report.updatedAt || report.createdAt || '');
    if (candidateUpdated >= existingUpdated) {
      byKey.set(key, {
        ...existing,
        ...report,
        screeningId: report.screeningId || existing.screeningId || screeningId || undefined,
      });
    }
  }
  return Array.from(byKey.values()).sort(
    (left, right) => Date.parse(right.updatedAt || right.createdAt || '') - Date.parse(left.updatedAt || left.createdAt || ''),
  );
};

const buildGatewayJobQueueMessage = (
  job: GatewayResearchJobSummary,
  language: 'en' | 'es',
): string => {
  const status = String(job.status || '').toLowerCase();
  const errorText = String(job.error || '').trim();
  if (status === 'failed' || status === 'interrupted') {
    if (errorText && !/^failed$/i.test(errorText)) {
      return errorText;
    }
    if (status === 'interrupted') {
      return language === 'es'
        ? 'Vantage se reinició durante el screening. Use Relanzar.'
        : 'Vantage restarted during screening. Use Rerun.';
    }
    const failedPhase = String(job.phase || '').trim();
    if (failedPhase && !/^failed$/i.test(failedPhase)) {
      return failedPhase;
    }
    return language === 'es'
      ? 'Vantage no terminó este reporte. Use Relanzar.'
      : 'Vantage did not finish this report. Use Rerun.';
  }
  const phase = String(job.phase || '').trim();
  const checkLabel = String(job.current_check || '').trim();
  const checksCompleted = typeof job.checks_completed === 'number' ? job.checks_completed : null;
  const checksTotal = typeof job.checks_total === 'number' ? job.checks_total : null;
  if (checkLabel) {
    const elapsed = typeof job.current_check_elapsed_ms === 'number' ? job.current_check_elapsed_ms : null;
    const elapsedLabel = elapsed != null ? formatCheckTimingDuration(elapsed, language) : '';
    return elapsedLabel ? `${checkLabel} · ${elapsedLabel}` : checkLabel;
  }
  if (phase) return phase;
  if (checksCompleted != null && checksTotal != null && checksTotal > 0) {
    return language === 'es'
      ? `Verificación ${checksCompleted} de ${checksTotal}`
      : `Check ${checksCompleted} of ${checksTotal}`;
  }
  if (String(job.status || '').toLowerCase() === 'queued') {
    return language === 'es' ? 'En cola' : 'Queued';
  }
  return language === 'es' ? 'Ejecutando screening de fuentes...' : 'Running source screening...';
};

const normalizeResearchAffinityGraph = (graph: ResearchAffinityGraph | null | undefined): ResearchAffinityGraph | undefined => {
  if (!graph || !Array.isArray(graph.nodes) || !Array.isArray(graph.edges) || graph.nodes.length === 0) {
    return undefined;
  }
  return {
    requested_depth: typeof graph.requested_depth === 'number' ? graph.requested_depth : 1,
    mermaid: typeof graph.mermaid === 'string' ? graph.mermaid : undefined,
    nodes: graph.nodes
      .filter((node) => node && typeof node.id === 'string' && typeof node.label === 'string')
      .map((node) => ({
        id: node.id,
        label: node.label,
        entity_type: node.entity_type || 'company',
        degree: Number.isFinite(Number(node.degree)) ? Number(node.degree) : 0,
        is_root: Boolean(node.is_root),
      })),
    edges: graph.edges
      .filter((edge) => edge && typeof edge.source === 'string' && typeof edge.target === 'string')
      .map((edge) => ({
        source: edge.source,
        target: edge.target,
        relationship: String(edge.relationship || 'related to'),
        degree: Number.isFinite(Number(edge.degree)) ? Number(edge.degree) : undefined,
        source_key: edge.source_key,
      })),
  };
};

const normalizeResearchRerunOptions = (options: unknown): ResearchRerunOption[] => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options
    .map((option) => {
      if (!option || typeof option !== 'object') {
        return null;
      }
      const record = option as { label?: unknown; request_overrides?: unknown; requestOverrides?: unknown };
      const rawOverrides = (record.request_overrides && typeof record.request_overrides === 'object')
        ? record.request_overrides
        : ((record.requestOverrides && typeof record.requestOverrides === 'object') ? record.requestOverrides : undefined);
      const affinityDepth = rawOverrides && typeof (rawOverrides as { affinity_depth?: unknown }).affinity_depth === 'number'
        ? Number((rawOverrides as { affinity_depth?: unknown }).affinity_depth)
        : undefined;

      return {
        label: String(record.label || '').trim() || 'Rerun',
        requestOverrides: affinityDepth
          ? { affinity_depth: Math.max(1, Math.min(4, affinityDepth)) }
          : undefined,
      } as ResearchRerunOption;
    })
    .filter((option): option is ResearchRerunOption => Boolean(option));
};

const RESEARCH_AFFINITY_DEPTH_OPTIONS = [1, 2, 3, 4] as const;

const GENERIC_RESEARCH_REPORT_NAME_PATTERN = /^(Company|Person)\s+(Research|Background)\s+Report$/i;

const getCustomResearchReportName = (reportName?: string): string => {
  const raw = String(reportName || '').trim();
  if (!raw || GENERIC_RESEARCH_REPORT_NAME_PATTERN.test(raw)) {
    return '';
  }
  return raw;
};

const resolveResearchReportDisplayTitle = (
  report: Pick<ResearchReportRecord, 'reportName' | 'targetName' | 'website' | 'targetType'>
    | Pick<ResearchQueueItem, 'reportName' | 'targetName' | 'targetType'> & { website?: string },
): string => formatResearchSubjectTitle({
  reportName: report.reportName,
  targetName: report.targetName,
  website: 'website' in report ? report.website : undefined,
  targetType: report.targetType,
});

const hasCustomResearchReportTitle = (
  report: Pick<ResearchReportRecord, 'reportName'>,
): boolean => Boolean(getCustomResearchReportName(report.reportName));

const extractResearchSearchTiming = (body: ResearchReportResponse): Pick<ResearchReportRecord, 'searchDurationMs' | 'searchStartedAt' | 'searchCompletedAt'> => {
  const timing = body.gateway_timing;
  const checkTimings = Array.isArray(body.check_timings)
    ? body.check_timings
    : (Array.isArray(body.fact_database?.metadata?.check_timings) ? body.fact_database?.metadata?.check_timings : []);
  const checkSumMs = Array.isArray(checkTimings)
    ? checkTimings.reduce((sum, row) => {
      const ms = Number(row?.duration_ms);
      return sum + (Number.isFinite(ms) && ms > 0 ? ms : 0);
    }, 0)
    : 0;

  const checkStarts = Array.isArray(checkTimings)
    ? checkTimings
      .map((row) => Date.parse(String(row?.started_at || '')))
      .filter((value) => Number.isFinite(value))
    : [];
  const checkEnds = Array.isArray(checkTimings)
    ? checkTimings
      .map((row) => Date.parse(String(row?.ended_at || '')))
      .filter((value) => Number.isFinite(value))
    : [];
  const checkStartedAt = checkStarts.length ? new Date(Math.min(...checkStarts)).toISOString() : undefined;
  const checkCompletedAt = checkEnds.length ? new Date(Math.max(...checkEnds)).toISOString() : undefined;

  const requestStartedAt = timing && typeof timing === 'object' && typeof timing.request_started_at === 'string'
    ? timing.request_started_at
    : undefined;
  const requestCompletedAt = timing && typeof timing === 'object' && typeof timing.request_completed_at === 'string'
    ? timing.request_completed_at
    : undefined;
  const searchStartedAt = requestStartedAt || checkStartedAt;
  const searchCompletedAt = requestCompletedAt || checkCompletedAt;

  let searchDurationMs: number | undefined;
  if (timing && typeof timing === 'object') {
    const screeningMs = Number(timing.screening_elapsed_ms);
    const requestMs = Number(timing.request_elapsed_ms);
    searchDurationMs = Number.isFinite(screeningMs) && screeningMs > 0
      ? screeningMs
      : (Number.isFinite(requestMs) && requestMs > 0 ? requestMs : undefined);
  }
  if (searchDurationMs == null && checkSumMs > 0) {
    searchDurationMs = checkSumMs;
  }
  const startMs = Date.parse(String(searchStartedAt || ''));
  const endMs = Date.parse(String(searchCompletedAt || ''));
  if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs >= startMs) {
    const wallMs = endMs - startMs;
    if (wallMs <= 24 * 60 * 60 * 1000) {
      searchDurationMs = wallMs;
    }
  }

  if (searchDurationMs == null && !searchStartedAt && !searchCompletedAt) {
    return {};
  }
  return {
    ...(searchDurationMs != null ? { searchDurationMs } : {}),
    ...(searchStartedAt ? { searchStartedAt } : {}),
    ...(searchCompletedAt ? { searchCompletedAt } : {}),
  };
};

const resolveResearchSearchTimingBounds = (
  report: Pick<ResearchReportRecord, 'searchStartedAt' | 'searchCompletedAt' | 'searchDurationMs' | 'createdAt' | 'updatedAt' | 'factDatabase' | 'timeEfficiencyAnalysis'>,
): { startedAt?: string; completedAt?: string; durationMs?: number } => {
  const checkTimings = Array.isArray(report.factDatabase?.metadata?.check_timings)
    ? report.factDatabase.metadata.check_timings
    : [];
  const checkStarts = checkTimings
    .map((row) => Date.parse(String(row?.started_at || '')))
    .filter((value) => Number.isFinite(value));
  const checkEnds = checkTimings
    .map((row) => Date.parse(String(row?.ended_at || '')))
    .filter((value) => Number.isFinite(value));

  let startedAt = report.searchStartedAt
    || (checkStarts.length ? new Date(Math.min(...checkStarts)).toISOString() : undefined)
    || report.createdAt
    || undefined;
  let completedAt = report.searchCompletedAt
    || (checkEnds.length ? new Date(Math.max(...checkEnds)).toISOString() : undefined)
    || report.updatedAt
    || undefined;

  let durationMs: number | undefined;
  const startMs = Date.parse(String(startedAt || ''));
  const endMs = Date.parse(String(completedAt || ''));
  if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs >= startMs) {
    const wallMs = endMs - startMs;
    // Overnight laptop sleep + re-hydrate often inflated this into multi-hour false durations.
    if (wallMs <= 24 * 60 * 60 * 1000) {
      durationMs = wallMs;
    }
  }

  if (durationMs == null && typeof report.searchDurationMs === 'number' && report.searchDurationMs > 0) {
    durationMs = report.searchDurationMs;
  }
  if (durationMs == null) {
    const efficiencyWall = Number(report.timeEfficiencyAnalysis?.wall_elapsed_ms);
    if (Number.isFinite(efficiencyWall) && efficiencyWall > 0) {
      durationMs = efficiencyWall;
    }
  }
  if (durationMs == null) {
    const checkSum = checkTimings.reduce((sum, row) => {
      const ms = Number(row?.duration_ms);
      return sum + (Number.isFinite(ms) && ms > 0 ? ms : 0);
    }, 0);
    if (checkSum > 0) {
      durationMs = checkSum;
    }
  }

  if ((!startedAt || !Number.isFinite(startMs)) && durationMs != null && Number.isFinite(endMs)) {
    startedAt = new Date(endMs - durationMs).toISOString();
  }
  if ((!completedAt || !Number.isFinite(endMs)) && durationMs != null && Number.isFinite(startMs)) {
    completedAt = new Date(startMs + durationMs).toISOString();
  }

  return {
    ...(startedAt ? { startedAt } : {}),
    ...(completedAt ? { completedAt } : {}),
    ...(durationMs != null ? { durationMs } : {}),
  };
};

const resolveResearchSearchDurationMs = (report: ResearchReportRecord): number | undefined => (
  resolveResearchSearchTimingBounds(report).durationMs
);

const formatResearchSearchDuration = (durationMs: number | undefined, language: string): string => {
  if (!durationMs || durationMs <= 0) {
    return '';
  }

  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  if (totalSeconds < 60) {
    return language === 'es' ? `${Math.max(1, totalSeconds)} s` : `${Math.max(1, totalSeconds)}s`;
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return language === 'es'
      ? `${hours} h ${minutes} min ${seconds} s`
      : `${hours}h ${minutes}m ${seconds}s`;
  }

  return language === 'es'
    ? (seconds > 0 ? `${minutes} min ${seconds} s` : `${minutes} min`)
    : (seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`);
};

const formatCheckTimingDuration = (durationMs: number | null | undefined, language: string): string => {
  if (durationMs == null || !Number.isFinite(durationMs) || durationMs < 0) {
    return language === 'es' ? 'en curso' : 'in progress';
  }
  return formatResearchSearchDuration(durationMs, language) || `${Math.round(durationMs / 1000)}s`;
};

const resolveReportCheckTimings = (report: ResearchReportRecord | null | undefined): ResearchCheckTiming[] => {
  const fromMeta = report?.factDatabase?.metadata?.check_timings;
  return Array.isArray(fromMeta) ? fromMeta : [];
};

const stripResearchLogHtml = (value: unknown, max = 280): string => {
  let text = String(value ?? '');
  text = text
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<\/?[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

const summarizeResearchLogValue = (value: unknown, max = 160): string => {
  if (value == null) return '';
  if (typeof value === 'string') {
    return stripResearchLogHtml(value, max);
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    if (!value.length) return '[]';
    if (value.every((item) => typeof item === 'string' || typeof item === 'number')) {
      return summarizeResearchLogValue(value.slice(0, 8).join(', '), max);
    }
    return `[${value.length} items]`;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const preferred = ['title', 'name', 'url', 'href', 'snippet', 'text', 'status', 'id'];
    const bits: string[] = [];
    for (const key of preferred) {
      if (record[key] == null) continue;
      bits.push(`${key}=${summarizeResearchLogValue(record[key], Math.max(40, Math.floor(max / 3)))}`);
      if (bits.length >= 4) break;
    }
    if (bits.length) return summarizeResearchLogValue(bits.join('; '), max);
    const keys = Object.keys(record);
    return `{${keys.slice(0, 6).join(', ')}${keys.length > 6 ? ', …' : ''}}`;
  }
  return String(value);
};

const resolveResearchLogWebsite = (run: Record<string, unknown>, meta: Record<string, unknown> = {}): string => {
  const candidates = [
    run.direct_url, run.url, run.final_url, run.request_url, run.search_url, run.portal_url,
    meta.final_url, meta.request_url, meta.url, meta.endpoint,
  ];
  for (const candidate of candidates) {
    const value = String(candidate || '').trim();
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
  }
  const query = String(run.query || '').trim();
  if (query.startsWith('direct_url:')) {
    const direct = query.slice('direct_url:'.length).trim();
    if (direct.startsWith('http://') || direct.startsWith('https://')) return direct;
  }
  return '';
};

const resolveResearchLogSentPayload = (run: Record<string, unknown>, meta: Record<string, unknown> = {}): string => {
  const candidates = [
    run.query, run.request_payload, run.request_body, run.payload, run.body,
    meta.request_payload, meta.request_body, meta.query,
  ];
  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue;
    if (typeof candidate === 'string' && candidate.startsWith('direct_url:')) {
      return `GET ${candidate.slice('direct_url:'.length).trim()}`;
    }
    return summarizeResearchLogValue(candidate, 220);
  }
  if (run.endpoint) return `endpoint=${summarizeResearchLogValue(run.endpoint, 160)}`;
  return '';
};

const resolveResearchLogReceivedPayload = (
  run: Record<string, unknown>,
  meta: Record<string, unknown> = {},
  escalation: Record<string, unknown> = {},
): string => {
  const quality = escalation.quality && typeof escalation.quality === 'object'
    ? escalation.quality as Record<string, unknown>
    : {};
  const explicit = [
    run.response_payload, run.response_body, run.response_text, run.response, run.body_text,
    meta.response_payload, meta.response_body, meta.response_text,
  ];
  for (const candidate of explicit) {
    if (candidate == null || candidate === '') continue;
    const cleaned = summarizeResearchLogValue(candidate, 260);
    if (cleaned) return cleaned;
  }
  const bits: string[] = [];
  if (meta.status_code != null) bits.push(`http=${meta.status_code}`);
  if (meta.fetch_status) bits.push(`status=${meta.fetch_status}`);
  if (meta.error_type) bits.push(`error=${meta.error_type}`);
  if (meta.error) bits.push(`error=${summarizeResearchLogValue(meta.error, 100)}`);
  if (quality.body_bytes != null) bits.push(`bytes=${quality.body_bytes}`);
  if (quality.visible_chars != null) bits.push(`visible=${quality.visible_chars}`);
  if (run.hits != null) bits.push(`hits=${run.hits}`);
  if (Array.isArray(run.results)) bits.push(`results=${run.results.length}`);
  if (Array.isArray(run.results_preview)) bits.push(`preview=${run.results_preview.length}`);
  if (Array.isArray(run.signals)) bits.push(`signals=${run.signals.length}`);
  if (run.total_results != null) bits.push(`results=${run.total_results}`);
  if (meta.browser_fallback_status) bits.push(`browser=${meta.browser_fallback_status}`);
  if (meta.searxng_fallback_status) bits.push(`searxng=${meta.searxng_fallback_status}`);
  const sampleHits = Array.isArray(run.results)
    ? run.results
    : (Array.isArray(run.results_preview)
      ? run.results_preview
      : (Array.isArray(run.hits) && typeof (run.hits as unknown[])[0] === 'object' ? run.hits as unknown[] : []));
  if (sampleHits.length && typeof sampleHits[0] === 'object') {
    const titles = sampleHits
      .slice(0, 3)
      .map((hit) => {
        const row = hit as Record<string, unknown>;
        return summarizeResearchLogValue(row.title || row.name || row.url || row.href || row.snippet || '', 80);
      })
      .filter(Boolean);
    if (titles.length) bits.push(`sample=${titles.join(' | ')}`);
  }
  return bits.join(' · ');
};

const appendResearchLogExchangeLines = (
  lines: ResearchLogEntry[],
  run: Record<string, unknown>,
  options: { at?: string | null; check?: string | null; indent?: string } = {},
) => {
  const meta = run.fetch_meta && typeof run.fetch_meta === 'object'
    ? run.fetch_meta as Record<string, unknown>
    : {};
  const escalation = run.escalation && typeof run.escalation === 'object'
    ? run.escalation as Record<string, unknown>
    : {};
  const website = resolveResearchLogWebsite(run, meta);
  const sent = resolveResearchLogSentPayload(run, meta);
  const recv = resolveResearchLogReceivedPayload(run, meta, escalation);
  if (!website && !sent && !recv) return;
  const indent = options.indent || '    ';
  if (website) {
    lines.push({ at: options.at || null, level: 'debug', check: options.check || null, text: `${indent}site: ${website}` });
  }
  if (sent) {
    lines.push({ at: options.at || null, level: 'debug', check: options.check || null, text: `${indent}sent: ${sent}` });
  }
  if (recv) {
    lines.push({ at: options.at || null, level: 'debug', check: options.check || null, text: `${indent}recv: ${recv}` });
  }
};

const normalizeResearchLogCheckKey = (value: unknown): string => (
  String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '')
);

const appendResearchLogPolicyRuns = (
  lines: ResearchLogEntry[],
  policyRuns: unknown,
  options: { at?: string | null; check?: string | null; indent?: string } = {},
) => {
  const runs = Array.isArray(policyRuns)
    ? policyRuns.filter((row): row is Record<string, unknown> => Boolean(row && typeof row === 'object'))
    : [];
  if (!runs.length) return;
  const indent = options.indent || '  ';
  runs.slice(0, 12).forEach((run) => {
    const meta = run.fetch_meta && typeof run.fetch_meta === 'object'
      ? run.fetch_meta as Record<string, unknown>
      : {};
    const website = resolveResearchLogWebsite(run, meta) || String(run.direct_url || '').trim() || '?';
    lines.push({ at: options.at || null, level: 'debug', check: options.check || null, text: `${indent}check: ${website}` });
    appendResearchLogExchangeLines(lines, run, { at: options.at, check: options.check, indent: `${indent}  ` });
  });
  if (runs.length > 12) {
    lines.push({
      at: options.at || null,
      level: 'debug',
      check: options.check || null,
      text: `${indent}… ${runs.length - 12} more website/query checks`,
    });
  }
};

const summarizeResearchLogRelevanceFiltering = (relevance: Record<string, unknown> | null | undefined): string[] => {
  if (!relevance || typeof relevance !== 'object') return [];
  const lines: string[] = [];
  const engine = relevance.engine || relevance.filter_engine || relevance.model || null;
  if (engine) lines.push(`engine=${summarizeResearchLogValue(engine, 80)}`);
  Object.entries(relevance).forEach(([key, bucket]) => {
    if (['engine', 'filter_engine', 'model', 'version'].includes(key)) return;
    if (!bucket || typeof bucket !== 'object') return;
    const row = bucket as Record<string, unknown>;
    if (!Array.isArray(row.included) && !Array.isArray(row.review) && !Array.isArray(row.excluded)) return;
    lines.push(`${key}: included=${Array.isArray(row.included) ? row.included.length : 0} review=${Array.isArray(row.review) ? row.review.length : 0} excluded=${Array.isArray(row.excluded) ? row.excluded.length : 0}`);
  });
  return lines;
};

const collectResearchLogUsefulFactBits = (
  live: Record<string, unknown> = {},
  row: Record<string, unknown> = {},
): string[] => {
  const bits: string[] = [];
  const signals = live.signals && typeof live.signals === 'object'
    ? live.signals as Record<string, unknown>
    : {};
  const pushList = (label: string, values: unknown, maxItems = 3) => {
    const list = Array.isArray(values)
      ? values
      : (values == null || values === '' ? [] : [values]);
    const cleaned = list
      .map((item) => {
        if (item == null) return '';
        if (typeof item === 'string' || typeof item === 'number') return String(item).trim();
        if (typeof item === 'object') {
          const record = item as Record<string, unknown>;
          return summarizeResearchLogValue(record.value || record.text || record.address || record.url || record.name || item, 80);
        }
        return '';
      })
      .filter(Boolean);
    if (!cleaned.length) return;
    bits.push(`${label}=${summarizeResearchLogValue(cleaned.slice(0, maxItems).join(' | '), 160)}${cleaned.length > maxItems ? ` (+${cleaned.length - maxItems})` : ''}`);
  };

  if (signals.primary_address) pushList('primary_address', signals.primary_address, 1);
  pushList('addresses', signals.addresses);
  pushList('phones', signals.phones);
  pushList('emails', signals.emails);
  pushList('name_variants', signals.name_variants);
  pushList('relatives', signals.relatives_or_associates);
  pushList('employment', signals.employment);
  pushList('social', signals.social_presence);

  const parsedFields = live.parsed_fields && typeof live.parsed_fields === 'object'
    ? live.parsed_fields as Record<string, unknown>
    : (row.parsed_fields && typeof row.parsed_fields === 'object'
      ? row.parsed_fields as Record<string, unknown>
      : null);
  if (parsedFields) {
    const keys = Object.keys(parsedFields).filter((key) => parsedFields[key] != null && parsedFields[key] !== '');
    if (keys.length) bits.push(`parsed_fields=${keys.slice(0, 8).join(',')}${keys.length > 8 ? ',…' : ''}`);
    pushList('applicant_name', live.applicant_name || parsedFields.applicant_name || parsedFields.full_name, 1);
    pushList('birthdate', live.birthdate || parsedFields.birthdate || parsedFields.date_of_birth, 1);
    pushList('addresses', live.addresses || parsedFields.addresses);
    pushList('employers', live.employers || parsedFields.employers);
  }
  if (Array.isArray(row.fact_updates) && row.fact_updates.length) {
    bits.push(`fact_updates=${row.fact_updates.length}`);
  }
  if (Array.isArray(live.results_preview) && live.results_preview.length) {
    const titles = live.results_preview
      .slice(0, 3)
      .map((hit) => {
        const record = hit as Record<string, unknown>;
        return summarizeResearchLogValue(record.title || record.url || record.snippet || '', 70);
      })
      .filter(Boolean);
    if (titles.length) bits.push(`preview=${titles.join(' | ')}`);
  }
  return bits;
};

const appendResearchLogRequestSummary = (
  lines: ResearchLogEntry[],
  live: Record<string, unknown>,
  row: Record<string, unknown>,
  options: { at?: string | null; check?: string | null; indent?: string } = {},
) => {
  const indent = options.indent || '  ';
  const bits: string[] = [];
  if (live.subject) bits.push(`subject=${summarizeResearchLogValue(live.subject, 80)}`);
  if (live.location_hint) bits.push(`location=${summarizeResearchLogValue(live.location_hint, 40)}`);
  const inferred = live.inferred_jurisdiction && typeof live.inferred_jurisdiction === 'object'
    ? live.inferred_jurisdiction as Record<string, unknown>
    : null;
  if (inferred?.state_code) bits.push(`state=${inferred.state_code}`);
  const policyCount = Array.isArray(live.policy_runs) ? live.policy_runs.length : 0;
  if (policyCount) bits.push(`queries=${policyCount}`);
  if (live.total_results != null) bits.push(`candidates=${live.total_results}`);
  if (live.provider) bits.push(`provider=${summarizeResearchLogValue(live.provider, 90)}`);
  const ageFilter = live.age_filter && typeof live.age_filter === 'object'
    ? live.age_filter as Record<string, unknown>
    : null;
  if (ageFilter) {
    bits.push(`age_intake=${summarizeResearchLogValue(ageFilter.intake || '(none)', 20)}`);
    bits.push(`age_applied=${summarizeResearchLogValue(ageFilter.applied || '(omitted)', 20)}`);
    if (ageFilter.omitted_reason) bits.push(`age_omit=${summarizeResearchLogValue(ageFilter.omitted_reason, 40)}`);
    const tpsUrls = Array.isArray(ageFilter.tps_urls) ? ageFilter.tps_urls : [];
    if (tpsUrls[0]) bits.push(`tps=${summarizeResearchLogValue(tpsUrls[0], 120)}`);
  }
  if (bits.length) {
    lines.push({ at: options.at || null, level: 'debug', check: options.check || null, text: `${indent}sent: ${bits.join(' · ')}` });
  }

  const relevance = live.relevance_filtering && typeof live.relevance_filtering === 'object'
    ? live.relevance_filtering as Record<string, unknown>
    : null;
  const filterLines = summarizeResearchLogRelevanceFiltering(relevance);
  if (filterLines.length) {
    lines.push({ at: options.at || null, level: 'debug', check: options.check || null, text: `${indent}filters: ${filterLines.join(' · ')}` });
    Object.entries(relevance || {}).forEach(([bucketName, bucket]) => {
      if (!bucket || typeof bucket !== 'object' || !Array.isArray((bucket as Record<string, unknown>).included)) return;
      ((bucket as Record<string, unknown>).included as unknown[])
        .slice(0, 2)
        .forEach((item) => {
          if (!item || typeof item !== 'object') return;
          const record = item as Record<string, unknown>;
          const value = summarizeResearchLogValue(record.value || record.text || record.address || record.url || item, 90);
          const reasons = Array.isArray(record.reasons)
            ? record.reasons.slice(0, 2).map((reason) => summarizeResearchLogValue(reason, 60)).filter(Boolean).join('; ')
            : summarizeResearchLogValue(record.reason || '', 80);
          lines.push({
            at: options.at || null,
            level: 'debug',
            check: options.check || null,
            text: `${indent}  keep ${bucketName}: ${value}${record.relevance_score != null ? ` score=${record.relevance_score}` : ''}${reasons ? ` (${reasons})` : ''}`,
          });
        });
    });
  }

  if (live.duck_ai_synthesis) {
    lines.push({
      at: options.at || null,
      level: 'debug',
      check: options.check || null,
      text: `${indent}ai_synthesis: ${summarizeResearchLogValue(live.duck_ai_synthesis, 220)}`,
    });
  }

  const factBits = collectResearchLogUsefulFactBits(live, row);
  if (factBits.length) {
    lines.push({ at: options.at || null, level: 'info', check: options.check || null, text: `${indent}facts: ${factBits.join(' · ')}` });
  }
};

const appendResearchLogInvestigationLedger = (
  lines: ResearchLogEntry[],
  factDatabase: Record<string, unknown> | null | undefined,
  options: { check?: string | null; at?: string | null } = {},
) => {
  const meta = factDatabase?.metadata && typeof factDatabase.metadata === 'object'
    ? factDatabase.metadata as Record<string, unknown>
    : null;
  const ledger = Array.isArray(meta?.investigation_ledger)
    ? meta.investigation_ledger as Array<Record<string, unknown>>
    : [];
  if (!ledger.length) return;
  const filtered = options.check
    ? ledger.filter((row) => String(row.check || '') === String(options.check))
    : ledger;

  filtered.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const at = (entry.at as string) || options.at || null;
    const check = (entry.check as string) || options.check || null;
    lines.push({
      at,
      level: 'info',
      check,
      status: 'assembled',
      text: `detective: ${String(entry.narrative || entry.check || 'identity update')}`,
    });
    const addedCount = Number(entry.facts_added_count || 0);
    const added = Array.isArray(entry.facts_added) ? entry.facts_added.slice(0, 8) : [];
    lines.push({
      at,
      level: addedCount > 0 ? 'info' : 'debug',
      check,
      text: addedCount > 0
        ? `  fact_db_update: +${addedCount} · ${added.join(' · ')}`
        : '  fact_db_update: (no new fingerprint pieces)',
    });
    const snap = entry.snapshot && typeof entry.snapshot === 'object'
      ? entry.snapshot as Record<string, unknown>
      : {};
    const entity = snap.entity && typeof snap.entity === 'object'
      ? snap.entity as Record<string, unknown>
      : {};
    const entityBits = [
      entity.canonical_name ? `name=${entity.canonical_name}` : null,
      entity.birthdate ? `dob=${entity.birthdate}` : null,
      Array.isArray(entity.addresses) && entity.addresses.length ? `addresses=${entity.addresses.length}` : null,
      Array.isArray(entity.phones) && entity.phones.length ? `phones=${entity.phones.length}` : null,
      Array.isArray(entity.emails) && entity.emails.length ? `emails=${entity.emails.length}` : null,
      snap.signal_count != null ? `signals=${snap.signal_count}` : null,
    ].filter(Boolean);
    if (entityBits.length) {
      lines.push({
        at,
        level: 'info',
        check,
        text: `  fact_db_state: ${entityBits.join(' | ')}`,
      });
    }
    (Array.isArray(entry.digital_requests) ? entry.digital_requests : []).slice(0, 10).forEach((req) => {
      if (!req || typeof req !== 'object') return;
      const row = req as Record<string, unknown>;
      lines.push({
        at,
        level: 'debug',
        check,
        text: [
          '  request:',
          row.provider ? `provider=${row.provider}` : null,
          row.query ? `q=${summarizeResearchLogValue(row.query, 120)}` : null,
          row.url ? `url=${summarizeResearchLogValue(row.url, 120)}` : null,
          row.http != null ? `http=${row.http}` : null,
          row.hits != null ? `hits=${row.hits}` : null,
        ].filter(Boolean).join(' '),
      });
    });
    (Array.isArray(entry.decisions) ? entry.decisions : []).slice(0, 12).forEach((decision) => {
      if (!decision || typeof decision !== 'object') return;
      const row = decision as Record<string, unknown>;
      const level = row.decision === 'review_band' || row.type === 'identity_gap' ? 'warn' : 'info';
      lines.push({
        at,
        level,
        check,
        text: [
          '  decision:',
          row.type,
          row.decision,
          row.match_probability != null ? `p=${row.match_probability}` : null,
          Array.isArray(row.members) ? `members=${row.members.slice(0, 6).join(',')}` : null,
          row.id ? `gap=${row.id}` : null,
          row.rationale ? `why=${summarizeResearchLogValue(row.rationale, 140)}` : null,
        ].filter(Boolean).join(' · '),
      });
    });
  });
};

const appendResearchLogCriticalFacts = (
  lines: ResearchLogEntry[],
  criticalFacts: ResearchReportRecord['criticalFacts'] | Record<string, unknown> | null | undefined,
  options: { at?: string | null } = {},
) => {
  if (!criticalFacts || typeof criticalFacts !== 'object') return;
  const summary = criticalFacts.summary && typeof criticalFacts.summary === 'object'
    ? criticalFacts.summary as Record<string, unknown>
    : {};
  lines.push({
    at: options.at || null,
    level: 'info',
    check: 'critical_facts_loop',
    status: 'done',
    text: [
      'critical_facts',
      summary.verified != null ? `verified=${summary.verified}` : null,
      summary.candidate != null ? `candidate=${summary.candidate}` : null,
      summary.missing != null ? `missing=${summary.missing}` : null,
      summary.required_missing != null ? `required_missing=${summary.required_missing}` : null,
    ].filter(Boolean).join(' · '),
  });
  const items = Array.isArray(criticalFacts.items) ? criticalFacts.items : [];
  items.slice(0, 16).forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const status = String(item.status || 'unknown');
    const label = summarizeResearchLogValue(item.label || item.id || 'fact', 80);
    const value = item.value != null && item.value !== '' ? summarizeResearchLogValue(item.value, 120) : '(empty)';
    lines.push({
      at: options.at || null,
      level: status === 'missing' ? 'warn' : 'debug',
      check: 'critical_facts_loop',
      status,
      text: `  ${status}: ${label} = ${value}`,
    });
  });
  if (items.length > 16) {
    lines.push({
      at: options.at || null,
      level: 'debug',
      check: 'critical_facts_loop',
      text: `  … ${items.length - 16} more critical-fact items`,
    });
  }
};

const appendResearchLogSourceRun = (lines: ResearchLogEntry[], row: Record<string, unknown>, index: number) => {
  const sourceKey = String(row.source_key || row.source || row.check || `check_${index}`).trim();
  const status = row.status != null ? String(row.status) : null;
  const description = summarizeResearchLogValue(row.description || '', 160);
  const durationMs = typeof row.duration_ms === 'number' ? row.duration_ms : null;
  const summary = row.summary && typeof row.summary === 'object'
    ? row.summary as Record<string, unknown>
    : null;
  const summaryBits: string[] = [];
  if (summary) {
    if (summary.total_sources != null) summaryBits.push(`sources=${summary.total_sources}`);
    if (summary.live_or_partial_live != null) summaryBits.push(`live=${summary.live_or_partial_live}`);
    if (summary.nationwide_exhaustive != null) summaryBits.push(`nationwide=${summary.nationwide_exhaustive}`);
  }
  const level = ['error', 'failed', 'captcha_blocked'].includes(String(status || '').toLowerCase())
    ? 'error'
    : ['partial_live', 'timeout', 'no_match'].includes(String(status || '').toLowerCase())
      ? 'warn'
      : 'info';
  const at = String(row.completed_at || row.ended_at || row.updated_at || '') || null;
  lines.push({
    at,
    level,
    check: sourceKey,
    status,
    duration_ms: durationMs,
    text: [
      `[${status || 'unknown'}] ${sourceKey}`,
      durationMs != null ? `(${formatCheckTimingDuration(durationMs, 'en')})` : null,
      row.risk_score != null ? `risk=${row.risk_score}` : null,
      summaryBits.length ? summaryBits.join(' ') : null,
      description || null,
    ].filter(Boolean).join(' — '),
  });

  const live = row.live_result && typeof row.live_result === 'object'
    ? row.live_result as Record<string, unknown>
    : null;
  if (!live) {
    appendResearchLogRequestSummary(lines, row, row, { at, check: sourceKey, indent: '  ' });
    appendResearchLogExchangeLines(lines, row, { at, check: sourceKey, indent: '  ' });
    appendResearchLogPolicyRuns(lines, row.policy_runs, { at, check: sourceKey, indent: '  ' });
    return;
  }

  const personCtx = live.person_context && typeof live.person_context === 'object'
    ? live.person_context as Record<string, unknown>
    : null;
  const ctxParts = [
    live.provider ? `provider=${summarizeResearchLogValue(live.provider, 100)}` : null,
    personCtx?.state ? `state=${personCtx.state}` : null,
    personCtx?.primary_county ? `primary_county=${personCtx.primary_county}` : null,
    Array.isArray(personCtx?.counties) && personCtx.counties.length
      ? `counties=${personCtx.counties.slice(0, 8).join(',')}${personCtx.counties.length > 8 ? '…' : ''}`
      : null,
    personCtx && personCtx.thorough_county_cluster != null
      ? `thorough_cluster=${String(personCtx.thorough_county_cluster)}`
      : null,
  ].filter(Boolean);
  if (ctxParts.length) {
    lines.push({
      at,
      level: 'debug',
      check: sourceKey,
      status,
      text: `  context: ${ctxParts.join(' | ')}`,
    });
  }

  appendResearchLogRequestSummary(lines, live, row, { at, check: sourceKey, indent: '  ' });
  appendResearchLogExchangeLines(lines, live, { at, check: sourceKey, indent: '  ' });
  appendResearchLogPolicyRuns(lines, live.policy_runs, { at, check: sourceKey, indent: '  ' });

  Object.entries(live).forEach(([nestedKey, nestedVal]) => {
    if (!nestedVal || typeof nestedVal !== 'object' || Array.isArray(nestedVal)) return;
    if (['checks', 'portals', 'person_context', 'fetch_meta', 'escalation', 'summary', 'signals', 'relevance_filtering', 'structured_profiles', 'results_preview', 'inferred_jurisdiction', 'parsed_fields', 'parse_quality'].includes(nestedKey)) return;
    const nested = nestedVal as Record<string, unknown>;
    if (!(nested.query || nested.direct_url || nested.fetch_meta || nested.url || nested.request_payload)) return;
    lines.push({
      at,
      level: 'debug',
      check: `${sourceKey}/${nestedKey}`,
      text: `  · ${sourceKey}/${nestedKey}`,
    });
    appendResearchLogExchangeLines(lines, nested, { at, check: `${sourceKey}/${nestedKey}`, indent: '    ' });
    appendResearchLogPolicyRuns(lines, nested.policy_runs, { at, check: `${sourceKey}/${nestedKey}`, indent: '    ' });
  });

  const checks = live.checks && typeof live.checks === 'object'
    ? live.checks as Record<string, Record<string, unknown>>
    : null;
  if (checks) {
    Object.entries(checks).forEach(([subKey, subVal]) => {
      if (!subVal || typeof subVal !== 'object') return;
      const subStatus = subVal.status || subVal.fetch_status || null;
      const signals = Array.isArray(subVal.signals) ? subVal.signals.length : null;
      lines.push({
        at,
        level: String(subStatus || '').toLowerCase() === 'captcha_blocked' ? 'warn' : 'debug',
        check: `${sourceKey}/${subKey}`,
        status: subStatus != null ? String(subStatus) : null,
        text: [
          `  · ${sourceKey}/${subKey}`,
          subStatus ? `status=${subStatus}` : null,
          subVal.total_results != null ? `results=${subVal.total_results}` : null,
          signals != null ? `signals=${signals}` : null,
          subVal.reason ? `detail=${summarizeResearchLogValue(subVal.reason, 120)}` : null,
        ].filter(Boolean).join(' | '),
      });
      appendResearchLogExchangeLines(lines, subVal, { at, check: `${sourceKey}/${subKey}`, indent: '    ' });
      appendResearchLogPolicyRuns(lines, subVal.policy_runs, { at, check: `${sourceKey}/${subKey}`, indent: '    ' });
    });
  }

  if (Array.isArray(live.portals)) {
    live.portals.slice(0, 12).forEach((portal) => {
      if (!portal || typeof portal !== 'object') return;
      const portalObj = portal as Record<string, unknown>;
      lines.push({
        at,
        level: ['captcha_blocked', 'error'].includes(String(portalObj.status || '').toLowerCase()) ? 'warn' : 'debug',
        check: sourceKey,
        status: portalObj.status != null ? String(portalObj.status) : null,
        text: `  portal=${portalObj.name || portalObj.portal || portalObj.id || '?'} status=${portalObj.status || '?'} ${portalObj.reason || portalObj.detail || ''}`.trim(),
      });
      appendResearchLogExchangeLines(lines, portalObj, { at, check: sourceKey, indent: '    ' });
    });
  }
};

const buildResearchLogEntries = (input: {
  researchLog?: ResearchLogEntry[] | null;
  checkTimings?: ResearchCheckTiming[] | null;
  checkResults?: Array<Record<string, unknown>> | null;
  sourceChecksRuns?: Array<Record<string, unknown>> | null;
  criticalFacts?: ResearchReportRecord['criticalFacts'] | null;
  factDatabase?: Record<string, unknown> | null;
  status?: string | null;
  phase?: string | null;
  currentCheck?: string | null;
} = {}): ResearchLogEntry[] => {
  const runs = Array.isArray(input.sourceChecksRuns) && input.sourceChecksRuns.length
    ? input.sourceChecksRuns
    : (Array.isArray(input.checkResults) ? input.checkResults : []);
  const timings = Array.isArray(input.checkTimings) ? [...input.checkTimings] : [];
  const existingLog = Array.isArray(input.researchLog)
    ? input.researchLog
      .filter((row) => row && typeof row === 'object' && String(row.text || '').trim())
      .map((row) => ({
        at: row.at || null,
        level: row.level || 'info',
        check: row.check || null,
        phase: row.phase || null,
        status: row.status || null,
        duration_ms: row.duration_ms ?? null,
        text: String(row.text),
      }))
    : [];

  // Prefer structured rebuild when we have source-run payloads; otherwise keep the stored log.
  if (!runs.length && !timings.length && existingLog.length > 0) {
    return existingLog;
  }
  if (existingLog.some((row) => /detective:|fact_db_update:|fact_db_state:/.test(String(row.text || '')))) {
    return existingLog;
  }
  if (!runs.length && existingLog.length > 0) {
    const hasDetail = existingLog.some((row) => /(?:^|\s)(sent:|recv:|filters:|facts:|site:)/.test(String(row.text || '')));
    if (hasDetail || !timings.length) return existingLog;
  }

  const lines: ResearchLogEntry[] = [];
  if (input.status || input.phase || input.currentCheck) {
    lines.push({
      level: 'info',
      check: input.currentCheck || null,
      phase: input.phase || null,
      status: input.status || null,
      text: `job status=${input.status || '?'} phase=${input.phase || '?'} current=${input.currentCheck || '(none)'}`,
    });
  }

  const usedRunIndexes = new Set<number>();
  timings
    .sort((left, right) => {
      const leftStart = Date.parse(String(left.started_at || '')) || 0;
      const rightStart = Date.parse(String(right.started_at || '')) || 0;
      return leftStart - rightStart;
    })
    .forEach((timing) => {
      const check = timing.check || timing.phase || 'check';
      const open = !timing.ended_at;
      const startedLabel = timing.started_at
        ? new Date(String(timing.started_at)).toISOString().replace('T', ' ').replace(/\.\d+Z$/, 'Z')
        : null;
      const endedLabel = timing.ended_at
        ? new Date(String(timing.ended_at)).toISOString().replace('T', ' ').replace(/\.\d+Z$/, 'Z')
        : null;
      const span = [
        startedLabel ? `started=${startedLabel}` : null,
        endedLabel ? `ended=${endedLabel}` : null,
        `elapsed=${formatCheckTimingDuration(timing.duration_ms, 'en')}`,
      ].filter(Boolean).join(' · ');
      lines.push({
        at: timing.ended_at || timing.started_at || null,
        level: 'info',
        check,
        phase: timing.phase || null,
        status: open ? 'running' : 'done',
        duration_ms: timing.duration_ms ?? null,
        text: `${open ? '▶' : '✓'} ${check}${timing.phase && timing.phase !== check ? ` [${timing.phase}]` : ''} — ${span}`,
      });

      const timingKey = normalizeResearchLogCheckKey(check);
      runs.forEach((row, index) => {
        if (!row || typeof row !== 'object' || usedRunIndexes.has(index)) return;
        const sourceKey = normalizeResearchLogCheckKey(row.source_key || row.source || row.check || '');
        if (!sourceKey || !timingKey) return;
        if (!(sourceKey === timingKey || sourceKey.includes(timingKey) || timingKey.includes(sourceKey))) return;
        usedRunIndexes.add(index);
        appendResearchLogSourceRun(lines, row, index);
      });

      appendResearchLogInvestigationLedger(lines, input.factDatabase, {
        check,
        at: timing.ended_at || timing.started_at || null,
      });

      if (normalizeResearchLogCheckKey(check).includes('criticalfacts') && input.criticalFacts) {
        appendResearchLogCriticalFacts(lines, input.criticalFacts, {
          at: timing.ended_at || timing.started_at || null,
        });
      }
    });

  runs.forEach((row, index) => {
    if (!row || typeof row !== 'object' || usedRunIndexes.has(index)) return;
    appendResearchLogSourceRun(lines, row, index);
  });

  if (
    input.criticalFacts
    && !timings.some((timing) => normalizeResearchLogCheckKey(timing.check).includes('criticalfacts'))
  ) {
    appendResearchLogCriticalFacts(lines, input.criticalFacts);
  }

  return lines;
};

const researchLogLevelColor = (level?: string | null): string => {
  const normalized = String(level || '').toLowerCase();
  if (normalized === 'error') return 'var(--color-danger)';
  if (normalized === 'warn') return 'color-mix(in srgb, #f59e0b 75%, var(--color-text-strong))';
  if (normalized === 'debug') return 'var(--color-text-muted)';
  return 'var(--color-text-strong)';
};

const formatResearchLogPlainText = (entries: ResearchLogEntry[]): string => (
  entries.map((entry) => {
    const ts = entry.at
      ? new Date(String(entry.at)).toISOString().replace('T', ' ').replace(/\.\d+Z$/, 'Z')
      : '';
    return `${ts ? `${ts} ` : ''}${entry.text}`.trimEnd();
  }).join('\n')
);

const ResearchLogCopyIcon = ({ state = 'idle' }: { state?: 'idle' | 'copied' | 'failed' }) => {
  if (state === 'copied') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M5 12.5 9.5 17 19 7.5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (state === 'failed') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 8v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="12" cy="17" r="1" fill="currentColor" />
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M6.5 15H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
};

type ResearchLogViewMode = 'waterfall' | 'raw';
type ResearchLogFocusFilter = 'all' | 'errors' | 'requests' | 'decisions' | 'facts';

type ResearchLogWaterfallSpan = {
  key: string;
  label: string;
  check: string;
  durationMs: number;
  level: string;
  status: string | null;
  entries: ResearchLogEntry[];
  errorCount: number;
  requestCount: number;
  decisionCount: number;
  factCount: number;
};

const classifyResearchLogEntryKind = (entry: ResearchLogEntry): ResearchLogFocusFilter | 'other' => {
  const text = String(entry.text || '').toLowerCase();
  const level = String(entry.level || '').toLowerCase();
  if (level === 'error' || /\berror\b|failed|captcha_blocked/.test(text)) return 'errors';
  if (/request:|sent —|recv —|site —|exchange|payload/.test(text)) return 'requests';
  if (/decision:|filters —|ai\//.test(text)) return 'decisions';
  if (/fact_db_|detective:|facts —|fact_db_update|fact_db_state/.test(text)) return 'facts';
  return 'other';
};

const buildResearchLogWaterfallSpans = (entries: ResearchLogEntry[]): ResearchLogWaterfallSpan[] => {
  const spans: ResearchLogWaterfallSpan[] = [];
  let current: ResearchLogWaterfallSpan | null = null;

  const pushCurrent = () => {
    if (current) spans.push(current);
    current = null;
  };

  entries.forEach((entry, index) => {
    const check = String(entry.check || '').trim();
    const text = String(entry.text || '');
    const looksLikeCheckHeader = Boolean(
      entry.duration_ms != null
      || /^\s*[✓✗\[!]/.test(text)
      || /^\[[^\]]+\]\s+\S+/.test(text)
      || /^(starting check|✓|✗)/i.test(text.trim()),
    );
    const spanKey = check || (looksLikeCheckHeader ? `line-${index}-${text.slice(0, 40)}` : '');

    if (!current || (spanKey && current.key !== spanKey && (looksLikeCheckHeader || (check && check !== current.check)))) {
      pushCurrent();
      current = {
        key: spanKey || `ungrouped-${index}`,
        label: check || text.replace(/^\[[^\]]+\]\s*/, '').slice(0, 72) || `Step ${spans.length + 1}`,
        check: check || 'general',
        durationMs: Number(entry.duration_ms) > 0 ? Number(entry.duration_ms) : 0,
        level: String(entry.level || 'info'),
        status: entry.status != null ? String(entry.status) : null,
        entries: [entry],
        errorCount: 0,
        requestCount: 0,
        decisionCount: 0,
        factCount: 0,
      };
    } else if (current) {
      current.entries.push(entry);
      if (Number(entry.duration_ms) > current.durationMs) {
        current.durationMs = Number(entry.duration_ms);
      }
      if (String(entry.level || '').toLowerCase() === 'error') current.level = 'error';
      else if (String(entry.level || '').toLowerCase() === 'warn' && current.level !== 'error') current.level = 'warn';
      if (!current.status && entry.status != null) current.status = String(entry.status);
    }

    if (!current) return;
    const kind = classifyResearchLogEntryKind(entry);
    if (kind === 'errors') current.errorCount += 1;
    if (kind === 'requests') current.requestCount += 1;
    if (kind === 'decisions') current.decisionCount += 1;
    if (kind === 'facts') current.factCount += 1;
  });
  pushCurrent();
  return spans;
};

const ResearchLogPanel = ({
  entries,
  language,
  defaultOpen = false,
}: {
  entries: ResearchLogEntry[];
  language: string;
  defaultOpen?: boolean;
}) => {
  const [isOpen, setIsOpen] = React.useState(Boolean(defaultOpen));
  const [copyState, setCopyState] = React.useState<'idle' | 'copied' | 'failed'>('idle');
  const [viewMode, setViewMode] = React.useState<ResearchLogViewMode>('waterfall');
  const [focusFilter, setFocusFilter] = React.useState<ResearchLogFocusFilter>('all');
  const [expandedSpanKeys, setExpandedSpanKeys] = React.useState<Set<string>>(() => new Set());
  const copyResetTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    setIsOpen(Boolean(defaultOpen));
  }, [defaultOpen]);

  const waterfallSpans = React.useMemo(() => buildResearchLogWaterfallSpans(entries), [entries]);
  const maxDurationMs = React.useMemo(
    () => Math.max(1, ...waterfallSpans.map((span) => span.durationMs || 0)),
    [waterfallSpans],
  );

  const filteredSpans = React.useMemo(() => {
    if (focusFilter === 'all') return waterfallSpans;
    return waterfallSpans.filter((span) => {
      if (focusFilter === 'errors') return span.errorCount > 0 || span.level === 'error';
      if (focusFilter === 'requests') return span.requestCount > 0;
      if (focusFilter === 'decisions') return span.decisionCount > 0;
      if (focusFilter === 'facts') return span.factCount > 0;
      return true;
    });
  }, [focusFilter, waterfallSpans]);

  const filteredEntries = React.useMemo(() => {
    if (focusFilter === 'all') return entries;
    return entries.filter((entry) => {
      const kind = classifyResearchLogEntryKind(entry);
      if (focusFilter === 'errors') return kind === 'errors';
      if (focusFilter === 'requests') return kind === 'requests';
      if (focusFilter === 'decisions') return kind === 'decisions';
      if (focusFilter === 'facts') return kind === 'facts';
      return true;
    });
  }, [entries, focusFilter]);

  const copyLogToClipboard = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = formatResearchLogPlainText(entries);
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = payload;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!ok) {
          throw new Error('Clipboard API unavailable');
        }
      }
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
    if (copyResetTimerRef.current != null) {
      window.clearTimeout(copyResetTimerRef.current);
    }
    copyResetTimerRef.current = window.setTimeout(() => setCopyState('idle'), 1800);
  }, [entries]);

  React.useEffect(() => () => {
    if (copyResetTimerRef.current != null) {
      window.clearTimeout(copyResetTimerRef.current);
    }
  }, []);

  const toggleSpan = React.useCallback((key: string) => {
    setExpandedSpanKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  if (!entries.length) return null;
  const copyLabel = copyState === 'copied'
    ? (language === 'es' ? 'Copiado' : 'Copied')
    : copyState === 'failed'
      ? (language === 'es' ? 'Error al copiar' : 'Copy failed')
      : (language === 'es' ? 'Copiar registro al portapapeles' : 'Copy research log to clipboard');
  const liveAnnouncement = copyState === 'idle' ? '' : copyLabel;
  const filterOptions: Array<{ id: ResearchLogFocusFilter; label: string }> = [
    { id: 'all', label: language === 'es' ? 'Todo' : 'All' },
    { id: 'errors', label: language === 'es' ? 'Errores' : 'Errors' },
    { id: 'requests', label: language === 'es' ? 'Solicitudes' : 'Requests' },
    { id: 'decisions', label: language === 'es' ? 'Decisiones' : 'Decisions' },
    { id: 'facts', label: language === 'es' ? 'Hechos' : 'Facts' },
  ];

  return (
    <details
      className="research-log-panel"
      open={isOpen}
      onToggle={(event) => {
        setIsOpen((event.currentTarget as HTMLDetailsElement).open);
      }}
    >
      <summary className="research-log-panel-summary">
        <span>
          {language === 'es' ? `Registro de investigación (${entries.length})` : `Research Log (${entries.length})`}
        </span>
        <button
          type="button"
          className="dashboard-security-inline-btn"
          onClick={copyLogToClipboard}
          aria-label={copyLabel}
          title={copyLabel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '34px',
            padding: '4px 8px',
            flexShrink: 0,
            color: copyState === 'copied' ? '#027a48' : copyState === 'failed' ? '#b42318' : 'var(--color-text-strong)',
          }}
        >
          <ResearchLogCopyIcon state={copyState} />
        </button>
        <span className="research-log-sr-only" aria-live="polite">{liveAnnouncement}</span>
      </summary>
      <p className="research-log-panel-help">
        {language === 'es'
          ? 'Vista waterfall (estilo tracing): duración por verificación, con filtros para errores, solicitudes, decisiones y fact DB.'
          : 'Waterfall view (trace-style): duration per check, with filters for errors, requests, decisions, and fact DB.'}
      </p>
      <div className="research-log-toolbar" role="toolbar" aria-label={language === 'es' ? 'Controles del registro' : 'Research log controls'}>
        <div className="research-log-seg">
          <button
            type="button"
            className={viewMode === 'waterfall' ? 'is-active' : undefined}
            aria-pressed={viewMode === 'waterfall'}
            onClick={(event) => { event.preventDefault(); setViewMode('waterfall'); }}
          >
            {language === 'es' ? 'Waterfall' : 'Waterfall'}
          </button>
          <button
            type="button"
            className={viewMode === 'raw' ? 'is-active' : undefined}
            aria-pressed={viewMode === 'raw'}
            onClick={(event) => { event.preventDefault(); setViewMode('raw'); }}
          >
            {language === 'es' ? 'Texto' : 'Raw'}
          </button>
        </div>
        <div className="research-log-seg" aria-label={language === 'es' ? 'Enfocar' : 'Focus'}>
          {filterOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              className={focusFilter === option.id ? 'is-active' : undefined}
              aria-pressed={focusFilter === option.id}
              onClick={(event) => { event.preventDefault(); setFocusFilter(option.id); }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {viewMode === 'waterfall' ? (
        <div className="research-log-waterfall" role="list">
          {filteredSpans.length === 0 ? (
            <p className="research-log-empty">
              {language === 'es' ? 'Ningún tramo coincide con este filtro.' : 'No spans match this filter.'}
            </p>
          ) : filteredSpans.map((span) => {
            const widthPct = Math.max(4, Math.round((span.durationMs / maxDurationMs) * 100));
            const isExpanded = expandedSpanKeys.has(span.key);
            const detailEntries = focusFilter === 'all'
              ? span.entries
              : span.entries.filter((entry) => classifyResearchLogEntryKind(entry) === focusFilter || classifyResearchLogEntryKind(entry) === 'other');
            return (
              <div
                key={span.key}
                className={`research-log-span level-${span.level || 'info'}`}
                role="listitem"
              >
                <button
                  type="button"
                  className="research-log-span-trigger"
                  aria-expanded={isExpanded}
                  onClick={() => toggleSpan(span.key)}
                >
                  <span className="research-log-span-label">{span.label}</span>
                  <span className="research-log-span-meta">
                    {span.status ? `${span.status} · ` : ''}
                    {span.durationMs > 0
                      ? formatCheckTimingDuration(span.durationMs, language)
                      : (language === 'es' ? 'sin tiempo' : 'no timing')}
                  </span>
                  <span className="research-log-span-track" aria-hidden="true">
                    <span
                      className="research-log-span-bar"
                      style={{ width: `${widthPct}%` }}
                    />
                  </span>
                  <span className="research-log-span-chevron" aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
                </button>
                {isExpanded ? (
                  <pre className="research-log-span-detail">
                    {detailEntries.map((entry, index) => {
                      const ts = entry.at ? new Date(String(entry.at)).toISOString().replace('T', ' ').replace(/\.\d+Z$/, 'Z') : '';
                      return (
                        <div key={`${span.key}-detail-${index}`} style={{ color: researchLogLevelColor(entry.level) }}>
                          {ts ? `${ts} ` : ''}{entry.text}
                        </div>
                      );
                    })}
                  </pre>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <pre className="research-log-raw">
          {filteredEntries.map((entry, index) => {
            const ts = entry.at ? new Date(String(entry.at)).toISOString().replace('T', ' ').replace(/\.\d+Z$/, 'Z') : '';
            return (
              <div key={`research-log-${index}`} style={{ color: researchLogLevelColor(entry.level) }}>
                {ts ? `${ts} ` : ''}{entry.text}
              </div>
            );
          })}
        </pre>
      )}
    </details>
  );
};

const formatResearchSearchDurationLabel = (report: ResearchReportRecord, language: string): string => {
  const bounds = resolveResearchSearchTimingBounds(report);
  const parts: string[] = [];
  if (bounds.startedAt) {
    parts.push(
      language === 'es'
        ? `Inicio: ${new Date(bounds.startedAt).toLocaleString()}`
        : `Started: ${new Date(bounds.startedAt).toLocaleString()}`,
    );
  }
  if (bounds.completedAt) {
    parts.push(
      language === 'es'
        ? `Fin: ${new Date(bounds.completedAt).toLocaleString()}`
        : `Ended: ${new Date(bounds.completedAt).toLocaleString()}`,
    );
  }
  const duration = formatResearchSearchDuration(bounds.durationMs, language);
  if (duration) {
    parts.push(language === 'es' ? `Duración: ${duration}` : `Elapsed: ${duration}`);
  }
  return parts.join(' · ');
};

const ResearchElapsedTimer: React.FC<{
  startedAt?: string | null;
  endedAt?: string | null;
  language: string;
}> = ({ startedAt, endedAt, language }) => {
  const startMs = Date.parse(String(startedAt || ''));
  const endMs = Date.parse(String(endedAt || ''));
  const isLive = !Number.isFinite(endMs);
  const [nowMs, setNowMs] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (!isLive) {
      return undefined;
    }
    const timerId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timerId);
  }, [isLive]);

  if (!Number.isFinite(startMs)) {
    return null;
  }
  const elapsedMs = Math.max(0, (isLive ? nowMs : endMs) - startMs);
  const label = formatResearchSearchDuration(elapsedMs, language) || '0s';
  return (
    <span>
      {language === 'es' ? 'Transcurrido' : 'Elapsed'}: {label}
    </span>
  );
};

const getResearchAffinityLevelLabel = (language: string) => (
  language === 'es' ? 'Nivel de afinidad' : 'Affinity Level'
);

const getResearchAffinityLevelHelp = (language: string) => (
  language === 'es'
    ? 'Nivel 1 usa entidades del reporte raíz. Nivel 2+ ejecuta una búsqueda completa por cada persona/empresa descubierta en el nivel anterior y amplía el grafo con esos resultados.'
    : 'Level 1 maps entities from the root report. Level 2+ runs a full follow-up search for each person/company found at the prior level, then expands the graph with those results.'
);

const getResearchSourceIntelligenceHelp = (language: string) => (
  language === 'es'
    ? 'Todas las búsquedas parten del catálogo integral. Una fuente se apaga solo cuando la evidencia de entrada demuestra que no aplica (por ejemplo, registros electorales del Reino Unido sin nexo con el Reino Unido).'
    : 'Every search starts from the full catalog. A source is turned off only when intake evidence shows it cannot apply (for example UK electoral records with no UK nexus). FINRA, CFTC, CourtListener, and courts stay on unless they are the wrong subject type.'
);

/** Decode HTML entities then render light markdown as HTML for Biography. */
const decodeHtmlEntities = (value: string): string => {
  const text = String(value || '');
  if (!text || !text.includes('&')) {
    return text;
  }
  if (typeof document !== 'undefined') {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }
  return text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, code) => String.fromCharCode(parseInt(code, 16)));
};

const renderReportSectionHtml = (
  title: string,
  body: string,
  options?: { mediaBaseUrl?: string; accessToken?: string; identityNames?: string[] },
): string => {
  const isRich = /^(biography|photos|biografía|fotos|follow-up research questions|preguntas de seguimiento)$/i.test(String(title || '').trim());
  if (!isRich) {
    return '';
  }
  if (/^(photos|fotos)$/i.test(String(title || '').trim())) {
    return '';
  }
  const decoded = collapseCompetingSocialCitations(
    scrubResearchBiographyText(decodeHtmlEntities(body)),
    options?.identityNames || [],
  );
  const mediaSrc = (url: string) => {
    const raw = String(url || '').trim();
    if (!raw) return '';
    const base = String(options?.mediaBaseUrl || '').replace(/\/$/, '');
    const token = String(options?.accessToken || '').trim();
    let href = raw;
    if (raw.startsWith('/api/v1/research/media')) {
      href = `${base}${raw}`;
    }
    if (token && /\/api\/v1\/research\/media/.test(href)) {
      href += `${href.includes('?') ? '&' : '?'}access_token=${encodeURIComponent(token)}`;
    }
    return href;
  };
  const inline = (value: string) =>
    linkifyEscapedHtml(escapeHtml(value).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'));
  const inlineWithImages = (value: string) => {
    const placeholders: string[] = [];
    const withTokens = String(value || '').replace(
      /!\[([^\]]*)\]\(([^)\s]+)\)/g,
      (_all, alt, url) => {
        const src = mediaSrc(url);
        if (!src || (!looksLikeReportPhotoUrl(url) && !looksLikeReportPhotoUrl(src))) {
          return '';
        }
        placeholders.push(wrapOpenableImageHtml(src, alt));
        return `__NCIMG${placeholders.length - 1}__`;
      },
    );
    let html = inline(withTokens);
    html = html.replace(/__NCIMG(\d+)__/g, (_all, idx) => placeholders[Number(idx)] || '');
    return html;
  };
  const blocks: string[] = [];
  let listOpen: 'ul' | 'ol' | null = null;
  const closeList = () => {
    if (listOpen) {
      blocks.push(`</${listOpen}>`);
      listOpen = null;
    }
  };
  for (const raw of String(decoded || '').split('\n')) {
    const line = raw.trimEnd();
    const trimmed = line.trim();
    if (!trimmed) {
      closeList();
      continue;
    }
    if (/^###\s+/.test(trimmed)) {
      closeList();
      blocks.push(`<h4 class="dashboard-bio-subhead">${inline(trimmed.replace(/^###\s+/, ''))}</h4>`);
      continue;
    }
    if (/^##\s+/.test(trimmed)) {
      closeList();
      blocks.push(`<h3 class="dashboard-bio-subhead">${inline(trimmed.replace(/^##\s+/, ''))}</h3>`);
      continue;
    }
    const ol = trimmed.match(/^(\d+)\.\s+(.*)$/);
    if (ol) {
      if (listOpen !== 'ol') {
        closeList();
        blocks.push('<ol class="dashboard-bio-list">');
        listOpen = 'ol';
      }
      blocks.push(`<li>${inline(ol[2])}</li>`);
      continue;
    }
    if (/^[-*]\s+/.test(trimmed)) {
      const item = trimmed.replace(/^[-*]\s+/, '');
      const imgOnly = item.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      if (imgOnly) {
        const url = String(imgOnly[2] || '').trim();
        if (!looksLikeReportPhotoUrl(url) && !/\/api\/v1\/research\/media/.test(url)) {
          continue;
        }
        closeList();
        const rendered = inlineWithImages(item);
        if (!rendered.trim()) continue;
        blocks.push(`<p class="dashboard-bio-photo-wrap">${rendered}</p>`);
        continue;
      }
      const rendered = inlineWithImages(item);
      if (!rendered.trim()) continue;
      if (listOpen !== 'ul') {
        closeList();
        blocks.push('<ul class="dashboard-bio-list">');
        listOpen = 'ul';
      }
      blocks.push(`<li>${rendered}</li>`);
      continue;
    }
    closeList();
    if (/^!\[[^\]]*\]\((https?:[^)\s]+|\/api\/v1\/research\/media[^)\s]+)\)$/.test(trimmed)) {
      blocks.push(`<p class="dashboard-bio-photo-wrap">${inlineWithImages(trimmed)}</p>`);
      continue;
    }
    blocks.push(`<p class="dashboard-bio-para">${inlineWithImages(trimmed)}</p>`);
  }
  closeList();
  return blocks.join('\n');
};

const buildDefaultResearchRerunOptions = (affinityDepth?: number): ResearchRerunOption[] => {
  const baseline = Math.max(1, Math.min(4, Number(affinityDepth || 1)));
  const options: ResearchRerunOption[] = [];
  for (let depth = baseline + 1; depth <= 4; depth += 1) {
    options.push({
      label: `Affinity Depth ${depth}`,
      requestOverrides: { affinity_depth: depth },
    });
  }
  return options;
};

const extractFactIntakeFactsFromReport = (reportContent: string): string[] => {
  const lines = String(reportContent || '').replace(/\r\n?/g, '\n').split('\n');
  const firstCheckPattern = /^\s*1\.\s*(fact intake and parsing|input parse \/ website parse)\s*$/i;
  let inFirstCheck = false;
  const facts: string[] = [];

  for (const rawLine of lines) {
    const line = String(rawLine || '');
    const trimmed = line.trim();

    if (!inFirstCheck) {
      if (firstCheckPattern.test(trimmed)) {
        inFirstCheck = true;
      }
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      break;
    }

    const factMatch = trimmed.match(/^[-*]\s*Fact:\s*(.+)$/i);
    if (!factMatch) {
      continue;
    }

    const value = String(factMatch[1] || '').trim();
    if (!value || /no discrete facts returned/i.test(value)) {
      continue;
    }
    facts.push(value);
  }

  const deduped: string[] = [];
  const seen = new Set<string>();
  for (const fact of facts) {
    const normalized = fact.toLowerCase();
    if (!seen.has(normalized)) {
      seen.add(normalized);
      deduped.push(fact);
    }
  }
  return deduped;
};

const formatFactProvenance = (
  row: FactProvenance | FactDatabaseSignal | FactRecord | null | undefined,
  language: string,
): string => {
  if (!row || typeof row !== 'object') {
    return '';
  }
  const check = String(row.check || ('source' in row ? row.source : '') || '').trim();
  const runId = String(row.run_id || '').trim();
  const screeningId = String(row.screening_id || '').trim();
  const foundAt = String(row.found_at || ('timestamp' in row ? row.timestamp : '') || '').trim();
  const parts = [
    check ? `${language === 'es' ? 'verificación' : 'check'}: ${check}` : null,
    runId ? `${language === 'es' ? 'ejecución' : 'run'}: ${runId}` : null,
    screeningId && screeningId !== runId ? `screening: ${screeningId}` : null,
    foundAt ? `${language === 'es' ? 'encontrado' : 'found'}: ${new Date(foundAt).toLocaleString()}` : null,
  ].filter(Boolean);
  return parts.join(' · ');
};

const formatFactSignalValue = (value: FactDatabaseSignal['value']): string => {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (value && typeof value === 'object') {
    const candidate = String(
      (value as Record<string, unknown>).url
      || (value as Record<string, unknown>).title
      || (value as Record<string, unknown>).text
      || (value as Record<string, unknown>).name
      || '',
    ).trim();
    if (candidate) {
      return candidate;
    }
    return JSON.stringify(value).slice(0, 240);
  }
  return '';
};

const formatFactMatchProbability = (signal: FactDatabaseSignal): string => {
  const probability = typeof signal.match_probability === 'number'
    ? signal.match_probability
    : signal.confidence;
  if (typeof probability !== 'number') {
    return 'n/a';
  }
  return `${Math.round(probability * 100)}%`;
};

const isFactSignalExcluded = (signal: FactDatabaseSignal): boolean => (
  signal.decision === 'excluded' || signal.decision === 'dismissed'
);

const INTAKE_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const normalizeIntakeEmail = (value: string): string => (
  String(value || '').trim().replace(/^mailto:/i, '').trim()
);

const isIntakeEmail = (value: string): boolean => INTAKE_EMAIL_RE.test(normalizeIntakeEmail(value));

const splitIntakeEmails = (value?: string): string[] => (
  String(value || '')
    .split(/[;,]/)
    .map((item) => normalizeIntakeEmail(item))
    .filter((item) => Boolean(item) && isIntakeEmail(item))
);

const divertEmailsFromSocialHandles = (
  socialRaw: string,
  emailsRaw: string,
): { social: string[]; emails: string[] } => {
  const emails = splitIntakeEmails(emailsRaw);
  const social: string[] = [];
  const seen = new Set(emails.map((item) => item.toLowerCase()));
  for (const part of String(socialRaw || '').split(',').map((item) => item.trim()).filter(Boolean)) {
    if (isIntakeEmail(part)) {
      const email = normalizeIntakeEmail(part);
      const token = email.toLowerCase();
      if (!seen.has(token)) {
        seen.add(token);
        emails.push(email);
      }
      continue;
    }
    social.push(part);
  }
  return { social, emails };
};

const buildResearchKeyData = (
  report: Pick<ResearchReportRecord, 'targetType' | 'targetName' | 'website' | 'jurisdiction' | 'knownAliases'>,
  input?: Partial<KeyData>,
): KeyData => {
  const subjectName = String(report.targetName || '').trim();
  const reportKnownAliases = Array.isArray(report.knownAliases) ? report.knownAliases.map((item) => String(item || '').trim()).filter(Boolean) : [];
  const website = String(report.website || '').trim();
  let websiteHost = '';
  if (website) {
    try {
      websiteHost = new URL(website).hostname.replace(/^www\./i, '').trim();
    } catch {
      websiteHost = website;
    }
  }

  return {
    names: String(input?.names || subjectName).trim(),
    // Never fall back to jurisdiction here — that incorrectly seeds people-finder with a state.
    addresses: String(input?.addresses || '').trim(),
    phones: String(input?.phones || '').trim(),
    emails: String(input?.emails || '').trim(),
    birthdate: String(input?.birthdate || '').trim(),
    ageRange: normalizeAgeRangeForSubmit(String(input?.ageRange || '')),
    ssn: String(input?.ssn || '').trim(),
    dlNumber: String(input?.dlNumber || '').trim(),
    companies: String(input?.companies || (report.targetType === 'company' ? subjectName : '')).trim(),
    socialMediaHandles: String(input?.socialMediaHandles || websiteHost).trim(),
    website: String(input?.website || website).trim(),
    knownAliases: String(input?.knownAliases || reportKnownAliases.join(', ')).trim(),
  };
};

const buildResearchFactDatabase = (
  report: Pick<ResearchReportRecord, 'targetType' | 'targetName' | 'website' | 'jurisdiction' | 'knownAliases' | 'notes' | 'documentParseReceipt'>,
  input?: Partial<KeyData>,
  affinityDepth?: number,
): FactDatabase => {
  const now = new Date().toISOString();
  const splitCsv = (value?: string): string[] => String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

  const subjectName = String(report.targetName || '').trim();
  const aliases = Array.isArray(report.knownAliases) ? report.knownAliases.map((item) => String(item || '').trim()).filter(Boolean) : [];
  const names = splitCsv(input?.names);
  const inputKnownAliases = splitCsv(input?.knownAliases);
  const addresses = splitCsv(input?.addresses);
  const phones = splitCsv(input?.phones);
  const diverted = divertEmailsFromSocialHandles(String(input?.socialMediaHandles || ''), String(input?.emails || ''));
  const emails = diverted.emails;
  const companies = splitCsv(input?.companies);
  const socialHandles = diverted.social;
  const parseReceipt = report.documentParseReceipt && typeof report.documentParseReceipt === 'object'
    ? report.documentParseReceipt as Record<string, unknown>
    : {};
  const parsedFields = parseReceipt.parsed_fields && typeof parseReceipt.parsed_fields === 'object'
    ? parseReceipt.parsed_fields as Record<string, unknown>
    : {};
  const extractedFacts = Array.isArray(parseReceipt.extracted_facts)
    ? parseReceipt.extracted_facts.map((item) => String(item || '').trim()).filter(Boolean)
    : [];

  const noteFacts = String(report.notes || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = line.match(/^([^:]{2,80}):\s*(.+)$/);
      if (!match) {
        return null;
      }
      return {
        label: match[1].trim().toLowerCase(),
        value: match[2].trim(),
      };
    })
    .filter((item): item is { label: string; value: string } => Boolean(item && item.value));

  const findNoteFact = (...labels: string[]): string => {
    const wanted = new Set(labels.map((label) => label.toLowerCase()));
    const hit = noteFacts.find((fact) => wanted.has(fact.label));
    return hit?.value || '';
  };

  const docName = String(parsedFields.applicant_name || parsedFields.applicant_s_name || '').trim();
  const docAddress = String(parsedFields.property_address || parsedFields.current_address || '').trim();
  const docPhone = String(parsedFields.mobile_phone || parsedFields.phone || parsedFields.phone_number || '').trim();
  const docEmail = String(parsedFields.email || parsedFields.email_address || parsedFields.emails || '').trim();
  const docBirthdate = String(parsedFields.date_of_birth || '').trim();
  const docSsn = String(parsedFields.social_security_number || '').trim();
  const docDlNumber = String(parsedFields.driver_license_number || '').trim();
  const docCompany = String(parsedFields.current_employer || '').trim();
  const docPreviousEmployer = String(parsedFields.previous_employer || '').trim();
  const website = String(input?.website || report.website || '').trim();
  let websiteHost = '';
  if (website) {
    try {
      websiteHost = new URL(website).hostname.replace(/^www\./i, '').trim();
    } catch {
      websiteHost = website;
    }
  }

  const factDatabase: FactDatabase = {
    entity: {
      canonical_name: subjectName,
      alt_names: Array.from(new Set([...aliases, ...names, ...inputKnownAliases, ...(docName ? [docName] : [])])),
      addresses: Array.from(new Set([
        ...addresses,
        ...(docAddress ? [docAddress] : []),
        ...(findNoteFact('property address', 'addresses', 'current address') ? [findNoteFact('property address', 'addresses', 'current address')] : []),
      ])),
      phones: Array.from(new Set([
        ...phones,
        ...(docPhone ? [docPhone] : []),
        ...(findNoteFact('mobile phone', 'phone', 'phone number') ? [findNoteFact('mobile phone', 'phone', 'phone number')] : []),
      ])),
      emails: Array.from(new Set([
        ...emails,
        ...splitIntakeEmails(docEmail),
        ...splitIntakeEmails(findNoteFact('email', 'email address', 'emails')),
      ])),
      birthdate: String(input?.birthdate || docBirthdate || findNoteFact('birthdate', 'date of birth') || '').trim() || undefined,
      age_range: normalizeAgeRangeForSubmit(String(input?.ageRange || findNoteFact('approximate age range', 'age range') || '')) || undefined,
      ssn: preferCompleteIdentifier(
        docSsn || findNoteFact('ssn', 'social security number'),
        String(input?.ssn || '').trim(),
        'ssn',
      ) || undefined,
      dl_number: preferCompleteIdentifier(
        docDlNumber || findNoteFact('driver license', 'driver license number'),
        String(input?.dlNumber || '').trim(),
        'dl',
      ) || undefined,
      company_affiliations: Array.from(new Set([
        ...companies,
        ...(docCompany ? [docCompany] : []),
        ...(docPreviousEmployer ? [docPreviousEmployer] : []),
      ])),
      social_handles: {},
    },
    signals: [],
    relationships: [],
    metadata: {
      created_at: now,
      last_updated: now,
      target_type: report.targetType,
      affinity_depth: affinityDepth,
      source_checks: Array.from(new Set([
        'research-form',
        ...(Object.keys(parsedFields).length > 0 ? ['document-parser'] : []),
        ...(noteFacts.length > 0 ? ['report-fact-lines'] : []),
      ])),
    },
  };

  const addSignal = (factType: string, value: string | Record<string, any>, confidence = 0.9): void => {
    factDatabase.signals.push({
      source: 'research-form',
      fact_type: factType,
      value,
      confidence,
      timestamp: now,
    });
  };

  if (subjectName) {
    addSignal('canonical_name', subjectName, 0.98);
  }
  aliases.forEach((alias) => addSignal('alias', alias, 0.9));
  inputKnownAliases.forEach((alias) => addSignal('known_alias', alias, 0.9));
  names.forEach((name) => addSignal('name', name, 0.9));
  addresses.forEach((address) => addSignal('address', address, 0.88));
  phones.forEach((phone) => addSignal('phone', phone, 0.95));
  if (docPhone && !phones.some((phone) => phone.replace(/\D/g, '') === docPhone.replace(/\D/g, ''))) {
    addSignal('phone', docPhone, 0.93);
  }
  emails.forEach((email) => addSignal('email', email, 0.95));
  if (String(report.jurisdiction || '').trim()) {
    addSignal('jurisdiction', String(report.jurisdiction || '').trim(), 0.82);
  }
  if (website) {
    addSignal('website', website, 0.9);
  }
  if (String(input?.birthdate || '').trim()) {
    addSignal('birthdate', String(input?.birthdate || '').trim(), 0.95);
  }
  if (!String(input?.birthdate || '').trim() && docBirthdate) {
    addSignal('birthdate', docBirthdate, 0.93);
  }
  const intakeAgeRange = normalizeAgeRangeForSubmit(String(input?.ageRange || ''));
  if (intakeAgeRange) {
    addSignal('age_range', intakeAgeRange, 0.82);
  }
  const resolvedSsn = preferCompleteIdentifier(docSsn, String(input?.ssn || '').trim(), 'ssn');
  if (resolvedSsn) {
    addSignal('ssn', resolvedSsn, 0.98);
  }
  const resolvedDl = preferCompleteIdentifier(docDlNumber, String(input?.dlNumber || '').trim(), 'dl');
  if (resolvedDl) {
    addSignal('driver_license', resolvedDl, 0.95);
  }
  companies.forEach((company) => addSignal('company_affiliation', company, 0.86));
  if (docCompany) {
    addSignal('company_affiliation', docCompany, 0.84);
  }
  if (docAddress) {
    addSignal('address', docAddress, 0.9);
  }
  extractedFacts.slice(0, 24).forEach((fact) => addSignal('document_fact', fact, 0.88));
  noteFacts.slice(0, 24).forEach((fact) => addSignal('note_fact', { label: fact.label, value: fact.value }, 0.82));
  socialHandles.forEach((handle, index) => {
    const platformKey = `handle_${index + 1}`;
    factDatabase.entity.social_handles[platformKey] = handle;
    addSignal('social_handle', handle, 0.8);
  });
  if (websiteHost) {
    factDatabase.entity.social_handles.website = websiteHost;
    addSignal('website_host', websiteHost, 0.84);
  }

  const aggregatedLabeledFacts: string[] = [];
  const seenAggregatedFacts = new Set<string>();
  const pushAggregatedFact = (label: string, value?: string): void => {
    const renderedValue = String(value || '').trim();
    if (!label || !renderedValue) {
      return;
    }
    const rendered = `${label}: ${renderedValue}`;
    const token = rendered.toLowerCase();
    if (seenAggregatedFacts.has(token)) {
      return;
    }
    seenAggregatedFacts.add(token);
    aggregatedLabeledFacts.push(rendered);
  };

  const canonicalName = String(factDatabase.entity.canonical_name || '').trim();
  const altNames = Array.isArray(factDatabase.entity.alt_names)
    ? factDatabase.entity.alt_names.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
  const renderedNames = Array.from(new Set([canonicalName, ...altNames].filter(Boolean))).join(', ');
  pushAggregatedFact('Names', renderedNames);

  const renderedAliases = Array.from(new Set([...aliases, ...inputKnownAliases].map((item) => String(item || '').trim()).filter(Boolean))).join(', ');
  pushAggregatedFact('Known Aliases', renderedAliases);

  pushAggregatedFact('Addresses', (factDatabase.entity.addresses || []).join(', '));
  pushAggregatedFact('Phones', (factDatabase.entity.phones || []).join(', '));
  pushAggregatedFact('Emails', (factDatabase.entity.emails || []).join(', '));
  pushAggregatedFact('Birthdate', String(factDatabase.entity.birthdate || '').trim());
  pushAggregatedFact('Approximate age range', String(factDatabase.entity.age_range || intakeAgeRange || '').trim());
  pushAggregatedFact('Social Security Number', String(factDatabase.entity.ssn || '').trim());
  pushAggregatedFact('Driver License Number', String(factDatabase.entity.dl_number || '').trim());
  pushAggregatedFact('Companies / Affiliations', (factDatabase.entity.company_affiliations || []).join(', '));
  pushAggregatedFact('Website', website || websiteHost);
  pushAggregatedFact('Social handles', Object.values(factDatabase.entity.social_handles || {}).map((item) => String(item || '').trim()).filter(Boolean).join(', '));

  extractedFacts.slice(0, 24).forEach((fact) => {
    const match = String(fact || '').match(/^([^:]{2,80}):\s*(.+)$/);
    if (match) {
      pushAggregatedFact(match[1].trim(), match[2].trim());
    } else {
      pushAggregatedFact('Document Fact', String(fact || '').trim());
    }
  });
  noteFacts.slice(0, 24).forEach((fact) => pushAggregatedFact(fact.label, fact.value));

  factDatabase.sourceCheckFacts = aggregatedLabeledFacts;
  factDatabase.sourceCheckFactCount = aggregatedLabeledFacts.length;

  return factDatabase;
};

const DEFAULT_TIP_OF_DAY_CATALOG: TipOfDayDefinition[] = [
  {
    id: 'tip-security-passkey',
    audience: 'non_super_admin',
    title: {
      en: 'Tip of the Day: Add a passkey',
      es: 'Consejo del día: agrega una passkey',
    },
    body: {
      en: 'Passkeys reduce password-only risk and speed up sign-in on trusted devices.',
      es: 'Las passkeys reducen el riesgo de acceso solo con contraseña y aceleran el inicio en dispositivos confiables.',
    },
    action: {
      navItem: 'security',
      label: {
        en: 'Open Security',
        es: 'Abrir Seguridad',
      },
    },
  },
  {
    id: 'tip-dashboard-subscriptions',
    audience: 'non_super_admin',
    title: {
      en: 'Tip of the Day: Tune your dashboards',
      es: 'Consejo del día: ajusta tus paneles',
    },
    body: {
      en: 'Keep only dashboards you use daily to reduce noise and improve focus.',
      es: 'Mantén solo los paneles que usas a diario para reducir ruido y mejorar el enfoque.',
    },
    action: {
      navItem: 'dashboards',
      label: {
        en: 'Open Dashboards',
        es: 'Abrir Paneles',
      },
    },
  },
  {
    id: 'tip-alert-email',
    audience: 'non_super_admin',
    title: {
      en: 'Tip of the Day: Verify alert email',
      es: 'Consejo del día: verifica el correo de alertas',
    },
    body: {
      en: 'A current alert email helps your team respond faster to compliance changes.',
      es: 'Un correo de alertas actualizado ayuda a tu equipo a responder más rápido a cambios de cumplimiento.',
    },
    action: {
      navItem: 'payments',
      label: {
        en: 'Open Payments & Subscriptions',
        es: 'Abrir Pagos y Suscripciones',
      },
    },
  },
];

const getDateKey = (value: Date = new Date()): string => value.toISOString().slice(0, 10);

const addDaysToDateKey = (dateKey: string, days: number): string => {
  const seed = new Date(`${dateKey}T00:00:00Z`);
  seed.setUTCDate(seed.getUTCDate() + days);
  return getDateKey(seed);
};

const compareDateKeys = (left: string, right: string): number => {
  if (left === right) {
    return 0;
  }
  return left < right ? -1 : 1;
};

const getTipDaySeed = (dateKey: string): number => {
  const [year, month, day] = dateKey.split('-').map((part) => Number(part));
  return (year * 372) + (month * 31) + day;
};

const getStoredTipOfDayState = (): TipOfDayState => {
  if (typeof window === 'undefined') {
    return {};
  }

  const raw = window.localStorage.getItem(TIP_OF_DAY_STATE_STORAGE_KEY);
  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw) as TipOfDayState;
    return {
      lastShownDate: parsed.lastShownDate,
      lastTipId: parsed.lastTipId,
      dismissedUntilDate: parsed.dismissedUntilDate,
    };
  } catch {
    return {};
  }
};

const saveTipOfDayState = (state: TipOfDayState): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(TIP_OF_DAY_STATE_STORAGE_KEY, JSON.stringify(state));
};

const getStoredTipCatalog = (): TipOfDayDefinition[] => {
  if (typeof window === 'undefined') {
    return DEFAULT_TIP_OF_DAY_CATALOG;
  }

  const raw = window.localStorage.getItem(TIP_OF_DAY_CATALOG_STORAGE_KEY);
  if (!raw) {
    return DEFAULT_TIP_OF_DAY_CATALOG;
  }

  try {
    const parsed = JSON.parse(raw) as TipOfDayDefinition[];
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return DEFAULT_TIP_OF_DAY_CATALOG;
    }
    return parsed.filter((tip) => tip && tip.id && tip.title?.en && tip.body?.en && tip.action?.navItem);
  } catch {
    return DEFAULT_TIP_OF_DAY_CATALOG;
  }
};

const saveTipCatalog = (catalog: TipOfDayDefinition[]): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(TIP_OF_DAY_CATALOG_STORAGE_KEY, JSON.stringify(catalog));
};

const getStoredResearchReports = (userId?: string | null): ResearchReportRecord[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  const scopedKey = researchReportsStorageKeyForUser(userId);
  const raw = window.localStorage.getItem(scopedKey)
    || (userId ? window.localStorage.getItem(RESEARCH_REPORTS_STORAGE_KEY) : null);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as ResearchReportRecord[];
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((report) => Boolean(report?.id && report?.targetName && report?.content));
  } catch {
    return [];
  }
};

const RESEARCH_REPORTS_STORAGE_SOFT_LIMIT_CHARS = 2_000_000;

const researchReportContentHasBiography = (content: string): boolean => (
  /^##\s+Biography\b/im.test(String(content || ''))
);

const slimResearchReportForStorage = (report: ResearchReportRecord, contentLimit = 48_000): ResearchReportRecord => ({
  ...report,
  content: reportContentKeepingBiography(String(report.content || ''), contentLimit),
  reportEvidence: buildReportEvidence(report),
  researchLog: Array.isArray(report.researchLog) ? report.researchLog.slice(-40) : report.researchLog,
  sourceChecksRuns: undefined,
  checkResults: undefined,
  affinityGraph: undefined,
  factDatabase: report.factDatabase
    ? {
        ...report.factDatabase,
        signals: Array.isArray(report.factDatabase.signals) ? report.factDatabase.signals.slice(0, 40) : report.factDatabase.signals,
        factRecords: Array.isArray(report.factDatabase.factRecords) ? report.factDatabase.factRecords.slice(0, 40) : report.factDatabase.factRecords,
        entityProvenance: undefined,
        sourceCheckFacts: Array.isArray(report.factDatabase.sourceCheckFacts)
          ? report.factDatabase.sourceCheckFacts.slice(0, 80)
          : report.factDatabase.sourceCheckFacts,
        sourceCheckFactsByCheck: undefined,
      }
    : report.factDatabase,
});

const saveResearchReports = (reports: ResearchReportRecord[], userId?: string | null): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const key = researchReportsStorageKeyForUser(userId);
  const attempts: ResearchReportRecord[][] = [
    reports.slice(0, 50).map((report) => slimResearchReportForStorage(report)),
    reports.slice(0, 12).map((report) => slimResearchReportForStorage(report, 16_000)),
    reports.slice(0, 4).map((report) => slimResearchReportForStorage({
      ...report,
      factDatabase: undefined,
      researchLog: undefined,
      timeEfficiencyAnalysis: undefined,
    }, 8_000)),
  ];
  for (const candidate of attempts) {
    try {
      const serialized = JSON.stringify(candidate);
      if (serialized.length > RESEARCH_REPORTS_STORAGE_SOFT_LIMIT_CHARS && candidate !== attempts[attempts.length - 1]) {
        continue;
      }
      window.localStorage.setItem(key, serialized);
      return;
    } catch (error) {
      const quotaExceeded = error instanceof DOMException && (
        error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
      );
      if (!quotaExceeded) {
        return;
      }
    }
  }
};

const getStoredDismissedResearchReportIds = (): string[] => {
  if (typeof window === 'undefined') {
    return [];
  }
  const raw = window.localStorage.getItem(RESEARCH_REPORTS_DISMISSED_STORAGE_KEY);
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.map((item) => String(item || '').trim()).filter(Boolean);
  } catch {
    return [];
  }
};

const saveDismissedResearchReportIds = (ids: string[]): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const unique = Array.from(new Set(ids.map((item) => String(item || '').trim()).filter(Boolean))).slice(-400);
  window.localStorage.setItem(RESEARCH_REPORTS_DISMISSED_STORAGE_KEY, JSON.stringify(unique));
};

const researchReportIdAliases = ({
  reportId,
  gatewayJobId,
  vantageJobId,
}: {
  reportId?: string;
  gatewayJobId?: string;
  vantageJobId?: string;
}): string[] => {
  const aliases = new Set<string>();
  const add = (value?: string) => {
    const text = String(value || '').trim();
    if (text) aliases.add(text);
  };
  add(reportId);
  let gatewayId = String(gatewayJobId || '').trim();
  const reportText = String(reportId || '').trim();
  if (!gatewayId && reportText.startsWith('report-') && !reportText.startsWith('report-v-')) {
    gatewayId = reportText.slice('report-'.length);
  }
  let vantageId = String(vantageJobId || '').trim();
  if (!vantageId && reportText.startsWith('report-v-')) {
    const part = reportText.slice('report-v-'.length);
    vantageId = part.startsWith('job_') ? part : `job_${part}`;
  }
  if (!vantageId && gatewayId.startsWith('rjob-v-')) {
    vantageId = `job_${gatewayId.slice(7)}`;
  }
  add(gatewayId);
  add(vantageId);
  if (gatewayId) add(`report-${gatewayId}`);
  if (vantageId) {
    add(`report-v-${vantageId}`);
    const suffix = vantageId.startsWith('job_') ? vantageId.slice(4) : vantageId;
    add(`rjob-v-${suffix}`);
    add(`report-rjob-v-${suffix}`);
    add(vantageId.startsWith('job_') ? vantageId : `job_${vantageId}`);
  }
  return Array.from(aliases);
};

const isDismissedResearchIdentity = (
  dismissed: Set<string>,
  identity: { reportId?: string; gatewayJobId?: string; vantageJobId?: string },
): boolean => researchReportIdAliases(identity).some((id) => dismissed.has(id));

const researchReportIsDismissed = (
  report: { id?: string; gatewayJobId?: string; vantageJobId?: string } | null | undefined,
  dismissed: Set<string>,
): boolean => {
  if (!report) return false;
  return isDismissedResearchIdentity(dismissed, {
    reportId: report.id,
    gatewayJobId: report.gatewayJobId,
    vantageJobId: report.vantageJobId,
  });
};

const getStoredResearchQueue = (): ResearchQueueItem[] => {
  if (typeof window === 'undefined') {
    return [];
  }
  const raw = window.localStorage.getItem(RESEARCH_QUEUE_STORAGE_KEY);
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as ResearchQueueItem[];
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((item) => Boolean(item?.id && item?.targetName));
  } catch {
    return [];
  }
};

const saveResearchQueue = (queue: ResearchQueueItem[]): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(RESEARCH_QUEUE_STORAGE_KEY, JSON.stringify(queue.slice(0, 100)));
  } catch {
    // Ignore quota failures; queue can resync from gateway.
  }
};

const LANGUAGE_OPTIONS = ['English', 'Español'];

const TIMEZONE_OPTIONS = [
  'UTC (Etc/UTC)',
  'UTC-5 (America/New_York)',
  'UTC-6 (America/Chicago)',
  'UTC-7 (America/Denver)',
  'UTC-8 (America/Los_Angeles)',
];

const DEFAULT_SUPPORT_PHONE = (process.env.REACT_APP_SUPPORT_PHONE || '').trim();
const DEFAULT_ACCOUNT_CONTACT_PHONE = (process.env.REACT_APP_ACCOUNT_CONTACT_PHONE || DEFAULT_SUPPORT_PHONE).trim();
const DEFAULT_SECURITY_RECOVERY_PHONE = (process.env.REACT_APP_SECURITY_RECOVERY_PHONE || DEFAULT_ACCOUNT_CONTACT_PHONE).trim();
const SERVICE_DASHBOARD_FALLBACK_KEYS = ['gateway', 'vantage', 'pursuit', 'email-relay', 'postfix'];

function finiteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

const OFAC_SERVICE_KEYS = new Set([
  'ofac',
  'ofac_general_licenses',
  'sdn_lists',
  'capta_list',
  'screening_gateway',
  'vantage',
]);

const WEBSITE_COMPLIANCE_SERVICE_KEYS = new Set([
  'website_compliance',
  'website_parser',
  'legal_contract_enforceability',
  'legal_privacy_data_law',
  'legal_security_obligations',
  'legal_marketing_claims',
  'legal_payments_billing_terms',
  'legal_ip_licensing',
  'legal_accessibility_wcag',
  'legal_regulated_use_alignment',
]);

const GOVERNMENTAL_COMPLIANCE_SERVICE_KEYS = new Set([
  'governmental_compliance',
  'legal_local_compliance',
  'legal_state_compliance',
  'legal_federal_compliance',
  'sec_edgar',
  'doe_state',
  'american_petroleum_institute',
  'national_association_manufacturers',
  'us_chamber_of_commerce',
]);

const formatServiceDashboardName = (value: string): string => {
  const normalized = String(value || '').trim();
  if (!normalized) return 'Unknown Service';

  const compact = normalized
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const acronyms = new Set(['ai', 'api', 'aws', 'db', 'dba', 'etl', 'id', 'kpi', 'ofac', 'pdf', 'sso', 'ui', 'ux']);
  return compact
    .split(' ')
    .map((token) => {
      const lower = token.toLowerCase();
      if (acronyms.has(lower)) return lower.toUpperCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
};

const parseServiceDashboardKey = (dashboardName: string): string => {
  const normalized = String(dashboardName || '').trim();
  if (!normalized) return '';
  return normalized
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
};

const isLegalComplianceServiceKey = (serviceKey: string): boolean => {
  const normalized = parseServiceDashboardKey(serviceKey);
  return normalized.startsWith('legal_');
};

const deriveLegalComplianceCounts = (
  serviceRecord: MetricsDashboardService | undefined,
  legalAreaInput: LegalExposureAreaInput | undefined,
): { total: number; compliant: number; nonCompliant: number; compliancePercentage?: number } => {
  const evidenceItems = Array.isArray(legalAreaInput?.evidence)
    ? legalAreaInput.evidence
    : (serviceRecord?.evidence_items ?? []);
  const lackingItems = Array.isArray(legalAreaInput?.lacking)
    ? legalAreaInput.lacking
    : (serviceRecord?.lacking_items ?? []);
  const requirementItems = Array.isArray(legalAreaInput?.requirements)
    ? legalAreaInput.requirements
    : [];

  const totalFromLists = requirementItems.length > 0
    ? requirementItems.length
    : evidenceItems.length + lackingItems.length;
  const totalFromRequests = serviceRecord?.requests_total ?? 0;
  const total = Math.max(totalFromLists, totalFromRequests, 0);

  const compliantFromEvidence = evidenceItems.length;
  const compliantFromRequests = serviceRecord?.requests_success ?? 0;
  const compliantRaw = Math.max(compliantFromEvidence, compliantFromRequests, 0);
  const compliant = total > 0 ? Math.min(total, compliantRaw) : compliantRaw;
  const nonCompliant = total > 0
    ? Math.max(total - compliant, lackingItems.length)
    : Math.max(lackingItems.length, 0);
  const compliancePercentage = total > 0
    ? (compliant / total) * 100
    : undefined;

  return {
    total,
    compliant,
    nonCompliant,
    compliancePercentage,
  };
};

const inferGatewayBaseUrl = (): string => {
  const configured = (process.env.REACT_APP_GATEWAY_URL || '').trim();
  if (configured) {
    // Prefer IPv4 loopback locally — Cloudflare WARP / IPv6 localhost can stall fetches.
    if (/^https?:\/\/localhost(?::|\/|$)/i.test(configured)) {
      return configured.replace(/localhost/i, '127.0.0.1').replace(/\/$/, '');
    }
    return configured.replace(/\/$/, '');
  }

  if (typeof window === 'undefined') {
    return 'http://127.0.0.1:8000';
  }

  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1';
  if (isLocal) {
    return 'http://127.0.0.1:8000';
  }

  const apexHost = host.replace(/^www\./, '').replace(/^dashboard\./, '').replace(/^metrics\./, '');
  return `https://${apexHost}`;
};
const EMPTY_KEY_DATA: KeyData = {
  names: '',
  addresses: '',
  phones: '',
  emails: '',
  birthdate: '',
  ageRange: '',
  ageBasis: 'birthdate',
  ssn: '',
  dlNumber: '',
  companies: '',
  socialMediaHandles: '',
  website: '',
  knownAliases: '',
  familyMembers: [],
  ...EMPTY_RESEARCH_PERSON_FIELDS,
};

const createEmptyResearchForm = () => ({
  reportName: '',
  /** True once the operator edits Report name; until then it tracks the derived subject name. */
  reportNameLocked: false,
  targetType: 'company' as 'company' | 'person',
  /** True once the operator picks Person/Company; until then type is inferred. */
  targetTypeTouched: false,
  jurisdiction: '',
  notes: '',
  keyData: { ...EMPTY_KEY_DATA, familyMembers: [] },
  affinityDepth: 1 as 1 | 2 | 3 | 4,
  sourceIntelligenceProfile: 'smart' as 'smart' | 'regulatory' | 'comprehensive' | 'default',
  emailAddresses: '',
  emailGroups: '',
  /** Opt-in: add/merge this intake subject into the OFAC screening portfolio. */
  addToOfacScreening: false,
  ofacRelationshipType: 'customer' as OfacScreeningRelationshipType,
});

type ResearchIntakeForm = ReturnType<typeof createEmptyResearchForm>;

type ResearchIntakeSnapshot = {
  form: ResearchIntakeForm;
  uploadFile: File | null;
  editingReportId: string | null;
};

const cloneResearchIntakeForm = (form: ResearchIntakeForm): ResearchIntakeForm => ({
  ...form,
  keyData: { ...form.keyData },
});

const EMPTY_RESEARCH_FORM = createEmptyResearchForm();

const FACT_LIST_FIELDS: FactListField[] = ['names', 'knownAliases', 'addresses', 'companies', 'socialMediaHandles'];

const splitFactListItems = (value: string): string[] => {
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const FACT_DATABASE_NOISE_PATTERNS: RegExp[] = [
  /^timed out$/i,
  /^timeout$/i,
  /^gateway[- ]?timeout$/i,
  /timed\s*out/i,
  /fetch_status\s*=/i,
  /no match rows returned/i,
  /^likely_clients\s*=\s*0$/i,
  /likely_clients\s*=\s*0/i,
  /website_social_links\s*=\s*0/i,
  /^counties_queried=Harris,\s*Dallas,\s*Tarrant/i,
  /counties_queried=none_inferred/i,
  /^results=0\b/i,
  /^live search did not complete$/i,
  /^checked;\s*no match rows returned/i,
];

const isUsefulFactDatabaseEntry = (item: string): boolean => {
  const text = String(item || '').trim();
  if (!text) {
    return false;
  }
  if (FACT_DATABASE_NOISE_PATTERNS.some((pattern) => pattern.test(text))) {
    return false;
  }
  const segments = text.split(';').map((part) => part.trim()).filter(Boolean);
  if (segments.length > 1 && segments.every((segment) => FACT_DATABASE_NOISE_PATTERNS.some((pattern) => pattern.test(segment)))) {
    return false;
  }
  return true;
};

const normalizeFactListItems = (items: string[]): string[] => {
  const deduped: string[] = [];
  const seen = new Set<string>();
  for (const rawItem of items) {
    const item = String(rawItem || '').trim();
    if (!item || !isUsefulFactDatabaseEntry(item)) {
      continue;
    }
    const normalized = item.toLowerCase();
    if (seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    deduped.push(item);
  }
  return deduped;
};

const harvestDisplayAddresses = (factDatabase: FactDatabase | null | undefined): string[] => {
  const collected: string[] = [];
  const seen = new Set<string>();
  const push = (value?: string): void => {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    if (!text || /^\[object object\]$/i.test(text)) {
      return;
    }
    const token = text.toLowerCase();
    if (seen.has(token)) {
      return;
    }
    seen.add(token);
    collected.push(text);
  };
  const entity = factDatabase?.entity;
  for (const row of Array.isArray(entity?.addresses) ? entity.addresses : []) {
    push(String(row || ''));
  }
  for (const fact of Array.isArray(factDatabase?.sourceCheckFacts) ? factDatabase.sourceCheckFacts : []) {
    const match = String(fact || '').match(
      /^(?:address|mailing address|registered office|outlet address|principal address)(?:\s*\([^)]+\))?\s*:\s*(.+)$/i,
    );
    if (match) {
      push(match[1]);
    }
  }
  const streets = collected.filter((row) => /\b\d{1,6}\s+\S+/.test(row));
  return streets.length ? [...streets, ...collected.filter((row) => !streets.includes(row))] : collected;
};

const buildSourceCheckFactsFromEntity = (factDatabase: FactDatabase): string[] => {
  const aggregatedLabeledFacts: string[] = [];
  const seenAggregatedFacts = new Set<string>();
  const pushAggregatedFact = (label: string, value?: string): void => {
    const renderedValue = String(value || '').trim();
    if (!label || !renderedValue) {
      return;
    }
    const rendered = `${label}: ${renderedValue}`;
    const token = rendered.toLowerCase();
    if (seenAggregatedFacts.has(token)) {
      return;
    }
    seenAggregatedFacts.add(token);
    aggregatedLabeledFacts.push(rendered);
  };

  const entity = factDatabase?.entity || {
    canonical_name: '',
    alt_names: [],
    addresses: [],
    company_affiliations: [],
    social_handles: {},
  };
  const canonicalName = String(entity.canonical_name || '').trim();
  const altNames = Array.isArray(entity.alt_names)
    ? entity.alt_names.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
  pushAggregatedFact('Names', Array.from(new Set([canonicalName, ...altNames].filter(Boolean))).join(', '));
  pushAggregatedFact('Addresses', harvestDisplayAddresses(factDatabase).join(', '));
  pushAggregatedFact(
    'Phones',
    (Array.isArray(entity.phones) ? entity.phones : []).map((item) => String(item || '').trim()).filter(Boolean).join(', '),
  );
  pushAggregatedFact(
    'Emails',
    (Array.isArray(entity.emails) ? entity.emails : []).map((item) => String(item || '').trim()).filter(Boolean).join(', '),
  );
  pushAggregatedFact('Birthdate', String(entity.birthdate || '').trim());
  pushAggregatedFact('Approximate age range', String(entity.age_range || '').trim());
  pushAggregatedFact('Social Security Number', String(entity.ssn || '').trim());
  pushAggregatedFact('Driver License Number', String(entity.dl_number || '').trim());
  pushAggregatedFact(
    'Companies / Affiliations',
    (entity.company_affiliations || []).map((item) => String(item || '').trim()).filter(Boolean).join(', '),
  );
  pushAggregatedFact(
    'Social handles',
    Object.values(entity.social_handles || {}).map((item) => String(item || '').trim()).filter(Boolean).join(', '),
  );

  const signals = Array.isArray(factDatabase?.signals) ? factDatabase.signals : [];
  for (const signal of signals) {
    if (!signal || isFactSignalExcluded(signal)) {
      continue;
    }
    const valueText = formatFactSignalValue(signal.value);
    if (!valueText) {
      continue;
    }
    const label = String(signal.fact_type || 'fact').trim() || 'fact';
    pushAggregatedFact(label, valueText);
    if (aggregatedLabeledFacts.length >= 48) {
      break;
    }
  }

  return normalizeFactListItems(aggregatedLabeledFacts);
};

const hydrateResearchFactDatabase = (factDatabase?: FactDatabase | null): FactDatabase | undefined => {
  if (!factDatabase || typeof factDatabase !== 'object') {
    return undefined;
  }

  const entity = factDatabase.entity && typeof factDatabase.entity === 'object'
    ? factDatabase.entity
    : {
      canonical_name: '',
      alt_names: [],
      addresses: [],
      company_affiliations: [],
      social_handles: {},
    };

  const hydrated: FactDatabase = {
    ...factDatabase,
    entity: {
      ...entity,
      alt_names: Array.isArray(entity.alt_names) ? entity.alt_names : [],
      addresses: Array.isArray(entity.addresses) ? entity.addresses : [],
      phones: Array.isArray(entity.phones) ? entity.phones : [],
      emails: Array.isArray(entity.emails) ? entity.emails : [],
      company_affiliations: Array.isArray(entity.company_affiliations) ? entity.company_affiliations : [],
      social_handles: entity.social_handles && typeof entity.social_handles === 'object' ? entity.social_handles : {},
    },
    signals: Array.isArray(factDatabase.signals) ? factDatabase.signals : [],
    relationships: Array.isArray(factDatabase.relationships) ? factDatabase.relationships : [],
    metadata: factDatabase.metadata && typeof factDatabase.metadata === 'object'
      ? factDatabase.metadata
      : {
        created_at: new Date().toISOString(),
        last_updated: new Date().toISOString(),
      },
  };

  const existingFacts = normalizeFactListItems(
    Array.isArray(hydrated.sourceCheckFacts) ? hydrated.sourceCheckFacts.map((item) => String(item || '')) : [],
  );
  if (existingFacts.length > 0) {
    hydrated.sourceCheckFacts = existingFacts;
    hydrated.sourceCheckFactCount = existingFacts.length;
    return hydrated;
  }

  const rebuiltFacts = buildSourceCheckFactsFromEntity(hydrated);
  hydrated.sourceCheckFacts = rebuiltFacts;
  hydrated.sourceCheckFactCount = rebuiltFacts.length;
  return hydrated;
};

const buildFactListDraftFromKeyData = (
  input?: Partial<KeyData> | null,
): Record<FactListField, string[]> => {
  return {
    names: splitFactListItems(String(input?.names || '')),
    knownAliases: splitFactListItems(String(input?.knownAliases || '')),
    addresses: splitFactListItems(String(input?.addresses || '')),
    companies: splitFactListItems(String(input?.companies || '')),
    socialMediaHandles: splitFactListItems(String(input?.socialMediaHandles || '')),
  };
};

const applyFactListDraftToKeyData = (
  base: KeyData,
  listDraft: Record<FactListField, string[]>,
): KeyData => {
  const next = { ...base };
  for (const field of FACT_LIST_FIELDS) {
    next[field] = normalizeFactListItems(listDraft[field] || []).join(', ');
  }
  return next;
};

const generatePersonUuid = (): string => {
  if (typeof globalThis.crypto !== 'undefined' && typeof globalThis.crypto.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  const randomHex = (): string => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0');
  return `${randomHex()}${randomHex()}-${randomHex()}-4${randomHex().slice(1)}-${((8 + Math.floor(Math.random() * 4)).toString(16))}${randomHex().slice(1)}-${randomHex()}${randomHex()}${randomHex()}`;
};

const generateResearchReportId = (): string => {
  return generatePersonUuid();
};

const RESEARCH_REPORT_NOTES_MAX_LENGTH = 8000;

const formatApiErrorDetail = (detail: unknown): string => {
  if (typeof detail === 'string') {
    return detail.trim();
  }

  if (Array.isArray(detail)) {
    const parts = detail
      .map((entry) => {
        if (typeof entry === 'string') {
          return entry.trim();
        }
        if (entry && typeof entry === 'object') {
          const record = entry as { loc?: unknown; msg?: unknown; type?: unknown };
          const loc = Array.isArray(record.loc)
            ? record.loc.map((value) => String(value)).join('.')
            : '';
          const msg = typeof record.msg === 'string' ? record.msg.trim() : '';
          const type = typeof record.type === 'string' ? record.type.trim() : '';
          return [loc, msg || type].filter(Boolean).join(': ');
        }
        return '';
      })
      .filter(Boolean);
    return parts.join(' | ');
  }

  if (detail && typeof detail === 'object') {
    try {
      return JSON.stringify(detail);
    } catch {
      return String(detail);
    }
  }

  return '';
};

const formatPhoneNumber = (value: string): string => {
  const trimmed = value.trimStart();
  // Pass through international numbers (start with +) without reformatting
  if (trimmed.startsWith('+')) {
    return trimmed.replace(/[^\d+\-() ]/g, '');
  }
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const DEFAULT_ACCOUNT_PROFILE: AccountProfile = {
  personUuid: generatePersonUuid(),
  displayName: 'NextCompli Operations Admin',
  email: 'operations@nextcompli.com',
  contactNumber: DEFAULT_ACCOUNT_CONTACT_PHONE,
  language: 'English',
  timezone: 'UTC-5 (America/New_York)',
};

const formatDisplayNameFromEmail = (email: string): string => {
  const localPart = email.split('@')[0] || '';
  const pieces = localPart
    .split(/[._-]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (pieces.length === 0) {
    return DEFAULT_ACCOUNT_PROFILE.displayName;
  }
  return pieces
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const ACCOUNT_PROFILE_STORAGE_KEY = 'nextcompli.account.profile';
const ACCOUNT_AVATAR_STORAGE_KEY = 'nextcompli.account.avatarByUuid';

const detectCurrentDeviceGroup = (): DeviceGroupKey => {
  if (typeof navigator === 'undefined') {
    return 'mac';
  }
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return 'iphone';
  }
  if (/Windows/i.test(ua)) {
    return 'windows';
  }
  return 'mac';
};

const buildCurrentDeviceSession = (email: string): DeviceSessionRecord => {
  const group = detectCurrentDeviceGroup();
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Safari\//.test(ua)
        ? 'Safari'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : 'Browser';
  const label = group === 'iphone'
    ? 'This iPhone'
    : group === 'windows'
      ? 'This Windows computer'
      : 'This Mac';
  return {
    id: `current-${group}`,
    label,
    browser,
    ipAddress: 'This device',
    location: email ? `Signed in as ${email}` : 'Current session',
    lastSeen: 'Active now',
  };
};

const EMPTY_SECURITY_DEVICE_SESSIONS: Record<DeviceGroupKey, DeviceSessionRecord[]> = {
  mac: [],
  iphone: [],
  windows: [],
};

const DEFAULT_SECURITY_DEVICE_SESSIONS: Record<DeviceGroupKey, DeviceSessionRecord[]> = EMPTY_SECURITY_DEVICE_SESSIONS;

const SKYLEAD_PRIVACY_POLICY_TEXT = `1. Introduction

In this policy, “We”, “Us” and “Our” refer to NextCompli, Inc., operating under the brand NextCompli and the website https://nextcompli.com .

This policy applies where we are acting as a Controller with respect to the personal data of our website visitors, service users, email recipients and personal data collected from public sources; in other words, where we determine the purposes and means of the processing of that personal data.

Where we are acting as a Processor, which means where we process personal data on behalf of our clients, which is when providing our services in line NextCompli’s Terms & Conditions, our collection and use of personal data is governed by our NextCompli Data Processing Addendum available at Data Processing Addendum

Any reference to a Data Subject means a natural person whose personal data is processed by us as a Data Controller or as a Data Processor, in other words, you.

Any reference to the GDPR applies to both the UK GDPR and EU GDPR. Likewise, any reference to EU countries will also apply to the UK. This will remain the case so long as the UK is subject to an adequacy decision by the EU. Should there be a conflict between the two, the version that grants data subjects the greatest protection will apply.

2. How We use Your personal data

We want to be clear with you about how we use your data, so in this section we talk about the general categories of personal data that we may process and, in the case of personal data that we did not obtain directly from you, we’ll tell you where we got your data and the purposes for which we may process your data; finally, and very importantly, we’ll explain the legal bases of the processing which applies to us and you.

The General Categories of Personal Data That We May Process:

Usage Data. We may process data about your use of our website and services (“Usage Data”). The Usage Data may include your IP address, geographical location, browser type and version, operating system, referral source, length of visit, page views and website navigation paths, as well as information about the timing, frequency and pattern of your service use. The source of the Usage Data is Google Analytics as well as our analytics tracking system. This Usage Data may be processed for the purposes of analysing the use of the website and services. The legal basis for this processing is our legitimate interests, namely monitoring and improving our website and services, and tailoring any communication with you. We, of course, use this data to help us run our business as well as we can.
Account Data. We may process your Account Data (“Account Data”). The Account Data may include your name, email address, company information, password and phone number, as well as, further data required for subscribing to our services including credit card details, country, city and physical address. The source of the Account Data is generally you or your employer. The Account Data may be processed for the purposes of providing our services, ensuring the security of our website and services, maintaining back-ups of our databases and communicating with you. The legal basis for this processing is the performance of a contract between you (or your employer) and us, and/or taking steps, at your request, to enter into such a contract. In some cases, we may also rely on Legitimate Interest as our lawful basis where we use your data for marketing purposes or in order to operate our business and improve our services.
Service Data. We may process your personal data that are provided in the course of the use of our services (“Service Data”). The Service Data may include name, email address, telephone number, and other related information. The source of the Service Data is you or your employer. The Service Data may be processed for the purposes of providing our services and communicating with you. The legal basis for this processing is the performance of a contract between you and us and/or taking steps, at your request, to enter into such a contract.
Enquiry Data. We may process information contained in any enquiry you submit through our website https://nextcompli.com/ or other channels of communication, regarding goods and/or services (“Enquiry Data”). The Enquiry Data may be processed for the purposes of us offering, marketing and selling relevant goods and/or services to you. The legal basis for this processing is consent, which may be withdrawn by you at any time by unsubscribing from our marketing emails or contacting us, or contract and/or taking steps, at your request, to enter into such a contract.
Notification Data. We may process information that you provide to us, such as email address, name and company name (“Notification Data”). The Notification Data may be processed for the purpose of subscribing to our email newsletters and/or eBook and sending you the relevant notifications and/or newsletters. The legal basis for this processing is consent, which may be withdrawn by you at any time by unsubscribing from our emails or contacting us.
Correspondence Data. We may process information contained in or relating to any communication that you send to us (“Correspondence Data”). The Correspondence Data may include your name, email address and the communication content and metadata associated with the communication. Our website will generate the metadata associated with communications made using the website contact forms. The Correspondence Data may be processed for the purposes of communicating with you and record-keeping. The legal basis for this processing is our legitimate interests, namely the proper administration of our website, business and communications with users.
Public Data. We may process information found on public social networking profiles and other from other publicly available sources (“Public Data”). This data may include name, employment information, career information. education details, job title, and other relevant information. Public Data may be processed for the purposes of offering, marketing and selling relevant goods and/or services to you and/or your employer. The legal basis for this processing is our legitimate interests, namely the administration of our business, to develop our business, and in order for us to make a profit.
Legal Claims Data. We may process any of your personal data identified in this policy where necessary for the establishment, exercise or defence of legal claims, whether in court proceedings or in an administrative or out-of-court procedure (“Legal Claims Data”). The legal basis for this processing is our legitimate interests, namely the protection and assertion of our legal rights, your legal rights and the legal rights of others.
Professional Use Data. We may process any of your personal data identified in this policy where necessary for the purposes of managing risks or obtaining professional advice (“Professional Use Data”). The legal basis for processing Professional Use Data is our legitimate interests, namely the proper protection of our business against risks.
In addition to the specific purposes for which we may process your personal data set out in this Section, we may also process any of your personal data where such processing is necessary for compliance with a legal obligation to which we are subject, or in order to protect your vital interests or the vital interests of another natural person.

3. Source of your Personal Data

Primarily, we obtain your data from you, when you use our website features and/or when you subscribe to our services, or from other publicly available sources.

4. Data sharing

NextCompli is a prospecting and lead generation company, where our services comprise the collection and sharing of personal data to our clients, and on behalf of our clients. In respect of personal data that we process on behalf of our clients, please refer to our clients’ privacy policies for the information about sharing of data provided to them.

Where NextCompli processes personal data as a Controller (website visitors, service users, email recipients, etc.), we may disclose your personal data to any member of our group of companies (this means our subsidiaries, our ultimate holding company and all its subsidiaries, together known as “Group Companies”) insofar as reasonably necessary for the purposes, and on the legal bases, set out in this policy.

We may disclose your personal data to our professional advisers insofar as reasonably necessary for the purposes of managing risks, obtaining professional advice, or the establishment, exercise or defence of legal claims, whether in court proceedings or in an administrative or out-of-court procedures.

We may also disclose your personal data to our suppliers, partners and other carefully managed third parties as required to operate our company and provide our services. Where this is the case, we will always ensure appropriate technical and administrative controls are in place.

As you would expect, in addition to the specific disclosures of personal data set out above, we may disclose your personal data where such disclosure is necessary for compliance with a binding legal obligation.

5. International transfers of your personal data

NextCompli processes your personal data primarily in the United Kingdom and United States. Transfer of personal data from the United Kingdom and United States to other countries is carried out only if it is necessary for the execution of contracts, complying with legal obligations and/or to achieve our legitimate interest.

NextCompli transfers personal data to countries which, according to EU, provide adequate level of personal data protection and based on signed agreements governing data processing.

When a country does not provide adequate level of personal data protection according to EU, personal data is then being transferred on basis of implementing appropriate safeguarding measures, such as European Standard Contractual Clauses, UK International Data Transfer Addendum and appropriate transfer risk assessments, or other safeguards in line with the GDPR.

6. Retaining and deleting personal data

This section sets out our data retention policies and procedure, which are designed to help ensure that we comply with our legal obligations in relation to the retention and deletion of personal data.

We retain your data for as long as you have an account. When you close an account, we will delete its content. We may retain your transactional information (for auditing, tax, and financial purposes). When we no longer have a business reason for retaining data, we will delete or anonymize it.

Data collected for the purposes of sending the relevant notifications and/or newsletters or offering, marketing and selling relevant goods and/or services is retained until you withdraw your consent or select unsubscribe link in the communication email received from us.

Notwithstanding the other provisions of this section, we may retain your personal data where such retention is necessary for compliance with a legal obligation to which we are subject, or in order to protect your vital interests or the vital interests of another natural person.

7. External links

Our website contains links to external websites run by other organisations. This privacy policy applies only to our websites not those external websites that we link to, who will have their own privacy policies. We are not responsible for these external websites and their privacy policies and practices. In addition, if you linked to our website from an external site, we cannot be responsible for the privacy policies and practices of the owners and operators of that external website and recommend that you check the privacy policy of that external site.

8. Automated decision-making, including profiling

Our processes are not designed in a way to be automated, including profiling, and to make decisions which produce legal effects concerning you, or decisions which similarly significantly affect you.

9. The rights of data subjects

In this section we have summarised the rights that you have under data protection law. Some of the rights are complex, and not all of the details have been included in our summaries. Accordingly, you should read the relevant laws and guidance from the regulatory authorities for a full explanation of these rights.

The Right to be Informed. This is the right of Data Subjects to know details regarding the processing of their data and how we acquired their data, this information is included in this Privacy Policy.

The Right to Access. You have the right to confirm as to whether or not we process your personal data and, where we do, access to the personal data, together with certain additional information. That additional information includes details of the purposes of the processing, the categories of personal data concerned and the recipients of the personal data. Providing the rights and freedoms of others are not affected, we will supply to you a copy of your personal data upon request and within one month of verification of your identity (we may extend the time within which we must provide the information by a further two months if the request is particularly complex, or if there are numerous requests and we will let you know if this is the case). The first copy will be provided free of charge, but additional copies may be subject to a reasonable fee.

The Right to Rectification. You have the right to have any inaccurate personal data about you rectified and, taking into account the purposes of the processing, to have any incomplete personal data about you completed. If the request is accepted by us, the rectification will be completed within one month (may be extended by us by a further two months if complex). We will use all reasonable endeavours to notify any third party with whom we have shared the data of any rectifications made.

The Right to Erasure (also known as the “Right to be Forgotten”). In some circumstances you have the right to the erasure of your personal data without undue delay. However, there are exclusions to the right to erasure. We will use all reasonable endeavours to notify any third party with whom we have shared the data of any actioned erasure requests.

The Right to Restrict Processing. In some circumstances you have the right to restrict the processing of your personal data. Where processing has been restricted, we may continue to store your personal data. However, we will only otherwise process it: with your consent; for the establishment, exercise or defence of legal claims; for the protection of the rights of another natural or legal person; or for reasons of important public interest. We will use all reasonable endeavours to notify any third party with whom we have shared the data of any restriction placed on the processing of your data.

The Right to Object to Processing. You have the right to object to our processing of your personal data on grounds relating to your particular situation, but only to the extent that the legal basis for the processing is that the processing is necessary for certain tasks. If you make such an objection, we will cease to process the personal information unless we can demonstrate compelling legitimate grounds for the processing which override your interests, rights and freedoms, or if the processing is for the establishment, exercise or defence of legal claims.

The Right to Data Portability. To the extent that the legal basis for our processing of your personal data is:

Consent; or
That the processing is necessary for the performance of a contract to which you are party or in order to take steps at your request prior to entering into a contract; and
Such processing is carried out by automated means.
You have the right to receive your personal data from us in a structured, commonly used and machine-readable format. However, this right does not apply where it would adversely affect the rights and freedoms of others.

Right to Complain. If you consider that our processing of your personal information infringes data protection laws, you should first contact us. If you are dissatisfied with the outcome, you have a legal right to lodge a complaint with a Supervisory Authority responsible for data protection. You may do so in the UK (Information Commissioner’s Office) or EU member state of your habitual residence, your place of work or the place of the alleged infringement.

10. Contact us

If your query relates to this Privacy Policy, NextCompli Data protection Addendum, or should you wish to exercise one of your data protection rights as a Data Subject, you can contact us in the following ways:

By email at: operations@nextcompli.com
By “Get in touch” contact form on our website: https://nextcompli.com
By post to: Canyon Foundry LLC, 5900 Balcones Dr Ste 100, Austin, TX 78731-4298, Attn: Andrew Goss
For business registration and filing records, contact:
Wyoming Secretary of State - Business Division
Phone: 307-777-7311
Email: Business@wyo.gov
Web: https://sos.wyo.gov/Business
Business Filing: https://wyobiz.wyo.gov

11. Amendments to this policy

We may update this policy from time to time by publishing a new version on our website.

You should check this page occasionally to ensure you are happy with any changes to this policy.

We may notify you of changes to this policy by email.

This policy was last updated on March 2023.`;

const SKYLEAD_PRIVACY_POLICY_TEXT_ES = `1. Introducción

En esta política, “Nosotros”, “Nos” y “Nuestro/a” se refieren a NextCompli, Inc., que opera bajo la marca NextCompli y el sitio web https://nextcompli.com.

Esta política aplica cuando actuamos como Responsable del tratamiento respecto a los datos personales de visitantes del sitio, usuarios de servicios, destinatarios de correo y datos obtenidos de fuentes públicas; es decir, cuando determinamos los fines y medios del tratamiento.

Cuando actuamos como Encargado del tratamiento, es decir, cuando tratamos datos personales en nombre de nuestros clientes al prestar nuestros servicios conforme a los Términos y Condiciones de NextCompli, nuestra recopilación y uso de datos personales se rige por nuestro Anexo de Tratamiento de Datos de NextCompli.

2. Cómo usamos tus datos personales

Queremos ser transparentes sobre cómo utilizamos tus datos. En esta sección explicamos las categorías generales de datos personales que podemos tratar, las fuentes de obtención y las finalidades, junto con las bases legales aplicables.

Podemos tratar datos de uso, datos de cuenta, datos de servicio, datos de consultas, datos de notificaciones, datos de correspondencia y datos de fuentes públicas para operar, proteger y mejorar nuestros servicios.

3. Fuente de tus datos personales

Principalmente obtenemos tus datos directamente de ti cuando usas nuestro sitio o te suscribes a nuestros servicios, así como de fuentes públicas disponibles.

4. Compartición de datos

Podemos compartir datos personales con empresas del grupo, asesores profesionales, proveedores e infraestructura técnica cuando sea necesario para prestar servicios, gestionar riesgos, cumplir obligaciones legales y mantener la seguridad operacional.

5. Transferencias internacionales

NextCompli procesa datos personales principalmente en Reino Unido y Estados Unidos. Cuando se requieren transferencias internacionales, aplicamos salvaguardas adecuadas, incluidas cláusulas contractuales estándar y mecanismos compatibles con GDPR.

6. Conservación y eliminación

Conservamos datos mientras exista una cuenta activa o una necesidad legal, contractual o de seguridad. Cuando ya no exista una finalidad legítima, eliminamos o anonimizamos los datos.

7. Enlaces externos

Nuestro sitio puede incluir enlaces a sitios de terceros. Esta política solo aplica a los sitios de NextCompli y no a políticas o prácticas de terceros.

8. Toma de decisiones automatizada

Nuestros procesos no están diseñados para tomar decisiones automatizadas, incluido perfilado, que produzcan efectos jurídicos significativos sobre las personas.

9. Derechos de los titulares

Según la normativa de protección de datos, puedes solicitar acceso, rectificación, supresión, restricción, oposición y portabilidad, además de presentar reclamaciones ante la autoridad competente cuando corresponda.

10. Contáctanos

Para consultas sobre esta política o para ejercer derechos de protección de datos, contáctanos en operations@nextcompli.com, mediante el formulario “Get in touch” en https://nextcompli.com, o por correo a Canyon Foundry LLC, 5900 Balcones Dr Ste 100, Austin, TX 78731-4298, Attn: Andrew Goss.

11. Cambios a esta política

Podemos actualizar esta política periódicamente publicando una nueva versión en nuestro sitio. Te recomendamos revisarla de forma regular.

Última actualización: marzo de 2023.`;

const SKYLEAD_PRIVACY_POLICY_TEXT_BY_LANGUAGE: Record<'en' | 'es', string> = {
  en: SKYLEAD_PRIVACY_POLICY_TEXT,
  es: SKYLEAD_PRIVACY_POLICY_TEXT_ES,
};

export const DASHBOARD_COPY: Record<DashboardLanguage, Record<string, string>> = {
  en: {
    sectionLabel: 'Account Settings',
    navHome: 'Home',
    navDashboards: 'Dashboards',
    navPersonal: 'Personal info',
    navTeam: 'Team',
    navSecurity: 'Security & sign-in',
    navPrivacy: 'Data & privacy',
    navPayments: 'Payments',
    navBilling: 'Customer billing',
    navSubscriptions: 'Paid vendors',
    navResearch: 'Research reports via Vantage',
    navCrm: 'CRM via Pursuit',
    navInlet: 'Inbound via Inlet',
    navRazzmatazz: 'Marketing via Razzmatazz',
    navOfacGroup: 'OFAC',
    navOfacCompliance: 'OFAC Compliance',
    navOfacDashboards: 'OFAC Dashboards',
    navOfac: 'Compliance',
    navOfacLiveFeed: 'Live Feed',
    navOfacScreening: 'Screening',
    navOfacInvestigations: 'Investigations',
    navOfacWhitelist: 'Whitelist',
    navOfacCapta: 'CAPTA lists',
    navWebsiteCompliance: 'Website Compliance',
    navCloudCompliance: 'Cloud Compliance',
    navDodContracts: 'Government Compliance',
    navEducationCompliance: 'Education Compliance',
    navAiCompliance: 'AI Compliance',
    navAgent: 'NextCompli Agent',
    navBusinessFormation: 'Legal Entity Formation',
    navWillsTrusts: 'Wills & Trusts',
    navTips: 'Tip manager',
    navStyle: 'Style governance',
    navGroupWork: 'Work',
    navWorkForm: 'Form & protect',
    navWorkComply: 'Compliance',
    navWorkDesk: 'Desk',
    navWorkGrow: 'Grow',
    navWorkOperate: 'Operate',
    navGroupBilling: 'Billing',
    navGroupSubscriptions: 'Subscriptions',
    navSubscriptionLevels: 'Subscription levels',
    navPayingSubscriptions: 'Paying subscriptions',
    navExpiredSubscriptions: 'Expired subscriptions',
    navGroupAccount: 'Account',
    navGroupStarred: 'Starred',
    navFindPlaceholder: 'Find a page',
    navJumpTo: 'Go to',
    navCloseMenu: 'Close console menu',
    navCloseMenuDone: 'Done',
    navOpenMenu: 'Open console menu',
    navNoResults: 'No matching pages',
    navMore: 'More',
    navTabHome: 'Home',
    navTabWork: 'Work',
    navTabAccount: 'Account',
    personalPanelLabel: 'Personal info details',
    securityPanelLabel: 'Security and sign in details',
    paymentsPanelLabel: 'Payments details',
    billingPanelLabel: 'Customer billing and plan details',
    subscriptionsPanelLabel: 'Paid vendor accounts and credentials',
    researchPanelLabel: 'Research report operations via Vantage',
    researchKicker: 'Vantage',
    researchPageLead: 'Open a case, screen the subject, and deliver a sourced PDF. Coverage and queue sit above the work, not in the way.',
    researchHealthCompleted: 'Completed',
    researchIntakeIdentity: 'Identity',
    researchIntakeContact: 'Contact',
    researchIntakeContactHint: 'These identify the subject. PDF delivery emails are in Search settings (gear in the page header).',
    researchIntakeIdentifiers: 'Identifiers',
    researchSubjectEmail: 'Subject email',
    researchSubjectEmailHint: 'Comma-separated. Used to find the person or company — not where the PDF is sent.',
    researchPdfRecipients: 'PDF delivery emails',
    researchEmailGroups: 'Email groups',
    crmPanelLabel: 'CRM operations via Pursuit',
    tipsPanelLabel: 'Tip management details',
    stylePanelLabel: 'Style governance details',
    homePanelLabel: 'Mission and vision details',
    accountSettings: 'Account Settings',
    securityAccess: 'Security & sign-in',
    billingPlan: 'Payments',
    dashboards: 'Dashboards',
    missionVision: 'Mission & Vision',
    dashboardLiveBadge: 'Live',
    metricUnitRecords: 'records',
    metricCaptionDataIndexed: 'Data records indexed',
    metricCaptionSuccessRate: 'Success rate',
    metricCaptionComplianceScore: 'Compliance score',
    metricCaptionAwaitingMetrics: 'Awaiting live metrics',
    metricLabelLastUpdated: 'Last updated',
    metricLabelStatus: 'Status',
    metricLabelResponseTime: 'Response Time',
    metricLabelSuccessRate: 'Success Rate',
    metricLabelErrorRate: 'Error Rate',
    metricLabelRiskLevel: 'Risk Level',
    metricLabelDataCount: 'Data Count',
    metricLabelCoverage: 'Coverage',
    metricLabelCompliance: 'Compliance',
    metricLabelEvidence: 'Evidence',
    metricLabelGaps: 'Gaps',
    metricLabelWebsiteSignal: 'Website Signal',
    metricLabelConfluenceSignal: 'Policy Signal',
    metricLabelMetricsSource: 'Metrics Source Required',
    metricLabelWebsiteScanCoverage: 'Website Scan Coverage',
    metricLabelWebsiteScanStatus: 'Website Scan Status',
    metricLabelCompliantRecords: 'Compliant Records',
    metricLabelNonCompliantRecords: 'Non-Compliant Records',
    metricLabelTotalRequirements: 'Total Requirements',
    metricLabelTermsReplayGate: 'Terms Replay Gate',
    metricLabelTermsPageAccessGate: 'Terms Page Access/Linkage Gate',
    metricLabelContractUpdateGate: 'Contract Update Notice Gate',
    metricLabelAuthenticatedEntrypointGate: 'Authenticated Entrypoint Assent Gate',
    metricLabelNegotiatedContractTermsGate: 'Negotiated Contract Terms Path Gate',
    metricLabelPublicTermsPageGate: 'Public Terms Page Publication Gate',
    metricLabelAssentReplay: 'Assent Replay',
    metricLabelAssentReplayEvents: 'Assent Replay Events',
    dashboardServiceFilterTitle: 'Service visibility',
    dashboardFilterOfac: 'OFAC services',
    dashboardFilterWebsiteCompliance: 'Website compliance services',
    dashboardFilterGovernmentalCompliance: 'Governmental compliance services',
    dashboardTagOfac: 'OFAC',
    dashboardTagWebsiteCompliance: 'Website Compliance',
    dashboardTagGovernmentalCompliance: 'Governmental Compliance',
    noVisibleDashboards: 'No dashboards are visible with the current service filters.',
    homeIntroTitle: 'Operate with confidence at enterprise scale',
    homeIntroBody: 'NextCompli combines sanctions intelligence, operational controls, and implementation-ready workflows so compliance and revenue teams can move quickly without sacrificing trust.',
    missionTitle: 'Mission',
    missionBody: 'Make sanctions and compliance checks simple, fast, and reliable for teams running real operations.',
    visionTitle: 'Vision',
    visionBody: 'Be the easiest platform for any team to stay compliant, respond faster, and remain audit-ready as they scale.',
    valuesTitle: 'Values',
    valuesBody: 'Operate with transparent pricing, clear documentation, modern UX, and accountable execution for customers, partners, and regulators.',
    securityTipsCardTitle: 'You have security tips',
    securityTipsCardBody: 'Security tips found in Security Checkup.',
    securityRecentTitle: 'Recent security activity',
    securityRecentPrimary: 'Signed in on this device',
    securityRecentSecondary: 'Current session',
    securityRecentAction: 'Review security activity',
    securityHowSignInTitle: 'How you sign in to NextCompli',
    securityHowSignInBody: 'Keep sign-in and recovery details up to date so you can always access your account.',
    securityTwoStepTitle: '2-Step Verification',
    securityTwoStepDetail: 'Not enabled',
    securityPasskeysTitle: 'Passkeys and security keys',
    securityPasskeysDetail: '1 passkey',
    securityPasswordTitle: 'Password',
    securityPasswordDetail: 'Managed for this account',
    securitySkipTitle: 'Skip password when possible',
    securitySkipDetail: 'On',
    securityPromptTitle: 'Google prompt',
    securityPromptDetail: '1 device',
    securityTwoStepPhonesTitle: '2-Step Verification phones',
    securityTwoStepPhonesDetail: 'Not set',
    securityRecoveryPhoneTitle: 'Recovery phone',
    securityRecoveryPhoneDetail: 'Not set',
    securityRecoveryEmailTitle: 'Recovery email',
    securityRecoveryEmailDetail: 'Verify your email',
    securityBackupCodesTitle: 'Backup codes',
    securityBackupCodesDetail: 'No backup codes',
    securityAddOptionsLabel: 'You can add more sign-in options',
    securityAddOptionContacts: 'Recovery contacts',
    securityAddOptionAuthenticator: 'Authenticator',
    securityAddOptionBackupPhones: 'Backup 2-Step Verification phones',
    securityAddOptionLinkedIds: 'Linked IDs',
    securityDevicesTitleBlock: 'Your devices',
    securityDevicesSubtitle: "Where you're signed in",
    securityDeviceMacTitle: '4 sessions on Mac computer(s)',
    securityDeviceMacDetail: 'macOS devices',
    securityDeviceIphoneTitle: '5 sessions on iPhone(s)',
    securityDeviceIphoneDetail: 'iPhone devices',
    securityDeviceWindowsTitle: '2 sessions on Windows computer(s)',
    securityDeviceWindowsDetail: 'Windows devices',
    securityFindLostDevice: 'Find a lost device',
    securityManageAllDevices: 'Manage all devices',
    securityFindLocation: 'Find location',
    securityDeleteDevice: 'Delete device',
    securityDeviceAuthorized: 'Authenticated and authorized',
    securityDeviceAccordionHint: 'View device session details',
    securityNoSessions: 'No active sessions for this device type.',
    homeValueTitle: 'Why enterprise teams choose NextCompli',
    homeValueOne: 'Accelerate implementation with API-first onboarding, sandbox validation, and production-ready integration patterns.',
    homeValueTwo: 'Reduce compliance operating cost through transparent pricing, modular service activation, and reusable workflows.',
    homeValueThree: 'Improve decision velocity with real-time and batch screening, continuous monitoring, and centralized audit history.',
    homeValueFour: 'Deploy where policy requires: local, Kubernetes, or AWS, with one architecture and consistent controls.',
    homeProofTitle: 'Business outcomes for compliance leaders',
    homeProofOne: 'Shorter time-to-value from pilot to production with structured rollout paths for regulated teams.',
    homeProofTwo: 'Lower integration risk using composable services, gateway orchestration, and observable health metrics.',
    homeProofThree: 'Stronger trust posture via explainable screening outputs and defensible case records for reviews and audits.',
    dashboardsCopy: 'Manage which dashboards this account can access.',
    subscribedDashboards: 'Subscribed Dashboards',
    noSubscribedDashboards: 'No dashboard subscriptions selected.',
    noServiceDashboards: 'No live service dashboards are currently available.',
    noLiveMetrics: 'No live metrics available for this service yet.',
    updateSubscriptions: 'Update Subscriptions',
    toggleDashboardSubscriptions: 'Toggle dashboard subscriptions',
    toggleSection: 'Toggle {section} section',
    dashboardsSection: 'Dashboards section',
    dashboardSubscriptions: 'Dashboard subscriptions',
    labelPersonUuid: 'Person UUID',
    labelDisplayName: 'Display Name',
    labelEmail: 'Email',
    labelContactNumber: 'Contact Number',
    labelLanguage: 'Language',
    labelTimezone: 'Timezone',
    appearanceTitle: 'Appearance',
    appearanceHint: 'These preferences apply immediately on this device. Theme defaults to your system setting.',
    labelTheme: 'Theme',
    themeSystem: 'System',
    themeLight: 'Light',
    themeDark: 'Dark',
    labelDensity: 'Density',
    densityComfortable: 'Comfortable',
    densityCompact: 'Compact',
    labelReduceMotion: 'Reduce motion',
    reduceMotionSystem: 'System',
    reduceMotionOn: 'On',
    reduceMotionOff: 'Off',
    labelFontScale: 'Text size',
    fontScaleSm: 'Small',
    fontScaleMd: 'Default',
    fontScaleLg: 'Large',
    appearanceResolvedTheme: 'Resolved theme',
    labelPlan: 'Plan',
    labelRenewal: 'Renewal',
    valueLanguage: DEFAULT_ACCOUNT_PROFILE.language,
    valueTimezone: DEFAULT_ACCOUNT_PROFILE.timezone,
    valuePlan: 'CompliComplete',
    valueRenewal: 'Aug 31, 2026',
    settingsEditAria: 'Edit account settings',
    settingsSaveAction: 'Save changes',
    settingsCancelAction: 'Cancel',
    settingsSaved: 'Account settings updated successfully.',
    settingsDisplayNameRequired: 'Display Name is required.',
    settingsEmailInvalid: 'Enter a valid email address.',
    settingsAvatarLabel: 'Profile photo',
    settingsAvatarHint: 'Upload a JPG, PNG, or WEBP image up to 5MB. Large images are auto-resized.',
    settingsAvatarRemoveAction: 'Remove photo',
    settingsAvatarInvalidType: 'Select a valid image file (JPG, PNG, or WEBP).',
    settingsAvatarTooLarge: 'Image must be 5MB or smaller.',
    settingsAvatarUpdated: 'Profile photo updated. Save changes to apply.',
    settingsAvatarRemoved: 'Profile photo removed. Save changes to apply.',
      settingsContactNumberHint: 'US format: (555) 555-5555  |  International: +1 555 555 5555',
      settingsContactNumberInvalid: 'Enter a valid phone number, e.g. (555) 555-5555 or +1 555 555 5555.',
    settingsAvatarAlt: 'Profile photo',
    securityNoTipsTitle: 'No security tips found',
    securityNoTipsBody: 'No security tips exist at this time.',
    securityTipsFoundBody: 'Security analysis found recommendations to improve your account security posture.',
    securityTipEnable2FA: 'Enable 2-Step Verification for stronger sign-in protection.',
    securityTipAddPasskey: 'Add at least one passkey to reduce password-only login risk.',
    securityTipVerifyRecoveryEmail: 'Verify your recovery email to avoid account recovery lockout.',
    securityTipRefreshBackupCodes: 'Regenerate backup codes so emergency sign-in codes are available.',
    securityActivityReportTitle: 'Security activity report',
    securityActivitySafeSummary: 'No suspicious sign-ins detected in the latest account checks.',
    securityActivityWarningSummary: 'Review recommended security updates from the latest account checks.',
    securityActivityLine2fa: '2-Step Verification status',
    securityActivityLinePasskey: 'Passkey status',
    securityActivityLineRecoveryEmail: 'Recovery email status',
    securityActivityLineBackupCodes: 'Backup codes remaining',
    secMfa: '2-step verification is enabled on this account.',
    secRotation: 'Password rotation policy: 90 days',
    secSessions: 'Active sessions: 2',
    secTokens: 'API tokens: 1 active',
    securityOverviewCopy: 'Review recommendations and account-sign in protections in one place.',
    securityRecommendationsTitle: 'Security recommendations available',
    securityRecommendationsBody: 'Complete suggested actions to improve account security posture.',
    securityActivityTitle: 'Recent security activity',
    securityActivityBody: 'No suspicious sign-ins detected in the last 28 days.',
    securitySignInTitle: 'Sign-in and recovery options',
    securitySignInBody: 'Manage password, 2-step verification, and backup recovery methods.',
    securityDevicesTitle: 'Your devices and sessions',
    securityDevicesBody: 'Review currently active devices and revoke access when needed.',
    securityReviewAction: 'Review',
    securityManageAction: 'Manage',
    paymentsOpenAction: 'Open page',
      paymentsOverviewCopy: 'Manage payment methods, invoices, and your NextCompli plan in place.',
      paymentsMethodTitle: 'Manage payment methods',
      paymentsMethodBody: 'Update card details and billing preferences.',
      paymentsInfoTitle: 'Payments info',
      paymentsInfoBody: 'Review transactions and invoices tied to this account.',
      paymentsSubscriptionsTitle: 'NextCompli plan',
      paymentsSubscriptionsBody: 'View your plan and manage Stripe subscriptions.',
      paymentsSearchAction: 'Search account',
      paymentsHelpAction: 'Get help',
    twoFactorTitle: 'Two-factor authentication',
    twoFactorEnabled: '2FA is enabled for this account.',
    twoFactorDisabled: '2FA is currently disabled.',
    twoFactorEnableAction: 'Enable 2FA',
    twoFactorDisableAction: 'Disable 2FA',
    passwordTitle: 'Change password',
    passwordCurrent: 'Current password',
    passwordNew: 'New password',
    passwordConfirm: 'Confirm new password',
    passwordSaveAction: 'Update password',
    passwordSuccess: 'Password updated successfully.',
    passwordMismatch: 'New password and confirmation must match.',
    passwordLength: 'New password must be at least 12 characters.',
    passwordRequirements: 'Use at least 12 characters. Show the password to verify typing; paste from a password manager is allowed.',
    passwordInvalidCurrent: 'Current password is incorrect.',
    passwordUpdating: 'Updating password…',
    passwordShow: 'Show',
    passwordHide: 'Hide',
    notificationsEmailTitle: 'Email alerts',
    notificationsEmailLabel: 'Alert recipient email',
    notificationsEmailAction: 'Save email alert settings',
    notificationsEmailSaved: 'Email alert subscriptions saved.',
    notificationsServiceChecklist: 'Service alert subscription checklist',
    notificationsEmptyServices: 'No services available yet for email alerts.',
    privacyDisclosuresTitle: 'Data & privacy policy',
    privacyDisclaimerTitle: 'Disclaimers',
    privacyDisclaimerBody: 'This summary mirrors NextCompli privacy-policy style language for account use and does not replace formal legal advice.',
    privacyDisclosureOne: 'NextCompli collects account, billing, device, and usage information to provide services, secure access, improve product performance, and communicate important updates.',
    privacyDisclosureTwo: 'NextCompli may process and share data with trusted processors, infrastructure partners, and legal authorities when required for service delivery, compliance, fraud prevention, or legal obligations.',
    privacyDisclosureThree: 'Users can request access, correction, deletion, export, and consent updates where applicable; retention periods are based on legal, contractual, and security requirements.',
    privacySourceOne: 'Source: NextCompli Privacy Policy (adapted policy format)',
    privacySourceTwo: 'Source: NextCompli Terms, Security, and Compliance commitments',
    privacySourceThree: 'Source: Applicable regional privacy rights and regulatory frameworks',
    notifCritical: 'Critical service health alerts',
    notifRisk: 'Compliance risk threshold alerts',
    notifWeekly: 'Weekly operations summary',
    notifRelease: 'New integration and release announcements',
  },
  es: {
    sectionLabel: 'Configuración de cuenta',
    navHome: 'Inicio',
    navDashboards: 'Paneles',
    navPersonal: 'Información personal',
    navTeam: 'Equipo',
    navSecurity: 'Seguridad e inicio de sesión',
    navPrivacy: 'Datos y privacidad',
    navPayments: 'Pagos',
    navBilling: 'Facturación de clientes',
    navSubscriptions: 'Proveedores de pago',
    navResearch: 'Reportes de investigación vía Vantage',
    navCrm: 'CRM vía Pursuit',
    navInlet: 'Entrada vía Inlet',
    navRazzmatazz: 'Marketing vía Razzmatazz',
    navOfacGroup: 'OFAC',
    navOfacCompliance: 'Cumplimiento OFAC',
    navOfacDashboards: 'Paneles OFAC',
    navOfac: 'Cumplimiento',
    navOfacLiveFeed: 'Feed en vivo',
    navOfacScreening: 'Screening',
    navOfacInvestigations: 'Investigaciones',
    navOfacWhitelist: 'Lista blanca',
    navOfacCapta: 'Listas CAPTA',
    navWebsiteCompliance: 'Cumplimiento del sitio web',
    navCloudCompliance: 'Cumplimiento de la nube',
    navDodContracts: 'Cumplimiento gubernamental',
    navEducationCompliance: 'Cumplimiento educativo',
    navAiCompliance: 'Cumplimiento de IA',
    navAgent: 'Agente de NextCompli',
    navBusinessFormation: 'Formación de entidad legal',
    navWillsTrusts: 'Testamentos y fideicomisos',
    navTips: 'Gestor de consejos',
    navStyle: 'Gobierno de estilos',
    navGroupWork: 'Trabajo',
    navWorkForm: 'Formar y proteger',
    navWorkComply: 'Cumplimiento',
    navWorkDesk: 'Escritorio',
    navWorkGrow: 'Crecer',
    navWorkOperate: 'Operar',
    navGroupBilling: 'Facturación',
    navGroupSubscriptions: 'Suscripciones',
    navSubscriptionLevels: 'Niveles de suscripción',
    navPayingSubscriptions: 'Suscripciones de pago',
    navExpiredSubscriptions: 'Suscripciones vencidas',
    navGroupAccount: 'Cuenta',
    navGroupStarred: 'Destacados',
    navFindPlaceholder: 'Buscar una página',
    navJumpTo: 'Ir a',
    navCloseMenu: 'Cerrar menú de consola',
    navCloseMenuDone: 'Listo',
    navOpenMenu: 'Abrir menú de consola',
    navNoResults: 'No hay páginas coincidentes',
    navMore: 'Más',
    navTabHome: 'Inicio',
    navTabWork: 'Trabajo',
    navTabAccount: 'Cuenta',
    personalPanelLabel: 'Detalles de información personal',
    securityPanelLabel: 'Detalles de seguridad e inicio de sesión',
    paymentsPanelLabel: 'Detalles de pagos',
    billingPanelLabel: 'Detalles de facturación y plan del cliente',
    subscriptionsPanelLabel: 'Cuentas y credenciales de proveedores de pago',
    researchPanelLabel: 'Operaciones de reportes de investigación vía Vantage',
    researchKicker: 'Vantage',
    researchPageLead: 'Abre un caso, examina al sujeto y entrega un PDF con fuentes. La cobertura y la cola quedan arriba, sin ocupar el trabajo.',
    researchHealthCompleted: 'Completados',
    researchIntakeIdentity: 'Identidad',
    researchIntakeContact: 'Contacto',
    researchIntakeContactHint: 'Identifican al sujeto. Las direcciones de entrega del PDF están en Configuración de búsqueda (engranaje en el encabezado).',
    researchIntakeIdentifiers: 'Identificadores',
    researchSubjectEmail: 'Correo del sujeto',
    researchSubjectEmailHint: 'Separados por coma. Sirven para encontrar a la persona o empresa, no para enviar el PDF.',
    researchPdfRecipients: 'Correos de entrega del PDF',
    researchEmailGroups: 'Grupos de correo',
    crmPanelLabel: 'Operaciones de CRM vía Pursuit',
    tipsPanelLabel: 'Detalles de gestión de consejos',
    stylePanelLabel: 'Detalles de gobierno de estilos',
    homePanelLabel: 'Detalles de misión y visión',
    accountSettings: 'Configuración de cuenta',
    securityAccess: 'Seguridad e inicio de sesión',
    billingPlan: 'Pagos',
    dashboards: 'Paneles',
    missionVision: 'Misión y visión',
    dashboardLiveBadge: 'En vivo',
    metricUnitRecords: 'registros',
    metricCaptionDataIndexed: 'Registros de datos indexados',
    metricCaptionSuccessRate: 'Tasa de éxito',
    metricCaptionComplianceScore: 'Puntuación de cumplimiento',
    metricCaptionAwaitingMetrics: 'Esperando métricas en vivo',
    metricLabelLastUpdated: 'Última actualización',
    metricLabelStatus: 'Estado',
    metricLabelResponseTime: 'Tiempo de respuesta',
    metricLabelSuccessRate: 'Tasa de éxito',
    metricLabelErrorRate: 'Tasa de error',
    metricLabelRiskLevel: 'Nivel de riesgo',
    metricLabelDataCount: 'Cantidad de datos',
    metricLabelCoverage: 'Cobertura',
    metricLabelCompliance: 'Cumplimiento',
    metricLabelEvidence: 'Evidencia',
    metricLabelGaps: 'Brechas',
    metricLabelWebsiteSignal: 'Señal del sitio web',
    metricLabelConfluenceSignal: 'Señal de política',
    metricLabelMetricsSource: 'Fuente de métricas requerida',
    metricLabelWebsiteScanCoverage: 'Cobertura de escaneo web',
    metricLabelWebsiteScanStatus: 'Estado de escaneo web',
    metricLabelCompliantRecords: 'Registros en cumplimiento',
    metricLabelNonCompliantRecords: 'Registros en incumplimiento',
    metricLabelTotalRequirements: 'Requisitos totales',
    metricLabelTermsReplayGate: 'Puerta de repetición de términos',
    metricLabelTermsPageAccessGate: 'Puerta de acceso/vinculación de términos',
    metricLabelContractUpdateGate: 'Puerta de aviso de actualización contractual',
    metricLabelAuthenticatedEntrypointGate: 'Puerta de asentimiento con entrada autenticada',
    metricLabelNegotiatedContractTermsGate: 'Puerta de ruta a términos contractuales negociados',
    metricLabelPublicTermsPageGate: 'Puerta de publicación pública de términos',
    metricLabelAssentReplay: 'Repetición de asentimiento',
    metricLabelAssentReplayEvents: 'Eventos de repetición de asentimiento',
    dashboardServiceFilterTitle: 'Visibilidad de servicios',
    dashboardFilterOfac: 'Servicios OFAC',
    dashboardFilterWebsiteCompliance: 'Servicios de cumplimiento del sitio web',
    dashboardFilterGovernmentalCompliance: 'Servicios de cumplimiento gubernamental',
    dashboardTagOfac: 'OFAC',
    dashboardTagWebsiteCompliance: 'Cumplimiento Web',
    dashboardTagGovernmentalCompliance: 'Cumplimiento Gubernamental',
    noVisibleDashboards: 'No hay paneles visibles con los filtros de servicio actuales.',
    homeIntroTitle: 'Opera con confianza a escala empresarial',
    homeIntroBody: 'NextCompli combina inteligencia de sanciones, controles operativos y flujos listos para implementación para que cumplimiento y ventas avancen rápido sin perder confianza.',
    missionTitle: 'Misión',
    missionBody: 'Hacer que los controles de sanciones y cumplimiento sean simples, rápidos y confiables para equipos en operación real.',
    visionTitle: 'Visión',
    visionBody: 'Ser la plataforma más fácil para que cualquier equipo se mantenga en cumplimiento, responda más rápido y esté listo para auditoría al crecer.',
    valuesTitle: 'Valores',
    valuesBody: 'Operar con precios transparentes, documentación clara, UX moderna y ejecución responsable para clientes, socios y reguladores.',
    securityTipsCardTitle: 'Tienes consejos de seguridad',
    securityTipsCardBody: 'Consejos encontrados en la revisión de seguridad.',
    securityRecentTitle: 'Actividad de seguridad reciente',
    securityRecentPrimary: 'Inicio de sesión en este dispositivo',
    securityRecentSecondary: 'Sesión actual',
    securityRecentAction: 'Revisar actividad de seguridad',
    securityHowSignInTitle: 'Cómo inicias sesión en NextCompli',
    securityHowSignInBody: 'Mantén actualizada la información de inicio y recuperación para acceder siempre a tu cuenta.',
    securityTwoStepTitle: 'Verificación en 2 pasos',
    securityTwoStepDetail: 'Activa desde 12 feb 2021',
    securityPasskeysTitle: 'Passkeys y llaves de seguridad',
    securityPasskeysDetail: '1 passkey',
    securityPasswordTitle: 'Contraseña',
    securityPasswordDetail: 'Último cambio: 13 dic 2020',
    securitySkipTitle: 'Omitir contraseña cuando sea posible',
    securitySkipDetail: 'Activado',
    securityPromptTitle: 'Google prompt',
    securityPromptDetail: '1 dispositivo',
    securityTwoStepPhonesTitle: 'Teléfonos de verificación en 2 pasos',
    securityTwoStepPhonesDetail: DEFAULT_SECURITY_RECOVERY_PHONE,
    securityRecoveryPhoneTitle: 'Teléfono de recuperación',
    securityRecoveryPhoneDetail: DEFAULT_SECURITY_RECOVERY_PHONE,
    securityRecoveryEmailTitle: 'Correo de recuperación',
    securityRecoveryEmailDetail: 'Verificar operations@nextcompli.com',
    securityBackupCodesTitle: 'Códigos de respaldo',
    securityBackupCodesDetail: '10 códigos disponibles',
    securityAddOptionsLabel: 'Puedes agregar más opciones de inicio de sesión',
    securityAddOptionContacts: 'Contactos de recuperación',
    securityAddOptionAuthenticator: 'Authenticator',
    securityAddOptionBackupPhones: 'Teléfonos de respaldo 2 pasos',
    securityAddOptionLinkedIds: 'IDs vinculados',
    securityDevicesTitleBlock: 'Tus dispositivos',
    securityDevicesSubtitle: 'Dónde iniciaste sesión',
    securityDeviceMacTitle: '4 sesiones en Mac',
    securityDeviceMacDetail: 'Dispositivos macOS',
    securityDeviceIphoneTitle: '5 sesiones en iPhone',
    securityDeviceIphoneDetail: 'Dispositivos iPhone',
    securityDeviceWindowsTitle: '2 sesiones en Windows',
    securityDeviceWindowsDetail: 'Dispositivos Windows',
    securityFindLostDevice: 'Buscar un dispositivo perdido',
    securityManageAllDevices: 'Administrar todos los dispositivos',
    securityFindLocation: 'Buscar ubicación',
    securityDeleteDevice: 'Eliminar dispositivo',
    securityDeviceAuthorized: 'Autenticado y autorizado',
    securityDeviceAccordionHint: 'Ver detalles de sesión del dispositivo',
    securityNoSessions: 'No hay sesiones activas para este tipo de dispositivo.',
    homeValueTitle: 'Por qué los equipos empresariales eligen NextCompli',
    homeValueOne: 'Acelera la implementación con onboarding API-first, validación en sandbox y patrones listos para producción.',
    homeValueTwo: 'Reduce el costo operativo de cumplimiento con precios transparentes, activación modular y flujos reutilizables.',
    homeValueThree: 'Mejora la velocidad de decisión con evaluación en tiempo real y por lotes, monitoreo continuo e historial centralizado.',
    homeValueFour: 'Despliega donde la política lo exija: local, Kubernetes o AWS, con una arquitectura y controles consistentes.',
    homeProofTitle: 'Resultados para líderes de cumplimiento',
    homeProofOne: 'Menor tiempo de valor desde piloto hasta producción con rutas de implementación estructuradas.',
    homeProofTwo: 'Menor riesgo de integración usando servicios componibles, orquestación por gateway y métricas observables.',
    homeProofThree: 'Mayor postura de confianza con resultados explicables y registros defendibles para revisiones y auditorías.',
    dashboardsCopy: 'Administra a qué paneles puede acceder esta cuenta.',
    subscribedDashboards: 'Paneles suscritos',
    noSubscribedDashboards: 'No hay suscripciones de paneles seleccionadas.',
    noServiceDashboards: 'No hay paneles de servicios en vivo disponibles actualmente.',
    noLiveMetrics: 'Aún no hay métricas en vivo para este servicio.',
    updateSubscriptions: 'Actualizar suscripciones',
    toggleDashboardSubscriptions: 'Alternar suscripciones de paneles',
    toggleSection: 'Alternar sección {section}',
    dashboardsSection: 'Sección de paneles',
    dashboardSubscriptions: 'Suscripciones de paneles',
    labelPersonUuid: 'UUID de persona',
    labelDisplayName: 'Nombre para mostrar',
    labelEmail: 'Correo',
    labelContactNumber: 'Número de contacto',
    labelLanguage: 'Idioma',
    labelTimezone: 'Zona horaria',
    appearanceTitle: 'Apariencia',
    appearanceHint: 'Estas preferencias se aplican de inmediato en este dispositivo. El tema sigue tu sistema por defecto.',
    labelTheme: 'Tema',
    themeSystem: 'Sistema',
    themeLight: 'Claro',
    themeDark: 'Oscuro',
    labelDensity: 'Densidad',
    densityComfortable: 'Cómoda',
    densityCompact: 'Compacta',
    labelReduceMotion: 'Reducir movimiento',
    reduceMotionSystem: 'Sistema',
    reduceMotionOn: 'Activado',
    reduceMotionOff: 'Desactivado',
    labelFontScale: 'Tamaño del texto',
    fontScaleSm: 'Pequeño',
    fontScaleMd: 'Predeterminado',
    fontScaleLg: 'Grande',
    appearanceResolvedTheme: 'Tema resuelto',
    labelPlan: 'Plan',
    labelRenewal: 'Renovación',
    valueLanguage: 'English',
    valueTimezone: 'UTC-5 (America/New_York)',
    valuePlan: 'Empresarial',
    valueRenewal: '31 ago 2026',
    settingsEditAria: 'Editar configuración de cuenta',
    settingsSaveAction: 'Guardar cambios',
    settingsCancelAction: 'Cancelar',
    settingsSaved: 'Configuración de cuenta actualizada correctamente.',
    settingsDisplayNameRequired: 'El nombre para mostrar es obligatorio.',
    settingsEmailInvalid: 'Ingresa un correo válido.',
    settingsAvatarLabel: 'Foto de perfil',
    settingsAvatarHint: 'Sube una imagen JPG, PNG o WEBP de hasta 5MB. Las imágenes grandes se redimensionan automáticamente.',
    settingsAvatarRemoveAction: 'Eliminar foto',
    settingsAvatarInvalidType: 'Selecciona una imagen válida (JPG, PNG o WEBP).',
    settingsAvatarTooLarge: 'La imagen debe ser de 5MB o menos.',
    settingsAvatarUpdated: 'Foto de perfil actualizada. Guarda para aplicar.',
    settingsAvatarRemoved: 'Foto de perfil eliminada. Guarda para aplicar.',
      settingsContactNumberHint: 'Formato EE.UU.: (555) 555-5555  |  Internacional: +1 555 555 5555',
      settingsContactNumberInvalid: 'Ingresa un número de teléfono válido, por ej. (555) 555-5555 o +1 555 555 5555.',
    settingsAvatarAlt: 'Foto de perfil',
    securityNoTipsTitle: 'No se encontraron consejos de seguridad',
    securityNoTipsBody: 'No existen consejos de seguridad en este momento.',
    securityTipsFoundBody: 'El análisis de seguridad encontró recomendaciones para mejorar la protección de tu cuenta.',
    securityTipEnable2FA: 'Activa la verificación en 2 pasos para reforzar la protección de inicio de sesión.',
    securityTipAddPasskey: 'Agrega al menos una passkey para reducir el riesgo de acceso solo con contraseña.',
    securityTipVerifyRecoveryEmail: 'Verifica tu correo de recuperación para evitar bloqueos al recuperar la cuenta.',
    securityTipRefreshBackupCodes: 'Regenera los códigos de respaldo para tener códigos de emergencia disponibles.',
    securityActivityReportTitle: 'Reporte de actividad de seguridad',
    securityActivitySafeSummary: 'No se detectaron inicios de sesión sospechosos en las revisiones recientes de la cuenta.',
    securityActivityWarningSummary: 'Revisa las actualizaciones de seguridad recomendadas en las revisiones recientes de la cuenta.',
    securityActivityLine2fa: 'Estado de verificación en 2 pasos',
    securityActivityLinePasskey: 'Estado de passkeys',
    securityActivityLineRecoveryEmail: 'Estado del correo de recuperación',
    securityActivityLineBackupCodes: 'Códigos de respaldo restantes',
    secMfa: 'La verificación en dos pasos está activada en esta cuenta.',
    secRotation: 'Política de rotación de contraseña: 90 días',
    secSessions: 'Sesiones activas: 2',
    secTokens: 'Tokens de API: 1 activo',
    securityOverviewCopy: 'Revisa recomendaciones y protecciones de inicio de sesión en un solo lugar.',
    securityRecommendationsTitle: 'Hay recomendaciones de seguridad disponibles',
    securityRecommendationsBody: 'Completa acciones sugeridas para mejorar la seguridad de la cuenta.',
    securityActivityTitle: 'Actividad de seguridad reciente',
    securityActivityBody: 'No se detectaron inicios de sesión sospechosos en los últimos 28 días.',
    securitySignInTitle: 'Inicio de sesión y recuperación',
    securitySignInBody: 'Administra contraseña, verificación en dos pasos y métodos de recuperación.',
    securityDevicesTitle: 'Tus dispositivos y sesiones',
    securityDevicesBody: 'Revisa dispositivos activos y revoca acceso cuando sea necesario.',
    securityReviewAction: 'Revisar',
    securityManageAction: 'Administrar',
    paymentsOpenAction: 'Abrir página',
      paymentsOverviewCopy: 'Administra métodos de pago, facturas y tu plan de NextCompli en esta página.',
      paymentsMethodTitle: 'Administrar métodos de pago',
      paymentsMethodBody: 'Actualiza datos de tarjeta y preferencias de facturación.',
      paymentsInfoTitle: 'Información de pagos',
      paymentsInfoBody: 'Revisa transacciones y facturas vinculadas a esta cuenta.',
      paymentsSubscriptionsTitle: 'Plan de NextCompli',
      paymentsSubscriptionsBody: 'Consulta tu plan y administra suscripciones de Stripe.',
      paymentsSearchAction: 'Buscar cuenta',
      paymentsHelpAction: 'Obtener ayuda',
    twoFactorTitle: 'Autenticación de dos factores',
    twoFactorEnabled: 'La autenticación 2FA está habilitada para esta cuenta.',
    twoFactorDisabled: 'La autenticación 2FA está deshabilitada.',
    twoFactorEnableAction: 'Habilitar 2FA',
    twoFactorDisableAction: 'Deshabilitar 2FA',
    passwordTitle: 'Cambiar contraseña',
    passwordCurrent: 'Contraseña actual',
    passwordNew: 'Nueva contraseña',
    passwordConfirm: 'Confirmar nueva contraseña',
    passwordSaveAction: 'Actualizar contraseña',
    passwordSuccess: 'Contraseña actualizada correctamente.',
    passwordMismatch: 'La nueva contraseña y la confirmación deben coincidir.',
    passwordLength: 'La nueva contraseña debe tener al menos 12 caracteres.',
    passwordRequirements: 'Usa al menos 12 caracteres. Muestra la contraseña para verificar; se permite pegar desde un gestor de contraseñas.',
    passwordInvalidCurrent: 'La contraseña actual es incorrecta.',
    passwordUpdating: 'Actualizando contraseña…',
    passwordShow: 'Mostrar',
    passwordHide: 'Ocultar',
    notificationsEmailTitle: 'Alertas por correo',
    notificationsEmailLabel: 'Correo receptor de alertas',
    notificationsEmailAction: 'Guardar alertas por correo',
    notificationsEmailSaved: 'Suscripciones de alertas guardadas.',
    notificationsServiceChecklist: 'Lista de servicios suscritos a alertas',
    notificationsEmptyServices: 'Aún no hay servicios disponibles para alertas por correo.',
    privacyDisclosuresTitle: 'Política de datos y privacidad',
    privacyDisclaimerTitle: 'Descargos de responsabilidad',
    privacyDisclaimerBody: 'Este resumen refleja lenguaje de política de privacidad para uso de cuenta en NextCompli y no reemplaza asesoría legal formal.',
    privacyDisclosureOne: 'NextCompli recopila información de cuenta, facturación, dispositivo y uso para prestar servicios, proteger accesos, mejorar el producto y comunicar actualizaciones importantes.',
    privacyDisclosureTwo: 'NextCompli puede procesar y compartir datos con procesadores confiables, socios de infraestructura y autoridades cuando sea necesario para prestación, cumplimiento, prevención de fraude u obligaciones legales.',
    privacyDisclosureThree: 'Los usuarios pueden solicitar acceso, corrección, eliminación, exportación y actualización de consentimiento cuando corresponda; los plazos de retención dependen de requisitos legales, contractuales y de seguridad.',
    privacySourceOne: 'Fuente: Política de privacidad de NextCompli (formato adaptado)',
    privacySourceTwo: 'Fuente: Términos y compromisos de seguridad y cumplimiento de NextCompli',
    privacySourceThree: 'Fuente: Marcos regulatorios y derechos regionales de privacidad aplicables',
    notifCritical: 'Alertas críticas de estado del servicio',
    notifRisk: 'Alertas de umbral de riesgo de cumplimiento',
    notifWeekly: 'Resumen semanal de operaciones',
    notifRelease: 'Nuevas integraciones y anuncios de versiones',
  },
};

type DashboardProps = {
  initialNavItem?: DashboardNavItemId;
};

const Dashboard: React.FC<DashboardProps> = ({ initialNavItem = 'home' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, accessToken, changePassword, logout, login, refreshProfile } = useAuth();
  const [language, setLanguage] = React.useState<DashboardLanguage>(() => getStoredDashboardLanguage());
  const copy = DASHBOARD_COPY[language];

  React.useEffect(() => {
    const syncLanguage = () => {
      setLanguage(getStoredDashboardLanguage());
    };

    window.addEventListener('storage', syncLanguage);
    window.addEventListener('nextcompli-language-change', syncLanguage as EventListener);

    return () => {
      window.removeEventListener('storage', syncLanguage);
      window.removeEventListener('nextcompli-language-change', syncLanguage as EventListener);
    };
  }, []);

  const [accountProfile, setAccountProfile] = React.useState<AccountProfile>(() => {
    const savedProfile = window.localStorage.getItem(ACCOUNT_PROFILE_STORAGE_KEY);
    if (!savedProfile) {
      return DEFAULT_ACCOUNT_PROFILE;
    }

    try {
      const parsed = JSON.parse(savedProfile) as Partial<AccountProfile> & { name?: string };
      return {
        personUuid: parsed.personUuid ?? generatePersonUuid(),
        displayName: parsed.displayName ?? parsed.name ?? DEFAULT_ACCOUNT_PROFILE.displayName,
        email: parsed.email ?? DEFAULT_ACCOUNT_PROFILE.email,
        contactNumber: parsed.contactNumber ?? DEFAULT_ACCOUNT_CONTACT_PHONE,
        language: parsed.language ?? DEFAULT_ACCOUNT_PROFILE.language,
        timezone: parsed.timezone ?? DEFAULT_ACCOUNT_PROFILE.timezone,
      };
    } catch {
      return DEFAULT_ACCOUNT_PROFILE;
    }
  });

  const [isEditingProfile, setIsEditingProfile] = React.useState(false);
  const [appearancePreferences, setAppearancePreferences] = React.useState<AppearancePreferences>(() => loadAppearancePreferences());

  React.useEffect(() => {
    const syncAppearance = (event?: Event) => {
      const detail = (event as CustomEvent<AppearancePreferences> | undefined)?.detail;
      setAppearancePreferences(detail ? { ...detail } : loadAppearancePreferences());
    };
    window.addEventListener(APPEARANCE_UPDATED_EVENT, syncAppearance as EventListener);
    window.addEventListener('storage', syncAppearance);
    return () => {
      window.removeEventListener(APPEARANCE_UPDATED_EVENT, syncAppearance as EventListener);
      window.removeEventListener('storage', syncAppearance);
    };
  }, []);

  const updateAppearancePreference = React.useCallback(<K extends keyof AppearancePreferences>(
    key: K,
    value: AppearancePreferences[K],
  ) => {
    setAppearancePreferences((current) => saveAppearancePreferences({
      ...current,
      [key]: value,
    }));
  }, []);
  const [profileDraft, setProfileDraft] = React.useState<AccountProfile>(accountProfile);
  const [avatarByUuid, setAvatarByUuid] = React.useState<AccountAvatarMap>(() => {
    const savedAvatarMap = window.localStorage.getItem(ACCOUNT_AVATAR_STORAGE_KEY);
    if (!savedAvatarMap) {
      return {};
    }

    try {
      const parsed = JSON.parse(savedAvatarMap) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return Object.fromEntries(
          Object.entries(parsed as Record<string, unknown>).filter(([, value]) => typeof value === 'string'),
        );
      }
      return {};
    } catch {
      return {};
    }
  });
  const [avatarDraftDataUrl, setAvatarDraftDataUrl] = React.useState<string | null>(null);
  const [profileError, setProfileError] = React.useState<string | null>(null);
  const [profileFeedback, setProfileFeedback] = React.useState<string | null>(null);

  const accountEmail = accountProfile.email;
  const accountDisplayName = accountProfile.displayName;
  const accountInitials = accountDisplayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'AN';
  const accountAvatar = avatarByUuid[accountProfile.personUuid] ?? null;
  const displayAvatar = isEditingProfile ? avatarDraftDataUrl : accountAvatar;
  const [staffNavRevoked, setStaffNavRevoked] = React.useState(false);
  const hasPlatformAdminAccess = isNextCompliAdmin(user) && !staffNavRevoked;
  const denyStaffNav = React.useCallback(() => {
    setStaffNavRevoked(true);
    void refreshProfile();
    navigate('/console/my-subscriptions', { replace: true });
  }, [navigate, refreshProfile]);
  // Operator tools (CRM, cost, socials, Access Management/RBAC) stay platform-only.
  const hasSuperUserAccess = hasPlatformAdminAccess;
  const researchAccessActor = React.useMemo<ResearchAccessActor>(() => ({
    userId: user?.user_id || null,
    email: user?.email || accountEmail || null,
    companyId: user?.companies?.[0]?.company_id ?? user?.effective_company_id ?? null,
    isPlatformAdmin: hasPlatformAdminAccess,
  }), [accountEmail, hasPlatformAdminAccess, user]);
  const stampResearchReportOwnership = React.useCallback((report: ResearchReportRecord): ResearchReportRecord => ({
    ...report,
    ownerUserId: report.ownerUserId || researchAccessActor.userId || undefined,
    ownerEmail: report.ownerEmail || researchAccessActor.email || undefined,
    companyId: report.companyId != null
      ? report.companyId
      : (researchAccessActor.companyId != null ? Number(researchAccessActor.companyId) : null),
  }), [researchAccessActor]);
  const effectiveFeatureList = React.useMemo(() => {
    const fromProfile = [...(user?.effective_features || [])];
    const fromCompanies = (user?.companies || []).flatMap((company) => company.features || []);
    return [...fromProfile, ...fromCompanies];
  }, [user]);
  const canUseEntitledProduct = React.useCallback(
    (entitlement: string) =>
      canAccessEntitledProductNav({
        isPlatformAdmin: hasPlatformAdminAccess,
        permissions: user?.effective_permissions,
        entitlement,
        companyHasEntitlement: hasComplianceEntitlement(effectiveFeatureList, entitlement, {
          isPlatformAdmin: hasPlatformAdminAccess,
        }),
      }),
    [effectiveFeatureList, hasPlatformAdminAccess, user?.effective_permissions],
  );
  const canAccessWebsiteCompliance = React.useMemo(
    () => canUseEntitledProduct(WEBSITE_COMPLIANCE_CONSOLE.entitlement),
    [canUseEntitledProduct],
  );
  const canAccessCloudCompliance = React.useMemo(
    () => canUseEntitledProduct(CLOUD_COMPLIANCE_CONSOLE.entitlement),
    [canUseEntitledProduct],
  );
  const canAccessDodContracts = React.useMemo(
    () => canUseEntitledProduct(DOD_CONTRACTS_COMPLIANCE_CONSOLE.entitlement),
    [canUseEntitledProduct],
  );
  const canAccessEducationCompliance = React.useMemo(
    () => canUseEntitledProduct(EDUCATION_COMPLIANCE_CONSOLE.entitlement),
    [canUseEntitledProduct],
  );
  const canAccessAiCompliance = React.useMemo(
    () => canUseEntitledProduct(AI_COMPLIANCE_CONSOLE.entitlement),
    [canUseEntitledProduct],
  );
  const canAccessBusinessFormation = React.useMemo(
    () => canUseEntitledProduct(BUSINESS_CREATION_COMPLIANCE_CONSOLE.entitlement)
      || canAccessWebsiteCompliance
      || canAccessDodContracts
      || canAccessEducationCompliance,
    [canAccessDodContracts, canAccessEducationCompliance, canAccessWebsiteCompliance, canUseEntitledProduct],
  );
  const canAccessWillsTrusts = React.useMemo(
    () => canUseEntitledProduct(WILLS_TRUSTS_COMPLIANCE_CONSOLE.entitlement) || canAccessBusinessFormation,
    [canAccessBusinessFormation, canUseEntitledProduct],
  );
  const canAccessAudits = React.useMemo(
    () => canUseEntitledProduct('case-audit-workspace') || canUseEntitledProduct('scheduled-audits'),
    [canUseEntitledProduct],
  );
  const canAccessResearch = React.useMemo(
    () => canUseEntitledProduct('research-reports'),
    [canUseEntitledProduct],
  );
  const canAccessCompliDesk = React.useMemo(
    () => canUseEntitledProduct('research-reports'),
    [canUseEntitledProduct],
  );
  const canAccessOfac = React.useMemo(
    () => canUseEntitledProduct('ofac-compliance'),
    [canUseEntitledProduct],
  );
  const canAccessCrm = React.useMemo(
    () =>
      canAccessCrmNav({
        isPlatformAdmin: hasPlatformAdminAccess,
        permissions: user?.effective_permissions,
      }),
    [hasPlatformAdminAccess, user?.effective_permissions],
  );
  const canAccessInlet = React.useMemo(
    () =>
      canAccessInletNav({
        isPlatformAdmin: hasPlatformAdminAccess,
        permissions: user?.effective_permissions,
      }),
    [hasPlatformAdminAccess, user?.effective_permissions],
  );
  const canAccessRazzmatazz = React.useMemo(
    () =>
      canAccessRazzmatazzNav({
        isPlatformAdmin: hasPlatformAdminAccess,
        permissions: user?.effective_permissions,
      }),
    [hasPlatformAdminAccess, user?.effective_permissions],
  );
  const canAccessContinuousMonitoring = React.useMemo(
    () => canUseEntitledProduct('continuous-monitoring'),
    [canUseEntitledProduct],
  );
  const canAccessRealtimeBatchScreening = React.useMemo(
    () => canUseEntitledProduct('real-time-batch-screening'),
    [canUseEntitledProduct],
  );

  const [isKeycloakAdmin, setIsKeycloakAdmin] = React.useState(false);

  React.useEffect(() => {
    if (!hasSuperUserAccess || !accessToken) {
      setIsKeycloakAdmin(false);
      return;
    }
    const gatewayUrl = inferGatewayBaseUrl();
    const controller = new AbortController();
    const checkKeycloakAdmin = async () => {
      try {
        const response = await fetch(`${gatewayUrl}/api/v1/auth/keycloak-admin-status`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: controller.signal,
        });
        if (!response.ok) { return; }
        const payload = (await response.json()) as { is_keycloak_admin?: boolean };
        setIsKeycloakAdmin(payload.is_keycloak_admin === true);
      } catch {
        // Network error or aborted — leave state as false.
      }
    };
    void checkKeycloakAdmin();
    return () => { controller.abort(); };
  }, [hasSuperUserAccess, accessToken]);

  type AccountHomeItem = { item_id: string; status: string; owner: string; steps_done?: string[] };
  type AccountHomeConsole = {
    console_kind: string;
    target_company_name: string;
    items: AccountHomeItem[];
    business_creation?: Record<string, string>;
    wills_trusts?: Record<string, string>;
  };
  type AccountHomeAccount = {
    company_id: number;
    company_name: string;
    projects: Array<{ project_id: string; project_name: string; console_kind?: string }>;
    consoles: AccountHomeConsole[];
  };
  const [accountHome, setAccountHome] = React.useState<{ email: string; accounts: AccountHomeAccount[] } | null>(null);
  const [homeCalendarOpen, setHomeCalendarOpen] = React.useState(false);
  const [homeLayout, setHomeLayout] = React.useState<DashboardLayout>(() => readLayout('home', HOME_DASHBOARD_WIDGETS));
  const homeWidgetsHidden = React.useMemo(() => new Set(homeLayout.hidden), [homeLayout]);
  const commitHomeLayout = React.useCallback((layout: DashboardLayout) => {
    const next = normalizeLayout(HOME_DASHBOARD_WIDGETS, layout);
    writeLayout('home', next);
    setHomeLayout(next);
    if (!accessToken) {
      return;
    }
    void writeAccountPreference(inferGatewayBaseUrl(), accessToken, 'dashboard-layout:home', next);
  }, [accessToken]);
  const [homeSectionId, setHomeSectionId] = React.useState('');
  const [homeTaskSort, setHomeTaskSort] = React.useState<'dependencies' | 'due' | 'unlocks' | 'blocking'>('dependencies');
  const [homeStatusFilter, setHomeStatusFilter] = React.useState<'any' | 'not_started' | 'in_progress' | 'reviewed' | 'gap' | 'dated'>('any');
  const [homeTask, setHomeTask] = React.useState<{
    consoleKind: string;
    itemId: string;
    title: string;
    category: string;
    companyId: number;
    companyName: string;
    projectId: string;
    projectName: string;
  } | null>(null);
  const [homeReload, setHomeReload] = React.useState(0);
  const [outsideCheckTick, setOutsideCheckTick] = React.useState(0);
  const [agentAwaitingCount, setAgentAwaitingCount] = React.useState(0);
  const closeHomeTask = React.useCallback(() => {
    setHomeTask(null);
    setHomeReload((value) => value + 1);
  }, []);
  React.useEffect(() => {
    if (!homeTask) {
      return undefined;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeHomeTask();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeHomeTask, homeTask]);

  React.useEffect(() => {
    if (!accessToken || !canAccessContinuousMonitoring) {
      setAgentAwaitingCount(0);
      return undefined;
    }
    const gatewayUrl = inferGatewayBaseUrl();
    const controller = new AbortController();
    const loadAgentQueue = async () => {
      try {
        const response = await fetch(`${gatewayUrl}/api/v1/agent/monitor`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: controller.signal,
        });
        if (!response.ok) {
          setAgentAwaitingCount(0);
          return;
        }
        const body = await response.json() as { awaiting_count?: number };
        setAgentAwaitingCount(Math.max(0, Number(body.awaiting_count || 0)));
      } catch {
        if (!controller.signal.aborted) {
          setAgentAwaitingCount(0);
        }
      }
    };
    void loadAgentQueue();
    return () => controller.abort();
  }, [accessToken, canAccessContinuousMonitoring, homeReload]);

  React.useEffect(() => {
    if (!accessToken) {
      setAccountHome(null);
      return;
    }
    const gatewayUrl = inferGatewayBaseUrl();
    const controller = new AbortController();
    const loadHome = async () => {
      try {
        const response = await fetch(`${gatewayUrl}/api/v1/account/home`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: controller.signal,
        });
        if (!response.ok) {
          return;
        }
        const payload = await response.json() as { email?: string; accounts?: AccountHomeAccount[] };
        setAccountHome({
          email: String(payload.email || user?.email || '').trim().toLowerCase(),
          accounts: Array.isArray(payload.accounts) ? payload.accounts : [],
        });
      } catch {
        /* leave the last server snapshot */
      }
    };
    void loadHome();
    return () => { controller.abort(); };
  }, [accessToken, homeReload, user?.email]);

  React.useEffect(() => {
    if (!accessToken) {
      return undefined;
    }
    let cancelled = false;
    const loadLayout = async () => {
      const result = await readAccountPreference(inferGatewayBaseUrl(), accessToken, 'dashboard-layout:home');
      if (cancelled || !result.ok) {
        return;
      }
      if (result.value && typeof result.value === 'object') {
        const next = normalizeLayout(HOME_DASHBOARD_WIDGETS, result.value);
        writeLayout('home', next);
        setHomeLayout(next);
        return;
      }
      void writeAccountPreference(
        inferGatewayBaseUrl(),
        accessToken,
        'dashboard-layout:home',
        readLayout('home', HOME_DASHBOARD_WIDGETS),
      );
    };
    void loadLayout();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const [tipOfDayState, setTipOfDayState] = React.useState<TipOfDayState>(() => getStoredTipOfDayState());
  const [tipCatalog, setTipCatalog] = React.useState<TipOfDayDefinition[]>(() => getStoredTipCatalog());
  const todayDateKey = getDateKey();
  const todaysTip = React.useMemo(() => {
    if (hasSuperUserAccess) {
      return null;
    }

    if (
      tipOfDayState.dismissedUntilDate
      && compareDateKeys(todayDateKey, tipOfDayState.dismissedUntilDate) <= 0
    ) {
      return null;
    }

    const eligibleTips = tipCatalog.filter((tip) => tip.audience === 'non_super_admin');
    if (eligibleTips.length === 0) {
      return null;
    }

    // If we already selected a tip for today, return it directly to prevent
    // the useEffect update from triggering a re-selection (flip-flop bug).
    if (tipOfDayState.lastShownDate === todayDateKey && tipOfDayState.lastTipId) {
      const alreadySelected = eligibleTips.find((tip) => tip.id === tipOfDayState.lastTipId);
      if (alreadySelected) {
        return alreadySelected;
      }
    }

    const seedIndex = getTipDaySeed(todayDateKey) % eligibleTips.length;
    let selected = eligibleTips[seedIndex];

    // Avoid repeating the same tip from the previous day.
    if (
      eligibleTips.length > 1
      && tipOfDayState.lastShownDate !== todayDateKey
      && tipOfDayState.lastTipId
      && selected.id === tipOfDayState.lastTipId
    ) {
      selected = eligibleTips[(seedIndex + 1) % eligibleTips.length];
    }

    return selected;
  }, [hasSuperUserAccess, tipCatalog, tipOfDayState.dismissedUntilDate, tipOfDayState.lastShownDate, tipOfDayState.lastTipId, todayDateKey]);

  React.useEffect(() => {
    if (!todaysTip) {
      return;
    }

    if (tipOfDayState.lastShownDate === todayDateKey && tipOfDayState.lastTipId === todaysTip.id) {
      return;
    }

    setTipOfDayState((current) => {
      const next: TipOfDayState = {
        ...current,
        lastShownDate: todayDateKey,
        lastTipId: todaysTip.id,
      };
      saveTipOfDayState(next);
      return next;
    });
  }, [todaysTip, tipOfDayState.lastShownDate, tipOfDayState.lastTipId, todayDateKey]);

  React.useEffect(() => {
    if (!user?.email) {
      return;
    }
    const session = buildCurrentDeviceSession(user.email);
    const group = detectCurrentDeviceGroup();
    setDeviceSessionsByGroup({
      mac: group === 'mac' ? [session] : [],
      iphone: group === 'iphone' ? [session] : [],
      windows: group === 'windows' ? [session] : [],
    });
    setRecoveryEmail((current) => current || user.email);
    setSecurityRecoveryEmailDraft((current) => current || user.email);
  }, [user?.email]);

  React.useEffect(() => {
    if (!user?.email) {
      return;
    }

    const normalizedUserEmail = user.email.trim().toLowerCase();
    setAccountProfile((current) => {
      const normalizedCurrentEmail = current.email.trim().toLowerCase();
      if (normalizedCurrentEmail === normalizedUserEmail) {
        return current;
      }

      const nextProfile: AccountProfile = {
        ...current,
        personUuid: user.user_id || current.personUuid,
        displayName: formatDisplayNameFromEmail(user.email),
        email: user.email,
      };
      setProfileDraft(nextProfile);
      return nextProfile;
    });
  }, [user]);

  // Subscriber self-service nav. Operator-only items (Paid vendors, RBAC/Team) stay off this list.
  // CRM via Pursuit is a product surface like Research via Vantage.
  // Privacy policy lives in the site footer (/privacy), not the product console sidebar.
  const hasOwnManagedMailbox = Boolean(user?.managed_mailbox_email);
  const canManageSubscriptionTeam = React.useMemo(() => {
    // The menu item is on the main menu for a subscription admin and for a
    // NextCompli admin. The job name stays company_admin; opening Team does
    // not turn the operator into that job.
    if (hasPlatformAdminAccess) {
      return true;
    }
    const membershipRoles = (user?.companies || []).flatMap((company) => company.roles || []);
    const roles = new Set(
      [...membershipRoles, ...(user?.effective_roles || [])].map((item) => String(item || '').toLowerCase()),
    );
    return roles.has('company_admin');
  }, [hasPlatformAdminAccess, user?.companies, user?.effective_roles]);
  const canManageBilling = React.useMemo(
    () =>
      canManageBillingNav({
        isPlatformAdmin: hasPlatformAdminAccess,
        permissions: user?.effective_permissions,
      }),
    [hasPlatformAdminAccess, user?.effective_permissions],
  );
  const canWriteWorkspace = React.useMemo(
    () =>
      canWriteWorkspaceNav({
        isPlatformAdmin: hasPlatformAdminAccess,
        permissions: user?.effective_permissions,
      }),
    [hasPlatformAdminAccess, user?.effective_permissions],
  );
  const subscriberBillingLabel = language === 'es' ? 'Facturación' : 'Billing';
  const subscriberSideNavItems = React.useMemo(() => {
    const items = [
      { id: 'home' as DashboardNavItemId, label: copy.navHome, icon: 'home' as const, tone: 'tone-blue' },
      { id: 'personal' as DashboardNavItemId, label: copy.navPersonal, icon: 'personal' as const, tone: 'tone-green' },
      { id: 'mySubscriptions' as DashboardNavItemId, label: 'mySubscriptions', icon: 'subscriptions' as const, tone: 'tone-sky' },
      { id: 'security' as DashboardNavItemId, label: copy.navSecurity, icon: 'security' as const, tone: 'tone-cyan' },
      ...(canManageSubscriptionTeam
        ? [{ id: 'team' as DashboardNavItemId, label: copy.navTeam, icon: 'personal' as const, tone: 'tone-indigo' }]
        : []),
      // Billing is Admin-only (Stripe portal / payment methods).
      ...(canManageBilling
        ? [{ id: 'payments' as DashboardNavItemId, label: subscriberBillingLabel, icon: 'payments' as const, tone: 'tone-orange' }]
        : []),
      ...(canAccessCrm
        ? [{ id: 'crm' as DashboardNavItemId, label: copy.navCrm, icon: 'crm' as const, tone: 'tone-indigo' }]
        : []),
      ...(canAccessInlet
        ? [{ id: 'inlet' as DashboardNavItemId, label: copy.navInlet, icon: 'inlet' as const, tone: 'tone-indigo' }]
        : []),
      ...(canAccessRazzmatazz
        ? [{ id: 'razzmatazz' as DashboardNavItemId, label: copy.navRazzmatazz, icon: 'razzmatazz' as const, tone: 'tone-rose' }]
        : []),
      ...(canAccessOfac
        ? [
          { id: 'ofacCompliance' as DashboardNavItemId, label: copy.navOfacCompliance, icon: 'ofac' as const, tone: 'tone-cyan' },
          { id: 'ofacDashboards' as DashboardNavItemId, label: copy.navOfacDashboards, icon: 'dashboards' as const, tone: 'tone-cyan' },
        ]
        : []),
      ...(canAccessWebsiteCompliance
        ? [{ id: 'websiteCompliance' as DashboardNavItemId, label: copy.navWebsiteCompliance, icon: 'websiteCompliance' as const, tone: 'tone-blue' }]
        : []),
      ...(canAccessCloudCompliance
        ? [{ id: 'cloudCompliance' as DashboardNavItemId, label: copy.navCloudCompliance, icon: 'cloudCompliance' as const, tone: 'tone-blue' }]
        : []),
      ...(canAccessDodContracts
        ? [{ id: 'dodContracts' as DashboardNavItemId, label: copy.navDodContracts, icon: 'dodContracts' as const, tone: 'tone-indigo' }]
        : []),
      ...(canAccessEducationCompliance
        ? [{ id: 'educationCompliance' as DashboardNavItemId, label: copy.navEducationCompliance, icon: 'educationCompliance' as const, tone: 'tone-green' }]
        : []),
      ...(canAccessAiCompliance
        ? [{ id: 'aiCompliance' as DashboardNavItemId, label: copy.navAiCompliance, icon: 'aiCompliance' as const, tone: 'tone-indigo' }]
        : []),
      ...(canAccessBusinessFormation
        ? [{ id: 'businessFormation' as DashboardNavItemId, label: copy.navBusinessFormation, icon: 'businessFormation' as const, tone: 'tone-indigo' }]
        : []),
      ...(canAccessWillsTrusts
        ? [{ id: 'willsTrusts' as DashboardNavItemId, label: copy.navWillsTrusts, icon: 'willsTrusts' as const, tone: 'tone-indigo' }]
        : []),
      { id: 'complianceCalendar' as DashboardNavItemId, label: language === 'es' ? 'Calendario de cumplimiento' : 'Compliance Calendar', icon: 'complianceCalendar' as const, tone: 'tone-blue' },
      ...(canAccessContinuousMonitoring
        ? [{ id: 'agent' as DashboardNavItemId, label: copy.navAgent, icon: 'alerts' as const, tone: 'tone-cyan' }]
        : []),
      ...(canAccessAudits
        ? [{ id: 'audits' as DashboardNavItemId, label: language === 'es' ? 'Auditorías' : 'Audits', icon: 'audits' as const, tone: 'tone-purple' }]
        : []),
      ...(canAccessResearch
        ? [
          { id: 'research' as DashboardNavItemId, label: copy.navResearch, icon: 'research' as const, tone: 'tone-green' },
          { id: 'complidesk' as DashboardNavItemId, label: 'CompliDesk', icon: 'complidesk' as const, tone: 'tone-cyan' },
        ]
        : []),
      ...(hasOwnManagedMailbox
        ? [{ id: 'emailInbox' as DashboardNavItemId, label: language === 'es' ? 'Correo' : 'Mail', icon: 'emailInbox' as const, tone: 'tone-cyan' }]
        : []),
    ];
    return items;
  }, [
    canAccessAudits,
    canAccessCrm,
    canAccessInlet,
    canAccessRazzmatazz,
    canAccessDodContracts,
    canAccessEducationCompliance,
    canAccessAiCompliance,
    canAccessBusinessFormation,
    canAccessWillsTrusts,
    canAccessContinuousMonitoring,
    copy.navAgent,
    canAccessOfac,
    canAccessResearch,
    canAccessWebsiteCompliance,
    canAccessCloudCompliance,
    canManageBilling,
    canManageSubscriptionTeam,
    copy.navCrm,
    copy.navInlet,
    copy.navDodContracts,
    copy.navEducationCompliance,
    copy.navAiCompliance,
    copy.navBusinessFormation,
    copy.navWillsTrusts,
    copy.navHome,
    copy.navOfacCompliance,
    copy.navOfacDashboards,
    copy.navOfacGroup,
    copy.navPersonal,
    copy.navResearch,
    copy.navSecurity,
    copy.navTeam,
    copy.navWebsiteCompliance,
    copy.navCloudCompliance,
    hasOwnManagedMailbox,
    language,
    subscriberBillingLabel,
  ]);
  const baseSideNavItems = [
    { id: 'home' as DashboardNavItemId, label: copy.navHome, icon: 'home' as const, tone: 'tone-blue' },
    { id: 'dashboards' as DashboardNavItemId, label: copy.navDashboards, icon: 'dashboards' as const, tone: 'tone-blue' },
    { id: 'personal' as DashboardNavItemId, label: copy.navPersonal, icon: 'personal' as const, tone: 'tone-green' },
    { id: 'security' as DashboardNavItemId, label: copy.navSecurity, icon: 'security' as const, tone: 'tone-cyan' },
    { id: 'payments' as DashboardNavItemId, label: copy.navPayments, icon: 'payments' as const, tone: 'tone-orange' },
    { id: 'billing' as DashboardNavItemId, label: copy.navBilling, icon: 'billing' as const, tone: 'tone-orange' },
  ];
  const complianceSideNavItems = [
    ...(canAccessOfac
      ? [
        { id: 'ofacCompliance' as DashboardNavItemId, label: copy.navOfacCompliance, icon: 'ofac' as const, tone: 'tone-cyan' },
        { id: 'ofacDashboards' as DashboardNavItemId, label: copy.navOfacDashboards, icon: 'dashboards' as const, tone: 'tone-cyan' },
      ]
      : []),
    ...(canAccessWebsiteCompliance
      ? [{ id: 'websiteCompliance' as DashboardNavItemId, label: copy.navWebsiteCompliance, icon: 'websiteCompliance' as const, tone: 'tone-blue' }]
      : []),
    ...(canAccessCloudCompliance
      ? [{ id: 'cloudCompliance' as DashboardNavItemId, label: copy.navCloudCompliance, icon: 'cloudCompliance' as const, tone: 'tone-blue' }]
      : []),
    ...(canAccessDodContracts
      ? [{ id: 'dodContracts' as DashboardNavItemId, label: copy.navDodContracts, icon: 'dodContracts' as const, tone: 'tone-indigo' }]
      : []),
    ...(canAccessEducationCompliance
      ? [{ id: 'educationCompliance' as DashboardNavItemId, label: copy.navEducationCompliance, icon: 'educationCompliance' as const, tone: 'tone-green' }]
      : []),
    ...(canAccessAiCompliance
      ? [{ id: 'aiCompliance' as DashboardNavItemId, label: copy.navAiCompliance, icon: 'aiCompliance' as const, tone: 'tone-indigo' }]
      : []),
    ...(canAccessBusinessFormation
      ? [{ id: 'businessFormation' as DashboardNavItemId, label: copy.navBusinessFormation, icon: 'businessFormation' as const, tone: 'tone-indigo' }]
      : []),
    ...(canAccessWillsTrusts
      ? [{ id: 'willsTrusts' as DashboardNavItemId, label: copy.navWillsTrusts, icon: 'willsTrusts' as const, tone: 'tone-indigo' }]
      : []),
    { id: 'complianceCalendar' as DashboardNavItemId, label: language === 'es' ? 'Calendario de cumplimiento' : 'Compliance Calendar', icon: 'complianceCalendar' as const, tone: 'tone-blue' },
    ...(canAccessContinuousMonitoring
      ? [{ id: 'agent' as DashboardNavItemId, label: copy.navAgent, icon: 'alerts' as const, tone: 'tone-cyan' }]
      : []),
    ...(canAccessAudits
      ? [{ id: 'audits' as DashboardNavItemId, label: language === 'es' ? 'Auditorías' : 'Audits', icon: 'audits' as const, tone: 'tone-purple' }]
      : []),
  ];
  const sideNavItems = React.useMemo(
    () => filterStaffNavItems(
      hasSuperUserAccess
        ? [
          { id: 'admin' as DashboardNavItemId, label: language === 'es' ? 'Gestion de acceso' : 'Access Management', icon: 'admin' as const, tone: 'tone-indigo' },
          ...(canAccessCrm
            ? [{ id: 'crm' as DashboardNavItemId, label: copy.navCrm, icon: 'crm' as const, tone: 'tone-indigo' }]
            : []),
          ...(canAccessInlet
            ? [{ id: 'inlet' as DashboardNavItemId, label: copy.navInlet, icon: 'inlet' as const, tone: 'tone-indigo' }]
            : []),
          ...(canAccessRazzmatazz
            ? [{ id: 'razzmatazz' as DashboardNavItemId, label: copy.navRazzmatazz, icon: 'razzmatazz' as const, tone: 'tone-rose' }]
            : []),
          { id: 'costOptimization' as DashboardNavItemId, label: language === 'es' ? 'Optimizacion de costos' : 'Cost Optimization', icon: 'costOptimization' as const, tone: 'tone-cyan' },
          { id: 'voiceMessages' as DashboardNavItemId, label: language === 'es' ? 'Mensajes automatizados' : 'Automated Messaging', icon: 'voiceMessages' as const, tone: 'tone-indigo' },
          { id: 'socials' as DashboardNavItemId, label: 'Socials', icon: 'socials' as const, tone: 'tone-rose' },
          ...(isKeycloakAdmin ? [{ id: 'keycloakAdmin' as DashboardNavItemId, label: language === 'es' ? 'Proveedor de identidad' : 'Identity Provider', icon: 'keycloakAdmin' as const, tone: 'tone-indigo' }] : []),
          { id: 'subscriptionLevels' as DashboardNavItemId, label: copy.navSubscriptionLevels, icon: 'billing' as const, tone: 'tone-sky' },
          { id: 'payingSubscriptions' as DashboardNavItemId, label: copy.navPayingSubscriptions, icon: 'subscriptions' as const, tone: 'tone-sky' },
          { id: 'expiredSubscriptions' as DashboardNavItemId, label: copy.navExpiredSubscriptions, icon: 'alerts' as const, tone: 'tone-orange' },
          ...(canManageSubscriptionTeam
            ? [{ id: 'team' as DashboardNavItemId, label: copy.navTeam, icon: 'personal' as const, tone: 'tone-indigo' }]
            : []),
          { id: 'mySubscriptions' as DashboardNavItemId, label: 'mySubscriptions', icon: 'subscriptions' as const, tone: 'tone-sky' },
          { id: 'payments' as DashboardNavItemId, label: copy.navPayments, icon: 'payments' as const, tone: 'tone-orange' },
          { id: 'billing' as DashboardNavItemId, label: copy.navBilling, icon: 'billing' as const, tone: 'tone-orange' },
          { id: 'subscriptions' as DashboardNavItemId, label: copy.navSubscriptions, icon: 'subscriptions' as const, tone: 'tone-sky' },
          ...(canAccessResearch
            ? [{ id: 'research' as DashboardNavItemId, label: copy.navResearch, icon: 'research' as const, tone: 'tone-green' }]
            : []),
          { id: 'complidesk' as DashboardNavItemId, label: 'CompliDesk', icon: 'complidesk' as const, tone: 'tone-cyan' },
          { id: 'emailInbox' as DashboardNavItemId, label: language === 'es' ? 'Correo' : 'Mail', icon: 'emailInbox' as const, tone: 'tone-cyan' },
          { id: 'postlude' as DashboardNavItemId, label: 'Postlude', icon: 'alerts' as const, tone: 'tone-cyan' },
          ...complianceSideNavItems,
          { id: 'tips' as DashboardNavItemId, label: copy.navTips, icon: 'tips' as const, tone: 'tone-cyan' },
          { id: 'style' as DashboardNavItemId, label: copy.navStyle, icon: 'style' as const, tone: 'tone-rose' },
          ...baseSideNavItems.filter((item) => item.id !== 'payments' && item.id !== 'billing'),
        ]
        : subscriberSideNavItems,
      hasSuperUserAccess,
    ),
    [
      baseSideNavItems,
      complianceSideNavItems,
      copy.navBilling,
      copy.navCrm,
      copy.navInlet,
      copy.navRazzmatazz,
      copy.navPayments,
      copy.navResearch,
      copy.navStyle,
      copy.navSubscriptions,
      copy.navSubscriptionLevels,
      copy.navPayingSubscriptions,
      copy.navExpiredSubscriptions,
      copy.navTeam,
      copy.navTips,
      canAccessCrm,
      canAccessInlet,
      canAccessRazzmatazz,
      canAccessResearch,
      canManageSubscriptionTeam,
      hasSuperUserAccess,
      isKeycloakAdmin,
      language,
      subscriberSideNavItems,
    ],
  );
  const [activeNavItem, setActiveNavItem] = React.useState<DashboardNavItemId>(initialNavItem);
  React.useEffect(() => {
    if (hasSuperUserAccess || !isStaffOnlyConsoleNavItem(activeNavItem)) {
      return;
    }
    navigate('/console/my-subscriptions', { replace: true });
  }, [activeNavItem, hasSuperUserAccess, navigate]);
  const [isAdminAccordionOpen, setIsAdminAccordionOpen] = React.useState<boolean>(true);
  const [isSideNavCollapsed, setIsSideNavCollapsed] = React.useState<boolean>(() => {
    try {
      return window.localStorage.getItem('nextcompli.dashboard.sideNavCollapsed') === '1';
    } catch {
      return false;
    }
  });
  const [isCompactNav, setIsCompactNav] = React.useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia(CONSOLE_NAV_COMPACT_MQ).matches,
  );
  const [isMobileNavSheetOpen, setIsMobileNavSheetOpen] = React.useState(false);
  const [navQuery, setNavQuery] = React.useState('');
  const [openNavGroupId, setOpenNavGroupId] = React.useState<ConsoleNavGroupId | null>(
    () => initialOpenConsoleNavGroup(initialNavItem),
  );
  const [openWorkSubgroupId, setOpenWorkSubgroupId] = React.useState<ConsoleWorkSubgroupId | null>(
    () => openWorkSubgroupForNavSelection(initialNavItem),
  );
  const starScope = String(user?.email || '').trim().toLowerCase();
  const [starredSectionIds, setStarredSectionIds] = React.useState<string[]>([]);
  const skipStarSaveRef = React.useRef(true);
  const navSearchInputRef = React.useRef<HTMLInputElement | null>(null);
  const isOfacNavItem = React.useCallback((itemId: DashboardNavItemId) => (
    itemId === 'ofacDashboards'
    || itemId === 'ofacLiveFeed'
    || itemId === 'ofacScreening'
    || itemId === 'ofacInvestigations'
    || itemId === 'ofacWhitelist'
    || itemId === 'ofacCapta'
  ), []);
  const ofacSubmenuItems = React.useMemo(
    () => [
      { id: 'ofacDashboards' as DashboardNavItemId, label: language === 'es' ? 'Inicio de sanciones' : 'Sanctions home', icon: 'dashboards' as const },
      { id: 'ofacCapta' as DashboardNavItemId, label: language === 'es' ? 'Restricciones de cuenta' : 'Account restrictions', icon: 'ofacCapta' as const },
      { id: 'ofacInvestigations' as DashboardNavItemId, label: language === 'es' ? 'Revisar una coincidencia' : 'Review a match', icon: 'ofacInvestigations' as const },
      { id: 'ofacLiveFeed' as DashboardNavItemId, label: language === 'es' ? 'Actualizaciones de listas' : 'List updates', icon: 'ofacLiveFeed' as const },
      { id: 'ofacScreening' as DashboardNavItemId, label: language === 'es' ? 'Cribar un nombre' : 'Screen a name', icon: 'ofacScreening' as const },
      { id: 'ofacWhitelist' as DashboardNavItemId, label: language === 'es' ? 'Nombres ya aclarados' : 'Names you cleared', icon: 'ofacWhitelist' as const },
    ].sort((left, right) => left.label.localeCompare(right.label, language === 'es' ? 'es' : 'en')),
    [copy.navOfacCapta, copy.navOfacDashboards, copy.navOfacInvestigations, copy.navOfacLiveFeed, copy.navOfacScreening, copy.navOfacWhitelist, language],
  );
  const isOfacNavActive = isOfacNavItem(activeNavItem);
  const navGroupLabels = React.useMemo(
    () => ({
      work: copy.navGroupWork,
      billing: copy.navGroupBilling,
      subscriptions: copy.navGroupSubscriptions,
      account: copy.navGroupAccount,
    }),
    [copy.navGroupAccount, copy.navGroupBilling, copy.navGroupSubscriptions, copy.navGroupWork],
  );
  const workSubgroupLabels = React.useMemo(
    () => (language === 'es'
      ? {
        form: copy.navWorkForm || DEFAULT_WORK_SUBGROUP_LABELS_ES.form,
        comply: copy.navWorkComply || DEFAULT_WORK_SUBGROUP_LABELS_ES.comply,
        desk: copy.navWorkDesk || DEFAULT_WORK_SUBGROUP_LABELS_ES.desk,
        grow: copy.navWorkGrow || DEFAULT_WORK_SUBGROUP_LABELS_ES.grow,
        operate: copy.navWorkOperate || DEFAULT_WORK_SUBGROUP_LABELS_ES.operate,
      }
      : {
        form: copy.navWorkForm || DEFAULT_WORK_SUBGROUP_LABELS_EN.form,
        comply: copy.navWorkComply || DEFAULT_WORK_SUBGROUP_LABELS_EN.comply,
        desk: copy.navWorkDesk || DEFAULT_WORK_SUBGROUP_LABELS_EN.desk,
        grow: copy.navWorkGrow || DEFAULT_WORK_SUBGROUP_LABELS_EN.grow,
        operate: copy.navWorkOperate || DEFAULT_WORK_SUBGROUP_LABELS_EN.operate,
      }),
    [copy.navWorkComply, copy.navWorkDesk, copy.navWorkForm, copy.navWorkGrow, copy.navWorkOperate, language],
  );
  const groupedSideNav = React.useMemo(
    () => groupConsoleNavItems(
      sideNavItems,
      navGroupLabels,
      language === 'es' ? 'es' : 'en',
      workSubgroupLabels,
    ),
    [language, navGroupLabels, sideNavItems, workSubgroupLabels],
  );
  const visibleNavGroups = React.useMemo(
    () => filterConsoleNavGroups(
      groupedSideNav.groups,
      navQuery,
      workSubgroupLabels,
      language === 'es' ? 'es' : 'en',
    ),
    [groupedSideNav.groups, language, navQuery, workSubgroupLabels],
  );
  const homeNavItem = groupedSideNav.home;
  const homeNavVisible = Boolean(homeNavItem && navItemMatchesQuery(homeNavItem, navQuery));
  const pinnedNavItems = React.useMemo(
    () => groupedSideNav.pinned.filter((item) => navItemMatchesQuery(item, navQuery)),
    [groupedSideNav.pinned, navQuery],
  );
  const starredNavItems = React.useMemo(
    () => starredSectionItems(
      sideNavItems.filter((item) => navItemMatchesQuery(item, navQuery)),
      withPermanentStars(starredSectionIds),
    ),
    [navQuery, sideNavItems, starredSectionIds],
  );
  const activeNavLabel = React.useMemo(() => {
    const fromList = sideNavItems.find((item) => item.id === activeNavItem);
    if (fromList) {
      return fromList.label;
    }
    if (isOfacNavItem(activeNavItem)) {
      const ofacMatch = ofacSubmenuItems.find((item) => item.id === activeNavItem);
      if (ofacMatch) {
        return ofacMatch.label;
      }
    }
    return copy.navJumpTo;
  }, [activeNavItem, copy.navJumpTo, isOfacNavItem, ofacSubmenuItems, sideNavItems]);
  const consoleHelpSignals = React.useMemo((): ConsoleHelpSignal[] => {
    const signals: ConsoleHelpSignal[] = [];
    const companyCount = Array.isArray(user?.companies) ? user.companies.length : 0;
    if (companyCount === 0) {
      signals.push({
        id: 'no-company-membership',
        tipEn: 'This login is not on a company yet. The assistant can explain invites and mySubscriptions.',
        tipEs: 'Este acceso aún no está en una empresa. El asistente puede explicar invitaciones y mySubscriptions.',
        promptEn: 'Why don’t I see any subscriptions or companies on this login?',
        promptEs: '¿Por qué no veo suscripciones ni empresas en este acceso?',
        urgency: 'strong',
      });
    }
    if (activeNavItem === 'mySubscriptions' && companyCount === 0) {
      signals.push({
        id: 'my-subscriptions-empty',
        tipEn: 'mySubscriptions only lists accounts you already accepted.',
        tipEs: 'mySubscriptions solo muestra cuentas que ya aceptó.',
        promptEn: 'I opened mySubscriptions and it is empty. What should I do?',
        promptEs: 'Abrí mySubscriptions y está vacío. ¿Qué debo hacer?',
        urgency: 'soft',
      });
    }
    if (activeNavItem === 'businessFormation') {
      signals.push({
        id: 'business-formation-page',
        tipEn: 'Forming a company? Ask the assistant what the next checklist step means.',
        tipEs: '¿Está formando una empresa? Pregunte al asistente qué significa el siguiente paso.',
        promptEn: 'What is the next thing I need for my company on Formation Home?',
        promptEs: '¿Cuál es lo siguiente que necesito para mi empresa en Inicio de formación?',
        urgency: 'soft',
      });
    }
    return signals;
  }, [activeNavItem, user?.companies]);
  const nextAdminResetState = React.useCallback(() => ({ adminResetNonce: Date.now() }), []);
  React.useEffect(() => {
    try {
      window.localStorage.setItem('nextcompli.dashboard.sideNavCollapsed', isSideNavCollapsed ? '1' : '0');
    } catch {
      /* ignore quota / private mode */
    }
  }, [isSideNavCollapsed]);
  React.useEffect(() => {
    skipStarSaveRef.current = true;
    if (!starScope) {
      setStarredSectionIds([]);
      return;
    }
    try {
      setStarredSectionIds(parseStarredSectionIds(window.localStorage.getItem(consoleStarStorageKey(starScope))));
    } catch {
      setStarredSectionIds([]);
    }
  }, [starScope]);
  React.useEffect(() => {
    if (!starScope) {
      return;
    }
    if (skipStarSaveRef.current) {
      skipStarSaveRef.current = false;
      return;
    }
    try {
      window.localStorage.setItem(consoleStarStorageKey(starScope), JSON.stringify(starredSectionIds));
    } catch {
      /* ignore quota / private mode */
    }
  }, [starScope, starredSectionIds]);
  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const media = window.matchMedia(CONSOLE_NAV_COMPACT_MQ);
    const onChange = () => setIsCompactNav(media.matches);
    onChange();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    }
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);
  React.useEffect(() => {
    if (hasSuperUserAccess) {
      setOpenNavGroupId((current) => current ?? 'work');
      // If Work opens with no subgroup yet, match the active page — never Form by default.
      setOpenWorkSubgroupId((current) => current ?? openWorkSubgroupForNavSelection(activeNavItem));
    }
  }, [activeNavItem, hasSuperUserAccess]);
  React.useEffect(() => {
    if (!isMobileNavSheetOpen) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileNavSheetOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMobileNavSheetOpen]);
  React.useEffect(() => {
    if (isMobileNavSheetOpen) {
      navSearchInputRef.current?.focus();
    }
  }, [isMobileNavSheetOpen]);
  const openMobileNavSheet = React.useCallback((groupId?: ConsoleNavGroupId | null, focusSearch = false) => {
    if (groupId) {
      setOpenNavGroupId(groupId);
    }
    if (focusSearch) {
      setNavQuery('');
    }
    setIsMobileNavSheetOpen(true);
  }, []);
  const closeMobileNavSheet = React.useCallback(() => {
    setIsMobileNavSheetOpen(false);
    setNavQuery('');
  }, []);
  const adminEmailInboxUrl = (process.env.REACT_APP_ADMIN_EMAIL_INBOX_URL || 'https://mail.google.com/mail/u/0/?authuser=andrew.goss@gosslife.biz').trim();
  // Platform Access Management only (CompliCore subscribers never see RBAC).
  const adminPrivilegeItems = React.useMemo(
    () => [
      { key: 'users' as AdminPrivilegeKey, label: language === 'es' ? 'Usuarios' : 'Users', path: '/console/admin/users' },
      { key: 'groups' as AdminPrivilegeKey, label: language === 'es' ? 'Cuentas' : 'Accounts', path: '/console/admin/groups' },
      { key: 'roles' as AdminPrivilegeKey, label: language === 'es' ? 'Roles' : 'Roles', path: '/console/admin/roles' },
      { key: 'observability' as AdminPrivilegeKey, label: language === 'es' ? 'Observabilidad' : 'Observability', path: '/console/admin/observability' },
      { key: 'experience' as AdminPrivilegeKey, label: language === 'es' ? 'Experiencia de usuario' : 'User experience', path: '/console/admin/experience' },
    ],
    [language],
  );
  const isLocalDashboardHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const gatewayBaseUrl = inferGatewayBaseUrl();
  const postludeTokenRef = React.useRef(accessToken);
  postludeTokenRef.current = accessToken;
  React.useEffect(() => {
    if (!accessToken) {
      return undefined;
    }
    const storageKey = 'nextcompli.postlude.session';
    let sessionId = window.sessionStorage.getItem(storageKey) || '';
    if (sessionId.length < 8) {
      sessionId = window.crypto.randomUUID();
      window.sessionStorage.setItem(storageKey, sessionId);
    }
    let started = Date.now();
    let page = `${window.location.pathname}${window.location.hash || ''}`;
    const company = user?.companies?.[0] as { plan?: string; tier?: string; subscription_tier?: string } | undefined;
    const planName = String(company?.plan || company?.tier || company?.subscription_tier || '');
    const rotateSession = () => {
      sessionId = window.crypto.randomUUID();
      window.sessionStorage.setItem(storageKey, sessionId);
      started = Date.now();
    };
    const send = (closed: boolean) => {
      const visible = Math.max(0, Date.now() - started);
      started = Date.now();
      const nextPage = `${window.location.pathname}${window.location.hash || ''}`;
      void fetch(`${gatewayBaseUrl.replace(/\/$/, '')}/api/v1/postlude/visits`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: window.crypto.randomUUID(),
          session_id: sessionId,
          path: page,
          visible_ms: visible,
          closed,
          converted: Boolean(user?.companies?.length && planName),
          plan_name: planName,
        }),
        keepalive: true,
      }).catch(() => undefined);
      page = nextPage;
      if (closed) {
        rotateSession();
      }
    };
    let idleTimer = 0;
    let abandonTimer = 0;
    const armIdle = () => {
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => send(true), 30 * 60 * 1000);
    };
    const onActivity = () => {
      if (document.visibilityState === 'visible') {
        armIdle();
      }
    };
    const onHide = () => {
      if (document.visibilityState === 'hidden') {
        send(false);
        window.clearTimeout(abandonTimer);
        // Background tabs rarely fire pagehide; close after a short abandon window.
        abandonTimer = window.setTimeout(() => send(true), 5 * 60 * 1000);
      } else {
        window.clearTimeout(abandonTimer);
        armIdle();
      }
    };
    const onLeave = () => send(true);
    armIdle();
    document.addEventListener('visibilitychange', onHide);
    document.addEventListener('keydown', onActivity);
    document.addEventListener('pointerdown', onActivity);
    window.addEventListener('pagehide', onLeave);
    return () => {
      window.clearTimeout(idleTimer);
      window.clearTimeout(abandonTimer);
      // Route changes flush page time without ending the visit; logout ends it.
      send(!postludeTokenRef.current);
      document.removeEventListener('visibilitychange', onHide);
      document.removeEventListener('keydown', onActivity);
      document.removeEventListener('pointerdown', onActivity);
      window.removeEventListener('pagehide', onLeave);
    };
  }, [accessToken, gatewayBaseUrl, location.pathname, location.hash, user?.companies]);
  const metricsServiceUrl = (
    process.env.REACT_APP_METRICS_URL
    || (isLocalDashboardHost ? '' : `${gatewayBaseUrl}/api/v1/metrics`)
  ).replace(/\/$/, '');
  const defaultResearchReportServiceUrl = `${gatewayBaseUrl}/api/v1/research/reports`;
  const researchReportServiceUrl = process.env.REACT_APP_RESEARCH_REPORT_URL || defaultResearchReportServiceUrl;
  const researchReportAsyncServiceUrl = `${gatewayBaseUrl}/api/v1/research/reports/async`;
  const authEmailAliasesUrl = `${gatewayBaseUrl}/api/v1/auth/email-aliases`;
  const socialsAccessAuditUrl = `${gatewayBaseUrl}/api/v1/socials/access-audit`;
  const socialsResearchCredentialsUrl = `${gatewayBaseUrl}/api/v1/socials/research-credentials`;
  const socialsResearchSessionsUrl = `${gatewayBaseUrl}/api/v1/socials/research-sessions`;
  const socialsVantagePlatformsUrl = `${gatewayBaseUrl}/api/v1/socials/vantage-platforms`;
  const [metricsDashboards, setMetricsDashboards] = React.useState<string[]>(() =>
    SERVICE_DASHBOARD_FALLBACK_KEYS.map(formatServiceDashboardName),
  );
  const [metricsDashboardData, setMetricsDashboardData] = React.useState<MetricsDashboardResponse | null>(null);
  const [metricsServiceData, setMetricsServiceData] = React.useState<MetricsServiceDataSummary | null>(null);
  const [aiInsightsData, setAiInsightsData] = React.useState<AIInsightsData | null>(null);
  const [deepLearningByService, setDeepLearningByService] = React.useState<Record<string, AIDeepLearningInferenceData>>({});
  const [forecastByService, setForecastByService] = React.useState<Record<string, AIPredictiveForecastData>>({});
  const [nlpInput, setNlpInput] = React.useState('');
  const [nlpAnalysis, setNlpAnalysis] = React.useState<AINlpAnalysisData | null>(null);
  const [isNlpLoading, setIsNlpLoading] = React.useState(false);
  const [expandedAiInsight, setExpandedAiInsight] = React.useState<string | null>(null);
  const [legalExposureAreaByService, setLegalExposureAreaByService] = React.useState<Record<string, LegalExposureAreaInput>>({});
  const [termsReplayGateStatus, setTermsReplayGateStatus] = React.useState<string | null>(null);
  const [assentReplaySummary, setAssentReplaySummary] = React.useState<AssentReplaySummaryResponse | null>(null);
  const [assentReplayStatus, setAssentReplayStatus] = React.useState<string | null>(null);
  const [contractUpdateNoticeGateStatus, setContractUpdateNoticeGateStatus] = React.useState<string | null>(null);
  const [termsPageAccessGateStatus, setTermsPageAccessGateStatus] = React.useState<string | null>(null);
  const [authenticatedEntrypointAssentGateStatus, setAuthenticatedEntrypointAssentGateStatus] = React.useState<string | null>(null);
  const [negotiatedContractTermsGateStatus, setNegotiatedContractTermsGateStatus] = React.useState<string | null>(null);
  const [publicTermsPageGateStatus, setPublicTermsPageGateStatus] = React.useState<string | null>(null);
  const [metricsLastUpdated, setMetricsLastUpdated] = React.useState<Date | null>(null);
  const [groupScopes, setGroupScopes] = React.useState(() => loadGroupScopes());
  const userGroups = React.useMemo(() => inferUserGroups(user), [user]);
  const salesScope = React.useMemo(() => resolveSalesScope(groupScopes), [groupScopes]);
  const shouldApplySalesScope = userGroups.includes('sales') && Boolean(salesScope);
  const rawAvailableDashboards = React.useMemo(
    () => Array.from(new Set(metricsDashboards)),
    [metricsDashboards],
  );
  const [dashboardServiceVisibility, setDashboardServiceVisibility] = React.useState<Record<DashboardServiceFilterKey, boolean>>({
    ofac: true,
    websiteCompliance: true,
    governmentalCompliance: true,
  });
  const availableDashboards = React.useMemo(
    () => rawAvailableDashboards.filter((dashboardName) => {
      const serviceKey = parseServiceDashboardKey(dashboardName);

      if (shouldApplySalesScope && salesScope && salesScope.services.length > 0) {
        const inScope = matchesScopedResource(serviceKey, salesScope.services)
          || matchesScopedResource(dashboardName, salesScope.services);
        if (!inScope) {
          return false;
        }
      }

      if (OFAC_SERVICE_KEYS.has(serviceKey)) {
        return dashboardServiceVisibility.ofac;
      }
      if (WEBSITE_COMPLIANCE_SERVICE_KEYS.has(serviceKey)) {
        return dashboardServiceVisibility.websiteCompliance;
      }
      if (GOVERNMENTAL_COMPLIANCE_SERVICE_KEYS.has(serviceKey)) {
        return dashboardServiceVisibility.governmentalCompliance;
      }

      return dashboardServiceVisibility.ofac;
    }),
    [rawAvailableDashboards, dashboardServiceVisibility, salesScope, shouldApplySalesScope],
  );
  const [dashboardSubscriptions, setDashboardSubscriptions] = React.useState<Record<string, boolean>>(() => {
    const next: Record<string, boolean> = {};
    SERVICE_DASHBOARD_FALLBACK_KEYS.map(formatServiceDashboardName).forEach((name) => {
      next[name] = true;
    });
    return next;
  });
  const [isDashboardSubscriptionsOpen, setIsDashboardSubscriptionsOpen] = React.useState(false);
  const [isTwoFactorOn, setIsTwoFactorOn] = React.useState<boolean>(() => isTwoFactorEnabled());
  const [currentPasswordInput, setCurrentPasswordInput] = React.useState('');
  const [newPasswordInput, setNewPasswordInput] = React.useState('');
  const [confirmPasswordInput, setConfirmPasswordInput] = React.useState('');
  const [passwordFeedback, setPasswordFeedback] = React.useState<string | null>(null);
  const [passwordError, setPasswordError] = React.useState<string | null>(null);
  const [passwordSubmitting, setPasswordSubmitting] = React.useState(false);
  const [showPasswordFields, setShowPasswordFields] = React.useState({
    current: false,
    next: false,
    confirm: false,
  });
  const [showSecurityPasswordEditor, setShowSecurityPasswordEditor] = React.useState(false);
  const [securityFeedback, setSecurityFeedback] = React.useState<string | null>(null);
  const [hasPasskey, setHasPasskey] = React.useState(false);
  const [recoveryEmailVerified, setRecoveryEmailVerified] = React.useState(false);
  const [recoveryEmail, setRecoveryEmail] = React.useState(accountEmail);
  const [backupCodesCount, setBackupCodesCount] = React.useState(0);
  const [twoStepPhone, setTwoStepPhone] = React.useState('');
  const [recoveryPhone, setRecoveryPhone] = React.useState('');
  const [deviceSessionsByGroup, setDeviceSessionsByGroup] = React.useState<Record<DeviceGroupKey, DeviceSessionRecord[]>>(
    EMPTY_SECURITY_DEVICE_SESSIONS,
  );
  const [activeSecurityOption, setActiveSecurityOption] = React.useState<SecurityOptionKey | null>(null);
  const [securityPhoneDraft, setSecurityPhoneDraft] = React.useState(DEFAULT_SECURITY_RECOVERY_PHONE);
  const [securityRecoveryPhoneDraft, setSecurityRecoveryPhoneDraft] = React.useState(DEFAULT_SECURITY_RECOVERY_PHONE);
  const [securityRecoveryEmailDraft, setSecurityRecoveryEmailDraft] = React.useState(accountEmail);
  const [loginEmailAliases, setLoginEmailAliases] = React.useState<string[]>([]);
  const [securityAliasInput, setSecurityAliasInput] = React.useState('');
  const [isSecurityAliasBusy, setIsSecurityAliasBusy] = React.useState(false);
  const [expandedDeviceGroups, setExpandedDeviceGroups] = React.useState<DeviceGroupKey[]>([]);
  const [isDeviceManagementOpen, setIsDeviceManagementOpen] = React.useState(false);
  const [linkedIdsEnabled, setLinkedIdsEnabled] = React.useState(false);
  const [backupPhonesEnabled, setBackupPhonesEnabled] = React.useState(false);
  const [authenticatorEnabled, setAuthenticatorEnabled] = React.useState(true);
  const [recoveryContactsEnabled, setRecoveryContactsEnabled] = React.useState(false);
  const [alertEmail, setAlertEmail] = React.useState<string>(() => window.localStorage.getItem('nextcompli.alerts.email') ?? accountProfile.email);
  const [alertChecklist, setAlertChecklist] = React.useState<Record<string, boolean>>({});
  const [alertFeedback, setAlertFeedback] = React.useState<string | null>(null);
  const [emailInboxRefreshNonce, setEmailInboxRefreshNonce] = React.useState(0);
  type SocialsTab = 'post' | 'analytics' | 'configuration';
  type SocialResearchSecret = {
    id: string;
    kind: 'mailbox' | 'website_login' | string;
    platform?: string | null;
    label: string;
    email: string;
    username?: string | null;
    password_configured: boolean;
    source: string;
    note?: string;
    password?: string | null;
  };
  type SocialPlatformKey =
    | 'facebook'
    | 'linkedin'
    | 'x'
    | 'instagram'
    | 'whatsapp'
    | 'telegram'
    | 'messenger'
    | 'snapchat'
    | 'github'
    | 'reddit'
    | 'youtube'
    | 'tiktok'
    | 'threads'
    | 'twitch'
    | 'truth_social'
    | 'bluesky'
    | 'medium'
    | 'substack'
    | 'gitlab'
    | 'gofundme'
    | 'kickstarter'
    | 'patreon'
    | 'givebutter'
    | 'givesendgo'
    | 'gab';
  type SocialPlatformConfigDraft = {
    accountId: string;
    managementUrl: string;
    contactEmail: string;
    apiStatus: string;
    notes: string;
  };
  type SocialAccessAuditEntry = {
    configured: boolean;
    can_access: boolean;
    capability: string;
    detail: string;
    http_status: number | null;
    credential_configured?: boolean | null;
    handle?: string;
    profile_url?: string;
    handle_configured?: boolean | null;
    handle_live?: boolean | null;
    read_pull_ok?: boolean | null;
    write_push_ok?: boolean | null;
    search_ok?: boolean | null;
    working?: boolean | null;
    cadence?: 'core' | 'support' | 'reserved' | 'skip' | string;
  };
  type SocialAccessAuditCriteria = {
    configured?: string;
    working?: string;
    notes?: string;
    criteria_version?: string;
  };
  const [socialsTab, setSocialsTab] = React.useState<SocialsTab>('analytics');
  const [socialsPostText, setSocialsPostText] = React.useState('');
  const [socialsPostLink, setSocialsPostLink] = React.useState('');
  const [socialsPostImageUrl, setSocialsPostImageUrl] = React.useState('');
  const [socialsPostStatus, setSocialsPostStatus] = React.useState<'idle' | 'posting' | 'success' | 'error'>('idle');
  const [socialsPostError, setSocialsPostError] = React.useState<string | null>(null);
  const [socialsRecentPosts, setSocialsRecentPosts] = React.useState<Array<{ id: string; message?: string; created_time?: string; permalink_url?: string; attachments?: { data?: Array<{ media?: { image?: { src?: string } }; url?: string }> } }>>([]);
  const [socialsPageName, setSocialsPageName] = React.useState<string | null>(null);
  const [socialsPageLoaded, setSocialsPageLoaded] = React.useState(false);
  const [socialsPageConnectionVerified, setSocialsPageConnectionVerified] = React.useState(false);
  const [socialAccessAuditByPlatform, setSocialAccessAuditByPlatform] = React.useState<Partial<Record<SocialPlatformKey, SocialAccessAuditEntry>>>({});
  const [socialAccessAuditLoaded, setSocialAccessAuditLoaded] = React.useState(false);
  const [socialAccessAuditRunning, setSocialAccessAuditRunning] = React.useState(false);
  const [socialAccessAuditError, setSocialAccessAuditError] = React.useState<string | null>(null);
  const [socialAccessAuditCheckedAt, setSocialAccessAuditCheckedAt] = React.useState<string | null>(null);
  const [socialAccessAuditCriteria, setSocialAccessAuditCriteria] = React.useState<SocialAccessAuditCriteria | null>(null);
  const [socialsFanCount, setSocialsFanCount] = React.useState<number | null>(null);
  const [socialsInsights, setSocialsInsights] = React.useState<{ impressions?: number; reach?: number; engagements?: number } | null>(null);
  const [socialsInsightsLoaded, setSocialsInsightsLoaded] = React.useState(false);
  const [openSocialPlatform, setOpenSocialPlatform] = React.useState<SocialPlatformKey | null>(null);
  const [socialResearchSecrets, setSocialResearchSecrets] = React.useState<SocialResearchSecret[]>([]);
  const [socialResearchSecretsRevealed, setSocialResearchSecretsRevealed] = React.useState(false);
  const [socialResearchSecretsLoaded, setSocialResearchSecretsLoaded] = React.useState(false);
  const [socialResearchSecretsLoading, setSocialResearchSecretsLoading] = React.useState(false);
  const [socialResearchSecretsError, setSocialResearchSecretsError] = React.useState<string | null>(null);
  const [socialResearchCopiedId, setSocialResearchCopiedId] = React.useState<string | null>(null);
  const [socialResearchSessions, setSocialResearchSessions] = React.useState<{
    ok?: boolean;
    available?: boolean;
    live_url?: string;
    error?: string;
    operator_hint?: string;
    proxy_url?: string;
    platforms?: Record<string, { email?: string; logged_in?: boolean; cookies?: number }>;
    connect?: Record<string, { status?: string }>;
  } | null>(null);
  const [socialResearchSessionsLoading, setSocialResearchSessionsLoading] = React.useState(false);
  const [socialResearchConnectPlatform, setSocialResearchConnectPlatform] = React.useState<string | null>(null);
  const [socialResearchLiveOpen, setSocialResearchLiveOpen] = React.useState(false);
  const [vantageSocialPlatforms, setVantageSocialPlatforms] = React.useState<{
    platforms?: Array<{ id: string; label: string; enabled: boolean; handle?: string; url?: string }>;
    enabled_ids?: string[];
    persisted?: boolean;
    updated_at?: string | null;
  } | null>(null);
  const [vantageSocialPlatformsLoading, setVantageSocialPlatformsLoading] = React.useState(false);
  const [vantageSocialPlatformsSaving, setVantageSocialPlatformsSaving] = React.useState(false);
  const [vantageSocialPlatformsError, setVantageSocialPlatformsError] = React.useState<string | null>(null);
  const [vantageResearchSectionOpen, setVantageResearchSectionOpen] = React.useState(false);
  const facebookPageToken = (process.env.REACT_APP_FACEBOOK_PAGE_TOKEN || '').trim();
  const facebookPageId = (process.env.REACT_APP_FACEBOOK_PAGE_ID || '').trim();
  const linkedinAccessToken = (process.env.REACT_APP_LINKEDIN_ACCESS_TOKEN || '').trim();
  const xAccessToken = (process.env.REACT_APP_X_ACCESS_TOKEN || process.env.REACT_APP_TWITTER_ACCESS_TOKEN || '').trim();
  const facebookPublicPageUrl = getFacebookPageUrl(facebookPageId);
  const socialsConfigStorageKey = 'nextcompli.socials.config.v1';
  const defaultSocialConfigDrafts: Record<SocialPlatformKey, SocialPlatformConfigDraft> = {
    facebook: {
      accountId: facebookPageId || '',
      managementUrl: facebookPublicPageUrl || 'https://www.facebook.com/business/',
      contactEmail: 'social@nextcompli.com',
      apiStatus: facebookPageToken && facebookPageId ? 'Connected via website env' : 'Needs website env configuration',
      notes: 'Primary Facebook page publishing is active through the website runtime configuration.',
    },
    linkedin: {
      accountId: 'nextcompli',
      managementUrl: 'https://www.linkedin.com/company/nextcompli',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Connected via SocialsMCP (token valid ~July 2026; refresh token active ~1 year)',
      notes: 'Access token + refresh token stored in SocialsMCP/.env. Auto-rotates via refresh token. Scopes: openid, profile, email, w_member_social.',
    },
    x: {
      accountId: '@nextcompli',
      managementUrl: 'https://console.x.com/onboarding',
      contactEmail: 'andrew.goss@gosslife.biz',
      apiStatus: 'Developer onboarding in progress',
      notes: 'Use the @nextcompli brand account for developer enrollment. After approval, generate user-context keys for SocialsMCP and rotate ownership to social@nextcompli.com.',
    },
    instagram: {
      accountId: '@nextcompli_official',
      managementUrl: 'https://business.facebook.com/latest/settings',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Live — linked to Meta Business (business account ID 17841415112770797)',
      notes: 'Canonical public profile is @nextcompli_official (https://www.instagram.com/nextcompli_official/). Legacy @nextcompli is not the live account. Threads inherits @nextcompli_official via the Instagram link.',
    },
    whatsapp: {
      accountId: '18667765961',
      managementUrl: 'https://wa.me/18667765961',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'WhatsApp Business number 1-866-776-5961. Create/attach a WhatsApp Business Account in Meta App Dashboard API Setup, save WABA ID + phone number ID, then issue a permanent system-user token.',
    },
    telegram: {
      accountId: '@nextcompli',
      managementUrl: 'https://telegram.org/',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Create and verify official Telegram channel/account, then track channel integrity, audience growth, and message-consistency signals.',
    },
    messenger: {
      accountId: 'nextcompli',
      managementUrl: 'https://www.messenger.com/',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Enable Messenger on the official Facebook Page and store inbox routing ownership for customer response and risk monitoring.',
    },
    snapchat: {
      accountId: '@nextcompli',
      managementUrl: 'https://business.snapchat.com/',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Create and verify official Snapchat Public Profile, then monitor audience growth and campaign narrative signals.',
    },
    github: {
      accountId: 'nextcompli',
      managementUrl: 'https://github.com/nextcompli',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Support ticket open — awaiting GitHub response',
      notes: 'Org "nextcompli" exists on GitHub. Recovery ticket filed: https://support.github.com/ticket/personal/0/4364360 (opened May 7, 2026). Awaiting owner access restoration from GitHub Support.',
    },
    reddit: {
      accountId: 'nextcompli',
      managementUrl: 'https://www.reddit.com/user/nextcompli/',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Canonical brand user is u/nextcompli. Create official subreddit and moderator team when community presence is needed.',
    },
    youtube: {
      accountId: '@nextcompli',
      managementUrl: 'https://studio.youtube.com/',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Create/claim official channel and store channel ID for public feed pulls and channel integrity checks.',
    },
    tiktok: {
      accountId: '@nextcompli',
      managementUrl: 'https://www.tiktok.com/business/',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Enroll business account and store canonical handle for profile-level pull and narrative monitoring.',
    },
    threads: {
      accountId: '@nextcompli_official',
      managementUrl: 'https://www.threads.net/@nextcompli_official',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Canonical Threads handle is @nextcompli_official (linked via Instagram). Use for real-time text signal tracking and profile consistency checks.',
    },
    twitch: {
      accountId: '@nextcompli',
      managementUrl: 'https://dashboard.twitch.tv/',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Create or claim the Twitch channel and store the canonical handle for live-stream and community monitoring.',
    },
    truth_social: {
      accountId: '@nextcompli',
      managementUrl: 'https://truthsocial.com/',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Create or claim the Truth Social handle and track the public profile for consistency and reach monitoring.',
    },
    bluesky: {
      accountId: '@nextcompli.bsky.social',
      managementUrl: 'https://bsky.app/',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Create or claim the Bluesky handle and store the handle plus DID for identity verification.',
    },
    medium: {
      accountId: 'nextcompli',
      managementUrl: 'https://medium.com/',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Register publication and canonical author handles for leadership narrative and policy post tracking.',
    },
    substack: {
      accountId: 'nextcompli',
      managementUrl: 'https://substack.com/',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Create publication host and map newsletter domain for long-form public-communication signal checks.',
    },
    gitlab: {
      accountId: 'nextcompli',
      managementUrl: 'https://gitlab.com/',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Track public GitLab namespace and project signals to complement GitHub footprint analysis.',
    },
    gofundme: {
      accountId: 'nextcompli',
      managementUrl: 'https://www.gofundme.com/',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Create and verify official GoFundMe organizer profile, then track campaign authenticity and update cadence.',
    },
    kickstarter: {
      accountId: 'nextcompli',
      managementUrl: 'https://www.kickstarter.com/',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Create and verify official Kickstarter creator profile, then monitor campaign launch and backer-signal trends.',
    },
    patreon: {
      accountId: 'nextcompli',
      managementUrl: 'https://www.patreon.com/',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Create and verify Patreon creator page, then track membership growth and payout-profile consistency.',
    },
    givebutter: {
      accountId: 'nextcompli',
      managementUrl: 'https://givebutter.com/',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Create and verify Givebutter organization account, then monitor campaign transparency and donation controls.',
    },
    givesendgo: {
      accountId: 'nextcompli',
      managementUrl: 'https://www.givesendgo.com/nextcompli',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Create and verify GiveSendGo profile, then track campaign consistency, disclosure quality, and support velocity.',
    },
    gab: {
      accountId: 'nextcompli',
      managementUrl: 'https://gab.com/nextcompli',
      contactEmail: 'social@nextcompli.com',
      apiStatus: 'Setup pending',
      notes: 'Create and verify Gab brand profile for public-post presence and narrative monitoring.',
    },
  };
  const [socialConfigDrafts, setSocialConfigDrafts] = React.useState<Record<SocialPlatformKey, SocialPlatformConfigDraft>>(() => {
    try {
      const raw = window.localStorage.getItem(socialsConfigStorageKey);
      const parsed = raw ? (JSON.parse(raw) as Partial<Record<SocialPlatformKey, Partial<SocialPlatformConfigDraft>>>) : null;
      return {
        facebook: { ...defaultSocialConfigDrafts.facebook, ...(parsed?.facebook || {}) },
        linkedin: { ...defaultSocialConfigDrafts.linkedin, ...(parsed?.linkedin || {}) },
        x: { ...defaultSocialConfigDrafts.x, ...(parsed?.x || {}) },
        instagram: { ...defaultSocialConfigDrafts.instagram, ...(parsed?.instagram || {}) },
        whatsapp: { ...defaultSocialConfigDrafts.whatsapp, ...(parsed?.whatsapp || {}) },
        telegram: { ...defaultSocialConfigDrafts.telegram, ...(parsed?.telegram || {}) },
        messenger: { ...defaultSocialConfigDrafts.messenger, ...(parsed?.messenger || {}) },
        snapchat: { ...defaultSocialConfigDrafts.snapchat, ...(parsed?.snapchat || {}) },
        github: { ...defaultSocialConfigDrafts.github, ...(parsed?.github || {}) },
        reddit: { ...defaultSocialConfigDrafts.reddit, ...(parsed?.reddit || {}) },
        youtube: { ...defaultSocialConfigDrafts.youtube, ...(parsed?.youtube || {}) },
        tiktok: { ...defaultSocialConfigDrafts.tiktok, ...(parsed?.tiktok || {}) },
        threads: { ...defaultSocialConfigDrafts.threads, ...(parsed?.threads || {}) },
        twitch: { ...defaultSocialConfigDrafts.twitch, ...(parsed?.twitch || {}) },
        truth_social: { ...defaultSocialConfigDrafts.truth_social, ...(parsed?.truth_social || {}) },
        bluesky: { ...defaultSocialConfigDrafts.bluesky, ...(parsed?.bluesky || {}) },
        medium: { ...defaultSocialConfigDrafts.medium, ...(parsed?.medium || {}) },
        substack: { ...defaultSocialConfigDrafts.substack, ...(parsed?.substack || {}) },
        gitlab: { ...defaultSocialConfigDrafts.gitlab, ...(parsed?.gitlab || {}) },
        gofundme: { ...defaultSocialConfigDrafts.gofundme, ...(parsed?.gofundme || {}) },
        kickstarter: { ...defaultSocialConfigDrafts.kickstarter, ...(parsed?.kickstarter || {}) },
        patreon: { ...defaultSocialConfigDrafts.patreon, ...(parsed?.patreon || {}) },
        givebutter: { ...defaultSocialConfigDrafts.givebutter, ...(parsed?.givebutter || {}) },
        givesendgo: { ...defaultSocialConfigDrafts.givesendgo, ...(parsed?.givesendgo || {}) },
        gab: { ...defaultSocialConfigDrafts.gab, ...(parsed?.gab || {}) },
      };
    } catch {
      return defaultSocialConfigDrafts;
    }
  });
  const [socialConfigSaveState, setSocialConfigSaveState] = React.useState<Partial<Record<SocialPlatformKey, string>>>({});
  const updateSocialConfigDraft = React.useCallback((platformKey: SocialPlatformKey, field: keyof SocialPlatformConfigDraft, value: string) => {
    setSocialConfigDrafts((current) => ({
      ...current,
      [platformKey]: {
        ...current[platformKey],
        [field]: value,
      },
    }));
    setSocialConfigSaveState((current) => ({
      ...current,
      [platformKey]: null,
    }));
  }, []);
  const saveSocialConfigDraft = React.useCallback((platformKey: SocialPlatformKey) => {
    try {
      window.localStorage.setItem(socialsConfigStorageKey, JSON.stringify(socialConfigDrafts));
      setSocialConfigSaveState((current) => ({
        ...current,
        [platformKey]: language === 'es' ? 'Configuración guardada localmente.' : 'Configuration saved locally.',
      }));
    } catch {
      setSocialConfigSaveState((current) => ({
        ...current,
        [platformKey]: language === 'es' ? 'No se pudo guardar la configuración local.' : 'Could not save local configuration.',
      }));
    }
  }, [language, socialConfigDrafts, socialsConfigStorageKey]);
  const [paymentsAccordion, setPaymentsAccordion] = React.useState({
    methods: false,
    info: false,
    plan: false,
  });
  const togglePaymentsAccordion = React.useCallback((section: 'methods' | 'info' | 'plan') => {
    setPaymentsAccordion((current) => ({ ...current, [section]: !current[section] }));
  }, []);
  const [securityAccordion, setSecurityAccordion] = React.useState({
    tips: false,
    activity: false,
    // Open by default so Change password / recovery controls are discoverable.
    signIn: true,
    devices: false,
  });
  const toggleSecurityAccordion = React.useCallback((section: 'tips' | 'activity' | 'signIn' | 'devices') => {
    setSecurityAccordion((current) => ({ ...current, [section]: !current[section] }));
  }, []);
  const [paymentEntryType, setPaymentEntryType] = React.useState<'card' | 'bank'>('card');
  const [paymentFormError, setPaymentFormError] = React.useState<string | null>(null);
  const [paymentFormFeedback, setPaymentFormFeedback] = React.useState<string | null>(null);
  const [cardholderName, setCardholderName] = React.useState('');
  const [cardNumberTail, setCardNumberTail] = React.useState('');
  const cardBrand = 'Visa';
  const [bankName, setBankName] = React.useState('');
  const [bankAccountTail, setBankAccountTail] = React.useState('');
  const [savedPaymentMethods, setSavedPaymentMethods] = React.useState<PaymentMethodRecord[]>([
    { id: 'pm-card-1', type: 'card', label: 'Corporate Visa', detail: '•••• 4242', isDefault: true },
  ]);
  const [paymentPlans, setPaymentPlans] = React.useState<PaymentPlanRecord[]>(DEFAULT_PAYMENT_PLANS);
  const allSubscriptions = React.useMemo(
    () => paymentPlans.filter((plan) => plan.isCurrent),
    [paymentPlans],
  );
  const productSubscriptionOptions = React.useMemo(
    () => [
      { id: 'support-basic', label: 'CompliCore (Screen)', route: '/products/support-basic' },
      { id: 'support-advanced', label: 'CompliAdvantage (Monitor)', route: '/products/support-advanced' },
      { id: 'support-enterprise', label: 'CompliComplete (Govern)', route: '/products/support-enterprise' },
      { id: 'service-ofac-compliance', label: 'OFAC Compliance', route: '/products/ofac-sdn-general-licenses' },
      { id: 'service-website-compliance', label: 'Website Compliance', route: '/console/website-compliance' },
      { id: 'service-dod-contracts-compliance', label: 'Government Compliance', route: '/console/government-compliance' },
      { id: 'service-education-compliance', label: 'Education Compliance', route: '/console/education-compliance' },
      { id: 'service-ai-compliance', label: 'AI Compliance', route: '/console/ai-compliance' },
      { id: 'feature-dashboards', label: 'Dashboards', route: '/products/dashboard-analytics' },
      { id: 'feature-live-monitoring', label: 'Live Environment Monitoring', route: '/products/real-time-batch-screening' },
      { id: 'feature-embedded-consultants', label: 'Embedded Consultants', route: '/products/embedded-consultants' },
      { id: 'feature-scheduled-audits', label: 'Scheduled Audits', route: '/console/audits' },
      { id: 'feature-exec-reporting', label: 'Executive Reporting', route: '/products/exec-reporting' },
      { id: 'feature-research-reports', label: 'Research Reports & Investigations', route: '/console/research' },
      { id: 'feature-usage-pricing', label: 'Usage-based Pricing Plans', route: '/products/usage-based-pricing-plans' },
    ].sort((left, right) => left.label.localeCompare(right.label)),
    [],
  );
  const [subscriptionCatalog, setSubscriptionCatalog] = React.useState<SubscriptionAdminRecord[]>([
    {
      id: 'sub-1001',
      name: 'OFAC Compliance',
      monthlyPrice: '$99/mo',
      status: 'active',
      renewalDate: '2026-08-01',
      ownerEmail: 'ops@nextcompli.com',
    },
    {
      id: 'sub-1002',
      name: 'Real-Time Alerts',
      monthlyPrice: 'Custom',
      status: 'trialing',
      renewalDate: '2026-05-15',
      ownerEmail: 'platform-admin@nextcompli.com',
    },
  ]);
  const [subscriptionForm, setSubscriptionForm] = React.useState<Omit<SubscriptionAdminRecord, 'id'>>({
    name: 'CompliCore (Screen)',
    monthlyPrice: '',
    status: 'trialing',
    renewalDate: '',
    ownerEmail: '',
  });
  const [isSubscriptionEditorOpen, setIsSubscriptionEditorOpen] = React.useState(false);
  const [editingSubscriptionId, setEditingSubscriptionId] = React.useState<string | null>(null);
  const [subscriptionFeedback, setSubscriptionFeedback] = React.useState<string | null>(null);
  const [subscriptionError, setSubscriptionError] = React.useState<string | null>(null);
  const [personalSubscriptionForm, setPersonalSubscriptionForm] = React.useState<Omit<SubscriptionAdminRecord, 'id' | 'ownerEmail'>>({
    name: '',
    monthlyPrice: '',
    status: 'trialing',
    renewalDate: '',
  });
  const [editingPersonalSubscriptionId, setEditingPersonalSubscriptionId] = React.useState<string | null>(null);
  const [personalSubscriptionFeedback, setPersonalSubscriptionFeedback] = React.useState<string | null>(null);
  const [personalSubscriptionError, setPersonalSubscriptionError] = React.useState<string | null>(null);
  const [editingTipId, setEditingTipId] = React.useState<string | null>(null);
  const [tipFeedback, setTipFeedback] = React.useState<string | null>(null);
  const [tipError, setTipError] = React.useState<string | null>(null);
  const [tipForm, setTipForm] = React.useState({
    titleEn: '',
    titleEs: '',
    bodyEn: '',
    bodyEs: '',
    navItem: 'dashboards' as DashboardNavItemId,
    actionLabelEn: 'Open Dashboards',
    actionLabelEs: 'Abrir Paneles',
  });
  const [activeResearchTab, setActiveResearchTab] = React.useState<'research' | 'queue' | 'completed' | 'subscriptions' | 'checks'>('research');
  const [researchSearchSettingsOpen, setResearchSearchSettingsOpen] = React.useState(false);
  const [researchForm, setResearchForm] = React.useState(() => createEmptyResearchForm());
  const [researchIntakeEpoch, setResearchIntakeEpoch] = React.useState(0);
  const [researchIntakeAutofillLocked, setResearchIntakeAutofillLocked] = React.useState(true);
  const [researchUploadFile, setResearchUploadFile] = React.useState<File | null>(null);
  const researchDocumentParseCacheRef = React.useRef<{
    fileKey: string;
    receipt: Record<string, unknown>;
    documentContext: string;
    parsedDocumentKeyData: Partial<Record<string, string>>;
  } | null>(null);
  const [researchReports, setResearchReports] = React.useState<ResearchReportRecord[]>(() => getStoredResearchReports());
  const researchReportsRef = React.useRef(researchReports);
  React.useEffect(() => {
    researchReportsRef.current = researchReports;
  }, [researchReports]);
  const [dismissedResearchReportIds, setDismissedResearchReportIds] = React.useState<Set<string>>(
    () => new Set(getStoredDismissedResearchReportIds()),
  );
  const dismissedResearchReportIdsRef = React.useRef(dismissedResearchReportIds);
  React.useEffect(() => {
    dismissedResearchReportIdsRef.current = dismissedResearchReportIds;
  }, [dismissedResearchReportIds]);
  React.useEffect(() => {
    // Auth often resolves after the first Vantage sync. Reloading only from
    // storage would wipe in-memory hydrations and leave Completed at 0 while
    // Queue still shows localStorage rows. Merge storage with what we already have.
    const dismissed = dismissedResearchReportIdsRef.current;
    const stored = filterResearchReportsForActor(
      getStoredResearchReports(researchAccessActor.userId),
      researchAccessActor,
    ).filter((report) => !researchReportIsDismissed(report, dismissed));
    setResearchReports((current) => {
      const byId = new Map<string, ResearchReportRecord>();
      stored.forEach((report) => byId.set(report.id, report));
      current.forEach((report) => {
        if (
          canAccessResearchReportRecord(report, researchAccessActor)
          && !researchReportIsDismissed(report, dismissed)
        ) {
          byId.set(report.id, report);
        }
      });
      return dedupeResearchReports(Array.from(byId.values()));
    });
    setSelectedResearchReportId((current) => {
      if (!current) return null;
      const stillVisible = filterResearchReportsForActor(
        researchReportsRef.current,
        researchAccessActor,
      ).some((report) => report.id === current && !researchReportIsDismissed(report, dismissed))
        || stored.some((report) => report.id === current);
      return stillVisible ? current : null;
    });
  }, [researchAccessActor]);
  const visibleResearchReports = React.useMemo(
    () => filterResearchReportsForActor(researchReports, researchAccessActor)
      .filter((report) => !researchReportIsDismissed(report, dismissedResearchReportIds)),
    [dismissedResearchReportIds, researchAccessActor, researchReports],
  );
  const [researchQueue, setResearchQueue] = React.useState<ResearchQueueItem[]>(() => getStoredResearchQueue());
  const [researchSessionExpired, setResearchSessionExpired] = React.useState(false);
  // Completed reports the gateway returned but that this browser is suppressing
  // because they were deleted here earlier. Surfaced so the tab is never silently empty.
  const [researchHiddenCompletedCount, setResearchHiddenCompletedCount] = React.useState(0);
  const [researchHiddenCompletedReportIds, setResearchHiddenCompletedReportIds] = React.useState<string[]>([]);
  const [isPurgingHiddenResearchReports, setIsPurgingHiddenResearchReports] = React.useState(false);
  const [isPurgingAllResearchReports, setIsPurgingAllResearchReports] = React.useState(false);
  const [researchQueueAck, setResearchQueueAck] = React.useState<string | null>(null);
  const [selectedResearchReportId, setSelectedResearchReportId] = React.useState<string | null>(null);
  const [editingResearchReportId, setEditingResearchReportId] = React.useState<string | null>(null);
  const [researchError, setResearchError] = React.useState<string | null>(null);
  const [researchFeedback, setResearchFeedback] = React.useState<string | null>(null);
  const [researchParseWarning, setResearchParseWarning] = React.useState<string | null>(null);
  const [researchIdentifierConfirmation, setResearchIdentifierConfirmation] = React.useState<IdentifierReadinessAssessment | null>(null);
  const [isResearchLoading, setIsResearchLoading] = React.useState(false);

  const resetResearchIntake = React.useCallback(() => {
    // Fresh object + form remount so leftover keyData and browser autofill cannot stick.
    setResearchForm(createEmptyResearchForm());
    setResearchIntakeAutofillLocked(true);
    setResearchIntakeEpoch((epoch) => epoch + 1);
    setEditingResearchReportId(null);
    setResearchUploadFile(null);
    researchDocumentParseCacheRef.current = null;
    setResearchParseWarning(null);
    setResearchIdentifierConfirmation(null);
    setResearchError(null);
  }, []);
  const [isResearchPdfGenerating, setIsResearchPdfGenerating] = React.useState(false);
  const [isResearchPreviewExpanded, setIsResearchPreviewExpanded] = React.useState(false);
  const [isResearchPreviewFactEditing, setIsResearchPreviewFactEditing] = React.useState(false);
  const [researchPreviewFactDraft, setResearchPreviewFactDraft] = React.useState<KeyData>({ ...EMPTY_KEY_DATA });
  const [researchPreviewFactListDraft, setResearchPreviewFactListDraft] = React.useState<Record<FactListField, string[]>>(() => buildFactListDraftFromKeyData(EMPTY_KEY_DATA));
  const [researchPreviewSourceCheckFactDraft, setResearchPreviewSourceCheckFactDraft] = React.useState<string[]>([]);
  const [researchPreviewSelectedRerunDepth, setResearchPreviewSelectedRerunDepth] = React.useState<number>(1);
  const [completedReportRerunDepthById, setCompletedReportRerunDepthById] = React.useState<Record<string, number>>({});
  const [researchPreviewFontSize, setResearchPreviewFontSize] = React.useState<'small' | 'medium' | 'large'>('medium');
  const researchReportPreviewRef = React.useRef<HTMLDivElement | null>(null);
  const researchPreviewCloseButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const researchPreviewLastFocusedRef = React.useRef<HTMLElement | null>(null);
  const [fullCheckHistory, setFullCheckHistory] = React.useState<ComprehensiveBackgroundCheckHistoryRecord[]>([]);
  const [fullCheckHistoryLoading, setFullCheckHistoryLoading] = React.useState(false);
  const [fullCheckHistoryError, setFullCheckHistoryError] = React.useState<string | null>(null);
  const [ofacScreeningSubjects, setOfacScreeningSubjects] = React.useState<OfacScreeningSubject[]>([]);
  const [ofacScreeningLoading, setOfacScreeningLoading] = React.useState(false);
  const [ofacScreeningError, setOfacScreeningError] = React.useState<string | null>(null);
  const [ofacScreeningMessage, setOfacScreeningMessage] = React.useState<string | null>(null);
  const [ofacScreeningFilter, setOfacScreeningFilter] = React.useState('');
  const [ofacScreeningRoleFilter, setOfacScreeningRoleFilter] = React.useState<'all' | OfacScreeningRelationshipType>('all');
  const [ofacScreeningStatusFilter, setOfacScreeningStatusFilter] = React.useState<'all' | 'clear' | 'review' | 'hit' | 'pending'>('all');
  const [ofacInvestigationOpenIds, setOfacInvestigationOpenIds] = React.useState<Set<number>>(() => new Set());
  const [ofacScreeningForm, setOfacScreeningForm] = React.useState({
    entityName: '',
    relationshipType: 'customer' as OfacScreeningRelationshipType,
    entityType: 'company',
    identifier: '',
    birthdate: '',
    country: '',
    hireLocation: '',
    notes: '',
    screenNow: true,
  });
  const [ofacWhitelistForm, setOfacWhitelistForm] = React.useState(() => ({
    ...emptyOfacWhitelistForm(),
    relationshipType: 'contractor' as OfacScreeningRelationshipType,
  }));
  const [ofacWhitelistIntakeKind, setOfacWhitelistIntakeKind] = React.useState<'' | OfacWhitelistIntakeKind>('');
  const [ofacNamedWhitelists, setOfacNamedWhitelists] = React.useState<OfacNamedWhitelist[]>([]);
  const [ofacCompanies, setOfacCompanies] = React.useState<OfacWorkspaceCompany[]>([]);
  const [ofacCanManageCompanies, setOfacCanManageCompanies] = React.useState(false);
  const [ofacSelectedCompanyId, setOfacSelectedCompanyId] = React.useState<number | null>(() => {
    try {
      const raw = window.localStorage.getItem(OFAC_ACTIVE_COMPANY_STORAGE_KEY);
      const parsed = Number(raw);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    } catch {
      return null;
    }
  });
  const [ofacCompanyEditor, setOfacCompanyEditor] = React.useState<'idle' | 'create' | 'rename'>('idle');
  const [ofacCompanyNameDraft, setOfacCompanyNameDraft] = React.useState('');
  const [ofacCompanyBusy, setOfacCompanyBusy] = React.useState(false);
  const [ofacWhitelistRenameOpen, setOfacWhitelistRenameOpen] = React.useState(false);
  const [ofacWhitelistRenameDraft, setOfacWhitelistRenameDraft] = React.useState('');
  const [ofacWhitelistBusy, setOfacWhitelistBusy] = React.useState(false);
  const [ofacActiveWhitelistId, setOfacActiveWhitelistId] = React.useState<number | null>(() => {
    try {
      const raw = window.localStorage.getItem(OFAC_ACTIVE_WHITELIST_STORAGE_KEY);
      const parsed = Number(raw);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    } catch {
      return null;
    }
  });
  const [ofacNamedWhitelistDefaultId, setOfacNamedWhitelistDefaultId] = React.useState<number | null>(null);
  const [ofacNewWhitelistName, setOfacNewWhitelistName] = React.useState('');
  const [ofacNewWhitelistNameError, setOfacNewWhitelistNameError] = React.useState<string | null>(null);
  const [ofacWhitelistCreating, setOfacWhitelistCreating] = React.useState(false);
  const [ofacWhitelistCreateOpen, setOfacWhitelistCreateOpen] = React.useState(false);
  const [ofacWhitelistControlDrafts, setOfacWhitelistControlDrafts] = React.useState<Record<number, { owner: string; recertifyDays: number }>>({});
  const [ofacNamedWhitelistsLoad, setOfacNamedWhitelistsLoad] = React.useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [ofacWhitelistCompany, setOfacWhitelistCompany] = React.useState<{ id: number | null; name: string | null }>({
    id: null,
    name: null,
  });
  const ofacNewWhitelistNameRef = React.useRef<HTMLInputElement | null>(null);
  const hasGatewaySession = isRealGatewayAccessToken(accessToken);
  const [ofacScreeningBusyId, setOfacScreeningBusyId] = React.useState<number | null>(null);
  const [ofacWhitelistSaving, setOfacWhitelistSaving] = React.useState<string | null>(null);
  const [ofacFalseHitPairs, setOfacFalseHitPairs] = React.useState<OfacFalseHitPair[]>([]);
  const [ofacFalseHitRationaleById, setOfacFalseHitRationaleById] = React.useState<Record<number, string>>({});
  const [ofacFalseHitSelectedById, setOfacFalseHitSelectedById] = React.useState<Record<number, string[]>>({});
  const [ofacFalseHitCsv, setOfacFalseHitCsv] = React.useState(
    'subject_id,list_entry_id,rationale\n',
  );
  const [ofacFalseHitImporting, setOfacFalseHitImporting] = React.useState(false);
  const [ofacScreeningScreeningAll, setOfacScreeningScreeningAll] = React.useState(false);
  const [ofacScreeningImporting, setOfacScreeningImporting] = React.useState(false);
  const [ofacScreeningImportScreenNow, setOfacScreeningImportScreenNow] = React.useState(false);
  const [ofacComplianceDashboard, setOfacComplianceDashboard] = React.useState<OfacComplianceDashboardPayload | null>(null);
  const [, setOfacComplianceLoading] = React.useState(false);
  const [ofacComplianceError, setOfacComplianceError] = React.useState<string | null>(null);
  const ofacScreeningFileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [fullCheckMonitoringMessage, setFullCheckMonitoringMessage] = React.useState<string | null>(null);

  const selectedResearchReport = React.useMemo(
    () => visibleResearchReports.find((report) => report.id === selectedResearchReportId) || null,
    [visibleResearchReports, selectedResearchReportId],
  );

  const selectedResearchReportIntakeFacts = React.useMemo(
    () => extractFactIntakeFactsFromReport(String(selectedResearchReport?.content || '')),
    [selectedResearchReport?.content],
  );

  const getCompletedReportRerunDepth = React.useCallback((reportId: string) => {
    const stored = completedReportRerunDepthById[reportId];
    if (typeof stored === 'number' && Number.isFinite(stored)) {
      return Math.max(1, Math.min(4, Math.round(stored)));
    }
    const report = researchReports.find((row) => row.id === reportId);
    const fromReport = Number(
      report?.affinityDepth
      || (report?.factDatabase?.metadata as { affinity_depth?: number } | undefined)?.affinity_depth
      || 0,
    );
    if (Number.isFinite(fromReport) && fromReport >= 1) {
      return Math.max(1, Math.min(4, Math.round(fromReport)));
    }
    return 1;
  }, [completedReportRerunDepthById, researchReports]);

  const mapResearchReportToFactDraft = React.useCallback((report: ResearchReportRecord | null): KeyData => {
    const merged = mergeResearchReportKeyData(report);
    return {
      ...EMPTY_KEY_DATA,
      ...merged,
      ageRange: normalizeAgeRangeForSubmit(merged.ageRange),
      ageBasis: researchPersonAgeBasis(merged),
    };
  }, []);

  const deriveTargetNameFromResearchForm = React.useCallback((form: ResearchIntakeForm = researchForm): string => {
    const firstCsvValue = (raw: string): string => (
      String(raw || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)[0] || ''
    );

    const composedPersonName = composeResearchPersonLegalName(form.keyData);
    const keyDataNames = composedPersonName || firstCsvValue(String(form.keyData?.names || ''));
    const keyDataCompanies = firstCsvValue(String(form.keyData?.companies || ''));
    const website = String(form.keyData?.website || '').trim();

    // Honor the operator's target-type toggle. Leftover person Name(s) from a prior
    // intake must not rename a company/website submission (e.g. venezuelanalysis.com
    // after an andrew l goss form fill).
    const resolvedType = form.targetTypeTouched
      ? form.targetType
      : (
        String(form.keyData?.birthdate || '').trim()
        || String(form.keyData?.ageRange || '').trim()
        || String(form.keyData?.ssn || '').trim()
        || String(form.keyData?.dlNumber || '').trim()
        || String(form.keyData?.givenName || '').trim()
        || String(form.keyData?.nationality || '').trim()
        || String(form.keyData?.searchCountry || '').trim()
          ? 'person'
          : (website || keyDataCompanies ? 'company' : form.targetType)
      );
    return deriveLocalResearchTargetName({
      targetType: resolvedType,
      personName: keyDataNames,
      companyName: keyDataCompanies,
      website,
    });
  }, [
    researchForm.keyData?.birthdate,
    researchForm.keyData?.ageRange,
    researchForm.keyData?.companies,
    researchForm.keyData?.dlNumber,
    researchForm.keyData?.givenName,
    researchForm.keyData?.givenName2,
    researchForm.keyData?.surname,
    researchForm.keyData?.surname2,
    researchForm.keyData?.nationality,
    researchForm.keyData?.searchCountry,
    researchForm.keyData?.nameStyle,
    researchForm.keyData?.names,
    researchForm.keyData?.ssn,
    researchForm.keyData?.website,
    researchForm.targetType,
    researchForm.targetTypeTouched,
  ]);

  React.useEffect(() => {
    if (researchForm.reportNameLocked) {
      return;
    }
    const next = deriveTargetNameFromResearchForm();
    setResearchForm((current) => (
      current.reportNameLocked || current.reportName === next
        ? current
        : { ...current, reportName: next }
    ));
  }, [
    deriveTargetNameFromResearchForm,
    researchForm.reportNameLocked,
    researchForm.keyData?.names,
    researchForm.keyData?.givenName,
    researchForm.keyData?.givenName2,
    researchForm.keyData?.surname,
    researchForm.keyData?.surname2,
    researchForm.keyData?.companies,
    researchForm.keyData?.website,
    researchForm.targetType,
    researchForm.targetTypeTouched,
  ]);

  const deriveWebsiteFromResearchForm = React.useCallback((form: ResearchIntakeForm = researchForm): string => {
    const keyDataWebsite = String(form.keyData?.website || '').trim();
    return keyDataWebsite;
  }, [researchForm.keyData?.website]);

  const deriveKnownAliasesFromResearchForm = React.useCallback((form: ResearchIntakeForm = researchForm): string[] => {
    const splitCsv = (value: string): string[] => value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    const operatorAliases = splitCsv(String(form.keyData?.knownAliases || ''));
    const nameOrderAliases = researchPersonSearchAliases(form.keyData, '');
    const familyAliases = researchFamilyMemberAliasSeeds(form.keyData?.familyMembers);
    return Array.from(new Set([...nameOrderAliases, ...operatorAliases, ...familyAliases]));
  }, [
    researchForm.keyData?.familyMembers,
    researchForm.keyData?.knownAliases,
    researchForm.keyData?.gender,
    researchForm.keyData?.includeMaidenName,
    researchForm.keyData?.givenName,
    researchForm.keyData?.givenName2,
    researchForm.keyData?.surname,
    researchForm.keyData?.surname2,
    researchForm.keyData?.nameStyle,
    researchForm.keyData?.nationality,
    researchForm.keyData?.formerNameStyle,
    researchForm.keyData?.formerGivenName,
    researchForm.keyData?.formerGivenName2,
    researchForm.keyData?.formerSurname,
    researchForm.keyData?.formerSurname2,
  ]);

  const deriveTargetTypeFromResearchForm = React.useCallback((form: ResearchIntakeForm = researchForm): 'company' | 'person' => {
    // Explicit console toggle wins once the operator has chosen. Until then, infer
    // from intake fields so a DOB-only person form still screens as a person.
    if (form.targetTypeTouched) {
      return form.targetType === 'person' ? 'person' : 'company';
    }

    const birthdate = String(form.keyData?.birthdate || '').trim();
    const ageRange = String(form.keyData?.ageRange || '').trim();
    const ssn = String(form.keyData?.ssn || '').trim();
    const dlNumber = String(form.keyData?.dlNumber || '').trim();
    const personNameParts = String(form.keyData?.givenName || '').trim() || String(form.keyData?.surname || '').trim();
    const nationality = String(form.keyData?.nationality || '').trim();
    const searchCountry = String(form.keyData?.searchCountry || '').trim();
    if (birthdate || ageRange || ssn || dlNumber || personNameParts || nationality || searchCountry) {
      return 'person';
    }

    const website = String(form.keyData?.website || '').trim();
    const companies = String(form.keyData?.companies || '').trim();
    if (website || companies) {
      return 'company';
    }

    const primaryName = String(form.keyData?.names || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)[0] || '';
    const normalized = ` ${primaryName.toLowerCase()} `;
    const companyHints = [' llc ', ' inc ', ' corp ', ' corporation ', ' ltd ', ' llp ', ' lp ', ' company ', ' co ', ' holdings ', ' group '];
    if (companyHints.some((hint) => normalized.includes(hint))) {
      return 'company';
    }

    const nameTokens = primaryName.split(/\s+/).filter(Boolean);
    if (nameTokens.length >= 2 && nameTokens.length <= 4) {
      return 'person';
    }

    return form.targetType;
  }, [
    researchForm.keyData?.birthdate,
    researchForm.keyData?.ageRange,
    researchForm.keyData?.companies,
    researchForm.keyData?.dlNumber,
    researchForm.keyData?.givenName,
    researchForm.keyData?.nationality,
    researchForm.keyData?.searchCountry,
    researchForm.keyData?.names,
    researchForm.keyData?.ssn,
    researchForm.keyData?.website,
    researchForm.targetType,
    researchForm.targetTypeTouched,
  ]);

  const buildResearchFallbackReport = React.useCallback((reportId: string): string => {
    const today = new Date().toISOString().slice(0, 10);
    const derivedTargetType = deriveTargetTypeFromResearchForm();
    const reportTitle = researchForm.reportName.trim() || deriveTargetNameFromResearchForm() || 'Report';
    const knownAliases = deriveKnownAliasesFromResearchForm();
    const aliasText = knownAliases.length > 0 ? knownAliases.join(', ') : (language === 'es' ? 'No proporcionado' : 'Not provided');
    const notesText = researchForm.notes.trim() || (language === 'es' ? 'Sin notas adicionales.' : 'No additional notes.');
    const jurisdictionText = researchForm.jurisdiction.trim() || (language === 'es' ? 'No especificada' : 'Not specified');
    const targetName = deriveTargetNameFromResearchForm();

    return [
      `# ${reportTitle}`,
      '',
      '## Report Overview',
      `Date: ${today}`,
      `Subject Name: ${targetName}`,
      `Jurisdiction: ${jurisdictionText}`,
      `Known Aliases: ${aliasText}`,
      '',
      '1) Identity and Entity Verification',
      '- Corporate registry or identity source checks',
      '- Ultimate beneficial owner and control-chain review',
      '- Adverse-name and alias normalization review',
      '',
      '2) Regulatory, Legal, and Enforcement Review',
      '- Sanctions, watchlist, and licensing context checks',
      '- Litigation, regulatory actions, and enforcement history',
      '- Sector and jurisdiction-specific obligations',
      '',
      '3) Financial and Operational Risk Signals',
      '- Revenue, solvency, and concentration indicators where available',
      '- Supplier/customer exposure and cross-border risk patterns',
      '- Media and reputation pattern review',
      '',
      '4) Recommended Next Actions',
      '- Escalate for analyst review when material risk indicators are found',
      '- Request missing KYC/KYB evidence for unresolved findings',
      '- Attach evidence package to the case audit timeline',
      '',
      'Analyst Notes',
      notesText,
    ].join('\n');
  }, [deriveKnownAliasesFromResearchForm, deriveTargetNameFromResearchForm, deriveTargetTypeFromResearchForm, language, researchForm.jurisdiction, researchForm.notes, researchForm.reportName]);

  const deriveResearchReportSubjectName = React.useCallback((report: ResearchReportRecord): string => {
    const rawTarget = String(report.targetName || '').trim();
    if (!rawTarget) {
      return language === 'es' ? 'Empresa objetivo' : 'Target Company';
    }

    const normalizeCompanyToken = (token: string): string => {
      const BUSINESS_SUFFIXES = [
        'technologies',
        'technology',
        'consultants',
        'consulting',
        'solutions',
        'services',
        'ventures',
        'holdings',
        'logistics',
        'systems',
        'global',
        'group',
        'labs',
        'tech',
        'llc',
        'inc',
        'corp',
        'company',
      ];
      const ACRONYMS = new Set(['llc', 'inc', 'corp', 'usa', 'uk', 'eu']);

      const splitSuffixes = (raw: string): string[] => {
        const working = raw.toLowerCase();
        for (const suffix of BUSINESS_SUFFIXES) {
          if (working.length > suffix.length + 2 && working.endsWith(suffix)) {
            const prefix = raw.slice(0, raw.length - suffix.length);
            return [...splitSuffixes(prefix), suffix];
          }
        }
        return [raw];
      };

      const cleaned = token.replace(/[-_]+/g, ' ').trim();
      if (!cleaned) {
        return token;
      }

      const baseWords = cleaned
        .split(/\s+/)
        .flatMap((word) => splitSuffixes(word))
        .map((word) => word.trim())
        .filter(Boolean);

      return baseWords
        .map((word) => {
          const lower = word.toLowerCase();
          if (ACRONYMS.has(lower)) {
            return lower.toUpperCase();
          }
          return lower.charAt(0).toUpperCase() + lower.slice(1);
        })
        .join(' ')
        .replace(/\s{2,}/g, ' ')
        .trim();
    };

    const social = socialProfileFromUrl(rawTarget) || socialProfileFromUrl(String(report.website || ''));
    if (social?.label) {
      return social.label;
    }
    if (isSocialPlatformHost(rawTarget)) {
      const fromWebsite = companySubjectFromWebsite(String(report.website || ''));
      if (fromWebsite) {
        return fromWebsite;
      }
    }

    try {
      const url = new URL(rawTarget.includes('://') ? rawTarget : `https://${rawTarget}`);
      const hostname = url.hostname.replace(/^www\./i, '').trim();
      if (isSocialPlatformHost(hostname)) {
        return rawTarget;
      }
      const baseToken = hostname.split('.')[0] || hostname;
      const normalized = normalizeCompanyToken(baseToken);
      return normalized || rawTarget;
    } catch {
      return rawTarget;
    }
  }, [language]);

  const formatResearchReportFileTimestamp = React.useCallback((value?: string): string => {
    const date = value ? new Date(value) : new Date();
    const source = Number.isNaN(date.getTime()) ? new Date() : date;
    const yyyy = String(source.getFullYear());
    const mm = String(source.getMonth() + 1).padStart(2, '0');
    const dd = String(source.getDate()).padStart(2, '0');
    const hh = String(source.getHours()).padStart(2, '0');
    const mi = String(source.getMinutes()).padStart(2, '0');
    const ss = String(source.getSeconds()).padStart(2, '0');
    return `${yyyy}${mm}${dd}-${hh}${mi}${ss}`;
  }, []);

  const buildResearchReportPdfBlob = React.useCallback((report: ResearchReportRecord): Blob => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'letter',
      compress: true,
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 52;
    const usableWidth = pageWidth - (margin * 2);
    const sectionSpacing = 12;
    const lineHeight = 15;
    let y = margin;

    const ensureSpace = (linesNeeded = 1) => {
      if (y + (linesNeeded * lineHeight) > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const writeWrapped = (text: string, fontSize: number, fontStyle: 'normal' | 'bold' = 'normal') => {
      const lines = doc.splitTextToSize(text, usableWidth) as string[];
      doc.setFont('helvetica', fontStyle);
      doc.setFontSize(fontSize);
      lines.forEach((line) => {
        ensureSpace();
        doc.text(line, margin, y);
        y += lineHeight;
      });
    };

    const trimRedundantReportIntro = (content: string): string => {
      const lines = content.split('\n');
      const firstSectionIndex = lines.findIndex((line) => /^##\s+/.test(line.trim()));
      if (firstSectionIndex > 0) {
        const introLines = lines.slice(0, firstSectionIndex);
        const introHasSourceChecks = introLines.some((line) => /\b(source|check|gleif|llc|dba|registry|watchlist|ofac|sdn|lei)\b/i.test(line));
        if (!introHasSourceChecks) {
          return lines.slice(firstSectionIndex).join('\n').trim();
        }
      }
      return content.trim();
    };

    const extractEvidenceRows = (content: string): Array<{ sourceCheck: string; check: string; result: string }> => {
      const rows: Array<{ sourceCheck: string; check: string; result: string }> = [];
      const lines = trimRedundantReportIntro(content)
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      let currentCategory = language === 'es' ? 'Hallazgos' : 'Findings';
      const reviewedLabel = language === 'es' ? 'Revisado' : 'Reviewed';

      const isResultSignal = (value: string): boolean =>
        /(no match|clear|passed|active|match found|failed|error|unavailable|not found|review required|reviewed|completed|queried|executed|ran)/i.test(value);

      const isLikelySourceName = (value: string): boolean =>
        /\b(gleif|llc|dba|ofac|sdn|lei|watchlist|registry|secretary of state|entity|business|sanctions?)\b/i.test(value);

      const parseEvidenceDetail = (detail: string): { sourceCheck: string; check: string; result: string } => {
        const colonIndex = detail.indexOf(':');
        if (colonIndex > 0 && colonIndex < detail.length - 1) {
          const rawCheck = detail.slice(0, colonIndex).trim();
          const rawResult = detail.slice(colonIndex + 1).trim();

          if (/^(source check|source|provider|registry)$/i.test(rawCheck)) {
            return {
              sourceCheck: rawResult,
              check: language === 'es' ? 'Estado' : 'Status',
              result: isResultSignal(rawResult) ? rawResult : reviewedLabel,
            };
          }

          return {
            sourceCheck: isLikelySourceName(rawCheck) ? rawCheck : currentCategory,
            check: rawCheck,
            result: rawResult,
          };
        }

        const signal = detail.match(/(no match|clear|passed|active|match found|failed|error|unavailable|not found|review required|reviewed|completed|queried|executed|ran)/i);
        const sourceFromDetail = isLikelySourceName(detail)
          ? detail.split(/[,-]/)[0].trim()
          : currentCategory;
        return {
          sourceCheck: sourceFromDetail,
          check: detail,
          result: signal ? signal[1] : reviewedLabel,
        };
      };

      lines.forEach((line) => {
        const sectionMatch = line.match(/^\d+\)\s+(.+)$/);
        if (sectionMatch) {
          const candidate = sectionMatch[1].trim();
          const looksLikeSectionTitle = !candidate.includes(':')
            && !isResultSignal(candidate)
            && candidate.length <= 100;
          if (looksLikeSectionTitle) {
            currentCategory = candidate;
            return;
          }
        }

        const markdownSectionMatch = line.match(/^#{1,6}\s+(.+)$/);
        if (markdownSectionMatch) {
          currentCategory = markdownSectionMatch[1].trim();
          return;
        }

        const itemMatch = line.match(/^(?:[-*]\s+|\d+[.)]\s+)(.+)$/);
        if (itemMatch) {
          rows.push(parseEvidenceDetail(itemMatch[1].trim()));
          return;
        }

        const plainKeyValue = line.match(/^([^:#]{2,120}):\s+(.+)$/);
        if (plainKeyValue) {
          rows.push(parseEvidenceDetail(line));
          return;
        }
      });

      return rows;
    };

    const buildExecutiveSummaryFacts = (rows: Array<{ sourceCheck: string; check: string; result: string }>): string[] => {
      if (!rows.length) {
        return [
          language === 'es'
            ? 'No se encontraron hallazgos estructurados. Revise la seccion de evidencia para detalles tecnicos.'
            : 'No structured findings were detected. Review the Evidence section for technical details.',
        ];
      }

      const highSignal = rows.filter((row) => {
        const value = `${row.check} ${row.result}`.toLowerCase();
        return /(match found|matched|hit|flag|failed|error|unavailable|sanction|adverse|denied|suspended|inactive|delinquent|penalty|watchlist)/.test(value);
      });
      const preferredRows = highSignal.length ? highSignal : rows.filter((row) => row.result.toLowerCase() !== 'reviewed');
      const selected = preferredRows.slice(0, 6);

      if (!selected.length) {
        return [
          language === 'es'
            ? `${rows.length} verificaciones completadas sin alertas explicitas.`
            : `${rows.length} checks completed with no explicit adverse flags.`,
        ];
      }

      return selected.map((row) => `${row.sourceCheck} - ${row.check}: ${row.result}`);
    };

    const reportTitle = resolveResearchReportDisplayTitle(report);
    doc.setProperties({
      title: reportTitle,
      subject: language === 'es' ? 'Informe de investigacion corporativa' : 'Corporate research report',
      author: 'NextCompli Vantage',
      creator: 'NextCompli',
      keywords: language === 'es' ? 'investigacion, evidencia, cumplimiento' : 'research, evidence, compliance',
    });
    const aliasText = report.knownAliases.length > 0 ? report.knownAliases.join(', ') : (language === 'es' ? 'No especificado' : 'Not specified');
    const jurisdictionText = report.jurisdiction.trim() || (language === 'es' ? 'No especificada' : 'Not specified');
    const notesText = report.notes.trim() || (language === 'es' ? 'Sin notas adicionales.' : 'No additional notes.');
    const normalizedFullReportContent = trimRedundantReportIntro(report.content);
    const evidenceRows = extractEvidenceRows(normalizedFullReportContent);
    const summaryFacts = buildExecutiveSummaryFacts(evidenceRows);

    writeWrapped(reportTitle, 17, 'bold');
    y += 4;

    writeWrapped(language === 'es' ? 'Entradas del reporte' : 'Report Inputs', 12, 'bold');
    y += 4;
    writeWrapped(`- ${language === 'es' ? 'Empresa objetivo' : 'Target company'}: ${subjectName}`, 10);
    writeWrapped(`- ${language === 'es' ? 'Entrada original' : 'Original input'}: ${report.targetName}`, 10);
    writeWrapped(`- ${language === 'es' ? 'Jurisdiccion principal' : 'Primary jurisdiction'}: ${jurisdictionText}`, 10);
    writeWrapped(`- ${language === 'es' ? 'Alias conocidos' : 'Known aliases'}: ${aliasText}`, 10);
    writeWrapped(`- ${language === 'es' ? 'Notas del analista' : 'Analyst notes'}: ${notesText}`, 10);
    {
      const timingBounds = resolveResearchSearchTimingBounds(report);
      writeWrapped(`- ${language === 'es' ? 'Inicio' : 'Started'}: ${
        timingBounds.startedAt
          ? new Date(timingBounds.startedAt).toLocaleString()
          : new Date(report.createdAt).toLocaleString()
      }`, 10);
      writeWrapped(`- ${language === 'es' ? 'Fin' : 'Ended'}: ${
        timingBounds.completedAt
          ? new Date(timingBounds.completedAt).toLocaleString()
          : new Date(report.updatedAt).toLocaleString()
      }`, 10);
      const elapsed = formatResearchSearchDuration(timingBounds.durationMs, language);
      if (elapsed) {
        writeWrapped(`- ${language === 'es' ? 'Duracion' : 'Elapsed'}: ${elapsed}`, 10);
      }
    }
    y += sectionSpacing;

    writeWrapped(language === 'es' ? 'Resumen ejecutivo' : 'Executive Summary', 12, 'bold');
    y += 4;
    summaryFacts.forEach((fact) => writeWrapped(`- ${fact}`, 10));
    y += sectionSpacing;

    writeWrapped(language === 'es' ? 'Evidencia' : 'Evidence', 12, 'bold');
    y += 4;
    if (evidenceRows.length === 0) {
      writeWrapped(language === 'es' ? '- No se encontraron evidencias estructuradas en el contenido del reporte.' : '- No structured evidence lines were detected in the report content.', 10);
    } else {
      evidenceRows.forEach((row, index) => {
        writeWrapped(`${index + 1}. ${language === 'es' ? 'Verificacion fuente' : 'Source Check'}: ${row.sourceCheck}`, 10, 'bold');
        writeWrapped(`   ${language === 'es' ? 'Chequeo' : 'Check'}: ${row.check}`, 10);
        writeWrapped(`   ${language === 'es' ? 'Resultado' : 'Result'}: ${row.result}`, 10);
        y += 4;
      });
    }
    y += sectionSpacing;

    writeWrapped(language === 'es' ? 'Informe de antecedentes completo' : 'Full Background Report', 12, 'bold');
    y += 6;

    normalizedFullReportContent.split('\n').forEach((rawLine) => {
      const trimmed = rawLine.trim();
      if (!trimmed) {
        y += 7;
        return;
      }
      writeWrapped(trimmed, 10, trimmed.match(/^\d+\)/) ? 'bold' : 'normal');
    });

    return doc.output('blob');
  }, [deriveResearchReportSubjectName, language]);

  const openResearchReportPdf = React.useCallback(async (report: ResearchReportRecord, options?: { downloadOnly?: boolean; suppressLoader?: boolean }) => {
    if (!options?.suppressLoader) {
      setIsResearchPdfGenerating(true);
      await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
    }

    try {
      const pdfBlob = buildResearchReportPdfBlob(report);
      const objectUrl = URL.createObjectURL(pdfBlob);
      const subjectName = deriveResearchReportSubjectName(report);
      const timestamp = formatResearchReportFileTimestamp(report.updatedAt);
      const fileName = `${toSafePdfFileName(`${subjectName}-research-report-${timestamp}`)}.pdf`;

      if (options?.downloadOnly) {
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = fileName;
        anchor.rel = 'noopener noreferrer';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        return;
      }

      const opened = window.open(objectUrl, RESEARCH_REPORT_PDF_WINDOW_NAME, 'noopener,noreferrer');
      if (!opened) {
        const fallback = document.createElement('a');
        fallback.href = objectUrl;
        fallback.target = '_blank';
        fallback.rel = 'noopener noreferrer';
        fallback.download = fileName;
        document.body.appendChild(fallback);
        fallback.click();
        fallback.remove();
      }

      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } finally {
      if (!options?.suppressLoader) {
        setIsResearchPdfGenerating(false);
      }
    }
  }, [buildResearchReportPdfBlob, deriveResearchReportSubjectName, formatResearchReportFileTimestamp]);

  const maskIdentifierValue = React.useCallback((value: string, sensitive?: boolean): string => {
    const trimmed = String(value || '').trim();
    if (!sensitive || !trimmed) {
      return trimmed;
    }
    if (trimmed.length <= 4) {
      return '••••';
    }
    return `${'•'.repeat(Math.max(0, trimmed.length - 4))}${trimmed.slice(-4)}`;
  }, []);

  const focusResearchKeyDataIntake = React.useCallback(() => {
    const element = document.getElementById('research-key-data-intake');
    if (element instanceof HTMLElement) {
      if (element instanceof HTMLDetailsElement) {
        element.open = true;
      }
      scrollElementWithinConsole(element);
    }
  }, []);

  const fetchResearchReportResponse = React.useCallback(async (
    payload: Record<string, unknown>,
    requestOptions?: {
      useSync?: boolean;
      useAsync?: boolean;
      detach?: boolean;
      onProgress?: (progress: number, message: string) => void;
    },
  ): Promise<ResearchReportResponse> => {
    const useAsync = requestOptions?.useSync === true
      ? false
      : requestOptions?.useAsync !== false;
    const submitUrl = useAsync ? researchReportAsyncServiceUrl : researchReportServiceUrl;
    // Auth is required so gateway can stamp created_by_*/company_id; without it
    // jobs are unscoped and hidden from non-admin queue/completed lists.
    const authHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    };
    let response = await fetch(submitUrl, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify(payload),
    });

    if (response.status === 404 && submitUrl !== defaultResearchReportServiceUrl && !useAsync) {
      response = await fetch(defaultResearchReportServiceUrl, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
    }

    if (!response.ok) {
      let detail = '';
      try {
        const errorBody = await response.json() as { detail?: unknown; message?: unknown };
        detail = formatApiErrorDetail(errorBody?.detail) || formatApiErrorDetail(errorBody?.message);
      } catch {
        // Ignore parse errors and fall back to status-only message.
      }
      throw new Error(detail ? `HTTP ${response.status}: ${detail}` : `HTTP ${response.status}`);
    }

    const initialBody = (await response.json()) as ResearchReportResponse & {
      job_id?: string;
      phase?: string;
      progress_percent?: number;
      result?: ResearchReportResponse;
      error?: string;
    };

    if (initialBody.status === 'identifier_confirmation_required' || !useAsync) {
      return initialBody;
    }

    if (requestOptions?.detach) {
      return initialBody;
    }

    const jobId = String(initialBody.job_id || '').trim();
    if (!jobId) {
      throw new Error('async-report-no-job-id');
    }

    const pollUrl = `${gatewayBaseUrl}/api/v1/research/reports/jobs/${encodeURIComponent(jobId)}`;
    const deadlineMs = Date.now() + (35 * 60 * 1000);
    while (Date.now() < deadlineMs) {
      await new Promise((resolve) => window.setTimeout(resolve, 3000));
      const pollResponse = await fetch(pollUrl, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (!pollResponse.ok) {
        continue;
      }
      const jobBody = await pollResponse.json() as {
        status?: string;
        phase?: string;
        progress_percent?: number;
        result?: ResearchReportResponse;
        error?: string;
      };
      if (typeof jobBody.progress_percent === 'number' && requestOptions?.onProgress) {
        const phaseLabel = String(jobBody.phase || '').trim();
        requestOptions.onProgress(jobBody.progress_percent, phaseLabel);
      }
      if (jobBody.status === 'completed' && jobBody.result) {
        return jobBody.result;
      }
      if (jobBody.status === 'failed') {
        throw new Error(String(jobBody.error || 'async-report-failed'));
      }
    }
    throw new Error('async-report-timeout');
  }, [
    accessToken,
    defaultResearchReportServiceUrl,
    formatApiErrorDetail,
    gatewayBaseUrl,
    researchReportAsyncServiceUrl,
    researchReportServiceUrl,
  ]);

  const handleGenerateResearchReport = React.useCallback(async (options?: {
    suppressLoader?: boolean;
    identifierConfirmed?: boolean;
    proceedWithoutIdentifiers?: boolean;
    useSync?: boolean;
    useAsync?: boolean;
    submitOnly?: boolean;
    intake?: ResearchIntakeSnapshot;
    onProgress?: (progress: number, message: string) => void;
  }): Promise<ResearchReportRecord | ResearchReportSubmitHandle | ResearchIdentifierConfirmationHandle | null> => {
    const intakeSnap = options?.intake || null;
    const form = intakeSnap?.form ?? researchForm;
    const uploadFile = intakeSnap ? intakeSnap.uploadFile : researchUploadFile;
    const editingId = intakeSnap ? intakeSnap.editingReportId : editingResearchReportId;
    const derivedWebsite = deriveWebsiteFromResearchForm(form);
    const derivedKnownAliases = deriveKnownAliasesFromResearchForm(form);
    const derivedTargetType = deriveTargetTypeFromResearchForm(form);
    const derivedTargetName = await fetchResolvedResearchTargetName({
      gatewayBaseUrl,
      accessToken: accessToken || undefined,
      targetType: derivedTargetType,
      targetName: deriveTargetNameFromResearchForm(form),
      website: derivedWebsite,
    });
    if (!derivedTargetName) {
      setResearchError(
        language === 'es'
          ? 'Ingresa un nombre, una compañía o un sitio web en Base de hechos.'
          : 'Enter a name, company, or website in Fact Database.',
      );
      return null;
    }

    setResearchError(null);
    setResearchFeedback(null);
    setResearchParseWarning(null);
    if (!options?.identifierConfirmed && !options?.proceedWithoutIdentifiers) {
      setResearchIdentifierConfirmation(null);
    }
    if (!options?.suppressLoader) {
      setIsResearchLoading(true);
    }

    const reportId = editingId || generateResearchReportId();

    // If a document is attached, parse it first and append extracted facts/text to notes context.
    let documentContext = '';
    const parsedDocumentKeyData: Partial<Record<string, string>> = {};
    let documentParseReceipt: Record<string, unknown> | null = null;
    const researchDocumentFileKey = uploadFile
      ? `${uploadFile.name}:${uploadFile.size}:${uploadFile.lastModified}`
      : '';
    const cachedDocumentParse = researchDocumentFileKey
      && researchDocumentParseCacheRef.current?.fileKey === researchDocumentFileKey
      ? researchDocumentParseCacheRef.current
      : null;
    if (cachedDocumentParse) {
      documentContext = cachedDocumentParse.documentContext;
      Object.assign(parsedDocumentKeyData, cachedDocumentParse.parsedDocumentKeyData);
      documentParseReceipt = cachedDocumentParse.receipt;
    } else if (uploadFile) {
      try {
        const formData = new FormData();
        formData.append('file', uploadFile);
        const uploadUrl = `${gatewayBaseUrl}/api/v1/research/parse-document`;
        const parseController = new AbortController();
        const parseTimer = window.setTimeout(() => parseController.abort(), 180000);
        let uploadResp: Response;
        try {
          uploadResp = await fetch(uploadUrl, { method: 'POST', body: formData, signal: parseController.signal });
        } finally {
          window.clearTimeout(parseTimer);
        }
        if (uploadResp.ok) {
          const uploadBody = await uploadResp.json() as {
            text?: string;
            parsed_fields?: Record<string, unknown>;
            extracted_facts?: unknown[];
            critical_id_status?: {
              overall_status?: string;
              requires_manual_review?: boolean;
              fields?: Record<string, unknown>;
            };
            filename?: string;
            size_bytes?: number;
            char_count?: number;
            page_count?: number;
            parser_mode?: string;
            coverage?: { required_coverage_ratio?: number; parsed_field_count?: number };
            field_confidence?: Record<string, unknown>;
            overall_confidence?: number;
            parser_job?: { job_id?: string; status?: string; provider?: string; mode?: string; completed_at?: string };
            file_sha256?: string;
            parsed_at?: string;
          };

          const parsedFields = uploadBody.parsed_fields && typeof uploadBody.parsed_fields === 'object'
            ? uploadBody.parsed_fields
            : {};
          const extractedFacts = Array.isArray(uploadBody.extracted_facts)
            ? uploadBody.extracted_facts.map((item) => String(item || '').trim()).filter(Boolean)
            : [];
          const parserText = String(uploadBody.text || '');

          const parsedFieldValueByKey: Record<string, string> = {};
          Object.entries(parsedFields).forEach(([key, value]) => {
            const normalizedKey = String(key || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
            const normalizedValue = String(value || '').trim();
            if (!normalizedKey || !normalizedValue) {
              return;
            }
            if (!parsedFieldValueByKey[normalizedKey]) {
              parsedFieldValueByKey[normalizedKey] = normalizedValue;
            }
          });

          const extractedFactValueByLabel: Record<string, string> = {};
          extractedFacts.forEach((fact) => {
            const parts = String(fact || '').split(':');
            if (parts.length < 2) {
              return;
            }
            const label = parts.shift() || '';
            const value = parts.join(':');
            const normalizedLabel = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
            const normalizedValue = value.trim();
            if (!normalizedLabel || !normalizedValue) {
              return;
            }
            if (!extractedFactValueByLabel[normalizedLabel]) {
              extractedFactValueByLabel[normalizedLabel] = normalizedValue;
            }
          });

          const pickDocValue = (...aliases: string[]): string => {
            const textPatternsByAlias: Record<string, RegExp[]> = {
              applicant_name: [/applicant\s+name\s*[:#-]?\s*([^\n]{2,140})/i],
              name: [/\bname\s*[:#-]?\s*([^\n]{2,140})/i],
              property_address: [/property\s+address\s*[:#-]?\s*([^\n]{4,180})/i],
              current_address: [/current\s+address\s*[:#-]?\s*([^\n]{4,180})/i],
              address: [/\baddress\s*[:#-]?\s*([^\n]{4,180})/i],
              date_of_birth: [/date\s+of\s+birth\s*[:#-]?\s*([^\n]{4,40})/i, /\bdob\b\s*[:#-]?\s*([^\n]{4,40})/i, /birthdate\s*[:#-]?\s*([^\n]{4,40})/i, /\bbday\b\s*[:#-]?\s*([^\n]{4,40})/i, /birthday\s*[:#-]?\s*([^\n]{4,40})/i],
              birthdate: [/birthdate\s*[:#-]?\s*([^\n]{4,40})/i, /date\s+of\s+birth\s*[:#-]?\s*([^\n]{4,40})/i, /\bbday\b\s*[:#-]?\s*([^\n]{4,40})/i, /birthday\s*[:#-]?\s*([^\n]{4,40})/i],
              dob: [/\bdob\b\s*[:#-]?\s*([^\n]{4,40})/i, /\bbday\b\s*[:#-]?\s*([^\n]{4,40})/i],
              bday: [/\bbday\b\s*[:#-]?\s*([^\n]{4,40})/i, /birthday\s*[:#-]?\s*([^\n]{4,40})/i, /date\s+of\s+birth\s*[:#-]?\s*([^\n]{4,40})/i],
              social_security_number: [/social\s+security(?:\s+number)?\s*[:#-]?\s*([^\n]{4,24})/i, /\bssn\b\s*[:#-]?\s*([^\n]{4,24})/i, /soc\.?\s*sec\.?\s*(?:no\.?|number)?\s*[:#-]?\s*([^\n]{4,24})/i],
              ssn: [/\bssn\b\s*[:#-]?\s*([^\n]{4,24})/i, /social\s+security(?:\s+number)?\s*[:#-]?\s*([^\n]{4,24})/i, /soc\.?\s*sec\.?\s*(?:no\.?|number)?\s*[:#-]?\s*([^\n]{4,24})/i],
              soc_sec_no: [/soc\.?\s*sec\.?\s*no\.?\s*[:#-]?\s*([^\n]{4,24})/i, /social\s+sec(?:urity)?\s*[:#-]?\s*([^\n]{4,24})/i],
              driver_license_number: [/driver\s+licen[sc]e(?:\s*(?:number|no\.?|id))?\s*[:#-]?\s*([^\n]{3,40})/i, /\bdl#?\b\s*[:#-]?\s*([^\n]{3,40})/i, /\bdln\b\s*[:#-]?\s*([^\n]{3,40})/i],
              dl_number: [/\bdl\b\s*[:#-]?\s*([^\n]{3,40})/i, /\bdln\b\s*[:#-]?\s*([^\n]{3,40})/i, /driver\s+licen[sc]e(?:\s*(?:number|no\.?|id))?\s*[:#-]?\s*([^\n]{3,40})/i],
              dln: [/\bdln\b\s*[:#-]?\s*([^\n]{3,40})/i, /\bdl#?\b\s*[:#-]?\s*([^\n]{3,40})/i, /driver\s+licen[sc]e(?:\s*(?:number|no\.?|id))?\s*[:#-]?\s*([^\n]{3,40})/i],
              current_employer: [/current\s+employer\s*[:#-]?\s*([^\n]{2,120})/i],
              previous_employer: [/previous\s+employer\s*[:#-]?\s*([^\n]{2,120})/i, /former\s+employer\s*[:#-]?\s*([^\n]{2,120})/i],
              employer: [/employer\s*[:#-]?\s*([^\n]{2,120})/i],
              monthly_rent: [/monthly\s+rent\s*[:#-]?\s*([^\n]{1,32})/i],
              mobile_phone: [/mobile\s+phone\s*[:#-]?\s*([^\n]{6,40})/i, /phone\s*[:#-]?\s*([^\n]{6,40})/i],
              height: [/\bheight\b\s*[:#-]?\s*([^\n]{1,24})/i],
              weight: [/\bweight\b\s*[:#-]?\s*([^\n]{1,24})/i],
              eye_color: [/eye(?:s)?(?:\s*color)?\s*[:#-]?\s*([^\n]{3,24})/i],
              hair_color: [/hair(?:\s*color)?\s*[:#-]?\s*([^\n]{3,24})/i],
              realtor_name: [/realtor(?:\s+name)?\s*[:#-]?\s*([^\n]{3,80})/i],
              realtor_phone: [/realtor\s+phone\s*[:#-]?\s*([^\n]{6,40})/i],
              emergency_contact_name: [/emergency\s+contact(?:\s+name)?\s*[:#-]?\s*([^\n]{3,80})/i],
              employment_verification_phone: [/employment\s+verification(?:\s+(?:phone|contact))?\s*[:#-]?\s*([^\n]{6,40})/i],
              vehicle_year: [/vehicle\s+year\s*[:#-]?\s*([^\n]{1,24})/i],
              vehicle_make: [/vehicle\s+make\s*[:#-]?\s*([^\n]{1,40})/i],
              vehicle_model: [/vehicle\s+model\s*[:#-]?\s*([^\n]{1,40})/i],
              vehicle_color: [/vehicle\s+color\s*[:#-]?\s*([^\n]{1,40})/i],
              vehicle_license_plate: [/license\s+plate\s*[:#-]?\s*([^\n]{1,32})/i, /\btag\s*#?\s*[:#-]?\s*([^\n]{1,32})/i],
              website: [/\bwebsite\b\s*[:#-]?\s*([^\n]{4,140})/i, /official\s+domain\s*[:#-]?\s*([^\n]{4,140})/i],
            };

            for (const alias of aliases) {
              const normalizedAlias = String(alias || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
              if (!normalizedAlias) {
                continue;
              }
              const parsedValue = parsedFieldValueByKey[normalizedAlias];
              if (parsedValue) {
                return parsedValue;
              }
              const extractedValue = extractedFactValueByLabel[normalizedAlias];
              if (extractedValue) {
                return extractedValue;
              }
              const textPatterns = textPatternsByAlias[normalizedAlias] || [];
              for (const pattern of textPatterns) {
                const match = parserText.match(pattern);
                const matchedValue = String(match?.[1] || '').trim();
                if (matchedValue) {
                  return matchedValue;
                }
              }
            }
            return '';
          };

          const applicantName = pickDocValue('applicant_name', 'name', 'names');
          const propertyAddress = pickDocValue('property_address', 'current_address', 'address', 'applicant_s_current_address');
          const birthdate = pickDocValue('date_of_birth', 'birthdate', 'dob', 'bday', 'birthday');
          const ssn = pickDocValue('social_security_number', 'ssn', 'soc_sec_no', 'social_sec');
          const dlNumber = pickDocValue('driver_license_number', 'dl_number', 'dln', 'driver_license', 'driver_licence_number');
          const currentEmployer = pickDocValue('current_employer', 'employer', 'company');
          const previousEmployer = pickDocValue('previous_employer', 'former_employer');
          const employer = [currentEmployer, previousEmployer].filter(Boolean).join(', ');
          const websiteFromDoc = pickDocValue('website', 'official_domain', 'domain');
          const mobilePhone = pickDocValue('mobile_phone', 'phone', 'phone_number');
          const emailFromDoc = pickDocValue('email', 'email_address', 'emails');

          if (applicantName) parsedDocumentKeyData.names = applicantName;
          if (propertyAddress) parsedDocumentKeyData.addresses = propertyAddress;
          if (mobilePhone) parsedDocumentKeyData.phones = mobilePhone;
          if (emailFromDoc) parsedDocumentKeyData.emails = emailFromDoc;
          if (birthdate) parsedDocumentKeyData.birthdate = birthdate;
          if (ssn) parsedDocumentKeyData.ssn = ssn;
          if (dlNumber) parsedDocumentKeyData.dlNumber = dlNumber;
          if (employer) parsedDocumentKeyData.companies = employer;
          if (websiteFromDoc) parsedDocumentKeyData.website = websiteFromDoc;

          const generatedFacts: string[] = [];
          const pushFact = (label: string, rawValue: unknown) => {
            const value = String(rawValue || '').trim();
            if (value) {
              generatedFacts.push(`${label}: ${value}`);
            }
          };
          pushFact('Property Address', pickDocValue('property_address', 'current_address', 'address'));
          pushFact('Monthly Rent', pickDocValue('monthly_rent', 'rent'));
          pushFact('Applicant Name', pickDocValue('applicant_name', 'name', 'names'));
          pushFact('Mobile Phone', pickDocValue('mobile_phone', 'phone', 'phone_number'));
          pushFact('Email', pickDocValue('email', 'email_address', 'emails'));
          pushFact('Date of Birth', pickDocValue('date_of_birth', 'birthdate', 'dob', 'bday', 'birthday'));
          pushFact('Social Security Number', pickDocValue('social_security_number', 'ssn', 'soc_sec_no', 'social_sec'));
          pushFact('Driver License Number', pickDocValue('driver_license_number', 'dl_number', 'dln', 'driver_license'));
          pushFact('Current Employer', pickDocValue('current_employer', 'employer', 'company'));
          pushFact('Previous Employer', pickDocValue('previous_employer', 'former_employer'));
          pushFact('Height', pickDocValue('height'));
          pushFact('Weight', pickDocValue('weight'));
          pushFact('Eye Color', pickDocValue('eye_color'));
          pushFact('Hair Color', pickDocValue('hair_color'));
          pushFact('Realtor Name', pickDocValue('realtor_name'));
          pushFact('Realtor Phone', pickDocValue('realtor_phone'));
          pushFact('Emergency Contact', pickDocValue('emergency_contact_name', 'emergency_contact'));
          pushFact('Employment Verification Phone', pickDocValue('employment_verification_phone'));
          pushFact('Vehicle Year', pickDocValue('vehicle_year'));
          pushFact('Vehicle Make', pickDocValue('vehicle_make'));
          pushFact('Vehicle Model', pickDocValue('vehicle_model'));
          pushFact('Vehicle Color', pickDocValue('vehicle_color'));
          pushFact('Vehicle License Plate', pickDocValue('vehicle_license_plate', 'tag'));

          const factLines = extractedFacts.length > 0 ? extractedFacts : generatedFacts;
          if (factLines.length > 0) {
            documentContext = `\n\n[Attached document: ${uploadFile.name}]\n${factLines.join('\n').slice(0, 8000)}`;
          } else if (uploadBody.text) {
            documentContext = `\n\n[Attached document: ${uploadFile.name}]\n${uploadBody.text.slice(0, 8000)}`;
          }

          documentParseReceipt = {
            event_name: 'document.parse.completed',
            status: 'completed',
            filename: String(uploadBody.filename || uploadFile.name || '').trim(),
            file_size_bytes: Number(uploadBody.size_bytes || uploadFile.size || 0),
            file_sha256: String(uploadBody.file_sha256 || '').trim(),
            parser_mode: String(uploadBody.parser_mode || '').trim(),
            parser_job_id: String(uploadBody.parser_job?.job_id || '').trim(),
            parser_job_status: String(uploadBody.parser_job?.status || '').trim(),
            parser_provider: String(uploadBody.parser_job?.provider || '').trim(),
            parser_mode_type: String(uploadBody.parser_job?.mode || '').trim(),
            parsed_at: String(uploadBody.parsed_at || uploadBody.parser_job?.completed_at || '').trim(),
            parsed_fields: parsedFields,
            extracted_facts: extractedFacts,
            page_count: Number(uploadBody.page_count || 0),
            char_count: Number(uploadBody.char_count || 0),
            parsed_field_count: Number(uploadBody.coverage?.parsed_field_count || Object.keys(parsedFields).length),
            required_coverage_ratio: Number(uploadBody.coverage?.required_coverage_ratio || 0),
            overall_confidence: Number(uploadBody.overall_confidence || 0),
            field_confidence: uploadBody.field_confidence && typeof uploadBody.field_confidence === 'object'
              ? uploadBody.field_confidence
              : undefined,
            template_detection: uploadBody.template_detection && typeof uploadBody.template_detection === 'object'
              ? uploadBody.template_detection
              : undefined,
            document_attestation: uploadBody.document_attestation && typeof uploadBody.document_attestation === 'object'
              ? uploadBody.document_attestation
              : undefined,
            critical_id_status: uploadBody.critical_id_status && typeof uploadBody.critical_id_status === 'object'
              ? uploadBody.critical_id_status
              : undefined,
          };
          if (Object.keys(parsedFields).length > 0) {
            researchDocumentParseCacheRef.current = {
              fileKey: researchDocumentFileKey,
              receipt: documentParseReceipt,
              documentContext,
              parsedDocumentKeyData: { ...parsedDocumentKeyData },
            };
          }
        } else {
          setResearchParseWarning(
            language === 'es'
              ? `No se pudo analizar el documento adjunto (HTTP ${uploadResp.status}). Se continuará con los datos ingresados manualmente.`
              : `Could not parse the attached document (HTTP ${uploadResp.status}). Continuing with manually entered data.`,
          );
          documentParseReceipt = {
            event_name: 'document.parse.failed',
            status: 'failed',
            filename: uploadFile.name,
            file_size_bytes: uploadFile.size,
            parser_http_status: uploadResp.status,
          };
          trackUxEvent({
            event_type: 'upload_failed',
            product: 'vantage',
            funnel_key: 'research_upload',
            step: 'parse-document',
            last_path: '/console/research',
            error_code: String(uploadResp.status),
            payload: { filename: uploadFile.name },
          });
        }
      } catch (error) {
        const timedOut = error instanceof DOMException && error.name === 'AbortError';
        setResearchParseWarning(
          language === 'es'
            ? (timedOut
              ? 'El análisis del documento tardó más de 3 minutos. Se continuará con los datos ingresados manualmente; vuelva a adjuntar el PDF si faltan identificadores.'
              : 'El servicio de análisis de documentos no respondió. Se continuará con los datos ingresados manualmente.')
            : (timedOut
              ? 'Document parser timed out after 3 minutes. Continuing with manually entered data; re-attach the PDF if identifiers are missing.'
              : 'Document parser service did not respond. Continuing with manually entered data.'),
        );
        documentParseReceipt = {
          event_name: timedOut ? 'document.parse.timeout' : 'document.parse.unavailable',
          status: timedOut ? 'timeout' : 'unavailable',
          filename: uploadFile.name,
          file_size_bytes: uploadFile.size,
        };
        trackUxEvent({
          event_type: 'upload_failed',
          product: 'vantage',
          funnel_key: 'research_upload',
          step: timedOut ? 'timeout' : 'unavailable',
          last_path: '/console/research',
          error_code: timedOut ? 'timeout' : 'network',
          payload: { filename: uploadFile.name },
        });
      }
    }

    const mergeCsvUnique = (primaryValue: string, secondaryValue: string): string => {
      const merged = [
        ...String(primaryValue || '').split(',').map((item) => item.trim()).filter(Boolean),
        ...String(secondaryValue || '').split(',').map((item) => item.trim()).filter(Boolean),
      ];
      const deduped: string[] = [];
      const seen = new Set<string>();
      for (const entry of merged) {
        const normalized = entry.toLowerCase();
        if (!seen.has(normalized)) {
          seen.add(normalized);
          deduped.push(entry);
        }
      }
      return deduped.join(', ');
    };

    const divertedContacts = divertEmailsFromSocialHandles(
      String(form.keyData?.socialMediaHandles || '').trim(),
      mergeCsvUnique(String(form.keyData?.emails || '').trim(), parsedDocumentKeyData.emails || ''),
    );

    const composedPersonName = composeResearchPersonLegalName(form.keyData);
    const composedPlace = composeResearchPersonPlace(form.keyData, language);
    const originCountryCodes = researchPersonOriginCountryCodes(form.keyData);
    const agePayload = researchPersonAgePayload({
      ageBasis: form.keyData?.ageBasis,
      birthdate: String(form.keyData?.birthdate || parsedDocumentKeyData.birthdate || '').trim(),
      ageRange: String(form.keyData?.ageRange || ''),
    });
    const familyMembersPayload = (form.keyData?.familyMembers || [])
      .map((row) => ({
        name: String(row.name || '').trim(),
        relation: String(row.relation || 'other').trim() || 'other',
      }))
      .filter((row) => row.name);
    const addressCandidates = mergeCsvUnique(
      composedPlace || String(form.keyData?.addresses || '').trim(),
      parsedDocumentKeyData.addresses || '',
    )
      .split(/[,;]/)
      .map((item) => item.trim())
      .filter((item) => item && !isCountryOnlyResearchPlace(item));

    const keyDataPayload: KeyData & {
      aliases?: string;
      nationality?: string;
      name_style?: string;
      national_id?: string;
      national_id_type?: string;
      family_members?: { name: string; relation: string }[];
      originCountries?: string;
    } = {
      // Seed names from company/website when Name(s) was left blank so the
      // fact database and gateway always have a canonical target label.
      names: mergeCsvUnique(
        composedPersonName || String(form.keyData?.names || '').trim() || derivedTargetName,
        parsedDocumentKeyData.names || '',
      ),
      // Keep legacy key for backend compatibility, sourced from knownAliases only.
      aliases: String(form.keyData?.knownAliases || '').trim(),
      addresses: addressCandidates.join(', '),
      phones: mergeCsvUnique(String(form.keyData?.phones || '').trim(), parsedDocumentKeyData.phones || ''),
      emails: divertedContacts.emails.join(', '),
      birthdate: agePayload.birthdate,
      ageRange: agePayload.ageRange,
      ageBasis: form.keyData?.ageBasis,
      ssn: preferCompleteIdentifier(parsedDocumentKeyData.ssn || '', String(form.keyData?.ssn || '').trim(), 'ssn'),
      dlNumber: preferCompleteIdentifier(parsedDocumentKeyData.dlNumber || '', String(form.keyData?.dlNumber || '').trim(), 'dl'),
      companies: mergeCsvUnique(String(form.keyData?.companies || '').trim(), parsedDocumentKeyData.companies || ''),
      socialMediaHandles: divertedContacts.social.join(', '),
      website: String(form.keyData?.website || parsedDocumentKeyData.website || '').trim(),
      knownAliases: String(form.keyData?.knownAliases || '').trim(),
      nationality: String(form.keyData?.nationality || '').trim(),
      nationalities: String(form.keyData?.nationalities || '').trim(),
      originCountries: originCountryCodes.join(','),
      searchCountry: String(form.keyData?.searchCountry || '').trim(),
      nameStyle: researchPersonNameStyle(form.keyData),
      nameStyleLocked: Boolean(form.keyData?.nameStyleLocked),
      givenName: String(form.keyData?.givenName || '').trim(),
      givenName2: String(form.keyData?.givenName2 || '').trim(),
      surname: String(form.keyData?.surname || '').trim(),
      surname2: String(form.keyData?.surname2 || '').trim(),
      nationalIdType: String(form.keyData?.nationalIdType || '').trim(),
      nationalId: String(form.keyData?.nationalId || '').trim(),
      locationCountry: String(form.keyData?.locationCountry || '').trim(),
      locationOtherCountry: String(form.keyData?.locationOtherCountry || '').trim(),
      locationRegion: String(form.keyData?.locationRegion || '').trim(),
      locationOtherRegion: String(form.keyData?.locationOtherRegion || '').trim(),
      locationCounty: String(form.keyData?.locationCounty || '').trim(),
      locationOtherCounty: String(form.keyData?.locationOtherCounty || '').trim(),
      locationCity: String(form.keyData?.locationCity || '').trim(),
      locationOtherCity: String(form.keyData?.locationOtherCity || '').trim(),
      addressLine: String(form.keyData?.addressLine || '').trim(),
      addressLine2: String(form.keyData?.addressLine2 || '').trim(),
      postalCode: String(form.keyData?.postalCode || '').trim(),
      includeMaidenName: Boolean(form.keyData?.includeMaidenName),
      familyMembers: familyMembersPayload,
      family_members: familyMembersPayload.length ? familyMembersPayload : undefined,
    };

    const factDatabasePayload = buildResearchFactDatabase(
      {
        targetType: derivedTargetType,
        targetName: derivedTargetName,
        website: derivedWebsite || undefined,
        jurisdiction: form.jurisdiction.trim(),
        knownAliases: derivedKnownAliases,
        notes: '',
        documentParseReceipt: documentParseReceipt || undefined,
      },
      keyDataPayload,
      form.affinityDepth,
    );

    const mergedNotes = `${form.notes.trim()}${documentContext}`.trim();
    const payload = {
      report_name: form.reportName.trim() || undefined,
      target_type: derivedTargetType,
      target_name: derivedTargetName,
      website: derivedWebsite || undefined,
      jurisdiction: form.jurisdiction.trim() || undefined,
      affinity_depth: form.affinityDepth,
      known_aliases: derivedKnownAliases,
      first_name: String(form.keyData?.givenName || '').trim() || undefined,
      middle_name: String(form.keyData?.givenName2 || '').trim() || undefined,
      last_name: String(form.keyData?.surname || '').trim() || undefined,
      country: String(form.keyData?.searchCountry || form.keyData?.locationCountry || '').trim() || undefined,
      nationality: String(form.keyData?.nationality || '').trim() || undefined,
      name_style: form.keyData?.nameStyleLocked
        ? researchPersonNameStyle(form.keyData)
        : undefined,
      date_of_birth: String(keyDataPayload.birthdate || '').trim() || undefined,
      age_range: String(keyDataPayload.ageRange || '').trim() || undefined,
      phone: String(keyDataPayload.phones || '').split(/[,;]/).map((item) => item.trim()).filter(Boolean)[0] || undefined,
      // Backend enforces 8000 max chars on notes; clip client payload to avoid 422.
      notes: mergedNotes ? mergedNotes.slice(0, RESEARCH_REPORT_NOTES_MAX_LENGTH) : undefined,
      key_data: keyDataPayload,
      fact_database: factDatabasePayload,
      document_parse_receipt: documentParseReceipt || undefined,
      identifier_confirmed: options?.identifierConfirmed === true,
      proceed_without_identifiers: options?.proceedWithoutIdentifiers === true,
      include_source_intelligence: true,
      source_intelligence_profile: form.sourceIntelligenceProfile,
      email_addresses: form.emailAddresses
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
      email_groups: form.emailGroups
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
    };

    if (
      derivedTargetType === 'person'
      && !options?.identifierConfirmed
      && !options?.proceedWithoutIdentifiers
    ) {
      try {
        const readinessResponse = await fetch(`${gatewayBaseUrl}/api/v1/research/identifier-readiness`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({
            target_type: derivedTargetType,
            target_name: derivedTargetName,
            key_data: keyDataPayload,
            fact_database: factDatabasePayload,
            parsed_fields: documentParseReceipt?.parsed_fields,
            field_confidence: documentParseReceipt && typeof documentParseReceipt.field_confidence === 'object'
              ? documentParseReceipt.field_confidence
              : undefined,
            document_attestation: documentParseReceipt && typeof documentParseReceipt.document_attestation === 'object'
              ? documentParseReceipt.document_attestation
              : undefined,
          }),
        });
        if (readinessResponse.ok) {
          const readinessBody = await readinessResponse.json() as {
            identifier_readiness?: IdentifierReadinessAssessment;
            status?: string;
          };
          const assessment = readinessBody.identifier_readiness;
          if (assessment?.blocked) {
            setResearchIdentifierConfirmation(assessment);
            setIsResearchLoading(false);
            return { kind: 'identifier_confirmation_required' };
          }
        }
      } catch {
        // Non-blocking: Vantage still enforces the gate if this pre-check fails.
      }
    }

    try {
      if (options?.submitOnly) {
        const body = await fetchResearchReportResponse(payload, {
          useAsync: true,
          detach: true,
        });
        if (body.status === 'identifier_confirmation_required') {
          setResearchIdentifierConfirmation(body.identifier_readiness || {
            blocked: true,
            guidance: body.message,
          });
          return { kind: 'identifier_confirmation_required' };
        }
        const jobId = String((body as { job_id?: string }).job_id || '').trim();
        if (!jobId) {
          throw new Error('async-report-no-job-id');
        }
        return { kind: 'async_submitted', jobId, reportId, keyDataPayload };
      }

      const body = await fetchResearchReportResponse(payload, {
        useSync: options?.useSync,
        useAsync: options?.useAsync,
        onProgress: options?.onProgress,
      });
      if (body.status === 'identifier_confirmation_required') {
        setResearchIdentifierConfirmation(body.identifier_readiness || {
          blocked: true,
          guidance: body.message,
        });
        setIsResearchLoading(false);
        return { kind: 'identifier_confirmation_required' };
      }
      const reportText = String(body.report || body.markdown || body.content || '').trim();
      if (!reportText && body.status === 'partial_timeout') {
        throw new Error(
          language === 'es'
            ? 'La búsqueda excedió el tiempo límite y no se generó un reporte parcial.'
            : 'Screening timed out before a partial report could be generated.',
        );
      }
      if (!reportText) {
        throw new Error('empty-report');
      }
      if (body.status === 'partial_timeout') {
        setResearchParseWarning(
          language === 'es'
            ? `La búsqueda exhaustiva nacional excedió el tiempo límite del gateway. Se guardó un reporte parcial (ID: ${String(body.screening_id || 'n/a')}).`
            : `Nationwide exhaustive screening exceeded the gateway time limit. A partial report was saved (ID: ${String(body.screening_id || 'n/a')}).`,
        );
      }

      const now = new Date().toISOString();
      const reportName = form.reportName.trim() || derivedTargetName;
      const searchTiming = extractResearchSearchTiming(body);
      const affinityGraph = normalizeResearchAffinityGraph(body.affinity_graph);
      const rerunOptions = normalizeResearchRerunOptions(body.rerun_options);
      const keyDataInput = keyDataPayload;
      const currentReportSnapshot = {
        id: reportId,
        reportName,
        targetType: derivedTargetType,
        targetName: derivedTargetName,
        website: derivedWebsite || undefined,
        jurisdiction: form.jurisdiction.trim(),
        knownAliases: derivedKnownAliases,
        notes: mergedNotes,
        affinityDepth: typeof body.affinity_depth === 'number' ? body.affinity_depth : form.affinityDepth,
        affinityGraph,
        documentParseReceipt: documentParseReceipt || undefined,
        emailAddresses: form.emailAddresses
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
        emailGroups: form.emailGroups
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
        content: reportText,
        createdAt: editingId
          ? (researchReports.find((item) => item.id === editingId)?.createdAt || now)
          : now,
        updatedAt: now,
      };
      const factDatabase = hydrateResearchFactDatabase(body.fact_database)
        || buildResearchFactDatabase(currentReportSnapshot, keyDataInput, currentReportSnapshot.affinityDepth);
      const nextRecord: ResearchReportRecord = stampResearchReportOwnership({
        ...currentReportSnapshot,
        ...searchTiming,
        screeningId: extractResearchScreeningId(body.screening_id || body) || undefined,
        riskScore: typeof body.risk_score === 'number' ? body.risk_score : undefined,
        matchCount: typeof body.match_count === 'number' ? body.match_count : undefined,
        keyData: buildResearchKeyData(currentReportSnapshot, keyDataInput),
        factDatabase,
        sourceOfTruth: String(body.source_of_truth || 'fact_database').trim(),
        rerunOptions: rerunOptions.length > 0 ? rerunOptions : buildDefaultResearchRerunOptions(currentReportSnapshot.affinityDepth),
        isFallback: false,
      });

      setResearchReports((current) => {
        if (editingId) {
          return dedupeResearchReports(current.map((item) => (item.id === editingId ? nextRecord : item)));
        }
        return dedupeResearchReports([nextRecord, ...current]);
      });
      setSelectedResearchReportId(nextRecord.id);
      const sentCount = Number(body.email_delivery?.sent || 0);
      const failedCount = Number(body.email_delivery?.failed || 0);
      if (sentCount > 0 || failedCount > 0) {
        setResearchFeedback(
          language === 'es'
            ? `Reporte generado. Emails enviados: ${sentCount}. Fallidos: ${failedCount}.`
            : `Report generated. Emails sent: ${sentCount}. Failed: ${failedCount}.`,
        );
      } else {
        setResearchFeedback(
          editingId
            ? (language === 'es' ? 'Reporte actualizado.' : 'Report updated.')
            : (language === 'es' ? 'Reporte generado y guardado.' : 'Report generated and saved.'),
        );
      }
      resetResearchIntake();
      return nextRecord;
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'service-unreachable';
      const fallbackText = buildResearchFallbackReport(reportId);
      const now = new Date().toISOString();
      const keyDataInput = keyDataPayload;
      const currentReportSnapshot = {
        id: reportId,
        reportName: form.reportName.trim() || derivedTargetName,
        targetType: derivedTargetType,
        targetName: derivedTargetName,
        website: derivedWebsite || undefined,
        jurisdiction: form.jurisdiction.trim(),
        knownAliases: derivedKnownAliases,
        notes: mergedNotes,
        affinityDepth: form.affinityDepth,
        affinityGraph: undefined,
        documentParseReceipt: documentParseReceipt || undefined,
        emailAddresses: form.emailAddresses
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
        emailGroups: form.emailGroups
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
        content: fallbackText,
        createdAt: editingId
          ? (researchReports.find((item) => item.id === editingId)?.createdAt || now)
          : now,
        updatedAt: now,
      };
      const factDatabase = buildResearchFactDatabase(currentReportSnapshot, keyDataInput, currentReportSnapshot.affinityDepth);
      const nextRecord: ResearchReportRecord = stampResearchReportOwnership({
        ...currentReportSnapshot,
        keyData: buildResearchKeyData(currentReportSnapshot, keyDataPayload),
        factDatabase,
        sourceOfTruth: 'fact_database',
        rerunOptions: buildDefaultResearchRerunOptions(currentReportSnapshot.affinityDepth),
        isFallback: true,
        fallbackReason: reason,
      });

      setResearchReports((current) => {
        if (editingId) {
          return current.map((item) => (item.id === editingId ? nextRecord : item));
        }
        return [nextRecord, ...current];
      });
      setSelectedResearchReportId(nextRecord.id);
      setResearchFeedback(language === 'es' ? 'Reporte preliminar guardado localmente.' : 'Local draft report saved.');
      setResearchError(
        language === 'es'
          ? `No se pudo conectar al servicio en este momento. Se mostró un reporte preliminar local. Detalle: ${reason}`
          : `Could not reach the research service right now. A local draft report was generated instead. Details: ${reason}`,
      );
      return nextRecord;
    } finally {
      setIsResearchLoading(false);
    }
  }, [
    accessToken,
    buildResearchFallbackReport,
    deriveKnownAliasesFromResearchForm,
    deriveTargetNameFromResearchForm,
    deriveTargetTypeFromResearchForm,
    deriveWebsiteFromResearchForm,
    editingResearchReportId,
    fetchResearchReportResponse,
    language,
    researchForm.jurisdiction,
    researchForm.notes,
    researchForm.keyData,
    researchForm.affinityDepth,
    researchForm.emailAddresses,
    researchForm.emailGroups,
    researchForm.reportName,
    researchForm.targetType,
    defaultResearchReportServiceUrl,
    gatewayBaseUrl,
    researchReportServiceUrl,
    researchReportAsyncServiceUrl,
    researchReports,
    researchUploadFile,
    resetResearchIntake,
    stampResearchReportOwnership,
  ]);

  const activeGatewayJobPollersRef = React.useRef<Set<string>>(new Set());
  const activeGatewayJobStreamsRef = React.useRef<Map<string, EventSource>>(new Map());

  const applyResearchResponseToRecord = React.useCallback((
    body: ResearchReportResponse,
    context: {
      reportId: string;
      reportName: string;
      targetType: 'company' | 'person' | 'unknown';
      targetName: string;
      website?: string;
      jurisdiction: string;
      knownAliases: string[];
      notes: string;
      affinityDepth: number;
      documentParseReceipt?: Record<string, unknown> | null;
      emailAddresses: string[];
      emailGroups: string[];
      keyDataPayload: KeyData & { aliases?: string };
      editingReportId?: string | null;
      createdAt?: string;
    },
  ): ResearchReportRecord => {
    let reportText = String(body.report || body.markdown || body.content || '').trim();
    if (!reportText && body.fact_database && typeof body.fact_database === 'object') {
      const subject = String(
        (body as { subject?: string }).subject || context.targetName || 'Research target',
      ).trim();
      const screeningId = extractResearchScreeningId(body.screening_id || body) || 'n/a';
      reportText = [
        `# ${subject}`,
        '',
        `Vantage research run ID: ${screeningId}`,
        `Status: ${String(body.status || 'completed')}`,
        typeof body.risk_score === 'number' ? `Risk score: ${body.risk_score}` : null,
        '',
        '_Report markdown was rebuilt from the fact database because Vantage returned structured results without a report body._',
      ].filter((line) => line != null).join('\n');
    }
    if (!reportText && body.status !== 'partial_timeout') {
      throw new Error('empty-report');
    }
    if (body.status === 'partial_timeout') {
      setResearchParseWarning(
        language === 'es'
          ? `La búsqueda exhaustiva nacional excedió el tiempo límite del gateway. Se guardó un reporte parcial (ID: ${String(body.screening_id || 'n/a')}).`
          : `Nationwide exhaustive screening exceeded the gateway time limit. A partial report was saved (ID: ${String(body.screening_id || 'n/a')}).`,
      );
    }

    const now = new Date().toISOString();
    const searchTiming = extractResearchSearchTiming(body);
    const affinityGraph = normalizeResearchAffinityGraph(body.affinity_graph);
    const rerunOptions = normalizeResearchRerunOptions(body.rerun_options);
    const currentReportSnapshot = {
      id: context.reportId,
      reportName: context.reportName,
      targetType: context.targetType,
      targetName: context.targetName,
      website: context.website,
      jurisdiction: context.jurisdiction,
      knownAliases: context.knownAliases,
      notes: context.notes,
      affinityDepth: typeof body.affinity_depth === 'number'
        ? body.affinity_depth
        : (typeof (body.fact_database?.metadata as { affinity_depth?: number } | undefined)?.affinity_depth === 'number'
          ? Number((body.fact_database?.metadata as { affinity_depth?: number }).affinity_depth)
          : context.affinityDepth),
      affinityGraph,
      documentParseReceipt: context.documentParseReceipt || undefined,
      emailAddresses: context.emailAddresses,
      emailGroups: context.emailGroups,
      content: reportText || buildResearchFallbackReport(context.reportId),
      createdAt: context.createdAt || context.editingReportId
        ? (researchReports.find((item) => item.id === context.editingReportId)?.createdAt || now)
        : now,
      updatedAt: now,
    };
    const factDatabase = hydrateResearchFactDatabase(body.fact_database)
      || buildResearchFactDatabase(currentReportSnapshot, context.keyDataPayload, currentReportSnapshot.affinityDepth);
    const checkTimings = Array.isArray(body.check_timings)
      ? body.check_timings
      : (Array.isArray(factDatabase?.metadata?.check_timings) ? factDatabase.metadata.check_timings : []);
    if (factDatabase && checkTimings.length > 0) {
      factDatabase.metadata = {
        ...factDatabase.metadata,
        check_timings: checkTimings,
      };
    }
    const efficiencyAnalysis = body.time_efficiency_analysis
      || (factDatabase?.metadata?.time_efficiency_analysis as ResearchTimeEfficiencyAnalysis | undefined);
    const sourceChecksRuns = Array.isArray(body.source_checks_runs)
      ? body.source_checks_runs as Array<Record<string, unknown>>
      : undefined;
    const checkResults = Array.isArray(body.check_results)
      ? body.check_results as Array<Record<string, unknown>>
      : undefined;
    const researchLog = buildResearchLogEntries({
      researchLog: Array.isArray(body.research_log) ? body.research_log : undefined,
      checkTimings: checkTimings,
      checkResults,
      sourceChecksRuns,
      criticalFacts: body.critical_facts && typeof body.critical_facts === 'object' ? body.critical_facts : undefined,
      factDatabase: body.fact_database && typeof body.fact_database === 'object'
        ? body.fact_database as Record<string, unknown>
        : undefined,
      status: body.status,
    });
    return stampResearchReportOwnership({
      ...currentReportSnapshot,
      ...searchTiming,
      screeningId: extractResearchScreeningId(body.screening_id || body) || undefined,
      riskScore: typeof body.risk_score === 'number' ? body.risk_score : undefined,
      matchCount: typeof body.match_count === 'number' ? body.match_count : undefined,
      timeEfficiencyAnalysis: efficiencyAnalysis && typeof efficiencyAnalysis === 'object' ? efficiencyAnalysis : undefined,
      researchLog: researchLog.length ? researchLog : undefined,
      sourceChecksRuns,
      checkResults,
      criticalFacts: body.critical_facts && typeof body.critical_facts === 'object' ? body.critical_facts : undefined,
      keyData: buildResearchKeyData(currentReportSnapshot, context.keyDataPayload),
      factDatabase,
      sourceOfTruth: String(body.source_of_truth || 'fact_database').trim(),
      rerunOptions: rerunOptions.length > 0 ? rerunOptions : buildDefaultResearchRerunOptions(currentReportSnapshot.affinityDepth),
      isFallback: !reportText,
      fallbackReason: !reportText ? 'empty-report' : undefined,
    });
  }, [buildResearchFallbackReport, language, researchReports, stampResearchReportOwnership]);

  const startResearchGatewayJobPoll = React.useCallback((params: {
    queueId: string;
    gatewayJobId: string;
    reportId: string;
    reportName: string;
    targetType: 'company' | 'person' | 'unknown';
    targetName: string;
    website?: string;
    jurisdiction: string;
    knownAliases: string[];
    notes: string;
    affinityDepth: number;
    documentParseReceipt?: Record<string, unknown> | null;
    emailAddresses: string[];
    emailGroups: string[];
    keyDataPayload: KeyData & { aliases?: string };
    editingReportId?: string | null;
    openPdfOnComplete?: boolean;
  }) => {
    const { gatewayJobId, queueId } = params;
    if (activeGatewayJobPollersRef.current.has(gatewayJobId)) {
      return;
    }
    activeGatewayJobPollersRef.current.add(gatewayJobId);

    const updateQueue = (patch: Partial<ResearchQueueItem>) => {
      setResearchQueue((current) => current.map((item) => {
        if (item.id !== queueId) return item;
        const nextStatus = (patch.status ?? item.status) as ResearchQueueStatus;
        const nextProgress = Number(patch.progress ?? item.progress ?? 0);
        const nextEtaSeconds = estimateQueueEtaSeconds({
          createdAt: item.createdAt,
          runStartedAt: patch.runStartedAt ?? item.runStartedAt,
          progress: nextProgress,
          status: nextStatus,
          previousEtaSeconds: item.etaSeconds,
          targetType: item.targetType,
        });
        return {
          ...item,
          ...patch,
          etaSeconds: nextEtaSeconds,
          updatedAt: new Date().toISOString(),
        };
      }));
    };

    const watchState = { terminal: false };

    const closeJobStream = () => {
      const source = activeGatewayJobStreamsRef.current.get(gatewayJobId);
      if (source) {
        source.close();
        activeGatewayJobStreamsRef.current.delete(gatewayJobId);
      }
    };
    const pollUrl = `${gatewayBaseUrl}/api/v1/research/reports/jobs/${encodeURIComponent(gatewayJobId)}`;
    // EventSource cannot send an Authorization header, so the stream takes the token as a query param.
    const eventsUrl = accessToken
      ? `${pollUrl}/events?access_token=${encodeURIComponent(accessToken)}`
      : `${pollUrl}/events`;

    const buildQueueMessage = (jobBody: {
      phase?: string;
      current_check?: string;
      checks_completed?: number;
      checks_total?: number;
      current_check_elapsed_ms?: number;
      check_timings?: ResearchCheckTiming[];
    }) => {
      const phase = String(jobBody?.phase || '').trim();
      const checkLabel = String(jobBody?.current_check || '').trim();
      const checksCompleted = typeof jobBody?.checks_completed === 'number' ? jobBody.checks_completed : null;
      const checksTotal = typeof jobBody?.checks_total === 'number' ? jobBody.checks_total : null;
      let message = phase || (language === 'es' ? 'Ejecutando screening de fuentes...' : 'Running source screening...');
      if (checkLabel) {
        const elapsed = typeof jobBody?.current_check_elapsed_ms === 'number'
          ? jobBody.current_check_elapsed_ms
          : null;
        const elapsedLabel = elapsed != null
          ? formatCheckTimingDuration(elapsed, language)
          : '';
        message = elapsedLabel
          ? `${checkLabel} · ${elapsedLabel}`
          : checkLabel;
      } else if (checksCompleted != null && checksTotal != null && checksTotal > 0) {
        message = language === 'es'
          ? `Verificación ${checksCompleted} de ${checksTotal}`
          : `Check ${checksCompleted} of ${checksTotal}`;
      }
      return message;
    };

    const applyProgressSnapshot = (jobBody: {
      status?: string;
      phase?: string;
      progress_percent?: number;
      current_check?: string;
      checks_completed?: number;
      checks_total?: number;
      current_check_elapsed_ms?: number;
      started_at?: string | null;
      check_timings?: ResearchCheckTiming[];
      research_log?: ResearchLogEntry[];
      result?: ResearchReportResponse;
    }) => {
      const progress = typeof jobBody?.progress_percent === 'number'
        ? Math.max(0, Math.min(100, Math.round(jobBody.progress_percent)))
        : undefined;
      const message = buildQueueMessage(jobBody);
      const researchLog = buildResearchLogEntries({
        researchLog: Array.isArray(jobBody.research_log) ? jobBody.research_log : undefined,
        checkTimings: Array.isArray(jobBody.check_timings) ? jobBody.check_timings : undefined,
        checkResults: Array.isArray(jobBody.result?.check_results)
          ? jobBody.result?.check_results as Array<Record<string, unknown>>
          : undefined,
        sourceChecksRuns: Array.isArray(jobBody.result?.source_checks_runs)
          ? jobBody.result?.source_checks_runs as Array<Record<string, unknown>>
          : undefined,
        criticalFacts: jobBody.result?.critical_facts && typeof jobBody.result.critical_facts === 'object'
          ? jobBody.result.critical_facts as ResearchReportRecord['criticalFacts']
          : undefined,
        factDatabase: (jobBody.result?.fact_database && typeof jobBody.result.fact_database === 'object'
          ? jobBody.result.fact_database
          : (jobBody.fact_database && typeof jobBody.fact_database === 'object' ? jobBody.fact_database : undefined)) as Record<string, unknown> | undefined,
        status: jobBody.status,
        phase: jobBody.phase,
        currentCheck: jobBody.current_check,
      });
      if (progress != null) {
        updateQueue({
          status: jobBody?.status === 'queued' ? 'queued' : 'screening',
          progress,
          message,
          gatewayJobId,
          reportId: params.reportId,
          runStartedAt: jobBody?.started_at || null,
          researchLog: researchLog.length ? researchLog : undefined,
        });
      } else if (message) {
        updateQueue({
          status: 'screening',
          message,
          gatewayJobId,
          reportId: params.reportId,
          runStartedAt: jobBody?.started_at || null,
          researchLog: researchLog.length ? researchLog : undefined,
        });
      }
    };

    const finalizeFromFullJob = async (jobBody: {
      status?: string;
      result?: ResearchReportResponse;
      error?: string;
    }): Promise<boolean> => {
      if (jobBody?.status === 'completed' && jobBody.result) {
        const resultWithTimings: ResearchReportResponse = {
          ...jobBody.result,
          check_timings: Array.isArray(jobBody.result.check_timings)
            ? jobBody.result.check_timings
            : (Array.isArray((jobBody as { check_timings?: ResearchCheckTiming[] }).check_timings)
              ? (jobBody as { check_timings?: ResearchCheckTiming[] }).check_timings
              : undefined),
          research_log: Array.isArray(jobBody.result.research_log)
            ? jobBody.result.research_log
            : (Array.isArray((jobBody as { research_log?: ResearchLogEntry[] }).research_log)
              ? (jobBody as { research_log?: ResearchLogEntry[] }).research_log
              : undefined),
        };
        const nextRecord = applyResearchResponseToRecord(resultWithTimings, params);
        setResearchReports((current) => {
          const existingIndex = current.findIndex((item) => item.id === params.reportId);
          const withRecord = existingIndex >= 0
            ? current.map((item) => (item.id === params.reportId ? nextRecord : item))
            : [nextRecord, ...current];
          return dedupeResearchReports(withRecord);
        });
        setSelectedResearchReportId(params.reportId);
        updateQueue({
          status: 'completed',
          progress: 100,
          etaSeconds: 0,
          message: language === 'es' ? 'Reporte listo' : 'Report ready',
          reportId: params.reportId,
          gatewayJobId,
        });
        setResearchFeedback(
          language === 'es' ? 'Reporte generado y guardado.' : 'Report generated and saved.',
        );
        if (params.openPdfOnComplete) {
          await openResearchReportPdf(nextRecord, { suppressLoader: true });
        }
        return true;
      }

      if (jobBody?.status === 'failed' || jobBody?.status === 'interrupted') {
        if (!String(jobBody.error || '').trim()) {
          const fullJob = await fetchFullJob();
          if (fullJob && typeof fullJob === 'object') {
            jobBody = { ...jobBody, ...fullJob };
          }
        }
        const interruptedHint = jobBody?.status === 'interrupted'
          ? (language === 'es'
            ? 'Vantage se reinició durante el screening. Use Relanzar para reintentar.'
            : 'Vantage restarted during screening. Use Rerun to try again.')
          : null;
        const errorText = String(jobBody.error || (language === 'es' ? 'Falló' : 'Failed'));
        updateQueue({
          status: 'failed',
          progress: 100,
          etaSeconds: 0,
          message: interruptedHint || errorText,
          gatewayJobId,
        });
        setResearchError(
          language === 'es'
            ? `No se pudo generar el reporte. Detalle: ${interruptedHint || errorText}`
            : `Could not generate report. Details: ${interruptedHint || errorText}`,
        );
        return true;
      }

      if (jobBody?.status === 'cancelled') {
        updateQueue({
          status: 'cancelled',
          etaSeconds: 0,
          message: language === 'es' ? 'Cancelado' : 'Cancelled',
          gatewayJobId,
        });
        return true;
      }

      return false;
    };

    const fetchFullJob = async () => {
      const pollResponse = await fetch(pollUrl, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (!pollResponse.ok) {
        return null;
      }
      return pollResponse.json();
    };

    const runPollingFallback = async () => {
      let pollIntervalMs = 3000;
      let lastSuccessAtMs = Date.now();
      let lastHttpStatus: number | null = null;

      while (activeGatewayJobPollersRef.current.has(gatewayJobId) && !watchState.terminal) {
        await new Promise((resolve) => window.setTimeout(resolve, pollIntervalMs));
        let jobBody: {
          status?: string;
          phase?: string;
          progress_percent?: number;
          result?: ResearchReportResponse;
          error?: string;
          checks_completed?: number;
          checks_total?: number;
          current_check?: string;
        } | null = null;
        try {
          const pollResponse = await fetch(pollUrl, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
          });
          lastHttpStatus = pollResponse.status;
          if (pollResponse.status === 401) {
            if (isRealGatewayAccessToken(accessToken)) {
              setResearchSessionExpired(true);
            }
            updateQueue({
              message: language === 'es'
                ? 'Sesión expirada. Inicie sesión; Vantage sigue ejecutando el trabajo.'
                : 'Session expired. Sign in again — Vantage is still running this job.',
            });
            return;
          }
          if (!pollResponse.ok) {
            const stale = decideResearchWatchFailure({
              nowMs: Date.now(),
              lastSuccessAtMs,
              lastHttpStatus,
            });
            if (stale.fail) {
              updateQueue({
                status: 'failed',
                progress: 100,
                etaSeconds: 0,
                message: language === 'es' ? 'Vantage no respondió' : 'Vantage stopped responding',
              });
              setResearchError(
                language === 'es'
                  ? 'No se pudo contactar Vantage. Revise la cola o reintente.'
                  : 'Could not reach Vantage. Check the queue or retry.',
              );
              trackUxEvent({
                event_type: 'vantage_job_failed',
                product: 'vantage',
                funnel_key: 'research_job',
                step: 'poll',
                last_path: '/console/research',
                error_code: String(lastHttpStatus || 'unreachable'),
                message: 'Vantage stopped responding',
              });
              return;
            }
            pollIntervalMs = Math.min(15000, Math.round(pollIntervalMs * 1.15));
            continue;
          }
          lastSuccessAtMs = Date.now();
          jobBody = await pollResponse.json();
        } catch {
          lastHttpStatus = null;
          const stale = decideResearchWatchFailure({
            nowMs: Date.now(),
            lastSuccessAtMs,
            lastHttpStatus,
          });
          if (stale.fail) {
            updateQueue({
              status: 'failed',
              progress: 100,
              etaSeconds: 0,
              message: language === 'es' ? 'Vantage no respondió' : 'Vantage stopped responding',
            });
            trackUxEvent({
              event_type: 'vantage_job_failed',
              product: 'vantage',
              funnel_key: 'research_job',
              step: 'poll',
              last_path: '/console/research',
              error_code: 'network',
              message: 'Vantage stopped responding',
            });
            return;
          }
          pollIntervalMs = Math.min(15000, Math.round(pollIntervalMs * 1.15));
          continue;
        }

        applyProgressSnapshot(jobBody);
        if (['completed', 'failed', 'cancelled', 'interrupted'].includes(String(jobBody?.status || ''))) {
          if (jobBody?.status === 'completed' && !jobBody.result) {
            const fullJob = await fetchFullJob();
            if (fullJob && await finalizeFromFullJob(fullJob)) {
              watchState.terminal = true;
              closeJobStream();
              return;
            }
          }
          if (await finalizeFromFullJob(jobBody)) {
            watchState.terminal = true;
            closeJobStream();
            return;
          }
        }

        pollIntervalMs = Math.min(15000, Math.round(pollIntervalMs * 1.1));
      }
    };

    void (async () => {
      try {
        const streamPromise = (async () => {
          if (typeof EventSource === 'undefined') return;
          await new Promise<void>((resolve) => {
            const source = new EventSource(eventsUrl);
            activeGatewayJobStreamsRef.current.set(gatewayJobId, source);

            source.onmessage = (event) => {
              void (async () => {
                try {
                  const jobBody = JSON.parse(event.data) as {
                    status?: string;
                    phase?: string;
                    progress_percent?: number;
                    result?: ResearchReportResponse;
                    error?: string;
                    checks_completed?: number;
                    checks_total?: number;
                    current_check?: string;
                    current_check_elapsed_ms?: number;
                  };
                  applyProgressSnapshot(jobBody);
                  const status = String(jobBody?.status || '').trim().toLowerCase();
                  if (!['completed', 'failed', 'cancelled', 'interrupted'].includes(status)) {
                    return;
                  }
                  watchState.terminal = true;
                  source.close();
                  activeGatewayJobStreamsRef.current.delete(gatewayJobId);
                  if (status === 'completed' && !jobBody.result) {
                    const fullJob = await fetchFullJob();
                    if (fullJob) await finalizeFromFullJob(fullJob);
                  } else {
                    await finalizeFromFullJob(jobBody);
                  }
                  resolve();
                } catch {
                  // Ignore malformed SSE payloads and keep listening.
                }
              })();
            };

            source.onerror = () => {
              source.close();
              activeGatewayJobStreamsRef.current.delete(gatewayJobId);
              resolve();
            };
          });
        })();

        await Promise.allSettled([streamPromise, runPollingFallback()]);
      } finally {
        watchState.terminal = true;
        closeJobStream();
        activeGatewayJobPollersRef.current.delete(gatewayJobId);
      }
    })();
  }, [
    accessToken,
    applyResearchResponseToRecord,
    gatewayBaseUrl,
    language,
    openResearchReportPdf,
  ]);

  const syncResearchQueueFromGateway = React.useCallback(async () => {
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/research/reports/jobs`, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (response.status === 401) {
        // Local-dev `__legacy__` is signed-in in the chrome but is not a JWT.
        // Only a real gateway token becoming 401 is an expired session.
        if (isRealGatewayAccessToken(accessToken)) {
          setResearchSessionExpired(true);
        }
        return;
      }
      setResearchSessionExpired(false);
      if (!response.ok) {
        return;
      }
      const body = await response.json() as { jobs?: GatewayResearchJobSummary[] };
      const serverJobs = (body.jobs || []).filter((job) => job && typeof job.job_id === 'string');
      const pollsToStart: Array<{
        item: ResearchQueueItem;
      }> = [];

      setResearchQueue((current) => {
        const merged = mergeResearchQueueFromGatewayJobs({
          current,
          serverJobs,
          language,
          dismissedIds: dismissedResearchReportIdsRef.current,
          buildMessage: (job) => buildGatewayJobQueueMessage(job, language),
        });
        merged.pollsToStart.forEach((item) => {
          const gatewayJobId = String(item.gatewayJobId || '').trim();
          if (gatewayJobId && !activeGatewayJobPollersRef.current.has(gatewayJobId)) {
            pollsToStart.push({ item: item as ResearchQueueItem });
          }
        });
        return merged.next as ResearchQueueItem[];
      });

      pollsToStart.forEach(({ item }) => {
        if (!item.gatewayJobId || !item.reportId) {
          return;
        }
        const existingReport = researchReportsRef.current.find((report) => report.id === item.reportId);
        startResearchGatewayJobPoll({
          queueId: item.id,
          gatewayJobId: item.gatewayJobId,
          reportId: item.reportId,
          reportName: item.reportName,
          targetType: item.targetType,
          targetName: item.targetName,
          website: existingReport?.website,
          jurisdiction: existingReport?.jurisdiction || '',
          knownAliases: existingReport?.knownAliases || [],
          notes: existingReport?.notes || '',
          affinityDepth: existingReport?.affinityDepth || 1,
          documentParseReceipt: existingReport?.documentParseReceipt || undefined,
          emailAddresses: existingReport?.emailAddresses || [],
          emailGroups: existingReport?.emailGroups || [],
          keyDataPayload: {
            ...(existingReport?.keyData || {}),
            names: String(existingReport?.keyData?.names || item.targetName || '').trim(),
            knownAliases: String(existingReport?.keyData?.knownAliases || '').trim(),
          },
          editingReportId: existingReport ? item.reportId : null,
        });
      });

      // Import completed Vantage-backed jobs into the Completed tab catalog.
      // List payloads are slim; seed stubs immediately so the Completed count matches
      // gateway/Vantage, then hydrate full report bodies in the background.
      const completedServerJobs = serverJobs.filter((job) => {
        const status = String(job.status || '').toLowerCase();
        return status === 'completed' && (Boolean(job.has_result) || (job.result && typeof job.result === 'object'));
      });
      const hiddenCompletedReportIds = completedServerJobs.reduce<string[]>((ids, job) => {
        const gatewayJobId = String(job.job_id || '').trim();
        const vantageJobId = String(job.vantage_job_id || '').trim();
        const reportId = gatewayJobId
          ? `report-${gatewayJobId}`
          : (vantageJobId ? `report-v-${vantageJobId}` : '');
        if (reportId && isDismissedResearchIdentity(dismissedResearchReportIdsRef.current, {
          reportId,
          gatewayJobId,
          vantageJobId,
        })) {
          ids.push(reportId);
        }
        return ids;
      }, []);
      setResearchHiddenCompletedReportIds(hiddenCompletedReportIds);
      setResearchHiddenCompletedCount(hiddenCompletedReportIds.length);

      if (completedServerJobs.length > 0) {
        const catalogSnapshot = researchReportsRef.current;
        const existingReportIds = new Set(catalogSnapshot.map((report) => report.id));
        const existingScreeningIds = new Set(
          catalogSnapshot
            .map((report) => extractResearchScreeningId(report.screeningId || report))
            .filter(Boolean),
        );

        const stubRecords: ResearchReportRecord[] = [];
        const newestCompletedJobs = [...completedServerJobs].sort((left, right) => {
          const leftStamp = Date.parse(String(left.completed_at || left.updated_at || left.created_at || '')) || 0;
          const rightStamp = Date.parse(String(right.completed_at || right.updated_at || right.created_at || '')) || 0;
          return rightStamp - leftStamp;
        });
        for (const job of newestCompletedJobs.slice(0, 20)) {
          const gatewayJobId = String(job.job_id || '').trim();
          const vantageJobId = String(job.vantage_job_id || '').trim();
          const reportId = gatewayJobId
            ? `report-${gatewayJobId}`
            : (vantageJobId ? `report-v-${vantageJobId}` : '');
          if (!reportId || isDismissedResearchIdentity(dismissedResearchReportIdsRef.current, {
            reportId,
            gatewayJobId,
            vantageJobId,
          })) {
            continue;
          }
          const listScreeningId = extractResearchScreeningId(job.result);
          if (
            existingReportIds.has(reportId)
            || (listScreeningId && existingScreeningIds.has(listScreeningId))
          ) {
            continue;
          }
          const targetName = String(job.target_name || '').trim() || 'Research report';
          const targetType = job.target_type === 'company' ? 'company' : 'person';
          const preview = String(job.result?.report || job.result?.markdown || job.result?.content || '').trim();
          const jobCreated = String(job.created_at || '').trim() || new Date().toISOString();
          const jobCompleted = String(job.completed_at || job.updated_at || '').trim() || jobCreated;
          stubRecords.push(stampResearchReportOwnership({
            id: reportId,
            reportName: targetName,
            targetType,
            targetName,
            jurisdiction: '',
            knownAliases: [],
            notes: '',
            affinityDepth: Number(
              (job as { affinity_depth?: number }).affinity_depth
              || (job.result as { affinity_depth?: number } | undefined)?.affinity_depth
              || (job.result?.fact_database?.metadata as { affinity_depth?: number } | undefined)?.affinity_depth
              || 1,
            ) || 1,
            emailAddresses: [],
            emailGroups: [],
            content: preview || `# ${targetName}\n\n_Loading full Vantage report…_`,
            createdAt: jobCreated,
            updatedAt: jobCompleted,
            screeningId: listScreeningId || undefined,
            riskScore: typeof job.result?.risk_score === 'number' ? job.result.risk_score : undefined,
            keyData: { names: targetName },
            sourceOfTruth: 'vantage',
            isFallback: !preview || preview.length < 400,
            fallbackReason: preview ? undefined : 'awaiting-hydrate',
            ownerUserId: String(job.created_by_user_id || '').trim() || undefined,
            ownerEmail: String(job.created_by_email || '').trim() || undefined,
            companyId: job.company_id != null ? Number(job.company_id) : null,
            searchStartedAt: jobCreated,
            searchCompletedAt: jobCompleted,
            gatewayJobId: gatewayJobId || undefined,
            vantageJobId: vantageJobId || undefined,
          }));
          existingReportIds.add(reportId);
          if (listScreeningId) {
            existingScreeningIds.add(listScreeningId);
          }
        }
        if (stubRecords.length > 0) {
          setResearchReports((current) => dedupeResearchReports([...stubRecords, ...current]).slice(0, 50));
        }

        const hydratedJobs: GatewayResearchJobSummary[] = [];
        for (const job of newestCompletedJobs.slice(0, 20)) {
          const gatewayJobId = String(job.job_id || '').trim();
          const vantageJobId = String(job.vantage_job_id || '').trim();
          const reportId = gatewayJobId
            ? `report-${gatewayJobId}`
            : (vantageJobId ? `report-v-${vantageJobId}` : '');
          if (!reportId || isDismissedResearchIdentity(dismissedResearchReportIdsRef.current, {
            reportId,
            gatewayJobId,
            vantageJobId,
          })) {
            continue;
          }
          const alreadyFull = researchReportsRef.current.some((report) => (
            report.id === reportId
            && !report.isFallback
            && String(report.content || '').length >= 400
            && researchReportContentHasBiography(String(report.content || ''))
            && Boolean(report.factDatabase)
          ));
          if (alreadyFull) {
            continue;
          }
          if (researchJobResultIsComplete(job)) {
            hydratedJobs.push(job);
            continue;
          }
          if (!gatewayJobId) {
            continue;
          }
          try {
            const detailResponse = await fetch(
              `${gatewayBaseUrl}/api/v1/research/reports/jobs/${encodeURIComponent(gatewayJobId)}`,
              { headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {} },
            );
            if (!detailResponse.ok) {
              continue;
            }
            const detail = await detailResponse.json() as GatewayResearchJobSummary;
            if (detail?.result && typeof detail.result === 'object') {
              hydratedJobs.push({ ...job, ...detail, result: detail.result, result_complete: true });
            }
          } catch {
            // Best-effort hydration — stubs already keep Completed populated.
          }
        }

        if (hydratedJobs.length > 0) {
          setResearchReports((current) => {
            let next = [...current];
            let changed = false;
            hydratedJobs.forEach((job) => {
              const gatewayJobId = String(job.job_id || '').trim();
              const vantageJobId = String(job.vantage_job_id || '').trim();
              const reportId = gatewayJobId
                ? `report-${gatewayJobId}`
                : (vantageJobId ? `report-v-${vantageJobId}` : '');
              if (!reportId || !job.result) {
                return;
              }
              if (isDismissedResearchIdentity(dismissedResearchReportIdsRef.current, {
                reportId,
                gatewayJobId,
                vantageJobId,
              })) {
                return;
              }
              const targetName = String(job.target_name || '').trim() || 'Research report';
              const targetType = job.target_type === 'company' ? 'company' : 'person';
              try {
                const jobCreated = String(job.created_at || '').trim();
                const jobCompleted = String(job.completed_at || '').trim();
                const jobWallMs = jobCreated && jobCompleted
                  ? Math.max(0, Date.parse(jobCompleted) - Date.parse(jobCreated))
                  : undefined;
                const plausibleWall = Number.isFinite(jobWallMs)
                  && (jobWallMs as number) > 0
                  && (jobWallMs as number) <= 24 * 60 * 60 * 1000;
                const resultWithJobTiming: ResearchReportResponse = {
                  ...job.result,
                  gateway_timing: {
                    ...(job.result.gateway_timing || {}),
                    request_started_at: job.result.gateway_timing?.request_started_at || jobCreated || undefined,
                    request_completed_at: job.result.gateway_timing?.request_completed_at || jobCompleted || undefined,
                    request_elapsed_ms: job.result.gateway_timing?.request_elapsed_ms
                      || (plausibleWall ? jobWallMs : undefined),
                    screening_elapsed_ms: job.result.gateway_timing?.screening_elapsed_ms
                      || (plausibleWall ? jobWallMs : undefined),
                  },
                  check_timings: Array.isArray(job.result.check_timings)
                    ? job.result.check_timings
                    : (Array.isArray((job as { check_timings?: ResearchCheckTiming[] }).check_timings)
                      ? (job as { check_timings?: ResearchCheckTiming[] }).check_timings
                      : undefined),
                  time_efficiency_analysis: job.result.time_efficiency_analysis
                    || (job.result.fact_database?.metadata?.time_efficiency_analysis as ResearchTimeEfficiencyAnalysis | undefined),
                };
                const record = applyResearchResponseToRecord(resultWithJobTiming, {
                  reportId,
                  reportName: targetName,
                  targetType,
                  targetName,
                  jurisdiction: '',
                  knownAliases: [],
                  notes: '',
                  affinityDepth: Number(
                    (job.result as { affinity_depth?: number } | undefined)?.affinity_depth
                    || (job.result?.fact_database?.metadata as { affinity_depth?: number } | undefined)?.affinity_depth
                    || 1,
                  ) || 1,
                  emailAddresses: [],
                  emailGroups: [],
                  keyDataPayload: { names: targetName },
                  createdAt: jobCreated || new Date().toISOString(),
                });
                const ownedRecord: ResearchReportRecord = {
                  ...record,
                  ownerUserId: String(job.created_by_user_id || record.ownerUserId || '').trim() || record.ownerUserId,
                  ownerEmail: String(job.created_by_email || record.ownerEmail || '').trim() || record.ownerEmail,
                  companyId: job.company_id != null ? Number(job.company_id) : record.companyId,
                  isFallback: false,
                  fallbackReason: undefined,
                };
                const existingIndex = next.findIndex((report) => report.id === reportId);
                if (existingIndex >= 0) {
                  next[existingIndex] = { ...next[existingIndex], ...ownedRecord, id: reportId };
                } else {
                  next = [ownedRecord, ...next];
                }
                changed = true;
              } catch {
                // Skip jobs whose result cannot be rendered as a report.
              }
            });
            return changed ? dedupeResearchReports(next).slice(0, 50) : current;
          });
        }
      }
    } catch {
      // Best-effort sync; local queue remains usable if gateway is unreachable.
    }
  }, [accessToken, applyResearchResponseToRecord, gatewayBaseUrl, language, stampResearchReportOwnership, startResearchGatewayJobPoll]);

  const clearFailedResearchQueueItems = React.useCallback(() => {
    setResearchQueue((current) => current.filter((item) => !['failed', 'cancelled'].includes(item.status)));
    setResearchFeedback(language === 'es' ? 'Fallos de cola eliminados.' : 'Cleared failed queue items.');
  }, [language]);

  const cancelResearchQueueJob = React.useCallback(async (item: ResearchQueueItem) => {
    const gatewayJobId = String(item.gatewayJobId || '').trim();
    if (gatewayJobId) {
      const stream = activeGatewayJobStreamsRef.current.get(gatewayJobId);
      if (stream) {
        stream.close();
        activeGatewayJobStreamsRef.current.delete(gatewayJobId);
      }
      activeGatewayJobPollersRef.current.delete(gatewayJobId);
      try {
        await fetch(
          `${gatewayBaseUrl}/api/v1/research/reports/jobs/${encodeURIComponent(gatewayJobId)}/cancel`,
          {
            method: 'POST',
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
          },
        );
      } catch {
        // Queue row still moves to cancelled so the operator can retry.
      }
    }

    setResearchQueue((current) => current.map((row) => (
      row.id === item.id
        ? {
          ...row,
          status: 'cancelled',
          etaSeconds: 0,
          message: language === 'es' ? 'Cancelado' : 'Cancelled',
          updatedAt: new Date().toISOString(),
        }
        : row
    )));
  }, [accessToken, gatewayBaseUrl, language]);

  const enqueueResearchReport = React.useCallback(async (options?: {
    openPdf?: boolean;
    identifierConfirmed?: boolean;
    proceedWithoutIdentifiers?: boolean;
  }) => {
    const derivedTargetType = deriveTargetTypeFromResearchForm();
    const derivedWebsite = deriveWebsiteFromResearchForm();
    const localTargetName = deriveTargetNameFromResearchForm();
    if (!localTargetName && !derivedWebsite) {
      setResearchError(
        language === 'es'
          ? 'Ingresa un nombre, una compañía o un sitio web en Base de hechos.'
          : 'Enter a name, company, or website in Fact Database.',
      );
      return;
    }

    const intakeSnapshot: ResearchIntakeSnapshot = {
      form: cloneResearchIntakeForm(researchForm),
      uploadFile: researchUploadFile,
      editingReportId: editingResearchReportId,
    };
    const intakeForm = intakeSnapshot.form;

    const now = new Date().toISOString();
    const queueId = `queue-${Date.now()}`;
    const queueItem: ResearchQueueItem = {
      id: queueId,
      reportName: intakeForm.reportName.trim() || localTargetName || derivedWebsite,
      targetType: derivedTargetType,
      targetName: localTargetName || derivedWebsite,
      website: String(intakeForm.keyData?.website || '').trim() || undefined,
      status: 'queued',
      progress: 0,
      message: language === 'es' ? 'Solicitud en cola' : 'Request queued',
      createdAt: now,
      updatedAt: now,
      etaSeconds: derivedTargetType === 'company' ? 420 : 900,
    };

    const updateQueue = (patch: Partial<ResearchQueueItem>) => {
      setResearchQueue((current) => current.map((item) => {
        if (item.id !== queueId) return item;
        const nextStatus = (patch.status ?? item.status) as ResearchQueueStatus;
        const nextProgress = Number(patch.progress ?? item.progress ?? 0);
        const nextEtaSeconds = estimateQueueEtaSeconds({
          createdAt: item.createdAt,
          runStartedAt: patch.runStartedAt ?? item.runStartedAt,
          progress: nextProgress,
          status: nextStatus,
          previousEtaSeconds: item.etaSeconds,
          targetType: item.targetType,
        });
        return {
          ...item,
          ...patch,
          etaSeconds: nextEtaSeconds,
          updatedAt: new Date().toISOString(),
        };
      }));
    };

    const reportId = intakeSnapshot.editingReportId || generateResearchReportId();
    queueItem.reportId = reportId;

    setResearchQueue((current) => [queueItem, ...current]);
    releaseDocumentFocus();
    setActiveResearchTab('queue');
    // Blank New report as soon as the queue row exists. Submit still uses the snapshot.
    resetResearchIntake();

    const restoreIntakeIfNeeded = () => {
      setResearchForm(cloneResearchIntakeForm(intakeForm));
      setResearchUploadFile(intakeSnapshot.uploadFile);
      setEditingResearchReportId(intakeSnapshot.editingReportId);
      setResearchIntakeAutofillLocked(false);
      setResearchIntakeEpoch((epoch) => epoch + 1);
    };

    const derivedTargetName = (
      await fetchResolvedResearchTargetName({
        gatewayBaseUrl,
        accessToken: accessToken || undefined,
        targetType: derivedTargetType,
        targetName: localTargetName,
        website: derivedWebsite,
      })
    ) || localTargetName || derivedWebsite;
    if (derivedTargetName !== queueItem.targetName) {
      updateQueue({
        targetName: derivedTargetName,
        reportName: intakeForm.reportName.trim() || derivedTargetName,
      });
      queueItem.targetName = derivedTargetName;
      queueItem.reportName = intakeForm.reportName.trim() || derivedTargetName;
    }

    const markQueueFailed = (reason: string) => {
      setResearchQueue((current) => current.map((item) => (
        item.id === queueId
          ? {
            ...item,
            status: 'failed',
            progress: 100,
            etaSeconds: 0,
            message: reason,
            updatedAt: new Date().toISOString(),
          }
          : item
      )));
      setResearchQueueAck(null);
    };

    try {
      const submission = await handleGenerateResearchReport({
        suppressLoader: true,
        submitOnly: true,
        intake: intakeSnapshot,
        identifierConfirmed: options?.identifierConfirmed,
        proceedWithoutIdentifiers: options?.proceedWithoutIdentifiers,
      });

      if (submission && 'kind' in submission && submission.kind === 'identifier_confirmation_required') {
        setResearchQueue((current) => current.filter((item) => item.id !== queueId));
        restoreIntakeIfNeeded();
        setActiveResearchTab('research');
        return;
      }

      if (!submission || !('kind' in submission) || submission.kind !== 'async_submitted') {
        markQueueFailed(
          language === 'es'
            ? 'No se pudo enviar a Vantage'
            : 'Could not submit to Vantage',
        );
        restoreIntakeIfNeeded();
        return;
      }

      setResearchQueueAck(
        language === 'es'
          ? `Reporte en cola: ${resolveResearchReportDisplayTitle(queueItem)}`
          : `Report queued: ${resolveResearchReportDisplayTitle(queueItem)}`,
      );

      updateQueue({
        status: 'queued',
        progress: 0,
        gatewayJobId: submission.jobId,
        reportId: submission.reportId,
        message: language === 'es' ? 'Enviado a Vantage' : 'Submitted to Vantage',
      });
      // Keep New report blank after a successful handoff (do not leave the prior subject filled).
      resetResearchIntake();

      if (intakeForm.addToOfacScreening && canAccessOfac) {
        try {
          const ofacResponse = await fetch(`${gatewayBaseUrl}/api/v1/ofac/screening-subjects`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            },
            body: JSON.stringify({
              entity_name: derivedTargetName,
              relationship_type: intakeForm.ofacRelationshipType || 'customer',
              entity_type: derivedTargetType === 'person' ? 'person' : 'company',
              identifiers: {
                birthdate: String(intakeForm.keyData?.birthdate || '').trim() || undefined,
                ssn: String(intakeForm.keyData?.ssn || '').trim() || undefined,
                website: String(intakeForm.keyData?.website || '').trim() || undefined,
                addresses: String(intakeForm.keyData?.addresses || '').trim() || undefined,
                emails: String(intakeForm.keyData?.emails || '').trim() || undefined,
              },
              reference_id: submission.reportId,
              source: 'research',
              screen_now: false,
            }),
          });
          if (!ofacResponse.ok) {
            const detail = await ofacResponse.text().catch(() => '');
            throw new Error(detail || `HTTP ${ofacResponse.status}`);
          }
          const ofacBody = await ofacResponse.json() as { subject?: OfacScreeningSubject };
          const uuid = String(ofacBody.subject?.entity_uuid || ofacBody.subject?.entity_key || '');
          setOfacScreeningMessage(
            language === 'es'
              ? `Sujeto en Screening OFAC: ${derivedTargetName}${uuid ? ` (${uuid.slice(0, 8)}…)` : ''}`
              : `Subject on OFAC Screening list: ${derivedTargetName}${uuid ? ` (${uuid.slice(0, 8)}…)` : ''}`,
          );
        } catch (ofacError) {
          const detail = ofacError instanceof Error ? ofacError.message : 'request-failed';
          setOfacScreeningError(
            language === 'es'
              ? `Reporte en cola, pero no se pudo añadir a Screening OFAC (${detail}).`
              : `Report queued, but could not add to OFAC Screening (${detail}).`,
          );
        }
      }

      const queuedWebsite = deriveWebsiteFromResearchForm(intakeForm);
      const queuedKnownAliases = deriveKnownAliasesFromResearchForm(intakeForm);
      startResearchGatewayJobPoll({
        queueId,
        gatewayJobId: submission.jobId,
        reportId: submission.reportId,
        reportName: intakeForm.reportName.trim() || derivedTargetName,
        targetType: derivedTargetType,
        targetName: derivedTargetName,
        website: queuedWebsite || undefined,
        jurisdiction: intakeForm.jurisdiction.trim(),
        knownAliases: queuedKnownAliases,
        notes: intakeForm.notes.trim(),
        affinityDepth: intakeForm.affinityDepth,
        emailAddresses: intakeForm.emailAddresses.split(',').map((item) => item.trim()).filter(Boolean),
        emailGroups: intakeForm.emailGroups.split(',').map((item) => item.trim()).filter(Boolean),
        keyDataPayload: submission.keyDataPayload,
        editingReportId: intakeSnapshot.editingReportId,
        openPdfOnComplete: options?.openPdf === true,
      });
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'service-unreachable';
      markQueueFailed(reason);
      restoreIntakeIfNeeded();
      setResearchError(
        language === 'es'
          ? `No se pudo encolar el reporte. Detalle: ${reason}`
          : `Could not queue report. Details: ${reason}`,
      );
    }
  }, [
    accessToken,
    canAccessOfac,
    deriveKnownAliasesFromResearchForm,
    deriveTargetNameFromResearchForm,
    deriveTargetTypeFromResearchForm,
    deriveWebsiteFromResearchForm,
    editingResearchReportId,
    gatewayBaseUrl,
    handleGenerateResearchReport,
    language,
    researchForm,
    researchUploadFile,
    resetResearchIntake,
    startResearchGatewayJobPoll,
  ]);

  React.useEffect(() => {
    void syncResearchQueueFromGateway();
  }, [syncResearchQueueFromGateway]);

  // Interaction-driven refresh: any click in the research pane, window focus, or tab
  // becoming visible re-syncs from the gateway, so the operator never has to press a
  // refresh button. Throttled so a burst of clicks issues at most one sync.
  const researchSyncInFlightRef = React.useRef(false);
  const researchSyncLastAtRef = React.useRef(0);
  const requestResearchQueueSync = React.useCallback((options?: { force?: boolean }) => {
    if (researchSyncInFlightRef.current) {
      return;
    }
    const minIntervalMs = options?.force ? 0 : 2500;
    const now = Date.now();
    if (now - researchSyncLastAtRef.current < minIntervalMs) {
      return;
    }
    researchSyncLastAtRef.current = now;
    researchSyncInFlightRef.current = true;
    void Promise.resolve(syncResearchQueueFromGateway()).finally(() => {
      researchSyncInFlightRef.current = false;
    });
  }, [syncResearchQueueFromGateway]);

  React.useEffect(() => {
    if (activeNavItem !== 'research') {
      return undefined;
    }
    const handleFocus = () => requestResearchQueueSync();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        requestResearchQueueSync();
      }
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [activeNavItem, requestResearchQueueSync]);

  const hasActiveResearchQueue = researchQueue.some((item) => ['queued', 'screening'].includes(item.status));
  React.useEffect(() => {
    if (!researchIntakeShouldClearForQueue({
      tabIsResearch: activeResearchTab === 'research',
      editingExistingReport: Boolean(editingResearchReportId),
      operatorIsTyping: !researchIntakeAutofillLocked,
      intakeName: deriveTargetNameFromResearchForm(),
      queue: researchQueue,
    })) {
      return;
    }
    resetResearchIntake();
  }, [
    activeResearchTab,
    deriveTargetNameFromResearchForm,
    editingResearchReportId,
    researchIntakeAutofillLocked,
    researchQueue,
    resetResearchIntake,
  ]);
  const activeResearchQueueRef = React.useRef(false);
  activeResearchQueueRef.current = hasActiveResearchQueue;
  React.useEffect(() => {
    if (!location.pathname.includes('/console/research')) {
      return undefined;
    }
    const step = activeResearchQueueRef.current ? 'running' : 'intake';
    markFunnel('research_job', step);
    trackUxEvent({
      event_type: 'funnel_step',
      product: 'vantage',
      funnel_key: 'research_job',
      step,
      last_path: '/console/research',
    });
    return () => {
      if (activeResearchQueueRef.current && !window.location.pathname.includes('/console/research')) {
        trackUxEvent({
          event_type: 'research_job_abandoned',
          product: 'vantage',
          funnel_key: 'research_job',
          step: 'left_console',
          last_path: '/console/research',
        });
      }
    };
  }, [location.pathname]);
  React.useEffect(() => {
    // Poll while Queue or Completed is open so the catalog stays aligned with Vantage,
    // and while Research is open with an active job so newly finished reports appear.
    if (
      activeResearchTab !== 'queue'
      && activeResearchTab !== 'completed'
      && !(hasActiveResearchQueue && activeResearchTab === 'research')
    ) {
      return undefined;
    }
    void syncResearchQueueFromGateway();
    const intervalId = window.setInterval(() => {
      void syncResearchQueueFromGateway();
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [activeResearchTab, hasActiveResearchQueue, syncResearchQueueFromGateway]);

  const handleResearchReportEdit = React.useCallback((reportId: string) => {
    const selected = researchReports.find((item) => item.id === reportId);
    if (!selected) {
      return;
    }

    const mergedKey = mergeResearchReportKeyData(selected);

    setEditingResearchReportId(selected.id);
    setSelectedResearchReportId(selected.id);
    setResearchForm({
      reportName: getCustomResearchReportName(selected.reportName) || selected.targetName || '',
      reportNameLocked: Boolean(getCustomResearchReportName(selected.reportName)),
      targetType: selected.targetType,
      targetTypeTouched: true,
      jurisdiction: selected.jurisdiction,
      notes: selected.notes,
      keyData: {
        ...EMPTY_KEY_DATA,
        ...mergedKey,
      },
      affinityDepth: selected.affinityDepth || 1,
      sourceIntelligenceProfile: 'smart',
      emailAddresses: (selected.emailAddresses || []).join(', '),
      emailGroups: (selected.emailGroups || []).join(', '),
      addToOfacScreening: false,
      ofacRelationshipType: 'customer',
    });
    setResearchError(null);
    setResearchFeedback(null);
  }, [researchReports]);

  const resolveResearchJobIdFromReportId = React.useCallback((reportId: string): string => {
    const normalizedReportId = String(reportId || '').trim();
    if (!normalizedReportId) return '';
    if (normalizedReportId.startsWith('report-v-')) {
      const vantagePart = normalizedReportId.slice('report-v-'.length);
      if (!vantagePart) return '';
      return vantagePart.startsWith('job_') ? vantagePart : `job_${vantagePart}`;
    }
    if (normalizedReportId.startsWith('report-')) {
      return normalizedReportId.slice('report-'.length);
    }
    return normalizedReportId;
  }, []);

  const collectResearchDeleteJobIds = React.useCallback((reportId: string): string[] => {
    const report = researchReportsRef.current.find((item) => item.id === reportId);
    const aliases = researchReportIdAliases({
      reportId,
      gatewayJobId: report?.gatewayJobId,
      vantageJobId: report?.vantageJobId,
    });
    const jobIds = aliases.filter((id) => !id.startsWith('report-'));
    const resolved = resolveResearchJobIdFromReportId(reportId);
    if (resolved && !jobIds.includes(resolved)) {
      jobIds.unshift(resolved);
    }
    // Prefer Vantage job_* ids so a gateway restart cannot 404 the original rjob-* id.
    return jobIds.sort((left, right) => Number(right.startsWith('job_')) - Number(left.startsWith('job_')));
  }, [resolveResearchJobIdFromReportId]);

  const purgeResearchJobFromGateway = React.useCallback(async (jobId: string): Promise<{
    ok: boolean;
    purged: boolean;
    notFound: boolean;
  }> => {
    const response = await fetch(
      `${gatewayBaseUrl}/api/v1/research/reports/jobs/${encodeURIComponent(jobId)}?purge_vantage=true`,
      {
        method: 'DELETE',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      },
    );
    const body = await response.json().catch(() => ({} as Record<string, unknown>));
    return {
      ok: response.ok,
      purged: Boolean(body?.purged_vantage),
      notFound: Boolean(body?.vantage_not_found),
    };
  }, [accessToken, gatewayBaseUrl]);

  const handleResearchReportDelete = React.useCallback((reportId: string) => {
    const normalizedReportId = String(reportId || '').trim();
    if (!normalizedReportId) {
      return;
    }
    const report = researchReportsRef.current.find((item) => item.id === normalizedReportId);
    const dismissAliases = researchReportIdAliases({
      reportId: normalizedReportId,
      gatewayJobId: report?.gatewayJobId,
      vantageJobId: report?.vantageJobId,
    });
    const deleteJobIds = collectResearchDeleteJobIds(normalizedReportId);

    setDismissedResearchReportIds((current) => {
      const next = new Set(current);
      dismissAliases.forEach((id) => next.add(id));
      dismissedResearchReportIdsRef.current = next;
      saveDismissedResearchReportIds(Array.from(next));
      return next;
    });
    setResearchReports((current) => current.filter((item) => item.id !== normalizedReportId));
    setResearchQueue((current) => current.filter((item) => item.reportId !== normalizedReportId));
    if (selectedResearchReportId === normalizedReportId) {
      setSelectedResearchReportId(null);
    }
    if (editingResearchReportId === normalizedReportId) {
      resetResearchIntake();
    }

    void (async () => {
      let purged = false;
      let reachedGateway = false;
      for (const jobId of deleteJobIds) {
        try {
          const result = await purgeResearchJobFromGateway(jobId);
          reachedGateway = reachedGateway || result.ok;
          if (result.purged || result.notFound) {
            purged = result.purged || result.notFound;
            if (result.purged) break;
          }
        } catch {
          // Try the next identifier spelling.
        }
      }
      if (!reachedGateway) {
        setResearchError(
          language === 'es'
            ? 'El reporte se ocultó aquí, pero NextCompli no pudo borrarlo de Vantage. Use “Eliminar todos los ocultos en Vantage”.'
            : 'Removed from this browser, but NextCompli could not delete it from Vantage. Use “Delete all hidden in Vantage”.',
        );
        return;
      }
      setResearchError(null);
      setResearchFeedback(
        language === 'es'
          ? (purged ? 'Reporte eliminado de Vantage.' : 'Reporte eliminado.')
          : (purged ? 'Report deleted from Vantage.' : 'Report deleted.'),
      );
      requestResearchQueueSync({ force: true });
    })();
  }, [
    collectResearchDeleteJobIds,
    editingResearchReportId,
    language,
    purgeResearchJobFromGateway,
    requestResearchQueueSync,
    resetResearchIntake,
    selectedResearchReportId,
  ]);

  const handlePurgeHiddenResearchReportsFromVantage = React.useCallback(async () => {
    const hiddenIds = researchHiddenCompletedReportIds.filter(Boolean);
    if (hiddenIds.length === 0 || isPurgingHiddenResearchReports) {
      return;
    }
    const confirmed = window.confirm(
      language === 'es'
        ? `Esto eliminará permanentemente ${hiddenIds.length} reporte(s) de Vantage. ¿Continuar?`
        : `This permanently deletes ${hiddenIds.length} report(s) from Vantage. Continue?`,
    );
    if (!confirmed) {
      return;
    }

    setIsPurgingHiddenResearchReports(true);
    setResearchError(null);
    setResearchFeedback(null);
    try {
      const results = await Promise.all(
        hiddenIds.map(async (reportId) => {
          const jobIds = collectResearchDeleteJobIds(reportId);
          if (jobIds.length === 0) {
            return { reportId, ok: false };
          }
          for (const jobId of jobIds) {
            const result = await purgeResearchJobFromGateway(jobId);
            if (result.ok && (result.purged || result.notFound)) {
              return { reportId, ok: true };
            }
          }
          return { reportId, ok: false };
        }),
      );
      const purgedCount = results.filter((item) => item.ok).length;
      const failedCount = results.length - purgedCount;

      // Only clear local dismiss state for IDs that were actually purged.
      setDismissedResearchReportIds((current) => {
        const next = new Set(current);
        for (const result of results) {
          if (result.ok) next.delete(result.reportId);
        }
        dismissedResearchReportIdsRef.current = next;
        saveDismissedResearchReportIds(Array.from(next));
        return next;
      });
      setResearchHiddenCompletedReportIds((current) =>
        failedCount === 0 ? [] : current.filter((id) => !results.some((row) => row.ok && row.reportId === id)),
      );
      setResearchHiddenCompletedCount((current) => (failedCount === 0 ? 0 : Math.max(0, current - purgedCount)));
      requestResearchQueueSync({ force: true });

      if (failedCount > 0) {
        setResearchError(
          language === 'es'
            ? `Se eliminaron ${purgedCount} de Vantage; ${failedCount} fallaron.`
            : `Deleted ${purgedCount} from Vantage; ${failedCount} failed.`,
        );
      } else {
        setResearchFeedback(
          language === 'es'
            ? `${purgedCount} reporte(s) eliminado(s) de Vantage.`
            : `${purgedCount} report(s) deleted from Vantage.`,
        );
      }
    } catch {
      setResearchError(
        language === 'es'
          ? 'No se pudieron eliminar los reportes ocultos de Vantage.'
          : 'Could not delete hidden reports from Vantage.',
      );
    } finally {
      setIsPurgingHiddenResearchReports(false);
    }
  }, [
    collectResearchDeleteJobIds,
    isPurgingHiddenResearchReports,
    language,
    purgeResearchJobFromGateway,
    researchHiddenCompletedReportIds,
    requestResearchQueueSync,
  ]);

  const handlePurgeAllResearchReportsFromVantage = React.useCallback(async () => {
    if (isPurgingAllResearchReports || isPurgingHiddenResearchReports) {
      return;
    }
    const confirmed = window.confirm(
      language === 'es'
        ? 'Esto eliminará PERMANENTEMENTE todos los reportes de investigación en Vantage (incluidos cola, en ejecución y completados). ¿Continuar?'
        : 'This PERMANENTLY deletes ALL research reports in Vantage (queued, running, and completed). Continue?',
    );
    if (!confirmed) {
      return;
    }

    setIsPurgingAllResearchReports(true);
    setResearchError(null);
    setResearchFeedback(null);
    try {
      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/research/reports/jobs?confirm=true`,
        {
          method: 'DELETE',
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        },
      );
      const body = await response.json().catch(() => ({}));
      let deletedCount = Number(body?.deleted_count || 0);
      let usedFallback = false;

      // Fallback: when bulk purge is forbidden/unavailable, hard-delete every known job id.
      if (!response.ok || (response.ok && deletedCount === 0 && (researchHiddenCompletedCount > 0 || researchReports.length > 0))) {
        const listResponse = await fetch(`${gatewayBaseUrl}/api/v1/research/reports/jobs`, {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        });
        const listBody = await listResponse.json().catch(() => ({}));
        const jobs = Array.isArray(listBody?.jobs) ? listBody.jobs : [];
        const jobIds = new Set<string>();
        for (const job of jobs) {
          const gatewayJobId = String(job?.job_id || '').trim();
          const vantageJobId = String(job?.vantage_job_id || '').trim();
          if (gatewayJobId) jobIds.add(gatewayJobId);
          if (vantageJobId) jobIds.add(vantageJobId);
        }
        for (const report of researchReports) {
          const resolved = resolveResearchJobIdFromReportId(report.id);
          if (resolved) jobIds.add(resolved);
        }
        for (const reportId of researchHiddenCompletedReportIds) {
          const resolved = resolveResearchJobIdFromReportId(reportId);
          if (resolved) jobIds.add(resolved);
        }

        if (!response.ok && jobIds.size === 0) {
          throw new Error(
            typeof body?.detail === 'string'
              ? body.detail
              : language === 'es'
                ? 'No se pudieron eliminar todos los reportes de Vantage.'
                : 'Could not delete all reports from Vantage.',
          );
        }

        if (jobIds.size > 0 && (!response.ok || deletedCount === 0)) {
          usedFallback = true;
          const purgeResults = await Promise.all(
            Array.from(jobIds).map(async (jobId) => {
              const purgeResponse = await fetch(
                `${gatewayBaseUrl}/api/v1/research/reports/jobs/${encodeURIComponent(jobId)}?purge_vantage=true`,
                {
                  method: 'DELETE',
                  headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
                },
              );
              return purgeResponse.ok;
            }),
          );
          deletedCount = purgeResults.filter(Boolean).length;
          if (deletedCount === 0 && !response.ok) {
            throw new Error(
              typeof body?.detail === 'string'
                ? body.detail
                : language === 'es'
                  ? 'No se pudieron eliminar todos los reportes de Vantage.'
                  : 'Could not delete all reports from Vantage.',
            );
          }
        }
      }

      setResearchReports([]);
      saveResearchReports([], researchAccessActor?.userId);
      setResearchQueue([]);
      saveResearchQueue([]);
      setSelectedResearchReportId(null);
      setEditingResearchReportId(null);
      resetResearchIntake();
      setDismissedResearchReportIds(() => {
        const cleared = new Set<string>();
        dismissedResearchReportIdsRef.current = cleared;
        saveDismissedResearchReportIds([]);
        return cleared;
      });
      setResearchHiddenCompletedReportIds([]);
      setResearchHiddenCompletedCount(0);
      requestResearchQueueSync({ force: true });

      setResearchFeedback(
        language === 'es'
          ? `Se eliminaron ${deletedCount} reporte(s) de Vantage${usedFallback ? ' (modo individual)' : ''}.`
          : `Deleted ${deletedCount} report(s) from Vantage${usedFallback ? ' (per-job fallback)' : ''}.`,
      );
    } catch (error) {
      setResearchError(
        error instanceof Error
          ? error.message
          : language === 'es'
            ? 'No se pudieron eliminar todos los reportes de Vantage.'
            : 'Could not delete all reports from Vantage.',
      );
    } finally {
      setIsPurgingAllResearchReports(false);
    }
  }, [
    accessToken,
    gatewayBaseUrl,
    isPurgingAllResearchReports,
    isPurgingHiddenResearchReports,
    language,
    requestResearchQueueSync,
    researchAccessActor?.userId,
    researchHiddenCompletedCount,
    researchHiddenCompletedReportIds,
    researchReports,
    resetResearchIntake,
    resolveResearchJobIdFromReportId,
  ]);

  const handleResearchQueueDelete = React.useCallback((queueId: string, reportId?: string) => {
    const item = researchQueue.find((row) => row.id === queueId);
    const dismissAliases = researchReportIdAliases({
      reportId: reportId || item?.reportId,
      gatewayJobId: item?.gatewayJobId,
    });
    setDismissedResearchReportIds((current) => {
      const next = new Set(current);
      dismissAliases.forEach((id) => next.add(id));
      dismissedResearchReportIdsRef.current = next;
      saveDismissedResearchReportIds(Array.from(next));
      return next;
    });
    if (reportId) {
      handleResearchReportDelete(reportId);
      return;
    }
    const gatewayJobId = String(item?.gatewayJobId || '').trim();
    setResearchQueue((current) => current.filter((row) => row.id !== queueId));
    setResearchError(null);
    setResearchFeedback(language === 'es' ? 'Elemento de cola eliminado.' : 'Queue item deleted.');
    if (gatewayJobId) {
      void purgeResearchJobFromGateway(gatewayJobId).then((result) => {
        if (!result.ok) {
          setResearchError(
            language === 'es'
              ? 'El reporte se ocultó aquí, pero NextCompli no pudo borrarlo de Vantage.'
              : 'Removed from this browser, but NextCompli could not delete it from Vantage.',
          );
        }
      }).catch(() => undefined);
    }
  }, [handleResearchReportDelete, language, purgeResearchJobFromGateway, researchQueue]);

  const handleResearchReportRetry = React.useCallback(async (reportId: string) => {
    const existing = researchReports.find((item) => item.id === reportId);
    if (!existing || !existing.isFallback) {
      return;
    }

    setResearchError(null);
    setResearchFeedback(null);
    setIsResearchLoading(true);

    const payload = {
      report_name: getCustomResearchReportName(existing.reportName) || undefined,
      target_type: existing.targetType,
      target_name: existing.targetName,
      website: existing.website || undefined,
      jurisdiction: existing.jurisdiction || undefined,
      affinity_depth: existing.targetType === 'company' ? (existing.affinityDepth || 1) : 1,
      known_aliases: Array.isArray(existing.knownAliases) ? existing.knownAliases.filter(Boolean) : [],
      date_of_birth: String(existing.keyData?.birthdate || existing.factDatabase?.entity?.birthdate || '').trim() || undefined,
      notes: existing.notes || undefined,
      key_data: existing.keyData || undefined,
      fact_database: existing.factDatabase || undefined,
      email_addresses: Array.isArray(existing.emailAddresses) ? existing.emailAddresses.filter(Boolean) : [],
      email_groups: Array.isArray(existing.emailGroups) ? existing.emailGroups.filter(Boolean) : [],
    };

    try {
      const body = await fetchResearchReportResponse(payload);
      const reportText = String(body.report || body.markdown || body.content || '').trim();
      if (!reportText) {
        throw new Error('empty-report');
      }

      const now = new Date().toISOString();
      const rerunOptions = normalizeResearchRerunOptions(body.rerun_options);
      const refreshed: ResearchReportRecord = {
        ...existing,
        content: reportText,
        ...extractResearchSearchTiming(body),
        affinityDepth: typeof body.affinity_depth === 'number' ? body.affinity_depth : existing.affinityDepth,
        affinityGraph: normalizeResearchAffinityGraph(body.affinity_graph),
        factDatabase: hydrateResearchFactDatabase(body.fact_database) || existing.factDatabase,
        sourceOfTruth: String(body.source_of_truth || existing.sourceOfTruth || 'fact_database').trim(),
        rerunOptions: rerunOptions.length > 0
          ? rerunOptions
          : buildDefaultResearchRerunOptions(typeof body.affinity_depth === 'number' ? body.affinity_depth : existing.affinityDepth),
        updatedAt: now,
        isFallback: false,
        fallbackReason: undefined,
      };

      setResearchReports((current) => current.map((item) => (item.id === reportId ? refreshed : item)));
      setResearchQueue((current) => current.map((item) => {
        if (item.reportId !== reportId) return item;
        return {
          ...item,
          status: 'completed',
          progress: 100,
          etaSeconds: 0,
          message: language === 'es' ? 'Reporte listo' : 'Report ready',
          updatedAt: now,
        };
      }));

      setSelectedResearchReportId(reportId);
      setResearchFeedback(language === 'es' ? 'Borrador actualizado desde Vantage.' : 'Draft refreshed from Vantage.');
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'service-unreachable';
      const now = new Date().toISOString();
      setResearchReports((current) => current.map((item) => (
        item.id === reportId
          ? { ...item, isFallback: true, fallbackReason: reason, updatedAt: now }
          : item
      )));
      setResearchError(
        language === 'es'
          ? `No se pudo reintentar el borrador local. Detalle: ${reason}`
          : `Could not retry local draft. Details: ${reason}`,
      );
    } finally {
      setIsResearchLoading(false);
    }
  }, [fetchResearchReportResponse, language, researchReports]);

  const handleResearchReportRerun = React.useCallback(async (
    reportId: string,
    overrides?: { affinity_depth?: number },
    reportOverride?: ResearchReportRecord,
  ) => {
    const existing = reportOverride || researchReports.find((item) => item.id === reportId);
    if (!existing) {
      return;
    }

    setResearchError(null);
    setResearchFeedback(null);

    const affinityDepth = Math.max(
      1,
      Math.min(
        4,
        Number(overrides?.affinity_depth || existing.affinityDepth || (existing.targetType === 'company' ? 1 : 1)),
      ),
    );
    const mergedKey = mergeResearchReportKeyData(existing);
    const knownAliases = mergeResearchReportKnownAliases(existing);
    const payload = {
      report_name: getCustomResearchReportName(existing.reportName) || undefined,
      target_type: existing.targetType,
      target_name: existing.targetName,
      website: existing.website || undefined,
      jurisdiction: existing.jurisdiction
        || String(existing.factDatabase?.entity?.state || existing.factDatabase?.entity?.county || '').trim()
        || undefined,
      affinity_depth: affinityDepth,
      known_aliases: knownAliases,
      date_of_birth: mergedKey.birthdate || undefined,
      notes: existing.notes || undefined,
      key_data: mergedKey,
      fact_database: existing.factDatabase || undefined,
      document_parse_receipt: existing.documentParseReceipt || undefined,
      email_addresses: Array.isArray(existing.emailAddresses) ? existing.emailAddresses.filter(Boolean) : [],
      email_groups: Array.isArray(existing.emailGroups) ? existing.emailGroups.filter(Boolean) : [],
      include_source_intelligence: true,
      source_intelligence_profile: 'smart',
      identifier_confirmed: true,
    };

    const now = new Date().toISOString();
    const queueId = `queue-rerun-${Date.now()}`;
    const queueItem: ResearchQueueItem = {
      id: queueId,
      reportName: getCustomResearchReportName(existing.reportName) || existing.targetName,
      targetType: existing.targetType,
      targetName: existing.targetName,
      status: 'queued',
      progress: 0,
      message: language === 'es' ? 'Reejecución en cola' : 'Rerun queued',
      createdAt: now,
      updatedAt: now,
      etaSeconds: existing.targetType === 'company' ? 420 : 900,
      reportId,
    };

    const markQueueFailed = (reason: string) => {
      setResearchQueue((current) => current.map((item) => (
        item.id === queueId
          ? {
            ...item,
            status: 'failed',
            progress: 100,
            etaSeconds: 0,
            message: reason,
            updatedAt: new Date().toISOString(),
          }
          : item
      )));
      setResearchQueueAck(null);
    };

    setResearchQueue((current) => [queueItem, ...current]);
    releaseDocumentFocus();
    setActiveResearchTab('queue');

    try {
      const body = await fetchResearchReportResponse(payload, { useAsync: true, detach: true });
      if (body.status === 'identifier_confirmation_required') {
        setResearchIdentifierConfirmation(body.identifier_readiness || {
          blocked: true,
          guidance: body.message,
        });
        markQueueFailed(
          language === 'es'
            ? 'Se requiere confirmación de identificadores'
            : 'Identifier confirmation required',
        );
        return;
      }
      const jobId = String((body as { job_id?: string }).job_id || '').trim();
      if (!jobId) {
        throw new Error('async-report-no-job-id');
      }

      setResearchQueue((current) => current.map((item) => (
        item.id === queueId
          ? {
            ...item,
            status: 'queued',
            progress: 0,
            gatewayJobId: jobId,
            message: language === 'es' ? 'Enviado a Vantage' : 'Submitted to Vantage',
            updatedAt: new Date().toISOString(),
          }
          : item
      )));
      setResearchQueueAck(
        language === 'es'
          ? `Reejecución en cola: ${resolveResearchReportDisplayTitle(queueItem)}`
          : `Rerun queued: ${resolveResearchReportDisplayTitle(queueItem)}`,
      );

      startResearchGatewayJobPoll({
        queueId,
        gatewayJobId: jobId,
        reportId,
        reportName: queueItem.reportName,
        targetType: existing.targetType,
        targetName: existing.targetName,
        website: existing.website,
        jurisdiction: existing.jurisdiction || '',
        knownAliases: existing.knownAliases || [],
        notes: existing.notes || '',
        affinityDepth,
        documentParseReceipt: existing.documentParseReceipt || undefined,
        emailAddresses: existing.emailAddresses || [],
        emailGroups: existing.emailGroups || [],
        keyDataPayload: {
          ...(existing.keyData || {}),
          names: String(existing.keyData?.names || existing.targetName || '').trim(),
          knownAliases: String(existing.keyData?.knownAliases || (existing.knownAliases || []).join(', ')).trim(),
        },
        editingReportId: reportId,
      });
      setResearchFeedback(
        language === 'es'
          ? `Reejecución enviada a Vantage (profundidad ${affinityDepth}). Siga el progreso en Cola.`
          : `Rerun submitted to Vantage (depth ${affinityDepth}). Track progress in Queue.`,
      );
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'service-unreachable';
      markQueueFailed(reason);
      setResearchError(
        language === 'es'
          ? `No se pudo relanzar el reporte. Detalle: ${reason}`
          : `Could not rerun report. Details: ${reason}`,
      );
    }
  }, [fetchResearchReportResponse, language, researchReports, startResearchGatewayJobPoll]);

  const handleResearchQueueRerun = React.useCallback(async (item: ResearchQueueItem) => {
    const targetName = item.targetName.trim();
    const targetType = item.targetType === 'company' ? 'company' : 'person';
    if (!targetName) {
      setResearchError(language === 'es' ? 'Este fallo no tiene un nombre para relanzar.' : 'This failure has no name to rerun.');
      return;
    }
    const now = new Date().toISOString();
    const queueId = `queue-rerun-${Date.now()}`;
    const reportId = generateResearchReportId();
    const queueItem: ResearchQueueItem = {
      id: queueId,
      reportName: item.reportName || targetName,
      targetType,
      targetName,
      status: 'queued',
      progress: 0,
      message: language === 'es' ? 'Reejecución en cola' : 'Rerun queued',
      createdAt: now,
      updatedAt: now,
      etaSeconds: targetType === 'company' ? 420 : 900,
      reportId,
    };
    setResearchError(null);
    setResearchQueue((current) => [queueItem, ...current]);
    releaseDocumentFocus();
    setActiveResearchTab('queue');
    try {
      const body = await fetchResearchReportResponse({
        target_type: targetType,
        target_name: targetName,
        affinity_depth: 1,
        include_source_intelligence: true,
        source_intelligence_profile: 'smart',
        identifier_confirmed: true,
        proceed_without_identifiers: true,
      }, { useAsync: true, detach: true });
      const jobId = String((body as { job_id?: string }).job_id || '').trim();
      if (!jobId) {
        throw new Error('async-report-no-job-id');
      }
      setResearchQueue((current) => current.map((row) => (
        row.id === queueId
          ? { ...row, gatewayJobId: jobId, message: language === 'es' ? 'Enviado a Vantage' : 'Submitted to Vantage', updatedAt: new Date().toISOString() }
          : row
      )));
      startResearchGatewayJobPoll({
        queueId,
        gatewayJobId: jobId,
        reportId,
        reportName: queueItem.reportName,
        targetType,
        targetName,
        jurisdiction: '',
        knownAliases: [],
        notes: '',
        affinityDepth: 1,
        emailAddresses: [],
        emailGroups: [],
        keyDataPayload: { names: targetName } as KeyData,
      });
      setResearchFeedback(
        language === 'es'
          ? `Reejecución enviada a Vantage: ${targetName}`
          : `Rerun submitted to Vantage: ${targetName}`,
      );
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'service-unreachable';
      setResearchQueue((current) => current.map((row) => (
        row.id === queueId
          ? { ...row, status: 'failed', progress: 100, etaSeconds: 0, message: reason, updatedAt: new Date().toISOString() }
          : row
      )));
      setResearchError(
        language === 'es'
          ? `No se pudo relanzar el reporte. Detalle: ${reason}`
          : `Could not rerun report. Details: ${reason}`,
      );
    }
  }, [fetchResearchReportResponse, language, startResearchGatewayJobPoll]);

  const [showDismissedResearchFacts, setShowDismissedResearchFacts] = React.useState(false);
  const [researchTransferSignalIndexes, setResearchTransferSignalIndexes] = React.useState<Set<number>>(() => new Set());
  const [researchTransferIncludeEntity, setResearchTransferIncludeEntity] = React.useState(true);
  const [researchTransferEntityFields, setResearchTransferEntityFields] = React.useState<Set<string>>(
    () => new Set(['canonical_name', 'alt_names', 'addresses', 'phones', 'emails', 'birthdate', 'company_affiliations']),
  );
  const [researchTransferSubjectId, setResearchTransferSubjectId] = React.useState<number | ''>('');
  const [researchTransferBusy, setResearchTransferBusy] = React.useState(false);

  const linkedOfacSubjectsForSelectedReport = React.useMemo(() => {
    if (!selectedResearchReport || !canAccessOfac) {
      return [] as OfacScreeningSubject[];
    }
    const reportId = String(selectedResearchReport.id || '').trim();
    const targetName = String(selectedResearchReport.targetName || '').trim().toLowerCase();
    return ofacScreeningSubjects.filter((subject) => {
      const reference = String(subject.reference_id || '').trim();
      const reportIds = Array.isArray(subject.identifiers?.research_report_ids)
        ? subject.identifiers?.research_report_ids.map((item) => String(item || '').trim())
        : [];
      const lastRef = String(subject.identifiers?.last_reference_id || '').trim();
      const nameMatch = targetName && String(subject.entity_name || '').trim().toLowerCase() === targetName;
      return (
        reference === reportId
        || lastRef === reportId
        || reportIds.includes(reportId)
        || Boolean(nameMatch)
      );
    });
  }, [canAccessOfac, ofacScreeningSubjects, selectedResearchReport]);

  React.useEffect(() => {
    if (!selectedResearchReport?.factDatabase?.signals) {
      setResearchTransferSignalIndexes(new Set());
      return;
    }
    const defaults = new Set<number>();
    selectedResearchReport.factDatabase.signals.forEach((signal, index) => {
      if (!signal || isFactSignalExcluded(signal)) {
        return;
      }
      // Default: transfer non-excluded facts (analyst can uncheck).
      defaults.add(index);
    });
    setResearchTransferSignalIndexes(defaults);
    setResearchTransferIncludeEntity(true);
    setResearchTransferEntityFields(new Set(['canonical_name', 'alt_names', 'addresses', 'birthdate', 'company_affiliations']));
  }, [selectedResearchReport?.id, selectedResearchReport?.factDatabase?.signals?.length]);

  React.useEffect(() => {
    if (linkedOfacSubjectsForSelectedReport.length === 1) {
      setResearchTransferSubjectId(linkedOfacSubjectsForSelectedReport[0].id);
      return;
    }
    if (
      researchTransferSubjectId !== ''
      && linkedOfacSubjectsForSelectedReport.some((row) => row.id === researchTransferSubjectId)
    ) {
      return;
    }
    if (linkedOfacSubjectsForSelectedReport.length > 0) {
      setResearchTransferSubjectId(linkedOfacSubjectsForSelectedReport[0].id);
      return;
    }
    setResearchTransferSubjectId('');
  }, [linkedOfacSubjectsForSelectedReport, researchTransferSubjectId]);

  const handleFactSignalDecision = React.useCallback(async (
    reportId: string,
    signalIndex: number,
    decision: 'included' | 'excluded' | 'review',
  ) => {
    const report = researchReports.find((item) => item.id === reportId);
    if (!report?.factDatabase?.signals?.[signalIndex]) {
      return;
    }

    setResearchError(null);
    setResearchFeedback(null);

    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/research/facts/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fact_database: report.factDatabase,
          signal_index: signalIndex,
          decision,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const body = (await response.json()) as { fact_database?: FactDatabase };
      const now = new Date().toISOString();
      const updatedReports = researchReports.map((item) => (
        item.id === reportId
          ? {
            ...item,
            factDatabase: hydrateResearchFactDatabase(body.fact_database) || item.factDatabase,
            updatedAt: now,
          }
          : item
      ));
      setResearchReports(updatedReports);
      saveResearchReports(updatedReports);
      setResearchFeedback(
        decision === 'excluded'
          ? (language === 'es' ? 'Hecho descartado.' : 'Fact dismissed.')
          : (language === 'es' ? 'Hecho confirmado.' : 'Fact confirmed.'),
      );
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'service-unreachable';
      setResearchError(
        language === 'es'
          ? `No se pudo actualizar la decisión del hecho. Detalle: ${reason}`
          : `Could not update fact decision. Details: ${reason}`,
      );
    }
  }, [gatewayBaseUrl, language, researchReports]);

  const buildResearchPreviewFactUpdatedReport = React.useCallback((): ResearchReportRecord | null => {
    if (!selectedResearchReport) {
      return null;
    }

    const draftWithLists = applyFactListDraftToKeyData(researchPreviewFactDraft, researchPreviewFactListDraft);

    const normalizedInput: KeyData = {
      ...EMPTY_KEY_DATA,
      names: String(draftWithLists.names || '').trim(),
      addresses: String(draftWithLists.addresses || '').trim(),
      phones: String(draftWithLists.phones || '').trim(),
      emails: String(draftWithLists.emails || '').trim(),
      birthdate: String(draftWithLists.birthdate || '').trim(),
      ageRange: normalizeAgeRangeForSubmit(String(draftWithLists.ageRange || '')),
      ssn: String(draftWithLists.ssn || '').trim(),
      dlNumber: String(draftWithLists.dlNumber || '').trim(),
      companies: String(draftWithLists.companies || '').trim(),
      socialMediaHandles: String(draftWithLists.socialMediaHandles || '').trim(),
      website: String(draftWithLists.website || '').trim(),
      knownAliases: String(draftWithLists.knownAliases || '').trim(),
    };

    const nextKeyData = buildResearchKeyData(selectedResearchReport, normalizedInput);
    const nextFactDatabase = buildResearchFactDatabase(selectedResearchReport, normalizedInput, selectedResearchReport.affinityDepth);
    const normalizedSourceCheckFacts = normalizeFactListItems(researchPreviewSourceCheckFactDraft);
    const autoAggregatedFacts = normalizeFactListItems(nextFactDatabase.sourceCheckFacts || []);
    const mergedFactList = normalizeFactListItems([...autoAggregatedFacts, ...normalizedSourceCheckFacts]);
    nextFactDatabase.sourceCheckFacts = mergedFactList;
    nextFactDatabase.sourceCheckFactCount = mergedFactList.length;
    const nextRerunOptions = selectedResearchReport.rerunOptions && selectedResearchReport.rerunOptions.length > 0
      ? selectedResearchReport.rerunOptions
      : buildDefaultResearchRerunOptions(selectedResearchReport.affinityDepth);
    const now = new Date().toISOString();

    return {
      ...selectedResearchReport,
      keyData: nextKeyData,
      factDatabase: nextFactDatabase,
      sourceOfTruth: 'fact_database',
      rerunOptions: nextRerunOptions,
      updatedAt: now,
    };
  }, [researchPreviewFactDraft, researchPreviewFactListDraft, researchPreviewSourceCheckFactDraft, selectedResearchReport]);

  const handleResearchPreviewFactSaveAndRerun = React.useCallback(async () => {
    const updatedReport = buildResearchPreviewFactUpdatedReport();
    if (!updatedReport) {
      return;
    }

    setResearchReports((current) => current.map((item) => (item.id === updatedReport.id ? updatedReport : item)));
    setIsResearchPreviewFactEditing(false);
    const override = typeof researchPreviewSelectedRerunDepth === 'number'
      ? { affinity_depth: researchPreviewSelectedRerunDepth }
      : undefined;
    await handleResearchReportRerun(updatedReport.id, override, updatedReport);
  }, [buildResearchPreviewFactUpdatedReport, handleResearchReportRerun, researchPreviewSelectedRerunDepth]);

  const hydrateResearchReportFromGateway = React.useCallback(async (reportId: string) => {
    const normalizedReportId = String(reportId || '').trim();
    if (!normalizedReportId.startsWith('report-')) {
      return;
    }
    const gatewayJobId = normalizedReportId.slice('report-'.length);
    if (!gatewayJobId || !accessToken) {
      return;
    }
    const existing = researchReportsRef.current.find((report) => report.id === normalizedReportId);
    if (existing && !existing.isFallback && existing.factDatabase && String(existing.content || '').length >= 400
      && researchReportContentHasBiography(String(existing.content || ''))) {
      return;
    }
    try {
      const detailResponse = await fetch(
        `${gatewayBaseUrl}/api/v1/research/reports/jobs/${encodeURIComponent(gatewayJobId)}`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!detailResponse.ok) {
        return;
      }
      const detail = await detailResponse.json() as GatewayResearchJobSummary;
      if (!detail?.result || typeof detail.result !== 'object') {
        return;
      }
      const targetName = String(detail.target_name || existing?.targetName || '').trim() || 'Research report';
      const targetType = detail.target_type === 'company' ? 'company' : 'person';
      const record = applyResearchResponseToRecord(detail.result, {
        reportId: normalizedReportId,
        reportName: existing?.reportName || targetName,
        targetType,
        targetName,
        website: existing?.website,
        jurisdiction: existing?.jurisdiction || '',
        knownAliases: existing?.knownAliases || [],
        notes: existing?.notes || '',
        affinityDepth: existing?.affinityDepth || 1,
        emailAddresses: existing?.emailAddresses || [],
        emailGroups: existing?.emailGroups || [],
        keyDataPayload: { ...(existing?.keyData || {}), names: targetName },
        createdAt: existing?.createdAt || detail.created_at || new Date().toISOString(),
      });
      const ownedRecord: ResearchReportRecord = {
        ...record,
        ownerUserId: String(detail.created_by_user_id || existing?.ownerUserId || record.ownerUserId || '').trim() || record.ownerUserId,
        ownerEmail: String(detail.created_by_email || existing?.ownerEmail || record.ownerEmail || '').trim() || record.ownerEmail,
        companyId: detail.company_id != null ? Number(detail.company_id) : (existing?.companyId ?? record.companyId),
        isFallback: false,
        fallbackReason: undefined,
      };
      setResearchReports((current) => {
        const index = current.findIndex((report) => report.id === normalizedReportId);
        if (index < 0) {
          return dedupeResearchReports([ownedRecord, ...current]).slice(0, 50);
        }
        const next = [...current];
        next[index] = { ...current[index], ...ownedRecord, id: normalizedReportId };
        return next;
      });
    } catch {
      // Preview can still show the stub until the next sync hydrates.
    }
  }, [accessToken, applyResearchResponseToRecord, gatewayBaseUrl]);

  const handleResearchReportView = React.useCallback((reportId: string, options?: { expanded?: boolean }) => {
    setSelectedResearchReportId(reportId);
    void hydrateResearchReportFromGateway(reportId);
    if (options?.expanded) {
      researchPreviewLastFocusedRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setIsResearchPreviewExpanded(true);
      return;
    }
    window.setTimeout(() => {
      const preview = researchReportPreviewRef.current;
      if (preview) {
        scrollElementWithinConsole(preview);
      }
    }, 0);
  }, [hydrateResearchReportFromGateway]);

  React.useEffect(() => {
    const hydratedFactDatabase = hydrateResearchFactDatabase(selectedResearchReport?.factDatabase);
    const nextDraft = mapResearchReportToFactDraft(
      hydratedFactDatabase && selectedResearchReport
        ? { ...selectedResearchReport, factDatabase: hydratedFactDatabase }
        : selectedResearchReport,
    );
    setResearchPreviewFactDraft(nextDraft);
    setResearchPreviewFactListDraft(buildFactListDraftFromKeyData(nextDraft));
    setResearchPreviewSourceCheckFactDraft(
      normalizeFactListItems(
        Array.isArray(hydratedFactDatabase?.sourceCheckFacts)
          ? hydratedFactDatabase?.sourceCheckFacts || []
          : [],
      ),
    );
    if (
      selectedResearchReport
      && hydratedFactDatabase
      && (!Array.isArray(selectedResearchReport.factDatabase?.sourceCheckFacts)
        || selectedResearchReport.factDatabase.sourceCheckFacts.length === 0)
      && Array.isArray(hydratedFactDatabase.sourceCheckFacts)
      && hydratedFactDatabase.sourceCheckFacts.length > 0
    ) {
      setResearchReports((current) => current.map((item) => (
        item.id === selectedResearchReport.id
          ? { ...item, factDatabase: hydratedFactDatabase }
          : item
      )));
    }
  }, [mapResearchReportToFactDraft, selectedResearchReport]);

  React.useEffect(() => {
    setResearchPreviewSelectedRerunDepth(getCompletedReportRerunDepth(selectedResearchReportId || ''));
    setIsResearchPreviewFactEditing(false);
  }, [selectedResearchReportId, getCompletedReportRerunDepth]);

  const handleResearchPreviewFactEditStart = React.useCallback(() => {
    const hydratedFactDatabase = hydrateResearchFactDatabase(selectedResearchReport?.factDatabase);
    const reportForDraft = hydratedFactDatabase && selectedResearchReport
      ? { ...selectedResearchReport, factDatabase: hydratedFactDatabase }
      : selectedResearchReport;
    const nextDraft = mapResearchReportToFactDraft(reportForDraft);
    setResearchPreviewFactDraft(nextDraft);
    setResearchPreviewFactListDraft(buildFactListDraftFromKeyData(nextDraft));
    setResearchPreviewSourceCheckFactDraft(
      normalizeFactListItems(
        Array.isArray(hydratedFactDatabase?.sourceCheckFacts)
          ? hydratedFactDatabase?.sourceCheckFacts || []
          : [],
      ),
    );
    setResearchPreviewSelectedRerunDepth(getCompletedReportRerunDepth(selectedResearchReport?.id || ''));
    setIsResearchPreviewFactEditing(true);
  }, [mapResearchReportToFactDraft, selectedResearchReport, getCompletedReportRerunDepth]);

  const handleResearchPreviewFactEditCancel = React.useCallback(() => {
    const hydratedFactDatabase = hydrateResearchFactDatabase(selectedResearchReport?.factDatabase);
    const reportForDraft = hydratedFactDatabase && selectedResearchReport
      ? { ...selectedResearchReport, factDatabase: hydratedFactDatabase }
      : selectedResearchReport;
    const nextDraft = mapResearchReportToFactDraft(reportForDraft);
    setResearchPreviewFactDraft(nextDraft);
    setResearchPreviewFactListDraft(buildFactListDraftFromKeyData(nextDraft));
    setResearchPreviewSourceCheckFactDraft(
      normalizeFactListItems(
        Array.isArray(hydratedFactDatabase?.sourceCheckFacts)
          ? hydratedFactDatabase?.sourceCheckFacts || []
          : [],
      ),
    );
    setResearchPreviewSelectedRerunDepth(getCompletedReportRerunDepth(selectedResearchReport?.id || ''));
    setIsResearchPreviewFactEditing(false);
  }, [mapResearchReportToFactDraft, selectedResearchReport, getCompletedReportRerunDepth]);

  const handleResearchPreviewFactListItemChange = React.useCallback((field: FactListField, index: number, value: string) => {
    setResearchPreviewFactListDraft((current) => {
      const nextItems = [...(current[field] || [])];
      if (index < 0 || index >= nextItems.length) {
        return current;
      }
      nextItems[index] = value;
      return {
        ...current,
        [field]: nextItems,
      };
    });
  }, []);

  const handleResearchPreviewFactListItemAdd = React.useCallback((field: FactListField) => {
    setResearchPreviewFactListDraft((current) => ({
      ...current,
      [field]: [...(current[field] || []), ''],
    }));
  }, []);

  const handleResearchPreviewFactListItemDelete = React.useCallback((field: FactListField, index: number) => {
    setResearchPreviewFactListDraft((current) => {
      const nextItems = [...(current[field] || [])];
      if (index < 0 || index >= nextItems.length) {
        return current;
      }
      nextItems.splice(index, 1);
      return {
        ...current,
        [field]: nextItems,
      };
    });
  }, []);

  const handleResearchPreviewSourceCheckFactChange = React.useCallback((index: number, value: string) => {
    setResearchPreviewSourceCheckFactDraft((current) => {
      const nextItems = [...current];
      if (index < 0 || index >= nextItems.length) {
        return current;
      }
      nextItems[index] = value;
      return nextItems;
    });
  }, []);

  const handleResearchPreviewSourceCheckFactAdd = React.useCallback(() => {
    setResearchPreviewSourceCheckFactDraft((current) => [...current, '']);
  }, []);

  const handleResearchPreviewSourceCheckFactDelete = React.useCallback((index: number) => {
    setResearchPreviewSourceCheckFactDraft((current) => {
      const nextItems = [...current];
      if (index < 0 || index >= nextItems.length) {
        return current;
      }
      nextItems.splice(index, 1);
      return nextItems;
    });
  }, []);

  const handleResearchPreviewFactSave = React.useCallback(() => {
    const updatedReport = buildResearchPreviewFactUpdatedReport();
    if (!updatedReport) {
      return;
    }

    setResearchReports((current) => current.map((item) => (item.id === updatedReport.id ? updatedReport : item)));

    setIsResearchPreviewFactEditing(false);
    setResearchFeedback(language === 'es' ? 'Base de hechos actualizada.' : 'Fact database updated.');
  }, [buildResearchPreviewFactUpdatedReport, language]);

  const closeResearchPreviewExpanded = React.useCallback(() => {
    setResearchPreviewSelectedRerunDepth(1);
    setIsResearchPreviewFactEditing(false);
    setIsResearchPreviewExpanded(false);
  }, []);

  React.useEffect(() => {
    if (!isResearchPreviewExpanded) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => {
      researchPreviewCloseButtonRef.current?.focus();
    }, 0);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeResearchPreviewExpanded();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      researchPreviewLastFocusedRef.current?.focus();
      researchPreviewLastFocusedRef.current = null;
    };
  }, [closeResearchPreviewExpanded, isResearchPreviewExpanded]);

  // Identifier confirmation is a true modal; lock page scroll only while it is open.
  React.useEffect(() => {
    if (!researchIdentifierConfirmation) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [researchIdentifierConfirmation]);

  // Queue ack is informational — never leave body scroll locked after queueing a report.
  React.useEffect(() => {
    if (!researchQueueAck) {
      return;
    }
    if (document.body.style.overflow === 'hidden' && !isResearchPreviewExpanded && !researchIdentifierConfirmation) {
      document.body.style.overflow = '';
    }
    const timer = window.setTimeout(() => setResearchQueueAck(null), 12000);
    return () => window.clearTimeout(timer);
  }, [isResearchPreviewExpanded, researchIdentifierConfirmation, researchQueueAck]);

  const navPathByItem: Record<DashboardNavItemId, string> = {
    home: '/console/home',
    dashboards: '/console/dashboards',
    crm: CONSOLE_PURSUIT_PATH,
    inlet: CONSOLE_INLET_PATH,
    razzmatazz: CONSOLE_RAZZMATAZZ_PATH,
    personal: '/console/personal',
    security: '/console/security',
    team: '/console/team',
    privacy: '/privacy',
    payments: '/console/payments',
    billing: '/console/billing',
    subscriptions: '/console/subscriptions',
    subscriptionHub: '/console/subscription-hub',
    subscriptionLevels: '/console/subscription-levels',
    payingSubscriptions: '/console/paying-subscriptions',
    expiredSubscriptions: '/console/expired-subscriptions',
    mySubscriptions: '/console/my-subscriptions',
    research: '/console/research',
    complidesk: '/console/complidesk',
  postlude: '/console/postlude',
    ofac: '/console/ofac-compliance',
    ofacCompliance: '/console/ofac-compliance',
    ofacDashboards: '/console/ofac-dashboards',
    ofacLiveFeed: '/console/ofac-live-feed',
    ofacScreening: '/console/ofac-screening',
    ofacInvestigations: '/console/ofac-investigations',
    ofacWhitelist: '/console/ofac-whitelist',
    ofacCapta: '/console/ofac-capta',
    websiteCompliance: '/console/website-compliance',
    cloudCompliance: '/console/cloud-compliance',
    dodContracts: '/console/government-compliance',
    educationCompliance: '/console/education-compliance',
    aiCompliance: '/console/ai-compliance',
    agent: '/console/agent',
    businessFormation: '/console/business-formation',
    willsTrusts: '/console/wills-trusts',
    complianceCalendar: '/console/compliance-calendar',
    audits: '/console/audits',
    tips: '/console/tips',
    style: '/console/style/designer',
    admin: '/console/admin/groups',
    costOptimization: '/console/admin/cost-optimization',
    emailInbox: '/console/email-accounts',
    voiceMessages: '/console/admin/voice-messages',
    keycloakAdmin: '/console/admin/keycloak',
    socials: '/console/socials',
  };

  const breadcrumbTrail = React.useMemo(() => {
    const navLabelByItem: Record<DashboardNavItemId, string> = {
      home: copy.navHome,
      dashboards: copy.navDashboards,
      crm: copy.navCrm,
      inlet: copy.navInlet,
      razzmatazz: copy.navRazzmatazz,
      personal: copy.navPersonal,
      security: copy.navSecurity,
      team: copy.navTeam,
      privacy: copy.navPrivacy,
      payments: copy.navPayments,
      billing: copy.navBilling,
      subscriptions: copy.navSubscriptions,
      subscriptionHub: language === 'es' ? 'Suscripciones' : 'Subscriptions',
      subscriptionLevels: copy.navSubscriptionLevels,
      payingSubscriptions: copy.navPayingSubscriptions,
      expiredSubscriptions: copy.navExpiredSubscriptions,
      mySubscriptions: 'mySubscriptions',
      research: copy.navResearch,
      complidesk: 'CompliDesk',
      postlude: 'Postlude',
      agent: copy.navAgent,
      ofac: copy.navOfacCompliance,
      ofacCompliance: copy.navOfacCompliance,
      ofacDashboards: copy.navOfacDashboards,
      ofacLiveFeed: copy.navOfacLiveFeed,
      ofacScreening: copy.navOfacScreening,
      ofacInvestigations: copy.navOfacInvestigations,
      ofacWhitelist: copy.navOfacWhitelist,
      ofacCapta: copy.navOfacCapta,
      websiteCompliance: copy.navWebsiteCompliance,
      cloudCompliance: copy.navCloudCompliance,
      dodContracts: copy.navDodContracts,
      educationCompliance: copy.navEducationCompliance,
      aiCompliance: copy.navAiCompliance,
      businessFormation: copy.navBusinessFormation,
      willsTrusts: copy.navWillsTrusts,
      complianceCalendar: language === 'es' ? 'Calendario de cumplimiento' : 'Compliance Calendar',
      audits: language === 'es' ? 'Auditorías' : 'Audits',
      tips: copy.navTips,
      style: copy.navStyle,
      admin: language === 'es' ? 'Gestion de acceso' : 'Access Management',
      costOptimization: language === 'es' ? 'Optimizacion de costos' : 'Cost Optimization',
      emailInbox: language === 'es' ? 'Correo' : 'Mail',
      voiceMessages: language === 'es' ? 'Mensajes automatizados' : 'Automated Messaging',
      keycloakAdmin: language === 'es' ? 'Proveedor de identidad' : 'Identity Provider',
      socials: 'Socials',
    };

    const trail: string[] = ['Console'];

    if (isPursuitOperatorPath(location.pathname)) {
      trail.push(copy.navCrm);
      const pursuitView = pursuitViewFromPath(location.pathname);
      if (pursuitView !== 'home') {
        trail.push(pursuitViewBreadcrumbLabel(pursuitView, language));
      }
      return trail;
    }

    if (isInletOperatorPath(location.pathname)) {
      trail.push(copy.navInlet);
      return trail;
    }

    if (isRazzmatazzOperatorPath(location.pathname)) {
      trail.push(copy.navRazzmatazz);
      return trail;
    }

    if (location.pathname.includes('/console/admin/')) {
    if (location.pathname.includes('/console/admin/crm')) {
        trail.push(copy.navCrm);
        return trail;
      }
      if (location.pathname.includes('/console/admin/cost-optimization')) {
        trail.push(language === 'es' ? 'Optimizacion de costos' : 'Cost Optimization');
        return trail;
      }
      const section = (location.pathname.split('/').pop() ?? 'users').replace(/-/g, ' ');
      trail.push(language === 'es' ? 'Gestion de acceso' : 'Access Management');
      trail.push(section.charAt(0).toUpperCase() + section.slice(1));
      return trail;
    }

    if (isOfacNavItem(activeNavItem)) {
      trail.push(copy.navOfacGroup);
      trail.push(navLabelByItem[activeNavItem]);
      return trail;
    }

    trail.push(navLabelByItem[activeNavItem]);
    return trail;
  }, [activeNavItem, copy.navBilling, copy.navBusinessFormation, copy.navCrm, copy.navInlet, copy.navRazzmatazz, copy.navDashboards, copy.navDodContracts, copy.navEducationCompliance, copy.navExpiredSubscriptions, copy.navHome, copy.navOfac, copy.navOfacCapta, copy.navOfacGroup, copy.navOfacInvestigations, copy.navOfacLiveFeed, copy.navOfacScreening, copy.navOfacWhitelist, copy.navPayingSubscriptions, copy.navPayments, copy.navPersonal, copy.navPrivacy, copy.navResearch, copy.navSecurity, copy.navStyle, copy.navSubscriptionLevels, copy.navSubscriptions, copy.navTips, copy.navWebsiteCompliance, copy.navCloudCompliance, isOfacNavItem, language, location.pathname]);
  const shouldLoadLiveMetrics = process.env.NODE_ENV !== 'test' && Boolean(metricsServiceUrl);

  React.useEffect(() => {
    window.localStorage.setItem(ACCOUNT_PROFILE_STORAGE_KEY, JSON.stringify(accountProfile));
  }, [accountProfile]);

  React.useEffect(() => {
    window.localStorage.setItem(ACCOUNT_AVATAR_STORAGE_KEY, JSON.stringify(avatarByUuid));
  }, [avatarByUuid]);

  React.useEffect(() => {
    if (!shouldLoadLiveMetrics) {
      return;
    }

    const loadMetricsDashboards = async () => {
      try {
        const aiInsightsPromise = fetch(`${metricsServiceUrl}/ai/insights?limit=3`)
          .then(async (response) => {
            if (!response.ok) {
              return null;
            }
            return (await response.json()) as AIInsightsData;
          })
          .catch(() => null);

        const termsReplayPromise = fetch(`${metricsServiceUrl}/metrics/legal/terms-replay-verification`)
          .then(async (response) => {
            if (!response.ok) {
              return null;
            }
            return (await response.json()) as TermsReplayVerificationResponse;
          })
          .catch(() => null);

        const assentReplaySummaryPromise = fetch(`${metricsServiceUrl}/metrics/legal/assent-events/summary`)
          .then(async (response) => {
            if (!response.ok) {
              return null;
            }
            return (await response.json()) as AssentReplaySummaryResponse;
          })
          .catch(() => null);

        const contractUpdateNoticePromise = fetch(`${metricsServiceUrl}/metrics/legal/contract-update-notice-status`)
          .then(async (response) => {
            if (!response.ok) {
              return null;
            }
            return (await response.json()) as ContractUpdateNoticeStatusResponse;
          })
          .catch(() => null);

        const termsPageAccessPromise = fetch(`${metricsServiceUrl}/metrics/legal/terms-page-access-and-linkage`)
          .then(async (response) => {
            if (!response.ok) {
              return null;
            }
            return (await response.json()) as TermsPageAccessAndLinkageStatusResponse;
          })
          .catch(() => null);

        const authenticatedEntrypointAssentPromise = fetch(`${metricsServiceUrl}/metrics/legal/authenticated-entrypoint-assent-status`)
          .then(async (response) => {
            if (!response.ok) {
              return null;
            }
            return (await response.json()) as AuthenticatedEntrypointAssentStatusResponse;
          })
          .catch(() => null);

        const negotiatedContractTermsPromise = fetch(`${metricsServiceUrl}/metrics/legal/negotiated-contract-terms-status`)
          .then(async (response) => {
            if (!response.ok) {
              return null;
            }
            return (await response.json()) as NegotiatedContractTermsStatusResponse;
          })
          .catch(() => null);

        const publicTermsPagePromise = fetch(`${metricsServiceUrl}/metrics/legal/public-terms-page-status`)
          .then(async (response) => {
            if (!response.ok) {
              return null;
            }
            return (await response.json()) as PublicTermsPageStatusResponse;
          })
          .catch(() => null);

        const [dashboardResponse, serviceDataResponse, legalExposureInputResponse] = await Promise.all([
          fetch(`${metricsServiceUrl}/metrics/dashboard`).catch(() => null),
          fetch(`${metricsServiceUrl}/metrics/service-data`).catch(() => null),
          fetch('/legal-exposure-input.json').catch(() => null),
        ]);

        let hasMetricsUpdate = false;
        let latestServiceRecords: MetricsDashboardService[] = [];

        if (dashboardResponse?.ok) {
          const metricsData = (await dashboardResponse.json()) as MetricsDashboardResponse;
          setMetricsDashboardData(metricsData);
          latestServiceRecords = metricsData.services ?? [];
          const serviceDashboards = (metricsData.services ?? [])
            .map((service) => service.service_name)
            .filter((serviceName) => serviceName && serviceName.trim().length > 0)
            .map(formatServiceDashboardName);

          if (serviceDashboards.length > 0) {
            setMetricsDashboards(
              Array.from(
                new Set([
                  ...SERVICE_DASHBOARD_FALLBACK_KEYS.map(formatServiceDashboardName),
                  ...serviceDashboards,
                ]),
              ),
            );
          }

          hasMetricsUpdate = true;
        }

        if (serviceDataResponse?.ok) {
          const serviceDataPayload = (await serviceDataResponse.json()) as { services?: MetricsServiceDataSummary };
          if (serviceDataPayload.services) {
            setMetricsServiceData(serviceDataPayload.services);
            const serviceDataDashboards = Object.keys(serviceDataPayload.services)
              .filter((serviceName) => serviceName && serviceName.trim().length > 0)
              .map(formatServiceDashboardName);
            if (serviceDataDashboards.length > 0) {
              setMetricsDashboards((current) =>
                Array.from(
                  new Set([
                    ...SERVICE_DASHBOARD_FALLBACK_KEYS.map(formatServiceDashboardName),
                    ...current,
                    ...serviceDataDashboards,
                  ]),
                ),
              );
            }
            hasMetricsUpdate = true;
          }
        }

        if (legalExposureInputResponse?.ok) {
          const legalExposurePayload = (await legalExposureInputResponse.json()) as { areas?: LegalExposureAreaInput[] };
          const legalAreas = Array.isArray(legalExposurePayload.areas)
            ? legalExposurePayload.areas.filter((area) => area && area.service_name)
            : [];

          if (legalAreas.length > 0) {
            setLegalExposureAreaByService(
              Object.fromEntries(legalAreas.map((area) => [area.service_name, area])) as Record<string, LegalExposureAreaInput>,
            );

            const legalAreaDashboards = legalAreas
              .map((area) => area.service_name)
              .filter((serviceName) => serviceName && serviceName.trim().length > 0)
              .map(formatServiceDashboardName);

            setMetricsDashboards((current) =>
              Array.from(
                new Set([
                  ...SERVICE_DASHBOARD_FALLBACK_KEYS.map(formatServiceDashboardName),
                  ...current,
                  ...legalAreaDashboards,
                ]),
              ),
            );

            hasMetricsUpdate = true;
          }
        }

        if (hasMetricsUpdate) {
          setMetricsLastUpdated(new Date());
        }

        const aiInsightsPayload = await aiInsightsPromise;
        if (aiInsightsPayload) {
          setAiInsightsData(aiInsightsPayload);
          const serviceRecords = latestServiceRecords;
          const topServiceNames = (aiInsightsPayload.top_risks ?? [])
            .map((risk) => risk.service_name)
            .filter((serviceName) => Boolean(serviceName))
            .slice(0, 3);

          const deepEntries = await Promise.all(
            topServiceNames.map(async (serviceName) => {
              const serviceRecord = serviceRecords.find((record) => record.service_name === serviceName);
              if (!serviceRecord) {
                return null;
              }

              const response = await fetch(`${metricsServiceUrl}/ai/deep-learning/risk-inference`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  service_name: serviceName,
                  status: serviceRecord.status ?? 'degraded',
                  risk_level: serviceRecord.risk_level ?? 'medium',
                  error_rate: serviceRecord.error_rate ?? 0,
                  response_time_ms: serviceRecord.response_time_ms ?? 0,
                  evidence_items_count: Math.max(1, serviceRecord.requests_success ?? 0),
                  lacking_items_count: Math.max(0, (serviceRecord.requests_total ?? 0) - (serviceRecord.requests_success ?? 0)),
                }),
              });
              if (!response.ok) {
                return null;
              }
              const payload = (await response.json()) as AIDeepLearningInferenceData;
              return [serviceName, payload] as const;
            }),
          );

          const deepMap: Record<string, AIDeepLearningInferenceData> = {};
          deepEntries.forEach((entry) => {
            if (!entry) {
              return;
            }
            deepMap[entry[0]] = entry[1];
          });
          setDeepLearningByService(deepMap);

          const forecastEntries = await Promise.all(
            topServiceNames.map(async (serviceName) => {
              const response = await fetch(
                `${metricsServiceUrl}/ai/predictive/risk-forecast?service_name=${encodeURIComponent(serviceName)}&horizon_days=3`,
              );
              if (!response.ok) {
                return null;
              }
              const payload = (await response.json()) as AIPredictiveForecastData;
              return [serviceName, payload] as const;
            }),
          );

          const forecastMap: Record<string, AIPredictiveForecastData> = {};
          forecastEntries.forEach((entry) => {
            if (!entry) {
              return;
            }
            forecastMap[entry[0]] = entry[1];
          });
          setForecastByService(forecastMap);

          const seed = topServiceNames
            .map((serviceName) => {
              const risk = (aiInsightsPayload.top_risks ?? []).find((item) => item.service_name === serviceName);
              return `${serviceName}: ${(risk?.rationale ?? []).join(' ')}`;
            })
            .join(' ')
            .trim();
          if (seed) {
            setNlpInput((current) => (current.trim() ? current : seed));
          }
        }

        const termsReplayPayload = await termsReplayPromise;
        setTermsReplayGateStatus(normalizeTermsReplayGateStatus(termsReplayPayload));

        const assentReplayPayload = await assentReplaySummaryPromise;
        setAssentReplaySummary(assentReplayPayload);
        setAssentReplayStatus(normalizeAssentReplayStatus(assentReplayPayload));

        const contractUpdatePayload = await contractUpdateNoticePromise;
        setContractUpdateNoticeGateStatus(normalizeContractUpdateNoticeGateStatus(contractUpdatePayload));

        const termsPageAccessPayload = await termsPageAccessPromise;
        setTermsPageAccessGateStatus(normalizeTermsPageAccessGateStatus(termsPageAccessPayload));

        const authenticatedEntrypointAssentPayload = await authenticatedEntrypointAssentPromise;
        setAuthenticatedEntrypointAssentGateStatus(
          normalizeAuthenticatedEntrypointAssentGateStatus(authenticatedEntrypointAssentPayload),
        );

        const negotiatedContractTermsPayload = await negotiatedContractTermsPromise;
        setNegotiatedContractTermsGateStatus(
          normalizeNegotiatedContractTermsGateStatus(negotiatedContractTermsPayload),
        );

        const publicTermsPagePayload = await publicTermsPagePromise;
        setPublicTermsPageGateStatus(
          normalizePublicTermsPageGateStatus(publicTermsPagePayload),
        );
      } catch {
      }
    };

    loadMetricsDashboards();

    const intervalId = window.setInterval(() => {
      loadMetricsDashboards();
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [metricsServiceUrl, shouldLoadLiveMetrics]);

  React.useEffect(() => {
    setDashboardSubscriptions((current) => {
      const next: Record<string, boolean> = {};

      availableDashboards.forEach((dashboardName) => {
        next[dashboardName] = current[dashboardName] ?? true;
      });

      return next;
    });
  }, [availableDashboards]);

  React.useEffect(() => {
    setAlertChecklist((current) => {
      const nextChecklist: Record<string, boolean> = {};

      availableDashboards.forEach((serviceName) => {
        const saved = window.localStorage.getItem(`nextcompli.alerts.service.${serviceName}`);
        nextChecklist[serviceName] = saved ? saved === 'true' : (current[serviceName] ?? true);
      });

      return nextChecklist;
    });
  }, [availableDashboards]);

  React.useEffect(() => {
    if (!recoveryEmailVerified) {
      setRecoveryEmail(accountEmail);
      setSecurityRecoveryEmailDraft(accountEmail);
    }
  }, [accountEmail, recoveryEmailVerified]);

  React.useEffect(() => {
    setSecurityPhoneDraft(twoStepPhone);
  }, [twoStepPhone]);

  React.useEffect(() => {
    setSecurityRecoveryPhoneDraft(recoveryPhone);
  }, [recoveryPhone]);

  React.useEffect(() => {
    setSecurityRecoveryEmailDraft(recoveryEmail);
  }, [recoveryEmail]);

  React.useEffect(() => {
    if (!accessToken) {
      setLoginEmailAliases([]);
      return;
    }

    const controller = new AbortController();
    const loadAliases = async () => {
      try {
        const response = await fetch(authEmailAliasesUrl, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          signal: controller.signal,
        });
        if (!response.ok) {
          return;
        }
        const payload = (await response.json()) as { email_aliases?: string[] };
        setLoginEmailAliases(Array.isArray(payload.email_aliases) ? payload.email_aliases : []);
      } catch {
      }
    };

    void loadAliases();
    return () => {
      controller.abort();
    };
  }, [accessToken, authEmailAliasesUrl]);

  React.useEffect(() => {
    setActiveNavItem(initialNavItem);
    // Keep the open group the operator chose. Collapsing on every route change
    // hid Access Management after /console?openAdmin=1 bounced to /console/home.
    // Still open the Work job subgroup that owns this page so Form & protect
    // is not left open on Government Compliance / other Comply pages.
    const workSub = openWorkSubgroupForNavSelection(initialNavItem);
    if (workSub) {
      setOpenWorkSubgroupId(workSub);
    }
  }, [initialNavItem]);

  React.useEffect(() => {
    const search = new URLSearchParams(location.search);
    if (search.get('checkout') !== 'success') {
      return;
    }
    const sessionId = String(search.get('session_id') || '').trim();
    const email = String(search.get('email') || getLocalUsername() || user?.email || '').trim().toLowerCase();
    const password = getLocalPassword();
    if (!sessionId || !email || !password || password.length < 8) {
      return;
    }
    const claimKey = `nextcompli.claimPassword.${sessionId}`;
    try {
      if (window.sessionStorage.getItem(claimKey) === '1') {
        return;
      }
      window.sessionStorage.setItem(claimKey, '1');
    } catch {
      /* ignore */
    }
    const company = String(search.get('company') || '').trim();
    const tier = String(search.get('tier') || 'basic').trim();
    void (async () => {
      try {
        await fetch(`${gatewayBaseUrl}/api/v1/billing/claim-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionId,
            email,
            password,
            company_name: company || undefined,
            tier_id: tier || undefined,
          }),
        });
        await login(email, password);
      } catch {
        /* claim is best-effort; invite email remains the fallback */
      }
    })();
  }, [gatewayBaseUrl, location.search, login, user?.email]);

  React.useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'nextcompli.iam.groupScopes') {
        setGroupScopes(loadGroupScopes());
        return;
      }
      if (event.key === 'nextcompli.accessManagement.users') {
        setEmailInboxRefreshNonce((current) => current + 1);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  React.useEffect(() => {
    if (activeNavItem !== 'emailInbox') {
      return;
    }
    const timer = window.setInterval(() => {
      setEmailInboxRefreshNonce((current) => current + 1);
    }, 15000);
    return () => window.clearInterval(timer);
  }, [activeNavItem]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  React.useEffect(() => {
    if (activeNavItem !== 'research') {
      return undefined;
    }
    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      const tabs = document.querySelector('.dashboard-research-tabs');
      if (tabs instanceof HTMLElement) {
        scrollElementWithinConsole(tabs);
      }
      secondFrame = window.requestAnimationFrame(() => {
        const again = document.querySelector('.dashboard-research-tabs');
        if (again instanceof HTMLElement) {
          scrollElementWithinConsole(again);
        }
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
    };
  }, [activeNavItem, activeResearchTab]);

  React.useEffect(() => {
    if (isCrmNavHighlightPath(location.pathname)) {
      setActiveNavItem('crm');
      return;
    }
    if (isInletOperatorPath(location.pathname)) {
      setActiveNavItem('inlet');
      return;
    }
    if (isRazzmatazzOperatorPath(location.pathname)) {
      setActiveNavItem('razzmatazz');
      return;
    }
    if (location.pathname.startsWith('/console/admin/')) {
      setIsAdminAccordionOpen(true);
      if (location.pathname.startsWith('/console/admin/cost-optimization')) {
        setActiveNavItem('costOptimization');
        return;
      }
      setActiveNavItem('admin');
    }
  }, [location.pathname]);

  React.useEffect(() => {
    if (new URLSearchParams(location.search).get('openAdmin') === '1') {
      setActiveNavItem('admin');
      setIsAdminAccordionOpen(true);
    }
  }, [location.search]);

  const handleNavSelection = (itemId: DashboardNavItemId) => {
    window.scrollTo(0, 0);
    setActiveNavItem(itemId);
    setOpenNavGroupId(openGroupForNavSelection(itemId));
    const workSub = openWorkSubgroupForNavSelection(itemId);
    if (workSub) {
      setOpenWorkSubgroupId(workSub);
    }
    const destination = navPathByItem[itemId];
    if (destination) {
      navigate(destination);
    }
    closeMobileNavSheet();
  };

  const renderNavStar = (itemId: DashboardNavItemId, label: string) => {
    const permanent = isPermanentStarredSection(itemId);
    const starred = permanent || starredSectionIds.includes(itemId);
    const actionLabel = permanent
      ? (language === 'es' ? `${label} permanece en Destacados` : `${label} stays in Starred`)
      : starred
        ? (language === 'es' ? `Quitar ${label} de Destacados` : `Remove ${label} from Starred`)
        : (language === 'es' ? `Destacar ${label}` : `Star ${label}`);
    return (
      <button
        type="button"
        className={`dashboard-nav-star${starred ? ' is-starred' : ''}`}
        aria-pressed={starred}
        aria-label={actionLabel}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (permanent) {
            return;
          }
          setStarredSectionIds((current) => toggleStarredSectionId(current, itemId));
        }}
      >
        <NavStarIcon filled={starred} />
      </button>
    );
  };

  const renderConsoleNavItem = (item: typeof sideNavItems[number], options?: { shortcut?: boolean }) => (
              <li key={`${options?.shortcut ? 'starred' : 'nav'}-${item.id}`} className="dashboard-nav-group">
                <div className={`dashboard-nav-row ${(item.id === 'ofacDashboards' ? isOfacNavActive : activeNavItem === item.id) ? 'active' : ''}`}>
                  {item.id === 'admin' && hasSuperUserAccess && !options?.shortcut ? (
                    <div className="dashboard-admin-submenu-wrap">
                      <div className="dashboard-admin-row-header">
                        <button
                          type="button"
                          className="dashboard-nav-item"
                          onClick={() => {
                            setActiveNavItem('admin');
                            setIsAdminAccordionOpen(true);
                            setOpenNavGroupId('work');
                            setOpenWorkSubgroupId('operate');
                            navigate('/console/admin/groups', { state: nextAdminResetState() });
                            closeMobileNavSheet();
                          }}
                          aria-expanded={isAdminAccordionOpen}
                        >
                          <span className={`dashboard-nav-icon ${item.tone}`} aria-hidden="true"><NavIcon name={item.icon} /></span>
                          <span>{item.label}</span>
                        </button>
                        {renderNavStar(item.id, item.label)}
                      </div>
                      {hasSuperUserAccess ? (
                        <div className="dashboard-admin-submenu-list">
                          {adminPrivilegeItems.map((entry) => (
                            (entry as { external?: boolean }).external ? (
                              <a
                                key={entry.key}
                                href={entry.path}
                                target="_blank"
                                rel="noreferrer"
                                className="dashboard-admin-submenu-btn"
                                style={{ textDecoration: 'none' }}
                              >
                                <span className="dashboard-admin-submenu-icon" aria-hidden="true">
                                  <AdminSubmenuIcon name={entry.key} />
                                </span>
                                {entry.label}
                              </a>
                            ) : (
                              <button
                                key={entry.key}
                                type="button"
                                className="dashboard-admin-submenu-btn"
                                onClick={() => {
                                  setActiveNavItem('admin');
                                  navigate(entry.path, { state: nextAdminResetState() });
                                  closeMobileNavSheet();
                                }}
                              >
                                <span className="dashboard-admin-submenu-icon" aria-hidden="true">
                                  <AdminSubmenuIcon name={entry.key} />
                                </span>
                                {entry.label}
                              </button>
                            )
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="dashboard-nav-item"
                      onClick={() => handleNavSelection(item.id)}
                    >
                      <span className={`dashboard-nav-icon ${item.tone}`} aria-hidden="true"><NavIcon name={item.icon} /></span>
                      <span>{item.label}</span>
                    </button>
                  )}
                  {item.id === 'admin' && hasSuperUserAccess && !options?.shortcut ? null : renderNavStar(item.id, item.label)}
                </div>
              </li>
  );

  const renderConsoleNavList = (listId: string) => (
          <ul id={listId} className="dashboard-side-nav-list">
            {(() => {
              const starredIds = new Set(starredNavItems.map((item) => item.id));
              const pinnedRest = pinnedNavItems.filter((item) => !starredIds.has(item.id));
              return (
                <>
            {homeNavVisible && homeNavItem && !starredIds.has(homeNavItem.id) ? renderConsoleNavItem(homeNavItem) : null}
            {starredNavItems.length > 0 ? (
              <li className="dashboard-nav-section is-open">
                <p className="dashboard-nav-section-label">{copy.navGroupStarred}</p>
                <ul className="dashboard-nav-section-list">
                  {starredNavItems.map((item) => renderConsoleNavItem(item, { shortcut: true }))}
                </ul>
              </li>
            ) : null}
            {pinnedRest.map((item) => renderConsoleNavItem(item))}
            {visibleNavGroups.map((group) => {
              const remaining = group.items.filter((item) => !starredIds.has(item.id));
              if (remaining.length === 0) {
                return null;
              }
              const expanded = shouldExpandConsoleNavGroup(group.id, activeNavItem, navQuery, openNavGroupId);
              const workSubgroups = group.id === 'work' && group.subgroups
                ? group.subgroups
                  .map((subgroup) => ({
                    ...subgroup,
                    items: subgroup.items.filter((item) => !starredIds.has(item.id)),
                  }))
                  .filter((subgroup) => subgroup.items.length > 0)
                : null;
              return (
                <li key={group.id} className={`dashboard-nav-section${expanded ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className="dashboard-nav-section-toggle"
                    aria-expanded={expanded}
                    onClick={() => {
                      const nextOpen = !(group.id === openNavGroupId && !navQuery.trim());
                      setOpenNavGroupId(nextOpen ? group.id : null);
                      if (nextOpen && group.id === 'work' && !openWorkSubgroupId) {
                        // Prefer the subgroup for the active page. Never default to
                        // Form & protect just because it is first in the list.
                        const matched = openWorkSubgroupForNavSelection(activeNavItem);
                        if (matched) {
                          setOpenWorkSubgroupId(matched);
                        }
                      }
                      if (group.id === 'subscriptions' && nextOpen) {
                        setActiveNavItem('subscriptionHub');
                        navigate('/console/subscription-hub');
                        closeMobileNavSheet();
                      }
                    }}
                  >
                    <span>{group.label}</span>
                    <span className="dashboard-nav-section-meta">
                      <span className={`dashboard-nav-section-chevron${expanded ? ' is-open' : ''}`} aria-hidden="true" />
                    </span>
                  </button>
                  {expanded ? (
                    workSubgroups ? (
                      <ul className="dashboard-nav-section-list dashboard-nav-work-subgroups">
                        {workSubgroups.map((subgroup) => {
                          const subExpanded = shouldExpandWorkSubgroup(
                            subgroup.id,
                            activeNavItem,
                            navQuery,
                            openWorkSubgroupId,
                          );
                          return (
                            <li
                              key={subgroup.id}
                              className={`dashboard-nav-subgroup${subExpanded ? ' is-open' : ''}`}
                            >
                              <button
                                type="button"
                                className="dashboard-nav-subgroup-toggle"
                                aria-expanded={subExpanded}
                                onClick={() => {
                                  setOpenWorkSubgroupId((current) => (
                                    current === subgroup.id && !navQuery.trim() ? null : subgroup.id
                                  ));
                                }}
                              >
                                <span>{subgroup.label}</span>
                                <span className="dashboard-nav-section-meta">
                                  <span className="dashboard-nav-subgroup-count" aria-hidden="true">
                                    {subgroup.items.length}
                                  </span>
                                  <span
                                    className={`dashboard-nav-section-chevron${subExpanded ? ' is-open' : ''}`}
                                    aria-hidden="true"
                                  />
                                </span>
                              </button>
                              {subExpanded ? (
                                <ul className="dashboard-nav-subgroup-list">
                                  {subgroup.items.map((item) => renderConsoleNavItem(item))}
                                </ul>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <ul className="dashboard-nav-section-list">
                        {remaining.map((item) => renderConsoleNavItem(item))}
                      </ul>
                    )
                  ) : null}
                </li>
              );
            })}
                </>
              );
            })()}
            {visibleNavGroups.length === 0 && !homeNavVisible && pinnedNavItems.length === 0 && starredNavItems.length === 0 ? (
              <li className="dashboard-nav-empty">{copy.navNoResults}</li>
            ) : null}
          </ul>
  );

  const handleTipPrimaryAction = () => {
    if (!todaysTip) {
      return;
    }

    const nextState: TipOfDayState = {
      ...tipOfDayState,
      dismissedUntilDate: todayDateKey,
      lastTipId: todaysTip.id,
      lastShownDate: todayDateKey,
    };
    setTipOfDayState(nextState);
    saveTipOfDayState(nextState);

    handleNavSelection(todaysTip.action.navItem);
  };

  const handleTipDismiss = () => {
    const nextState: TipOfDayState = {
      ...tipOfDayState,
      dismissedUntilDate: addDaysToDateKey(todayDateKey, 1),
    };
    setTipOfDayState(nextState);
    saveTipOfDayState(nextState);
  };

  const handleTipSnooze = () => {
    const nextState: TipOfDayState = {
      ...tipOfDayState,
      dismissedUntilDate: addDaysToDateKey(todayDateKey, 3),
    };
    setTipOfDayState(nextState);
    saveTipOfDayState(nextState);
  };

  const resetSubscriptionForm = () => {
    setSubscriptionForm({
      name: 'CompliCore (Screen)',
      monthlyPrice: '',
      status: 'trialing',
      renewalDate: '',
      ownerEmail: '',
    });
    setEditingSubscriptionId(null);
  };

  const handleSubscriptionSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubscriptionFeedback(null);
    setSubscriptionError(null);

    if (!subscriptionForm.name.trim() || !subscriptionForm.ownerEmail.trim()) {
      setSubscriptionError(language === 'es' ? 'Nombre y correo son obligatorios.' : 'Name and owner email are required.');
      return;
    }

    if (editingSubscriptionId) {
      setSubscriptionCatalog((current) => current.map((record) => (
        record.id === editingSubscriptionId ? { ...record, ...subscriptionForm } : record
      )));
      setSubscriptionFeedback(language === 'es' ? 'Suscripción actualizada.' : 'Subscription updated.');
      resetSubscriptionForm();
      return;
    }

    const nextId = `sub-${Date.now()}`;
    setSubscriptionCatalog((current) => [
      {
        id: nextId,
        ...subscriptionForm,
      },
      ...current,
    ]);
    setSubscriptionFeedback(language === 'es' ? 'Suscripción creada.' : 'Subscription created.');
    resetSubscriptionForm();
  };

  const handleSubscriptionEdit = (subscriptionId: string) => {
    const selected = subscriptionCatalog.find((record) => record.id === subscriptionId);
    if (!selected) {
      return;
    }
    setEditingSubscriptionId(selected.id);
    setSubscriptionForm({
      name: selected.name,
      monthlyPrice: selected.monthlyPrice,
      status: selected.status,
      renewalDate: selected.renewalDate,
      ownerEmail: selected.ownerEmail,
    });
    setSubscriptionFeedback(null);
    setSubscriptionError(null);
  };

  const handleSubscriptionDelete = (subscriptionId: string) => {
    setSubscriptionCatalog((current) => current.filter((record) => record.id !== subscriptionId));
    if (editingSubscriptionId === subscriptionId) {
      resetSubscriptionForm();
    }
    setSubscriptionFeedback(language === 'es' ? 'Suscripción eliminada.' : 'Subscription deleted.');
    setSubscriptionError(null);
  };

  const getSubscriptionStatusLabel = (status: SubscriptionAdminRecord['status']): string => {
    if (status === 'trialing') {
      return language === 'es' ? 'Periodo de prueba' : 'Trial period';
    }
    if (status === 'past_due') {
      return language === 'es' ? 'Pago vencido' : 'Past due';
    }
    if (status === 'canceled') {
      return language === 'es' ? 'Cancelada' : 'Canceled';
    }
    return language === 'es' ? 'Activa' : 'Active';
  };

  const vantageImpactSubscriptions = React.useMemo(
    () => [
      {
        rank: 1,
        name: 'LSEG World-Check',
        why: language === 'es'
          ? 'Cobertura PEP/RCA crítica para decisiones de onboarding de alto riesgo.'
          : 'Critical PEP/RCA coverage for high-risk onboarding decisions.',
      },
      {
        rank: 2,
        name: 'ComplyAdvantage',
        why: language === 'es'
          ? 'Señales AML/KYB de alto impacto para screening operativo continuo.'
          : 'High-impact AML/KYB signals for continuous operational screening.',
      },
      {
        rank: 3,
        name: 'PACER',
        why: language === 'es'
          ? 'Visibilidad de litigios federales para cerrar brechas de riesgo legal.'
          : 'Federal litigation visibility to close legal risk blind spots.',
      },
      {
        rank: 4,
        name: 'Dun & Bradstreet',
        why: language === 'es'
          ? 'Cobertura amplia para riesgo de proveedores y crédito comercial.'
          : 'Broad coverage for supplier and commercial credit risk.',
      },
      {
        rank: 5,
        name: 'OpenSanctions (enhanced support)',
        why: language === 'es'
          ? 'Enriquecimiento abierto de alto valor para ampliar contexto de sanciones/PEP.'
          : 'High-value open enrichment to broaden sanctions/PEP context.',
      },
      {
        rank: 6,
        name: 'NewsAPI',
        why: language === 'es'
          ? 'Señales de adverse media a escala para monitoreo continuo.'
          : 'Scaled adverse media signals for ongoing monitoring.',
      },
    ],
    [language],
  );

  const resetTipForm = () => {
    setTipForm({
      titleEn: '',
      titleEs: '',
      bodyEn: '',
      bodyEs: '',
      navItem: 'dashboards',
      actionLabelEn: 'Open Dashboards',
      actionLabelEs: 'Abrir Paneles',
    });
    setEditingTipId(null);
  };

  const handleTipEdit = (tipId: string) => {
    const selectedTip = tipCatalog.find((tip) => tip.id === tipId);
    if (!selectedTip) {
      return;
    }
    setEditingTipId(selectedTip.id);
    setTipForm({
      titleEn: selectedTip.title.en,
      titleEs: selectedTip.title.es,
      bodyEn: selectedTip.body.en,
      bodyEs: selectedTip.body.es,
      navItem: selectedTip.action.navItem,
      actionLabelEn: selectedTip.action.label.en,
      actionLabelEs: selectedTip.action.label.es,
    });
    setTipError(null);
    setTipFeedback(null);
  };

  const handleTipDelete = (tipId: string) => {
    setTipCatalog((current) => current.filter((tip) => tip.id !== tipId));
    if (editingTipId === tipId) {
      resetTipForm();
    }
    setTipFeedback(language === 'es' ? 'Consejo eliminado.' : 'Tip removed.');
    setTipError(null);
  };

  const handleTipSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTipError(null);
    setTipFeedback(null);

    if (!tipForm.titleEn.trim() || !tipForm.titleEs.trim() || !tipForm.bodyEn.trim() || !tipForm.bodyEs.trim()) {
      setTipError(language === 'es' ? 'Completa todos los campos del consejo.' : 'Complete all tip fields.');
      return;
    }

    const nextTip: TipOfDayDefinition = {
      id: editingTipId ?? `tip-custom-${Date.now()}`,
      audience: 'non_super_admin',
      title: {
        en: tipForm.titleEn.trim(),
        es: tipForm.titleEs.trim(),
      },
      body: {
        en: tipForm.bodyEn.trim(),
        es: tipForm.bodyEs.trim(),
      },
      action: {
        navItem: tipForm.navItem,
        label: {
          en: tipForm.actionLabelEn.trim() || 'Open section',
          es: tipForm.actionLabelEs.trim() || 'Abrir sección',
        },
      },
    };

    setTipCatalog((current) => {
      if (editingTipId) {
        return current.map((tip) => (tip.id === editingTipId ? nextTip : tip));
      }
      return [nextTip, ...current];
    });
    setTipFeedback(language === 'es' ? 'Consejo guardado.' : 'Tip saved.');
    resetTipForm();
  };

  const personalSubscriptions = React.useMemo(
    () => subscriptionCatalog.filter((subscription) => subscription.ownerEmail.toLowerCase() === accountEmail.toLowerCase()),
    [accountEmail, subscriptionCatalog],
  );

  const resetPersonalSubscriptionForm = () => {
    setPersonalSubscriptionForm({
      name: '',
      monthlyPrice: '',
      status: 'trialing',
      renewalDate: '',
    });
    setEditingPersonalSubscriptionId(null);
  };

  const handlePersonalSubscriptionSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPersonalSubscriptionFeedback(null);
    setPersonalSubscriptionError(null);

    if (!personalSubscriptionForm.name.trim()) {
      setPersonalSubscriptionError(language === 'es' ? 'El nombre de la suscripción es obligatorio.' : 'Subscription name is required.');
      return;
    }

    if (editingPersonalSubscriptionId) {
      const hasEditableRecord = subscriptionCatalog.some(
        (subscription) => subscription.id === editingPersonalSubscriptionId
          && subscription.ownerEmail.toLowerCase() === accountEmail.toLowerCase(),
      );
      if (!hasEditableRecord) {
        setPersonalSubscriptionError(language === 'es' ? 'No se pudo actualizar esta suscripción.' : 'Unable to update this subscription.');
        return;
      }

      setSubscriptionCatalog((current) => current.map((record) => (
        record.id === editingPersonalSubscriptionId
          ? { ...record, ...personalSubscriptionForm, ownerEmail: accountEmail }
          : record
      )));
      setPersonalSubscriptionFeedback(language === 'es' ? 'Suscripción actualizada.' : 'Subscription updated.');
      resetPersonalSubscriptionForm();
      return;
    }

    const nextId = `sub-self-${Date.now()}`;
    setSubscriptionCatalog((current) => [
      {
        id: nextId,
        ownerEmail: accountEmail,
        ...personalSubscriptionForm,
      },
      ...current,
    ]);
    setPersonalSubscriptionFeedback(language === 'es' ? 'Suscripción creada.' : 'Subscription created.');
    resetPersonalSubscriptionForm();
  };

  const handlePersonalSubscriptionEdit = (subscriptionId: string) => {
    const selected = subscriptionCatalog.find(
      (record) => record.id === subscriptionId && record.ownerEmail.toLowerCase() === accountEmail.toLowerCase(),
    );
    if (!selected) {
      return;
    }

    setEditingPersonalSubscriptionId(selected.id);
    setPersonalSubscriptionForm({
      name: selected.name,
      monthlyPrice: selected.monthlyPrice,
      status: selected.status,
      renewalDate: selected.renewalDate,
    });
    setPersonalSubscriptionFeedback(null);
    setPersonalSubscriptionError(null);
  };

  const handlePersonalSubscriptionDelete = (subscriptionId: string) => {
    setSubscriptionCatalog((current) => current.filter(
      (record) => !(record.id === subscriptionId && record.ownerEmail.toLowerCase() === accountEmail.toLowerCase()),
    ));
    if (editingPersonalSubscriptionId === subscriptionId) {
      resetPersonalSubscriptionForm();
    }
    setPersonalSubscriptionFeedback(language === 'es' ? 'Suscripción eliminada.' : 'Subscription deleted.');
    setPersonalSubscriptionError(null);
  };

  React.useEffect(() => {
    saveTipCatalog(tipCatalog);
  }, [tipCatalog]);

  React.useEffect(() => {
    const deduped = dedupeResearchReports(researchReports);
    if (deduped.length !== researchReports.length || deduped.some((report, index) => report.id !== researchReports[index]?.id)) {
      setResearchReports(deduped);
      return;
    }
    const kept = researchReports.filter(
      (report) => !researchReportIsDismissed(report, dismissedResearchReportIdsRef.current),
    );
    saveResearchReports(kept, researchAccessActor.userId);
    // The first paint loads the unscoped catalog key. Keep it aligned so a refresh
    // cannot merge deleted reports back from nextcompli.research_reports.catalog.v1.
    if (researchAccessActor.userId) {
      saveResearchReports(kept);
    }
  }, [researchAccessActor.userId, researchReports]);

  React.useEffect(() => {
    saveResearchQueue(researchQueue);
  }, [researchQueue]);

  const loadComprehensiveCheckHistory = React.useCallback(async () => {
    setFullCheckHistoryLoading(true);
    setFullCheckHistoryError(null);
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/compliance/background-check/full/history?limit=25`, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const body = (await response.json()) as { history?: ComprehensiveBackgroundCheckHistoryRecord[] };
      setFullCheckHistory(Array.isArray(body.history) ? body.history : []);
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setFullCheckHistoryError(
        language === 'es'
          ? `No se pudo cargar el historial de verificaciones completas (${detail}).`
          : `Could not load comprehensive check history (${detail}).`,
      );
    } finally {
      setFullCheckHistoryLoading(false);
    }
  }, [accessToken, gatewayBaseUrl, language]);

  const runComprehensiveCheckMonitoring = React.useCallback(async () => {
    setFullCheckMonitoringMessage(null);
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/compliance/background-check/full/monitoring/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const body = (await response.json()) as { seeded?: number; monitoring?: { ran?: number; blocked?: number; review_required?: number; clear?: number } };
      const seeded = Number(body.seeded || 0);
      const ran = Number(body.monitoring?.ran || 0);
      const blocked = Number(body.monitoring?.blocked || 0);
      const reviewRequired = Number(body.monitoring?.review_required || 0);
      const clear = Number(body.monitoring?.clear || 0);
      setFullCheckMonitoringMessage(
        language === 'es'
          ? `Monitoreo ejecutado. Sembrados: ${seeded}. Ejecutados: ${ran}. Bloqueados: ${blocked}. Revisión: ${reviewRequired}. Limpios: ${clear}.`
          : `Monitoring run complete. Seeded: ${seeded}. Ran: ${ran}. Blocked: ${blocked}. Review required: ${reviewRequired}. Clear: ${clear}.`,
      );
      await loadComprehensiveCheckHistory();
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setFullCheckMonitoringMessage(
        language === 'es'
          ? `No se pudo ejecutar el monitoreo (${detail}).`
          : `Could not run monitoring (${detail}).`,
      );
    }
  }, [gatewayBaseUrl, language, loadComprehensiveCheckHistory]);

  const loadOfacScreeningSubjects = React.useCallback(async () => {
    if (!canAccessOfac) {
      return;
    }
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 15000);
    setOfacScreeningLoading(true);
    setOfacScreeningError(null);
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/ofac/screening-subjects`, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const body = (await response.json()) as { subjects?: OfacScreeningSubject[] };
      setOfacScreeningSubjects(Array.isArray(body.subjects) ? body.subjects : []);
      try {
        const pairsResponse = await fetch(`${gatewayBaseUrl}/api/v1/ofac/false-hit-pairs`, {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        });
        if (pairsResponse.ok) {
          const pairsBody = (await pairsResponse.json()) as { pairs?: OfacFalseHitPair[] };
          setOfacFalseHitPairs(Array.isArray(pairsBody.pairs) ? pairsBody.pairs : []);
        }
      } catch {
        setOfacFalseHitPairs([]);
      }
    } catch (error) {
      const detail = error instanceof Error
        ? (error.name === 'AbortError' ? 'timed out' : error.message)
        : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo cargar la lista de screening OFAC (${detail}).`
          : `Could not load OFAC screening list (${detail}).`,
      );
    } finally {
      window.clearTimeout(timeoutId);
      setOfacScreeningLoading(false);
    }
  }, [accessToken, canAccessOfac, gatewayBaseUrl, language]);

  const persistOfacCompanyId = React.useCallback((nextId: number | null) => {
    setOfacSelectedProject('');
    setOfacSelectedCompanyId(nextId);
    try {
      if (nextId == null) {
        window.localStorage.removeItem(OFAC_ACTIVE_COMPANY_STORAGE_KEY);
      } else {
        window.localStorage.setItem(OFAC_ACTIVE_COMPANY_STORAGE_KEY, String(nextId));
      }
    } catch {
      /* ignore quota / private mode */
    }
  }, []);

  const loadOfacCompanies = React.useCallback(async () => {
    if (!canAccessOfac) {
      return;
    }
    if (!isRealGatewayAccessToken(accessToken)) {
      setOfacCompanies([]);
      setOfacCanManageCompanies(false);
      setOfacScreeningError(ofacGatewaySessionRequiredMessage(language));
      return;
    }
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/ofac/companies`, {
        headers: gatewayAuthorizationHeaders(accessToken),
      });
      if (!response.ok) {
        throw new Error(response.status === 401 ? 'gateway-unauthorized' : `HTTP ${response.status}`);
      }
      const body = (await response.json()) as {
        companies?: Array<{ company_id?: number; name?: string; group_type?: string }>;
        effective_company_id?: number | null;
        can_manage?: boolean;
      };
      const companies = (Array.isArray(body.companies) ? body.companies : [])
        .map((row) => ({
          company_id: Number(row.company_id),
          name: String(row.name || '').trim(),
          group_type: String(row.group_type || 'organization'),
        }))
        .filter((row) => (
          Number.isFinite(row.company_id)
          && row.company_id > 0
          && isOfacCustomerDirectoryCompany(row.name, row.group_type)
        ));
      setOfacCompanies(companies);
      setOfacCanManageCompanies(Boolean(body.can_manage));
      setOfacSelectedCompanyId((current) => {
        const stillExists = current != null && companies.some((row) => row.company_id === current);
        const effectiveId = typeof body.effective_company_id === 'number' ? body.effective_company_id : null;
        const nextId = stillExists
          ? current
          : (effectiveId != null && companies.some((row) => row.company_id === effectiveId)
            ? effectiveId
            : null);
        try {
          if (nextId == null) {
            window.localStorage.removeItem(OFAC_ACTIVE_COMPANY_STORAGE_KEY);
          } else {
            window.localStorage.setItem(OFAC_ACTIVE_COMPANY_STORAGE_KEY, String(nextId));
          }
        } catch {
          /* ignore */
        }
        return nextId;
      });
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacCompanies([]);
      setOfacScreeningError(
        detail === 'gateway-unauthorized'
          ? ofacGatewaySessionRequiredMessage(language)
          : (language === 'es'
            ? `No se pudieron cargar las empresas (${detail}).`
            : `Could not load companies (${detail}).`),
      );
    }
  }, [accessToken, canAccessOfac, gatewayBaseUrl, language]);

  const loadOfacNamedWhitelists = React.useCallback(async () => {
    if (!canAccessOfac) {
      return;
    }
    if (!isRealGatewayAccessToken(accessToken)) {
      setOfacNamedWhitelistsLoad('error');
      setOfacWhitelistCreateOpen(false);
      setOfacScreeningError(ofacGatewaySessionRequiredMessage(language));
      return;
    }
    if (
      ofacSelectedCompanyId == null
      || !ofacCompanies.some((row) => row.company_id === ofacSelectedCompanyId)
    ) {
      setOfacNamedWhitelists([]);
      setOfacWhitelistCompany({ id: null, name: null });
      setOfacNamedWhitelistDefaultId(null);
      setOfacActiveWhitelistId(null);
      setOfacNamedWhitelistsLoad('ready');
      setOfacWhitelistCreateOpen(false);
      return;
    }
    setOfacNamedWhitelistsLoad((current) => (current === 'ready' ? 'ready' : 'loading'));
    try {
      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/ofac/whitelists?company_id=${encodeURIComponent(String(ofacSelectedCompanyId))}`,
        {
          headers: gatewayAuthorizationHeaders(accessToken),
        },
      );
      if (!response.ok) {
        throw new Error(response.status === 401 ? 'gateway-unauthorized' : `HTTP ${response.status}`);
      }
      const body = (await response.json()) as {
        lists?: OfacNamedWhitelist[];
        default_id?: number | null;
        company_id?: number | null;
        company_name?: string | null;
      };
      const lists = Array.isArray(body.lists) ? body.lists : [];
      setOfacNamedWhitelists(lists);
      setOfacWhitelistCompany({
        id: typeof body.company_id === 'number' ? body.company_id : ofacSelectedCompanyId,
        name: String(body.company_name || '').trim() || null,
      });
      const defaultId = typeof body.default_id === 'number' ? body.default_id : (lists[0]?.id ?? null);
      setOfacNamedWhitelistDefaultId(defaultId);
      setOfacActiveWhitelistId((current) => {
        const stillExists = current != null && lists.some((row) => row.id === current);
        const nextId = stillExists ? current : defaultId;
        if (nextId != null) {
          try {
            window.localStorage.setItem(OFAC_ACTIVE_WHITELIST_STORAGE_KEY, String(nextId));
          } catch {
            /* ignore quota / private mode */
          }
        }
        return nextId;
      });
      setOfacNamedWhitelistsLoad('ready');
      if (!lists.length) {
        setOfacWhitelistCreateOpen(true);
      }
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacNamedWhitelistsLoad('error');
      setOfacWhitelistCreateOpen(false);
      setOfacScreeningError(
        detail === 'gateway-unauthorized'
          ? ofacGatewaySessionRequiredMessage(language)
          : (language === 'es'
            ? `No se pudieron cargar las listas blancas (${detail}).`
            : `Could not load named whitelists (${detail}).`),
      );
    }
  }, [accessToken, canAccessOfac, gatewayBaseUrl, language, ofacCompanies, ofacSelectedCompanyId]);

  React.useEffect(() => {
    if (canAccessOfac && selectedResearchReport && ofacScreeningSubjects.length === 0) {
      void loadOfacScreeningSubjects();
    }
  }, [canAccessOfac, loadOfacScreeningSubjects, ofacScreeningSubjects.length, selectedResearchReport]);

  const loadOfacComplianceDashboard = React.useCallback(async () => {
    if (!canAccessOfac) {
      setOfacComplianceLoading(false);
      return;
    }
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 15000);
    setOfacComplianceLoading(true);
    setOfacComplianceError(null);
    try {
      const params = new URLSearchParams({ language });
      if (ofacSelectedCompanyId != null) {
        params.set('company_id', String(ofacSelectedCompanyId));
      }
      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/ofac/compliance-dashboard?${params.toString()}`,
        {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
          signal: controller.signal,
        },
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const body = (await response.json()) as OfacComplianceDashboardPayload;
      setOfacComplianceDashboard(body);
    } catch (error) {
      const detail = error instanceof Error
        ? (error.name === 'AbortError' ? 'timed out' : error.message)
        : 'request-failed';
      setOfacComplianceDashboard(null);
      setOfacComplianceError(
        language === 'es'
          ? `No se pudo cargar el panel de cumplimiento OFAC (${detail}).`
          : `Could not load OFAC compliance dashboard (${detail}).`,
      );
    } finally {
      window.clearTimeout(timeoutId);
      setOfacComplianceLoading(false);
    }
  }, [accessToken, canAccessOfac, gatewayBaseUrl, language, ofacSelectedCompanyId]);

  const upsertOfacScreeningSubject = React.useCallback(async (payload: {
    entityName: string;
    relationshipType: OfacScreeningRelationshipType;
    entityType?: string;
    identifiers?: Record<string, unknown>;
    referenceId?: string;
    source?: 'manual' | 'research' | 'vantage';
    screenNow?: boolean;
    namedWhitelistId?: number | null;
    companyId?: number | null;
  }) => {
    const response = await fetch(`${gatewayBaseUrl}/api/v1/ofac/screening-subjects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({
        entity_name: payload.entityName,
        relationship_type: payload.relationshipType,
        entity_type: payload.entityType,
        identifiers: payload.identifiers || {},
        reference_id: payload.referenceId,
        source: payload.source || 'manual',
        screen_now: Boolean(payload.screenNow),
        named_whitelist_id: payload.namedWhitelistId ?? undefined,
        company_id: payload.companyId ?? undefined,
      }),
    });
    if (!response.ok) {
      const detail = await response.text().catch(() => '');      throw new Error(detail || `HTTP ${response.status}`);
    }
    return response.json() as Promise<{ subject?: OfacScreeningSubject }>;
  }, [accessToken, gatewayBaseUrl]);

  const handleResearchFactsTransfer = React.useCallback(async () => {
    if (!selectedResearchReport?.factDatabase || !canAccessOfac) {
      return;
    }
    const reportId = String(selectedResearchReport.id || '').trim();
    const targetName = String(
      selectedResearchReport.targetName
      || selectedResearchReport.factDatabase.entity?.canonical_name
      || '',
    ).trim();
    if (!targetName) {
      setResearchError(
        language === 'es'
          ? 'Se necesita un nombre de entidad para transferir a NextCompli.'
          : 'An entity name is required to transfer into NextCompli.',
      );
      return;
    }

    const signalIndices = Array.from(researchTransferSignalIndexes).sort((a, b) => a - b);
    const includeEntity = researchTransferIncludeEntity && researchTransferEntityFields.size > 0;
    if (signalIndices.length === 0 && !includeEntity) {
      setResearchError(
        language === 'es'
          ? 'Selecciona al menos una señal o un campo de entidad para transferir.'
          : 'Select at least one signal or entity field to transfer.',
      );
      return;
    }

    const signals = selectedResearchReport.factDatabase.signals || [];
    const includeExcluded = signalIndices.some((index) => {
      const signal = signals[index];
      return Boolean(signal && isFactSignalExcluded(signal));
    });

    setResearchTransferBusy(true);
    setResearchError(null);
    setResearchFeedback(null);
    try {
      let subjectId = researchTransferSubjectId === '' ? null : Number(researchTransferSubjectId);
      if (subjectId == null || !Number.isFinite(subjectId)) {
        const created = await upsertOfacScreeningSubject({
          entityName: targetName,
          relationshipType: 'customer',
          referenceId: reportId || undefined,
          source: 'research',
          identifiers: reportId
            ? { research_report_ids: [reportId], last_reference_id: reportId }
            : {},
          screenNow: false,
        });
        subjectId = created.subject?.id ?? null;
        await loadOfacScreeningSubjects();
        if (subjectId != null) {
          setResearchTransferSubjectId(subjectId);
        }
      }
      if (subjectId == null || !Number.isFinite(subjectId)) {
        throw new Error(
          language === 'es'
            ? 'No se pudo resolver el sujeto OFAC.'
            : 'Could not resolve OFAC screening subject.',
        );
      }

      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/ofac/screening-subjects/${subjectId}/facts/transfer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({
            fact_database: selectedResearchReport.factDatabase,
            signal_indices: signalIndices,
            include_entity: includeEntity,
            include_entity_fields: includeEntity ? Array.from(researchTransferEntityFields) : [],
            include_excluded_signals: includeExcluded,
            research_report_id: reportId || undefined,
            note: `Selective transfer from Vantage report ${reportId || selectedResearchReport.reportName || ''}`.trim(),
          }),
        },
      );
      if (!response.ok) {
        const detail = await response.text().catch(() => '');
        throw new Error(detail || `HTTP ${response.status}`);
      }
      const body = (await response.json()) as {
        transferred_signal_count?: number;
        include_entity?: boolean;
      };
      await loadOfacScreeningSubjects();
      const count = Number(body.transferred_signal_count || 0);
      setResearchFeedback(
        language === 'es'
          ? `Transferidos ${count} señal(es)${body.include_entity ? ' + campos de entidad' : ''} a la base de hechos NextCompli.`
          : `Transferred ${count} signal(s)${body.include_entity ? ' + entity fields' : ''} into the NextCompli fact database.`,
      );
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setResearchError(
        language === 'es'
          ? `No se pudo transferir a NextCompli (${detail}).`
          : `Could not transfer into NextCompli (${detail}).`,
      );
    } finally {
      setResearchTransferBusy(false);
    }
  }, [
    accessToken,
    canAccessOfac,
    gatewayBaseUrl,
    language,
    loadOfacScreeningSubjects,
    researchTransferEntityFields,
    researchTransferIncludeEntity,
    researchTransferSignalIndexes,
    researchTransferSubjectId,
    selectedResearchReport,
    upsertOfacScreeningSubject,
  ]);

  const buildOfacIdentifiers = React.useCallback((input: {
    relationshipType: string;
    identifier?: string;
    birthdate?: string;
    country?: string;
    hireLocation?: string;
    notes?: string;
    owner?: string;
    recertifyDays?: number;
  }) => {
    const identifiers: Record<string, string | number> = {};
    const identifier = String(input.identifier || '').trim();
    if (identifier) {
      const key = input.relationshipType === 'vessel'
        ? 'imo'
        : input.relationshipType === 'aircraft'
          ? 'tail_number'
          : 'id';
      identifiers[key] = identifier;
    }
    if (input.birthdate?.trim()) {
      identifiers.birthdate = input.birthdate.trim();
    }
    if (input.country?.trim()) {
      identifiers.country = input.country.trim();
    }
    if (input.hireLocation?.trim()) {
      identifiers.hire_location = input.hireLocation.trim();
    }
    if (input.notes?.trim()) {
      identifiers.notes = input.notes.trim();
    }
    if (input.owner?.trim()) {
      identifiers.whitelist_owner = input.owner.trim();
    }
    if (input.recertifyDays != null) {
      identifiers.hire_recertify_days = normalizeOfacReviewCycleDays(input.recertifyDays);
    }
    return identifiers;
  }, []);

  const handleOfacScreeningAdd = React.useCallback(async () => {
    const name = ofacScreeningForm.entityName.trim();
    if (!name) {
      setOfacScreeningError(language === 'es' ? 'El nombre es obligatorio.' : 'Name is required.');
      return;
    }
    setOfacScreeningError(null);
    setOfacScreeningMessage(null);
    try {
      await upsertOfacScreeningSubject({
        entityName: name,
        relationshipType: ofacScreeningForm.relationshipType,
        entityType: ofacScreeningForm.entityType,
        identifiers: buildOfacIdentifiers(ofacScreeningForm),
        source: 'manual',
        screenNow: ofacScreeningForm.screenNow,
      });
      setOfacScreeningForm((current) => ({
        ...current,
        entityName: '',
        identifier: '',
        birthdate: '',
        notes: '',
      }));
      setOfacScreeningMessage(language === 'es' ? 'Sujeto añadido a la lista de screening.' : 'Subject added to the screening list.');
      await loadOfacScreeningSubjects();
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo añadir el sujeto (${detail}).`
          : `Could not add subject (${detail}).`,
      );
    }
  }, [buildOfacIdentifiers, language, loadOfacScreeningSubjects, ofacScreeningForm, upsertOfacScreeningSubject]);

  const handleOfacWhitelistAdd = React.useCallback(async () => {
    const syncedForm = syncOfacIntakeLegalName({
      ...ofacWhitelistForm,
      placeOfBirth: composeOfacIntakePlace(ofacWhitelistForm) || ofacWhitelistForm.placeOfBirth,
    });
    const name = syncedForm.entityName.trim();
    if (!name) {
      setOfacScreeningError(language === 'es' ? 'El nombre es obligatorio.' : 'Name is required.');
      return;
    }
    if (!ofacSelectedCompanyId) {
      setOfacScreeningError(ofacCompanyRequiredMessage(language));
      return;
    }
    if (!ofacActiveWhitelistId) {
      setOfacScreeningError(
        language === 'es'
          ? 'Crea o elige una lista blanca de esta empresa primero.'
          : 'Create or select a whitelist for this company first.',
      );
      return;
    }
    const owner = ofacWhitelistForm.owner.trim();
    if (owner.length < 2) {
      setOfacScreeningError(ofacOwnerRequiredMessage(language));
      return;
    }
    setOfacScreeningError(null);
    setOfacScreeningMessage(null);
    setOfacWhitelistSaving('primary');
    try {
      await upsertOfacScreeningSubject({
        entityName: name,
        relationshipType: ofacWhitelistForm.relationshipType,
        entityType: ofacEntityTypeForRelationship(ofacWhitelistForm.relationshipType),
        identifiers: buildOfacWhitelistIdentifiers({
          ...syncedForm,
          owner,
        }),
        source: 'manual',
        screenNow: true,
        namedWhitelistId: ofacActiveWhitelistId,
        companyId: ofacSelectedCompanyId,
      });
      setOfacWhitelistForm((current) => ({
        ...emptyOfacWhitelistForm(current.relationshipType),
        relationshipType: current.relationshipType,
        recertifyDays: current.recertifyDays,
        owner: current.owner,
      }));
      setOfacScreeningMessage(
        language === 'es'
          ? 'Persona guardada en la lista. SDN se ejecutó ahora. La última validación se sella tras la firma en EE.UU. (contratación) o al revalidar SDN (cliente/proveedor/UBO).'
          : 'Person saved to the list. SDN ran now. Last validated stamps after U.S. hire sign-off, or after SDN re-validate for customers, vendors, and UBOs.',
      );
      await loadOfacScreeningSubjects();
      await loadOfacNamedWhitelists();
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo guardar a la persona (${detail}).`
          : `Could not save this person (${detail}).`,
      );
    } finally {
      setOfacWhitelistSaving(null);
    }
  }, [language, loadOfacNamedWhitelists, loadOfacScreeningSubjects, ofacActiveWhitelistId, ofacSelectedCompanyId, ofacWhitelistForm, upsertOfacScreeningSubject]);

  const handleOfacWhitelistSaveFamilyMember = React.useCallback(async (index: number) => {
    const row = ofacWhitelistForm.familyMembers[index];
    const name = String(row?.name || '').trim();
    if (!name) {
      setOfacScreeningError(
        language === 'es'
          ? 'Escribe el nombre legal de esta persona antes de guardar.'
          : 'Type this person’s legal name before saving.',
      );
      return;
    }
    if (!ofacSelectedCompanyId) {
      setOfacScreeningError(ofacCompanyRequiredMessage(language));
      return;
    }
    if (!ofacActiveWhitelistId) {
      setOfacScreeningError(
        language === 'es'
          ? 'Crea o elige una lista blanca de esta empresa primero.'
          : 'Create or select a whitelist for this company first.',
      );
      return;
    }
    const owner = ofacWhitelistForm.owner.trim();
    if (owner.length < 2) {
      setOfacScreeningError(ofacOwnerRequiredMessage(language));
      return;
    }
    const relatedTo = composeOfacIntakeLegalName(ofacWhitelistForm) || ofacWhitelistForm.entityName.trim();
    setOfacScreeningError(null);
    setOfacScreeningMessage(null);
    setOfacWhitelistSaving(`family-${index}`);
    try {
      await upsertOfacScreeningSubject({
        entityName: name,
        relationshipType: 'associate',
        entityType: 'person',
        identifiers: buildOfacFamilyMemberIdentifiers({
          relation: row.relation || 'other',
          relatedTo,
          owner,
          recertifyDays: ofacWhitelistForm.recertifyDays,
        }),
        source: 'manual',
        screenNow: true,
        namedWhitelistId: ofacActiveWhitelistId,
        companyId: ofacSelectedCompanyId,
      });
      setOfacWhitelistForm((current) => ({
        ...current,
        familyMembers: removeOfacIntakeRow(current.familyMembers, index, emptyOfacIntakeFamilyMember()),
      }));
      setOfacScreeningMessage(
        language === 'es'
          ? `${name} se guardó en la lista como familiar/asociado. SDN se ejecutó ahora.`
          : `${name} was saved to the list as a family member / associate. SDN ran now.`,
      );
      await loadOfacScreeningSubjects();
      await loadOfacNamedWhitelists();
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo guardar a ${name} (${detail}).`
          : `Could not save ${name} (${detail}).`,
      );
    } finally {
      setOfacWhitelistSaving(null);
    }
  }, [language, loadOfacNamedWhitelists, loadOfacScreeningSubjects, ofacActiveWhitelistId, ofacSelectedCompanyId, ofacWhitelistForm, upsertOfacScreeningSubject]);

  const handleOfacCreateWhitelist = React.useCallback(async () => {
    const name = ofacNewWhitelistName.trim();
    if (!name) {
      const message = ofacCreateListNameRequiredMessage(language, firstOfacHireLocation(ofacWhitelistForm));
      setOfacNewWhitelistNameError(message);
      setOfacScreeningError(message);
      ofacNewWhitelistNameRef.current?.focus();
      return;
    }
    if (!ofacSelectedCompanyId) {
      const message = ofacCompanyRequiredMessage(language);
      setOfacNewWhitelistNameError(message);
      setOfacScreeningError(message);
      return;
    }
    if (!isRealGatewayAccessToken(accessToken)) {
      const message = ofacGatewaySessionRequiredMessage(language);
      setOfacNewWhitelistNameError(null);
      setOfacScreeningError(message);
      return;
    }
    setOfacWhitelistCreating(true);
    setOfacNewWhitelistNameError(null);
    setOfacScreeningError(null);
    setOfacScreeningMessage(null);
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/ofac/whitelists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...gatewayAuthorizationHeaders(accessToken),
        },
        body: JSON.stringify({ name, purpose: 'hire', company_id: ofacSelectedCompanyId }),
      });
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(ofacGatewaySessionRequiredMessage(language));
        }
        const detail = await response.text().catch(() => '');
        throw new Error(detail || `HTTP ${response.status}`);
      }
      const body = (await response.json()) as { list?: OfacNamedWhitelist };
      const created = body.list;
      setOfacNewWhitelistName('');
      setOfacWhitelistCreateOpen(false);
      await loadOfacNamedWhitelists();
      if (created?.id) {
        setOfacActiveWhitelistId(created.id);
        try {
          window.localStorage.setItem(OFAC_ACTIVE_WHITELIST_STORAGE_KEY, String(created.id));
        } catch {
          /* ignore */
        }
      }
      setOfacScreeningMessage(
        language === 'es'
          ? `Lista blanca “${created?.name || name}” creada. Añade personas a esta lista.`
          : `Whitelist “${created?.name || name}” created. Add people to this list.`,
      );
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo crear la lista blanca (${detail}).`
          : `Could not create the whitelist (${detail}).`,
      );
    } finally {
      setOfacWhitelistCreating(false);
    }
  }, [accessToken, gatewayBaseUrl, language, loadOfacNamedWhitelists, ofacNewWhitelistName, ofacSelectedCompanyId, ofacWhitelistForm]);

  const parseOfacGatewayError = React.useCallback(async (response: Response) => {
    if (response.status === 401) {
      return ofacGatewaySessionRequiredMessage(language);
    }
    const detail = await response.text().catch(() => '');
    try {
      const parsed = JSON.parse(detail) as { detail?: unknown };
      if (typeof parsed.detail === 'string' && parsed.detail.trim()) {
        return parsed.detail;
      }
    } catch {
      /* keep raw text */
    }
    return detail || `HTTP ${response.status}`;
  }, [language]);

  const handleOfacSaveCompany = React.useCallback(async () => {
    const name = ofacCompanyNameDraft.trim();
    if (!name) {
      setOfacScreeningError(ofacCompanyNameRequiredMessage(language));
      return;
    }
    if (!isOfacCustomerDirectoryCompany(name)) {
      setOfacScreeningError(ofacDirectoryCompanyRejectedMessage(language));
      return;
    }
    if (!isRealGatewayAccessToken(accessToken)) {
      setOfacScreeningError(ofacGatewaySessionRequiredMessage(language));
      return;
    }
    setOfacCompanyBusy(true);
    setOfacScreeningError(null);
    setOfacScreeningMessage(null);
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 45000);
    try {
      const renaming = ofacCompanyEditor === 'rename' && ofacSelectedCompanyId != null;
      const response = await fetch(
        renaming
          ? `${gatewayBaseUrl}/api/v1/ofac/companies/${ofacSelectedCompanyId}`
          : `${gatewayBaseUrl}/api/v1/ofac/companies`,
        {
          method: renaming ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...gatewayAuthorizationHeaders(accessToken),
          },
          body: JSON.stringify({ name }),
          signal: controller.signal,
        },
      );
      if (!response.ok) {
        throw new Error(await parseOfacGatewayError(response));
      }
      const body = (await response.json()) as { company?: OfacWorkspaceCompany };
      const saved = body.company;
      setOfacCompanyEditor('idle');
      setOfacCompanyNameDraft('');
      await loadOfacCompanies();
      if (saved?.company_id) {
        persistOfacCompanyId(saved.company_id);
      }
      setOfacScreeningMessage(
        language === 'es'
          ? (renaming ? `Empresa “${saved?.name || name}” actualizada.` : `Empresa “${saved?.name || name}” creada. Ahora puedes crear listas blancas.`)
          : (renaming ? `Company “${saved?.name || name}” updated.` : `Company “${saved?.name || name}” created. You can add whitelists now.`),
      );
    } catch (error) {
      const timedOut = error instanceof DOMException && error.name === 'AbortError';
      const detail = timedOut
        ? (language === 'es' ? 'tiempo agotado' : 'timed out')
        : (error instanceof Error ? error.message : 'request-failed');
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo guardar la empresa (${detail}).`
          : `Could not save the company (${detail}).`,
      );
    } finally {
      window.clearTimeout(timeoutId);
      setOfacCompanyBusy(false);
    }
  }, [accessToken, gatewayBaseUrl, language, loadOfacCompanies, ofacCompanyEditor, ofacCompanyNameDraft, ofacSelectedCompanyId, parseOfacGatewayError, persistOfacCompanyId]);

  const handleOfacDeleteCompany = React.useCallback(async () => {
    if (!ofacSelectedCompanyId) {
      setOfacScreeningError(ofacCompanyRequiredMessage(language));
      return;
    }
    const selected = ofacCompanies.find((row) => row.company_id === ofacSelectedCompanyId);
    const confirmed = window.confirm(
      language === 'es'
        ? `¿Eliminar la empresa “${selected?.name || ofacSelectedCompanyId}” y sus listas blancas?`
        : `Delete company “${selected?.name || ofacSelectedCompanyId}” and its whitelists?`,
    );
    if (!confirmed) {
      return;
    }
    if (!isRealGatewayAccessToken(accessToken)) {
      setOfacScreeningError(ofacGatewaySessionRequiredMessage(language));
      return;
    }
    setOfacCompanyBusy(true);
    setOfacScreeningError(null);
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/ofac/companies/${ofacSelectedCompanyId}`, {
        method: 'DELETE',
        headers: gatewayAuthorizationHeaders(accessToken),
      });
      if (!response.ok) {
        throw new Error(await parseOfacGatewayError(response));
      }
      persistOfacCompanyId(null);
      setOfacNamedWhitelists([]);
      await loadOfacCompanies();
      setOfacScreeningMessage(
        language === 'es' ? 'Empresa eliminada.' : 'Company deleted.',
      );
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo eliminar la empresa (${detail}).`
          : `Could not delete the company (${detail}).`,
      );
    } finally {
      setOfacCompanyBusy(false);
    }
  }, [accessToken, gatewayBaseUrl, language, loadOfacCompanies, ofacCompanies, ofacSelectedCompanyId, parseOfacGatewayError, persistOfacCompanyId]);

  const handleOfacRenameWhitelist = React.useCallback(async () => {
    const name = ofacWhitelistRenameDraft.trim();
    if (!ofacSelectedCompanyId) {
      setOfacScreeningError(ofacCompanyRequiredMessage(language));
      return;
    }
    if (!ofacActiveWhitelistId) {
      setOfacScreeningError(
        language === 'es'
          ? 'Elige una lista blanca para cambiarle el nombre.'
          : 'Select a whitelist to rename.',
      );
      return;
    }
    if (!name) {
      setOfacScreeningError(ofacCreateListNameRequiredMessage(language, firstOfacHireLocation(ofacWhitelistForm)));
      return;
    }
    if (!isRealGatewayAccessToken(accessToken)) {
      setOfacScreeningError(ofacGatewaySessionRequiredMessage(language));
      return;
    }
    setOfacWhitelistBusy(true);
    setOfacScreeningError(null);
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/ofac/whitelists/${ofacActiveWhitelistId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...gatewayAuthorizationHeaders(accessToken),
        },
        body: JSON.stringify({ name, company_id: ofacSelectedCompanyId }),
      });
      if (!response.ok) {
        throw new Error(await parseOfacGatewayError(response));
      }
      setOfacWhitelistRenameOpen(false);
      setOfacWhitelistRenameDraft('');
      await loadOfacNamedWhitelists();
      setOfacScreeningMessage(
        language === 'es' ? `Lista blanca “${name}” actualizada.` : `Whitelist “${name}” renamed.`,
      );
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo cambiar el nombre de la lista (${detail}).`
          : `Could not rename the whitelist (${detail}).`,
      );
    } finally {
      setOfacWhitelistBusy(false);
    }
  }, [accessToken, gatewayBaseUrl, language, loadOfacNamedWhitelists, ofacActiveWhitelistId, ofacSelectedCompanyId, ofacWhitelistForm, ofacWhitelistRenameDraft, parseOfacGatewayError]);

  const handleOfacDeleteWhitelist = React.useCallback(async () => {
    if (!ofacSelectedCompanyId) {
      setOfacScreeningError(ofacCompanyRequiredMessage(language));
      return;
    }
    if (!ofacActiveWhitelistId) {
      setOfacScreeningError(
        language === 'es' ? 'Elige una lista blanca para eliminar.' : 'Select a whitelist to delete.',
      );
      return;
    }
    const selected = ofacNamedWhitelists.find((row) => row.id === ofacActiveWhitelistId);
    const confirmed = window.confirm(
      language === 'es'
        ? `¿Eliminar la lista blanca “${selected?.name || ofacActiveWhitelistId}”? Las personas siguen en Screening.`
        : `Delete whitelist “${selected?.name || ofacActiveWhitelistId}”? People stay on Screening.`,
    );
    if (!confirmed) {
      return;
    }
    if (!isRealGatewayAccessToken(accessToken)) {
      setOfacScreeningError(ofacGatewaySessionRequiredMessage(language));
      return;
    }
    setOfacWhitelistBusy(true);
    setOfacScreeningError(null);
    try {
      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/ofac/whitelists/${ofacActiveWhitelistId}?company_id=${encodeURIComponent(String(ofacSelectedCompanyId))}`,
        {
          method: 'DELETE',
          headers: gatewayAuthorizationHeaders(accessToken),
        },
      );
      if (!response.ok) {
        throw new Error(await parseOfacGatewayError(response));
      }
      setOfacActiveWhitelistId(null);
      await loadOfacNamedWhitelists();
      setOfacScreeningMessage(
        language === 'es' ? 'Lista blanca eliminada.' : 'Whitelist deleted.',
      );
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo eliminar la lista blanca (${detail}).`
          : `Could not delete the whitelist (${detail}).`,
      );
    } finally {
      setOfacWhitelistBusy(false);
    }
  }, [accessToken, gatewayBaseUrl, language, loadOfacNamedWhitelists, ofacActiveWhitelistId, ofacNamedWhitelists, ofacSelectedCompanyId, parseOfacGatewayError]);

  const importOfacScreeningRows = React.useCallback(async (
    rows: OfacImportRow[],
    options?: { forceScreenNow?: boolean },
  ) => {
    let imported = 0;
    let screened = 0;
    const failures: string[] = [];
    for (const row of rows) {
      try {
        const screenNow = Boolean(options?.forceScreenNow || row.screenNow);
        await upsertOfacScreeningSubject({
          entityName: row.entityName,
          relationshipType: row.relationshipType,
          entityType: row.entityType,
          identifiers: buildOfacIdentifiers(row),
          source: 'csv-import',
          screenNow,
        });
        imported += 1;
        if (screenNow) {
          screened += 1;
        }
      } catch (error) {
        const detail = error instanceof Error ? error.message : 'request-failed';
        failures.push(`Line ${row.lineNumber}: ${detail}`);
      }
    }
    return { imported, screened, failures };
  }, [buildOfacIdentifiers, upsertOfacScreeningSubject]);

  const handleOfacScreeningCsvSelected = React.useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    setOfacScreeningImporting(true);
    setOfacScreeningError(null);
    setOfacScreeningMessage(null);
    try {
      const text = await file.text();
      const parsed = parseOfacScreeningCsv(text, { defaultScreenNow: ofacScreeningImportScreenNow });
      if (parsed.rows.length === 0) {
        setOfacScreeningError(
          parsed.errors[0]
            || (language === 'es' ? 'No se encontraron filas válidas en el CSV.' : 'No valid rows found in the CSV.'),
        );
        return;
      }
      const { imported, screened, failures } = await importOfacScreeningRows(parsed.rows, {
        forceScreenNow: ofacScreeningImportScreenNow,
      });
      await loadOfacScreeningSubjects();
      const parseNotes = parsed.errors.length
        ? (language === 'es'
          ? ` ${parsed.errors.length} fila(s) omitidas por formato.`
          : ` ${parsed.errors.length} row(s) skipped due to format.`)
        : '';
      const failNotes = failures.length
        ? (language === 'es'
          ? ` ${failures.length} fila(s) fallaron al guardar.`
          : ` ${failures.length} row(s) failed to save.`)
        : '';
      setOfacScreeningMessage(
        language === 'es'
          ? `Importación completada: ${imported} sujeto(s)${screened ? `, ${screened} con screening` : ''}.${parseNotes}${failNotes}`
          : `Import complete: ${imported} subject(s)${screened ? `, ${screened} screened` : ''}.${parseNotes}${failNotes}`,
      );
      if (parsed.errors.length || failures.length) {
        setOfacScreeningError([...parsed.errors.slice(0, 5), ...failures.slice(0, 5)].join(' '));
      }
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo importar el CSV (${detail}).`
          : `Could not import CSV (${detail}).`,
      );
    } finally {
      setOfacScreeningImporting(false);
    }
  }, [importOfacScreeningRows, language, loadOfacScreeningSubjects, ofacScreeningImportScreenNow]);

  const handleOfacScreeningRemove = React.useCallback(async (subjectId: number) => {
    setOfacScreeningBusyId(subjectId);
    setOfacScreeningError(null);
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/ofac/screening-subjects/${subjectId}`, {
        method: 'DELETE',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      await loadOfacScreeningSubjects();
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo eliminar (${detail}).`
          : `Could not remove subject (${detail}).`,
      );
    } finally {
      setOfacScreeningBusyId(null);
    }
  }, [accessToken, gatewayBaseUrl, language, loadOfacScreeningSubjects]);

  const handleOfacScreeningScreenNow = React.useCallback(async (subjectId: number) => {
    setOfacScreeningBusyId(subjectId);
    setOfacScreeningError(null);
    setOfacScreeningMessage(null);
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/ofac/screening-subjects/${subjectId}/screen`, {
        method: 'POST',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const body = (await response.json()) as { screen_result?: { status?: string } };
      setOfacScreeningMessage(
        language === 'es'
          ? `Screening completado: ${body.screen_result?.status || 'UNKNOWN'}.`
          : `Screening complete: ${body.screen_result?.status || 'UNKNOWN'}.`,
      );
      await loadOfacScreeningSubjects();
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo ejecutar el screening (${detail}).`
          : `Could not run screening (${detail}).`,
      );
    } finally {
      setOfacScreeningBusyId(null);
    }
  }, [accessToken, gatewayBaseUrl, language, loadOfacScreeningSubjects]);

  const handleOfacHireValidate = React.useCallback(async (subjectId: number) => {
    setOfacScreeningBusyId(subjectId);
    setOfacScreeningError(null);
    setOfacScreeningMessage(null);
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/ofac/screening-subjects/${subjectId}/hire-validate`, {
        method: 'POST',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null) as { detail?: unknown } | null;
        const detail = typeof payload?.detail === 'string'
          ? payload.detail
          : `HTTP ${response.status}`;
        throw new Error(detail);
      }
      const body = (await response.json()) as {
        validated?: boolean;
        last_validated_at?: string | null;
        blockers?: string[];
        screen_result?: { status?: string };
        subject?: OfacScreeningSubject;
      };
      if (body.subject) {
        setOfacScreeningSubjects((prev) => prev.map((row) => (
          row.id === subjectId ? { ...row, ...body.subject } : row
        )));
      } else {
        await loadOfacScreeningSubjects();
      }
      const stamp = formatOfacTimestamp(body.last_validated_at, language);
      const screenStatus = body.screen_result?.status || 'UNKNOWN';
      setOfacScreeningMessage(
        body.validated
          ? (language === 'es'
            ? `Revalidación correcta (${screenStatus}). Última validación: ${stamp}.`
            : `Re-validation passed (${screenStatus}). Last validated: ${stamp}.`)
          : (language === 'es'
            ? `Revalidación no superada (${screenStatus}). Última validación correcta: ${stamp}.`
            : `Re-validation did not pass (${screenStatus}). Last successful validation: ${stamp}.`),
      );
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo revalidar la lista blanca (${detail}).`
          : `Could not re-validate the whitelist (${detail}).`,
      );
    } finally {
      setOfacScreeningBusyId(null);
    }
  }, [accessToken, gatewayBaseUrl, language, loadOfacScreeningSubjects]);

  const handleOfacWhitelistControlSave = React.useCallback(async (subjectId: number) => {
    const subject = ofacScreeningSubjects.find((row) => row.id === subjectId);
    const draft = ofacWhitelistControlDrafts[subjectId] || {
      owner: String(subject?.hire_clearance?.owner || ''),
      recertifyDays: normalizeOfacReviewCycleDays(subject?.hire_clearance?.recertify_days),
    };
    const owner = String(draft.owner || '').trim();
    if (owner.length < 2) {
      setOfacScreeningError(ofacOwnerRequiredMessage(language));
      return;
    }
    setOfacScreeningError(null);
    setOfacScreeningMessage(null);
    setOfacScreeningBusyId(subjectId);
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/ofac/screening-subjects/${subjectId}/whitelist-control`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          owner,
          recertify_days: normalizeOfacReviewCycleDays(draft?.recertifyDays),
          company_id: ofacSelectedCompanyId ?? undefined,
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null) as { detail?: unknown } | null;
        const detail = typeof payload?.detail === 'string'
          ? payload.detail
          : `HTTP ${response.status}`;
        throw new Error(detail);
      }
      const body = (await response.json()) as { subject?: OfacScreeningSubject };
      if (body.subject) {
        setOfacScreeningSubjects((prev) => prev.map((row) => (
          row.id === subjectId ? { ...row, ...body.subject } : row
        )));
      } else {
        await loadOfacScreeningSubjects();
      }
      setOfacWhitelistControlDrafts((current) => {
        const next = { ...current };
        delete next[subjectId];
        return next;
      });
      setOfacScreeningMessage(
        language === 'es'
          ? 'Dueño y ciclo de revisión guardados. SDN no se omite.'
          : 'Owner and review cycle saved. SDN still runs.',
      );
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo guardar el dueño (${detail}).`
          : `Could not save owner (${detail}).`,
      );
    } finally {
      setOfacScreeningBusyId(null);
    }
  }, [accessToken, gatewayBaseUrl, language, loadOfacScreeningSubjects, ofacScreeningSubjects, ofacSelectedCompanyId, ofacWhitelistControlDrafts]);

  const handleOfacHireValidateDue = React.useCallback(async (subjectIds: number[]) => {
    if (!subjectIds.length || ofacScreeningScreeningAll) {
      return;
    }
    setOfacScreeningScreeningAll(true);
    setOfacScreeningError(null);
    setOfacScreeningMessage(
      language === 'es'
        ? `Revalidando ${subjectIds.length} sujeto(s) de la lista blanca…`
        : `Re-validating ${subjectIds.length} whitelist subject(s)…`,
    );
    let passedCount = 0;
    const failures: string[] = [];
    try {
      for (const subjectId of subjectIds) {
        setOfacScreeningBusyId(subjectId);
        try {
          const response = await fetch(`${gatewayBaseUrl}/api/v1/ofac/screening-subjects/${subjectId}/hire-validate`, {
            method: 'POST',
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
          });
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const body = (await response.json()) as { validated?: boolean };
          if (body.validated) {
            passedCount += 1;
          }
        } catch (error) {
          const detail = error instanceof Error ? error.message : 'request-failed';
          failures.push(`#${subjectId}: ${detail}`);
        }
      }
      await loadOfacScreeningSubjects();
      setOfacScreeningMessage(
        language === 'es'
          ? `Revalidación de lista blanca: ${passedCount}/${subjectIds.length} vigentes.`
          : `Whitelist re-validation: ${passedCount}/${subjectIds.length} still clear.`,
      );
      if (failures.length) {
        setOfacScreeningError(failures.slice(0, 5).join(' '));
      }
    } finally {
      setOfacScreeningBusyId(null);
      setOfacScreeningScreeningAll(false);
    }
  }, [accessToken, gatewayBaseUrl, language, loadOfacScreeningSubjects, ofacScreeningScreeningAll]);

  const handleOfacInvestigationUpdate = React.useCallback(async (
    subjectId: number,
    patch: {
      investigation_status?: OfacInvestigationStatus;
      investigation_disposition?: OfacInvestigationDisposition;
      investigation_notes?: string;
      checklist?: Partial<Record<OfacChecklistStepId, boolean | OfacChecklistStepState>>;
      hire_checklist?: Partial<Record<OfacHireChecklistStepId, boolean | OfacChecklistStepState>>;
      capta_disposition?: string;
      capta_notes?: string;
    },
  ) => {
    setOfacScreeningBusyId(subjectId);
    setOfacScreeningError(null);
    try {
      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/ofac/screening-subjects/${subjectId}/investigation`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify(patch),
        },
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => null) as { detail?: unknown } | null;
        const detail = typeof payload?.detail === 'string'
          ? payload.detail
          : `HTTP ${response.status}`;
        throw new Error(detail);
      }
      const body = (await response.json()) as {
        subject?: OfacScreeningSubject;
        false_hit_notice?: string | null;
        false_hit_pairs?: OfacFalseHitPair[];
      };
      if (body.subject) {
        setOfacScreeningSubjects((prev) => prev.map((row) => (
          row.id === subjectId ? { ...row, ...body.subject } : row
        )));
      } else {
        await loadOfacScreeningSubjects();
      }
      if (Array.isArray(body.false_hit_pairs) && body.false_hit_pairs.length) {
        setOfacFalseHitPairs((prev) => {
          const next = [...body.false_hit_pairs!];
          const seen = new Set(next.map((row) => row.id));
          for (const row of prev) {
            if (!seen.has(row.id)) {
              next.push(row);
            }
          }
          return next;
        });
      }
      if (body.false_hit_notice) {
        setOfacScreeningMessage(body.false_hit_notice);
      }
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo actualizar la investigación (${detail}).`
          : `Could not update investigation (${detail}).`,
      );
    } finally {
      setOfacScreeningBusyId(null);
    }
  }, [accessToken, gatewayBaseUrl, language, loadOfacScreeningSubjects]);

  const handleOfacFalseHitCreate = React.useCallback(async (subject: OfacScreeningSubject) => {
    const rationale = String(ofacFalseHitRationaleById[subject.id] || '').trim();
    if (rationale.length < 8) {
      setOfacScreeningError(
        language === 'es'
          ? 'Escribe una justificación (mínimo 8 caracteres) antes de crear el par de falso hit.'
          : 'Write a rationale (at least 8 characters) before creating the false-hit pair.',
      );
      return;
    }
    const selected = ofacFalseHitSelectedById[subject.id] || [];
    setOfacScreeningBusyId(subject.id);
    setOfacScreeningError(null);
    try {
      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/ofac/screening-subjects/${subject.id}/false-hit-pairs`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({
            rationale,
            list_entry_ids: selected.length ? selected : undefined,
          }),
        },
      );
      if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || `HTTP ${response.status}`);
      }
      setOfacScreeningMessage(
        language === 'es'
          ? 'Par de falso hit creado. Un segundo revisor debe aprobarlo.'
          : 'False-hit pair created. A second reviewer must approve it.',
      );
      await loadOfacScreeningSubjects();
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo crear el par de falso hit (${detail}).`
          : `Could not create the false-hit pair (${detail}).`,
      );
    } finally {
      setOfacScreeningBusyId(null);
    }
  }, [
    accessToken,
    gatewayBaseUrl,
    language,
    loadOfacScreeningSubjects,
    ofacFalseHitRationaleById,
    ofacFalseHitSelectedById,
  ]);

  const handleOfacFalseHitAction = React.useCallback(async (
    pairId: number,
    action: 'approve' | 'revoke' | 'recertify',
  ) => {
    setOfacScreeningBusyId(pairId);
    setOfacScreeningError(null);
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/ofac/false-hit-pairs/${pairId}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || `HTTP ${response.status}`);
      }
      await loadOfacScreeningSubjects();
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo ${action} el par (${detail}).`
          : `Could not ${action} the pair (${detail}).`,
      );
    } finally {
      setOfacScreeningBusyId(null);
    }
  }, [accessToken, gatewayBaseUrl, language, loadOfacScreeningSubjects]);

  const handleOfacFalseHitImport = React.useCallback(async () => {
    const csvText = ofacFalseHitCsv.trim();
    if (csvText.length < 8) {
      setOfacScreeningError(
        language === 'es'
          ? 'CSV necesita encabezado y al menos una fila subject_id,list_entry_id.'
          : 'CSV needs a header and at least one subject_id,list_entry_id row.',
      );
      return;
    }
    setOfacFalseHitImporting(true);
    setOfacScreeningError(null);
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/ofac/false-hit-pairs/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ csv_text: csvText }),
      });
      if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || `HTTP ${response.status}`);
      }
      const body = (await response.json()) as {
        created_count?: number;
        error_count?: number;
        notice?: string;
      };
      setOfacScreeningMessage(
        body.notice
          || (language === 'es'
            ? `Importados ${body.created_count || 0} pares (${body.error_count || 0} errores). Quedan pendientes de cuatro ojos.`
            : `Imported ${body.created_count || 0} pairs (${body.error_count || 0} errors). They stay pending four-eyes.`),
      );
      await loadOfacScreeningSubjects();
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo importar el CSV de falso hit (${detail}).`
          : `Could not import the false-hit CSV (${detail}).`,
      );
    } finally {
      setOfacFalseHitImporting(false);
    }
  }, [accessToken, gatewayBaseUrl, language, loadOfacScreeningSubjects, ofacFalseHitCsv]);

  const [ofacOwnersDraftById, setOfacOwnersDraftById] = React.useState<Record<number, string>>({});
  const [ofacFiftyPercentRunningAll, setOfacFiftyPercentRunningAll] = React.useState(false);

  const handleOfacFiftyPercentRun = React.useCallback(async (subjectId: number) => {
    setOfacScreeningBusyId(subjectId);
    setOfacScreeningError(null);
    setOfacScreeningMessage(null);
    try {
      const ownersText = ofacOwnersDraftById[subjectId] || '';
      const owners = parseOfacOwnersText(ownersText);
      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/ofac/screening-subjects/${subjectId}/fifty-percent`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify(owners.length ? { owners } : {}),
        },
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const body = (await response.json()) as {
        subject?: OfacScreeningSubject;
        summary?: { status?: string; aggregate_blocked_percent?: number; has_owners?: boolean };
      };
      if (body.subject) {
        setOfacScreeningSubjects((prev) => prev.map((row) => (
          row.id === subjectId ? { ...row, ...body.subject } : row
        )));
      } else {
        await loadOfacScreeningSubjects();
      }
      const summary = body.summary;
      setOfacScreeningMessage(
        language === 'es'
          ? `Regla del 50%: ${summary?.status || 'unknown'}${summary?.aggregate_blocked_percent != null ? ` · ${summary.aggregate_blocked_percent}% bloqueado agregado` : ''}${summary?.has_owners === false ? ' · sin dueños en archivo' : ''}.`
          : `50% rule: ${summary?.status || 'unknown'}${summary?.aggregate_blocked_percent != null ? ` · ${summary.aggregate_blocked_percent}% aggregate blocked` : ''}${summary?.has_owners === false ? ' · no owners on file' : ''}.`,
      );
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo ejecutar la regla del 50% (${detail}).`
          : `Could not run 50% rule check (${detail}).`,
      );
    } finally {
      setOfacScreeningBusyId(null);
    }
  }, [accessToken, gatewayBaseUrl, language, loadOfacScreeningSubjects, ofacOwnersDraftById]);

  const handleOfacVantageBackgroundCheck = React.useCallback(async (
    subject: OfacScreeningSubject,
    purpose: 'investigation' | 'whitelist_identity' = 'investigation',
  ) => {
    if (!canAccessResearch) {
      setOfacScreeningError(
        language === 'es'
          ? 'Las verificaciones Vantage requieren CompliCore (research-reports).'
          : 'Vantage background checks require CompliCore (research-reports).',
      );
      return;
    }
    setOfacScreeningBusyId(subject.id);
    setOfacScreeningError(null);
    setOfacScreeningMessage(null);
    try {
      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/ofac/screening-subjects/${subject.id}/vantage-research?purpose=${encodeURIComponent(purpose)}`,
        {
          method: 'POST',
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        },
      );
      if (!response.ok) {
        const detail = await response.text().catch(() => '');
        throw new Error(detail || `HTTP ${response.status}`);
      }
      const body = (await response.json()) as {
        job_id?: string;
        subject?: OfacScreeningSubject;
        message?: string;
      };
      const jobId = String(body.job_id || '').trim();
      if (body.subject) {
        setOfacScreeningSubjects((prev) => prev.map((row) => (
          row.id === subject.id ? { ...row, ...body.subject } : row
        )));
      }
      setOfacInvestigationOpenIds((prev) => {
        const next = new Set(prev);
        next.add(subject.id);
        return next;
      });
      if (jobId) {
        const now = new Date().toISOString();
        const queueId = `queue-ofac-${subject.id}-${Date.now()}`;
        const targetType: 'company' | 'person' = (
          subject.entity_type === 'person'
          || subject.relationship_type === 'ubo_director'
          || subject.relationship_type === 'associate'
            ? 'person'
            : 'company'
        );
        setResearchQueue((current) => [
          {
            id: queueId,
            reportName: `OFAC investigation — ${subject.entity_name}`,
            targetType,
            targetName: subject.entity_name,
            status: 'queued',
            progress: 0,
            message: language === 'es' ? 'Enviado desde Investigaciones' : 'Queued from Investigations',
            createdAt: now,
            updatedAt: now,
            etaSeconds: targetType === 'company' ? 420 : 900,
            gatewayJobId: jobId,
            reportId: jobId,
          },
          ...current,
        ]);
        startResearchGatewayJobPoll({
          queueId,
          gatewayJobId: jobId,
          reportId: jobId,
          reportName: `OFAC investigation — ${subject.entity_name}`,
          targetType,
          targetName: subject.entity_name,
          jurisdiction: String(subject.identifiers?.jurisdiction || ''),
          knownAliases: [],
          notes: String(subject.investigation_notes || ''),
          affinityDepth: 2,
          emailAddresses: [],
          emailGroups: [],
          keyDataPayload: {
            names: subject.entity_name,
            addresses: String(subject.identifiers?.addresses || subject.identifiers?.address || ''),
            birthdate: String(subject.identifiers?.birthdate || subject.identifiers?.dob || ''),
            ssn: String(subject.identifiers?.ssn || ''),
            website: String(subject.identifiers?.website || ''),
          },
        });
      }
      setOfacScreeningMessage(
        language === 'es'
          ? (purpose === 'whitelist_identity'
            ? `Verificación de identidad Vantage en cola${jobId ? ` (${jobId})` : ''}. Al terminar, comparamos DOB, dirección e IDs con lo que ingresaste.`
            : `Vantage en cola${jobId ? ` (${jobId})` : ''}. Al terminar, dueños y hechos se sincronizan a este caso.`)
          : (purpose === 'whitelist_identity'
            ? `Vantage identity check queued${jobId ? ` (${jobId})` : ''}. When it finishes, we compare DOB, address, and IDs to intake.`
            : `Vantage queued${jobId ? ` (${jobId})` : ''}. When it finishes, owners and facts sync into this case.`),
      );
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo encolar Vantage (${detail}).`
          : `Could not queue Vantage background check (${detail}).`,
      );
    } finally {
      setOfacScreeningBusyId(null);
    }
  }, [accessToken, canAccessResearch, gatewayBaseUrl, language, startResearchGatewayJobPoll]);

  const handleOfacMonitoringReconcile = React.useCallback(async (prune: boolean) => {
    setOfacScreeningError(null);
    setOfacScreeningMessage(null);
    try {
      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/ofac/monitoring/reconcile?prune=${prune ? 'true' : 'false'}`,
        {
          method: 'POST',
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        },
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const body = (await response.json()) as {
        portfolio_count?: number;
        monitoring_count?: number;
        orphan_count?: number;
        pruned?: number;
      };
      setOfacScreeningMessage(
        language === 'es'
          ? `Monitoreo: cartera ${body.portfolio_count || 0}, filas monitor ${body.monitoring_count || 0}, huérfanas ${body.orphan_count || 0}${prune ? `, eliminadas ${body.pruned || 0}` : ''}.`
          : `Monitoring: portfolio ${body.portfolio_count || 0}, monitor rows ${body.monitoring_count || 0}, orphans ${body.orphan_count || 0}${prune ? `, pruned ${body.pruned || 0}` : ''}.`,
      );
      if (prune) {
        void loadOfacComplianceDashboard();
      }
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo reconciliar monitoreo (${detail}).`
          : `Could not reconcile monitoring (${detail}).`,
      );
    }
  }, [accessToken, gatewayBaseUrl, language, loadOfacComplianceDashboard]);

  const handleOfacFiftyPercentRunAll = React.useCallback(async () => {
    if (ofacFiftyPercentRunningAll) {
      return;
    }
    setOfacFiftyPercentRunningAll(true);
    setOfacScreeningError(null);
    setOfacScreeningMessage(
      language === 'es'
        ? 'Ejecutando chequeos automáticos del 50% en toda la cartera…'
        : 'Running automated 50% checks across the portfolio…',
    );
    try {
      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/ofac/screening-subjects/fifty-percent-run-all`,
        {
          method: 'POST',
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        },
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const body = (await response.json()) as { count?: number; error_count?: number };
      await loadOfacScreeningSubjects();
      setOfacScreeningMessage(
        language === 'es'
          ? `Chequeos del 50% completados: ${body.count || 0} ok${body.error_count ? `, ${body.error_count} error(es)` : ''}. Completa los pasos manuales del checklist.`
          : `50% checks complete: ${body.count || 0} ok${body.error_count ? `, ${body.error_count} error(s)` : ''}. Finish remaining manual checklist steps.`,
      );
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo ejecutar el lote del 50% (${detail}).`
          : `Could not run batch 50% checks (${detail}).`,
      );
    } finally {
      setOfacFiftyPercentRunningAll(false);
    }
  }, [accessToken, gatewayBaseUrl, language, loadOfacScreeningSubjects, ofacFiftyPercentRunningAll]);

  const handleOfacScreeningScreenAll = React.useCallback(async (subjectIds: number[]) => {
    if (!subjectIds.length || ofacScreeningScreeningAll) {
      return;
    }
    setOfacScreeningScreeningAll(true);
    setOfacScreeningError(null);
    setOfacScreeningMessage(
      language === 'es'
        ? `Ejecutando screening en ${subjectIds.length} sujeto(s)…`
        : `Screening ${subjectIds.length} subject(s)…`,
    );
    let okCount = 0;
    const failures: string[] = [];
    try {
      for (const subjectId of subjectIds) {
        setOfacScreeningBusyId(subjectId);
        try {
          const response = await fetch(`${gatewayBaseUrl}/api/v1/ofac/screening-subjects/${subjectId}/screen`, {
            method: 'POST',
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
          });
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          okCount += 1;
        } catch (error) {
          const detail = error instanceof Error ? error.message : 'request-failed';
          failures.push(`#${subjectId}: ${detail}`);
        }
      }
      await loadOfacScreeningSubjects();
      setOfacScreeningMessage(
        language === 'es'
          ? `Screen all completado: ${okCount}/${subjectIds.length} ok.`
          : `Screen all complete: ${okCount}/${subjectIds.length} ok.`,
      );
      if (failures.length) {
        setOfacScreeningError(failures.slice(0, 5).join(' '));
      }
    } finally {
      setOfacScreeningBusyId(null);
      setOfacScreeningScreeningAll(false);
    }
  }, [accessToken, gatewayBaseUrl, language, loadOfacScreeningSubjects, ofacScreeningScreeningAll]);

  const handleOfacRescreenBook = React.useCallback(async () => {
    if (ofacScreeningScreeningAll) {
      return;
    }
    setOfacScreeningScreeningAll(true);
    setOfacScreeningError(null);
    setOfacScreeningMessage(
      language === 'es'
        ? 'Re-cribando la cartera contra las listas OFAC actuales…'
        : 'Rescreening the book against current OFAC lists…',
    );
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/ofac/screening-subjects/rescreen-book`, {
        method: 'POST',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (!response.ok) {
        throw new Error(await parseOfacGatewayError(response));
      }
      const body = (await response.json()) as { count?: number; error_count?: number; skipped?: number };
      await loadOfacScreeningSubjects();
      void loadOfacComplianceDashboard();
      setOfacScreeningMessage(
        language === 'es'
          ? `Re-cribado de cartera: ${body.count || 0} ok${body.error_count ? `, ${body.error_count} error(es)` : ''}${body.skipped ? `, ${body.skipped} omitidos` : ''}.`
          : `Book rescreen: ${body.count || 0} ok${body.error_count ? `, ${body.error_count} error(s)` : ''}${body.skipped ? `, ${body.skipped} skipped` : ''}.`,
      );
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'request-failed';
      setOfacScreeningError(
        language === 'es'
          ? `No se pudo re-cribar la cartera (${detail}).`
          : `Could not rescreen the book (${detail}).`,
      );
    } finally {
      setOfacScreeningScreeningAll(false);
    }
  }, [
    accessToken,
    gatewayBaseUrl,
    language,
    loadOfacComplianceDashboard,
    loadOfacScreeningSubjects,
    ofacScreeningScreeningAll,
    parseOfacGatewayError,
  ]);

  React.useEffect(() => {
    if (activeNavItem !== 'research') {
      return;
    }
    loadComprehensiveCheckHistory();
  }, [activeNavItem, loadComprehensiveCheckHistory]);

  React.useEffect(() => {
    if (
      activeNavItem !== 'ofacScreening'
      && activeNavItem !== 'ofacInvestigations'
      && activeNavItem !== 'ofacWhitelist'
      && activeNavItem !== 'ofacCapta'
    ) {
      return;
    }
    void loadOfacScreeningSubjects();
  }, [activeNavItem, loadOfacScreeningSubjects]);

  React.useEffect(() => {
    if (activeNavItem !== 'ofacWhitelist' && activeNavItem !== 'ofac' && activeNavItem !== 'ofacScreening') {
      return;
    }
    void loadOfacCompanies();
  }, [activeNavItem, loadOfacCompanies]);

  React.useEffect(() => {
    if (activeNavItem !== 'ofacWhitelist') {
      return;
    }
    void loadOfacNamedWhitelists();
  }, [activeNavItem, loadOfacNamedWhitelists]);

  React.useEffect(() => {
    if (activeNavItem !== 'ofac' && activeNavItem !== 'dashboards' && activeNavItem !== 'home') {
      return;
    }
    void loadOfacComplianceDashboard();
  }, [activeNavItem, loadOfacComplianceDashboard]);

  const toggleDashboardSubscription = (dashboardName: string) => {
    setDashboardSubscriptions((current) => ({
      ...current,
      [dashboardName]: !current[dashboardName],
    }));
  };

  const runNlpAnalysis = async () => {
    if (!nlpInput.trim()) {
      return;
    }

    try {
      setIsNlpLoading(true);
      if (!metricsServiceUrl) {
        return;
      }
      const response = await fetch(`${metricsServiceUrl}/ai/nlp/compliance-analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: nlpInput }),
      });
      if (!response.ok) {
        return;
      }
      const payload = (await response.json()) as AINlpAnalysisData;
      setNlpAnalysis(payload);
    } catch {
    } finally {
      setIsNlpLoading(false);
    }
  };

  const handleTwoFactorToggle = () => {
    const nextEnabledState = !isTwoFactorOn;
    setTwoFactorEnabled(nextEnabledState);
    setIsTwoFactorOn(nextEnabledState);
  };

  const toggleServiceAlert = (serviceName: string) => {
    setAlertChecklist((current) => ({
      ...current,
      [serviceName]: !current[serviceName],
    }));
  };

  const handleProfileDraftChange = (field: keyof AccountProfile, value: string) => {
    setProfileDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleProfileCancel = () => {
    setProfileDraft(accountProfile);
    setAvatarDraftDataUrl(accountAvatar);
    setProfileError(null);
    setProfileFeedback(null);
    setIsEditingProfile(false);
  };

  const handleAvatarFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      return;
    }

    if (!selectedFile.type.startsWith('image/')) {
      setProfileError(copy.settingsAvatarInvalidType);
      setProfileFeedback(null);
      event.target.value = '';
      return;
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      setProfileError(copy.settingsAvatarTooLarge);
      setProfileFeedback(null);
      event.target.value = '';
      return;
    }

    const applyAvatarDataUrl = (dataUrl: string) => {
      // Auto-resize if the image data exceeds ~1 MB to keep storage lightweight
      if (dataUrl.length > 1_048_576) {
        const img = new Image();
        img.onload = () => {
          const MAX_SIDE = 1200;
          const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            setProfileError(copy.settingsAvatarInvalidType);
            setProfileFeedback(null);
            return;
          }
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const resized = canvas.toDataURL('image/jpeg', 0.85);
          setAvatarDraftDataUrl(resized);
          setProfileError(null);
          setProfileFeedback(copy.settingsAvatarUpdated);
        };
        img.onerror = () => {
          setProfileError(copy.settingsAvatarInvalidType);
          setProfileFeedback(null);
        };
        img.src = dataUrl;
      } else {
        setAvatarDraftDataUrl(dataUrl);
        setProfileError(null);
        setProfileFeedback(copy.settingsAvatarUpdated);
      }
    };

    const reader = new FileReader();
    reader.onload = () => {
      const nextAvatarDataUrl = typeof reader.result === 'string' ? reader.result : null;
      if (!nextAvatarDataUrl) {
        setProfileError(copy.settingsAvatarInvalidType);
        setProfileFeedback(null);
        return;
      }
      applyAvatarDataUrl(nextAvatarDataUrl);
    };
    reader.onerror = () => {
      setProfileError(copy.settingsAvatarInvalidType);
      setProfileFeedback(null);
    };
    reader.readAsDataURL(selectedFile);
    event.target.value = '';
  };

  const handleAvatarRemove = () => {
    setAvatarDraftDataUrl(null);
    setProfileError(null);
    setProfileFeedback(copy.settingsAvatarRemoved);
  };

  const handleProfileSave = () => {
    const trimmedDisplayName = profileDraft.displayName.trim();
    const trimmedEmail = profileDraft.email.trim();
    const trimmedContactNumber = profileDraft.contactNumber.trim();

    if (!trimmedDisplayName) {
      setProfileError(copy.settingsDisplayNameRequired);
      setProfileFeedback(null);
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setProfileError(copy.settingsEmailInvalid);
          if (trimmedContactNumber && !/^\(\d{3}\) \d{3}-\d{4}$/.test(trimmedContactNumber) && !/^\+[\d\s\-()+]{7,}$/.test(trimmedContactNumber)) {
            setProfileError(copy.settingsContactNumberInvalid);
            setProfileFeedback(null);
            return;
          }

      setProfileFeedback(null);
      return;
    }

    const nextProfile: AccountProfile = {
      personUuid: accountProfile.personUuid,
      displayName: trimmedDisplayName,
      email: trimmedEmail,
      contactNumber: trimmedContactNumber || DEFAULT_ACCOUNT_PROFILE.contactNumber,
      language: profileDraft.language || DEFAULT_ACCOUNT_PROFILE.language,
      timezone: profileDraft.timezone || DEFAULT_ACCOUNT_PROFILE.timezone,
    };

    const nextAvatarByUuid: AccountAvatarMap = {
      ...avatarByUuid,
    };

    if (avatarDraftDataUrl) {
      nextAvatarByUuid[nextProfile.personUuid] = avatarDraftDataUrl;
    } else {
      delete nextAvatarByUuid[nextProfile.personUuid];
    }

    setAccountProfile(nextProfile);
    setProfileDraft(nextProfile);
    setAvatarByUuid(nextAvatarByUuid);
    setAlertEmail((current) => (current === accountProfile.email ? nextProfile.email : current));
    window.localStorage.setItem(ACCOUNT_PROFILE_STORAGE_KEY, JSON.stringify(nextProfile));
    window.localStorage.setItem(ACCOUNT_AVATAR_STORAGE_KEY, JSON.stringify(nextAvatarByUuid));
    setProfileError(null);
    setProfileFeedback(copy.settingsSaved);
    setIsEditingProfile(false);
  };

  const handleNotificationsSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    window.localStorage.setItem('nextcompli.alerts.email', alertEmail);
    Object.entries(alertChecklist).forEach(([serviceName, enabled]) => {
      window.localStorage.setItem(`nextcompli.alerts.service.${serviceName}`, enabled ? 'true' : 'false');
    });
    setAlertFeedback(copy.notificationsEmailSaved);
  };

  const handleAddPaymentMethod = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (paymentEntryType === 'card') {
      const trimmedCardholder = cardholderName.trim();
      const sanitizedTail = cardNumberTail.replace(/\D/g, '').slice(-4);
      if (!trimmedCardholder || sanitizedTail.length !== 4) {
        setPaymentFormError(language === 'es'
          ? 'Ingresa titular y últimos 4 dígitos de la tarjeta.'
          : 'Enter cardholder name and the last 4 card digits.');
        setPaymentFormFeedback(null);
        return;
      }

      setSavedPaymentMethods((current) => [
        ...current,
        {
          id: `pm-card-${Date.now()}`,
          type: 'card',
          label: `${cardBrand} · ${trimmedCardholder}`,
          detail: `•••• ${sanitizedTail}`,
          isDefault: current.length === 0,
        },
      ]);
    } else {
      const trimmedBankName = bankName.trim();
      const sanitizedTail = bankAccountTail.replace(/\D/g, '').slice(-4);
      if (!trimmedBankName || sanitizedTail.length !== 4) {
        setPaymentFormError(language === 'es'
          ? 'Ingresa banco y últimos 4 dígitos de la cuenta.'
          : 'Enter bank name and the last 4 account digits.');
        setPaymentFormFeedback(null);
        return;
      }

      setSavedPaymentMethods((current) => [
        ...current,
        {
          id: `pm-bank-${Date.now()}`,
          type: 'bank',
          label: `${trimmedBankName} ACH`,
          detail: `Acct •••• ${sanitizedTail}`,
          isDefault: current.length === 0,
        },
      ]);
    }

    setPaymentFormError(null);
    setPaymentFormFeedback(language === 'es'
      ? 'Método de pago guardado correctamente.'
      : 'Payment method saved successfully.');
    setCardholderName('');
    setCardNumberTail('');
    setBankName('');
    setBankAccountTail('');
  };

  const makeDefaultPaymentMethod = (methodId: string) => {
    setSavedPaymentMethods((current) => current.map((method) => ({
      ...method,
      isDefault: method.id === methodId,
    })));
    setPaymentFormFeedback(language === 'es'
      ? 'Método de pago predeterminado actualizado.'
      : 'Default payment method updated.');
  };

  const selectPlan = (planId: string) => {
    setPaymentPlans((current) => current.map((plan) => ({
      ...plan,
      isCurrent: plan.id === planId,
    })));
    setPaymentFormFeedback(language === 'es'
      ? 'Suscripción actualizada correctamente.'
      : 'Subscription updated successfully.');
  };

  const handlePasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (passwordSubmitting) {
      return;
    }

    if (newPasswordInput !== confirmPasswordInput) {
      setPasswordError(copy.passwordMismatch);
      setPasswordFeedback(null);
      return;
    }

    if (newPasswordInput.length < 12) {
      setPasswordError(copy.passwordLength);
      setPasswordFeedback(null);
      return;
    }

    setPasswordSubmitting(true);
    setPasswordError(null);
    setPasswordFeedback(null);
    try {
      const result = await changePassword(currentPasswordInput, newPasswordInput);
      if (!result.ok) {
        setPasswordError(result.error || copy.passwordInvalidCurrent);
        return;
      }
      setPasswordFeedback(
        result.warning
          ? `${copy.passwordSuccess} (${result.warning})`
          : copy.passwordSuccess,
      );
      setCurrentPasswordInput('');
      setNewPasswordInput('');
      setConfirmPasswordInput('');
      setSecurityFeedback(language === 'es' ? 'La contraseña de la cuenta se actualizó.' : 'Account password was updated.');
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const subscribedDashboards = availableDashboards.filter((dashboardName) => dashboardSubscriptions[dashboardName]);
  const dashboardServices = metricsDashboardData?.services ?? [];
  const isLiveDataFresh = Boolean(metricsLastUpdated && Date.now() - metricsLastUpdated.getTime() < 90000);
  const researchAutofillIgnoreProps = {
    autoComplete: 'off',
    'data-lpignore': 'true',
    'data-1p-ignore': 'true',
    'data-op-ignore': 'true',
    'data-bwignore': 'true',
    'data-form-type': 'other',
    spellCheck: false,
  } as const;
  const researchIntakeFieldProps = (field: string) => ({
    ...researchAutofillIgnoreProps,
    id: `nc-research-${field}-${researchIntakeEpoch}`,
    name: `nc-research-${field}-${researchIntakeEpoch}`,
    readOnly: researchIntakeAutofillLocked,
    onFocus: () => setResearchIntakeAutofillLocked(false),
  });

  const getServiceTags = (serviceKey: string): string[] => {
    if (OFAC_SERVICE_KEYS.has(serviceKey)) {
      return [copy.dashboardTagOfac];
    }
    if (GOVERNMENTAL_COMPLIANCE_SERVICE_KEYS.has(serviceKey)) {
      return [copy.dashboardTagGovernmentalCompliance];
    }
    if (WEBSITE_COMPLIANCE_SERVICE_KEYS.has(serviceKey)) {
      return [copy.dashboardTagWebsiteCompliance];
    }

    return [copy.dashboardTagOfac];
  };

  const toggleDashboardServiceVisibility = (filterKey: DashboardServiceFilterKey) => {
    setDashboardServiceVisibility((current) => ({
      ...current,
      [filterKey]: !current[filterKey],
    }));
  };

  const getHealthColor = (health: string | undefined): string => {
    switch (health) {
      case 'healthy':
        return '#10b981';
      case 'degraded':
        return '#f59e0b';
      case 'unhealthy':
        return '#ef4444';
      default:
        return 'var(--color-text-muted)';
    }
  };

  const formatServiceRisk = (riskLevelText: string | undefined): string => {
    if (!riskLevelText) {
      return 'UNKNOWN';
    }

    return riskLevelText.toUpperCase();
  };

  const formatServiceStatusLabel = (serviceKey: string, statusText: string | undefined): string => {
    const normalizedStatus = String(statusText || 'unknown').toLowerCase();
    if (isLegalComplianceServiceKey(serviceKey)) {
      return normalizedStatus === 'healthy' ? 'COMPLIANT' : 'NON COMPLIANT';
    }

    return normalizedStatus.toUpperCase();
  };

  const buildPrimaryMetric = (
    serviceKey: string,
    serviceRecord: MetricsDashboardService | undefined,
    serviceSummary: MetricsServiceDataSummary[string] | undefined,
    legalAreaInput: LegalExposureAreaInput | undefined,
  ): { value: string; unit: string; caption: string } => {
    if (isLegalComplianceServiceKey(serviceKey)) {
      const legalCounts = deriveLegalComplianceCounts(serviceRecord, legalAreaInput);
      const compliance = finiteNumber(legalCounts.compliancePercentage);
      if (compliance !== null) {
        return {
          value: `${compliance.toFixed(1)}`,
          unit: '%',
          caption: copy.metricCaptionComplianceScore,
        };
      }
    }

    const recordCompliance = finiteNumber(serviceRecord?.compliance_percentage);
    if (recordCompliance !== null) {
      return {
        value: `${recordCompliance.toFixed(1)}`,
        unit: '%',
        caption: copy.metricCaptionComplianceScore,
      };
    }

    if (serviceSummary && serviceSummary.count > 0) {
      return {
        value: serviceSummary.count.toLocaleString(),
        unit: copy.metricUnitRecords,
        caption: copy.metricCaptionDataIndexed,
      };
    }

    if (serviceSummary && ((serviceSummary.live_count ?? 0) > 0 || (serviceSummary.reliable_count ?? 0) > 0)) {
      const summaryCount = Math.max(serviceSummary.live_count ?? 0, serviceSummary.reliable_count ?? 0);
      return {
        value: summaryCount.toLocaleString(),
        unit: copy.metricUnitRecords,
        caption: copy.metricCaptionDataIndexed,
      };
    }

    if (serviceRecord) {
      const successRate = ((serviceRecord.requests_success ?? 0) / Math.max(serviceRecord.requests_total ?? 1, 1)) * 100;
      return {
        value: `${successRate.toFixed(1)}`,
        unit: '%',
        caption: copy.metricCaptionSuccessRate,
      };
    }

    return {
      value: '--',
      unit: '',
      caption: copy.metricCaptionAwaitingMetrics,
    };
  };

  const getServiceTileIcon = (serviceKey: string): string => {
    const iconMap: Record<string, string> = {
      ofac_general_licenses: '⚖️',
      sdn_lists: '📋',
      capta_list: '🛡️',
      doe_state: '🏛️',
      american_petroleum_institute: '⛽',
      sec_filings: '📑',
      national_association_manufacturers: '🏭',
      us_chamber_of_commerce: '🏢',
      sec_edgar: '📊',
      screening_gateway: '🔎',
      compliance: '✅',
      legal_contract_enforceability: '📝',
      legal_privacy_data_law: '🔒',
      legal_security_obligations: '🛡️',
      legal_marketing_claims: '📢',
      legal_payments_billing_terms: '💳',
      legal_ip_licensing: '©️',
      legal_accessibility_wcag: '♿',
      legal_regulated_use_alignment: '⚠️',
      legal_local_compliance: '🏙️',
      legal_state_compliance: '🗺️',
      legal_federal_compliance: '🏛️',
    };

    return iconMap[serviceKey] ?? '📈';
  };

  const getDashboardDetailPath = (serviceKey: string): string => {
    const routeName = resolveServiceRouteName(serviceKey);
    return `/dashboard/index.html#service/${routeName}`;
  };

  const renderDashboardsPane = (): React.ReactNode => {
    if (!hasSuperUserAccess) {
      const companyName = user?.companies?.[0]?.company_name
        || (language === 'es' ? 'tu organización' : 'your organization');
      const signedInScope = String(user?.email || '').trim().toLowerCase();
      const homeStorageKey = (storageKey: string): string => (
        signedInScope ? `${storageKey}:signed-in:${signedInScope}` : storageKey
      );
      const websiteConsoleState = loadComplianceConsoleState(homeStorageKey(WEBSITE_COMPLIANCE_CONSOLE.storageKey));
      const dodConsoleState = loadComplianceConsoleState(homeStorageKey(DOD_CONTRACTS_COMPLIANCE_CONSOLE.storageKey));
      const educationConsoleState = loadComplianceConsoleState(homeStorageKey(EDUCATION_COMPLIANCE_CONSOLE.storageKey));
      const hasWebsiteCompany = Boolean(String(websiteConsoleState.targetCompanyName || '').trim());
      const hasDodCompany = Boolean(String(dodConsoleState.targetCompanyName || '').trim());
      const hasEducationCompany = Boolean(String(educationConsoleState.targetCompanyName || '').trim());
      const vantageCompleted = researchReports.length;
      const vantageQueued = researchQueue.filter((item) => ['queued', 'screening'].includes(item.status)).length;
      const vantageFailed = researchQueue.filter((item) => ['failed', 'cancelled'].includes(item.status)).length;
      const vantageClear = Math.max(0, vantageCompleted - vantageFailed);
      const ofacHomeIds = new Set(['screened_30d', 'clear', 'in_review', 'potential_hit']);
      const ofacMetricsFromApi = (ofacComplianceDashboard?.kpis || [])
        .filter((row) => row.id && ofacHomeIds.has(row.id))
        .map((row) => ({
          label: row.id === 'screened_30d'
            ? (language === 'es' ? 'Evaluados' : 'Screened')
            : row.label,
          value: row.value,
          tone: (row.tone || 'neutral') as 'neutral' | 'ok' | 'warn' | 'alert',
        }));
      const ofacMetrics = ofacMetricsFromApi.length
        ? ofacMetricsFromApi
        : [
          { label: language === 'es' ? 'Evaluados' : 'Screened', value: '0', tone: 'neutral' as const },
          { label: language === 'es' ? 'Sin coincidencia' : 'Clear', value: '0', tone: 'ok' as const },
          { label: language === 'es' ? 'Revisión' : 'Review', value: '0', tone: 'warn' as const },
          { label: language === 'es' ? 'Posible hit' : 'Potential hit', value: '0', tone: 'alert' as const },
        ];
      const ofacHomeHasData = Number(ofacMetrics[0]?.value || 0) > 0
        || Number(ofacComplianceDashboard?.kpis?.find((row) => row.id === 'portfolio')?.value || 0) > 0;
      const ofacHomeVolumePoints = Array.isArray(ofacComplianceDashboard?.trend?.volume_points)
        ? ofacComplianceDashboard.trend.volume_points
        : [];
      const ofacHomeExceptionPoints = Array.isArray(ofacComplianceDashboard?.trend?.exception_points)
        ? ofacComplianceDashboard.trend.exception_points
        : [];
      const vantageBars = [
        { label: language === 'es' ? 'Completados' : 'Completed', value: Math.max(vantageCompleted, 1), max: Math.max(vantageCompleted + vantageQueued + vantageFailed, 8) },
        { label: language === 'es' ? 'En cola' : 'In queue', value: Math.max(vantageQueued, 0), max: Math.max(vantageCompleted + vantageQueued + vantageFailed, 8) },
        { label: language === 'es' ? 'Fallidos / cancelados' : 'Failed / cancelled', value: Math.max(vantageFailed, 0), max: Math.max(vantageCompleted + vantageQueued + vantageFailed, 8) },
        { label: language === 'es' ? 'Listos p/ PDF' : 'Ready for PDF', value: Math.max(vantageClear, 0), max: Math.max(vantageCompleted + vantageQueued + vantageFailed, 8) },
      ];
      const websiteProgress = hasWebsiteCompany
        ? complianceProgressPercent(
          WEBSITE_COMPLIANCE_CONSOLE,
          websiteConsoleState,
          planFromFeatures(effectiveFeatureList, { isPlatformAdmin: hasPlatformAdminAccess }),
        )
        : 0;
      const dodProgress = hasDodCompany
        ? complianceProgressPercent(
          DOD_CONTRACTS_COMPLIANCE_CONSOLE,
          dodConsoleState,
          planFromFeatures(effectiveFeatureList, { isPlatformAdmin: hasPlatformAdminAccess }),
        )
        : 0;
      const educationProgress = hasEducationCompany
        ? complianceProgressPercent(EDUCATION_COMPLIANCE_CONSOLE, educationConsoleState)
        : 0;

      return (
        <section className="dashboard-settings-sections dashboard-settings-sections-centered" aria-label={copy.dashboardsSection}>
          <article className="card settings-card">
            <div className="settings-card-header">
              <h2>{copy.dashboards}</h2>
            </div>
            <p className="dashboard-panel-subtitle">
              {language === 'es'
                ? `Panel de ${companyName}: métricas de las funciones incluidas en tu plan.`
                : `${companyName} dashboard: live-style metrics for features included in your plan.`}
            </p>
            <div className="dashboard-widget-grid" role="list">
              {canAccessOfac ? (
                <article className="dashboard-metric-widget" role="listitem" aria-label="OFAC">
                  <header className="dashboard-metric-widget__head">
                    <span className="dashboard-nav-icon tone-cyan" aria-hidden="true"><NavIcon name="ofac" /></span>
                    <div>
                      <strong>OFAC</strong>
                      <span>
                        {ofacHomeHasData
                          ? (language === 'es' ? 'Cobertura de screening y excepciones' : 'Screening coverage and exceptions')
                          : (language === 'es' ? 'Feed de licencias + screening vía Investigación' : 'License feed + screening via Research')}
                      </span>
                    </div>
                    {ofacHomeHasData ? (
                      <span className="dashboard-metric-widget__badge is-ok">live</span>
                    ) : (
                      <span className="dashboard-metric-widget__badge">{language === 'es' ? 'Sin datos' : 'No data'}</span>
                    )}
                  </header>
                  <div className="dashboard-metric-kpi-row">
                    {ofacMetrics.map((metric) => (
                      <div key={metric.label} className={`dashboard-metric-kpi tone-${metric.tone}`}>
                        <em>{metric.value}</em>
                        <span>{metric.label}</span>
                      </div>
                    ))}
                  </div>
                  {ofacHomeHasData && ofacHomeVolumePoints.some((value) => value > 0) ? (
                    <svg className="dashboard-metric-spark" viewBox="0 0 220 48" role="img" aria-label={language === 'es' ? 'Tendencia de screening' : 'Screening trend'}>
                      <polyline fill="none" stroke="#1a7a4c" strokeWidth="2.5" points={ofacSparklinePoints(ofacHomeVolumePoints, 0, 220, 8, 40)} />
                      {ofacHomeExceptionPoints.length ? (
                        <polyline fill="none" stroke="#c45c12" strokeWidth="2" strokeDasharray="4 3" points={ofacSparklinePoints(ofacHomeExceptionPoints, 0, 220, 8, 40)} />
                      ) : null}
                    </svg>
                  ) : null}
                  <p className="dashboard-metric-widget__footnote">
                    {ofacHomeHasData
                      ? (language === 'es'
                        ? 'KPIs en vivo desde sujetos de screening OFAC.'
                        : 'Live KPIs from OFAC screening subjects.')
                      : (language === 'es'
                        ? 'Importa CSV en Screening y ejecuta Screen now para poblar estos KPIs.'
                        : 'Import a CSV on Screening and run Screen now to populate these KPIs.')}
                  </p>
                  <div className="dashboard-action-buttons" style={{ marginTop: '0.65rem', flexWrap: 'wrap' }}>
                    <button type="button" className="dashboard-security-inline-btn" onClick={() => handleNavSelection('ofac')}>
                      {language === 'es' ? 'Abrir panel OFAC' : 'Open OFAC dashboard'}
                    </button>
                    <button type="button" className="dashboard-security-inline-btn" onClick={() => handleNavSelection('ofacLiveFeed')}>
                      {language === 'es' ? 'Feed en vivo' : 'Live Feed'}
                    </button>
                  </div>
                </article>
              ) : null}

              {canAccessResearch ? (
                <article className="dashboard-metric-widget" role="listitem" aria-label="Vantage">
                  <header className="dashboard-metric-widget__head">
                    <span className="dashboard-nav-icon tone-green" aria-hidden="true"><NavIcon name="research" /></span>
                    <div>
                      <strong>{copy.navResearch}</strong>
                      <span>{language === 'es' ? 'Métricas de verificaciones de antecedentes' : 'Background check metrics'}</span>
                    </div>
                  </header>
                  <div className="dashboard-metric-bar-list" aria-label={language === 'es' ? 'Estado de verificaciones' : 'Check status mix'}>
                    {vantageBars.map((bar) => {
                      const widthPct = Math.min(100, Math.round((bar.value / Math.max(bar.max, 1)) * 100));
                      return (
                        <div key={bar.label} className="dashboard-metric-bar-row">
                          <div className="dashboard-metric-bar-meta">
                            <span>{bar.label}</span>
                            <strong>{bar.value}</strong>
                          </div>
                          <div className="dashboard-metric-bar-track" aria-hidden="true">
                            <span className="dashboard-metric-bar-fill" style={{ width: `${widthPct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <button type="button" className="dashboard-security-inline-btn" onClick={() => handleNavSelection('research')}>
                    {language === 'es' ? 'Abrir investigación' : 'Open research'}
                  </button>
                </article>
              ) : null}

              {canAccessWebsiteCompliance ? (
                <article className="dashboard-metric-widget" role="listitem" aria-label={copy.navWebsiteCompliance}>
                  <header className="dashboard-metric-widget__head">
                    <span className="dashboard-nav-icon tone-blue" aria-hidden="true"><NavIcon name="websiteCompliance" /></span>
                    <div>
                      <strong>{copy.navWebsiteCompliance}</strong>
                      <span>{language === 'es' ? 'Progreso del checklist' : 'Checklist progress'}</span>
                    </div>
                  </header>
                  {hasWebsiteCompany ? (
                    <>
                      <div className="dashboard-metric-ring-wrap">
                        <svg viewBox="0 0 84 84" className="dashboard-metric-ring" role="img" aria-label={`${websiteProgress}%`}>
                          <circle cx="42" cy="42" r="32" fill="none" stroke="var(--color-border)" strokeWidth="8" />
                          <circle
                            cx="42"
                            cy="42"
                            r="32"
                            fill="none"
                            stroke="#2f6fed"
                            strokeWidth="8"
                            strokeLinecap="round"
                            strokeDasharray={`${(websiteProgress / 100) * 201} 201`}
                            transform="rotate(-90 42 42)"
                          />
                          <text x="42" y="46" textAnchor="middle" fontSize="16" fontWeight="700" fill="currentColor">{websiteProgress}%</text>
                        </svg>
                        <ul>
                          <li>{language === 'es' ? 'Políticas publicadas' : 'Published policies'}</li>
                          <li>{language === 'es' ? 'Evidencia pendiente' : 'Evidence outstanding'}</li>
                          <li>{language === 'es' ? 'Revisión trimestral' : 'Quarterly review'}</li>
                        </ul>
                      </div>
                      <button type="button" className="dashboard-security-inline-btn" onClick={() => handleNavSelection('websiteCompliance')}>
                        {language === 'es' ? 'Abrir cumplimiento web' : 'Open website compliance'}
                      </button>
                    </>
                  ) : (
                    <div className="dashboard-metric-empty" role="status">
                      <strong>{language === 'es' ? 'Añade una empresa para empezar' : 'Add a company to get started'}</strong>
                      <p>
                        {language === 'es'
                          ? 'Sin una empresa vinculada no hay checklist que medir. Configura la organización que quieres evaluar y verás el progreso aquí.'
                          : 'Without a linked company there is nothing to measure yet. Specify the organization you want to assess, then checklist progress will appear here.'}
                      </p>
                      <button type="button" className="dashboard-btn dashboard-btn-primary" onClick={() => handleNavSelection('websiteCompliance')}>
                        {language === 'es' ? 'Configurar cumplimiento web' : 'Set up website compliance'}
                      </button>
                    </div>
                  )}
                </article>
              ) : null}

              {canAccessDodContracts ? (
                <article className="dashboard-metric-widget" role="listitem" aria-label={copy.navDodContracts}>
                  <header className="dashboard-metric-widget__head">
                    <span className="dashboard-nav-icon tone-indigo" aria-hidden="true"><NavIcon name="dodContracts" /></span>
                    <div>
                      <strong>{copy.navDodContracts}</strong>
                      <span>{language === 'es' ? 'Preparación contractual' : 'Contract readiness'}</span>
                    </div>
                  </header>
                  {hasDodCompany ? (
                    <>
                      <div className="dashboard-metric-ring-wrap">
                        <svg viewBox="0 0 84 84" className="dashboard-metric-ring" role="img" aria-label={`${dodProgress}%`}>
                          <circle cx="42" cy="42" r="32" fill="none" stroke="var(--color-border)" strokeWidth="8" />
                          <circle
                            cx="42"
                            cy="42"
                            r="32"
                            fill="none"
                            stroke="#4f46e5"
                            strokeWidth="8"
                            strokeLinecap="round"
                            strokeDasharray={`${(dodProgress / 100) * 201} 201`}
                            transform="rotate(-90 42 42)"
                          />
                          <text x="42" y="46" textAnchor="middle" fontSize="16" fontWeight="700" fill="currentColor">{dodProgress}%</text>
                        </svg>
                        <ul>
                          <li>{language === 'es' ? 'Controles mapeados' : 'Controls mapped'}</li>
                          <li>{language === 'es' ? 'Brechas abiertas' : 'Open gaps'}</li>
                          <li>{language === 'es' ? 'Paquete de evidencia' : 'Evidence pack'}</li>
                        </ul>
                      </div>
                      <button type="button" className="dashboard-security-inline-btn" onClick={() => handleNavSelection('dodContracts')}>
                        {language === 'es' ? 'Abrir DoD' : 'Open DoD contracts'}
                      </button>
                    </>
                  ) : (
                    <div className="dashboard-metric-empty" role="status">
                      <strong>{language === 'es' ? 'Añade una empresa contratista' : 'Add a contracting company'}</strong>
                      <p>
                        {language === 'es'
                          ? 'El panel DoD se activa cuando especificas la empresa cuyos controles y evidencias vas a rastrear.'
                          : 'The DoD panel activates once you specify the company whose controls and evidence you will track.'}
                      </p>
                      <button type="button" className="dashboard-btn dashboard-btn-primary" onClick={() => handleNavSelection('dodContracts')}>
                        {language === 'es' ? 'Configurar cumplimiento DoD' : 'Set up DoD compliance'}
                      </button>
                    </div>
                  )}
                </article>
              ) : null}

              {canAccessEducationCompliance ? (
                <article className="dashboard-metric-widget" role="listitem" aria-label={copy.navEducationCompliance}>
                  <header className="dashboard-metric-widget__head">
                    <span className="dashboard-nav-icon tone-green" aria-hidden="true"><NavIcon name="educationCompliance" /></span>
                    <div>
                      <strong>{copy.navEducationCompliance}</strong>
                      <span>{language === 'es' ? 'Programa de campus' : 'Campus program'}</span>
                    </div>
                  </header>
                  {hasEducationCompany ? (
                    <>
                      <div className="dashboard-metric-ring-wrap">
                        <svg viewBox="0 0 84 84" className="dashboard-metric-ring" role="img" aria-label={`${educationProgress}%`}>
                          <circle cx="42" cy="42" r="32" fill="none" stroke="var(--color-border)" strokeWidth="8" />
                          <circle
                            cx="42"
                            cy="42"
                            r="32"
                            fill="none"
                            stroke="#059669"
                            strokeWidth="8"
                            strokeLinecap="round"
                            strokeDasharray={`${(educationProgress / 100) * 201} 201`}
                            transform="rotate(-90 42 42)"
                          />
                          <text x="42" y="46" textAnchor="middle" fontSize="16" fontWeight="700" fill="currentColor">{educationProgress}%</text>
                        </svg>
                        <ul>
                          <li>{language === 'es' ? 'Partes restringidas' : 'Restricted parties'}</li>
                          <li>{language === 'es' ? 'Exportación / CUI' : 'Export / CUI'}</li>
                          <li>{language === 'es' ? 'HECVAT y FERPA' : 'HECVAT and FERPA'}</li>
                        </ul>
                      </div>
                      <button type="button" className="dashboard-security-inline-btn" onClick={() => handleNavSelection('educationCompliance')}>
                        {language === 'es' ? 'Abrir educación' : 'Open education compliance'}
                      </button>
                    </>
                  ) : (
                    <div className="dashboard-metric-empty" role="status">
                      <strong>{language === 'es' ? 'Añade el campus o la oficina de investigación' : 'Add the campus or research office'}</strong>
                      <p>
                        {language === 'es'
                          ? 'El panel de educación se activa cuando especificas la institución cuyos controles de campus vas a rastrear.'
                          : 'The education panel activates once you specify the institution whose campus controls you will track.'}
                      </p>
                      <button type="button" className="dashboard-btn dashboard-btn-primary" onClick={() => handleNavSelection('educationCompliance')}>
                        {language === 'es' ? 'Configurar cumplimiento educativo' : 'Set up education compliance'}
                      </button>
                    </div>
                  )}
                </article>
              ) : null}
            </div>
          </article>
        </section>
      );
    }

    return (
    <section className="dashboard-settings-sections" aria-label={copy.dashboardsSection}>
      <article className="card settings-card dashboards-card">
        <div className="dashboards-header-row">
          <div className="dashboards-title-wrap">
            <h2>{copy.dashboards}</h2>
          </div>
          <button
            type="button"
            className={`dashboard-subscriptions-btn ${isDashboardSubscriptionsOpen ? 'open' : ''}`}
            aria-label={copy.toggleDashboardSubscriptions}
            aria-expanded={isDashboardSubscriptionsOpen}
            onClick={() => setIsDashboardSubscriptionsOpen((current) => !current)}
          >
            <span>{copy.updateSubscriptions}</span>
            <AccordionChevronIcon />
          </button>
        </div>
        <p className="dashboards-copy">
          {language === 'es'
            ? 'Salud de los servicios de la plataforma (puerta de enlace, investigación, correo). El trabajo de tu suscripción está en Inicio.'
            : 'Platform service health (gateway, research, mail). Your subscription work is on Home.'}
        </p>

        <div className="dashboard-service-filter-block" aria-label={copy.dashboardServiceFilterTitle}>
          <p className="dashboard-service-filter-title">{copy.dashboardServiceFilterTitle}</p>
          <div className="dashboard-service-filter-row">
            <button
              type="button"
              className={`dashboard-service-filter-btn ${dashboardServiceVisibility.ofac ? 'active' : ''}`}
              onClick={() => toggleDashboardServiceVisibility('ofac')}
              aria-pressed={dashboardServiceVisibility.ofac}
            >
              {copy.dashboardFilterOfac}
            </button>
            <button
              type="button"
              className={`dashboard-service-filter-btn ${dashboardServiceVisibility.websiteCompliance ? 'active' : ''}`}
              onClick={() => toggleDashboardServiceVisibility('websiteCompliance')}
              aria-pressed={dashboardServiceVisibility.websiteCompliance}
            >
              {copy.dashboardFilterWebsiteCompliance}
            </button>
            <button
              type="button"
              className={`dashboard-service-filter-btn ${dashboardServiceVisibility.governmentalCompliance ? 'active' : ''}`}
              onClick={() => toggleDashboardServiceVisibility('governmentalCompliance')}
              aria-pressed={dashboardServiceVisibility.governmentalCompliance}
            >
              {copy.dashboardFilterGovernmentalCompliance}
            </button>
          </div>
        </div>

        {isDashboardSubscriptionsOpen && (
          <div className="dashboard-subscription-table-wrap">
            {availableDashboards.length > 0 ? (
              <ul className="dashboard-subscription-list" aria-label={copy.dashboardSubscriptions}>
                {availableDashboards.map((dashboardName) => (
                  <li key={dashboardName} className="dashboard-subscription-row">
                    <div className="dashboard-subscription-service-meta">
                      <span className="dashboard-subscription-service-name">{dashboardName}</span>
                      {getServiceTags(parseServiceDashboardKey(dashboardName)).length > 0 && (
                        <div className="dashboard-service-tags">
                          {getServiceTags(parseServiceDashboardKey(dashboardName)).map((tag) => (
                            <span key={`${dashboardName}-${tag}`} className="dashboard-service-tag">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={dashboardSubscriptions[dashboardName]}
                      onChange={() => toggleDashboardSubscription(dashboardName)}
                      aria-label={`Toggle ${dashboardName} subscription`}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="dashboard-subscribed-empty">
                {rawAvailableDashboards.length > 0 ? copy.noVisibleDashboards : copy.noServiceDashboards}
              </p>
            )}
          </div>
        )}

        <section className="dashboard-live-section" aria-label="Live dashboards">
          {availableDashboards.length === 0 ? (
            <p>{rawAvailableDashboards.length > 0 ? copy.noVisibleDashboards : copy.noServiceDashboards}</p>
          ) : subscribedDashboards.length === 0 ? (
            <p>{copy.noSubscribedDashboards}</p>
          ) : (
            subscribedDashboards.map((dashboardName) => {
              const matchedDashboardService = dashboardServices.find(
                (serviceRecord) => formatServiceDashboardName(serviceRecord.service_name) === dashboardName,
              );
              const serviceKey = matchedDashboardService?.service_name ?? parseServiceDashboardKey(dashboardName);
              const legalAreaInput = legalExposureAreaByService[serviceKey];
              const fallbackLegalService: MetricsDashboardService | undefined = !matchedDashboardService && legalAreaInput
                ? {
                  service_name: serviceKey,
                  status: legalAreaInput.status,
                  response_time_ms: legalAreaInput.response_time_ms,
                  requests_total: Array.isArray(legalAreaInput.requirements) ? legalAreaInput.requirements.length : 0,
                  requests_success: Array.isArray(legalAreaInput.evidence) ? legalAreaInput.evidence.length : 0,
                  error_rate: undefined,
                  risk_level: undefined,
                  compliance_percentage: legalAreaInput.compliance_percentage,
                  evidence_items: Array.isArray(legalAreaInput.evidence) ? legalAreaInput.evidence : [],
                  lacking_items: Array.isArray(legalAreaInput.lacking) ? legalAreaInput.lacking : [],
                  website_signal: legalAreaInput.website_signal,
                  metrics_source_required: legalAreaInput.metrics_source_required,
                }
                : undefined;
              const service = matchedDashboardService ?? fallbackLegalService;
              const serviceDataSummary = metricsServiceData?.[serviceKey];
              const serviceStatus = service?.status ?? serviceDataSummary?.status ?? 'unknown';
              const hasPulledServiceData = Boolean(service || serviceDataSummary);
              const isLegalComplianceService = isLegalComplianceServiceKey(serviceKey);
              const legalCounts = deriveLegalComplianceCounts(service, legalAreaInput);
              const primaryMetric = buildPrimaryMetric(serviceKey, service, serviceDataSummary, legalAreaInput);
              const tileIcon = getServiceTileIcon(serviceKey);
              const hasActualLiveData = Boolean(
                (serviceDataSummary?.live_count ?? 0) > 0 ||
                ((service?.requests_total ?? 0) > 0 && (service?.requests_success ?? 0) > 0),
              );
              const isCardLive = hasActualLiveData && isLiveDataFresh;
              const successRate = isLegalComplianceService && legalCounts.compliancePercentage !== undefined
                ? legalCounts.compliancePercentage
                : (((service?.requests_success ?? 0) / Math.max(service?.requests_total ?? 1, 1)) * 100);

              return (
                <a
                  key={`live-${dashboardName}`}
                  className="dashboard-live-card"
                  href={getDashboardDetailPath(serviceKey)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${dashboardName} details in a new tab`}
                >
                  <div className="dashboard-live-card-head">
                    <div className="dashboard-live-card-icon" aria-hidden="true">{tileIcon}</div>
                    <div className="dashboard-live-card-primary">
                      <div className="dashboard-live-card-title-row">
                        <h3>{dashboardName}</h3>
                        {isCardLive && (
                          <span
                            className="dashboard-card-live-btn live"
                            aria-label={`${dashboardName} live`}
                          >
                            <span className="dashboard-live-dot live" aria-hidden="true" />
                            <span>{copy.dashboardLiveBadge}</span>
                          </span>
                        )}
                      </div>
                      {getServiceTags(serviceKey).length > 0 && (
                        <div className="dashboard-service-tags">
                          {getServiceTags(serviceKey).map((tag) => (
                            <span key={`${dashboardName}-${tag}`} className="dashboard-service-tag">{tag}</span>
                          ))}
                        </div>
                      )}
                      <p className="dashboard-primary-metric">
                        <strong>{primaryMetric.value}</strong>
                        {primaryMetric.unit && <span>{primaryMetric.unit}</span>}
                      </p>
                      <p className="dashboard-primary-caption">{primaryMetric.caption}</p>
                    </div>
                  </div>
                  {metricsLastUpdated && <p className="dashboard-updated-time">{copy.metricLabelLastUpdated}: {metricsLastUpdated.toLocaleTimeString()}</p>}
                  {hasPulledServiceData ? (
                    <ul className="dashboard-service-details-list">
                      <li className="dashboard-service-details-item">
                        <span>{copy.metricLabelStatus}: {formatServiceStatusLabel(serviceKey, serviceStatus)}</span>
                        {service && <span>{copy.metricLabelResponseTime}: {(finiteNumber(service.response_time_ms) ?? 0).toFixed(2)}ms</span>}
                        {service && (
                          <span>
                            {copy.metricLabelSuccessRate}: {successRate.toFixed(1)}%
                          </span>
                        )}
                        {isLegalComplianceService && legalCounts.total > 0 && (
                          <span>{copy.metricLabelCompliantRecords}: {legalCounts.compliant.toLocaleString()}</span>
                        )}
                        {isLegalComplianceService && legalCounts.total > 0 && (
                          <span>{copy.metricLabelNonCompliantRecords}: {legalCounts.nonCompliant.toLocaleString()}</span>
                        )}
                        {isLegalComplianceService && legalCounts.total > 0 && (
                          <span>{copy.metricLabelTotalRequirements}: {legalCounts.total.toLocaleString()}</span>
                        )}
                        {serviceKey === TERMS_REPLAY_SERVICE_KEY && termsReplayGateStatus && (
                          <span>{copy.metricLabelTermsReplayGate}: {termsReplayGateStatus}</span>
                        )}
                        {serviceKey === TERMS_REPLAY_SERVICE_KEY && termsPageAccessGateStatus && (
                          <span>{copy.metricLabelTermsPageAccessGate}: {termsPageAccessGateStatus}</span>
                        )}
                        {serviceKey === TERMS_REPLAY_SERVICE_KEY && contractUpdateNoticeGateStatus && (
                          <span>{copy.metricLabelContractUpdateGate}: {contractUpdateNoticeGateStatus}</span>
                        )}
                        {serviceKey === TERMS_REPLAY_SERVICE_KEY && authenticatedEntrypointAssentGateStatus && (
                          <span>{copy.metricLabelAuthenticatedEntrypointGate}: {authenticatedEntrypointAssentGateStatus}</span>
                        )}
                        {serviceKey === TERMS_REPLAY_SERVICE_KEY && negotiatedContractTermsGateStatus && (
                          <span>{copy.metricLabelNegotiatedContractTermsGate}: {negotiatedContractTermsGateStatus}</span>
                        )}
                        {serviceKey === TERMS_REPLAY_SERVICE_KEY && publicTermsPageGateStatus && (
                          <span>{copy.metricLabelPublicTermsPageGate}: {publicTermsPageGateStatus}</span>
                        )}
                        {serviceKey === TERMS_REPLAY_SERVICE_KEY && assentReplayStatus && (
                          <span>{copy.metricLabelAssentReplay}: {assentReplayStatus}</span>
                        )}
                        {serviceKey === TERMS_REPLAY_SERVICE_KEY && assentReplaySummary && (
                          <span>
                            {copy.metricLabelAssentReplayEvents}: {Number(assentReplaySummary.recent_events ?? 0).toLocaleString()}/{Number(assentReplaySummary.minimum_recent_events_required ?? 1).toLocaleString()}
                          </span>
                        )}
                        {service && <span>{copy.metricLabelErrorRate}: {((finiteNumber(service.error_rate) ?? 0) * 100).toFixed(1)}%</span>}
                        {service && (
                          <span style={{ color: getHealthColor(serviceStatus) }}>{copy.metricLabelRiskLevel}: {formatServiceRisk(service.risk_level)}</span>
                        )}
                        {finiteNumber(service?.compliance_percentage) !== null && (
                          <span>{copy.metricLabelCompliance}: {finiteNumber(service?.compliance_percentage)?.toFixed(1)}%</span>
                        )}
                        {service?.website_signal && (
                          <span>{copy.metricLabelWebsiteSignal}: {service.website_signal}</span>
                        )}
                        {service?.confluence_signal && (
                          <span>{copy.metricLabelConfluenceSignal}: {service.confluence_signal.toUpperCase()}</span>
                        )}
                        {service?.metrics_source_required && (
                          <span>{copy.metricLabelMetricsSource}: {service.metrics_source_required.toUpperCase()}</span>
                        )}
                        {finiteNumber(service?.website_scan_coverage) !== null && (
                          <span>{copy.metricLabelWebsiteScanCoverage}: {finiteNumber(service?.website_scan_coverage)?.toFixed(1)}%</span>
                        )}
                        {service?.website_scan_status && (
                          <span>{copy.metricLabelWebsiteScanStatus}: {service.website_scan_status.toUpperCase()}</span>
                        )}
                        {service?.evidence_items && service.evidence_items.length > 0 && (
                          <span>{copy.metricLabelEvidence}: {service.evidence_items.slice(0, 2).join(' • ')}</span>
                        )}
                        {service?.lacking_items && service.lacking_items.length > 0 && (
                          <span>{copy.metricLabelGaps}: {service.lacking_items.slice(0, 2).join(' • ')}</span>
                        )}
                        {serviceDataSummary && (
                          <span>
                            {copy.metricLabelDataCount}: {Math.max(serviceDataSummary.live_count ?? 0, serviceDataSummary.reliable_count ?? serviceDataSummary.count ?? 0).toLocaleString()} {copy.metricUnitRecords}
                          </span>
                        )}
                        {serviceDataSummary?.percent_scraped !== undefined && (
                          <span>{copy.metricLabelCoverage}: {serviceDataSummary.percent_scraped}%</span>
                        )}
                      </li>
                    </ul>
                  ) : (
                    <p>{copy.noLiveMetrics}</p>
                  )}
                </a>
              );
            })
          )}
        </section>

        {aiInsightsData && (
          <section className="dashboard-ai-section" aria-label="AI insights and forecasts">
            <h3>{language === 'es' ? 'Informes de IA' : 'AI Insights'}</h3>
            <p className="dashboard-ai-meta">
              {language === 'es' ? 'Modelo' : 'Model'}: {aiInsightsData.model_version} | {language === 'es' ? 'Riesgo IA general' : 'Overall AI risk'}: {finiteNumber(aiInsightsData.overall_risk_score)?.toFixed(1) ?? '--'}
            </p>

            <div className="dashboard-live-section">
              {(aiInsightsData.top_risks ?? []).slice(0, 3).map((risk) => {
                const deep = deepLearningByService[risk.service_name];
                const forecast = forecastByService[risk.service_name];
                return (
                  <article key={`ai-${risk.service_name}`} className="dashboard-live-card">
                    <div className="dashboard-live-card-title-row">
                      <h4>{formatServiceDashboardName(risk.service_name)}</h4>
                      <span className="dashboard-service-tag">{risk.risk_band}</span>
                    </div>
                    <p className="dashboard-primary-caption">
                      {language === 'es' ? 'Riesgo estimado' : 'Estimated risk'}: {finiteNumber(risk.predicted_risk_score)?.toFixed(1) ?? '--'}
                    </p>
                    {deep && (
                      <p className="dashboard-primary-caption">
                        {language === 'es' ? 'Modelo profundo' : 'Deep model'}: {finiteNumber(deep.predicted_risk_score)?.toFixed(1) ?? '--'} ({((finiteNumber(deep.confidence) ?? 0) * 100).toFixed(0)}%)
                      </p>
                    )}
                    {forecast && (
                      <p className="dashboard-primary-caption">
                        {language === 'es' ? 'Tendencia 3 dias' : '3-day trend'}: {(finiteNumber(forecast.daily_trend) ?? 0) >= 0 ? '+' : ''}{(finiteNumber(forecast.daily_trend) ?? 0).toFixed(2)} / {language === 'es' ? 'dia' : 'day'}
                      </p>
                    )}
                    <p className="dashboard-primary-caption">
                      {(risk.recommended_actions ?? [])[0] ?? (language === 'es' ? 'Sin accion recomendada' : 'No recommended action')}
                    </p>
                    <button
                      type="button"
                      className="dashboard-ai-drilldown-toggle"
                      onClick={() => setExpandedAiInsight(
                        expandedAiInsight === risk.service_name ? null : risk.service_name,
                      )}
                    >
                      {expandedAiInsight === risk.service_name
                        ? (language === 'es' ? 'Ocultar detalle' : 'Hide rationale & evidence')
                        : (language === 'es' ? 'Ver detalle' : 'View rationale & evidence')}
                    </button>
                    {expandedAiInsight === risk.service_name && (
                      <div className="dashboard-ai-drilldown-panel">
                        {(risk.rationale ?? []).length > 0 && (
                          <div className="dashboard-ai-drilldown-block">
                            <strong>{language === 'es' ? 'Razonamiento' : 'Rationale'}</strong>
                            <ul>
                              {(risk.rationale ?? []).map((line) => (
                                <li key={`${risk.service_name}-rationale-${line}`}>{line}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {(risk.evidence ?? []).length > 0 && (
                          <div className="dashboard-ai-drilldown-block">
                            <strong>{language === 'es' ? 'Evidencia' : 'Evidence'}</strong>
                            <ul>
                              {(risk.evidence ?? []).map((line) => (
                                <li key={`${risk.service_name}-evidence-${line}`}>{line}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {(risk.recommended_actions ?? []).length > 0 && (
                          <div className="dashboard-ai-drilldown-block">
                            <strong>{language === 'es' ? 'Acciones recomendadas' : 'Recommended actions'}</strong>
                            <ul>
                              {(risk.recommended_actions ?? []).map((line) => (
                                <li key={`${risk.service_name}-action-${line}`}>{line}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>

            <div className="dashboard-ai-nlp-card">
              <h4>{language === 'es' ? 'Analizador NLP de cumplimiento' : 'NLP Compliance Analyzer'}</h4>
              <textarea
                className="dashboard-ai-nlp-input"
                value={nlpInput}
                onChange={(event) => setNlpInput(event.target.value)}
                placeholder={language === 'es' ? 'Pega notas de incidentes o texto legal para analizar' : 'Paste incident notes or legal text to analyze'}
              />
              <button type="button" className="dashboard-ai-run-btn" onClick={runNlpAnalysis} disabled={!nlpInput.trim() || isNlpLoading}>
                {isNlpLoading ? (language === 'es' ? 'Analizando...' : 'Analyzing...') : (language === 'es' ? 'Ejecutar NLP' : 'Run NLP')}
              </button>
              {nlpAnalysis && (
                <div className="dashboard-ai-nlp-results">
                  <span>{language === 'es' ? 'Sentimiento' : 'Sentiment'}: {nlpAnalysis.sentiment}</span>
                  <span>{language === 'es' ? 'Urgencia' : 'Urgency'}: {(nlpAnalysis.urgency_score * 100).toFixed(0)}%</span>
                  <span>{language === 'es' ? 'Senales' : 'Signals'}: {(nlpAnalysis.signals ?? []).slice(0, 3).map((signal) => signal.name).join(', ') || '-'}</span>
                  <span>{language === 'es' ? 'Accion' : 'Action'}: {(nlpAnalysis.recommended_actions ?? [])[0] ?? '-'}</span>
                </div>
              )}
            </div>
          </section>
        )}
      </article>
    </section>
    );
  };

  const renderHomePane = (): React.ReactNode => {
    const search = new URLSearchParams(location.search);
    const showOnboard = search.get('onboard') === '1' || search.get('checkout') === 'success';
    const workViewRaw = (search.get('home') || 'dashboard').toLowerCase();
    const workView = workViewRaw === 'gaps' || workViewRaw === 'intake' ? workViewRaw : 'dashboard';
    const taskTabRaw = (search.get('tasks') || 'all').toLowerCase();
    const taskTab = taskTabRaw === 'mine' || taskTabRaw === 'overdue' ? taskTabRaw : 'all';
    const onboardDismissed = (() => {
      try {
        return window.localStorage.getItem('nextcompli.onboard.dismissed') === '1';
      } catch {
        return false;
      }
    })();
    const onboardVisible = showOnboard && !onboardDismissed && !hasSuperUserAccess;
    const plan = planFromFeatures(effectiveFeatureList, { isPlatformAdmin: hasPlatformAdminAccess });
    const signedInEmail = String(accountHome?.email || user?.email || '').trim().toLowerCase();
    const homeDefinition = (kind: string): ComplianceConsoleDefinition | null => {
      if (kind === 'website-compliance') return canAccessWebsiteCompliance ? WEBSITE_COMPLIANCE_CONSOLE : null;
      if (kind === 'dod-contracts-compliance') return canAccessDodContracts ? DOD_CONTRACTS_COMPLIANCE_CONSOLE : null;
      if (kind === 'education-compliance') return canAccessEducationCompliance ? EDUCATION_COMPLIANCE_CONSOLE : null;
      if (kind === 'ai-compliance') return canAccessAiCompliance ? AI_COMPLIANCE_CONSOLE : null;
      if (kind === 'cloud-compliance') return canAccessCloudCompliance ? CLOUD_COMPLIANCE_CONSOLE : null;
      if (kind === 'business-creation-compliance') return canAccessBusinessFormation ? BUSINESS_CREATION_COMPLIANCE_CONSOLE : null;
      if (kind === 'wills-trusts-compliance') return canAccessWillsTrusts ? WILLS_TRUSTS_COMPLIANCE_CONSOLE : null;
      if (kind === 'ofac-compliance') return canAccessOfac ? OFAC_COMPLIANCE_CONSOLE : null;
      return null;
    };
    const homeworkOwners = new Map<string, string>();
    const homework = (accountHome?.accounts || []).flatMap((account) => (
      account.consoles.flatMap((consoleRow) => {
        const definition = homeDefinition(consoleRow.console_kind);
        if (!definition) {
          return [];
        }
        const savedIds = new Set(consoleRow.items.map((item) => item.item_id));
        const generatedPillars = consoleRow.console_kind === 'business-creation-compliance'
          ? businessCreationPillars(consoleRow.business_creation || {}, language === 'es' ? 'es' : 'en')
          : consoleRow.console_kind === 'wills-trusts-compliance'
            ? willsTrustsPillars(parseWillsTrustsAnswers(consoleRow.wills_trusts || {}), language === 'es' ? 'es' : 'en')
            : [];
        const rowDefinition = generatedPillars.length
          ? { ...definition, pillars: [...generatedPillars, ...definition.pillars] }
          : definition;
        if (savedIds.size === 0 && generatedPillars.length === 0) {
          return [];
        }
        const state = emptyComplianceState();
        state.targetCompanyName = consoleRow.target_company_name || account.company_name;
        consoleRow.items.forEach((item) => {
          const status = item.status;
          if (status !== 'not_started' && status !== 'in_progress' && status !== 'reviewed' && status !== 'gap') {
            return;
          }
          const stepsDone = Array.isArray(item.steps_done) ? item.steps_done : [];
          state.items[item.item_id] = {
            status,
            notes: '',
            stepProgress: Object.fromEntries(stepsDone.map((stepId) => [stepId, { done: true }])),
          };
          if (item.owner) {
            homeworkOwners.set(`${consoleRow.console_kind}:${item.item_id}`, item.owner);
          }
        });
        const namedProject = account.projects.find((project) => project.console_kind === consoleRow.console_kind)
          || account.projects.find((project) => project.project_name)
          || account.projects[0];
        const projectName = namedProject?.project_name || '';
        const projectId = namedProject?.project_id || '';
        return collectHomeworkRows(rowDefinition, state, plan)
          .filter((row) => savedIds.size === 0 || savedIds.has(row.itemId))
          .map((row) => ({
            ...row,
            companyId: account.company_id,
            companyName: account.company_name,
            projectId,
            projectName,
            depth: checklistDependencyDepth(definition, row.itemId),
            openDeps: (() => {
              const item = findChecklistItem(definition, row.itemId);
              return item ? checklistDependencyStates(definition, state, item).filter((dep) => !dep.done).length : 0;
            })(),
            waitsOutside: (() => {
              const item = findChecklistItem(definition, row.itemId);
              return Boolean(item && (item.completeRequires === 'uei-cage' || isDodWaitingOnExternal(item, state)));
            })(),
            outsideWait: (() => {
              const item = findChecklistItem(rowDefinition, row.itemId);
              return item ? outsideWaitFor(item, state) : null;
            })(),
            heldOutside: (() => {
              const item = findChecklistItem(rowDefinition, row.itemId);
              return Boolean(item && isDodWaitingOnExternal(item, state) && !outsideWaitFor(item, state));
            })(),
            checkUrl: (() => {
              const item = findChecklistItem(rowDefinition, row.itemId);
              return item?.sourceUrl || item?.docsUrl || '';
            })(),
            unlocks: definition.pillars.reduce((count, pillar) => (
              count + pillar.items.filter((item) => (item.dependsOn || []).includes(row.itemId)).length
            ), 0),
            dueDate: publishedDeadlinesFor(row.consoleKind).find((deadline) => deadline.itemId === row.itemId)?.date || '',
          }));
      })
    ));
    const serverProjects = (accountHome?.accounts || []).flatMap((account) => account.projects);
    const namedCompany = serverProjects.length > 0 || (accountHome?.accounts || []).some((account) => (
      account.consoles.some((consoleRow) => String(consoleRow.target_company_name || account.company_name || '').trim())
    ));
    const websiteState = { targetCompanyName: (accountHome?.accounts || []).flatMap((account) => account.consoles).find((row) => row.console_kind === 'website-compliance')?.target_company_name || '' };
    const dodState = { targetCompanyName: (accountHome?.accounts || []).flatMap((account) => account.consoles).find((row) => row.console_kind === 'dod-contracts-compliance')?.target_company_name || '' };
    const ownerLabel = signedInEmail || (language === 'es' ? 'Admin de la cuenta' : 'Account admin');
    const ofacQueue = (canAccessOfac ? ofacComplianceDashboard?.queue : null) || [];
    const tally = homeworkStatusTally(homework);
    const blockingOpen = homework.filter((row) => row.consoleKind === 'dod-contracts-compliance' && row.priority === 'blocking' && row.status !== 'reviewed').length;
    const recommendedOpen = homework.filter((row) => row.priority === 'recommended' && row.status !== 'reviewed').length;
    const gapRows = homework.filter((row) => row.status === 'gap' || (row.priority === 'blocking' && row.status !== 'reviewed'));
    const nextDod = homework.filter((row) => row.consoleKind === 'dod-contracts-compliance' && row.status !== 'reviewed').slice(0, 3);
    const navForKind = (kind: string): DashboardNavItemId => {
      if (kind === 'website-compliance') return 'websiteCompliance';
      if (kind === 'cloud-compliance') return 'cloudCompliance';
      if (kind === 'education-compliance') return 'educationCompliance';
      if (kind === 'ai-compliance') return 'aiCompliance';
      if (kind === 'business-creation-compliance') return 'businessFormation';
      if (kind === 'wills-trusts-compliance') return 'willsTrusts';
      if (kind === 'ofac-compliance') return 'ofacCompliance';
      return 'dodContracts';
    };
    const openHomework = (row: {
      consoleKind: string;
      itemId: string;
      title: string;
      category: string;
      companyId: number;
      companyName: string;
      projectId: string;
      projectName: string;
    }) => {
      if (!homeDefinition(row.consoleKind)) {
        return;
      }
      setHomeTask({
        consoleKind: row.consoleKind,
        itemId: row.itemId,
        title: row.title,
        category: row.category,
        companyId: row.companyId,
        companyName: row.companyName,
        projectId: row.projectId,
        projectName: row.projectName,
      });
    };
    const programId = (kind: string): string => {
      if (kind === 'website-compliance') return 'web';
      if (kind === 'cloud-compliance') return 'cloud';
      if (kind === 'education-compliance') return 'education';
      if (kind === 'ai-compliance') return 'ai';
      if (kind === 'business-creation-compliance') return 'formation';
      if (kind === 'wills-trusts-compliance') return 'wills';
      if (kind === 'ofac-compliance') return 'ofac';
      return 'gov';
    };
    const programLabel = (kind: string): string => {
      if (kind === 'website-compliance') return language === 'es' ? 'Sitio' : 'Website';
      if (kind === 'cloud-compliance') return language === 'es' ? 'Nube' : 'Cloud';
      if (kind === 'education-compliance') return language === 'es' ? 'Educación' : 'Education';
      if (kind === 'ai-compliance') return language === 'es' ? 'IA' : 'AI';
      if (kind === 'business-creation-compliance') return language === 'es' ? 'Formación' : 'Formation';
      if (kind === 'wills-trusts-compliance') return language === 'es' ? 'Testamentos y fideicomisos' : 'Wills & Trusts';
      if (kind === 'ofac-compliance') return language === 'es' ? 'Sanciones' : 'Sanctions';
      return language === 'es' ? 'Gobierno' : 'Government';
    };
    const lens = parseHomeLens(search.get('lens'));
    const setHomeQuery = (next: { home?: string; tasks?: string; lens?: string }) => {
      const params = new URLSearchParams(location.search);
      if (next.home) {
        if (next.home === 'dashboard') params.delete('home');
        else params.set('home', next.home);
      }
      if (next.tasks) {
        if (next.tasks === 'all') params.delete('tasks');
        else params.set('tasks', next.tasks);
      }
      if (next.lens !== undefined) {
        if (!next.lens) params.delete('lens');
        else params.set('lens', next.lens);
      }
      const query = params.toString();
      navigate(`/console/home${query ? `?${query}` : ''}`, { replace: true });
    };
    const matchesLens = (row: { status: string; priority?: string; consoleKind: string }) => rowMatchesHomeLens(row, lens);
    const showOfacRows = !lens || lens === 'program:ofac';
    const tableRows = (taskTab === 'overdue' ? gapRows : homework)
      .filter((row) => {
        if (!matchesLens(row)) return false;
        if (homeStatusFilter === 'dated' && !row.dueDate) return false;
        if (homeStatusFilter !== 'any' && homeStatusFilter !== 'dated' && row.status !== homeStatusFilter) return false;
        if (taskTab !== 'mine') return true;
        const owner = String(homeworkOwners.get(`${row.consoleKind}:${row.itemId}`) || '').trim().toLowerCase();
        return !owner || owner === signedInEmail;
      });
    const homeSortKey = (row: (typeof homework)[number]): string => {
      if (homeTaskSort === 'due') return row.dueDate || '9999-99-99';
      if (homeTaskSort === 'unlocks') return String(1000 - row.unlocks).padStart(4, '0');
      if (homeTaskSort === 'blocking') return `${row.priority === 'blocking' ? '0' : '1'}${String(row.depth).padStart(2, '0')}`;
      return String(row.depth).padStart(2, '0');
    };
    const sortedTableRows = tableRows.slice().sort((left, right) => (
      homeSortKey(left).localeCompare(homeSortKey(right)) || left.title.localeCompare(right.title)
    ));
    const statusCount = tally.not_started + tally.in_progress + tally.reviewed + tally.gap;
    const attentionRows = (lens ? homework.filter((row) => matchesLens(row)) : homework.filter((row) => row.status !== 'reviewed'))
      .filter((row) => !row.outsideWait && !row.heldOutside)
      .slice()
      .sort((left, right) => recommendedWorkRank(left).localeCompare(recommendedWorkRank(right)) || left.title.localeCompare(right.title))
      .slice(0, 6);
    const stackOf = (rows: typeof homework) => {
      const parts = [
        { key: 'gap', value: rows.filter((row) => row.status === 'gap').length, color: '#e24b4b' },
        { key: 'not_started', value: rows.filter((row) => row.status === 'not_started').length, color: '#c9a227' },
        { key: 'in_progress', value: rows.filter((row) => row.status === 'in_progress').length, color: '#4c8dff' },
        { key: 'reviewed', value: rows.filter((row) => row.status === 'reviewed').length, color: '#3dd68c' },
      ];
      return { parts, total: parts.reduce((sum, part) => sum + part.value, 0) };
    };
    const grouped = (keyOf: (row: (typeof homework)[number]) => string) => {
      const groups = new Map<string, typeof homework>();
      homework.forEach((row) => {
        const key = keyOf(row) || (language === 'es' ? 'Cuenta' : 'Account');
        groups.set(key, [...(groups.get(key) || []), row]);
      });
      return Array.from(groups.entries()).map(([label, rows]) => ({ label, ...stackOf(rows) }));
    };
    const companyStacks = (() => {
      const groups = new Map<string, { label: string; detail: string; rows: typeof homework }>();
      homework.forEach((row) => {
        const label = row.companyName || (language === 'es' ? 'Cuenta' : 'Account');
        const detail = row.projectName && row.projectName !== row.companyName ? row.projectName : '';
        const key = detail ? `${label}\n${detail}` : label;
        const current = groups.get(key);
        groups.set(key, { label, detail, rows: [...(current?.rows || []), row] });
      });
      return Array.from(groups.values()).map(({ label, detail, rows }) => ({ label, detail, ...stackOf(rows) }));
    })();
    const areaStacks = grouped((row) => programLabel(row.consoleKind)).map((row) => ({ ...row, detail: '' }));

    return (
      <section className="dashboard-settings-sections dashboard-settings-sections-wide" aria-label={copy.homePanelLabel}>
        <article className="card settings-card dashboard-home-card">
          <div className="dashboard-home-task-tabs" role="tablist" aria-label={language === 'es' ? 'Vista de trabajo' : 'Work view'}>
            {([
              { id: 'dashboard', en: 'Dashboard', es: 'Panel' },
              { id: 'gaps', en: 'Gaps', es: 'Brechas' },
              { id: 'intake', en: 'Intake', es: 'Alta' },
            ] as const).map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={workView === tab.id}
                className={workView === tab.id ? 'is-active' : undefined}
                onClick={() => setHomeQuery({ home: tab.id })}
              >
                {language === 'es' ? tab.es : tab.en}
              </button>
            ))}
          </div>
          {workView === 'intake' ? (
            <>
              <h2>{language === 'es' ? 'Alta de cuenta y proyecto' : 'Account and project intake'}</h2>
              <p className="dashboard-panel-subtitle">
                {language === 'es'
                  ? 'Una puerta de entrada: nombrar, clasificar papeles, asignar dueño, archivar prueba. No se cierra un paso sin prueba.'
                  : 'One front door: name the company, classify the papers, assign an owner, file proof. Do not close a step without proof.'}
              </p>
              <ol className="dashboard-home-intake">
                <li className={namedCompany ? 'is-done' : 'is-current'}>
                  <strong>{language === 'es' ? '1. Nombrar la empresa' : '1. Name the company'}</strong>
                  <p>{namedCompany
                    ? (dodState.targetCompanyName || websiteState.targetCompanyName)
                    : (language === 'es' ? 'Todavía no hay un nombre legal en el expediente.' : 'No legal name is on file yet.')}</p>
                  <button type="button" className="dashboard-btn dashboard-home-tip-primary" onClick={() => handleNavSelection(canAccessDodContracts ? 'dodContracts' : 'websiteCompliance')}>
                    {language === 'es' ? 'Abrir alta' : 'Open intake'}
                  </button>
                </li>
                <li className={namedCompany ? 'is-current' : undefined}>
                  <strong>{language === 'es' ? '2. Clasificar los papeles' : '2. Classify the papers'}</strong>
                  <p>
                    <DodTermText
                      language={language}
                      seen={new Set()}
                      text={language === 'es'
                        ? 'La clasificación se guarda en la cuenta, en Cumplimiento gubernamental.'
                        : 'Classification is stored on the account, in Government Compliance.'}
                    />
                  </p>
                </li>
                <li className={nextDod.length ? 'is-current' : undefined}>
                  <strong>{language === 'es' ? '3. Márquelo terminado' : '3. Mark it finished'}</strong>
                  <p>{nextDod[0]
                    ? nextDod[0].title
                    : (language === 'es' ? 'Cuando haya trabajo guardado, aquí aparece la primera tarjeta.' : 'Once work is saved on the account, the first card shows here.')}</p>
                  {nextDod[0] ? (
                    <button type="button" className="dashboard-btn dashboard-home-tip-primary" onClick={() => handleNavSelection('dodContracts')}>
                      {language === 'es' ? 'Ir a la tarjeta' : 'Go to the card'}
                    </button>
                  ) : null}
                </li>
                <li>
                  <strong>{language === 'es' ? '4. Asignar dueño' : '4. Assign an owner'}</strong>
                  <p>{language === 'es' ? 'Las personas viven en la suscripción de pago.' : 'People live on the paying subscription.'}</p>
                  {canManageSubscriptionTeam ? (
                    <button type="button" className="dashboard-btn dashboard-home-tip-primary" onClick={() => handleNavSelection('team')}>
                      {language === 'es' ? 'Invitar equipo' : 'Invite team'}
                    </button>
                  ) : null}
                </li>
              </ol>
            </>
          ) : null}
          {homeTask && homeDefinition(homeTask.consoleKind) ? (
            <div
              className="home-task-dialog-backdrop"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  closeHomeTask();
                }
              }}
            >
              <div
                className="home-task-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="home-task-dialog-title"
              >
                <header className="home-task-dialog-head">
                  <div>
                    <nav className="home-task-crumbs" aria-label={language === 'es' ? 'Ruta' : 'Breadcrumb'}>
                      <button type="button" onClick={closeHomeTask}>{language === 'es' ? 'Inicio' : 'Home'}</button>
                      {homeTask.companyName ? <span aria-hidden="true">/</span> : null}
                      {homeTask.companyName ? <span>{homeTask.companyName}</span> : null}
                      {homeTask.projectName ? <span aria-hidden="true">/</span> : null}
                      {homeTask.projectName ? <span>{homeTask.projectName}</span> : null}
                      {homeTask.category ? <span aria-hidden="true">/</span> : null}
                      {homeTask.category ? <span>{homeTask.category}</span> : null}
                    </nav>
                    <h2 id="home-task-dialog-title">
                      <DodTermText text={homeTask.title} language={language} seen={new Set()} />
                    </h2>
                  </div>
                  <button type="button" className="dashboard-btn home-task-close" onClick={closeHomeTask} aria-label={language === 'es' ? 'Cerrar' : 'Close'}>
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M7 7l10 10M17 7 7 17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                    {language === 'es' ? 'Cerrar' : 'Close'}
                    <span className="icon-tip" aria-hidden="true">{language === 'es' ? 'Cerrar este paso' : 'Close this step'}</span>
                  </button>
                </header>
                <ComplianceConsolePanel
                  definition={homeDefinition(homeTask.consoleKind) as ComplianceConsoleDefinition}
                  language={language}
                  gatewayBaseUrl={gatewayBaseUrl}
                  accessToken={accessToken}
                  platformAdmin={hasPlatformAdminAccess}
                  effectiveFeatures={effectiveFeatureList}
                  taskFocus={{
                    companyId: homeTask.companyId,
                    projectId: homeTask.projectId,
                    taskId: homeTask.itemId,
                    companyName: homeTask.companyName,
                    entityName: homeTask.projectName,
                  }}
                />
              </div>
            </div>
          ) : null}
          {workView !== 'intake' ? (
            <>
              {workView === 'dashboard' ? (
                <>
                  <div className="dashboard-glance-head">
                    <h2>{language === 'es' ? 'De un vistazo' : 'At a glance'}</h2>
                    <div className="dashboard-glance-actions">
                      <DashboardWidgetEditor
                        surface="home"
                        language={language}
                        widgets={HOME_DASHBOARD_WIDGETS}
                        hidden={homeWidgetsHidden}
                        order={homeLayout.order}
                        onChange={commitHomeLayout}
                      />
                      <button
                        type="button"
                        className={`compliance-calendar-launch${homeCalendarOpen ? ' is-on' : ''}`}
                        aria-pressed={homeCalendarOpen}
                        aria-label={language === 'es' ? 'Calendario de cumplimiento' : 'Compliance Calendar'}
                        onClick={() => setHomeCalendarOpen((open) => !open)}
                      >
                        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                          <rect x="3.5" y="5" width="17" height="15" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  {homeCalendarOpen ? (
                    <CompliancePortfolioPage
                      language={language}
                      accounts={(accountHome?.accounts || []).map((account) => ({
                        company_id: account.company_id,
                        company_name: account.company_name,
                      }))}
                      apiBase={gatewayBaseUrl}
                      accessToken={accessToken || ''}
                    />
                  ) : null}
                  <p className="dashboard-panel-subtitle">
                    <DodTermText
                      language={language}
                      seen={new Set()}
                      text={language === 'es'
                        ? `Trabajo guardado para ${ownerLabel}. Esto no es un puntaje SPRS ni un porcentaje de “listo para auditoría”.`
                        : `Work saved for ${ownerLabel}. This is not an SPRS score or an “audit-ready” percentage.`}
                    />
                  </p>
                  {canAccessContinuousMonitoring && agentAwaitingCount > 0 ? (
                    <div className="dashboard-home-agent-queue" role="status">
                      <p>
                        <DodTermText
                          language={language}
                          seen={new Set()}
                          text={
                            language === 'es'
                              ? `El Agente Monitor tiene ${agentAwaitingCount} borrador${agentAwaitingCount === 1 ? '' : 'es'} en espera de usted. No marcó ningún paso como terminado.`
                              : `The Monitor Agent has ${agentAwaitingCount} draft${agentAwaitingCount === 1 ? '' : 's'} waiting on you. It did not mark any step done.`
                          }
                        />
                      </p>
                      <button type="button" className="dashboard-btn dashboard-btn-primary" onClick={() => handleNavSelection('agent')}>
                        {language === 'es' ? 'Abrir el Agente' : 'Open the Agent'}
                      </button>
                    </div>
                  ) : null}
                  <div className="dashboard-home-arranged">
                  {statusCount > 0 ? (
                    <div
                      className="dashboard-glance"
                      style={{ order: (() => {
                        const ranks = ['status', 'priority', 'dates', 'accounts', 'areas']
                          .filter((id) => !homeWidgetsHidden.has(id))
                          .map((id) => homeLayout.order.indexOf(id))
                          .filter((index) => index >= 0);
                        return ranks.length ? Math.min(...ranks) : 0;
                      })() }}
                    >
                      {homeWidgetsHidden.has('status') ? null : (
                      <section className="dashboard-glance-card dashboard-glance-wide glance-summary" style={{ order: homeLayout.order.indexOf('status') }} aria-label={language === 'es' ? 'Pasos guardados' : 'Saved steps'}>
                        <h3>{language === 'es' ? `Pasos guardados · ${statusCount}` : `Saved steps · ${statusCount}`}</h3>
                        <p>
                          {language === 'es'
                            ? `${tally.reviewed} de ${statusCount} están terminados. Bloqueante y recomendado son etiquetas de pasos que ya están en esta barra. No es un puntaje de auditoría.`
                            : `${tally.reviewed} of ${statusCount} are finished. Blocking and recommended are labels on steps already in this bar. This is not an audit score.`}
                        </p>
                        <div
                          className="glance-mix"
                          role="img"
                          aria-label={language === 'es'
                            ? `${tally.reviewed} terminados, ${tally.in_progress} en progreso, ${tally.not_started} sin iniciar, ${tally.gap} brechas`
                            : `${tally.reviewed} finished, ${tally.in_progress} in progress, ${tally.not_started} not started, ${tally.gap} gaps`}
                        >
                          {[
                            { key: 'done', value: tally.reviewed, color: '#3dd68c' },
                            { key: 'progress', value: tally.in_progress, color: '#4c8dff' },
                            { key: 'not-started', value: tally.not_started, color: '#c9a227' },
                            { key: 'gap', value: tally.gap, color: '#e24b4b' },
                          ].filter((part) => part.value > 0).map((part) => (
                            <i key={part.key} style={{ width: `${Math.round((part.value / Math.max(1, statusCount)) * 100)}%`, background: part.color }} />
                          ))}
                        </div>
                        <ul className="glance-summary-legend">
                          {[
                            { key: 'done', lensKey: 'status:reviewed', value: tally.reviewed, label: language === 'es' ? 'Terminado' : 'Finished', color: '#3dd68c' },
                            { key: 'progress', lensKey: 'status:in_progress', value: tally.in_progress, label: language === 'es' ? 'En progreso' : 'In progress', color: '#4c8dff' },
                            { key: 'not-started', lensKey: 'status:not_started', value: tally.not_started, label: language === 'es' ? 'Sin iniciar' : 'Not started', color: '#c9a227' },
                            { key: 'gap', lensKey: 'status:gap', value: tally.gap, label: language === 'es' ? 'Brecha' : 'Gap', color: '#e24b4b' },
                          ].map((part) => (
                            <li key={part.key}>
                              <button
                                type="button"
                                className={lens === part.lensKey ? 'is-selected' : undefined}
                                aria-pressed={lens === part.lensKey}
                                onClick={() => setHomeQuery({ lens: lens === part.lensKey ? '' : part.lensKey })}
                              >
                                <i style={{ background: part.color }} aria-hidden="true" />
                                <span>{part.label}</span>
                                <strong>{part.value}</strong>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </section>
                      )}
                      {['priority', 'dates', 'accounts', 'areas'].some((id) => !homeWidgetsHidden.has(id)) ? (
                      <details
                        className="compliance-chart-fold dashboard-glance-wide"
                        style={{ order: (() => {
                          const ranks = ['priority', 'dates', 'accounts', 'areas']
                            .filter((id) => !homeWidgetsHidden.has(id))
                            .map((id) => homeLayout.order.indexOf(id))
                            .filter((index) => index >= 0);
                          return ranks.length ? Math.min(...ranks) : 1;
                        })() }}
                      >
                        <summary>
                          <span>{language === 'es' ? 'Más gráficos' : 'More charts'}</span>
                          <span className="compliance-fold-hint">
                            {language === 'es'
                              ? 'Los mismos pasos, por prioridad, fecha, cuenta o área.'
                              : 'The same steps, by priority, date, account, or area.'}
                          </span>
                        </summary>
                        <div className="dashboard-glance-more">
                      {homeWidgetsHidden.has('priority') ? null : (
                      <section className="dashboard-glance-card dashboard-glance-pair" style={{ order: homeLayout.order.indexOf('priority') }} aria-label={language === 'es' ? 'Mezcla de prioridad' : 'Priority mix'}>
                        <h3>{language === 'es' ? 'Mezcla de prioridad' : 'Priority mix'}</h3>
                        <GlanceColumns
                          selectedLens={lens}
                          onPick={(lensKey) => setHomeQuery({ lens: lens === lensKey ? '' : lensKey })}
                          slices={[
                            { key: 'blocking', lensKey: 'status:blocking', value: blockingOpen, label: language === 'es' ? 'Bloqueante' : 'Blocking', color: '#f97066' },
                            { key: 'recommended', lensKey: 'status:recommended', value: recommendedOpen, label: language === 'es' ? 'Recomendado' : 'Recommended', color: '#c9a227' },
                            {
                              key: 'other',
                              value: Math.max(0, statusCount - tally.reviewed - blockingOpen - recommendedOpen),
                              label: language === 'es' ? 'Otro trabajo abierto' : 'Other open work',
                              color: '#8b93a7',
                            },
                          ]}
                        />
                      </section>
                      )}
                      {homeWidgetsHidden.has('dates') ? null : (
                      <section className="dashboard-glance-card dashboard-glance-pair" style={{ order: homeLayout.order.indexOf('dates') }} aria-label={language === 'es' ? 'Fechas próximas' : 'Upcoming dates'}>
                        <h3>{language === 'es' ? 'Fechas próximas' : 'Upcoming dates'}</h3>
                        <GlanceTimeline
                          emptyLabel={language === 'es' ? 'Ningún paso abierto tiene fecha de presentación.' : 'No open step has a filing date.'}
                          onOpen={(key) => {
                            const row = homework.find((item) => `${item.consoleKind}:${item.itemId}` === key);
                            if (row) openHomework(row);
                          }}
                          rows={homework
                            .filter((row) => row.dueDate && row.status !== 'reviewed')
                            .slice()
                            .sort((left, right) => left.dueDate.localeCompare(right.dueDate) || left.title.localeCompare(right.title))
                            .slice(0, 6)
                            .map((row) => ({
                              key: `${row.consoleKind}:${row.itemId}`,
                              date: row.dueDate,
                              title: row.title,
                              place: row.projectName && row.projectName !== row.companyName ? `${row.companyName} · ${row.projectName}` : row.companyName,
                            }))}
                        />
                      </section>
                      )}
                      {([
                        { id: 'company', widget: 'accounts', title: language === 'es' ? 'Por cuenta y proyecto' : 'By account and project', rows: companyStacks, lensFor: null as string | null },
                        { id: 'area', widget: 'areas', title: language === 'es' ? 'Por área de cumplimiento' : 'By compliance area', rows: areaStacks, lensFor: 'area' },
                      ]).filter((chart) => !homeWidgetsHidden.has(chart.widget)).map((chart) => (
                        <section key={chart.id} className={`dashboard-glance-card dashboard-glance-span`} style={{ order: homeLayout.order.indexOf(chart.widget) }} aria-label={chart.title}>
                          <h3>{chart.title}</h3>
                          <ul className="dashboard-glance-stacks dashboard-glance-stacks--readable">
                            {chart.rows.map((row) => {
                              const kind = homework.find((item) => programLabel(item.consoleKind) === row.label)?.consoleKind || '';
                              const nextLens = `program:${programId(kind)}`;
                              const detail = row.detail;
                              const copy = (
                                <span className="dashboard-glance-stack-copy">
                                  <strong>{row.label}</strong>
                                  {detail ? <small>{detail}</small> : null}
                                </span>
                              );
                              return (
                                <li key={`${row.label}\n${detail}`}>
                                  {chart.lensFor === 'area' ? (
                                    <button
                                      type="button"
                                      className={lens === nextLens ? 'is-selected' : undefined}
                                      aria-pressed={lens === nextLens}
                                      onClick={() => setHomeQuery({ lens: lens === nextLens ? '' : nextLens })}
                                    >
                                      {copy}
                                    </button>
                                  ) : copy}
                                  <span className="dashboard-glance-stack" title={String(row.total)}>
                                    {row.parts.filter((part) => part.value > 0).map((part) => (
                                      <i key={part.key} style={{ width: `${Math.round((part.value / Math.max(1, row.total)) * 100)}%`, background: part.color }} />
                                    ))}
                                  </span>
                                  <strong className="dashboard-glance-stack-total">{row.total}</strong>
                                </li>
                              );
                            })}
                          </ul>
                        </section>
                      ))}
                        </div>
                      </details>
                      ) : null}
                    </div>
                  ) : null}
                  {lens ? (
                    <p className="dashboard-home-lens" style={{ order: 80 }}>
                      <span>{language === 'es' ? 'Mostrando solo lo que eligió arriba.' : 'Showing only what you picked above.'}</span>
                      <button type="button" className="dashboard-btn dashboard-btn-secondary" onClick={() => setHomeQuery({ lens: '' })}>
                        {language === 'es' ? 'Ver todo' : 'Show everything'}
                      </button>
                    </p>
                  ) : null}
                  {homeWidgetsHidden.has('waiting') ? null : (() => {
                    const today = new Date();
                    const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                    const outsideChecks = (() => {
                      void outsideCheckTick;
                      try {
                        const raw = JSON.parse(window.localStorage.getItem('nextcompli.outside-check.v1') || '{}') as Record<string, unknown>;
                        const checks: Record<string, string> = {};
                        Object.entries(raw || {}).forEach(([key, value]) => {
                          if (typeof value === 'string') checks[key] = value;
                        });
                        return checks;
                      } catch {
                        return {} as Record<string, string>;
                      }
                    })();
                    const markOutsideCheck = (key: string) => {
                      try {
                        const raw = JSON.parse(window.localStorage.getItem('nextcompli.outside-check.v1') || '{}') as Record<string, unknown>;
                        const next: Record<string, string> = {};
                        Object.entries(raw || {}).forEach(([entryKey, value]) => {
                          if (typeof value === 'string') next[entryKey] = value;
                        });
                        next[key] = todayIso;
                        window.localStorage.setItem('nextcompli.outside-check.v1', JSON.stringify(next));
                      } catch {
                        // This browser cannot store the check. The step still stays on the list.
                      }
                      setOutsideCheckTick((value) => value + 1);
                    };
                    const waitingRows = (lens ? homework.filter((row) => matchesLens(row)) : homework)
                      .filter((row) => row.status !== 'reviewed' && row.outsideWait)
                      .slice()
                      .sort((left, right) => {
                        const leftKey = `${left.companyId}:${left.consoleKind}:${left.itemId}`;
                        const rightKey = `${right.companyId}:${right.consoleKind}:${right.itemId}`;
                        const leftDue = outsideCheckStatus(outsideChecks[leftKey], left.outsideWait?.checkDays || 1, todayIso) === 'fresh' ? 1 : 0;
                        const rightDue = outsideCheckStatus(outsideChecks[rightKey], right.outsideWait?.checkDays || 1, todayIso) === 'fresh' ? 1 : 0;
                        return leftDue - rightDue || left.title.localeCompare(right.title);
                      });
                    if (!waitingRows.length) return null;
                    return (
                      <section className="dashboard-home-widget" style={{ order: homeLayout.order.indexOf('waiting') }}>
                        <h3 className="dashboard-home-section">{language === 'es' ? 'En espera de otros' : 'Waiting on others'}</h3>
                        <p className="dashboard-panel-subtitle">
                          {language === 'es'
                            ? 'Estos pasos se quedan aquí hasta que la otra parte responda. Una revisión atrasada queda arriba.'
                            : 'These steps stay here until the other party answers. A late check stays at the top.'}
                        </p>
                        <ul className="dashboard-home-attention dashboard-home-waiting">
                          {waitingRows.map((row) => {
                            const wait = row.outsideWait;
                            if (!wait) return null;
                            const place = row.projectName && row.projectName !== row.companyName
                              ? `${row.companyName} · ${row.projectName}`
                              : row.companyName;
                            const key = `${row.companyId}:${row.consoleKind}:${row.itemId}`;
                            const last = outsideChecks[key];
                            const clock = outsideCheckStatus(last, wait.checkDays, todayIso);
                            const party = language === 'es' ? wait.party.es : wait.party.en;
                            const clockText = clock === 'never'
                              ? (language === 'es' ? 'No hay una revisión guardada en este navegador.' : 'No check is saved on this browser.')
                              : clock === 'due'
                                ? (language === 'es' ? `Revise de nuevo. La última revisión fue ${last}.` : `Check again. Last check was ${last}.`)
                                : (language === 'es' ? `Última revisión ${last}. La siguiente es dentro de ${wait.checkDays} días.` : `Last check ${last}. Next check is in ${wait.checkDays} days.`);
                            return (
                              <li key={`${row.consoleKind}-${row.itemId}`} className={clock === 'fresh' ? 'is-fresh' : 'is-due'}>
                                <button type="button" onClick={() => openHomework(row)} aria-label={language === 'es' ? `Abrir ${row.title}` : `Open ${row.title}`}>
                                  <strong>
                                    <svg viewBox="0 0 24 24" aria-hidden="true">
                                      <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" />
                                      <path d="M12 8v4.5l3 2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                    </svg>
                                    {row.title}
                                  </strong>
                                  <span>{place} · {programLabel(row.consoleKind)}</span>
                                  <span className="icon-tip" aria-hidden="true">{language === 'es' ? 'Abrir este paso' : 'Open this step'}</span>
                                </button>
                                <p>
                                  <DodTermText
                                    language={language}
                                    seen={new Set()}
                                    text={language === 'es' ? `En espera de ${party}. ${clockText}` : `Waiting on ${party}. ${clockText}`}
                                  />
                                </p>
                                {row.checkUrl ? (
                                  <a href={row.checkUrl} target="_blank" rel="noopener noreferrer" onClick={() => markOutsideCheck(key)}>
                                    {language === 'es' ? 'Abrir la página oficial' : 'Open the official page'}
                                  </a>
                                ) : null}
                              </li>
                            );
                          })}
                        </ul>
                      </section>
                    );
                  })()}
                  {homeWidgetsHidden.has('ready') ? null : (
                  <section className="dashboard-home-widget" style={{ order: homeLayout.order.indexOf('ready') }}>
                  <h3 className="dashboard-home-section">{language === 'es' ? 'Listo para hacer' : 'Ready to do'}</h3>
                  <p className="dashboard-panel-subtitle">
                    {language === 'es'
                      ? 'Fechas primero, luego lo que puede hacer ahora, luego lo que bloquea otro trabajo, luego los pasos que liberan más trabajo.'
                      : 'Due dates first, then what you can do now, then what blocks other work, then the steps that free the most other work.'}
                  </p>
                  {attentionRows.length ? (
                    <ul className="compliance-ready-list dashboard-home-ready-list">
                      {attentionRows.map((row) => {
                        const place = row.projectName && row.projectName !== row.companyName
                          ? `${row.companyName} · ${row.projectName}`
                          : row.companyName;
                        const priority = row.priority === 'blocking'
                          ? (language === 'es' ? 'Bloquea' : 'Blocking')
                          : row.priority === 'recommended'
                            ? (language === 'es' ? 'Sugerida' : 'Suggested')
                            : row.priority === 'required'
                              ? (language === 'es' ? 'Requerida' : 'Required')
                              : '';
                        const statusKey = row.status === 'reviewed' || row.status === 'in_progress' || row.status === 'gap'
                          ? row.status
                          : 'not_started';
                        return (
                          <li key={`${row.consoleKind}-${row.itemId}`}>
                            <button
                              type="button"
                              className="compliance-ready-card"
                              onClick={() => openHomework(row)}
                              aria-label={language === 'es' ? `Abrir ${row.title}` : `Open ${row.title}`}
                            >
                              <strong>{row.title}</strong>
                              <span className="compliance-ready-meta">
                                <span>{place} · {programLabel(row.consoleKind)}</span>
                                {priority ? (
                                  <span className={`compliance-ready-chip priority-${row.priority || 'required'}`}>
                                    {priority}
                                  </span>
                                ) : null}
                                <span className={`compliance-ready-chip status-${statusKey}`}>
                                  {workflowStatusLabel(row.status, language, row.consoleKind)}
                                </span>
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="dashboard-panel-subtitle">
                      {language === 'es'
                        ? 'No hay pasos abiertos en el trabajo guardado.'
                        : 'No open steps in the saved work.'}
                    </p>
                  )}
                  </section>
                  )}
                  </div>
                </>
              ) : (
                <>
                  <h2>{language === 'es' ? 'Pasos que siguen abiertos' : 'Steps still open'}</h2>
                  <p className="dashboard-panel-subtitle">
                    {language === 'es'
                      ? 'Cada brecha tiene dueño. No se marca cerrada sin archivo.'
                      : 'Each gap has an owner. Do not mark it closed without a file.'}
                  </p>
                </>
              )}
              {workView === 'dashboard' ? (
                <h3 className="dashboard-home-section">{language === 'es' ? 'Trabajo abierto' : 'Open work'}</h3>
              ) : null}
              <div className="dashboard-home-work-tools">
                <div className="dashboard-home-task-tabs" role="tablist" aria-label={language === 'es' ? 'Filtro' : 'Filter'}>
                  {([
                    { id: 'all', en: 'All', es: 'Todas' },
                    { id: 'mine', en: 'Mine', es: 'Mías' },
                    { id: 'overdue', en: 'Gaps', es: 'Brechas' },
                  ] as const).map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={taskTab === tab.id}
                      className={taskTab === tab.id ? 'is-active' : undefined}
                      onClick={() => setHomeQuery({ tasks: tab.id })}
                    >
                      {language === 'es' ? tab.es : tab.en}
                    </button>
                  ))}
                </div>
                <div className="dashboard-home-task-sort" role="group" aria-label={language === 'es' ? 'Filtrar por estado' : 'Filter by status'}>
                  {([
                    { id: 'any' as const, label: language === 'es' ? 'Cualquier estado' : 'Any status' },
                    { id: 'not_started' as const, label: language === 'es' ? 'Sin iniciar' : 'Not started' },
                    { id: 'in_progress' as const, label: language === 'es' ? 'En progreso' : 'In progress' },
                    { id: 'reviewed' as const, label: language === 'es' ? 'Terminado' : 'Finished' },
                    { id: 'gap' as const, label: language === 'es' ? 'Brecha' : 'Gap' },
                    { id: 'dated' as const, label: language === 'es' ? 'Con fecha de presentación' : 'Has a filing date' },
                  ]).map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      aria-pressed={homeStatusFilter === option.id}
                      aria-label={option.label}
                      className={homeStatusFilter === option.id ? 'is-on' : ''}
                      onClick={() => setHomeStatusFilter(option.id)}
                    >
                      {option.id === 'any' ? (
                        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" /><circle cx="12" cy="12" r="3" fill="currentColor" /></svg>
                      ) : null}
                      {option.id === 'not_started' ? (
                        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" /></svg>
                      ) : null}
                      {option.id === 'in_progress' ? (
                        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M12 4a8 8 0 0 1 0 16" fill="currentColor" /></svg>
                      ) : null}
                      {option.id === 'reviewed' ? (
                        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M8 12.2 10.8 15 16 9.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      ) : null}
                      {option.id === 'gap' ? (
                        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M12 8v5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><circle cx="12" cy="16.2" r="0.9" fill="currentColor" /></svg>
                      ) : null}
                      {option.id === 'dated' ? (
                        <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="5" width="16" height="15" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M4 9.5h16M8 3.5v3M16 3.5v3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                      ) : null}
                      <span className="icon-tip">{option.label}</span>
                    </button>
                  ))}
                </div>
                <div className="dashboard-home-task-sort" role="group" aria-label={language === 'es' ? 'Orden de las tareas' : 'Task order'}>
                  {([
                    { id: 'dependencies' as const, label: language === 'es' ? 'Menos dependencias' : 'Fewest earlier steps' },
                    { id: 'due' as const, label: language === 'es' ? 'Fecha de presentación' : 'Filing date' },
                    { id: 'unlocks' as const, label: language === 'es' ? 'Desbloquea más pasos' : 'Unlocks the most steps' },
                    { id: 'blocking' as const, label: language === 'es' ? 'Bloqueantes primero' : 'Blocking first' },
                  ]).map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      aria-pressed={homeTaskSort === option.id}
                      aria-label={option.label}
                      className={homeTaskSort === option.id ? 'is-on' : ''}
                      onClick={() => setHomeTaskSort(option.id)}
                    >
                      {option.id === 'dependencies' ? (
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 12h6M8 8H6a4 4 0 0 0 0 8h2M16 8h2a4 4 0 0 1 0 8h-2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                      ) : null}
                      {option.id === 'due' ? (
                        <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="5" width="16" height="15" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M4 9.5h16M8 3.5v3M16 3.5v3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                      ) : null}
                      {option.id === 'unlocks' ? (
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 11V8a5 5 0 0 1 9.5-2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><rect x="5" y="11" width="14" height="9" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" /></svg>
                      ) : null}
                      {option.id === 'blocking' ? (
                        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M7 17 17 7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                      ) : null}
                      <span className="icon-tip">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              {tableRows.length === 0 && ofacQueue.length === 0 ? (
                <p className="dashboard-panel-subtitle">
                  {language === 'es'
                    ? `No hay trabajo guardado para ${ownerLabel}. Los proyectos de la cuenta aparecen aquí cuando están en el expediente.`
                    : `No saved work for ${ownerLabel}. Account projects show up here once they are on the record.`}
                </p>
              ) : (
                <div className="compliance-section-tabs" role="tablist" aria-label={language === 'es' ? 'Grupos de pasos' : 'Step groups'}>
                  {Array.from(sortedTableRows.reduce((groups, row) => {
                    const key = `${row.consoleKind}::${row.category}`;
                    groups.set(key, [...(groups.get(key) || []), row]);
                    return groups;
                  }, new Map<string, typeof tableRows>())).map(([key, rows], index) => {
                    const open = homeSectionId === key;
                    const tone = ['#c23b73', '#1f8a70', '#7a3e9d', '#1f3f73', '#d06a1f'][index % 5];
                    const done = rows.filter((row) => row.status === 'reviewed').length;
                    const blocked = rows.filter((row) => row.status === 'gap' || row.priority === 'blocking').length;
                    const pct = rows.length ? Math.round((done / rows.length) * 100) : 0;
                    const ring = 2 * Math.PI * 15.5;
                    return (
                      <div key={key} className="compliance-section">
                        <button
                          type="button"
                          role="tab"
                          aria-selected={open}
                          className={`compliance-section-tab${open ? ' is-open' : ''}`}
                          style={{ ['--project-tone' as string]: tone }}
                          onClick={() => setHomeSectionId(open ? '' : key)}
                        >
                          <svg className="compliance-section-meter" viewBox="0 0 36 36" role="img" aria-label={`${done} of ${rows.length}`}>
                            <circle className="compliance-ring-track" cx="18" cy="18" r="15.5" />
                            <circle
                              className={blocked > 0 && done < rows.length ? 'compliance-ring-value is-blocked' : 'compliance-ring-value'}
                              cx="18"
                              cy="18"
                              r="15.5"
                              strokeDasharray={`${(pct / 100) * ring} ${ring}`}
                            />
                            <text x="18" y="21" textAnchor="middle">{pct}</text>
                          </svg>
                          <span className="compliance-step-section-name">{rows[0]?.category}</span>
                          <span className="compliance-section-count">{done}/{rows.length}</span>
                        </button>
                        {open ? (
                          <div className="compliance-section-panel" role="tabpanel" style={{ ['--project-tone' as string]: tone }}>
                            <ul>
                              {rows.map((row) => (
                                <li key={`${row.consoleKind}-${row.itemId}`}>
                                  <button type="button" className="dashboard-home-section-task" onClick={() => openHomework(row)}>
                                    {row.title}
                                    <span>
                                      {workflowStatusLabel(row.status, language, row.consoleKind)}
                                      {row.dueDate ? ` · ${row.dueDate}` : ''}
                                    </span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                  {showOfacRows && taskTab !== 'overdue' && canAccessOfac && ofacQueue.length ? (
                    <div className="compliance-section">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={homeSectionId === 'ofac'}
                        className={`compliance-section-tab${homeSectionId === 'ofac' ? ' is-open' : ''}`}
                        style={{ ['--project-tone' as string]: '#1f3f73' }}
                        onClick={() => setHomeSectionId(homeSectionId === 'ofac' ? '' : 'ofac')}
                      >
                        <span className="compliance-step-section-name">OFAC</span>
                        <span className="compliance-section-count">0/{ofacQueue.length}</span>
                      </button>
                      {homeSectionId === 'ofac' ? (
                        <div className="compliance-section-panel" role="tabpanel">
                          <ul>
                            {ofacQueue.slice(0, 8).map((item) => (
                              <li key={`ofac-${item.name}`}>
                                <button type="button" className="dashboard-home-section-task" onClick={() => handleNavSelection('ofacScreening')}>
                                  {item.name}
                                  <span>{item.status}</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}
            </>
          ) : null}
        </article>
        {onboardVisible ? (
          <article className="card settings-card dashboard-home-tip-card" aria-label={language === 'es' ? 'Primeros pasos' : 'First jobs'}>
            <div className="dashboard-home-tip-head">
              <span className="dashboard-home-tip-kicker">{language === 'es' ? 'Cuenta lista' : 'Account ready'}</span>
              <h2>{language === 'es' ? 'Tres trabajos para empezar' : 'Three jobs to get started'}</h2>
            </div>
            <p>
              {language === 'es'
                ? 'Tu plan ya está activo. Haz uno de estos trabajos primero — no hace falta un recorrido largo.'
                : 'Your plan is active. Do one of these jobs first — no long product tour.'}
            </p>
            <ol className="dashboard-onboard-list">
              {canAccessOfac ? (
                <li>
                  <button type="button" className="dashboard-btn dashboard-home-tip-primary" onClick={() => handleNavSelection('ofac')}>
                    {language === 'es' ? 'Cribar un nombre (OFAC)' : 'Screen a name (OFAC)'}
                  </button>
                </li>
              ) : null}
              {canManageSubscriptionTeam ? (
                <li>
                  <button type="button" className="dashboard-btn dashboard-home-tip-primary" onClick={() => handleNavSelection('team')}>
                    {language === 'es' ? 'Invitar a tu equipo' : 'Invite your team'}
                  </button>
                </li>
              ) : null}
              {canAccessDodContracts ? (
                <li>
                  <button type="button" className="dashboard-btn dashboard-home-tip-primary" onClick={() => handleNavSelection('dodContracts')}>
                    {language === 'es' ? 'Abrir cumplimiento gubernamental' : 'Open Government Compliance'}
                  </button>
                </li>
              ) : null}
              <li>
                <button type="button" className="dashboard-btn dashboard-home-tip-primary" onClick={() => handleNavSelection('payments')}>
                  {language === 'es' ? 'Revisar facturación y plan' : 'Review billing and plan'}
                </button>
              </li>
            </ol>
            <div className="dashboard-home-tip-actions">
              <button
                type="button"
                className="dashboard-btn dashboard-home-tip-secondary"
                onClick={() => {
                  try {
                    window.localStorage.setItem('nextcompli.onboard.dismissed', '1');
                  } catch {
                    /* ignore */
                  }
                  navigate('/console/home', { replace: true });
                }}
              >
                {language === 'es' ? 'Ocultar' : 'Dismiss'}
              </button>
            </div>
          </article>
        ) : null}
        {!hasSuperUserAccess && todaysTip ? (
          <article className="card settings-card dashboard-home-tip-card" aria-label={language === 'es' ? 'Consejo del dia' : 'Tip of the day'}>
            <div className="dashboard-home-tip-head">
              <span className="dashboard-home-tip-kicker">{language === 'es' ? 'Consejo del dia' : 'Tip of the Day'}</span>
              <h2>{todaysTip.title[language]}</h2>
            </div>
            <p>{todaysTip.body[language]}</p>
            <div className="dashboard-home-tip-actions">
              <button type="button" className="dashboard-btn dashboard-home-tip-primary" onClick={handleTipPrimaryAction}>
                {todaysTip.action.label[language]}
              </button>
              <button type="button" className="dashboard-btn dashboard-home-tip-secondary" onClick={handleTipDismiss}>
                {language === 'es' ? 'Ocultar hoy' : 'Hide for today'}
              </button>
              <button type="button" className="dashboard-btn dashboard-home-tip-secondary" onClick={handleTipSnooze}>
                {language === 'es' ? 'Recordarmelo en 3 dias' : 'Remind me in 3 days'}
              </button>
            </div>
          </article>
        ) : null}
      </section>
    );
  };

  const renderRightPane = (): React.ReactNode => {
    if (activeNavItem === 'home') {
      return renderHomePane();
    }

    if (activeNavItem === 'dashboards') {
      // Subscribers: Home covers landing; feature entry is left nav. Admins keep live dashboards.
      if (!hasSuperUserAccess) {
        return renderHomePane();
      }
      return renderDashboardsPane();
    }

    if (activeNavItem === 'personal') {
      return (
        <section className="dashboard-settings-sections dashboard-settings-sections-centered" aria-label={copy.personalPanelLabel}>
          <article className="card settings-card">
            <div className="settings-card-header">
              <h2>{copy.accountSettings}</h2>
              <button
                type="button"
                className="dashboard-icon-btn dashboard-settings-cog-btn"
                aria-label={copy.settingsEditAria}
                onClick={() => {
                  setProfileDraft(accountProfile);
                  setAvatarDraftDataUrl(accountAvatar);
                  setProfileError(null);
                  setProfileFeedback(null);
                  setIsEditingProfile(true);
                }}
              >
                <SettingsCogIcon />
              </button>
            </div>
            <div className="dashboard-personal-shell">
              <div className="dashboard-personal-identity">
                <div className="dashboard-personal-avatar" aria-hidden="true">
                  {displayAvatar ? (
                    <img src={displayAvatar} alt={copy.settingsAvatarAlt} className="dashboard-personal-avatar-image" />
                  ) : accountInitials}
                </div>
                <div>
                  <h3>{accountDisplayName}</h3>
                  <p>{accountEmail}</p>
                </div>
                {isEditingProfile ? (
                  <div className="dashboard-personal-avatar-controls">
                    <label htmlFor="dashboard-avatar-upload">{copy.settingsAvatarLabel}</label>
                    <input
                      id="dashboard-avatar-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarFileChange}
                    />
                    <button type="button" className="dashboard-btn" onClick={handleAvatarRemove}>{copy.settingsAvatarRemoveAction}</button>
                    <p>{copy.settingsAvatarHint}</p>
                  </div>
                ) : null}
              </div>
              <div className="dashboard-personal-grid">
                <div className="dashboard-personal-item">
                  <span>{copy.labelDisplayName}</span>
                  {isEditingProfile ? (
                    <input
                      type="text"
                      value={profileDraft.displayName}
                      onChange={(event) => handleProfileDraftChange('displayName', event.target.value)}
                    />
                  ) : (
                    <strong>{accountDisplayName}</strong>
                  )}
                </div>
                <div className="dashboard-personal-item">
                  <span>{copy.labelEmail}</span>
                  {isEditingProfile ? (
                    <input
                      type="email"
                      value={profileDraft.email}
                      onChange={(event) => handleProfileDraftChange('email', event.target.value)}
                    />
                  ) : (
                    <strong>{accountEmail}</strong>
                  )}
                </div>
                <div className="dashboard-personal-item">
                  <span>{copy.labelContactNumber}</span>
                  {isEditingProfile ? (
                    <input
                      type="tel"
                      value={profileDraft.contactNumber}
                      onChange={(event) => handleProfileDraftChange('contactNumber', formatPhoneNumber(event.target.value))}
                      placeholder="(555) 555-5555"
                    />
                  ) : (
                    <strong>{accountProfile.contactNumber}</strong>
                  )}
                </div>
                <div className="dashboard-personal-item">
                  <span>{copy.labelLanguage}</span>
                  {isEditingProfile ? (
                    <select
                      value={profileDraft.language}
                      onChange={(event) => handleProfileDraftChange('language', event.target.value)}
                    >
                      {LANGUAGE_OPTIONS.map((languageOption) => (
                        <option key={languageOption} value={languageOption}>{languageOption}</option>
                      ))}
                    </select>
                  ) : (
                    <strong>{accountProfile.language}</strong>
                  )}
                </div>
                <div className="dashboard-personal-item">
                  <span>{copy.labelTimezone}</span>
                  {isEditingProfile ? (
                    <select
                      value={profileDraft.timezone}
                      onChange={(event) => handleProfileDraftChange('timezone', event.target.value)}
                    >
                      {TIMEZONE_OPTIONS.map((timezoneOption) => (
                        <option key={timezoneOption} value={timezoneOption}>{timezoneOption}</option>
                      ))}
                    </select>
                  ) : (
                    <strong>{accountProfile.timezone}</strong>
                  )}
                </div>
              </div>
              {profileError ? <p className="dashboard-form-error">{profileError}</p> : null}
              {profileFeedback ? <p className="dashboard-form-success">{profileFeedback}</p> : null}
              {isEditingProfile ? (
                <div className="dashboard-action-buttons">
                  <button type="button" className="dashboard-btn dashboard-btn-primary" onClick={handleProfileSave}>{copy.settingsSaveAction}</button>
                  <button type="button" className="dashboard-btn" onClick={handleProfileCancel}>{copy.settingsCancelAction}</button>
                </div>
              ) : null}
            </div>
          </article>
          <article className="card settings-card appearance-prefs-card" aria-label={copy.appearanceTitle}>
            <div className="settings-card-header">
              <h2>{copy.appearanceTitle}</h2>
            </div>
            <div className="appearance-prefs-grid">
              <label className="appearance-prefs-item">
                <span>{copy.labelTheme}</span>
                <select
                  value={appearancePreferences.themeMode}
                  onChange={(event) => updateAppearancePreference('themeMode', event.target.value as AppearanceThemeMode)}
                >
                  <option value="system">{copy.themeSystem}</option>
                  <option value="light">{copy.themeLight}</option>
                  <option value="dark">{copy.themeDark}</option>
                </select>
              </label>
              <label className="appearance-prefs-item">
                <span>{copy.labelDensity}</span>
                <select
                  value={appearancePreferences.density}
                  onChange={(event) => updateAppearancePreference('density', event.target.value as AppearanceDensity)}
                >
                  <option value="comfortable">{copy.densityComfortable}</option>
                  <option value="compact">{copy.densityCompact}</option>
                </select>
              </label>
              <label className="appearance-prefs-item">
                <span>{copy.labelReduceMotion}</span>
                <select
                  value={appearancePreferences.reduceMotion}
                  onChange={(event) => updateAppearancePreference('reduceMotion', event.target.value as AppearanceReduceMotion)}
                >
                  <option value="system">{copy.reduceMotionSystem}</option>
                  <option value="on">{copy.reduceMotionOn}</option>
                  <option value="off">{copy.reduceMotionOff}</option>
                </select>
              </label>
              <label className="appearance-prefs-item">
                <span>{copy.labelFontScale}</span>
                <select
                  value={appearancePreferences.fontScale}
                  onChange={(event) => updateAppearancePreference('fontScale', event.target.value as AppearanceFontScale)}
                >
                  <option value="sm">{copy.fontScaleSm}</option>
                  <option value="md">{copy.fontScaleMd}</option>
                  <option value="lg">{copy.fontScaleLg}</option>
                </select>
              </label>
            </div>
            <p className="appearance-prefs-hint">
              {copy.appearanceHint}
              {' '}
              {copy.appearanceResolvedTheme}
              {': '}
              {resolveThemeMode(appearancePreferences.themeMode) === 'dark' ? copy.themeDark : copy.themeLight}
            </p>
          </article>
        </section>
      );
    }

    if (activeNavItem === 'security') {
      const pushSecurityFeedback = (englishText: string, spanishText: string) => {
        setSecurityFeedback(language === 'es' ? spanishText : englishText);
      };

      const toggleDeviceGroupAccordion = (groupKey: DeviceGroupKey) => {
        setExpandedDeviceGroups((current) => (
          current.includes(groupKey)
            ? current.filter((key) => key !== groupKey)
            : [...current, groupKey]
        ));
      };

      const openSessionLocation = (session: DeviceSessionRecord) => {
        const mapsQuery = encodeURIComponent(session.location);
        window.open(`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`, '_blank', 'noopener,noreferrer');
        pushSecurityFeedback('Device location lookup opened.', 'Se abrió la búsqueda de ubicación del dispositivo.');
      };

      const removeDeviceSession = (groupKey: DeviceGroupKey, sessionId: string, sessionLabel: string) => {
        setDeviceSessionsByGroup((current) => {
          const nextSessions = current[groupKey].filter((session) => session.id !== sessionId);
          return {
            ...current,
            [groupKey]: nextSessions,
          };
        });

        pushSecurityFeedback(
          `${sessionLabel} was removed from authentication and authorization.`,
          `${sessionLabel} fue eliminado de autenticación y autorización.`,
        );
      };

      const toggleManageAllDevices = () => {
        setIsDeviceManagementOpen((current) => {
          const nextOpen = !current;
          if (nextOpen) {
            setExpandedDeviceGroups(['mac', 'iphone', 'windows']);
            pushSecurityFeedback('Device management mode is enabled. You can now delete sessions.', 'El modo de administración de dispositivos está habilitado. Ahora puedes eliminar sesiones.');
          } else {
            pushSecurityFeedback('Device management mode is disabled.', 'El modo de administración de dispositivos está deshabilitado.');
          }
          return nextOpen;
        });
      };

      const securitySignInRows = [
        {
          key: 'two-step',
          icon: 'shield' as const,
          title: copy.securityTwoStepTitle,
          detail: isTwoFactorOn ? (language === 'es' ? 'Activado' : 'Enabled') : (language === 'es' ? 'Desactivado' : 'Disabled'),
          badge: true,
          onAction: () => {
            setActiveSecurityOption('two-step');
          },
        },
        {
          key: 'passkeys',
          icon: 'passkey' as const,
          title: copy.securityPasskeysTitle,
          detail: hasPasskey ? copy.securityPasskeysDetail : (language === 'es' ? 'Sin passkeys' : 'No passkeys'),
          onAction: () => {
            setActiveSecurityOption('passkeys');
          },
        },
        {
          key: 'password',
          icon: 'password' as const,
          title: copy.securityPasswordTitle,
          detail: copy.securityPasswordDetail,
          onAction: () => {
            setActiveSecurityOption('password');
            setShowSecurityPasswordEditor(true);
            pushSecurityFeedback('Password editor opened.', 'Se abrió el editor de contraseña.');
          },
        },
        {
          key: 'two-step-phones',
          icon: 'phone' as const,
          title: copy.securityTwoStepPhonesTitle,
          detail: twoStepPhone || (language === 'es' ? 'No configurado' : 'Not set'),
          onAction: () => {
            setActiveSecurityOption('two-step-phones');
          },
        },
        {
          key: 'recovery-phone',
          icon: 'phone' as const,
          title: copy.securityRecoveryPhoneTitle,
          detail: recoveryPhone || (language === 'es' ? 'No configurado' : 'Not set'),
          onAction: () => {
            setActiveSecurityOption('recovery-phone');
          },
        },
        {
          key: 'recovery-email',
          icon: 'email' as const,
          title: copy.securityRecoveryEmailTitle,
          detail: recoveryEmailVerified ? recoveryEmail : `${language === 'es' ? 'Verificar' : 'Verify'} ${recoveryEmail}`,
          warn: !recoveryEmailVerified,
          onAction: () => {
            setActiveSecurityOption('recovery-email');
          },
        },
        {
          key: 'login-aliases',
          icon: 'email' as const,
          title: language === 'es' ? 'Alias de correo de inicio de sesión' : 'Login email aliases',
          detail: loginEmailAliases.length > 0
            ? `${loginEmailAliases.length} ${language === 'es' ? 'alias activos' : 'active aliases'}`
            : (language === 'es' ? 'Sin alias' : 'No aliases'),
          onAction: () => {
            setActiveSecurityOption('login-aliases');
          },
        },
        {
          key: 'backup-codes',
          icon: 'backup' as const,
          title: copy.securityBackupCodesTitle,
          detail: `${backupCodesCount} ${language === 'es' ? 'códigos disponibles' : 'codes available'}`,
          onAction: () => {
            setActiveSecurityOption('backup-codes');
          },
        },
      ];

      const buildDeviceRowTitle = (groupKey: DeviceGroupKey, sessionCount: number): string => {
        if (language === 'es') {
          if (groupKey === 'mac') {
            return `${sessionCount} sesiones en Mac`;
          }
          if (groupKey === 'iphone') {
            return `${sessionCount} sesiones en iPhone`;
          }
          return `${sessionCount} sesiones en Windows`;
        }

        if (groupKey === 'mac') {
          return `${sessionCount} sessions on Mac computer(s)`;
        }
        if (groupKey === 'iphone') {
          return `${sessionCount} sessions on iPhone(s)`;
        }
        return `${sessionCount} sessions on Windows computer(s)`;
      };

      const securityDeviceRows = [
        {
          key: 'mac',
          icon: 'device' as const,
          title: buildDeviceRowTitle('mac', deviceSessionsByGroup.mac.length),
          detail: copy.securityDeviceMacDetail,
          sessions: deviceSessionsByGroup.mac,
        },
        {
          key: 'iphone',
          icon: 'device' as const,
          title: buildDeviceRowTitle('iphone', deviceSessionsByGroup.iphone.length),
          detail: copy.securityDeviceIphoneDetail,
          sessions: deviceSessionsByGroup.iphone,
        },
        {
          key: 'windows',
          icon: 'device' as const,
          title: buildDeviceRowTitle('windows', deviceSessionsByGroup.windows.length),
          detail: copy.securityDeviceWindowsDetail,
          sessions: deviceSessionsByGroup.windows,
        },
      ].filter((row) => row.sessions.length > 0);

      const securityOptionChips = [
        {
          key: 'contacts',
          label: copy.securityAddOptionContacts,
          enabled: recoveryContactsEnabled,
          onAction: () => {
            setRecoveryContactsEnabled((current) => !current);
            pushSecurityFeedback('Recovery contacts settings were updated.', 'La configuración de contactos de recuperación fue actualizada.');
          },
        },
        {
          key: 'authenticator',
          label: copy.securityAddOptionAuthenticator,
          enabled: authenticatorEnabled,
          onAction: () => {
            setAuthenticatorEnabled((current) => {
              const nextValue = !current;
              setIsTwoFactorOn(nextValue);
              return nextValue;
            });
            pushSecurityFeedback('Authenticator settings were updated.', 'La configuración del autenticador fue actualizada.');
          },
        },
        {
          key: 'backup-phones',
          label: copy.securityAddOptionBackupPhones,
          enabled: backupPhonesEnabled,
          onAction: () => {
            setBackupPhonesEnabled((current) => !current);
            pushSecurityFeedback('Backup phone settings were updated.', 'La configuración de teléfonos de respaldo fue actualizada.');
          },
        },
        {
          key: 'linked-ids',
          label: copy.securityAddOptionLinkedIds,
          enabled: linkedIdsEnabled,
          onAction: () => {
            setLinkedIdsEnabled((current) => !current);
            pushSecurityFeedback('Linked IDs settings were updated.', 'La configuración de IDs vinculados fue actualizada.');
          },
        },
      ];

      return (
        <section className="dashboard-settings-sections dashboard-settings-sections-centered" aria-label={copy.securityPanelLabel}>
          <article className="card settings-card">
            <div className="settings-card-header">
              <h2>{copy.securityAccess}</h2>
            </div>
            <div className="dashboard-security-overview dashboard-payments-accordion" role="presentation">
              {(() => {
                const securityTips: string[] = [];

                if (!isTwoFactorOn) {
                  securityTips.push(copy.securityTipEnable2FA);
                }

                if (!hasPasskey) {
                  securityTips.push(copy.securityTipAddPasskey);
                }

                if (!recoveryEmailVerified) {
                  securityTips.push(copy.securityTipVerifyRecoveryEmail);
                }

                if (backupCodesCount < 8) {
                  securityTips.push(copy.securityTipRefreshBackupCodes);
                }

                const hasSecurityTips = securityTips.length > 0;

                return (
              <div className={`dashboard-payments-accordion-item${securityAccordion.tips ? ' is-open' : ''}`}>
                <h3 className="dashboard-payments-accordion-heading">
                  <button
                    type="button"
                    className="dashboard-payments-accordion-trigger"
                    aria-expanded={securityAccordion.tips}
                    aria-controls="security-acc-tips-panel"
                    id="security-acc-tips-btn"
                    onClick={() => toggleSecurityAccordion('tips')}
                  >
                    <span className="dashboard-security-item-icon" aria-hidden="true"><SecurityRowIcon name="tips" /></span>
                    <span className="dashboard-action-copy">
                      <strong>{hasSecurityTips ? copy.securityTipsCardTitle : copy.securityNoTipsTitle}</strong>
                      <span>{hasSecurityTips ? copy.securityTipsFoundBody : copy.securityNoTipsBody}</span>
                    </span>
                    <span className="dashboard-payments-accordion-chevron" aria-hidden="true">{securityAccordion.tips ? '▾' : '▸'}</span>
                  </button>
                </h3>
                {securityAccordion.tips ? (
                  <div
                    id="security-acc-tips-panel"
                    role="region"
                    aria-labelledby="security-acc-tips-btn"
                    className="dashboard-payments-panel"
                  >
                    {hasSecurityTips ? (
                      <ul className="dashboard-security-tips-list" aria-label="Security analysis tips">
                        {securityTips.map((tip) => (
                          <li key={tip}>{tip}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="dashboard-panel-subtitle" style={{ margin: 0 }}>{copy.securityNoTipsBody}</p>
                    )}
                  </div>
                ) : null}
              </div>
                );
              })()}

              <div className={`dashboard-payments-accordion-item${securityAccordion.activity ? ' is-open' : ''}`}>
                <h3 className="dashboard-payments-accordion-heading">
                  <button
                    type="button"
                    className="dashboard-payments-accordion-trigger"
                    aria-expanded={securityAccordion.activity}
                    aria-controls="security-acc-activity-panel"
                    id="security-acc-activity-btn"
                    onClick={() => toggleSecurityAccordion('activity')}
                  >
                    <span className="dashboard-security-item-icon" aria-hidden="true"><SecurityRowIcon name="activity" /></span>
                    <span className="dashboard-action-copy">
                      <strong>{copy.securityRecentTitle}</strong>
                      <span>{copy.securityRecentSecondary}</span>
                    </span>
                    <span className="dashboard-payments-accordion-chevron" aria-hidden="true">{securityAccordion.activity ? '▾' : '▸'}</span>
                  </button>
                </h3>
                {securityAccordion.activity ? (
                  <div
                    id="security-acc-activity-panel"
                    role="region"
                    aria-labelledby="security-acc-activity-btn"
                    className="dashboard-payments-panel"
                  >
                    <div className="dashboard-security-activity-card">
                      <div className="dashboard-security-row">
                        <span className="dashboard-security-item-icon" aria-hidden="true"><SecurityRowIcon name="activity" /></span>
                        <div>
                          <strong>{copy.securityRecentPrimary}</strong>
                          <p>{copy.securityRecentSecondary}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className={`dashboard-payments-accordion-item${securityAccordion.signIn ? ' is-open' : ''}`}>
                <h3 className="dashboard-payments-accordion-heading">
                  <button
                    type="button"
                    className="dashboard-payments-accordion-trigger"
                    aria-expanded={securityAccordion.signIn}
                    aria-controls="security-acc-signin-panel"
                    id="security-acc-signin-btn"
                    onClick={() => toggleSecurityAccordion('signIn')}
                  >
                    <span className="dashboard-security-item-icon" aria-hidden="true"><SecurityRowIcon name="password" /></span>
                    <span className="dashboard-action-copy">
                      <strong>{copy.securityHowSignInTitle}</strong>
                      <span>{copy.securityHowSignInBody}</span>
                    </span>
                    <span className="dashboard-payments-accordion-chevron" aria-hidden="true">{securityAccordion.signIn ? '▾' : '▸'}</span>
                  </button>
                </h3>
                {securityAccordion.signIn ? (
                  <div
                    id="security-acc-signin-panel"
                    role="region"
                    aria-labelledby="security-acc-signin-btn"
                    className="dashboard-payments-panel"
                  >
              <div className="dashboard-security-list-card">
                {securitySignInRows.map((item) => {
                  const isPasswordRowExpanded = item.key === 'password' && showSecurityPasswordEditor;

                  return (
                    <div key={item.key} className="dashboard-security-signin-group">
                      <button
                        type="button"
                        className="dashboard-security-list-row dashboard-security-list-row-btn"
                        onClick={item.onAction}
                        aria-expanded={item.key === 'password' ? isPasswordRowExpanded : undefined}
                        aria-controls={item.key === 'password' ? 'security-password-editor-accordion' : undefined}
                      >
                        <span className="dashboard-security-item-icon" aria-hidden="true"><SecurityRowIcon name={item.icon} /></span>
                        <div className="dashboard-security-row-copy">
                          <strong>{item.title}</strong>
                          {item.badge ? (
                            <span className={`dashboard-security-status-pill ${item.warn ? 'warn' : 'ok'}`}>{item.detail}</span>
                          ) : (
                            <p>{item.detail}</p>
                          )}
                        </div>
                      </button>
                      {isPasswordRowExpanded ? (
                        <div
                          id="security-password-editor-accordion"
                          className="dashboard-security-device-accordion"
                          role="region"
                          aria-label={copy.passwordTitle}
                        >
                          <form
                            className="dashboard-password-form"
                            onSubmit={(event) => {
                              void handlePasswordSubmit(event);
                            }}
                            action="#"
                            method="post"
                          >
                            <h3>{copy.passwordTitle}</h3>
                            <p className="research-form-panel-meta" id="password-change-requirements">
                              {copy.passwordRequirements}
                            </p>
                            <label className="dashboard-password-field">
                              <span className="dashboard-password-field__label">{copy.passwordCurrent}</span>
                              <span className="dashboard-password-field__row">
                                <input
                                  type={showPasswordFields.current ? 'text' : 'password'}
                                  placeholder={copy.passwordCurrent}
                                  value={currentPasswordInput}
                                  onChange={(event) => setCurrentPasswordInput(event.target.value)}
                                  autoComplete="current-password"
                                  name="current-password"
                                  required
                                  disabled={passwordSubmitting}
                                  aria-describedby="password-change-requirements"
                                />
                                <button
                                  type="button"
                                  className="dashboard-password-toggle"
                                  onClick={() => setShowPasswordFields((current) => ({ ...current, current: !current.current }))}
                                  aria-pressed={showPasswordFields.current}
                                >
                                  {showPasswordFields.current ? copy.passwordHide : copy.passwordShow}
                                </button>
                              </span>
                            </label>
                            <label className="dashboard-password-field">
                              <span className="dashboard-password-field__label">{copy.passwordNew}</span>
                              <span className="dashboard-password-field__row">
                                <input
                                  type={showPasswordFields.next ? 'text' : 'password'}
                                  placeholder={copy.passwordNew}
                                  value={newPasswordInput}
                                  onChange={(event) => setNewPasswordInput(event.target.value)}
                                  autoComplete="new-password"
                                  name="new-password"
                                  minLength={12}
                                  required
                                  disabled={passwordSubmitting}
                                />
                                <button
                                  type="button"
                                  className="dashboard-password-toggle"
                                  onClick={() => setShowPasswordFields((current) => ({ ...current, next: !current.next }))}
                                  aria-pressed={showPasswordFields.next}
                                >
                                  {showPasswordFields.next ? copy.passwordHide : copy.passwordShow}
                                </button>
                              </span>
                            </label>
                            <label className="dashboard-password-field">
                              <span className="dashboard-password-field__label">{copy.passwordConfirm}</span>
                              <span className="dashboard-password-field__row">
                                <input
                                  type={showPasswordFields.confirm ? 'text' : 'password'}
                                  placeholder={copy.passwordConfirm}
                                  value={confirmPasswordInput}
                                  onChange={(event) => setConfirmPasswordInput(event.target.value)}
                                  autoComplete="new-password"
                                  name="confirm-new-password"
                                  minLength={12}
                                  required
                                  disabled={passwordSubmitting}
                                />
                                <button
                                  type="button"
                                  className="dashboard-password-toggle"
                                  onClick={() => setShowPasswordFields((current) => ({ ...current, confirm: !current.confirm }))}
                                  aria-pressed={showPasswordFields.confirm}
                                >
                                  {showPasswordFields.confirm ? copy.passwordHide : copy.passwordShow}
                                </button>
                              </span>
                            </label>
                            {passwordError && <p className="dashboard-form-error" role="alert">{passwordError}</p>}
                            {passwordFeedback && <p className="dashboard-form-success">{passwordFeedback}</p>}
                            <div className="dashboard-action-buttons">
                              <button type="submit" className="dashboard-btn dashboard-btn-primary" disabled={passwordSubmitting}>
                                {passwordSubmitting ? copy.passwordUpdating : copy.passwordSaveAction}
                              </button>
                              <button
                                type="button"
                                className="dashboard-btn"
                                disabled={passwordSubmitting}
                                onClick={() => {
                                  setShowSecurityPasswordEditor(false);
                                  setPasswordError(null);
                                  setPasswordFeedback(null);
                                }}
                              >
                                {copy.settingsCancelAction}
                              </button>
                            </div>
                          </form>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              {activeSecurityOption ? (
                <div className="dashboard-security-option-panel" role="region" aria-label="Sign-in option details">
                  {activeSecurityOption === 'two-step' ? (
                    <>
                      <h4>{copy.securityTwoStepTitle}</h4>
                      <p>{isTwoFactorOn ? copy.twoFactorEnabled : copy.twoFactorDisabled}</p>
                      <button
                        type="button"
                        className="dashboard-btn"
                        onClick={() => {
                          handleTwoFactorToggle();
                          pushSecurityFeedback('2-Step Verification was updated.', 'La verificación en 2 pasos fue actualizada.');
                        }}
                      >
                        {isTwoFactorOn ? copy.twoFactorDisableAction : copy.twoFactorEnableAction}
                      </button>
                    </>
                  ) : null}
                  {activeSecurityOption === 'passkeys' ? (
                    <>
                      <h4>{copy.securityPasskeysTitle}</h4>
                      <p>{hasPasskey ? copy.securityPasskeysDetail : (language === 'es' ? 'Sin passkeys' : 'No passkeys')}</p>
                      <button
                        type="button"
                        className="dashboard-btn"
                        onClick={() => {
                          setHasPasskey((current) => !current);
                          pushSecurityFeedback('Passkey settings were updated.', 'La configuración de passkeys fue actualizada.');
                        }}
                      >
                        {hasPasskey ? (language === 'es' ? 'Quitar passkey' : 'Remove passkey') : (language === 'es' ? 'Agregar passkey' : 'Add passkey')}
                      </button>
                    </>
                  ) : null}
                  {activeSecurityOption === 'two-step-phones' ? (
                    <>
                      <h4>{copy.securityTwoStepPhonesTitle}</h4>
                      <input
                        type="tel"
                        value={securityPhoneDraft}
                        onChange={(event) => setSecurityPhoneDraft(event.target.value)}
                      />
                      <button
                        type="button"
                        className="dashboard-btn"
                        onClick={() => {
                          if (!securityPhoneDraft.trim()) {
                            pushSecurityFeedback('Phone number is required.', 'Se requiere número de teléfono.');
                            return;
                          }
                          setTwoStepPhone(securityPhoneDraft.trim());
                          pushSecurityFeedback('2-Step phone was updated.', 'El teléfono de verificación en 2 pasos fue actualizado.');
                        }}
                      >
                        {language === 'es' ? 'Guardar teléfono' : 'Save phone'}
                      </button>
                    </>
                  ) : null}
                  {activeSecurityOption === 'recovery-phone' ? (
                    <>
                      <h4>{copy.securityRecoveryPhoneTitle}</h4>
                      <input
                        type="tel"
                        value={securityRecoveryPhoneDraft}
                        onChange={(event) => setSecurityRecoveryPhoneDraft(event.target.value)}
                      />
                      <button
                        type="button"
                        className="dashboard-btn"
                        onClick={() => {
                          if (!securityRecoveryPhoneDraft.trim()) {
                            pushSecurityFeedback('Recovery phone is required.', 'Se requiere teléfono de recuperación.');
                            return;
                          }
                          setRecoveryPhone(securityRecoveryPhoneDraft.trim());
                          pushSecurityFeedback('Recovery phone was updated.', 'El teléfono de recuperación fue actualizado.');
                        }}
                      >
                        {language === 'es' ? 'Guardar teléfono' : 'Save phone'}
                      </button>
                    </>
                  ) : null}
                  {activeSecurityOption === 'recovery-email' ? (
                    <>
                      <h4>{copy.securityRecoveryEmailTitle}</h4>
                      <input
                        type="email"
                        value={securityRecoveryEmailDraft}
                        onChange={(event) => setSecurityRecoveryEmailDraft(event.target.value)}
                      />
                      <button
                        type="button"
                        className="dashboard-btn"
                        onClick={() => {
                          const nextRecoveryEmail = securityRecoveryEmailDraft.trim();
                          const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextRecoveryEmail);
                          if (!isValid) {
                            pushSecurityFeedback('Recovery email was not updated: invalid email format.', 'No se actualizó el correo de recuperación: formato inválido.');
                            return;
                          }
                          setRecoveryEmail(nextRecoveryEmail);
                          setRecoveryEmailVerified(true);
                          pushSecurityFeedback('Recovery email status was updated.', 'El estado del correo de recuperación fue actualizado.');
                        }}
                      >
                        {language === 'es' ? 'Guardar correo de recuperación' : 'Save recovery email'}
                      </button>
                    </>
                  ) : null}
                  {activeSecurityOption === 'login-aliases' ? (
                    <>
                      <h4>{language === 'es' ? 'Alias de correo de inicio de sesión' : 'Login email aliases'}</h4>
                      <p>
                        {language === 'es'
                          ? 'Tu correo principal recibe notificaciones. Los alias solo agregan formas adicionales de iniciar sesión.'
                          : 'Your primary email receives notifications. Aliases only add additional sign-in identifiers.'}
                      </p>
                      <div className="theme-admin-grid" style={{ marginBottom: '0.5rem' }}>
                        <input
                          type="email"
                          value={securityAliasInput}
                          placeholder={language === 'es' ? 'correo.alias@dominio.com' : 'alias.email@domain.com'}
                          onChange={(event) => setSecurityAliasInput(event.target.value)}
                        />
                        <button
                          type="button"
                          className="dashboard-btn"
                          disabled={isSecurityAliasBusy}
                          onClick={async () => {
                            const nextAlias = securityAliasInput.trim().toLowerCase();
                            const isValid = /^\S+@\S+\.\S+$/.test(nextAlias);
                            if (!isValid) {
                              pushSecurityFeedback('Alias was not added: invalid email format.', 'No se agregó el alias: formato de correo inválido.');
                              return;
                            }
                            if (!accessToken) {
                              pushSecurityFeedback('Alias update failed: missing authentication.', 'No se pudo actualizar el alias: falta autenticación.');
                              return;
                            }
                            try {
                              setIsSecurityAliasBusy(true);
                              const response = await fetch(authEmailAliasesUrl, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${accessToken}`,
                                },
                                body: JSON.stringify({ email: nextAlias }),
                              });
                              const payload = (await response.json().catch(() => ({}))) as { detail?: string; email_aliases?: string[] };
                              if (!response.ok) {
                                pushSecurityFeedback(
                                  payload.detail || 'Alias could not be added.',
                                  payload.detail || 'No se pudo agregar el alias.',
                                );
                                return;
                              }
                              setLoginEmailAliases(Array.isArray(payload.email_aliases) ? payload.email_aliases : []);
                              setSecurityAliasInput('');
                              pushSecurityFeedback('Login alias added.', 'Alias de inicio de sesión agregado.');
                            } catch {
                              pushSecurityFeedback('Alias could not be added.', 'No se pudo agregar el alias.');
                            } finally {
                              setIsSecurityAliasBusy(false);
                            }
                          }}
                        >
                          {language === 'es' ? 'Agregar alias' : 'Add alias'}
                        </button>
                      </div>
                      {loginEmailAliases.length > 0 ? (
                        <ul className="dashboard-security-tips-list" aria-label={language === 'es' ? 'Alias de inicio de sesión' : 'Login aliases'}>
                          {loginEmailAliases.map((alias) => (
                            <li key={alias}>
                              <span>{alias}</span>
                              <button
                                type="button"
                                className="dashboard-security-link-btn"
                                disabled={isSecurityAliasBusy}
                                onClick={async () => {
                                  if (!accessToken) {
                                    pushSecurityFeedback('Alias removal failed: missing authentication.', 'No se pudo eliminar el alias: falta autenticación.');
                                    return;
                                  }
                                  try {
                                    setIsSecurityAliasBusy(true);
                                    const response = await fetch(`${authEmailAliasesUrl}/${encodeURIComponent(alias)}`, {
                                      method: 'DELETE',
                                      headers: {
                                        Authorization: `Bearer ${accessToken}`,
                                      },
                                    });
                                    const payload = (await response.json().catch(() => ({}))) as { detail?: string; email_aliases?: string[] };
                                    if (!response.ok) {
                                      pushSecurityFeedback(
                                        payload.detail || 'Alias could not be removed.',
                                        payload.detail || 'No se pudo eliminar el alias.',
                                      );
                                      return;
                                    }
                                    setLoginEmailAliases(Array.isArray(payload.email_aliases) ? payload.email_aliases : []);
                                    pushSecurityFeedback('Login alias removed.', 'Alias de inicio de sesión eliminado.');
                                  } catch {
                                    pushSecurityFeedback('Alias could not be removed.', 'No se pudo eliminar el alias.');
                                  } finally {
                                    setIsSecurityAliasBusy(false);
                                  }
                                }}
                              >
                                {language === 'es' ? 'Eliminar' : 'Remove'}
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p>{language === 'es' ? 'No hay alias de inicio de sesión configurados.' : 'No login aliases configured.'}</p>
                      )}
                    </>
                  ) : null}
                  {activeSecurityOption === 'backup-codes' ? (
                    <>
                      <h4>{copy.securityBackupCodesTitle}</h4>
                      <p>{`${backupCodesCount} ${language === 'es' ? 'códigos disponibles' : 'codes available'}`}</p>
                      <button
                        type="button"
                        className="dashboard-btn"
                        onClick={() => {
                          setBackupCodesCount(10);
                          pushSecurityFeedback('Backup codes were regenerated.', 'Los códigos de respaldo fueron regenerados.');
                        }}
                      >
                        {language === 'es' ? 'Regenerar códigos de respaldo' : 'Regenerate backup codes'}
                      </button>
                    </>
                  ) : null}
                </div>
              ) : null}
              <p className="dashboard-security-help-label">{copy.securityAddOptionsLabel}</p>
              <div className="dashboard-security-chip-list">
                {securityOptionChips.map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    className={`dashboard-security-chip dashboard-security-chip-btn ${chip.enabled ? 'enabled' : ''}`}
                    onClick={chip.onAction}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
                  </div>
                ) : null}
              </div>

              <div className={`dashboard-payments-accordion-item${securityAccordion.devices ? ' is-open' : ''}`}>
                <h3 className="dashboard-payments-accordion-heading">
                  <button
                    type="button"
                    className="dashboard-payments-accordion-trigger"
                    aria-expanded={securityAccordion.devices}
                    aria-controls="security-acc-devices-panel"
                    id="security-acc-devices-btn"
                    onClick={() => toggleSecurityAccordion('devices')}
                  >
                    <span className="dashboard-security-item-icon" aria-hidden="true"><SecurityRowIcon name="device" /></span>
                    <span className="dashboard-action-copy">
                      <strong>{copy.securityDevicesTitleBlock}</strong>
                      <span>{copy.securityDevicesSubtitle}</span>
                    </span>
                    <span className="dashboard-payments-accordion-chevron" aria-hidden="true">{securityAccordion.devices ? '▾' : '▸'}</span>
                  </button>
                </h3>
                {securityAccordion.devices ? (
                  <div
                    id="security-acc-devices-panel"
                    role="region"
                    aria-labelledby="security-acc-devices-btn"
                    className="dashboard-payments-panel"
                  >
              <div className="dashboard-security-list-card">
                {securityDeviceRows.map((item) => (
                  <div key={item.key} className="dashboard-security-device-group">
                    <button
                      type="button"
                      className="dashboard-security-list-row dashboard-security-list-row-btn"
                      onClick={() => toggleDeviceGroupAccordion(item.key as DeviceGroupKey)}
                      aria-expanded={expandedDeviceGroups.includes(item.key as DeviceGroupKey)}
                    >
                      <span className="dashboard-security-item-icon" aria-hidden="true"><SecurityRowIcon name={item.icon} /></span>
                      <div className="dashboard-security-row-copy">
                        <strong>{item.title}</strong>
                        {item.detail ? <p>{item.detail}</p> : null}
                      </div>
                      <span className="dashboard-security-row-affordance">{copy.securityDeviceAccordionHint}</span>
                    </button>
                    {expandedDeviceGroups.includes(item.key as DeviceGroupKey) ? (
                      <div className="dashboard-security-device-accordion" role="region" aria-label={`${item.title} details`}>
                        {item.sessions.length === 0 ? (
                          <p className="dashboard-security-device-empty">{copy.securityNoSessions}</p>
                        ) : (
                          item.sessions.map((session) => (
                            <div key={session.id} className="dashboard-security-device-session">
                              <div className="dashboard-security-device-session-copy">
                                <strong>{session.label}</strong>
                                <p>{session.browser} · IP: {session.ipAddress}</p>
                                <p>{session.location}</p>
                                <p>{language === 'es' ? 'Última actividad' : 'Last seen'}: {session.lastSeen}</p>
                                <p>{copy.securityDeviceAuthorized}</p>
                              </div>
                              <div className="dashboard-security-device-actions">
                                <button type="button" className="dashboard-security-link-btn" onClick={() => openSessionLocation(session)}>{copy.securityFindLocation}</button>
                                {isDeviceManagementOpen ? (
                                  <button
                                    type="button"
                                    className="dashboard-security-link-btn dashboard-security-link-btn-danger"
                                    onClick={() => removeDeviceSession(item.key as DeviceGroupKey, session.id, session.label)}
                                  >
                                    {copy.securityDeleteDevice}
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    ) : null}
                  </div>
                ))}
                <button
                  type="button"
                  className="dashboard-security-link-btn"
                  onClick={toggleManageAllDevices}
                >
                  {copy.securityManageAllDevices}
                </button>
              </div>
                  </div>
                ) : null}
              </div>
            </div>
            {securityFeedback ? <p className="dashboard-form-success">{securityFeedback}</p> : null}
          </article>
        </section>
      );
    }

    if (activeNavItem === 'notifications') {
      return (
        <section className="dashboard-settings-sections dashboard-settings-sections-centered" aria-label={copy.notificationsPanelLabel}>
          <article className="card settings-card">
            <div className="settings-card-header">
              <h2>{copy.navNotifications}</h2>
            </div>
            <ul className="settings-options">
              <li>{copy.notifCritical}</li>
              <li>{copy.notifRisk}</li>
              <li>{copy.notifWeekly}</li>
              <li>{copy.notifRelease}</li>
            </ul>
            <form className="dashboard-notifications-form" onSubmit={handleNotificationsSubmit}>
              <h3>{copy.notificationsEmailTitle}</h3>
              <input
                type="email"
                value={alertEmail}
                onChange={(event) => setAlertEmail(event.target.value)}
                placeholder={copy.notificationsEmailLabel}
                required
              />
              <div className="dashboard-alert-checklist" aria-label={copy.notificationsServiceChecklist}>
                <p><strong>{copy.notificationsServiceChecklist}</strong></p>
                {availableDashboards.length === 0 ? (
                  <p>{copy.notificationsEmptyServices}</p>
                ) : (
                  availableDashboards.map((serviceName) => (
                    <label key={`alert-${serviceName}`} className="dashboard-alert-checklist-item">
                      <input
                        type="checkbox"
                        checked={alertChecklist[serviceName] ?? false}
                        onChange={() => toggleServiceAlert(serviceName)}
                      />
                      <span>{serviceName}</span>
                    </label>
                  ))
                )}
              </div>
              {alertFeedback && <p className="dashboard-form-success">{alertFeedback}</p>}
              <button type="submit" className="dashboard-btn dashboard-btn-primary">{copy.notificationsEmailAction}</button>
            </form>
          </article>
        </section>
      );
    }

    if (activeNavItem === 'privacy') {
      // Privacy policy belongs in the public footer (/privacy), not the product console.
      navigate('/privacy');
      return renderHomePane();
    }

    if (activeNavItem === 'payments') {
      if (!canManageBilling && !hasSuperUserAccess) {
        return renderHomePane();
      }
      return (
        <section className="dashboard-settings-sections dashboard-settings-sections-centered" aria-label={copy.paymentsPanelLabel}>
          <article className="card settings-card">
            <div className="settings-card-header">
              <h2>{hasSuperUserAccess ? copy.navPayments : (language === 'es' ? 'Facturación' : 'Billing')}</h2>
            </div>
            <p className="dashboard-panel-subtitle">
              {hasSuperUserAccess
                ? copy.paymentsOverviewCopy
                : (language === 'es'
                  ? 'Gestiona métodos de pago, facturas y tu plan de NextCompli en un solo lugar.'
                  : 'Manage payment methods, invoices, and your NextCompli plan in one place.')}
            </p>
            <div className="dashboard-payments-accordion" role="presentation">
              <div className={`dashboard-payments-accordion-item${paymentsAccordion.methods ? ' is-open' : ''}`}>
                <h3 className="dashboard-payments-accordion-heading">
                  <button
                    type="button"
                    id="payments-acc-methods-btn"
                    className="dashboard-payments-accordion-trigger"
                    aria-expanded={paymentsAccordion.methods}
                    aria-controls="payments-acc-methods-panel"
                    onClick={() => togglePaymentsAccordion('methods')}
                  >
                    <span className="dashboard-action-icon" aria-hidden="true">💳</span>
                    <span className="dashboard-action-copy">
                      <strong>{copy.paymentsMethodTitle}</strong>
                      <span>{copy.paymentsMethodBody}</span>
                    </span>
                    <span className="dashboard-payments-accordion-chevron" aria-hidden="true">{paymentsAccordion.methods ? '▾' : '▸'}</span>
                  </button>
                </h3>
                {paymentsAccordion.methods ? (
                  <div
                    id="payments-acc-methods-panel"
                    role="region"
                    aria-labelledby="payments-acc-methods-btn"
                    className="dashboard-payments-panel"
                  >
                    <p className="dashboard-panel-subtitle">{language === 'es'
                      ? 'Agrega y administra tarjetas y cuentas bancarias (ACH) para pagos de la cuenta.'
                      : 'Add and manage cards and bank accounts (ACH) for account payments.'}</p>

                    <div className="dashboard-payment-type-toggle" role="tablist" aria-label="Payment type">
                      <button type="button" className={`dashboard-security-chip dashboard-security-chip-btn ${paymentEntryType === 'card' ? 'enabled' : ''}`} onClick={() => setPaymentEntryType('card')}>
                        {language === 'es' ? 'Tarjeta' : 'Credit card'}
                      </button>
                      <button type="button" className={`dashboard-security-chip dashboard-security-chip-btn ${paymentEntryType === 'bank' ? 'enabled' : ''}`} onClick={() => setPaymentEntryType('bank')}>
                        {language === 'es' ? 'Banco (ACH)' : 'Bank (ACH)'}
                      </button>
                    </div>

                    <form className="dashboard-password-form" onSubmit={handleAddPaymentMethod}>
                      {paymentEntryType === 'card' ? (
                        <>
                          <input type="text" placeholder={language === 'es' ? 'Nombre del titular' : 'Cardholder name'} value={cardholderName} onChange={(event) => setCardholderName(event.target.value)} required />
                          <input type="text" placeholder={language === 'es' ? 'Últimos 4 dígitos de la tarjeta' : 'Card last 4 digits'} value={cardNumberTail} onChange={(event) => setCardNumberTail(event.target.value)} required />
                        </>
                      ) : (
                        <>
                          <input type="text" placeholder={language === 'es' ? 'Nombre del banco' : 'Bank name'} value={bankName} onChange={(event) => setBankName(event.target.value)} required />
                          <input type="text" placeholder={language === 'es' ? 'Últimos 4 dígitos de la cuenta' : 'Bank account last 4 digits'} value={bankAccountTail} onChange={(event) => setBankAccountTail(event.target.value)} required />
                        </>
                      )}
                      {paymentFormError && <p className="dashboard-form-error" role="alert">{paymentFormError}</p>}
                      {paymentFormFeedback && <p className="dashboard-form-success">{paymentFormFeedback}</p>}
                      <div className="dashboard-action-buttons">
                        <button type="submit" className="dashboard-btn dashboard-btn-primary">{language === 'es' ? 'Guardar método' : 'Save method'}</button>
                      </div>
                    </form>

                    <div className="dashboard-payment-methods-list">
                      {savedPaymentMethods.map((method) => (
                        <div key={method.id} className="dashboard-payment-method-item">
                          <div>
                            <strong>{method.label}</strong>
                            <p>{method.detail}{method.isDefault ? ` · ${language === 'es' ? 'Predeterminado' : 'Default'}` : ''}</p>
                          </div>
                          {!method.isDefault && (
                            <button type="button" className="dashboard-security-inline-btn" onClick={() => makeDefaultPaymentMethod(method.id)}>
                              {language === 'es' ? 'Hacer predeterminado' : 'Make default'}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    <ul className="dashboard-payment-best-practices">
                      <li>{language === 'es' ? 'Usar tokenización y nunca almacenar números completos de tarjeta o cuenta.' : 'Use tokenization and never store full card/account numbers.'}</li>
                      <li>{language === 'es' ? 'Aplicar 3D Secure/SCA para pagos con tarjeta cuando corresponda.' : 'Apply 3D Secure/SCA for card payments where applicable.'}</li>
                      <li>{language === 'es' ? 'Verificar cuentas bancarias (instantánea o microdepósitos) antes de débito ACH.' : 'Verify bank accounts (instant verification or microdeposits) before ACH debit.'}</li>
                      <li>{language === 'es' ? 'Usar webhooks para estados asíncronos de pago, devoluciones y contracargos.' : 'Use webhooks for async payment status, refunds, and chargebacks.'}</li>
                    </ul>
                  </div>
                ) : null}
              </div>

              <div className={`dashboard-payments-accordion-item${paymentsAccordion.info ? ' is-open' : ''}`}>
                <h3 className="dashboard-payments-accordion-heading">
                  <button
                    type="button"
                    id="payments-acc-info-btn"
                    className="dashboard-payments-accordion-trigger"
                    aria-expanded={paymentsAccordion.info}
                    aria-controls="payments-acc-info-panel"
                    onClick={() => togglePaymentsAccordion('info')}
                  >
                    <span className="dashboard-action-icon" aria-hidden="true">🧾</span>
                    <span className="dashboard-action-copy">
                      <strong>{copy.paymentsInfoTitle}</strong>
                      <span>{copy.paymentsInfoBody}</span>
                    </span>
                    <span className="dashboard-payments-accordion-chevron" aria-hidden="true">{paymentsAccordion.info ? '▾' : '▸'}</span>
                  </button>
                </h3>
                {paymentsAccordion.info ? (
                  <div
                    id="payments-acc-info-panel"
                    role="region"
                    aria-labelledby="payments-acc-info-btn"
                    className="dashboard-payments-panel"
                  >
                    <div className="dashboard-payment-info-grid">
                      <div className="dashboard-payment-info-row"><strong>INV-2026-0018</strong><span>{language === 'es' ? 'Pagado' : 'Paid'} · $99.00 · 2026-02-01</span></div>
                      <div className="dashboard-payment-info-row"><strong>INV-2026-0017</strong><span>{language === 'es' ? 'Pagado' : 'Paid'} · $99.00 · 2026-01-01</span></div>
                      <div className="dashboard-payment-info-row"><strong>INV-2026-0012</strong><span>{language === 'es' ? 'Pagado' : 'Paid'} · $99.00 · 2025-12-01</span></div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className={`dashboard-payments-accordion-item${paymentsAccordion.plan ? ' is-open' : ''}`}>
                <h3 className="dashboard-payments-accordion-heading">
                  <button
                    type="button"
                    id="payments-acc-plan-btn"
                    className="dashboard-payments-accordion-trigger"
                    aria-expanded={paymentsAccordion.plan}
                    aria-controls="payments-acc-plan-panel"
                    onClick={() => togglePaymentsAccordion('plan')}
                  >
                    <span className="dashboard-action-icon" aria-hidden="true">🗂️</span>
                    <span className="dashboard-action-copy">
                      <strong>{copy.paymentsSubscriptionsTitle}</strong>
                      <span>{copy.paymentsSubscriptionsBody}</span>
                    </span>
                    <span className="dashboard-payments-accordion-chevron" aria-hidden="true">{paymentsAccordion.plan ? '▾' : '▸'}</span>
                  </button>
                </h3>
                {paymentsAccordion.plan ? (
                  <div
                    id="payments-acc-plan-panel"
                    role="region"
                    aria-labelledby="payments-acc-plan-btn"
                    className="dashboard-payments-panel"
                  >
                    <CustomerBillingPanel
                      language={language}
                      gatewayBaseUrl={gatewayBaseUrl}
                      accessToken={accessToken}
                      showAdminRevenue={false}
                      effectiveFeatures={effectiveFeatureList}
                      accountEmail={accountEmail || user?.email || null}
                      audience="self-serve"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </article>
        </section>
      );
    }

    if (activeNavItem === 'billing') {
      if (!hasSuperUserAccess) {
        // Subscribers use the unified Billing (payments) surface — avoid a duplicate page.
        navigate('/console/payments');
        return null;
      }
      return (
        <section className="dashboard-settings-sections dashboard-settings-sections-centered" aria-label={copy.billingPanelLabel}>
          <article className="card settings-card">
            <div className="settings-card-header">
              <h2>{copy.navBilling}</h2>
            </div>
            <CustomerBillingPanel
              language={language}
              gatewayBaseUrl={gatewayBaseUrl}
              accessToken={accessToken}
              showAdminRevenue={hasPlatformAdminAccess}
              effectiveFeatures={effectiveFeatureList}
              accountEmail={accountEmail || user?.email || null}
              audience="operator"
            />
          </article>
        </section>
      );
    }


    if (activeNavItem === 'subscriptions') {
      if (!hasSuperUserAccess) {
        return renderHomePane();
      }
      return <SubscriptionsDashboard showAuditLog={hasSuperUserAccess} />;
    }

    if (activeNavItem === 'subscriptionHub') {
      return (
        <SubscriptionHubPanel
          language={language}
          canManageCatalog={hasSuperUserAccess}
          onOpen={(target: SubscriptionHubTarget) => handleNavSelection(target)}
        />
      );
    }

    if (activeNavItem === 'subscriptionLevels' || activeNavItem === 'payingSubscriptions' || activeNavItem === 'expiredSubscriptions' || activeNavItem === 'mySubscriptions') {
      if (activeNavItem !== 'mySubscriptions' && !hasSuperUserAccess) {
        return renderHomePane();
      }
      const mode = activeNavItem === 'subscriptionLevels'
        ? 'levels'
        : activeNavItem === 'payingSubscriptions'
          ? 'paying'
          : activeNavItem === 'mySubscriptions'
            ? 'mine'
            : 'expired';
      return (
        <SubscriptionCatalogPanel
          mode={mode}
          language={language}
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          onAccessDenied={denyStaffNav}
        />
      );
    }

    if (activeNavItem === 'team') {
      if (!canManageSubscriptionTeam) {
        return renderHomePane();
      }
      return (
        <section className="dashboard-settings-sections dashboard-settings-sections-wide" aria-label={copy.navTeam}>
          <SubscriptionTeamPanel
            language={language}
            gatewayBaseUrl={gatewayBaseUrl}
            accessToken={accessToken || undefined}
            mode="account"
          />
        </section>
      );
    }

    if (activeNavItem === 'tips') {
      if (!hasSuperUserAccess) {
        return renderHomePane();
      }

      return (
        <section className="dashboard-settings-sections dashboard-settings-sections-centered" aria-label={copy.tipsPanelLabel}>
          <article className="card settings-card">
            <div className="settings-card-header">
              <h2>{copy.navTips}</h2>
            </div>
            <p className="dashboard-panel-subtitle">
              {language === 'es'
                ? 'Visualiza y modifica los consejos del día para usuarios no administradores.'
                : 'View and update Tip of the Day entries for non-admin users.'}
            </p>

            <div className="dashboard-subscription-plan-list">
              {tipCatalog.map((tip) => (
                <div key={tip.id} className="dashboard-subscription-plan-item">
                  <div>
                    <strong>{tip.title[language]}</strong>
                    <p>{tip.body[language]}</p>
                  </div>
                  <div className="dashboard-action-buttons">
                    <button type="button" className="dashboard-security-inline-btn" onClick={() => handleTipEdit(tip.id)}>
                      {language === 'es' ? 'Editar' : 'Edit'}
                    </button>
                    <button type="button" className="dashboard-security-inline-btn" onClick={() => handleTipDelete(tip.id)}>
                      {language === 'es' ? 'Eliminar' : 'Delete'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <form className="dashboard-password-form" onSubmit={handleTipSubmit}>
              <input
                type="text"
                placeholder="Tip title (English)"
                value={tipForm.titleEn}
                onChange={(event) => setTipForm((current) => ({ ...current, titleEn: event.target.value }))}
                required
              />
              <input
                type="text"
                placeholder="Titulo del consejo (Español)"
                value={tipForm.titleEs}
                onChange={(event) => setTipForm((current) => ({ ...current, titleEs: event.target.value }))}
                required
              />
              <textarea
                placeholder="Tip body (English)"
                value={tipForm.bodyEn}
                onChange={(event) => setTipForm((current) => ({ ...current, bodyEn: event.target.value }))}
                required
              />
              <textarea
                placeholder="Contenido del consejo (Español)"
                value={tipForm.bodyEs}
                onChange={(event) => setTipForm((current) => ({ ...current, bodyEs: event.target.value }))}
                required
              />
              <select
                value={tipForm.navItem}
                onChange={(event) => setTipForm((current) => ({ ...current, navItem: event.target.value as DashboardNavItemId }))}
              >
                <option value="dashboards">Dashboards</option>
                <option value="ofac">OFAC Compliance</option>
                <option value="personal">Personal info</option>
                <option value="security">Security & sign-in</option>
                <option value="payments">Payments & subscriptions</option>
                <option value="subscriptions">Subscriptions</option>
                <option value="websiteCompliance">Website Compliance</option>
                <option value="dodContracts">Government Compliance</option>
                <option value="educationCompliance">Education Compliance</option>
              </select>
              <input
                type="text"
                placeholder="Action label (English)"
                value={tipForm.actionLabelEn}
                onChange={(event) => setTipForm((current) => ({ ...current, actionLabelEn: event.target.value }))}
              />
              <input
                type="text"
                placeholder="Etiqueta de acción (Español)"
                value={tipForm.actionLabelEs}
                onChange={(event) => setTipForm((current) => ({ ...current, actionLabelEs: event.target.value }))}
              />
              {tipError ? <p className="dashboard-form-error" role="alert">{tipError}</p> : null}
              {tipFeedback ? <p className="dashboard-form-success">{tipFeedback}</p> : null}
              <div className="dashboard-action-buttons">
                <button type="submit" className="dashboard-btn dashboard-btn-primary">
                  {editingTipId ? (language === 'es' ? 'Actualizar consejo' : 'Update tip') : (language === 'es' ? 'Crear consejo' : 'Create tip')}
                </button>
                {editingTipId ? (
                  <button type="button" className="dashboard-btn" onClick={resetTipForm}>
                    {language === 'es' ? 'Cancelar edición' : 'Cancel edit'}
                  </button>
                ) : null}
              </div>
            </form>
          </article>
        </section>
      );
    }

    if (activeNavItem === 'ofacDashboards') {
      if (!canAccessOfac) {
        return renderHomePane();
      }
      const ofacKpi = (id: string) => Number(ofacComplianceDashboard?.kpis?.find((row) => row.id === id)?.value || 0);
      const ofacLive: OfacDashboardLive = {
        queueCount: ofacComplianceDashboard?.queue?.length || 0,
        screened30d: ofacKpi('screened_30d'),
        openClocks: Number(ofacComplianceDashboard?.reporting_clocks?.open || 0),
        overdueClocks: Number(ofacComplianceDashboard?.reporting_clocks?.overdue || 0),
        noticeCount: ofacComplianceDashboard?.email_notices?.length || 0,
        planLabel: ofacComplianceDashboard?.plan_coverage?.plan_label || '',
        lists: (ofacComplianceDashboard?.list_coverage || []).map((row) => ({
          name: row.name,
          freshness: row.freshness,
          status: row.status,
        })),
        clearCount: ofacKpi('clear'),
        reviewCount: ofacKpi('in_review'),
        hitCount: ofacKpi('potential_hit'),
        volumePoints: ofacComplianceDashboard?.trend?.volume_points || [],
        exceptionPoints: ofacComplianceDashboard?.trend?.exception_points || [],
      };
      return (
        <OfacDashboardsPanel
          language={language === 'es' ? 'es' : 'en'}
          accountId=""
          accountLabel={accountDisplayName || accountEmail || (language === 'es' ? 'esta suscripción' : 'this subscription')}
          live={ofacLive}
          onOpen={(nav) => handleNavSelection(nav)}
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken || undefined}
        />
      );
    }

    if (activeNavItem === 'ofac' || activeNavItem === 'ofacCompliance') {
      if (!canAccessOfac) {
        return renderHomePane();
      }
      return (
        <>
          {ofacComplianceError ? <p className="dashboard-ofac-note" role="alert">{ofacComplianceError}</p> : null}
          <ComplianceConsolePanel
            definition={OFAC_COMPLIANCE_CONSOLE}
            language={language}
            reviewerLabel={accountEmail || accountDisplayName}
            gatewayBaseUrl={gatewayBaseUrl}
            accessToken={accessToken}
            platformAdmin={hasPlatformAdminAccess}
            effectiveFeatures={effectiveFeatureList}
            workspaceReadOnly={!canWriteWorkspace}
          />
        </>
      );
    }

    if (activeNavItem === 'ofacLiveFeed') {
      if (!canAccessOfac) {
        return renderHomePane();
      }
      // embed=1 strips classic SPA breadcrumb/title chrome so console breadcrumb is the only menu layer.
      // theme= mirrors CompliCore appearance so the classic dashboard iframe is not stuck in light mode.
      const liveFeedTheme = resolveThemeMode(appearancePreferences.themeMode);
      const liveFeedSrc = `/dashboard/index.html?embed=1&theme=${encodeURIComponent(liveFeedTheme)}#service/ofac_general_licenses`;
      const liveFeedTitle = `${copy.navOfacGroup} ${copy.navOfacLiveFeed}`;
      const emailNotices = Array.isArray(ofacComplianceDashboard?.email_notices)
        ? ofacComplianceDashboard.email_notices
        : [];
      return (
        <section
          className="dashboard-settings-sections dashboard-settings-sections-research dashboard-ofac-live-feed"
          aria-label={liveFeedTitle}
        >
          <article className="dashboard-ofac-notices" aria-label={language === 'es' ? 'Avisos OFAC por correo' : 'OFAC email notices'}>
            <div className="dashboard-ofac-notices-head">
              <h2>{language === 'es' ? 'Avisos OFAC (GovDelivery)' : 'OFAC email notices'}</h2>
              <p>
                {language === 'es'
                  ? `Buzón ${ofacComplianceDashboard?.email_notice_mailbox || 'operations@nextcompli.com'}. El correo dispara la recarga de SDN, CAPTA, licencias generales y acciones recientes. SLS sigue siendo la fuente oficial de las listas.`
                  : `Mailbox ${ofacComplianceDashboard?.email_notice_mailbox || 'operations@nextcompli.com'}. Incoming Treasury mail kicks SDN, CAPTA, general-license, and recent-actions refresh. SLS remains the source of record for list files.`}
              </p>
              <p>
                {canAccessContinuousMonitoring
                  ? (language === 'es'
                    ? 'CompliAdvantage re-criba la cartera automáticamente cuando entra un aviso. Puede forzar un re-cribado ahora.'
                    : 'CompliAdvantage auto-rescreens the book when a notice arrives. You can still force a rescreen now.')
                  : (language === 'es'
                    ? 'CompliCore: tras un aviso, pulse Re-cribar cartera. El re-cribado automático es CompliAdvantage.'
                    : 'CompliCore: after a notice, click Rescreen book. Automatic book rescreen is CompliAdvantage.')}
              </p>
              {ofacComplianceDashboard?.plan_coverage?.plan_label ? (
                <p className="research-form-panel-meta">
                  {language === 'es'
                    ? `Plan actual: ${ofacComplianceDashboard.plan_coverage.plan_label} (${ofacComplianceDashboard.plan_coverage.rescreen_mode === 'automatic' ? 're-cribado automático' : 're-cribado del operador'}).`
                    : `Current plan: ${ofacComplianceDashboard.plan_coverage.plan_label} (${ofacComplianceDashboard.plan_coverage.rescreen_mode === 'automatic' ? 'automatic rescreen' : 'operator rescreen'}).`}
                </p>
              ) : null}
              <div className="dashboard-ofac-notices-actions">
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn-primary"
                  disabled={ofacScreeningScreeningAll}
                  onClick={() => void handleOfacRescreenBook()}
                >
                  {ofacScreeningScreeningAll
                    ? (language === 'es' ? 'Re-cribando…' : 'Rescreening…')
                    : (language === 'es' ? 'Re-cribar cartera' : 'Rescreen book')}
                </button>
                <button type="button" className="dashboard-security-inline-btn" onClick={() => handleNavSelection('ofacScreening')}>
                  {language === 'es' ? 'Abrir Screening' : 'Open Screening'}
                </button>
              </div>
              {ofacScreeningError ? <p className="dashboard-form-error" role="alert">{ofacScreeningError}</p> : null}
              {ofacScreeningMessage ? <p className="dashboard-form-success">{ofacScreeningMessage}</p> : null}
            </div>
            {emailNotices.length === 0 ? (
              <p className="dashboard-ofac-notices-empty">
                {language === 'es'
                  ? 'Aún no hay avisos integrados. El sondeo IMAP y el archivo de acciones recientes se cargan al arrancar el gateway.'
                  : 'No notices ingested yet. IMAP polling and the recent-actions archive load after gateway startup.'}
              </p>
            ) : (
              <ul className="dashboard-ofac-notices-list">
                {emailNotices.slice(0, 12).map((notice) => {
                  const href = Array.isArray(notice.recent_action_urls) ? notice.recent_action_urls[0] : '';
                  const meta = [
                    notice.sent_at,
                    ...(notice.lists || []),
                    ...(notice.general_licenses || []).map((item) => `GL ${item}`),
                  ].filter(Boolean).join(' · ');
                  const title = notice.title || 'OFAC notice';
                  return (
                    <li key={notice.notice_id || title}>
                      {href ? (
                        <a href={href} target="_blank" rel="noreferrer">{title}</a>
                      ) : (
                        <span>{title}</span>
                      )}
                      {meta ? <small>{meta}</small> : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </article>
          <article className="dashboard-ofac-live-feed-shell">
            <iframe
              key={`ofac-live-feed-${liveFeedTheme}`}
              className="dashboard-ofac-live-feed-frame"
              title={liveFeedTitle}
              src={liveFeedSrc}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </article>
        </section>
      );
    }

    if (activeNavItem === 'ofacScreening') {
      if (!canAccessOfac) {
        return renderHomePane();
      }
      const relationshipLabel = (value: string) => {
        const match = OFAC_SCREENING_RELATIONSHIP_OPTIONS.find((option) => option.value === value);
        if (!match) {
          return value;
        }
        return language === 'es' ? match.labelEs : match.labelEn;
      };
      const filteredSubjects = ofacScreeningSubjects.filter((subject) => {
        if (ofacScreeningRoleFilter !== 'all' && subject.relationship_type !== ofacScreeningRoleFilter) {
          return false;
        }
        if (ofacScreeningStatusFilter !== 'all') {
          const status = String(subject.current_status || '').trim().toUpperCase();
          const bucket = (
            ['CLEAR', 'NO_MATCH', 'OK'].includes(status)
              ? 'clear'
              : ['BLOCKED', 'HIT', 'MATCH', 'POTENTIAL_HIT'].includes(status)
                ? 'hit'
                : ['REVIEW_REQUIRED', 'REVIEW', 'NEEDS_REVIEW', 'ERROR', 'UNKNOWN'].includes(status)
                  ? 'review'
                  : 'pending'
          );
          if (bucket !== ofacScreeningStatusFilter) {
            return false;
          }
        }
        const needle = ofacScreeningFilter.trim().toLowerCase();
        if (!needle) {
          return true;
        }
        return [
          subject.entity_name,
          subject.relationship_type,
          subject.entity_type,
          subject.source,
          subject.current_status || '',
        ].join(' ').toLowerCase().includes(needle);
      });
      return (
        <section
          className="dashboard-settings-sections dashboard-settings-sections-wide"
          aria-label={`${copy.navOfacGroup} ${copy.navOfacScreening}`}
        >
          <article className="card settings-card">
            <div className="settings-card-header">
              <h2>{`${copy.navOfacGroup} ${copy.navOfacScreening}`}</h2>
            </div>
            <p className="dashboard-panel-subtitle">
              {language === 'es'
                ? 'Cartera viva de sujetos OFAC (clientes, proveedores, empleados, contratistas, UBOs, activos). Añade uno a uno, importa un CSV, o marca “Añadir a Screening OFAC” en Research. Empleado o contratista abre el checklist de contratación. La lista blanca es otra vista: personas ya autorizadas, con fecha de última validación. Ninguna omite SDN.'
                : 'Live OFAC book of anyone you currently deal with (customers, vendors, employees, contractors, UBOs, assets). Add one by one, import a CSV, or check “Add to OFAC Screening list” in Research. Employee or contractor opens the hire checklist. Whitelist is the dated clearance roster — last validated date, with a way to rerun. Neither skips SDN.'}
              <HelpBadge label={language === 'es' ? 'Ayuda sobre Screening OFAC' : 'Help about OFAC Screening'}>
                <p>
                  {language === 'es'
                    ? 'Usa identificadores fuertes (fecha de nacimiento, EIN, IMO) para reducir falsos positivos. El mismo sujeto con el mismo rol se actualiza en lugar de duplicarse.'
                    : 'Use strong identifiers (date of birth, EIN, IMO) to reduce false positives. The same subject with the same role updates in place instead of creating a duplicate.'}
                </p>
              </HelpBadge>
            </p>
            {gatewayBaseUrl && accessToken ? (
              <FeatureAgentGates
                feature="ofac-compliance"
                language={language}
                gatewayBaseUrl={gatewayBaseUrl}
                accessToken={String(accessToken)}
              />
            ) : null}

            <details className="dashboard-payments-panel dashboard-ofac-screening-accordion">
              <summary className="dashboard-ofac-screening-accordion__summary">
                <span>{language === 'es' ? 'Añadir sujeto' : 'Add subject'}</span>
                <span className="dashboard-ofac-screening-accordion__chevron" aria-hidden="true" />
              </summary>
              <div className="dashboard-ofac-screening-accordion__body">
              <div className="dashboard-form-grid">
                <label>
                  <span>{language === 'es' ? 'Nombre' : 'Name'}</span>
                  <input
                    type="text"
                    value={ofacScreeningForm.entityName}
                    onChange={(event) => setOfacScreeningForm((current) => ({ ...current, entityName: event.target.value }))}
                    placeholder={language === 'es' ? 'Nombre de persona, empresa o activo' : 'Person, company, or asset name'}
                  />
                </label>
                <label>
                  <span className="help-badge-label-row">
                    {language === 'es' ? 'Rol / relación' : 'Role / relationship'}
                    <HelpBadge label={language === 'es' ? 'Ayuda sobre rol' : 'Help about role'}>
                      <p>
                        {language === 'es'
                          ? 'Describe por qué está en tu cartera (cliente, proveedor, empleado, contratista, UBO, buque, etc.). El rol forma parte de la clave única. Empleado y contratista abren el checklist de contratación: el sitio recopila, un revisor en EE.UU. firma.'
                          : 'Describe why they are in your portfolio (customer, vendor, employee/contractor, UBO, vessel, etc.). Role is part of the unique key. Employee and contractor open the hire checklist: local staff collect, a U.S. reviewer signs off.'}
                      </p>
                    </HelpBadge>
                  </span>
                  <select
                    value={ofacScreeningForm.relationshipType}
                    onChange={(event) => {
                      const relationshipType = event.target.value as OfacScreeningRelationshipType;
                      setOfacScreeningForm((current) => ({
                        ...current,
                        relationshipType,
                        entityType: ofacEntityTypeForRelationship(relationshipType),
                      }));
                    }}
                  >
                    {OFAC_SCREENING_RELATIONSHIP_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {language === 'es' ? option.labelEs : option.labelEn}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className="help-badge-label-row">
                    {language === 'es' ? 'Identificador (opcional)' : 'Identifier (optional)'}
                    <HelpBadge label={language === 'es' ? 'Ayuda sobre identificador' : 'Help about identifier'}>
                      <p>
                        {language === 'es'
                          ? 'Ejemplos: EIN/registro para empresas, IMO para buques, matrícula para aeronaves. Mejora la coincidencia y la deduplicación.'
                          : 'Examples: EIN/registration for companies, IMO for vessels, tail number for aircraft. Improves matching and deduplication.'}
                      </p>
                    </HelpBadge>
                  </span>
                  <input
                    type="text"
                    value={ofacScreeningForm.identifier}
                    onChange={(event) => setOfacScreeningForm((current) => ({ ...current, identifier: event.target.value }))}
                    placeholder={
                      ofacScreeningForm.relationshipType === 'vessel'
                        ? 'IMO'
                        : ofacScreeningForm.relationshipType === 'aircraft'
                          ? (language === 'es' ? 'Matrícula' : 'Tail number')
                          : isOfacHireRelationship(ofacScreeningForm.relationshipType)
                            ? (language === 'es' ? 'ID nacional' : 'National ID')
                            : (language === 'es' ? 'ID / registro' : 'ID / registration')
                    }
                  />
                </label>
                <label>
                  <span className="help-badge-label-row">
                    {language === 'es' ? 'Fecha de nacimiento (personas)' : 'Birthdate (people)'}
                    <HelpBadge label={language === 'es' ? 'Ayuda sobre fecha de nacimiento' : 'Help about birthdate'}>
                      <p>
                        {language === 'es'
                          ? 'Recomendada para personas. Formato YYYY-MM-DD. Ayuda a distinguir homónimos en listas SDN.'
                          : 'Recommended for people. Use YYYY-MM-DD. Helps distinguish same-name hits on SDN lists.'}
                      </p>
                    </HelpBadge>
                  </span>
                  <input
                    type="date"
                    value={ofacScreeningForm.birthdate}
                    onChange={(event) => setOfacScreeningForm((current) => ({ ...current, birthdate: event.target.value }))}
                  />
                </label>
                {isOfacHireRelationship(ofacScreeningForm.relationshipType) ? (
                  <>
                    <label>
                      <span className="help-badge-label-row">
                        {language === 'es' ? 'País' : 'Country'}
                        <HelpBadge label={language === 'es' ? 'Ayuda sobre país' : 'Help about country'}>
                          <p>
                            {language === 'es'
                              ? 'Código ISO. Si es Venezuela (VE), aplica FAQ 680: personas privadas sí; GoV/SDN/50% no, salvo licencia. La GL 52C puede cubrir cierta actividad de PdVSA, pero vivir en una ciudad no autoriza la contratación.'
                              : 'ISO country code. If Venezuela (VE), FAQ 680 applies: private people yes; GoV/SDN/50% no, unless licensed. GL 52C can cover certain PdVSA activity, but living in a city is not hire clearance.'}
                          </p>
                        </HelpBadge>
                      </span>
                      <input
                        type="text"
                        value={ofacScreeningForm.country}
                        onChange={(event) => setOfacScreeningForm((current) => ({ ...current, country: event.target.value }))}
                        placeholder={language === 'es' ? 'Código ISO' : 'ISO code'}
                      />
                    </label>
                    <label>
                      <span>{language === 'es' ? 'Lugar de trabajo' : 'Work location'}</span>
                      <input
                        type="text"
                        value={ofacScreeningForm.hireLocation}
                        onChange={(event) => setOfacScreeningForm((current) => ({ ...current, hireLocation: event.target.value }))}
                        placeholder={language === 'es' ? 'Ciudad o sitio' : 'City or site'}
                      />
                    </label>
                  </>
                ) : null}
                <label>
                  <span>{language === 'es' ? 'Notas (opcional)' : 'Notes (optional)'}</span>
                  <input
                    type="text"
                    value={ofacScreeningForm.notes}
                    onChange={(event) => setOfacScreeningForm((current) => ({ ...current, notes: event.target.value }))}
                  />
                </label>
              </div>
              <label className="dashboard-checkbox-row">
                <input
                  type="checkbox"
                  checked={ofacScreeningForm.screenNow}
                  onChange={(event) => setOfacScreeningForm((current) => ({ ...current, screenNow: event.target.checked }))}
                />
                <span className="help-badge-label-row">
                  {language === 'es' ? 'Ejecutar screening ahora' : 'Screen now'}
                  <HelpBadge label={language === 'es' ? 'Ayuda sobre Screen now' : 'Help about Screen now'}>
                    <p>
                      {language === 'es'
                        ? 'Si está marcado, se consulta SDN al guardar. Para cargas grandes, importa primero y ejecuta screening por fila.'
                        : 'When checked, SDN is queried on save. For large uploads, import first, then screen row by row.'}
                    </p>
                  </HelpBadge>
                </span>
              </label>
              <div className="dashboard-action-buttons">
                <button type="button" className="dashboard-btn dashboard-btn-primary" onClick={() => void handleOfacScreeningAdd()}>
                  {language === 'es' ? 'Añadir a la lista' : 'Add to list'}
                </button>
              </div>
              </div>
            </details>

            <details className="dashboard-payments-panel dashboard-ofac-screening-accordion">
              <summary className="dashboard-ofac-screening-accordion__summary">
                <span>{language === 'es' ? 'Importar objetivos (CSV)' : 'Import targets (CSV)'}</span>
                <span className="dashboard-ofac-screening-accordion__chevron" aria-hidden="true" />
              </summary>
              <div className="dashboard-ofac-screening-accordion__body">
                {canAccessRealtimeBatchScreening ? (
                <>
                <div className="help-badge-label-row dashboard-ofac-screening-accordion__help">
                  <span>{language === 'es' ? 'Formato de importación' : 'Import format'}</span>
                  <HelpBadge label={language === 'es' ? 'Ayuda sobre importación CSV' : 'Help about CSV import'}>
                  <p>
                    {language === 'es'
                      ? 'Descarga la plantilla o el CSV de muestra. Solo incluye identidad de cartera (no asignado, disposición, FP ni latencia). Usa YYYY-MM-DD para fechas.'
                      : 'Download the template or sample CSV. It covers portfolio identity only (not assignee, disposition, FP rate, or latency). Use YYYY-MM-DD for dates.'}
                  </p>
                  </HelpBadge>
                </div>
              <p className="research-form-panel-meta">
                {language === 'es'
                  ? 'Columnas: name, entity_type, relationship, identifier, birthdate, country, hire_location, notes, screen_now. Usa employee o contractor para la vía de contratación.'
                  : 'Columns: name, entity_type, relationship, identifier, birthdate, country, hire_location, notes, screen_now. Keep the header row unchanged. Use employee or contractor for the hire pathway.'}
              </p>
              <label className="dashboard-checkbox-row">
                <input
                  type="checkbox"
                  checked={ofacScreeningImportScreenNow}
                  onChange={(event) => setOfacScreeningImportScreenNow(event.target.checked)}
                />
                <span>
                  {language === 'es'
                    ? 'Ejecutar screening al importar (incluye filas con screen_now=false)'
                    : 'Screen on import (overrides screen_now=false in the CSV)'}
                </span>
              </label>
              <div className="dashboard-action-buttons">
                <button
                  type="button"
                  className="dashboard-security-inline-btn"
                  onClick={() => downloadOfacScreeningCsvTemplate()}
                >
                  {language === 'es' ? 'Descargar plantilla CSV' : 'Download CSV template'}
                </button>
                <a
                  className="dashboard-security-inline-btn"
                  href="/samples/nextcompli-ofac-screening-sample.csv"
                  download
                >
                  {language === 'es' ? 'Descargar CSV de muestra' : 'Download sample CSV'}
                </a>
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn-primary"
                  disabled={ofacScreeningImporting}
                  onClick={() => ofacScreeningFileInputRef.current?.click()}
                >
                  {ofacScreeningImporting
                    ? (language === 'es' ? 'Importando…' : 'Importing…')
                    : (language === 'es' ? 'Subir CSV' : 'Upload CSV')}
                </button>
                <input
                  ref={ofacScreeningFileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  hidden
                  onChange={(event) => void handleOfacScreeningCsvSelected(event)}
                />
              </div>
                </>
                ) : (
                  <p className="research-form-panel-meta">
                    {language === 'es'
                      ? 'La importación CSV y el cribado por lote de partes de pago son CompliAdvantage (real-time + batch). En CompliCore añada un sujeto a la vez y use Screen now o Re-cribar cartera tras un aviso OFAC.'
                      : 'CSV import and payment-party batch screening are CompliAdvantage (real-time + batch). On CompliCore add one subject at a time, then Screen now or Rescreen book after an OFAC notice.'}
                  </p>
                )}
              </div>
            </details>

            <div className="dashboard-payments-panel" role="region" aria-label={language === 'es' ? 'Lista de screening' : 'Screening list'}>
              <div className="dashboard-ofac-screening-list__header">
                <h3>{language === 'es' ? 'Lista de screening' : 'Screening list'}</h3>
                <button
                  type="button"
                  className="dashboard-security-inline-btn"
                  onClick={() => void loadOfacScreeningSubjects()}
                  disabled={ofacScreeningLoading}
                >
                  {ofacScreeningLoading
                    ? (language === 'es' ? 'Actualizando…' : 'Refreshing…')
                    : (language === 'es' ? 'Actualizar' : 'Refresh')}
                </button>
              </div>
              <div className="dashboard-form-grid">
                <label>
                  <span>{language === 'es' ? 'Buscar' : 'Search'}</span>
                  <input
                    type="search"
                    value={ofacScreeningFilter}
                    onChange={(event) => setOfacScreeningFilter(event.target.value)}
                    placeholder={language === 'es' ? 'Filtrar por nombre, rol o estado' : 'Filter by name, role, or status'}
                  />
                </label>
                <label>
                  <span>{language === 'es' ? 'Rol' : 'Role'}</span>
                  <select
                    value={ofacScreeningRoleFilter}
                    onChange={(event) => setOfacScreeningRoleFilter(event.target.value as 'all' | OfacScreeningRelationshipType)}
                  >
                    <option value="all">{language === 'es' ? 'Todos' : 'All'}</option>
                    {OFAC_SCREENING_RELATIONSHIP_OPTIONS.map((option) => (
                      <option key={`filter-${option.value}`} value={option.value}>
                        {language === 'es' ? option.labelEs : option.labelEn}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>{language === 'es' ? 'Estado' : 'Status'}</span>
                  <select
                    value={ofacScreeningStatusFilter}
                    onChange={(event) => setOfacScreeningStatusFilter(event.target.value as typeof ofacScreeningStatusFilter)}
                  >
                    <option value="all">{language === 'es' ? 'Todos' : 'All'}</option>
                    <option value="clear">{language === 'es' ? 'Claro' : 'Clear'}</option>
                    <option value="review">{language === 'es' ? 'En revisión' : 'In review'}</option>
                    <option value="hit">{language === 'es' ? 'Posible hit' : 'Potential hit'}</option>
                    <option value="pending">{language === 'es' ? 'Pendiente / sin screen' : 'Pending / unscanned'}</option>
                  </select>
                </label>
              </div>
              <div className="dashboard-action-buttons" style={{ marginBottom: '0.75rem' }}>
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn-primary"
                  disabled={
                    ofacScreeningScreeningAll
                    || ofacScreeningLoading
                    || ofacScreeningImporting
                    || filteredSubjects.length === 0
                  }
                  onClick={() => void handleOfacScreeningScreenAll(filteredSubjects.map((subject) => subject.id))}
                >
                  {ofacScreeningScreeningAll
                    ? (language === 'es' ? 'Screening en curso…' : 'Screening all…')
                    : (language === 'es'
                      ? `Screen all now (${filteredSubjects.length})`
                      : `Screen all now (${filteredSubjects.length})`)}
                </button>
              </div>
              {ofacScreeningError ? <p className="dashboard-form-error" role="alert">{ofacScreeningError}</p> : null}
              {ofacScreeningMessage ? <p className="dashboard-form-success">{ofacScreeningMessage}</p> : null}
              {filteredSubjects.length > 0 ? (
                <div className="dashboard-subscription-plan-list">
                  {filteredSubjects.map((subject) => (
                    <div key={subject.id} className="dashboard-subscription-plan-item">
                      <div>
                        <strong>{subject.entity_name}</strong>
                        <p>
                          {relationshipLabel(String(subject.relationship_type))}
                          {' · '}
                          {subject.entity_type}
                          {' · '}
                          {subject.source}
                          {' · '}
                          {subject.current_status || (language === 'es' ? 'Sin screening' : 'Not screened')}
                          {typeof subject.risk_score === 'number'
                            ? ` · ${language === 'es' ? 'riesgo' : 'risk'} ${subject.risk_score} / 100`
                            : ''}
                          {subject.last_checked_at
                            ? ` · ${new Date(subject.last_checked_at).toLocaleString()}`
                            : ''}
                        </p>
                        {subject.match_rationale ? (
                          <p className="research-form-panel-meta" style={{ marginTop: '6px' }}>
                            {language === 'es' ? 'Racional de coincidencia' : 'Match rationale'}: {subject.match_rationale}
                          </p>
                        ) : null}
                        {subject.false_hit_summary?.active || subject.false_hit_summary?.pending ? (
                          <p className="research-form-panel-meta" style={{ marginTop: '4px' }}>
                            {language === 'es' ? 'Falsos hits' : 'False-hit pairs'}
                            {`: ${subject.false_hit_summary?.active || 0} ${language === 'es' ? 'activos' : 'active'}`}
                            {subject.false_hit_summary?.pending
                              ? ` · ${subject.false_hit_summary.pending} ${language === 'es' ? 'pendientes' : 'pending'}`
                              : ''}
                            {subject.last_suppressed_matches?.length
                              ? ` · ${subject.last_suppressed_matches.length} ${language === 'es' ? 'UID suprimidos en último screen' : 'UIDs suppressed on last screen'}`
                              : ''}
                          </p>
                        ) : null}
                        {subject.entity_uuid || subject.entity_key ? (
                          <p className="research-form-panel-meta" style={{ marginTop: '4px' }}>
                            UUID: {String(subject.entity_uuid || subject.entity_key)}
                          </p>
                        ) : null}
                      </div>
                      <div className="dashboard-action-buttons">
                        <button
                          type="button"
                          className="dashboard-security-inline-btn"
                          disabled={ofacScreeningScreeningAll || ofacScreeningBusyId === subject.id}
                          onClick={() => void handleOfacScreeningScreenNow(subject.id)}
                        >
                          {language === 'es' ? 'Screen ahora' : 'Screen now'}
                        </button>
                        <button
                          type="button"
                          className="dashboard-security-inline-btn"
                          disabled={ofacScreeningScreeningAll || ofacScreeningBusyId === subject.id}
                          onClick={() => void handleOfacScreeningRemove(subject.id)}
                        >
                          {language === 'es' ? 'Quitar' : 'Remove'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="dashboard-panel-subtitle">
                  {ofacScreeningLoading
                    ? (language === 'es' ? 'Cargando...' : 'Loading...')
                    : (language === 'es' ? 'La lista de screening está vacía.' : 'Screening list is empty.')}
                </p>
              )}
            </div>
          </article>
        </section>
      );
    }

    if (activeNavItem === 'ofacCapta') {
      if (!canAccessOfac) {
        return renderHomePane();
      }
      const CAPTA_TERMINAL = new Set(['cleared', 'false_positive']);
      // CAPTA page is a disposition workspace for correspondent / payable-through and
      // non-SDN consolidated exceptions — not the SDN adjudication queue and not 50% ownership.
      const exceptionSubjects = ofacScreeningSubjects.filter((subject) => {
        const disposition = String(
          subject.capta_disposition || subject.identifiers?.capta_disposition || 'pending',
        ).trim().toLowerCase();
        if (CAPTA_TERMINAL.has(disposition)) {
          return false;
        }
        const status = String(subject.current_status || '').trim().toUpperCase();
        const isListException = (
          status === 'REVIEW_REQUIRED'
          || status === 'REVIEW'
          || status === 'NEEDS_REVIEW'
          || status === 'HIT'
          || status === 'MATCH'
          || status === 'POTENTIAL_HIT'
          || status === 'BLOCKED'
          || (Number(subject.match_count || 0) > 0 && status !== 'CLEAR' && status !== 'NO_MATCH' && status !== 'OK')
        );
        const dispositionInProgress = disposition !== 'pending';
        return isListException || dispositionInProgress;
      });
      const glossaryDef = (key: keyof typeof OFAC_GLOSSARY) => (
        language === 'es' ? OFAC_GLOSSARY[key].es : OFAC_GLOSSARY[key].en
      );
      return (
        <section
          className="dashboard-settings-sections dashboard-settings-sections-wide dashboard-ofac-console"
          aria-label={`${copy.navOfacGroup} ${copy.navOfacCapta}`}
        >
          <article className="card settings-card dashboard-ofac-shell">
            <div className="settings-card-header">
              <h2 className="help-badge-label-row">
                {language === 'es' ? (
                  <>
                    {`${copy.navOfacGroup} Listas `}
                    <GlossaryTerm term="CAPTA" definition={glossaryDef('capta')}>CAPTA</GlossaryTerm>
                  </>
                ) : (
                  <>
                    {`${copy.navOfacGroup} `}
                    <GlossaryTerm term="CAPTA" definition={glossaryDef('capta')}>CAPTA</GlossaryTerm>
                    {' lists'}
                  </>
                )}
                <HelpBadge label={language === 'es' ? 'Ayuda sobre listas CAPTA' : 'Help about CAPTA lists'}>
                  <p>
                    {language === 'es'
                      ? 'CAPTA identifica instituciones financieras extranjeras con restricciones de cuenta corresponsal / payable-through. No es bloqueo SDN: no congela bienes de la entidad de forma general.'
                      : 'CAPTA identifies foreign financial institutions under correspondent / payable-through account restrictions. It is not SDN blocking: it does not generally freeze the entity’s property.'}
                  </p>
                  <p>
                    {language === 'es'
                      ? 'Las listas consolidadas (no SDN) se disponen aquí junto a CAPTA. Importa sujetos en Screening; usa esta página solo para disposición de excepciones de cuenta / lista consolidada.'
                      : 'Non-SDN consolidated lists are disposed here alongside CAPTA. Import subjects on Screening; use this page only for account-restriction / consolidated-list exception disposition.'}
                  </p>
                </HelpBadge>
              </h2>
            </div>
            <p className="dashboard-panel-subtitle dashboard-ofac-lede">
              {language === 'es' ? (
                <>
                  Cola de disposición{' '}
                  <GlossaryTerm term="CAPTA" definition={glossaryDef('capta')}>CAPTA</GlossaryTerm>
                  {' + consolidadas: excepciones abiertas que requieren análisis de relación de cuenta — distinto de un hit '}
                  <GlossaryTerm term="SDN" definition={glossaryDef('sdn')}>SDN</GlossaryTerm>
                  {' o de la '}
                  <GlossaryTerm term="50% rule" definition={glossaryDef('fiftyPercent')}>regla del 50%</GlossaryTerm>
                  .
                </>
              ) : (
                <>
                  <GlossaryTerm term="CAPTA" definition={glossaryDef('capta')}>CAPTA</GlossaryTerm>
                  {' + Consolidated disposition queue: open exceptions that need account-relationship analysis — different from an '}
                  <GlossaryTerm term="SDN" definition={glossaryDef('sdn')}>SDN</GlossaryTerm>
                  {' hit or the '}
                  <GlossaryTerm term="50% rule" definition={glossaryDef('fiftyPercent')}>50% rule</GlossaryTerm>
                  .
                </>
              )}
            </p>
            <div className="dashboard-ofac-kpi-grid dashboard-ofac-kpi-grid--compact" role="list">
              <div className={`dashboard-metric-kpi ${exceptionSubjects.length ? 'tone-warn' : 'tone-ok'}`} role="listitem">
                <em>{exceptionSubjects.length}</em>
                <span>{language === 'es' ? 'Excepciones CAPTA abiertas' : 'Open CAPTA exceptions'}</span>
              </div>
            </div>
            <div className="dashboard-ofac-panel-actions">
              <button
                type="button"
                className="dashboard-security-inline-btn"
                onClick={() => void loadOfacScreeningSubjects()}
                disabled={ofacScreeningLoading}
              >
                {ofacScreeningLoading
                  ? (language === 'es' ? 'Actualizando…' : 'Refreshing…')
                  : (language === 'es' ? 'Actualizar' : 'Refresh')}
              </button>
              <button type="button" className="dashboard-security-inline-btn" onClick={() => handleNavSelection('ofacScreening')}>
                {language === 'es' ? 'Ir a Screening (import / Screen now)' : 'Go to Screening (import / Screen now)'}
              </button>
            </div>
            {ofacScreeningError ? <p className="dashboard-form-error" role="alert">{ofacScreeningError}</p> : null}
            <article className="dashboard-metric-widget">
              <header className="dashboard-metric-widget__head">
                <span className="dashboard-nav-icon tone-orange" aria-hidden="true"><NavIcon name="ofacCapta" /></span>
                <div>
                  <strong>{language === 'es' ? 'Cola de disposición CAPTA' : 'CAPTA disposition queue'}</strong>
                  <span>
                    {language === 'es'
                      ? 'Restricción corresponsal / payable-through o lista consolidada — no tratar como bloqueo SDN'
                      : 'Correspondent / payable-through or consolidated-list — do not treat as SDN blocking'}
                  </span>
                </div>
              </header>
              {exceptionSubjects.length ? (
                <ul className="dashboard-ofac-queue">
                  {exceptionSubjects.map((subject) => {
                    const captaDisposition = String(subject.capta_disposition || subject.identifiers?.capta_disposition || 'pending');
                    const busy = ofacScreeningBusyId === subject.id;
                    return (
                      <li key={subject.id}>
                        <div>
                          <strong>{subject.entity_name}</strong>
                          <span>
                            {language === 'es'
                              ? `Estado ${subject.current_status || '—'} · ${subject.match_count || 0} matches · disposición de cuenta / lista consolidada`
                              : `Status ${subject.current_status || '—'} · ${subject.match_count || 0} matches · account / consolidated-list disposition`}
                          </span>
                          <div className="dashboard-ofac-queue-meta">
                            <label className="dashboard-ofac-capta-disposition">
                              <span>{language === 'es' ? 'Disposición CAPTA' : 'CAPTA disposition'}</span>
                              <select
                                value={captaDisposition}
                                disabled={busy}
                                onChange={(event) => {
                                  void handleOfacInvestigationUpdate(subject.id, {
                                    capta_disposition: event.target.value,
                                  });
                                }}
                              >
                                <option value="pending">{language === 'es' ? 'Pendiente' : 'Pending'}</option>
                                <option value="correspondent_restriction">{language === 'es' ? 'Restricción corresponsal / payable-through' : 'Correspondent / payable-through restriction'}</option>
                                <option value="consolidated_hit">{language === 'es' ? 'Hit lista consolidada' : 'Consolidated list hit'}</option>
                                <option value="false_positive">{language === 'es' ? 'Falso positivo' : 'False positive'}</option>
                                <option value="escalate">{language === 'es' ? 'Escalar' : 'Escalate'}</option>
                                <option value="cleared">{language === 'es' ? 'Despejado' : 'Cleared'}</option>
                              </select>
                            </label>
                            <span>{subject.relationship_type}</span>
                          </div>
                        </div>
                        <em>{subject.last_checked_at ? new Date(subject.last_checked_at).toLocaleDateString() : '—'}</em>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="dashboard-panel-subtitle">
                  {ofacScreeningLoading
                    ? (language === 'es' ? 'Cargando…' : 'Loading…')
                    : (language === 'es'
                      ? 'No hay excepciones CAPTA/consolidadas abiertas. Cuando un screen deje una revisión no SDN, aparece aquí para disposición de cuenta.'
                      : 'No open CAPTA/consolidated exceptions. When a screen leaves a non-SDN review, it appears here for account-restriction disposition.')}
                </p>
              )}
            </article>
          </article>
        </section>
      );
    }

    if (activeNavItem === 'ofacInvestigations') {
      if (!canAccessOfac) {
        return renderHomePane();
      }
      const statusLabel = (value: string) => {
        const key = String(value || 'not_started').toLowerCase();
        const labels: Record<string, { en: string; es: string }> = {
          not_started: { en: 'Not started', es: 'Sin iniciar' },
          in_progress: { en: 'In progress', es: 'En curso' },
          escalated: { en: 'Escalated', es: 'Escalado' },
          closed: { en: 'Closed', es: 'Cerrado' },
        };
        const row = labels[key] || labels.not_started;
        return language === 'es' ? row.es : row.en;
      };
      const dispositionLabel = (value: string) => {
        const key = String(value || 'pending').toLowerCase();
        const labels: Record<string, { en: string; es: string }> = {
          pending: { en: 'Ownership review pending', es: 'Revisión de propiedad pendiente' },
          cleared: { en: 'Cleared (not blocked under 50% rule)', es: 'Despejado (no bloqueado bajo regla del 50%)' },
          likely_blocked: { en: 'Likely blocked (50% rule)', es: 'Probablemente bloqueado (regla del 50%)' },
          confirmed_blocked: { en: 'Confirmed blocked ownership', es: 'Bloqueo por propiedad confirmado' },
          insufficient_data: { en: 'Insufficient ownership data', es: 'Datos de propiedad insuficientes' },
          false_positive: { en: 'False positive (list match)', es: 'Falso positivo (match de lista)' },
        };
        const row = labels[key] || labels.pending;
        return language === 'es' ? row.es : row.en;
      };
      const monitoredCount = ofacScreeningSubjects.length;
      const openCount = ofacScreeningSubjects.filter((row) => {
        const disposition = String(row.investigation_disposition || 'pending').toLowerCase();
        const status = String(row.investigation_status || 'not_started').toLowerCase();
        return disposition === 'pending' && status !== 'closed';
      }).length;
      const flaggedCount = ofacScreeningSubjects.filter((row) => {
        const disposition = String(row.investigation_disposition || '').toLowerCase();
        return disposition === 'likely_blocked' || disposition === 'confirmed_blocked';
      }).length;
      const reportingDueCount = ofacScreeningSubjects.filter((row) => Boolean(row.report_due_at)).length;
      const reportingOverdueCount = ofacScreeningSubjects.filter((row) => {
        const due = String(row.report_due_at || '').trim();
        if (!due) return false;
        const parsed = Date.parse(due);
        return Number.isFinite(parsed) && parsed < Date.now();
      }).length;
      const sortedSubjects = [...ofacScreeningSubjects].sort((left, right) => {
        const leftFlag = ['likely_blocked', 'confirmed_blocked'].includes(String(left.investigation_disposition || ''));
        const rightFlag = ['likely_blocked', 'confirmed_blocked'].includes(String(right.investigation_disposition || ''));
        if (leftFlag !== rightFlag) {
          return leftFlag ? -1 : 1;
        }
        return String(left.entity_name || '').localeCompare(String(right.entity_name || ''));
      });
      return (
        <section
          className="dashboard-settings-sections dashboard-settings-sections-wide dashboard-ofac-console"
          aria-label={`${copy.navOfacGroup} ${copy.navOfacInvestigations}`}
        >
          <article className="card settings-card dashboard-ofac-shell">
            <div className="settings-card-header">
              <h2 className="help-badge-label-row">
                {`${copy.navOfacGroup} ${copy.navOfacInvestigations}`}
                <HelpBadge label={language === 'es' ? 'Ayuda sobre Investigaciones OFAC' : 'Help about OFAC Investigations'}>
                  <p>
                    {language === 'es'
                      ? 'Lleva el progreso de investigación de cada persona/entidad en tu cartera: estado del caso y resultado probable bajo la regla del 50% u otras revisiones de propiedad.'
                      : 'Track investigation progress for each person/entity in your portfolio: case status and likely outcome under the 50% rule or other ownership reviews.'}
                  </p>
                  <p>
                    {language === 'es'
                      ? 'Usa terminología OFAC: “probablemente bloqueado” o “bloqueo confirmado”, nunca “culpable”. CAPTA sigue en Screening con disposición distinta a SDN. Un falso positivo de lista crea un par sujeto↔UID SDN (cuatro ojos); nunca omite el screening por nombre.'
                      : 'Use OFAC terminology: “likely blocked” or “confirmed blocked,” never “guilty.” CAPTA stays on Screening with disposition separate from SDN. A list false positive creates a subject↔SDN UID pair (four-eyes); it never skips screening by name.'}
                  </p>
                </HelpBadge>
              </h2>
            </div>
            <p className="dashboard-panel-subtitle dashboard-ofac-lede">
              {language === 'es'
                ? 'Cada caso es un acordeón (cerrado por defecto): el encabezado muestra estado, checklist y resultado probable; expandir para el procedimiento FAQ 401, dueños y notas.'
                : 'Each case is an accordion (collapsed by default): the header shows status, checklist progress, and likely outcome; expand for the FAQ 401 procedure, owners, and notes.'}
            </p>
            <div className="dashboard-ofac-kpi-grid" role="list">
              <div className="dashboard-metric-kpi tone-neutral" role="listitem">
                <em>{monitoredCount}</em>
                <span>{language === 'es' ? 'En lista / monitoreados' : 'On list / monitored'}</span>
              </div>
              <div className="dashboard-metric-kpi tone-warn" role="listitem">
                <em>{openCount}</em>
                <span>{language === 'es' ? 'Revisión de propiedad abierta' : 'Ownership review open'}</span>
              </div>
              <div className="dashboard-metric-kpi tone-alert" role="listitem">
                <em>{flaggedCount}</em>
                <span>{language === 'es' ? 'Probable / bloqueo confirmado' : 'Likely / confirmed blocked'}</span>
              </div>
              <div className={`dashboard-metric-kpi ${reportingOverdueCount ? 'tone-alert' : 'tone-warn'}`} role="listitem">
                <em>{reportingOverdueCount || reportingDueCount}</em>
                <span>
                  {language === 'es'
                    ? (reportingOverdueCount ? 'Reloj 10 días vencido' : 'Reloj reporte 10 días hábiles')
                    : (reportingOverdueCount ? '10-day clock overdue' : '10-business-day report clock')}
                </span>
              </div>
              <div className="dashboard-metric-kpi tone-neutral" role="listitem">
                <em>
                  {ofacFalseHitPairs.filter((row) => row.status === 'pending_approval').length}
                </em>
                <span>{language === 'es' ? 'Pares falso hit pendientes' : 'False-hit pairs pending'}</span>
              </div>
            </div>
            <div className="dashboard-ofac-panel-actions">
              <button
                type="button"
                className="dashboard-security-inline-btn"
                onClick={() => void loadOfacScreeningSubjects()}
                disabled={ofacScreeningLoading}
              >
                {ofacScreeningLoading
                  ? (language === 'es' ? 'Actualizando…' : 'Refreshing…')
                  : (language === 'es' ? 'Actualizar' : 'Refresh')}
              </button>
              <button
                type="button"
                className="dashboard-btn"
                onClick={() => void handleOfacFiftyPercentRunAll()}
                disabled={
                  !canAccessContinuousMonitoring
                  || ofacFiftyPercentRunningAll
                  || ofacScreeningLoading
                  || !sortedSubjects.length
                }
              >
                {ofacFiftyPercentRunningAll
                  ? (language === 'es' ? 'Ejecutando 50%…' : 'Running 50%…')
                  : (language === 'es' ? 'Automatizar 50% (toda la cartera)' : 'Automate 50% (full portfolio)')}
              </button>
              {canAccessContinuousMonitoring ? null : (
                <p className="research-form-panel-meta">
                  {language === 'es'
                    ? 'Automatizar la regla del 50% en toda la cartera es CompliAdvantage. En CompliCore abra cada caso e investigue a mano.'
                    : 'Automating the 50% rule across the book is CompliAdvantage. On CompliCore open each case and investigate by hand.'}
                </p>
              )}
              <button type="button" className="dashboard-security-inline-btn" onClick={() => handleNavSelection('ofacScreening')}>
                {language === 'es' ? 'Ir a Screening' : 'Go to Screening'}
              </button>
              <button type="button" className="dashboard-security-inline-btn" onClick={() => handleNavSelection('research')}>
                {language === 'es' ? 'Abrir Research' : 'Open Research'}
              </button>
              {canAccessContinuousMonitoring ? (
                <>
                  <button
                    type="button"
                    className="dashboard-security-inline-btn"
                    onClick={() => void handleOfacMonitoringReconcile(false)}
                  >
                    {language === 'es' ? 'Reconciliar monitoreo' : 'Reconcile monitoring'}
                  </button>
                  {hasPlatformAdminAccess ? (
                    <button
                      type="button"
                      className="dashboard-security-inline-btn"
                      onClick={() => void handleOfacMonitoringReconcile(true)}
                    >
                      {language === 'es' ? 'Purgar huérfanos de monitoreo' : 'Prune monitoring orphans'}
                    </button>
                  ) : null}
                </>
              ) : null}
            </div>
            {ofacScreeningError ? <p className="dashboard-form-error" role="alert">{ofacScreeningError}</p> : null}
            {ofacScreeningMessage ? <p className="dashboard-form-success">{ofacScreeningMessage}</p> : null}
            <div className="dashboard-ofac-false-hit-queue" aria-label={language === 'es' ? 'Falsos hits de lista' : 'List false hits'}>
              <strong>
                {language === 'es'
                  ? 'Falsos hits: pares sujeto ↔ UID SDN'
                  : 'False hits: subject ↔ SDN UID pairs'}
              </strong>
              <p>
                {language === 'es'
                  ? 'El matcher siempre corre. Un par activo solo suprime ese UID. Cambios en el registro SDN o en KYC invalidan el par. Cuatro ojos: un segundo revisor aprueba.'
                  : 'The matcher always runs. An active pair only suppresses that UID. SDN record or KYC changes invalidate the pair. Four-eyes: a second reviewer approves.'}
              </p>
              <label className="dashboard-ofac-investigation-notes">
                <span>
                  {language === 'es'
                    ? 'Importar CSV (subject_id, list_entry_id, rationale). UID, no nombre.'
                    : 'Import CSV (subject_id, list_entry_id, rationale). UID, not a name.'}
                </span>
                <textarea
                  rows={4}
                  value={ofacFalseHitCsv}
                  onChange={(event) => setOfacFalseHitCsv(event.target.value)}
                  disabled={ofacFalseHitImporting}
                  spellCheck={false}
                />
              </label>
              <div className="dashboard-ofac-panel-actions">
                <button
                  type="button"
                  className="dashboard-btn"
                  disabled={ofacFalseHitImporting}
                  onClick={() => void handleOfacFalseHitImport()}
                >
                  {ofacFalseHitImporting
                    ? (language === 'es' ? 'Importando…' : 'Importing…')
                    : (language === 'es' ? 'Importar pares pendientes' : 'Import pending pairs')}
                </button>
              </div>
              {ofacFalseHitPairs.length ? (
                <ul>
                  {ofacFalseHitPairs.slice(0, 24).map((pair) => {
                    const subjectName = ofacScreeningSubjects.find((row) => row.id === pair.subject_id)?.entity_name
                      || `#${pair.subject_id}`;
                    const pairBusy = ofacScreeningBusyId === pair.id;
                    return (
                      <li key={pair.id}>
                        <span>
                          <em>{subjectName}</em>
                          {` · UID ${pair.list_entry_id}`}
                          {pair.list_entry_name ? ` · ${pair.list_entry_name}` : ''}
                          {` · ${pair.status}`}
                          {pair.recertify_due ? (language === 'es' ? ' · recertificar' : ' · recertify due') : ''}
                          {pair.four_eyes_waived ? (language === 'es' ? ' · admin waiver' : ' · admin waiver') : ''}
                        </span>
                        <span className="dashboard-ofac-false-hit-queue__actions">
                          {pair.status === 'pending_approval' ? (
                            <button
                              type="button"
                              className="dashboard-security-inline-btn"
                              disabled={pairBusy}
                              onClick={() => void handleOfacFalseHitAction(pair.id, 'approve')}
                            >
                              {language === 'es' ? 'Aprobar' : 'Approve'}
                            </button>
                          ) : null}
                          {pair.status === 'active' || pair.status === 'expired' ? (
                            <button
                              type="button"
                              className="dashboard-security-inline-btn"
                              disabled={pairBusy}
                              onClick={() => void handleOfacFalseHitAction(pair.id, 'recertify')}
                            >
                              {language === 'es' ? 'Recertificar' : 'Recertify'}
                            </button>
                          ) : null}
                          {pair.status === 'pending_approval' || pair.status === 'active' ? (
                            <button
                              type="button"
                              className="dashboard-security-inline-btn"
                              disabled={pairBusy}
                              onClick={() => void handleOfacFalseHitAction(pair.id, 'revoke')}
                            >
                              {language === 'es' ? 'Revocar' : 'Revoke'}
                            </button>
                          ) : null}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="dashboard-panel-subtitle">
                  {language === 'es'
                    ? 'Aún no hay pares. Marca un caso como falso positivo (match de lista) o crea un par abajo.'
                    : 'No pairs yet. Mark a case as false positive (list match) or create a pair on a case below.'}
                </p>
              )}
            </div>
            {sortedSubjects.length ? (
              <>
                <div className="dashboard-ofac-panel-actions">
                  <button
                    type="button"
                    className="dashboard-security-inline-btn"
                    onClick={() => {
                      setOfacInvestigationOpenIds(new Set(sortedSubjects.map((row) => row.id)));
                    }}
                  >
                    {language === 'es' ? 'Expandir todo' : 'Expand all'}
                  </button>
                  <button
                    type="button"
                    className="dashboard-security-inline-btn"
                    onClick={() => setOfacInvestigationOpenIds(new Set())}
                  >
                    {language === 'es' ? 'Contraer todo' : 'Collapse all'}
                  </button>
                </div>
                <div className="dashboard-ofac-investigations" role="list">
                  {sortedSubjects.map((subject) => {
                    const status = (subject.investigation_status || 'not_started') as OfacInvestigationStatus;
                    const disposition = (subject.investigation_disposition || 'pending') as OfacInvestigationDisposition;
                    const busy = ofacScreeningBusyId === subject.id;
                    const checklist = subject.investigation_checklist || {};
                    const progress = subject.investigation_checklist_progress || {
                      done: OFAC_FIFTY_PERCENT_CHECKLIST.filter((step) => checklist[step.id]?.done).length,
                      total: OFAC_FIFTY_PERCENT_CHECKLIST.length,
                    };
                    const lastRun = subject.fifty_percent_last_run;
                    const reportDueRaw = String(subject.report_due_at || '').trim();
                    const reportDueOverdue = reportDueRaw
                      ? (Number.isFinite(Date.parse(reportDueRaw)) && Date.parse(reportDueRaw) < Date.now())
                      : false;
                    const reportDueLabel = reportDueRaw
                      ? reportDueRaw.slice(0, 10)
                      : '';
                    const existingOwners = Array.isArray(subject.identifiers?.owners)
                      ? (subject.identifiers?.owners as Array<Record<string, unknown>>)
                        .map((row) => {
                          const name = String(row?.name || '').trim();
                          const percent = row?.percent ?? row?.ownership_percent ?? '';
                          return name ? `${name}${percent !== '' && percent != null ? `, ${percent}` : ''}` : '';
                        })
                        .filter(Boolean)
                        .join('\n')
                      : '';
                    const isExpanded = ofacInvestigationOpenIds.has(subject.id);
                    const panelId = `ofac-investigation-panel-${subject.id}`;
                    const toggleExpanded = () => {
                      setOfacInvestigationOpenIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(subject.id)) {
                          next.delete(subject.id);
                        } else {
                          next.add(subject.id);
                        }
                        return next;
                      });
                    };
                    return (
                      <article
                        key={subject.id}
                        className={`dashboard-ofac-investigation-card${isExpanded ? ' is-expanded' : ' is-collapsed'}`}
                        role="listitem"
                      >
                        <h3 className="dashboard-ofac-investigation-card__heading">
                          <button
                            type="button"
                            className="dashboard-ofac-investigation-card__summary"
                            aria-expanded={isExpanded}
                            aria-controls={panelId}
                            onClick={toggleExpanded}
                          >
                            <span className="dashboard-ofac-investigation-card__summary-main">
                              <strong>{subject.entity_name}</strong>
                              <span>
                                {subject.relationship_type}
                                {` · ${statusLabel(status)}`}
                                {` · checklist ${progress.done || 0}/${progress.total || OFAC_FIFTY_PERCENT_CHECKLIST.length}`}
                                {isOfacHireSubject(subject) && subject.hire_clearance?.progress
                                  ? ` · ${language === 'es' ? 'contratación' : 'hire'} ${subject.hire_clearance.progress.done || 0}/${subject.hire_clearance.progress.total || OFAC_HIRE_CHECKLIST.length}`
                                  : ''}
                                {subject.false_hit_summary?.pending
                                  ? ` · ${language === 'es' ? 'falso hit pendiente' : 'false-hit pending'} ${subject.false_hit_summary.pending}`
                                  : ''}
                                {subject.false_hit_summary?.active
                                  ? ` · ${language === 'es' ? 'suprimidos' : 'suppressed'} ${subject.false_hit_summary.active}`
                                  : ''}
                                {reportDueLabel
                                  ? ` · ${language === 'es' ? (reportDueOverdue ? 'reporte vencido' : 'reporte') : (reportDueOverdue ? 'report overdue' : 'report due')} ${reportDueLabel}`
                                  : ''}
                              </span>
                            </span>
                            <em className="dashboard-ofac-investigation-card__outcome">
                              {dispositionLabel(disposition)}
                            </em>
                            <span className="dashboard-ofac-investigation-card__chevron" aria-hidden="true" />
                          </button>
                        </h3>
                        {isExpanded ? (
                          <div id={panelId} className="dashboard-ofac-investigation-card__body">
                            <div className="dashboard-ofac-investigation-fields">
                              <label>
                                <span>{language === 'es' ? 'Estado' : 'Status'}</span>
                                <select
                                  value={status}
                                  disabled={busy}
                                  onChange={(event) => {
                                    void handleOfacInvestigationUpdate(subject.id, {
                                      investigation_status: event.target.value as OfacInvestigationStatus,
                                    });
                                  }}
                                >
                                  <option value="not_started">{statusLabel('not_started')}</option>
                                  <option value="in_progress">{statusLabel('in_progress')}</option>
                                  <option value="escalated">{statusLabel('escalated')}</option>
                                  <option value="closed">{statusLabel('closed')}</option>
                                </select>
                              </label>
                              <label>
                                <span>{language === 'es' ? 'Resultado probable' : 'Likely outcome'}</span>
                                <select
                                  value={disposition}
                                  disabled={busy}
                                  onChange={(event) => {
                                    const next = event.target.value as OfacInvestigationDisposition;
                                    void handleOfacInvestigationUpdate(subject.id, {
                                      investigation_disposition: next,
                                      checklist: next === 'pending' ? undefined : { confirm_disposition: true },
                                    });
                                  }}
                                >
                                  <option value="pending">{dispositionLabel('pending')}</option>
                                  <option value="cleared">{dispositionLabel('cleared')}</option>
                                  <option value="likely_blocked">{dispositionLabel('likely_blocked')}</option>
                                  <option value="confirmed_blocked">{dispositionLabel('confirmed_blocked')}</option>
                                  <option value="insufficient_data">{dispositionLabel('insufficient_data')}</option>
                                  <option value="false_positive">{dispositionLabel('false_positive')}</option>
                                </select>
                              </label>
                            </div>
                            {isOfacHireSubject(subject) ? (
                              <div className="dashboard-ofac-hire-clearance">
                                <strong className="help-badge-label-row">
                                  {language === 'es' ? 'Autorización de contratación' : 'Hire clearance'}
                                  <HelpBadge label={language === 'es' ? 'Ayuda sobre contratación' : 'Help about hire clearance'}>
                                    <p>
                                      {language === 'es'
                                        ? 'El sitio recopila identidad y hechos. Un revisor en EE.UU. firma “autorizado para contratar”. No omite SDN ni sustituye los pares sujeto↔UID.'
                                        : 'Local staff collect identity and facts. A U.S. reviewer signs off “cleared to hire.” This does not skip SDN or replace subject↔UID false-hit pairs.'}
                                    </p>
                                  </HelpBadge>
                                </strong>
                                <p>
                                  {subject.hire_clearance?.role_split
                                    || (language === 'es'
                                      ? 'El personal local recopila. Un revisor en EE.UU. firma. Vivir en la ciudad de trabajo no es autorización.'
                                      : 'Local staff collect. A U.S. reviewer signs off. Living in the work city is not clearance.')}
                                </p>
                                <p className="dashboard-panel-subtitle">
                                  {(subject.hire_clearance?.pathway || 'venezuela_caracas')}
                                  {subject.hire_clearance?.country ? ` · ${subject.hire_clearance.country}` : ''}
                                  {subject.hire_clearance?.location ? ` · ${subject.hire_clearance.location}` : ''}
                                  {subject.hire_clearance?.can_clear
                                    ? (language === 'es' ? ' · listo para despejar' : ' · ready to clear')
                                    : (language === 'es' ? ' · aún no autorizado' : ' · not yet cleared to hire')}
                                </p>
                                <p className={`dashboard-ofac-whitelist-stamp${subject.hire_clearance?.recertify_due ? ' is-due' : ''}`}>
                                  {language === 'es' ? 'Última validación' : 'Last validated'}
                                  {': '}
                                  {formatOfacTimestamp(subject.hire_clearance?.last_validated_at, language)}
                                  {subject.hire_clearance?.recertify_due
                                    ? (language === 'es' ? ' · revalidación vencida' : ' · re-validation due')
                                    : ''}
                                  {subject.hire_clearance?.last_validated_status === 'failed'
                                    ? (language === 'es' ? ' · último intento falló' : ' · last rerun failed')
                                    : ''}
                                </p>
                                <div className="dashboard-action-buttons">
                                  <button
                                    type="button"
                                    className="dashboard-security-inline-btn"
                                    disabled={busy || ofacScreeningScreeningAll}
                                    onClick={() => void handleOfacHireValidate(subject.id)}
                                  >
                                    {language === 'es' ? 'Revalidar lista blanca' : 'Re-validate whitelist'}
                                  </button>
                                </div>
                                {Array.isArray(subject.hire_clearance?.blockers) && subject.hire_clearance.blockers.length ? (
                                  <ul className="dashboard-ofac-hire-blockers">
                                    {subject.hire_clearance.blockers.map((blocker) => (
                                      <li key={blocker}>{blocker}</li>
                                    ))}
                                  </ul>
                                ) : null}
                                <ul>
                                  {OFAC_HIRE_CHECKLIST.map((step) => {
                                    const stepState = subject.hire_clearance?.checklist?.[step.id]
                                      || (subject.identifiers?.hire_checklist as Record<string, OfacChecklistStepState> | undefined)?.[step.id];
                                    const checked = Boolean(stepState?.done);
                                    return (
                                      <li key={step.id}>
                                        <label>
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={busy}
                                            onChange={(event) => {
                                              void handleOfacInvestigationUpdate(subject.id, {
                                                hire_checklist: { [step.id]: event.target.checked },
                                                investigation_status: event.target.checked && status === 'not_started'
                                                  ? 'in_progress'
                                                  : undefined,
                                              });
                                            }}
                                          />
                                          <span>
                                            <em>{language === 'es' ? step.labelEs : step.labelEn}</em>
                                            <small>
                                              <span className={`dashboard-ofac-hire-role dashboard-ofac-hire-role--${step.role}`}>
                                                {step.role === 'collector'
                                                  ? (language === 'es' ? 'Recolector' : 'Collector')
                                                  : (language === 'es' ? 'Revisor EE.UU.' : 'U.S. reviewer')}
                                              </span>
                                              {' · '}
                                              {step.automatable
                                                ? (language === 'es' ? 'Auto' : 'Auto')
                                                : (language === 'es' ? 'Manual' : 'Manual')}
                                              {' · '}
                                              {language === 'es' ? step.detailEs : step.detailEn}
                                            </small>
                                          </span>
                                        </label>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            ) : null}
                            {(() => {
                              const uidMatches = collectOfacScreenMatches(subject);
                              const selected = ofacFalseHitSelectedById[subject.id] || [];
                              return (
                                <div className="dashboard-ofac-false-hit-create">
                                  <strong>
                                    {language === 'es'
                                      ? 'Par de falso hit (sujeto ↔ UID SDN)'
                                      : 'False-hit pair (subject ↔ SDN UID)'}
                                  </strong>
                                  <p>
                                    {language === 'es'
                                      ? 'No omite SDN. Tras cuatro ojos, solo esos UID se suprimen en el próximo screen de este sujeto. Screen primero si no hay UID.'
                                      : 'Does not skip SDN. After four-eyes, only those UIDs suppress on this subject’s next screen. Screen first if no UIDs are listed.'}
                                  </p>
                                  {uidMatches.length ? (
                                    <ul>
                                      {uidMatches.map((match) => {
                                        const uid = String(match.uid || '');
                                        const checked = selected.includes(uid);
                                        return (
                                          <li key={uid}>
                                            <label>
                                              <input
                                                type="checkbox"
                                                checked={checked}
                                                disabled={busy}
                                                onChange={(event) => {
                                                  setOfacFalseHitSelectedById((prev) => {
                                                    const current = new Set(prev[subject.id] || []);
                                                    if (event.target.checked) {
                                                      current.add(uid);
                                                    } else {
                                                      current.delete(uid);
                                                    }
                                                    return { ...prev, [subject.id]: Array.from(current) };
                                                  });
                                                }}
                                              />
                                              <span>
                                                {`UID ${uid}`}
                                                {match.name ? ` · ${match.name}` : ''}
                                                {Array.isArray(match.programs) && match.programs.length
                                                  ? ` · ${match.programs.join(', ')}`
                                                  : ''}
                                              </span>
                                            </label>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  ) : (
                                    <p className="dashboard-panel-subtitle">
                                      {language === 'es'
                                        ? 'Sin UID SDN en el último screen. Ejecuta Screen ahora, luego crea el par.'
                                        : 'No SDN UIDs on the last screen. Run Screen now, then create the pair.'}
                                    </p>
                                  )}
                                  <label className="dashboard-ofac-investigation-notes">
                                    <span>
                                      {language === 'es'
                                        ? 'Justificación (mín. 8 caracteres; DOB, EIN, dirección vs SDN)'
                                        : 'Rationale (min. 8 chars; DOB, EIN, address vs SDN)'}
                                    </span>
                                    <textarea
                                      rows={2}
                                      value={ofacFalseHitRationaleById[subject.id] || ''}
                                      disabled={busy}
                                      onChange={(event) => {
                                        setOfacFalseHitRationaleById((prev) => ({
                                          ...prev,
                                          [subject.id]: event.target.value,
                                        }));
                                      }}
                                      placeholder={
                                        language === 'es'
                                          ? 'Ej. DOB del cliente 1998, SDN 1965; EIN no coincide.'
                                          : 'e.g. Customer DOB 1998, SDN 1965; EIN does not match.'
                                      }
                                    />
                                  </label>
                                  <div className="dashboard-ofac-panel-actions">
                                    <button
                                      type="button"
                                      className="dashboard-btn"
                                      disabled={busy || !uidMatches.length}
                                      onClick={() => void handleOfacFalseHitCreate(subject)}
                                    >
                                      {language === 'es' ? 'Crear par pendiente' : 'Create pending pair'}
                                    </button>
                                  </div>
                                </div>
                              );
                            })()}
                            <label className="dashboard-ofac-investigation-notes">
                              <span>
                                {language === 'es'
                                  ? 'Dueños para automatizar (una línea: Nombre, porcentaje)'
                                  : 'Owners for automation (one line: Name, percent)'}
                              </span>
                              <textarea
                                value={ofacOwnersDraftById[subject.id] ?? existingOwners}
                                rows={2}
                                disabled={busy}
                                onChange={(event) => {
                                  setOfacOwnersDraftById((prev) => ({ ...prev, [subject.id]: event.target.value }));
                                }}
                                placeholder={
                                  language === 'es'
                                    ? 'Ej. Acme Holdings LLC, 40\nBlocked Person X, 15'
                                    : 'e.g. Acme Holdings LLC, 40\nBlocked Person X, 15'
                                }
                              />
                            </label>
                            <div className="dashboard-ofac-panel-actions">
                              {canAccessResearch ? (
                                <button
                                  type="button"
                                  className="dashboard-btn dashboard-btn-primary"
                                  disabled={
                                    busy
                                    || ofacFiftyPercentRunningAll
                                    || subject.vantage_action?.mode === 'in_progress'
                                  }
                                  onClick={() => void handleOfacVantageBackgroundCheck(subject)}
                                  title={
                                    language === 'es'
                                      ? (subject.vantage_action?.helper || 'Verificación Vantage para la base de hechos de esta entidad')
                                      : (subject.vantage_action?.helper || 'Vantage check for this entity’s fact database')
                                  }
                                >
                                  {busy
                                    ? (language === 'es' ? 'Encolando Vantage…' : 'Queueing Vantage…')
                                    : (
                                      subject.vantage_action?.label
                                      || (language === 'es' ? 'Ejecutar verificación Vantage' : 'Run Vantage background check')
                                    )}
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="dashboard-btn"
                                disabled={busy || ofacFiftyPercentRunningAll}
                                onClick={() => void handleOfacFiftyPercentRun(subject.id)}
                              >
                                {busy
                                  ? (language === 'es' ? 'Ejecutando…' : 'Running…')
                                  : (language === 'es' ? 'Ejecutar chequeo 50%' : 'Run 50% check')}
                              </button>
                              {canAccessResearch ? (
                                <button
                                  type="button"
                                  className="dashboard-security-inline-btn"
                                  onClick={() => {
                                    handleNavSelection('research');
                                  }}
                                >
                                  {language === 'es' ? 'Abrir cola Research' : 'Open Research queue'}
                                </button>
                              ) : null}
                            </div>
                            {canAccessResearch && subject.vantage_action?.helper ? (
                              <p className="dashboard-ofac-automation-summary">{subject.vantage_action.helper}</p>
                            ) : null}
                            {(() => {
                              const factDb = (subject.fact_database || subject.identifiers?.fact_database) as Record<string, unknown> | undefined;
                              const summary = subject.fact_summary;
                              const hasFacts = Boolean(
                                summary
                                && (
                                  (summary.signal_count || 0) > 0
                                  || (summary.address_count || 0) > 0
                                  || (summary.alias_count || 0) > 0
                                  || (summary.affiliation_count || 0) > 0
                                  || (summary.fact_record_count || 0) > 0
                                  || (summary.source_count || 0) > 0
                                )
                              );
                              if (!factDb && !hasFacts) {
                                return (
                                  <div className="dashboard-ofac-fact-panel" aria-label={language === 'es' ? 'Base de hechos' : 'Fact database'}>
                                    <strong>{language === 'es' ? 'Base de hechos de la entidad' : 'Entity fact database'}</strong>
                                    <p className="dashboard-ofac-automation-summary">
                                      {language === 'es'
                                        ? 'Aún vacía. Ejecuta Vantage o añade dueños/notas de investigación; las fuentes se fusionan con procedencia.'
                                        : 'Empty so far. Run Vantage or add investigation owners/notes; sources merge with provenance.'}
                                    </p>
                                  </div>
                                );
                              }
                              const entity = (factDb?.entity && typeof factDb.entity === 'object'
                                ? factDb.entity
                                : {}) as {
                                canonical_name?: string;
                                alt_names?: string[];
                                addresses?: string[];
                                birthdate?: string;
                                company_affiliations?: string[];
                              };
                              const sources = summary?.sources || [];
                              const displayAddresses = harvestDisplayAddresses(factDb as FactDatabase);
                              const lastJob = String(
                                summary?.vantage?.last_job_id
                                || subject.identifiers?.last_vantage_research_job_id
                                || subject.reference_id
                                || '',
                              );
                              return (
                                <div className="dashboard-ofac-fact-panel" aria-label={language === 'es' ? 'Base de hechos' : 'Fact database'}>
                                  <strong>{language === 'es' ? 'Base de hechos de la entidad' : 'Entity fact database'}</strong>
                                  <ul>
                                    {entity.canonical_name || summary?.canonical_name ? (
                                      <li>{language === 'es' ? 'Nombre' : 'Name'}: {entity.canonical_name || summary?.canonical_name}</li>
                                    ) : null}
                                    {entity.birthdate ? <li>{language === 'es' ? 'Fecha de nacimiento' : 'DOB'}: {entity.birthdate}</li> : null}
                                    {displayAddresses.length ? (
                                      <li>{language === 'es' ? 'Direcciones' : 'Addresses'}: {displayAddresses.slice(0, 4).join(' · ')}</li>
                                    ) : null}
                                    {Array.isArray(entity.alt_names) && entity.alt_names.length ? (
                                      <li>{language === 'es' ? 'Alias' : 'Aliases'}: {entity.alt_names.slice(0, 5).join(', ')}</li>
                                    ) : null}
                                    {Array.isArray(entity.company_affiliations) && entity.company_affiliations.length ? (
                                      <li>{language === 'es' ? 'Afiliaciones' : 'Affiliations'}: {entity.company_affiliations.slice(0, 5).join(', ')}</li>
                                    ) : null}
                                    <li>
                                      {language === 'es' ? 'Señales' : 'Signals'}
                                      {`: ${summary?.signal_count ?? (Array.isArray(factDb?.signals) ? factDb.signals.length : 0)}`}
                                      {summary?.fact_record_count != null ? ` · ${language === 'es' ? 'registros' : 'records'} ${summary.fact_record_count}` : ''}
                                    </li>
                                    {sources.length ? (
                                      <li>{language === 'es' ? 'Fuentes' : 'Sources'}: {sources.join(', ')}</li>
                                    ) : null}
                                    {lastJob ? <li>{language === 'es' ? 'Último job Vantage' : 'Last Vantage job'}: {lastJob}</li> : null}
                                    {summary?.last_updated ? (
                                      <li>{language === 'es' ? 'Actualizado' : 'Updated'}: {new Date(String(summary.last_updated)).toLocaleString()}</li>
                                    ) : null}
                                  </ul>
                                </div>
                              );
                            })()}
                            {lastRun ? (
                              <p className="dashboard-ofac-automation-summary">
                                {language === 'es' ? 'Último motor 50%:' : 'Last 50% engine:'}
                                {' '}
                                {lastRun.status || '—'}
                                {lastRun.aggregate_blocked_percent != null ? ` · ${lastRun.aggregate_blocked_percent}%` : ''}
                                {lastRun.has_owners === false
                                  ? (language === 'es' ? ' · sin dueños' : ' · no owners')
                                  : ''}
                                {lastRun.generated_at ? ` · ${new Date(lastRun.generated_at).toLocaleString()}` : ''}
                              </p>
                            ) : null}
                            <div className="dashboard-ofac-checklist">
                              <strong>
                                {language === 'es' ? 'Checklist de investigación (FAQ 401)' : 'Investigation checklist (FAQ 401)'}
                              </strong>
                              <ul>
                                {OFAC_FIFTY_PERCENT_CHECKLIST.map((step) => {
                                  const stepState = checklist[step.id];
                                  const checked = Boolean(stepState?.done);
                                  return (
                                    <li key={step.id}>
                                      <label>
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          disabled={busy}
                                          onChange={(event) => {
                                            void handleOfacInvestigationUpdate(subject.id, {
                                              checklist: { [step.id]: event.target.checked },
                                              investigation_status: event.target.checked && status === 'not_started'
                                                ? 'in_progress'
                                                : undefined,
                                            });
                                          }}
                                        />
                                        <span>
                                          <em>{language === 'es' ? step.labelEs : step.labelEn}</em>
                                          <small>
                                            {step.automatable
                                              ? (language === 'es' ? 'Auto' : 'Auto')
                                              : (language === 'es' ? 'Manual' : 'Manual')}
                                            {' · '}
                                            {language === 'es' ? step.detailEs : step.detailEn}
                                          </small>
                                        </span>
                                      </label>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                            {(() => {
                              const lastMatches = Array.isArray(subject.last_screen_matches)
                                ? subject.last_screen_matches
                                : [];
                              const suppressedMatches = Array.isArray(subject.last_suppressed_matches)
                                ? subject.last_suppressed_matches
                                : [];
                              const subjectPairs = ofacFalseHitPairs.filter((row) => row.subject_id === subject.id);
                              const selected = ofacFalseHitSelectedById[subject.id] || [];
                              const toggleUid = (uid: string, checked: boolean) => {
                                setOfacFalseHitSelectedById((prev) => {
                                  const current = new Set(prev[subject.id] || []);
                                  if (checked) {
                                    current.add(uid);
                                  } else {
                                    current.delete(uid);
                                  }
                                  return { ...prev, [subject.id]: Array.from(current) };
                                });
                              };
                              return (
                                <div className="dashboard-ofac-false-hit-panel">
                                  <strong>
                                    {language === 'es'
                                      ? 'Lista de falso hit (par sujeto ↔ UID SDN)'
                                      : 'False-hit list (subject ↔ SDN UID pair)'}
                                  </strong>
                                  <p>
                                    {language === 'es'
                                      ? 'Nunca se omite SDN por nombre. Marca los UID confirmados como no coincidentes; un segundo revisor debe aprobar. Un UID SDN nuevo o un cambio en el registro vuelve a alertar.'
                                      : 'SDN is never skipped by name. Select UIDs confirmed as non-matches; a second reviewer must approve. A new SDN UID or a changed list record still alerts.'}
                                  </p>
                                  {lastMatches.length ? (
                                    <ul className="dashboard-ofac-false-hit-matches">
                                      {lastMatches.map((match) => {
                                        const uid = String(match.uid || '').trim();
                                        if (!uid) {
                                          return null;
                                        }
                                        return (
                                          <li key={uid}>
                                            <label>
                                              <input
                                                type="checkbox"
                                                checked={selected.includes(uid)}
                                                disabled={busy}
                                                onChange={(event) => toggleUid(uid, event.target.checked)}
                                              />
                                              <span>
                                                {match.name || uid}
                                                {` · UID ${uid}`}
                                                {match.programs?.length ? ` · ${match.programs.join(', ')}` : ''}
                                              </span>
                                            </label>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  ) : (
                                    <p>
                                      {language === 'es'
                                        ? 'No hay matches SDN en el último screening. Ejecuta Screen now en Screening primero.'
                                        : 'No SDN matches from the last screen. Run Screen now on Screening first.'}
                                    </p>
                                  )}
                                  {suppressedMatches.length ? (
                                    <p>
                                      {language === 'es'
                                        ? `Suprimidos activos: ${suppressedMatches.map((row) => row.name || row.uid).join(', ')}`
                                        : `Actively suppressed: ${suppressedMatches.map((row) => row.name || row.uid).join(', ')}`}
                                    </p>
                                  ) : null}
                                  {subjectPairs.length ? (
                                    <ul className="dashboard-ofac-false-hit-pairs">
                                      {subjectPairs.map((pair) => (
                                        <li key={pair.id}>
                                          <span>
                                            {pair.list_entry_name || pair.list_entry_id}
                                            {` · ${pair.status}`}
                                            {pair.four_eyes_waived ? (language === 'es' ? ' · waiver admin' : ' · admin waiver') : ''}
                                          </span>
                                          <span className="dashboard-ofac-false-hit-queue__actions">
                                            {pair.status === 'pending_approval' ? (
                                              <button
                                                type="button"
                                                className="dashboard-security-inline-btn"
                                                disabled={busy}
                                                onClick={() => void handleOfacFalseHitAction(pair.id, 'approve')}
                                              >
                                                {language === 'es' ? 'Aprobar' : 'Approve'}
                                              </button>
                                            ) : null}
                                            {pair.status === 'active' || pair.status === 'expired' ? (
                                              <button
                                                type="button"
                                                className="dashboard-security-inline-btn"
                                                disabled={busy}
                                                onClick={() => void handleOfacFalseHitAction(pair.id, 'recertify')}
                                              >
                                                {language === 'es' ? 'Recertificar' : 'Recertify'}
                                              </button>
                                            ) : null}
                                            {pair.status === 'pending_approval' || pair.status === 'active' ? (
                                              <button
                                                type="button"
                                                className="dashboard-security-inline-btn"
                                                disabled={busy}
                                                onClick={() => void handleOfacFalseHitAction(pair.id, 'revoke')}
                                              >
                                                {language === 'es' ? 'Revocar' : 'Revoke'}
                                              </button>
                                            ) : null}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : null}
                                  <label className="dashboard-ofac-investigation-notes">
                                    <span>
                                      {language === 'es'
                                        ? 'Justificación del par (mínimo 8 caracteres)'
                                        : 'Pair rationale (at least 8 characters)'}
                                    </span>
                                    <textarea
                                      rows={2}
                                      disabled={busy}
                                      value={ofacFalseHitRationaleById[subject.id] || ''}
                                      onChange={(event) => {
                                        setOfacFalseHitRationaleById((prev) => ({
                                          ...prev,
                                          [subject.id]: event.target.value,
                                        }));
                                      }}
                                      placeholder={
                                        language === 'es'
                                          ? 'Ej. DOB y dirección no coinciden con UID 12345; evidencia en Research…'
                                          : 'e.g. DOB and address do not match UID 12345; evidence in Research…'
                                      }
                                    />
                                  </label>
                                  <div className="dashboard-ofac-panel-actions">
                                    <button
                                      type="button"
                                      className="dashboard-btn"
                                      disabled={busy || !lastMatches.length}
                                      onClick={() => void handleOfacFalseHitCreate(subject)}
                                    >
                                      {busy
                                        ? (language === 'es' ? 'Creando…' : 'Creating…')
                                        : (language === 'es'
                                          ? 'Crear par de falso hit (pendiente)'
                                          : 'Create false-hit pair (pending)')}
                                    </button>
                                  </div>
                                </div>
                              );
                            })()}
                            <label className="dashboard-ofac-investigation-notes">
                              <span>{language === 'es' ? 'Notas / evidencia' : 'Notes / evidence'}</span>
                              <textarea
                                defaultValue={subject.investigation_notes || ''}
                                key={`${subject.id}-${subject.investigation_updated_at || 'new'}`}
                                rows={2}
                                disabled={busy}
                                onBlur={(event) => {
                                  const next = event.target.value.trim();
                                  const prev = String(subject.investigation_notes || '').trim();
                                  if (next === prev) {
                                    return;
                                  }
                                  void handleOfacInvestigationUpdate(subject.id, { investigation_notes: next });
                                }}
                                placeholder={
                                  language === 'es'
                                    ? 'Racional, fuentes UBO, enlaces a Research…'
                                    : 'Rationale, UBO sources, Research links…'
                                }
                              />
                            </label>
                          </div>
                        ) : null}
                      </article>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="dashboard-panel-subtitle">
                {ofacScreeningLoading
                  ? (language === 'es' ? 'Cargando…' : 'Loading…')
                  : (language === 'es'
                    ? 'No hay sujetos. Impórtalos en Screening para iniciar investigaciones.'
                    : 'No subjects yet. Import them on Screening to start investigations.')}
              </p>
            )}
          </article>
        </section>
      );
    }

    if (activeNavItem === 'ofacWhitelist') {
      if (!canAccessOfac) {
        return renderHomePane();
      }
      const rosterSubjects = ofacScreeningSubjects.filter((subject) => {
        const listIds = Array.isArray(subject.named_whitelist_ids) ? subject.named_whitelist_ids : [];
        if (ofacActiveWhitelistId == null) {
          return false;
        }
        if (listIds.includes(ofacActiveWhitelistId)) {
          return true;
        }
        return listIds.length === 0 && ofacActiveWhitelistId === ofacNamedWhitelistDefaultId && isOfacHireSubject(subject);
      });
      const dueIds = rosterSubjects
        .filter((subject) => Boolean(subject.hire_clearance?.recertify_due) || !subject.hire_clearance?.last_validated_at)
        .map((subject) => subject.id);
      const activeWhitelist = ofacNamedWhitelists.find((row) => row.id === ofacActiveWhitelistId)
        || ofacNamedWhitelists[0]
        || null;
      const activeWhitelistName = activeWhitelist?.name
        || (language === 'es' ? 'Lista blanca' : 'Whitelist');
      const selectedCompany = ofacCompanies.find((row) => row.company_id === ofacSelectedCompanyId) || null;
      const companySelected = ofacSelectedCompanyId != null && selectedCompany != null;
      const whitelistCompanyName = selectedCompany?.name
        || (companySelected
          ? `${language === 'es' ? 'Empresa' : 'Company'} ${ofacSelectedCompanyId}`
          : (language === 'es' ? 'Sin empresa' : 'No company'));
      const ofacWhitelistKindChosen = ofacWhitelistIntakeKind === 'person' || ofacWhitelistIntakeKind === 'company';
      const ofacWhitelistIsPerson = ofacWhitelistIntakeKind === 'person';
      const ofacWhitelistKindRoles = ofacWhitelistKindChosen
        ? ofacWhitelistRolesForKind(ofacWhitelistIntakeKind)
        : [];
      const ofacWhitelistBlankAddress = emptyOfacIntakeAddress(ofacWhitelistIsPerson ? 'home' : 'work');
      const ofacWhitelistAddressKindOptions = ofacWhitelistKindChosen
        ? ofacWhitelistAddressKinds(ofacWhitelistIntakeKind)
        : [];
      const ofacWhitelistIdTypeOptions = ofacWhitelistKindChosen
        ? ofacWhitelistIdentifierTypes(ofacWhitelistIntakeKind, ofacWhitelistForm.identifierCountry)
        : [];
      const applyWhitelistIntakeKind = (kind: OfacWhitelistIntakeKind) => {
        setOfacWhitelistIntakeKind(kind);
        setOfacWhitelistForm((current) => applyOfacWhitelistIntakeKind(current, kind));
      };
      const activeMemberCount = Number(activeWhitelist?.member_count || 0);
      const activeListMeta = `${
        language === 'es'
          ? (activeMemberCount === 1 ? '1 persona' : `${activeMemberCount} personas`)
          : (activeMemberCount === 1 ? '1 person' : `${activeMemberCount} people`)
      }${activeWhitelist?.is_default ? (language === 'es' ? ' · Predeterminada' : ' · Default') : ''}`;
      const relationshipLabel = (value: string) => {
        const match = OFAC_SCREENING_RELATIONSHIP_OPTIONS.find((option) => option.value === value);
        if (!match) {
          return value;
        }
        return language === 'es' ? match.labelEs : match.labelEn;
      };
      const currentStepLabel = (subject: OfacScreeningSubject) => {
        const step = subject.hire_clearance?.current_step;
        if (!step) {
          return language === 'es' ? 'Sin paso' : 'No step yet';
        }
        return language === 'es' ? (step.short_es || step.label_es || step.id) : (step.short_en || step.label_en || step.id);
      };
      return (
        <section
          className="dashboard-settings-sections dashboard-settings-sections-wide dashboard-ofac-console"
          aria-label={`${copy.navOfacGroup} ${copy.navOfacWhitelist}`}
        >
          <article className="card settings-card dashboard-ofac-shell">
            <div className="settings-card-header">
              <h2 className="help-badge-label-row">
                {`${copy.navOfacGroup} ${copy.navOfacWhitelist}`}
                <HelpBadge label={language === 'es' ? 'Ayuda sobre lista blanca OFAC' : 'Help about OFAC Whitelist'}>
                  <p>
                    {language === 'es'
                      ? 'Workspace de autorización fechada: añadir clientes, proveedores, UBOs, empleados o contratistas, revalidar SDN, y recertificar pares sujeto↔UID. No omite SDN ni sustituye Screening.'
                      : 'Dated-clearance workspace: add customers, vendors, UBOs, employees, or contractors, re-validate SDN, and recertify subject↔UID pairs. It does not skip SDN or replace Screening.'}
                  </p>
                  <p>
                    {language === 'es'
                      ? 'Screening es la cartera viva. Un nombre en esta lista no omite SDN. Los pares de falso hit son 1 a 1 (sujeto ↔ UID). Puedes crear varias listas y cambiar entre ellas.'
                      : 'Screening is the live book. A name here does not skip SDN. False-hit pairs are 1-to-1 (subject ↔ UID). Create multiple lists and switch between them.'}
                  </p>
                </HelpBadge>
              </h2>
            </div>
            {ofacScreeningError ? (
              <p className="dashboard-form-error" role="alert">
                {ofacScreeningError}
                {!hasGatewaySession ? (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="dashboard-ofac-list-switcher__retry"
                      onClick={() => {
                        logout();
                        navigate('/login');
                      }}
                    >
                      {language === 'es' ? 'Iniciar sesión' : 'Sign in again'}
                    </button>
                  </>
                ) : null}
              </p>
            ) : null}
            {ofacScreeningMessage ? <p className="dashboard-form-success">{ofacScreeningMessage}</p> : null}
            <div
              className="dashboard-ofac-list-switcher"
              aria-busy={ofacNamedWhitelistsLoad === 'idle' || ofacNamedWhitelistsLoad === 'loading'}
            >
              <div className="dashboard-ofac-list-switcher__company">
                <span className="dashboard-ofac-list-switcher__label help-badge-label-row">
                  {language === 'es' ? 'Empresa' : 'Company'}
                  <HelpBadge label={language === 'es' ? 'Ayuda sobre empresas' : 'Help about companies'}>
                    <p>
                      {language === 'es'
                        ? 'La empresa es el cliente que estás autorizando, no CompliCore ni un tenant de demo. Aquí ves el directorio, y puedes añadir, renombrar o eliminar empresas.'
                        : 'Company is the customer you are clearing, not CompliCore or a demo tenant. This list is the directory: add, rename, or delete companies here.'}
                    </p>
                  </HelpBadge>
                </span>
                {ofacCompanyEditor !== 'idle' ? (
                  <div className="dashboard-ofac-list-switcher__create">
                    <label htmlFor="ofac-company-name">
                      <span>
                        {ofacCompanyEditor === 'rename'
                          ? (language === 'es' ? 'Renombrar empresa' : 'Rename company')
                          : (language === 'es' ? 'Nueva empresa' : 'New company')}
                      </span>
                      <input
                        id="ofac-company-name"
                        type="text"
                        value={ofacCompanyNameDraft}
                        autoFocus
                        autoComplete="off"
                        onChange={(event) => setOfacCompanyNameDraft(event.target.value)}
                        placeholder={language === 'es' ? 'p. ej. GossLed' : 'e.g. GossLed'}
                        maxLength={200}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            void handleOfacSaveCompany();
                          }
                          if (event.key === 'Escape') {
                            setOfacCompanyEditor('idle');
                            setOfacCompanyNameDraft('');
                          }
                        }}
                      />
                    </label>
                    <p className="dashboard-ofac-list-switcher__hint">
                      {language === 'es'
                        ? 'Usa el nombre del cliente que vas a autorizar. No uses CompliCore, NextCompli ni un tenant de demo.'
                        : 'Use the customer you will screen. Do not use CompliCore, NextCompli, or a demo tenant.'}
                    </p>
                    <div className="dashboard-ofac-list-switcher__create-btns">
                      <button
                        type="button"
                        className="dashboard-btn dashboard-btn-primary"
                        disabled={ofacCompanyBusy}
                        onClick={() => void handleOfacSaveCompany()}
                      >
                        {ofacCompanyBusy
                          ? (language === 'es' ? 'Guardando…' : 'Saving…')
                          : (language === 'es' ? 'Guardar' : 'Save')}
                      </button>
                      <button
                        type="button"
                        className="dashboard-security-inline-btn"
                        disabled={ofacCompanyBusy}
                        onClick={() => {
                          setOfacCompanyEditor('idle');
                          setOfacCompanyNameDraft('');
                        }}
                      >
                        {language === 'es' ? 'Cancelar' : 'Cancel'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="dashboard-ofac-list-switcher__company-row">
                      <div className="dashboard-ofac-list-switcher__select-wrap">
                        <select
                          value={ofacSelectedCompanyId ?? ''}
                          aria-label={language === 'es' ? 'Empresa activa' : 'Active company'}
                          onChange={(event) => {
                            const nextId = Number(event.target.value);
                            persistOfacCompanyId(Number.isFinite(nextId) && nextId > 0 ? nextId : null);
                            setOfacWhitelistCreateOpen(false);
                            setOfacWhitelistRenameOpen(false);
                          }}
                        >
                          {ofacSelectedCompanyId == null
                          || !ofacCompanies.some((row) => row.company_id === ofacSelectedCompanyId) ? (
                            <option value="">
                              {language === 'es' ? 'Selecciona una empresa' : 'Select a company'}
                            </option>
                          ) : null}
                          {ofacCompanies.map((row) => (
                            <option key={row.company_id} value={row.company_id}>
                              {row.name || `${language === 'es' ? 'Empresa' : 'Company'} ${row.company_id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      {ofacCanManageCompanies ? (
                        <div className="dashboard-ofac-list-switcher__toolbar">
                          <button
                            type="button"
                            className="dashboard-ofac-list-switcher__new"
                            disabled={ofacCompanyBusy}
                            onClick={() => {
                              setOfacCompanyNameDraft('');
                              setOfacCompanyEditor('create');
                            }}
                          >
                            {language === 'es' ? '+ Nueva empresa' : '+ New company'}
                          </button>
                          <button
                            type="button"
                            className="dashboard-security-inline-btn"
                            disabled={ofacCompanyBusy || !companySelected}
                            onClick={() => {
                              setOfacCompanyNameDraft(whitelistCompanyName === (language === 'es' ? 'Sin empresa' : 'No company') ? '' : whitelistCompanyName);
                              setOfacCompanyEditor('rename');
                            }}
                          >
                            {language === 'es' ? 'Renombrar' : 'Rename'}
                          </button>
                          <button
                            type="button"
                            className="dashboard-security-inline-btn"
                            disabled={ofacCompanyBusy || !companySelected}
                            onClick={() => void handleOfacDeleteCompany()}
                          >
                            {language === 'es' ? 'Eliminar' : 'Delete'}
                          </button>
                        </div>
                      ) : null}
                    </div>
                    <p className="dashboard-ofac-list-switcher__meta">
                      {ofacCompanies.length
                        ? (language === 'es'
                          ? 'Directorio de clientes. Selecciona uno para gestionar sus listas blancas.'
                          : 'Customer directory. Select a company to manage its whitelists.')
                        : ofacCanManageCompanies
                          ? (language === 'es'
                            ? 'Crea una empresa para empezar.'
                            : 'Create a company to start.')
                          : (language === 'es'
                            ? 'Pide a un administrador que te asigne a una empresa.'
                            : 'Ask an admin to assign you to a company.')}
                    </p>
                  </>
                )}
              </div>
              <div className={`dashboard-ofac-list-switcher__field${companySelected ? '' : ' dashboard-ofac-list-switcher__field--locked'}`}>
                <span className="dashboard-ofac-list-switcher__label">
                  {language === 'es' ? 'Listas blancas' : 'Whitelists'}
                </span>
                {!companySelected ? (
                  <p className="dashboard-ofac-list-switcher__status" role="status">
                    {ofacCompanyRequiredMessage(language)}
                  </p>
                ) : ofacNamedWhitelists.length ? (
                  <>
                    <div className="dashboard-ofac-list-switcher__select-wrap">
                      <select
                        value={ofacActiveWhitelistId ?? ''}
                        aria-label={language === 'es' ? 'Lista activa' : 'Active whitelist'}
                        disabled={!companySelected}
                        onChange={(event) => {
                          const nextId = Number(event.target.value);
                          if (!Number.isFinite(nextId) || nextId <= 0) {
                            return;
                          }
                          setOfacActiveWhitelistId(nextId);
                          try {
                            window.localStorage.setItem(OFAC_ACTIVE_WHITELIST_STORAGE_KEY, String(nextId));
                          } catch {
                            /* ignore */
                          }
                        }}
                      >
                        {ofacNamedWhitelists.map((row) => (
                          <option key={row.id} value={row.id}>
                            {row.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <p className="dashboard-ofac-list-switcher__meta">{activeListMeta}</p>
                    <div className="dashboard-ofac-list-switcher__toolbar">
                      <button
                        type="button"
                        className="dashboard-security-inline-btn"
                        disabled={ofacWhitelistBusy || ofacActiveWhitelistId == null}
                        onClick={() => {
                          setOfacWhitelistRenameDraft(activeWhitelist?.name || '');
                          setOfacWhitelistRenameOpen(true);
                          setOfacWhitelistCreateOpen(false);
                        }}
                      >
                        {language === 'es' ? 'Renombrar lista' : 'Rename list'}
                      </button>
                      <button
                        type="button"
                        className="dashboard-security-inline-btn"
                        disabled={ofacWhitelistBusy || ofacActiveWhitelistId == null}
                        onClick={() => void handleOfacDeleteWhitelist()}
                      >
                        {language === 'es' ? 'Eliminar lista' : 'Delete list'}
                      </button>
                    </div>
                  </>
                ) : ofacNamedWhitelistsLoad === 'error' ? (
                  <p className="dashboard-ofac-list-switcher__status" role="status">
                    {language === 'es'
                      ? 'No se pudieron cargar las listas. Crea una o reintenta.'
                      : 'Couldn’t load lists. Create one or retry.'}
                    {' '}
                    <button
                      type="button"
                      className="dashboard-ofac-list-switcher__retry"
                      onClick={() => void loadOfacNamedWhitelists()}
                    >
                      {language === 'es' ? 'Reintentar' : 'Retry'}
                    </button>
                  </p>
                ) : ofacNamedWhitelistsLoad === 'ready' ? (
                  <p className="dashboard-ofac-list-switcher__status" role="status">
                    {language === 'es'
                      ? 'Aún no hay listas. Ponle un nombre para empezar.'
                      : 'No lists yet. Name one to start.'}
                  </p>
                ) : (
                  <p className="dashboard-ofac-list-switcher__status" role="status">
                    {language === 'es' ? 'Cargando listas…' : 'Loading lists…'}
                  </p>
                )}
              </div>
              <div className="dashboard-ofac-list-switcher__actions">
                {!companySelected ? (
                  <p className="dashboard-ofac-list-switcher__status" role="status">
                    {language === 'es'
                      ? 'Las listas se desbloquean al elegir una empresa.'
                      : 'Whitelist actions unlock after you select a company.'}
                  </p>
                ) : ofacWhitelistRenameOpen ? (
                  <div className="dashboard-ofac-list-switcher__create">
                    <label htmlFor="ofac-rename-list-name">
                      <span>{language === 'es' ? 'Renombrar lista' : 'Rename list'}</span>
                      <input
                        id="ofac-rename-list-name"
                        type="text"
                        value={ofacWhitelistRenameDraft}
                        autoFocus
                        autoComplete="off"
                        onChange={(event) => setOfacWhitelistRenameDraft(event.target.value)}
                        maxLength={80}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            void handleOfacRenameWhitelist();
                          }
                          if (event.key === 'Escape') {
                            setOfacWhitelistRenameOpen(false);
                            setOfacWhitelistRenameDraft('');
                          }
                        }}
                      />
                    </label>
                    <div className="dashboard-ofac-list-switcher__create-btns">
                      <button
                        type="button"
                        className="dashboard-btn dashboard-btn-primary"
                        disabled={ofacWhitelistBusy}
                        onClick={() => void handleOfacRenameWhitelist()}
                      >
                        {ofacWhitelistBusy
                          ? (language === 'es' ? 'Guardando…' : 'Saving…')
                          : (language === 'es' ? 'Guardar' : 'Save')}
                      </button>
                      <button
                        type="button"
                        className="dashboard-security-inline-btn"
                        disabled={ofacWhitelistBusy}
                        onClick={() => {
                          setOfacWhitelistRenameOpen(false);
                          setOfacWhitelistRenameDraft('');
                        }}
                      >
                        {language === 'es' ? 'Cancelar' : 'Cancel'}
                      </button>
                    </div>
                  </div>
                ) : ofacWhitelistCreateOpen ? (
                  <div className="dashboard-ofac-list-switcher__create">
                    <label htmlFor="ofac-new-list-name">
                      <span>{language === 'es' ? 'Nombre de la nueva lista' : 'New list name'}</span>
                      <input
                        id="ofac-new-list-name"
                        ref={ofacNewWhitelistNameRef}
                        type="text"
                        value={ofacNewWhitelistName}
                        autoFocus
                        autoComplete="off"
                        aria-invalid={Boolean(ofacNewWhitelistNameError)}
                        aria-describedby="ofac-new-list-name-hint"
                        onChange={(event) => {
                          setOfacNewWhitelistName(event.target.value);
                          if (ofacNewWhitelistNameError) {
                            setOfacNewWhitelistNameError(null);
                          }
                        }}
                        placeholder={language === 'es' ? 'p. ej. Caracas' : 'e.g. Caracas'}
                        maxLength={80}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            void handleOfacCreateWhitelist();
                          }
                          if (event.key === 'Escape') {
                            setOfacWhitelistCreateOpen(false);
                            setOfacNewWhitelistName('');
                            setOfacNewWhitelistNameError(null);
                          }
                        }}
                      />
                    </label>
                    <p id="ofac-new-list-name-hint" className="dashboard-ofac-list-switcher__hint">
                      {language === 'es'
                        ? 'Puede ser una ciudad, un equipo o un año. Cada empresa puede tener varias listas. Ubicación de trabajo (abajo) es el sitio de la persona.'
                        : 'A city, team, or year is fine. Each company can have several lists. Work location below is the person’s site.'}
                    </p>
                    {ofacNewWhitelistNameError ? (
                      <p className="dashboard-ofac-list-switcher__field-error" role="alert">
                        {ofacNewWhitelistNameError}
                      </p>
                    ) : null}
                    <div className="dashboard-ofac-list-switcher__create-btns">
                      <button
                        type="button"
                        className="dashboard-btn dashboard-btn-primary"
                        disabled={ofacWhitelistCreating || !companySelected}
                        onClick={() => void handleOfacCreateWhitelist()}
                      >
                        {ofacWhitelistCreating
                          ? (language === 'es' ? 'Creando…' : 'Creating…')
                          : (language === 'es' ? 'Crear' : 'Create')}
                      </button>
                      <button
                        type="button"
                        className="dashboard-security-inline-btn"
                        disabled={ofacWhitelistCreating}
                        onClick={() => {
                          setOfacWhitelistCreateOpen(false);
                          setOfacNewWhitelistName('');
                          setOfacNewWhitelistNameError(null);
                        }}
                      >
                        {language === 'es' ? 'Cancelar' : 'Cancel'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="dashboard-ofac-list-switcher__new"
                    disabled={!companySelected || ofacNamedWhitelistsLoad === 'idle' || ofacNamedWhitelistsLoad === 'loading'}
                    onClick={() => {
                      setOfacWhitelistRenameOpen(false);
                      setOfacWhitelistCreateOpen(true);
                    }}
                  >
                    {language === 'es' ? '+ Nueva lista' : '+ New list'}
                  </button>
                )}
              </div>
            </div>
            <div className="dashboard-ofac-kpi-grid dashboard-ofac-kpi-grid--whitelist" role="list">
              <div role="listitem">
                <span>{language === 'es' ? 'En esta lista' : 'On this list'}</span>
                <strong>{rosterSubjects.length}</strong>
              </div>
              <div role="listitem">
                <span>{language === 'es' ? 'Vigentes' : 'Currently valid'}</span>
                <strong>{rosterSubjects.filter((row) => Boolean(row.hire_clearance?.validated)).length}</strong>
              </div>
              <div role="listitem">
                <span>{language === 'es' ? 'Revalidación vencida' : 'Re-validation due'}</span>
                <strong>{dueIds.length}</strong>
              </div>
            </div>
            {rosterSubjects.some((row) => isOfacHireSubject(row)) ? (
            <ol className="dashboard-ofac-step-legend" aria-label={language === 'es' ? 'Pasos de contratación' : 'Hire steps'}>
              {OFAC_HIRE_CHECKLIST.map((step, index) => (
                <li key={step.id}>
                  <span>{index + 1}</span>
                  {language === 'es' ? OFAC_HIRE_STEP_SHORT[step.id].es : OFAC_HIRE_STEP_SHORT[step.id].en}
                </li>
              ))}
            </ol>
            ) : null}
            <div className="dashboard-ofac-false-hit-queue">
              <strong className="help-badge-label-row">
                {language === 'es' ? `Añadir a ${activeWhitelistName}` : `Add to ${activeWhitelistName}`}
                <HelpBadge label={language === 'es' ? 'Ayuda para añadir a la lista blanca' : 'Help about adding to the whitelist'}>
                  <p>
                    {language === 'es'
                      ? 'Añade cliente, proveedor, UBO, empleado o contratista. Entra a la cartera viva y a esta lista. SDN corre al guardar. Nombre y dueño son obligatorios. Dirección, IDs y familiares conocidos (incluidos hijos) ayudan a comparar con SDN (FAQ 5 de OFAC / CIP de FinCEN). Empleado/contratista: última validación tras firma en EE.UU. Los demás: al revalidar SDN.'
                      : 'Add a customer, vendor, UBO, employee, or contractor. They join the live book and this roster. SDN runs on save. Name and owner are required. Address, IDs, and known family (including children) help match SDN listings (OFAC FAQ 5 / FinCEN CIP). Employees/contractors stamp last validated after U.S. sign-off. Other roles stamp it on SDN re-validate.'}
                  </p>
                </HelpBadge>
              </strong>
              <p className="dashboard-ofac-whitelist-intake__hint">
                {ofacWhitelistKindChosen
                  ? (ofacWhitelistIsPerson
                    ? (language === 'es'
                      ? 'Nombre y dueño son obligatorios. El resto es voluntario: dirección, IDs y familiares conocidos (incluidos hijos) ayudan a comparar con SDN.'
                      : 'Name and owner are required. Everything else is voluntary: address, IDs, and known family (including children) help compare against an SDN hit.')
                    : (language === 'es'
                      ? 'Nombre y dueño son obligatorios. El resto es voluntario: dirección comercial e ID fiscal ayudan a comparar con SDN.'
                      : 'Name and owner are required. Everything else is voluntary: business address and tax ID help compare against an SDN hit.'))
                  : (language === 'es'
                    ? 'Elige persona o empresa. Luego aparece el nombre y sólo las secciones que aplican.'
                    : 'Choose person or company. The name fields and only the sections that apply will appear next.')}
              </p>
              <div className="dashboard-ofac-whitelist-intake">
                <fieldset className="research-intake-target-type">
                  <legend>{language === 'es' ? 'Tipo' : 'Type'}</legend>
                  <label>
                    <input
                      type="radio"
                      name="ofac-whitelist-intake-kind"
                      checked={ofacWhitelistIntakeKind === 'person'}
                      onChange={() => applyWhitelistIntakeKind('person')}
                    />
                    {language === 'es' ? 'Persona' : 'Person'}
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="ofac-whitelist-intake-kind"
                      checked={ofacWhitelistIntakeKind === 'company'}
                      onChange={() => applyWhitelistIntakeKind('company')}
                    />
                    {language === 'es' ? 'Empresa' : 'Company'}
                  </label>
                </fieldset>
                {ofacWhitelistKindChosen ? (
                <>
                <details className="dashboard-payments-panel dashboard-ofac-screening-accordion" open>
                  <summary className="dashboard-ofac-screening-accordion__summary">
                    {language === 'es' ? 'Identidad' : 'Identity'}
                    <span className="dashboard-ofac-screening-accordion__chevron" aria-hidden="true" />
                  </summary>
                  <div className="dashboard-ofac-screening-accordion__body">
                    <div className="dashboard-form-grid">
                      <label>
                        <span>{language === 'es' ? 'Rol' : 'Role'}</span>
                        <select
                          value={ofacWhitelistForm.relationshipType}
                          onChange={(event) => setOfacWhitelistForm((current) => ({
                            ...current,
                            relationshipType: event.target.value as OfacScreeningRelationshipType,
                          }))}
                        >
                          {OFAC_SCREENING_RELATIONSHIP_OPTIONS.filter((option) => (
                            (ofacWhitelistKindRoles as readonly string[]).includes(option.value)
                          )).map((option) => (
                            <option key={option.value} value={option.value}>
                              {language === 'es' ? option.labelEs : option.labelEn}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    {ofacWhitelistIsPerson ? (
                      <OfacNameOrderIntake
                        language={language}
                        value={{
                          nameStyle: ofacWhitelistForm.nameStyle,
                          givenName: ofacWhitelistForm.givenName,
                          givenName2: ofacWhitelistForm.givenName2,
                          surname: ofacWhitelistForm.surname,
                          surname2: ofacWhitelistForm.surname2,
                          entityName: ofacWhitelistForm.entityName,
                        }}
                        onChange={(next) => setOfacWhitelistForm((current) => ({
                          ...current,
                          ...next,
                        }))}
                      />
                    ) : (
                      <div className="dashboard-form-grid">
                        <label className="dashboard-ofac-whitelist-intake__span">
                          <span>{language === 'es' ? 'Nombre legal' : 'Legal name'}</span>
                          <input
                            type="text"
                            value={ofacWhitelistForm.entityName}
                            onChange={(event) => setOfacWhitelistForm((current) => ({ ...current, entityName: event.target.value }))}
                            placeholder={language === 'es' ? 'Nombre legal' : 'Legal name'}
                          />
                        </label>
                      </div>
                    )}
                    <div className="dashboard-form-grid">
                      <label className="dashboard-ofac-whitelist-intake__span">
                        <span>{language === 'es' ? 'Alias / nombres anteriores' : 'Aliases / former names'}</span>
                        <input
                          type="text"
                          value={ofacWhitelistForm.aliases}
                          onChange={(event) => setOfacWhitelistForm((current) => ({ ...current, aliases: event.target.value }))}
                          placeholder={language === 'es' ? 'Separados por coma' : 'Comma-separated'}
                        />
                      </label>
                      {ofacWhitelistIsPerson ? (
                      <label>
                        <span>{language === 'es' ? 'Fecha de nacimiento' : 'Date of birth'}</span>
                        <input
                          type="date"
                          value={ofacWhitelistForm.birthdate}
                          onChange={(event) => setOfacWhitelistForm((current) => ({ ...current, birthdate: event.target.value }))}
                        />
                      </label>
                      ) : null}
                      {ofacWhitelistIsPerson ? (
                      <label>
                        <span>{language === 'es' ? 'Nacionalidad' : 'Nationality'}</span>
                        <select
                          value={ofacWhitelistForm.nationality}
                          onChange={(event) => setOfacWhitelistForm((current) => ({ ...current, nationality: event.target.value }))}
                        >
                          <option value="">{language === 'es' ? 'Elegir país' : 'Select country'}</option>
                          {withOtherOption(ofacCountryOptions(language), language).map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </label>
                      ) : null}
                    </div>
                    {ofacWhitelistIsPerson ? (
                      <div>
                        <p className="dashboard-ofac-whitelist-intake__hint">
                          {language === 'es' ? 'Lugar de nacimiento' : 'Place of birth'}
                        </p>
                        <OfacLocationSelect
                          language={language}
                          includeCountry
                          value={{
                            country: ofacWhitelistForm.birthCountry,
                            otherCountry: ofacWhitelistForm.otherBirthCountry,
                            region: ofacWhitelistForm.birthRegion,
                            otherRegion: ofacWhitelistForm.otherBirthRegion,
                            county: ofacWhitelistForm.birthCounty,
                            otherCounty: ofacWhitelistForm.otherBirthCounty,
                            city: ofacWhitelistForm.birthCity,
                            otherCity: ofacWhitelistForm.otherBirthCity,
                          }}
                          onChange={(geo) => setOfacWhitelistForm((current) => {
                            const next = {
                              ...current,
                              birthCountry: geo.country || '',
                              otherBirthCountry: geo.otherCountry || '',
                              birthRegion: geo.region || '',
                              otherBirthRegion: geo.otherRegion || '',
                              birthCounty: geo.county || '',
                              otherBirthCounty: geo.otherCounty || '',
                              birthCity: geo.city || '',
                              otherBirthCity: geo.otherCity || '',
                            };
                            return { ...next, placeOfBirth: composeOfacIntakePlace(next) };
                          })}
                        />
                      </div>
                    ) : null}
                  </div>
                </details>
                <details className="dashboard-payments-panel dashboard-ofac-screening-accordion">
                  <summary className="dashboard-ofac-screening-accordion__summary">
                    <span>
                      {language === 'es' ? 'IDs oficiales' : 'Government IDs'}
                      {' '}
                      <span className="dashboard-ofac-whitelist-intake__optional">{language === 'es' ? '(recomendado)' : '(recommended)'}</span>
                    </span>
                    <span className="dashboard-ofac-screening-accordion__chevron" aria-hidden="true" />
                  </summary>
                  <div className="dashboard-ofac-screening-accordion__body">
                    <p className="dashboard-ofac-whitelist-intake__hint">
                      {language === 'es'
                        ? 'CIP de FinCEN pide un número de identificación. No hace falta un campo aparte de SSN: usa ID nacional, pasaporte o ID fiscal.'
                        : 'FinCEN CIP asks for an identification number. Do not add a dedicated SSN field — use national ID, passport, or tax ID.'}
                    </p>
                    <div className="dashboard-form-grid">
                      <label>
                        <span>{language === 'es' ? 'País emisor' : 'Issuing country'}</span>
                        <select
                          value={ofacWhitelistForm.identifierCountry}
                          onChange={(event) => setOfacWhitelistForm((current) => ({
                            ...current,
                            identifierCountry: event.target.value,
                            identifierType: '',
                          }))}
                        >
                          <option value="">{language === 'es' ? 'Elegir país' : 'Select country'}</option>
                          {withOtherOption(ofacCountryOptions(language), language).map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        <span>{language === 'es' ? 'Tipo de documento' : 'Document type'}</span>
                        <select
                          value={ofacWhitelistForm.identifierType}
                          onChange={(event) => setOfacWhitelistForm((current) => ({
                            ...current,
                            identifierType: event.target.value,
                          }))}
                        >
                          <option value="">{language === 'es' ? 'Elegir tipo' : 'Select type'}</option>
                          {ofacWhitelistIdTypeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {language === 'es' ? option.labelEs : option.labelEn}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        <span>
                          {ofacWhitelistIsPerson
                            ? (language === 'es' ? 'Número' : 'Number')
                            : (language === 'es' ? 'ID fiscal' : 'Tax ID')}
                        </span>
                        <input
                          type="text"
                          value={ofacWhitelistForm.identifier}
                          onChange={(event) => setOfacWhitelistForm((current) => ({
                            ...current,
                            identifier: event.target.value,
                            passport: current.identifierType === 'passport' ? event.target.value : current.passport,
                          }))}
                          placeholder={
                            ofacWhitelistIsPerson
                              ? (language === 'es' ? 'Número del documento' : 'Document number')
                              : (language === 'es' ? 'EIN / NIT' : 'EIN / tax ID')
                          }
                        />
                      </label>
                      {ofacWhitelistIsPerson && ofacWhitelistForm.identifierType !== 'passport' ? (
                      <label>
                        <span>
                          {language === 'es' ? 'Pasaporte' : 'Passport'}
                          {' '}
                          <span className="dashboard-ofac-whitelist-intake__optional">{language === 'es' ? '(opcional)' : '(optional)'}</span>
                        </span>
                        <input
                          type="text"
                          value={ofacWhitelistForm.passport}
                          onChange={(event) => setOfacWhitelistForm((current) => ({ ...current, passport: event.target.value }))}
                          placeholder={language === 'es' ? 'Número de pasaporte' : 'Passport number'}
                        />
                      </label>
                      ) : null}
                    </div>
                  </div>
                </details>
                <details className="dashboard-payments-panel dashboard-ofac-screening-accordion">
                  <summary className="dashboard-ofac-screening-accordion__summary">
                    <span>
                      {language === 'es' ? 'Direcciones' : 'Addresses'}
                      {' '}
                      <span className="dashboard-ofac-whitelist-intake__optional">{language === 'es' ? '(recomendado)' : '(recommended)'}</span>
                    </span>
                    <span className="dashboard-ofac-screening-accordion__chevron" aria-hidden="true" />
                  </summary>
                  <div className="dashboard-ofac-screening-accordion__body">
                    <p className="dashboard-ofac-whitelist-intake__hint">
                      {ofacWhitelistIsPerson
                        ? (language === 'es'
                          ? 'Sí: dirección residencial o de negocio. OFAC FAQ 5 y CIP de FinCEN la usan para confirmar si un hallazgo SDN es la misma persona. Un apartado de correos solo no basta. Añade domicilio, trabajo o direcciones anteriores.'
                          : 'Yes: residential or business street address. OFAC FAQ 5 and FinCEN CIP use it to confirm whether an SDN hit is the same person. A P.O. Box alone is not enough. Add home, work, or previous addresses.')
                        : (language === 'es'
                          ? 'Dirección comercial. OFAC FAQ 5 y CIP de FinCEN la usan para confirmar si un hallazgo SDN es la misma empresa. Un apartado de correos solo no basta.'
                          : 'Business street address. OFAC FAQ 5 and FinCEN CIP use it to confirm whether an SDN hit is the same company. A P.O. Box alone is not enough.')}
                    </p>
                    <div className="dashboard-ofac-whitelist-intake__repeat">
                      {(ofacWhitelistForm.addresses.length ? ofacWhitelistForm.addresses : [ofacWhitelistBlankAddress]).map((row, index) => (
                        <div className="dashboard-ofac-whitelist-intake__row" key={`address-${index}`}>
                          <div className="dashboard-ofac-whitelist-intake__row-head">
                            <span className="dashboard-ofac-whitelist-intake__row-title">
                              {language === 'es' ? `Dirección ${index + 1}` : `Address ${index + 1}`}
                            </span>
                            {ofacWhitelistForm.addresses.length > 1 ? (
                              <button
                                type="button"
                                className="dashboard-security-inline-btn"
                                onClick={() => setOfacWhitelistForm((current) => ({
                                  ...current,
                                  addresses: removeOfacIntakeRow(current.addresses, index, ofacWhitelistBlankAddress),
                                }))}
                              >
                                {language === 'es' ? 'Quitar' : 'Remove'}
                              </button>
                            ) : null}
                          </div>
                          <div className="dashboard-form-grid">
                            <label>
                              <span>{language === 'es' ? 'Tipo' : 'Kind'}</span>
                              <select
                                value={row.kind}
                                onChange={(event) => setOfacWhitelistForm((current) => ({
                                  ...current,
                                  addresses: patchOfacIntakeRow(current.addresses, index, { kind: event.target.value }, ofacWhitelistBlankAddress),
                                }))}
                              >
                                {ofacWhitelistAddressKindOptions.map((kind) => (
                                  <option key={kind.value} value={kind.value}>
                                    {language === 'es' ? kind.labelEs : kind.labelEn}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <OfacLocationSelect
                              language={language}
                              includeCountry
                              followLayout
                              value={{
                                country: row.country,
                                otherCountry: row.otherCountry,
                                region: row.region,
                                otherRegion: row.otherRegion,
                                county: row.county,
                                otherCounty: row.otherCounty,
                                city: row.city,
                                otherCity: row.otherCity,
                              }}
                              onChange={(geo) => setOfacWhitelistForm((current) => ({
                                ...current,
                                addresses: patchOfacIntakeRow(current.addresses, index, {
                                  country: geo.country || '',
                                  otherCountry: geo.otherCountry || '',
                                  region: geo.region || '',
                                  otherRegion: geo.otherRegion || '',
                                  county: geo.county || '',
                                  otherCounty: geo.otherCounty || '',
                                  city: geo.city || '',
                                  otherCity: geo.otherCity || '',
                                }, ofacWhitelistBlankAddress),
                              }))}
                            />
                            {ofacAddressLayout(row.country)
                              .filter((field) => !['country', 'region', 'county', 'city'].includes(field))
                              .map((field) => {
                                const key = field === 'postal' ? 'postalCode' : field;
                                const textValue = field === 'line2'
                                  ? row.line2
                                  : field === 'neighborhood'
                                    ? row.neighborhood
                                    : field === 'postal'
                                      ? row.postalCode
                                      : row.line;
                                return (
                                  <label key={`${field}-${index}`}>
                                    <span>{ofacAddressFieldLabel(field as OfacAddressField, row.country, language)}</span>
                                    <input
                                      type="text"
                                      value={textValue || ''}
                                      onChange={(event) => setOfacWhitelistForm((current) => ({
                                        ...current,
                                        addresses: patchOfacIntakeRow(
                                          current.addresses,
                                          index,
                                          { [key]: event.target.value },
                                          ofacWhitelistBlankAddress,
                                        ),
                                      }))}
                                      placeholder={
                                        field === 'line'
                                          ? (language === 'es' ? 'Calle y número' : 'Street and number')
                                          : undefined
                                      }
                                    />
                                  </label>
                                );
                              })}
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="dashboard-security-inline-btn dashboard-ofac-whitelist-intake__add"
                        disabled={ofacWhitelistForm.addresses.length >= OFAC_INTAKE_MAX_ROWS}
                        onClick={() => setOfacWhitelistForm((current) => ({
                          ...current,
                          addresses: appendOfacIntakeRow(current.addresses, ofacWhitelistBlankAddress),
                        }))}
                      >
                        {language === 'es' ? 'Añadir otra dirección' : 'Add another address'}
                      </button>
                    </div>
                  </div>
                </details>
                {ofacWhitelistIsPerson ? (
                <details className="dashboard-payments-panel dashboard-ofac-screening-accordion">
                  <summary className="dashboard-ofac-screening-accordion__summary">
                    <span>
                      {language === 'es' ? 'Familia y asociados' : 'Family and associates'}
                      {' '}
                      <span className="dashboard-ofac-whitelist-intake__optional">{language === 'es' ? '(opcional)' : '(optional)'}</span>
                    </span>
                    <span className="dashboard-ofac-screening-accordion__chevron" aria-hidden="true" />
                  </summary>
                  <div className="dashboard-ofac-screening-accordion__body">
                    <p className="dashboard-ofac-whitelist-intake__hint">
                      {language === 'es'
                        ? 'Añade todos los familiares y asociados conocidos, incluidos hijos. Nombre legal y relación. Pulsa Guardar esta persona en cada uno. Sirve cuando el SDN dice “esposo/a de…” o nombra parientes.'
                        : 'Add every known relative and associate, including children. Legal name and relation. Click Save this person on each one. That helps when an SDN listing says “spouse of…” or names family.'}
                    </p>
                    <div className="dashboard-ofac-whitelist-intake__repeat">
                      {(ofacWhitelistForm.familyMembers.length ? ofacWhitelistForm.familyMembers : [emptyOfacIntakeFamilyMember()]).map((row, index) => (
                        <div className="dashboard-ofac-whitelist-intake__row" key={`family-${index}`}>
                          <div className="dashboard-ofac-whitelist-intake__row-head">
                            <span className="dashboard-ofac-whitelist-intake__row-title">
                              {language === 'es' ? `Persona ${index + 1}` : `Person ${index + 1}`}
                            </span>
                            {ofacWhitelistForm.familyMembers.length > 1 ? (
                              <button
                                type="button"
                                className="dashboard-security-inline-btn"
                                onClick={() => setOfacWhitelistForm((current) => ({
                                  ...current,
                                  familyMembers: removeOfacIntakeRow(current.familyMembers, index, emptyOfacIntakeFamilyMember()),
                                }))}
                              >
                                {language === 'es' ? 'Quitar' : 'Remove'}
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="dashboard-security-inline-btn dashboard-ofac-whitelist-intake__save-person"
                              disabled={ofacWhitelistSaving != null || ofacScreeningScreeningAll || !companySelected || ofacActiveWhitelistId == null}
                              onClick={() => void handleOfacWhitelistSaveFamilyMember(index)}
                            >
                              {ofacWhitelistSaving === `family-${index}`
                                ? (language === 'es' ? 'Guardando…' : 'Saving…')
                                : (language === 'es' ? 'Guardar esta persona' : 'Save this person')}
                            </button>
                          </div>
                          <div className="dashboard-form-grid">
                            <label>
                              <span>{language === 'es' ? 'Relación' : 'Relation'}</span>
                              <select
                                value={row.relation}
                                onChange={(event) => setOfacWhitelistForm((current) => ({
                                  ...current,
                                  familyMembers: patchOfacIntakeRow(current.familyMembers, index, { relation: event.target.value }, emptyOfacIntakeFamilyMember()),
                                }))}
                              >
                                {OFAC_FAMILY_RELATIONS.map((relation) => (
                                  <option key={relation.value} value={relation.value}>
                                    {language === 'es' ? relation.labelEs : relation.labelEn}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label>
                              <span>{language === 'es' ? 'Nombre legal' : 'Legal name'}</span>
                              <input
                                type="text"
                                value={row.name}
                                onChange={(event) => setOfacWhitelistForm((current) => ({
                                  ...current,
                                  familyMembers: patchOfacIntakeRow(current.familyMembers, index, { name: event.target.value }, emptyOfacIntakeFamilyMember()),
                                }))}
                                placeholder={language === 'es' ? 'Nombre legal' : 'Legal name'}
                              />
                            </label>
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="dashboard-security-inline-btn dashboard-ofac-whitelist-intake__add"
                        disabled={ofacWhitelistForm.familyMembers.length >= OFAC_INTAKE_MAX_ROWS}
                        onClick={() => setOfacWhitelistForm((current) => ({
                          ...current,
                          familyMembers: appendOfacIntakeRow(current.familyMembers, { name: '', relation: 'other' }),
                        }))}
                      >
                        {language === 'es' ? 'Añadir otro familiar' : 'Add another family member'}
                      </button>
                    </div>
                  </div>
                </details>
                ) : null}
                {ofacWhitelistIsPerson ? (
                <details className="dashboard-payments-panel dashboard-ofac-screening-accordion">
                  <summary className="dashboard-ofac-screening-accordion__summary">
                    <span>
                      {language === 'es' ? 'Empleadores' : 'Employers'}
                      {' '}
                      <span className="dashboard-ofac-whitelist-intake__optional">{language === 'es' ? '(opcional)' : '(optional)'}</span>
                    </span>
                    <span className="dashboard-ofac-screening-accordion__chevron" aria-hidden="true" />
                  </summary>
                  <div className="dashboard-ofac-screening-accordion__body">
                    <p className="dashboard-ofac-whitelist-intake__hint">
                      {language === 'es'
                        ? 'Añade empleador actual y anteriores. El primer sitio se usa como ubicación de trabajo en contratación.'
                        : 'Add current and previous employers. The first site is used as the hire work location.'}
                    </p>
                    <div className="dashboard-ofac-whitelist-intake__repeat">
                      {(ofacWhitelistForm.employers.length ? ofacWhitelistForm.employers : [emptyOfacIntakeEmployer()]).map((row, index) => (
                        <div className="dashboard-ofac-whitelist-intake__row" key={`employer-${index}`}>
                          <div className="dashboard-ofac-whitelist-intake__row-head">
                            <span className="dashboard-ofac-whitelist-intake__row-title">
                              {language === 'es' ? `Empleador ${index + 1}` : `Employer ${index + 1}`}
                            </span>
                            {ofacWhitelistForm.employers.length > 1 ? (
                              <button
                                type="button"
                                className="dashboard-security-inline-btn"
                                onClick={() => setOfacWhitelistForm((current) => ({
                                  ...current,
                                  employers: removeOfacIntakeRow(current.employers, index, emptyOfacIntakeEmployer()),
                                }))}
                              >
                                {language === 'es' ? 'Quitar' : 'Remove'}
                              </button>
                            ) : null}
                          </div>
                          <div className="dashboard-form-grid">
                            <label>
                              <span>{language === 'es' ? 'Empleador' : 'Employer'}</span>
                              <input
                                type="text"
                                value={row.name}
                                onChange={(event) => setOfacWhitelistForm((current) => ({
                                  ...current,
                                  employers: patchOfacIntakeRow(current.employers, index, { name: event.target.value }, emptyOfacIntakeEmployer()),
                                }))}
                                placeholder={language === 'es' ? 'Nombre de la empresa' : 'Company name'}
                              />
                            </label>
                            <label>
                              <span>{language === 'es' ? 'Lugar de trabajo' : 'Work location'}</span>
                              <input
                                type="text"
                                value={row.location}
                                onChange={(event) => setOfacWhitelistForm((current) => ({
                                  ...current,
                                  employers: patchOfacIntakeRow(current.employers, index, { location: event.target.value }, emptyOfacIntakeEmployer()),
                                }))}
                                placeholder={language === 'es' ? 'Ciudad o sitio' : 'City or site'}
                              />
                            </label>
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="dashboard-security-inline-btn dashboard-ofac-whitelist-intake__add"
                        disabled={ofacWhitelistForm.employers.length >= OFAC_INTAKE_MAX_ROWS}
                        onClick={() => setOfacWhitelistForm((current) => ({
                          ...current,
                          employers: appendOfacIntakeRow(current.employers, emptyOfacIntakeEmployer()),
                        }))}
                      >
                        {language === 'es' ? 'Añadir otro empleador' : 'Add another employer'}
                      </button>
                    </div>
                  </div>
                </details>
                ) : null}
                <details className="dashboard-payments-panel dashboard-ofac-screening-accordion" open>
                  <summary className="dashboard-ofac-screening-accordion__summary">
                    {language === 'es' ? 'Control del expediente' : 'File control'}
                    <span className="dashboard-ofac-screening-accordion__chevron" aria-hidden="true" />
                  </summary>
                  <div className="dashboard-ofac-screening-accordion__body">
                    <div className="dashboard-form-grid">
                      <label>
                        <span>{language === 'es' ? 'Dueño' : 'Owner'}</span>
                        <input
                          type="text"
                          value={ofacWhitelistForm.owner}
                          onChange={(event) => setOfacWhitelistForm((current) => ({ ...current, owner: event.target.value }))}
                          placeholder={language === 'es' ? 'Persona o mesa' : 'Person or desk'}
                        />
                      </label>
                      <label>
                        <span>{language === 'es' ? 'Ciclo de revisión' : 'Review cycle'}</span>
                        <select
                          value={ofacWhitelistForm.recertifyDays}
                          onChange={(event) => setOfacWhitelistForm((current) => ({
                            ...current,
                            recertifyDays: normalizeOfacReviewCycleDays(event.target.value),
                          }))}
                        >
                          {OFAC_REVIEW_CYCLE_DAYS.map((days) => (
                            <option key={days} value={days}>
                              {days === 90
                                ? (language === 'es' ? '90 días (alto riesgo)' : '90 days (high-risk)')
                                : days === 180
                                  ? (language === 'es' ? '180 días' : '180 days')
                                  : (language === 'es' ? '365 días (anual)' : '365 days (annual)')}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        <span>{language === 'es' ? 'Notas' : 'Notes'}</span>
                        <input
                          type="text"
                          value={ofacWhitelistForm.notes}
                          onChange={(event) => setOfacWhitelistForm((current) => ({ ...current, notes: event.target.value }))}
                          placeholder={language === 'es' ? 'Opcional' : 'Optional'}
                        />
                      </label>
                    </div>
                  </div>
                </details>
                </>
                ) : null}
              </div>
              {ofacWhitelistKindChosen ? (
              <div className="dashboard-ofac-whitelist-intake__save-bar">
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn-primary"
                  disabled={ofacWhitelistSaving != null || ofacScreeningScreeningAll || !companySelected || ofacActiveWhitelistId == null}
                  onClick={() => void handleOfacWhitelistAdd()}
                >
                  {ofacWhitelistSaving === 'primary'
                    ? (language === 'es' ? 'Guardando…' : 'Saving…')
                    : ofacWhitelistIsPerson
                      ? (language === 'es' ? 'Guardar esta persona' : 'Save this person')
                      : (language === 'es' ? 'Guardar esta empresa' : 'Save this company')}
                </button>
                <p className="dashboard-ofac-whitelist-intake__hint">
                  {ofacWhitelistIsPerson
                    ? (language === 'es'
                      ? 'Guarda cada persona por separado. Familiares tienen su propio botón Guardar. SDN corre al guardar. Nombre y dueño son obligatorios.'
                      : 'Save each person separately. Family members have their own Save. SDN runs on save. Name and owner are required.')
                    : (language === 'es'
                      ? 'Guarda cada empresa por separado. SDN corre al guardar. Nombre y dueño son obligatorios.'
                      : 'Save each company separately. SDN runs on save. Name and owner are required.')}
                </p>
              </div>
              ) : null}
            </div>
            <div className="dashboard-ofac-false-hit-queue">
              <strong className="help-badge-label-row">
                {language === 'es' ? 'Personas y pasos' : 'People and steps'}
                <HelpBadge label={language === 'es' ? 'Ayuda sobre revalidación' : 'Help about re-validation'}>
                  <p>
                    {language === 'es'
                      ? 'Revalidar vuelve a screenear SDN. En contratación también comprueba el checklist. Un fallo no borra la última fecha correcta. El ciclo de revisión es 90, 180 o 365 días (práctica CGSS/Wolfsberg).'
                      : 'Re-validate reruns SDN. Hire roles also check the hire checklist. A failed rerun does not wipe the last successful date. Review cycle is 90, 180, or 365 days (CGSS/Wolfsberg practice).'}
                  </p>
                </HelpBadge>
              </strong>
              <div className="dashboard-action-buttons" style={{ marginBottom: '0.75rem' }}>
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn-primary"
                  disabled={ofacScreeningScreeningAll || dueIds.length === 0}
                  onClick={() => void handleOfacHireValidateDue(dueIds)}
                >
                  {ofacScreeningScreeningAll
                    ? (language === 'es' ? 'Revalidando…' : 'Re-validating…')
                    : (language === 'es'
                      ? `Revalidar vencidos (${dueIds.length})`
                      : `Re-validate due (${dueIds.length})`)}
                </button>
                <button
                  type="button"
                  className="dashboard-btn"
                  disabled={!companySelected || rosterSubjects.length === 0}
                  onClick={() => {
                    const csv = ofacWhitelistRecordkeepingCsv({
                      companyName: whitelistCompanyName,
                      whitelistName: activeWhitelistName,
                      subjects: rosterSubjects,
                      pairs: ofacFalseHitPairs.map((pair) => ({
                        ...pair,
                        subject_name: ofacScreeningSubjects.find((row) => row.id === pair.subject_id)?.entity_name
                          || `#${pair.subject_id}`,
                      })),
                    });
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `ofac-whitelist-${activeWhitelistName.replace(/[^\w.-]+/g, '-')}.csv`;
                    link.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  {language === 'es' ? 'Exportar registros (10 años)' : 'Export records (10-year)'}
                </button>
              </div>
              {rosterSubjects.length ? (
                <div className="dashboard-subscription-plan-list">
                  {rosterSubjects.map((subject) => {
                    const busy = ofacScreeningBusyId === subject.id;
                    const due = Boolean(subject.hire_clearance?.recertify_due) || !subject.hire_clearance?.last_validated_at;
                    const nextDue = nextOfacValidationDue(
                      subject.hire_clearance?.last_validated_at,
                      subject.hire_clearance?.recertify_days || 365,
                    );
                    const hireRow = isOfacHireSubject(subject);
                    const controlDraft = ofacWhitelistControlDrafts[subject.id] || {
                      owner: String(subject.hire_clearance?.owner || ''),
                      recertifyDays: normalizeOfacReviewCycleDays(subject.hire_clearance?.recertify_days),
                    };
                    const controlDirty = (
                      controlDraft.owner.trim() !== String(subject.hire_clearance?.owner || '').trim()
                      || controlDraft.recertifyDays !== normalizeOfacReviewCycleDays(subject.hire_clearance?.recertify_days)
                    );
                    return (
                      <div key={subject.id} className="dashboard-subscription-plan-item">
                        <div>
                          <strong>{subject.entity_name}</strong>
                          <p>
                            {relationshipLabel(String(subject.relationship_type))}
                            {' · '}
                            {subject.current_status || (language === 'es' ? 'Sin screening' : 'Not screened')}
                            {subject.hire_clearance?.location ? ` · ${subject.hire_clearance.location}` : ''}
                            {hireRow ? ` · ${language === 'es' ? 'paso' : 'step'} ${currentStepLabel(subject)}` : ''}
                          </p>
                          {hireRow ? (
                          <ol className="dashboard-ofac-hire-stepper" aria-label={language === 'es' ? 'Progreso de contratación' : 'Hire progress'}>
                            {OFAC_HIRE_CHECKLIST.map((step, index) => {
                              const done = Boolean(subject.hire_clearance?.checklist?.[step.id]?.done);
                              const isCurrent = subject.hire_clearance?.current_step?.id === step.id;
                              const lane = laneForVerifyStep({
                                done,
                                waitingOnExternal: false,
                                isCurrent,
                              });
                              return (
                                <li
                                  key={step.id}
                                  className={`dashboard-ofac-hire-step is-${lane}`}
                                  aria-current={isCurrent ? 'step' : undefined}
                                  title={language === 'es' ? step.labelEs : step.labelEn}
                                >
                                  <span>{index + 1}</span>
                                  {language === 'es' ? OFAC_HIRE_STEP_SHORT[step.id].es : OFAC_HIRE_STEP_SHORT[step.id].en}
                                </li>
                              );
                            })}
                          </ol>
                          ) : null}
                          <p className={`dashboard-ofac-whitelist-stamp${due ? ' is-due' : ''}`}>
                            {language === 'es' ? 'Última validación' : 'Last validated'}
                            {': '}
                            {formatOfacTimestamp(subject.hire_clearance?.last_validated_at, language)}
                            {nextDue
                              ? ` · ${language === 'es' ? 'próxima' : 'next due'} ${nextDue.toLocaleDateString(language === 'es' ? 'es' : 'en')}`
                              : ''}
                            {subject.hire_clearance?.last_validation_attempt_at
                              ? ` · ${language === 'es' ? 'último intento' : 'last attempt'} ${formatOfacTimestamp(subject.hire_clearance.last_validation_attempt_at, language)}`
                              : ''}
                            {due ? (language === 'es' ? ' · vence revalidar' : ' · re-validate due') : ''}
                            {subject.hire_clearance?.last_validated_status === 'failed'
                              ? (language === 'es' ? ' · último intento falló' : ' · last rerun failed')
                              : ''}
                            {subject.hire_clearance?.owner_missing
                              ? (language === 'es' ? ' · falta dueño' : ' · owner missing')
                              : ''}
                          </p>
                          {(() => {
                            const check = subject.identifiers?.vantage_identity_check as {
                              status?: string;
                              match_count?: number;
                              mismatch_count?: number;
                              missing_count?: number;
                              job_id?: string | null;
                            } | undefined;
                            if (!check || typeof check !== 'object') {
                              return null;
                            }
                            const status = String(check.status || '');
                            const label = language === 'es'
                              ? (status === 'corroborated' ? 'Identidad corroborada por Vantage'
                                : status === 'mismatch' ? 'Vantage no coincide con el intake'
                                  : status === 'partial' ? 'Vantage parcial'
                                    : 'Verificación Vantage incompleta')
                              : (status === 'corroborated' ? 'Identity corroborated by Vantage'
                                : status === 'mismatch' ? 'Vantage does not match intake'
                                  : status === 'partial' ? 'Vantage partial match'
                                    : 'Vantage identity check incomplete');
                            return (
                              <p className={`dashboard-ofac-whitelist-stamp${status === 'mismatch' ? ' is-due' : ''}`}>
                                {label}
                                {check.match_count != null ? ` · ${check.match_count} match` : ''}
                                {check.mismatch_count ? ` · ${check.mismatch_count} mismatch` : ''}
                                {check.missing_count ? ` · ${check.missing_count} missing` : ''}
                              </p>
                            );
                          })()}
                          <div className="dashboard-form-grid" style={{ marginTop: '0.5rem' }}>
                            <label>
                              <span>{language === 'es' ? 'Dueño' : 'Owner'}</span>
                              <input
                                type="text"
                                value={controlDraft.owner}
                                onChange={(event) => {
                                  const owner = event.target.value;
                                  setOfacWhitelistControlDrafts((current) => {
                                    const previous = current[subject.id] || controlDraft;
                                    return {
                                      ...current,
                                      [subject.id]: {
                                        owner,
                                        recertifyDays: previous.recertifyDays,
                                      },
                                    };
                                  });
                                }}
                                placeholder={language === 'es' ? 'Persona o mesa' : 'Person or desk'}
                              />
                            </label>
                            <label>
                              <span>{language === 'es' ? 'Ciclo de revisión' : 'Review cycle'}</span>
                              <select
                                value={controlDraft.recertifyDays}
                                onChange={(event) => {
                                  const recertifyDays = normalizeOfacReviewCycleDays(event.target.value);
                                  setOfacWhitelistControlDrafts((current) => {
                                    const previous = current[subject.id] || controlDraft;
                                    return {
                                      ...current,
                                      [subject.id]: {
                                        owner: previous.owner,
                                        recertifyDays,
                                      },
                                    };
                                  });
                                }}
                              >
                                {OFAC_REVIEW_CYCLE_DAYS.map((days) => (
                                  <option key={days} value={days}>
                                    {days === 90
                                      ? (language === 'es' ? '90 días' : '90 days')
                                      : days === 180
                                        ? (language === 'es' ? '180 días' : '180 days')
                                        : (language === 'es' ? '365 días' : '365 days')}
                                  </option>
                                ))}
                              </select>
                            </label>
                          </div>
                        </div>
                        <div className="dashboard-action-buttons">
                          <button
                            type="button"
                            className="dashboard-security-inline-btn"
                            disabled={busy || ofacScreeningScreeningAll}
                            onClick={() => void handleOfacHireValidate(subject.id)}
                          >
                            {language === 'es' ? 'Revalidar' : 'Re-validate'}
                          </button>
                          <button
                            type="button"
                            className="dashboard-security-inline-btn"
                            disabled={busy || ofacScreeningScreeningAll}
                            onClick={() => void handleOfacVantageBackgroundCheck(subject, 'whitelist_identity')}
                          >
                            {language === 'es' ? 'Verificar identidad (Vantage)' : 'Check identity (Vantage)'}
                          </button>
                          <button
                            type="button"
                            className="dashboard-security-inline-btn"
                            disabled={busy || (!controlDirty && !subject.hire_clearance?.owner_missing)}
                            onClick={() => void handleOfacWhitelistControlSave(subject.id)}
                          >
                            {language === 'es' ? 'Guardar dueño' : 'Save owner'}
                          </button>
                          <button
                            type="button"
                            className="dashboard-security-inline-btn"
                            onClick={() => handleNavSelection('ofacInvestigations')}
                          >
                            {language === 'es' ? 'Abrir investigación' : 'Open investigation'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="dashboard-panel-subtitle">
                  {ofacScreeningLoading
                    ? (language === 'es' ? 'Cargando...' : 'Loading...')
                    : (language === 'es'
                      ? `Nadie en “${activeWhitelistName}” todavía. Usa el formulario de arriba.`
                      : `No one on “${activeWhitelistName}” yet. Use the form above.`)}
                </p>
              )}
            </div>
            <div className="dashboard-ofac-false-hit-queue">
              <strong className="help-badge-label-row">
                {language === 'es'
                  ? 'Pares de falso hit (sujeto ↔ UID SDN)'
                  : 'False-hit pairs (subject ↔ SDN UID)'}
                <HelpBadge label={language === 'es' ? 'Ayuda sobre pares de falso hit' : 'Help about false-hit pairs'}>
                  <p>
                    {language === 'es'
                      ? 'El matcher siempre corre. Recertificar renueva la fecha de ese par, no omite el nombre.'
                      : 'The matcher always runs. Recertify refreshes that pair’s date; it does not skip the name.'}
                  </p>
                </HelpBadge>
              </strong>
              {ofacFalseHitPairs.length ? (
                <ul>
                  {ofacFalseHitPairs.slice(0, 24).map((pair) => {
                    const subjectName = ofacScreeningSubjects.find((row) => row.id === pair.subject_id)?.entity_name
                      || `#${pair.subject_id}`;
                    const pairBusy = ofacScreeningBusyId === pair.id;
                    return (
                      <li key={pair.id}>
                        <span>
                          <em>{subjectName}</em>
                          {` · UID ${pair.list_entry_id}`}
                          {pair.list_entry_name ? ` · ${pair.list_entry_name}` : ''}
                          {` · ${pair.status}`}
                          {pair.approved_at
                            ? ` · ${language === 'es' ? 'aprobado' : 'approved'} ${formatOfacTimestamp(pair.approved_at, language)}`
                            : ''}
                          {pair.recertified_at
                            ? ` · ${language === 'es' ? 'recertificado' : 'recertified'} ${formatOfacTimestamp(pair.recertified_at, language)}`
                            : ''}
                          {pair.recertify_due ? (language === 'es' ? ' · recertificar' : ' · recertify due') : ''}
                        </span>
                        <span className="dashboard-ofac-false-hit-queue__actions">
                          {pair.status === 'active' || pair.status === 'expired' ? (
                            <button
                              type="button"
                              className="dashboard-security-inline-btn"
                              disabled={pairBusy}
                              onClick={() => void handleOfacFalseHitAction(pair.id, 'recertify')}
                            >
                              {language === 'es' ? 'Recertificar' : 'Recertify'}
                            </button>
                          ) : null}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="dashboard-panel-subtitle">
                  {language === 'es'
                    ? 'Aún no hay pares. Créalos en Investigaciones cuando un match de lista sea un homónimo.'
                    : 'No pairs yet. Create them in Investigations when a list match is a namesake.'}
                </p>
              )}
            </div>
          </article>
        </section>
      );
    }

    if (activeNavItem === 'cloudCompliance') {
      if (!canAccessCloudCompliance) {
        return renderHomePane();
      }
      return (
        <ComplianceConsolePanel
          definition={CLOUD_COMPLIANCE_CONSOLE}
          language={language}
          reviewerLabel={accountEmail || accountDisplayName}
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          platformAdmin={hasPlatformAdminAccess}
          effectiveFeatures={effectiveFeatureList}
          workspaceReadOnly={!canWriteWorkspace}
        />
      );
    }

    if (activeNavItem === 'websiteCompliance') {
      if (!canAccessWebsiteCompliance) {
        return renderHomePane();
      }
      return (
        <ComplianceConsolePanel
          definition={WEBSITE_COMPLIANCE_CONSOLE}
          language={language}
          reviewerLabel={accountEmail || accountDisplayName}
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          platformAdmin={hasPlatformAdminAccess}
          effectiveFeatures={effectiveFeatureList}
          workspaceReadOnly={!canWriteWorkspace}
        />
      );
    }

    if (activeNavItem === 'dodContracts') {
      if (!canAccessDodContracts) {
        return renderHomePane();
      }
      return (
        <ComplianceConsolePanel
          definition={DOD_CONTRACTS_COMPLIANCE_CONSOLE}
          language={language}
          reviewerLabel={accountEmail || accountDisplayName}
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          platformAdmin={hasPlatformAdminAccess}
          workspaceReadOnly={!canWriteWorkspace}
        />
      );
    }

    if (activeNavItem === 'educationCompliance') {
      if (!canAccessEducationCompliance) {
        return renderHomePane();
      }
      return (
        <ComplianceConsolePanel
          definition={EDUCATION_COMPLIANCE_CONSOLE}
          language={language}
          reviewerLabel={accountEmail || accountDisplayName}
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          platformAdmin={hasPlatformAdminAccess}
          workspaceReadOnly={!canWriteWorkspace}
        />
      );
    }

    if (activeNavItem === 'aiCompliance') {
      if (!canAccessAiCompliance) {
        return renderHomePane();
      }
      return (
        <ComplianceConsolePanel
          definition={AI_COMPLIANCE_CONSOLE}
          language={language}
          reviewerLabel={accountEmail || accountDisplayName}
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          platformAdmin={hasPlatformAdminAccess}
          workspaceReadOnly={!canWriteWorkspace}
        />
      );
    }

    if (activeNavItem === 'complianceCalendar') {
      return (
        <CompliancePortfolioPage
          language={language}
          accounts={(accountHome?.accounts || []).map((account) => ({
            company_id: account.company_id,
            company_name: account.company_name,
          }))}
          apiBase={gatewayBaseUrl}
          accessToken={accessToken || ''}
        />
      );
    }

    if (activeNavItem === 'businessFormation') {
      if (!canAccessBusinessFormation) {
        return renderHomePane();
      }
      return (
        <ComplianceConsolePanel
          definition={BUSINESS_CREATION_COMPLIANCE_CONSOLE}
          language={language}
          reviewerLabel={accountEmail || accountDisplayName}
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          platformAdmin={hasPlatformAdminAccess}
          effectiveFeatures={effectiveFeatureList}
          workspaceReadOnly={!canWriteWorkspace}
        />
      );
    }

    if (activeNavItem === 'willsTrusts') {
      if (!canAccessWillsTrusts) {
        return renderHomePane();
      }
      return (
        <ComplianceConsolePanel
          definition={WILLS_TRUSTS_COMPLIANCE_CONSOLE}
          language={language}
          reviewerLabel={accountEmail || accountDisplayName}
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          platformAdmin={hasPlatformAdminAccess}
          effectiveFeatures={effectiveFeatureList}
          workspaceReadOnly={!canWriteWorkspace}
        />
      );
    }

    if (activeNavItem === 'audits') {
      if (!canAccessAudits) {
        return renderHomePane();
      }
      return (
        <EvidenceExportPanel
          language={language}
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
        />
      );
    }

    if (activeNavItem === 'agent') {
      if (!canAccessContinuousMonitoring) {
        return renderHomePane();
      }
      return (
        <section
          className="dashboard-settings-sections dashboard-settings-sections-wide"
          aria-label={language === 'es' ? 'Agente Monitor de NextCompli' : 'NextCompli Monitor Agent'}
        >
          <AgentInstallPanel
            language={language === 'es' ? 'es' : 'en'}
            gatewayBaseUrl={gatewayBaseUrl}
            accessToken={accessToken}
          />
        </section>
      );
    }

    if (activeNavItem === 'postlude') {
      if (!hasPlatformAdminAccess) {
        return renderHomePane();
      }
      return (
        <section
          className="dashboard-settings-sections dashboard-settings-sections-wide"
          aria-label="Postlude"
        >
          <PostludePanel
            language={language}
            gatewayBaseUrl={gatewayBaseUrl}
            accessToken={accessToken}
          />
        </section>
      );
    }

    if (activeNavItem === 'complidesk') {
      if (!canAccessCompliDesk) {
        return renderHomePane();
      }
      return (
        <CompliDeskWorkspace
          language={language}
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          onAccessDenied={denyStaffNav}
          onSignIn={() => {
            logout();
            navigate('/login');
          }}
        />
      );
    }

    if (activeNavItem === 'research') {
      if (!canAccessResearch) {
        return renderHomePane();
      }

      const researchTabLabels = {
        research: language === 'es' ? 'Investigación' : 'Research',
        checks: language === 'es' ? 'Verificaciones' : 'Checks',
        queue: language === 'es' ? 'Cola' : 'Queue',
        completed: language === 'es' ? 'Completados' : 'Completed',
        subscriptions: language === 'es' ? 'Suscripciones' : 'Subscriptions',
      };

      const researchTabs = (['research', 'checks', 'queue', 'completed', 'subscriptions'] as const);
      const queueDisplayItems = researchQueue.filter((item) => ['queued', 'screening'].includes(item.status));
      const failedQueueItems = researchQueue.filter((item) => ['failed', 'cancelled'].includes(item.status));
      const activeQueueReportIds = new Set(
        researchQueue
          .filter((item) => item.status === 'queued' || item.status === 'screening')
          .map((item) => item.reportId)
          .filter(Boolean),
      );
      const completedReports = dedupeResearchReports(
        visibleResearchReports.filter((report) => !activeQueueReportIds.has(report.id)),
      );
      const queueTabLabel = `${researchTabLabels.queue} (${queueDisplayItems.length})`;
      const completedTabLabel = `${researchTabLabels.completed} (${completedReports.length})`;

      return (
        <section
          className="dashboard-settings-sections dashboard-settings-sections-centered dashboard-settings-sections-research"
          aria-label={copy.researchPanelLabel}
          onClickCapture={(event) => {
            const target = event.target as HTMLElement | null;
            if (target?.closest('form button[type="submit"], .dashboard-action-buttons button')) {
              return;
            }
            requestResearchQueueSync();
          }}
        >
          <article className="card settings-card">
            <div className="settings-card-header dashboard-research-page-header">
              <div className="dashboard-research-page-header-copy">
                <p className="dashboard-research-kicker">{copy.researchKicker}</p>
                <h2>{copy.navResearch}</h2>
              </div>
              <div className="dashboard-research-health dashboard-research-health--inline" aria-label={language === 'es' ? 'Salud del tablero' : 'Dashboard health'}>
                <div>
                  <span>{language === 'es' ? 'OFAC cribado' : 'OFAC screened'}</span>
                  <strong>
                    {ofacScreeningSubjects.length > 0
                      ? `${ofacScreeningSubjects.filter((row) => row.last_checked_at).length}/${ofacScreeningSubjects.length}`
                      : (language === 'es' ? 'Sin cartera' : 'No portfolio')}
                  </strong>
                </div>
                <div>
                  <span>{language === 'es' ? 'Cola de investigación' : 'Research queue'}</span>
                  <strong>{queueDisplayItems.length}</strong>
                </div>
                <div>
                  <span>{copy.researchHealthCompleted}</span>
                  <strong>{completedReports.length}</strong>
                </div>
              </div>
              <button
                type="button"
                className="dashboard-icon-btn dashboard-settings-cog-btn"
                aria-label={language === 'es' ? 'Configuración de búsqueda' : 'Search settings'}
                aria-expanded={researchSearchSettingsOpen}
                aria-controls="research-search-settings"
                onClick={() => setResearchSearchSettingsOpen((open) => !open)}
              >
                <SettingsCogIcon />
              </button>
            </div>
            {researchSearchSettingsOpen ? (
              <div
                id="research-search-settings"
                className="research-search-settings-panel"
                role="region"
                aria-label={language === 'es' ? 'Configuración de búsqueda' : 'Search settings'}
              >
                <div className="research-search-settings-panel__header">
                  <h3>{language === 'es' ? 'Configuración de búsqueda' : 'Search settings'}</h3>
                  <button
                    type="button"
                    className="dashboard-security-inline-btn"
                    onClick={() => setResearchSearchSettingsOpen(false)}
                  >
                    {language === 'es' ? 'Listo' : 'Done'}
                  </button>
                </div>
                <div className="research-intake-affinity">
                  <label htmlFor="research-settings-affinity-depth">
                    {getResearchAffinityLevelLabel(language)}
                  </label>
                  <HelpBadge
                    label={language === 'es' ? 'Ayuda sobre el nivel de afinidad' : 'Help about affinity level'}
                  >
                    {getResearchAffinityLevelHelp(language)}
                  </HelpBadge>
                  <select
                    id="research-settings-affinity-depth"
                    className="research-intake-affinity-select"
                    value={researchForm.affinityDepth}
                    onChange={(event) => setResearchForm((current) => ({
                      ...current,
                      affinityDepth: Math.max(1, Math.min(4, Number(event.target.value) || 1)) as 1 | 2 | 3 | 4,
                    }))}
                    aria-label={getResearchAffinityLevelLabel(language)}
                  >
                    {RESEARCH_AFFINITY_DEPTH_OPTIONS.map((depth) => (
                      <option key={`research-form-affinity-${depth}`} value={depth}>
                        {depth}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="research-form-panel-label">
                  <span>
                    {language === 'es' ? 'Perfil de inteligencia de fuentes' : 'Source intelligence profile'}
                  </span>
                  <select
                    value={researchForm.sourceIntelligenceProfile}
                    onChange={(event) => setResearchForm((current) => ({
                      ...current,
                      sourceIntelligenceProfile: event.target.value as 'smart' | 'regulatory' | 'comprehensive' | 'default',
                    }))}
                    aria-describedby="research-source-intelligence-help"
                  >
                    <option value="smart">{language === 'es' ? 'Inteligente (integral; omitir solo con evidencia)' : 'Smart (comprehensive; skip only with evidence)'}</option>
                    <option value="comprehensive">{language === 'es' ? 'Integral (sin omitir)' : 'Comprehensive (run every catalog)'}</option>
                    <option value="regulatory">{language === 'es' ? 'Regulatorio (FINRA/CFTC/CourtListener)' : 'Regulatory (FINRA/CFTC/CourtListener)'}</option>
                    <option value="default">{language === 'es' ? 'Noticias/SEC solamente' : 'News/SEC only'}</option>
                  </select>
                  <span id="research-source-intelligence-help" className="research-form-panel-meta">
                    {getResearchSourceIntelligenceHelp(language)}
                  </span>
                </label>
                <label className="research-form-panel-label">
                  <span>{copy.researchPdfRecipients}</span>
                  <input
                    type="text"
                    placeholder={language === 'es' ? 'Separados por coma' : 'Comma separated'}
                    value={researchForm.emailAddresses}
                    onChange={(event) => setResearchForm((current) => ({ ...current, emailAddresses: event.target.value }))}
                  />
                </label>
                <label className="research-form-panel-label">
                  <span>{copy.researchEmailGroups}</span>
                  <input
                    type="text"
                    placeholder={language === 'es' ? 'Separados por coma' : 'Comma separated'}
                    value={researchForm.emailGroups}
                    onChange={(event) => setResearchForm((current) => ({ ...current, emailGroups: event.target.value }))}
                  />
                </label>
              </div>
            ) : null}
            <p className="dashboard-panel-subtitle dashboard-research-page-lead">
              {copy.researchPageLead}
            </p>

            {researchSessionExpired ? (
              <p className="dashboard-form-error" role="alert">
                {language === 'es'
                  ? 'Tu sesión expiró. Vantage sigue ejecutando los trabajos; vuelve a iniciar sesión para restaurar la cola. No vuelvas a enviar los mismos nombres.'
                  : 'Your session expired. Vantage is still running those jobs — sign in again to restore the queue. Do not resubmit the same names.'}
              </p>
            ) : null}

            <div className="dashboard-research-tabs" role="tablist" aria-label={language === 'es' ? 'Pestañas de investigación' : 'Research tabs'}>
              {researchTabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={activeResearchTab === tab}
                  className={`dashboard-research-tab-btn${activeResearchTab === tab ? ' active' : ''}`}
                  onClick={() => {
                    setActiveResearchTab(tab);
                    if (tab === 'queue' || tab === 'completed') {
                      void syncResearchQueueFromGateway();
                    }
                  }}
                >
                  {tab === 'queue' ? queueTabLabel : tab === 'completed' ? completedTabLabel : researchTabLabels[tab]}
                </button>
              ))}
            </div>
            {activeResearchTab === 'checks' && (
            <div className="dashboard-payments-panel" role="region" aria-label={language === 'es' ? 'Lista unificada de verificaciones' : 'Unified check list'}>
              <h3>{language === 'es' ? 'Verificaciones Vantage unificadas' : 'Vantage Unified Checks'}</h3>
              <p className="dashboard-panel-subtitle" style={{ marginBottom: '20px' }}>
                {language === 'es'
                  ? 'La misma secuencia de verificaciones se ejecuta para empresa y persona. La base de hechos se va acumulando en cada paso.'
                  : 'The same check sequence runs for company and person targets. The fact base accumulates as each check runs.'}
              </p>

              {([
                { order: 1, name: language === 'es' ? 'Input Parse (extracción de entrada)' : 'Input Parse (extract input)', desc: language === 'es' ? 'Extrae los campos del formulario, normaliza nombres, analiza el sitio web y adjunta documentos cargados para construir la base inicial de hechos.' : 'Extracts the form fields, normalizes names, parses the website, and attaches uploaded documents to build the initial fact base.' },
                { order: 2, name: language === 'es' ? 'Presencia en internet' : 'Internet presence', desc: language === 'es' ? 'Busca en directorios de empresas, CorporationWiki y otras fuentes de inteligencia web.' : 'Searches company directories, CorporationWiki, and other web intelligence sources.' },
                { order: 3, name: language === 'es' ? 'Presencia en redes sociales' : 'Social media footprint', desc: language === 'es' ? 'Busca perfiles y menciones recientes en LinkedIn, X, Facebook, Instagram, YouTube y otros.' : 'Searches for profiles and recent mentions on LinkedIn, X, Facebook, Instagram, YouTube, and others.' },
                { order: 4, name: language === 'es' ? 'Registros LLC – todos los estados y territorios' : 'LLC registry – all US states & territories', desc: language === 'es' ? 'Busca registros de entidades comerciales en los 50 estados y territorios para confirmar incorporación, estado y agente registrado.' : 'Searches business entity records across all 50 states and territories to confirm incorporation, standing, and registered agent.' },
                { order: 5, name: language === 'es' ? 'Registros UCC' : 'UCC filings', desc: language === 'es' ? 'Revisa presentaciones UCC-1 para identificar gravámenes activos y posibles tensiones financieras.' : 'Reviews UCC-1 filings to surface active liens and potential financial stress.' },
                { order: 6, name: language === 'es' ? 'Verificación de empresa pública' : 'Publicly traded check', desc: language === 'es' ? 'Busca en EDGAR de la SEC para confirmar si la empresa cotiza en bolsa y su historial de presentaciones.' : 'Searches SEC EDGAR to confirm whether the company is publicly traded and to review filing history.' },
                { order: 7, name: language === 'es' ? 'Clasificación de corporación privada' : 'Private corporation classification', desc: language === 'es' ? 'Determina si la entidad es una corporación privada no listada, incluyendo su tipo (S-corp, C-corp, LP, etc.).' : 'Determines whether the entity is a private non-listed corporation, including entity type (S-corp, C-corp, LP, etc.).' },
                { order: 8, name: language === 'es' ? 'DBA / nombres supuestos' : 'DBA / assumed names', desc: language === 'es' ? 'Explora registros Doing Business As para identificar nombres comerciales alternativos.' : 'Scans DBA filings to uncover alternative trade names.' },
                { order: 9, name: language === 'es' ? 'Registros judiciales federales/estatales' : 'Federal/State court records', desc: language === 'es' ? 'Busca en PACER y CourtListener para casos federales y estatales, quiebras y litigios.' : 'Searches PACER and CourtListener for federal and state cases, bankruptcies, and litigation.' },
                { order: 10, name: language === 'es' ? 'Registros del condado' : 'County records', desc: language === 'es' ? 'Revisa primero bases de datos civiles y penales del condado y luego gravámenes, permisos y presentaciones locales.' : 'Checks county civil and criminal databases first, then liens, permits, and local filings.' },
                { order: 18, name: language === 'es' ? 'Reclusos y fichas policiales' : 'Inmate and mugshot lookup', desc: language === 'es' ? 'Busca el localizador federal de prisiones, el aviso de víctimas por estado, la prisión estatal, la lista de la cárcel del condado y páginas de fichas en Facebook. Un hit por nombre es una pista hasta que coincidan la fecha de nacimiento o el condado.' : 'Searches the federal prison inmate locator, the state victim-notification inmate search, the state prison locator, the county jail roster, and Facebook mugshot pages. A name hit is a lead until date of birth or county agrees.' },
                { order: 11, name: language === 'es' ? 'Contratos federales' : 'Federal contracting', desc: language === 'es' ? 'Verifica SAM.gov y USASpending para contratos federales y suspensiones.' : 'Checks SAM.gov and USAspending for federal contracts and debarment flags.' },
                { order: 12, name: language === 'es' ? 'OFAC / sanciones globales' : 'OFAC / global sanctions', desc: language === 'es' ? 'Consulta OFAC SDN y bases de sanciones globales para detectar restricciones.' : 'Queries OFAC SDN and global sanctions databases to detect restrictions.' },
                { order: 13, name: language === 'es' ? 'SEC EDGAR – menciones' : 'SEC EDGAR counterparty mentions', desc: language === 'es' ? 'Busca menciones en presentaciones de otras compañías públicas para revelar relaciones comerciales.' : 'Searches mentions in other public companies’ filings to surface business relationships.' },
                { order: 14, name: language === 'es' ? 'Señales laborales / OSHA' : 'Labor / OSHA signals', desc: language === 'es' ? 'Revisa inspecciones de OSHA, violaciones y multas para identificar patrones de cumplimiento.' : 'Reviews OSHA inspections, violations, and penalties for compliance patterns.' },
                { order: 15, name: language === 'es' ? 'Prensa y noticias' : 'Press & news', desc: language === 'es' ? 'Rastrea comunicados de prensa y cobertura de medios para eventos recientes o controversias.' : 'Tracks press releases and media coverage for recent events or controversies.' },
                { order: 16, name: language === 'es' ? 'Búsqueda de personas (People-finder)' : 'People-finder aggregators', desc: language === 'es' ? 'Consulta agregadores públicos para direcciones históricas, teléfonos, edad estimada y asociados conocidos.' : 'Queries public aggregators for historical addresses, phone numbers, age estimates, and known associates.' },
                { order: 17, name: language === 'es' ? 'Oil & Gas regulatorio (si aplica)' : 'Oil & Gas regulatory (if applicable)', desc: language === 'es' ? 'Cuando aplica, agrega fuentes especializadas como RRC de Texas, BOEM, BSEE, EPA y bases de pozos estatales.' : 'When applicable, adds specialist sources such as Texas RRC, BOEM, BSEE, EPA, and state well databases.' },
              ] as { order: number; name: string; desc: string }[]).map((chk) => (
                <details key={chk.order} style={{ borderBottom: '1px solid var(--color-border)', padding: '10px 0' }}>
                  <summary style={{ cursor: 'pointer', display: 'flex', alignItems: 'baseline', gap: '10px', listStyle: 'none', userSelect: 'none' }}>
                    <span style={{ minWidth: '24px', fontWeight: 700, color: 'var(--color-primary-strong)', fontSize: '0.82rem' }}>{chk.order}.</span>
                    <span style={{ fontWeight: 600, color: 'var(--color-text-strong)', fontSize: '0.92rem' }}>{chk.name}</span>
                  </summary>
                  <p style={{ margin: '8px 0 4px 34px', color: 'var(--color-text-muted)', fontSize: '0.87rem', lineHeight: 1.6 }}>{chk.desc}</p>
                </details>
              ))}
            </div>
            )}

            {activeResearchTab === 'subscriptions' && (
            <div className="dashboard-payments-panel" role="region" aria-label={language === 'es' ? 'Historial de verificaciones completas' : 'Comprehensive check history'}>
              <h3>{language === 'es' ? 'Verificaciones completas US + VE' : 'Comprehensive checks (US + VE)'}</h3>
              <p className="dashboard-panel-subtitle">
                {language === 'es'
                  ? 'Estos resultados se guardan en Gateway y alimentan monitoreo continuo para entidades previamente evaluadas.'
                  : 'These results are persisted in Gateway and can seed continuous monitoring for previously screened entities.'}
              </p>
              <div className="dashboard-action-buttons">
                <button type="button" className="dashboard-security-inline-btn" onClick={loadComprehensiveCheckHistory} disabled={fullCheckHistoryLoading}>
                  {fullCheckHistoryLoading
                    ? (language === 'es' ? 'Cargando...' : 'Loading...')
                    : (language === 'es' ? 'Actualizar historial' : 'Refresh history')}
                </button>
                {canAccessContinuousMonitoring ? (
                  <button type="button" className="dashboard-security-inline-btn" onClick={runComprehensiveCheckMonitoring}>
                    {language === 'es' ? 'Ejecutar monitoreo continuo' : 'Run continuous monitoring'}
                  </button>
                ) : (
                  <a className="dashboard-security-inline-btn" href="/products/continuous-monitoring">
                    {language === 'es' ? 'Monitoreo continuo (CompliAdvantage)' : 'Continuous monitoring (CompliAdvantage)'}
                  </a>
                )}
              </div>
              {fullCheckHistoryError ? <p className="dashboard-form-error" role="alert">{fullCheckHistoryError}</p> : null}
              {fullCheckMonitoringMessage ? <p className="dashboard-form-success">{fullCheckMonitoringMessage}</p> : null}
              {fullCheckHistory.length > 0 ? (
                <div className="dashboard-subscription-plan-list">
                  {fullCheckHistory.map((record) => (
                    <div key={record.request_id} className="dashboard-subscription-plan-item">
                      <div>
                        <strong>{record.subject}</strong>
                        <p>
                          {(record.target_type || 'unknown').toUpperCase()}
                          {' · '}
                          {(record.countries || []).join(', ') || 'US,VE'}
                          {' · '}
                          {record.summary?.overall_status || 'UNKNOWN'}
                          {' · '}
                          {new Date(record.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="dashboard-panel-subtitle">
                  {language === 'es' ? 'Aún no hay verificaciones completas guardadas.' : 'No comprehensive checks saved yet.'}
                </p>
              )}
            </div>
            )}

            {activeResearchTab === 'queue' && (
            <div className="dashboard-payments-panel" role="region" aria-label={language === 'es' ? 'Cola de reportes de investigación' : 'Research report queue'}>
              <h3>{language === 'es' ? 'Cola de reportes Vantage' : 'Vantage report queue'}</h3>
              <p className="dashboard-panel-subtitle">
                {language === 'es'
                  ? 'Cada solicitud entra en cola, muestra progreso por etapa y deja el reporte listo para vista previa/PDF.'
                  : 'Each request is queued, shows stage-by-stage progress, and leaves the report ready for preview/PDF.'}
              </p>
              {queueDisplayItems.length > 0 || failedQueueItems.length > 0 ? (
                <div className="dashboard-subscription-plan-list">
                  {queueDisplayItems.map((item) => {
                    const linkedReport = item.reportId
                      ? (researchReports.find((record) => record.id === item.reportId) ?? null)
                      : null;
                    const linkedTimingLabel = linkedReport
                      ? formatResearchSearchDurationLabel(linkedReport, language)
                      : '';
                    return (
                      <div key={item.id} className="dashboard-subscription-plan-item dashboard-queue-item">
                        <div>
                          <strong>{resolveResearchReportDisplayTitle(item)}</strong>
                          <p>
                            {item.targetType === 'company' ? (language === 'es' ? 'Compañía' : 'Company') : (language === 'es' ? 'Persona' : 'Person')}
                            {hasCustomResearchReportTitle(item) && item.targetName ? ` · ${item.targetName}` : ''}
                          </p>
                          <p className="dashboard-queue-meta-text">
                            {linkedTimingLabel
                              || `${language === 'es' ? 'Inicio:' : 'Started:'} ${new Date(item.createdAt || item.updatedAt).toLocaleString()}`}
                            {!linkedTimingLabel && ['queued', 'screening'].includes(item.status) && item.updatedAt && item.createdAt !== item.updatedAt
                              ? ` · ${language === 'es' ? 'Actualizado:' : 'Updated:'} ${new Date(item.updatedAt).toLocaleString()}`
                              : ''}
                            {resolveResearchQueueTimerStart(item) ? (
                              <>
                                {' · '}
                                <ResearchElapsedTimer
                                  startedAt={resolveResearchQueueTimerStart(item)}
                                  endedAt={['queued', 'screening'].includes(item.status) ? null : item.updatedAt}
                                  language={language}
                                />
                              </>
                            ) : null}
                          </p>
                          <p className="dashboard-queue-status-text">
                            {['queued', 'screening'].includes(item.status) ? (
                              <span className="dashboard-queue-inline-spinner" aria-hidden="true" />
                            ) : item.status === 'completed' ? (
                              <span className="dashboard-queue-check" aria-hidden="true">✓</span>
                            ) : item.status === 'failed' ? (
                              <span className="dashboard-queue-fail" aria-hidden="true">✕</span>
                            ) : item.status === 'cancelled' ? (
                              <span className="dashboard-queue-fail" aria-hidden="true">⊘</span>
                            ) : null}
                            {item.message}
                          </p>
                          <div
                            className="dashboard-queue-progress"
                            role="progressbar"
                            aria-label={language === 'es' ? 'Progreso de cola' : 'Queue progress'}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={Math.max(0, Math.min(100, Number(item.progress) || 0))}
                          >
                            <span className="dashboard-queue-progress-fill" style={{ width: `${Math.max(0, Math.min(100, Number(item.progress) || 0))}%` }} />
                          </div>
                          {['queued', 'screening'].includes(item.status) && typeof item.etaSeconds === 'number' && item.etaSeconds > 0 ? (
                            <p className="dashboard-queue-eta-text">{formatEtaText(item.etaSeconds, language)}</p>
                          ) : null}
                          {hasPlatformAdminAccess ? (
                          <ResearchLogPanel
                            language={language}
                            defaultOpen={['queued', 'screening'].includes(item.status)}
                            entries={buildResearchLogEntries({
                              researchLog: item.researchLog || linkedReport?.researchLog,
                              checkTimings: linkedReport ? resolveReportCheckTimings(linkedReport) : undefined,
                              sourceChecksRuns: linkedReport?.sourceChecksRuns,
                              checkResults: linkedReport?.checkResults,
                              criticalFacts: linkedReport?.criticalFacts,
                              factDatabase: (linkedReport?.factDatabase || item.factDatabase) as Record<string, unknown> | undefined,
                              status: item.status,
                              phase: item.message,
                              currentCheck: item.message,
                            })}
                          />
                          ) : null}
                        </div>
                        <div className="dashboard-action-buttons">
                          <button
                            type="button"
                            className="dashboard-security-inline-btn"
                            disabled={!linkedReport}
                            onClick={() => {
                              if (!linkedReport) return;
                              handleResearchReportView(linkedReport.id, { expanded: true });
                            }}
                          >
                            {language === 'es' ? 'Ver' : 'View'}
                          </button>
                          <button
                            type="button"
                            className="dashboard-security-inline-btn"
                            disabled={!linkedReport || isResearchPdfGenerating}
                            onClick={() => {
                              if (!linkedReport) return;
                              void openResearchReportPdf(linkedReport);
                            }}
                          >
                            {language === 'es' ? 'PDF' : 'PDF'}
                          </button>
                          <button
                            type="button"
                            className="dashboard-security-inline-btn"
                            disabled={!linkedReport}
                            onClick={() => {
                              if (!linkedReport) return;
                              handleResearchReportEdit(linkedReport.id);
                            }}
                          >
                            {language === 'es' ? 'Editar' : 'Edit'}
                          </button>
                          <button
                            type="button"
                            className="dashboard-security-inline-btn"
                            disabled={!['queued', 'screening'].includes(item.status)}
                            onClick={() => {
                              void cancelResearchQueueJob(item);
                            }}
                          >
                            {language === 'es' ? 'Cancelar' : 'Cancel'}
                          </button>
                          <button
                            type="button"
                            className="dashboard-security-inline-btn"
                            disabled={false}
                            onClick={() => {
                              handleResearchQueueDelete(item.id, linkedReport?.id);
                            }}
                          >
                            {language === 'es' ? 'Eliminar' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {failedQueueItems.length > 0 ? (
                    <div className="dashboard-queue-failed-section">
                      <div className="dashboard-queue-failed-header">
                        <h4>
                          {failedQueueItems.every((item) => item.status === 'cancelled')
                            ? (language === 'es' ? 'Solicitudes canceladas recientes' : 'Recent cancelled requests')
                            : failedQueueItems.some((item) => item.status === 'cancelled')
                              ? (language === 'es' ? 'Solicitudes fallidas o canceladas recientes' : 'Recent failed or cancelled requests')
                              : (language === 'es' ? 'Solicitudes fallidas recientes' : 'Recent failed requests')}
                        </h4>
                        <button
                          type="button"
                          className="dashboard-security-inline-btn"
                          onClick={clearFailedResearchQueueItems}
                        >
                          {failedQueueItems.every((item) => item.status === 'cancelled')
                            ? (language === 'es' ? 'Limpiar canceladas' : 'Clear cancelled')
                            : (language === 'es' ? 'Limpiar fallos' : 'Clear failed')}
                        </button>
                      </div>
                      {failedQueueItems.map((item) => {
                        const linkedReport = item.reportId
                          ? (researchReports.find((record) => record.id === item.reportId) ?? null)
                          : (researchReports.find((record) => (
                            record.targetType === item.targetType
                            && record.targetName.trim().toLowerCase() === item.targetName.trim().toLowerCase()
                          )) ?? null);
                        return (
                          <div key={item.id} className="dashboard-subscription-plan-item dashboard-queue-item dashboard-queue-item-failed">
                            <div>
                              <strong>{resolveResearchReportDisplayTitle(item)}</strong>
                              <p>
                                {item.targetType === 'company' ? (language === 'es' ? 'Compañía' : 'Company') : (language === 'es' ? 'Persona' : 'Person')}
                                {hasCustomResearchReportTitle(item) && item.targetName ? ` · ${item.targetName}` : ''}
                                {' · '}
                                {new Date(item.updatedAt).toLocaleString()}
                              </p>
                              <p className="dashboard-queue-status-text">
                                <span className="dashboard-queue-fail" aria-hidden="true">{item.status === 'cancelled' ? '⊘' : '✕'}</span>
                                {item.message}
                              </p>
                            </div>
                            <div className="dashboard-action-buttons">
                              {item.status !== 'cancelled' ? (
                                <button
                                  type="button"
                                  className="dashboard-security-inline-btn"
                                  onClick={() => {
                                    if (linkedReport && !linkedReport.isFallback) {
                                      void handleResearchReportRerun(linkedReport.id);
                                      return;
                                    }
                                    void handleResearchQueueRerun(item);
                                  }}
                                >
                                  {language === 'es' ? 'Relanzar' : 'Rerun'}
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="dashboard-security-inline-btn"
                                onClick={() => {
                                  handleResearchQueueDelete(item.id, linkedReport?.id);
                                }}
                              >
                                {language === 'es' ? 'Eliminar' : 'Delete'}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="dashboard-panel-subtitle">
                  {language === 'es' ? 'Aún no hay reportes en cola.' : 'No queued reports yet.'}
                </p>
              )}
            </div>
            )}

            {activeResearchTab === 'completed' && (
            <div className="dashboard-payments-panel" role="region" aria-label={language === 'es' ? 'Reportes completados' : 'Completed reports'}>
              <h3>{language === 'es' ? 'Reportes completados' : 'Completed reports'}</h3>
              <p className="dashboard-panel-subtitle">
                {language === 'es'
                  ? 'Reportes finalizados listos para vista previa, edición o exportación a PDF.'
                  : 'Finished reports ready for preview, editing, or PDF export.'}
              </p>
              {queueDisplayItems.length > 0 ? (
                <p className="dashboard-form-success" role="status">
                  {language === 'es'
                    ? `${queueDisplayItems.length} reporte(s) aún en cola/ejecución. Ábrelos en la pestaña Cola — aparecerán aquí al terminar.`
                    : `${queueDisplayItems.length} report(s) still queued/running. Open the Queue tab — they appear here when finished.`}
                  {' '}
                  <button
                    type="button"
                    className="dashboard-security-inline-btn"
                    onClick={() => {
                      setActiveResearchTab('queue');
                      void syncResearchQueueFromGateway();
                    }}
                  >
                    {language === 'es' ? 'Ir a Cola' : 'Go to Queue'}
                  </button>
                </p>
              ) : null}
              {researchHiddenCompletedCount > 0 ? (
                <p className="dashboard-form-error" role="alert">
                  {language === 'es'
                    ? `${researchHiddenCompletedCount} reporte(s) completado(s) siguen en Vantage después de un borrado solo en este navegador. No se quitaron del motor de screening, por eso reaparecen.`
                    : `${researchHiddenCompletedCount} completed report(s) still exist in Vantage after a browser-only delete. They were not removed from the screening engine, so they keep coming back.`}
                  {' '}
                  <button
                    type="button"
                    className="dashboard-security-inline-btn"
                    onClick={() => {
                      setDismissedResearchReportIds(() => {
                        const cleared = new Set<string>();
                        dismissedResearchReportIdsRef.current = cleared;
                        saveDismissedResearchReportIds([]);
                        return cleared;
                      });
                      setResearchHiddenCompletedReportIds([]);
                      setResearchHiddenCompletedCount(0);
                      requestResearchQueueSync({ force: true });
                    }}
                  >
                    {language === 'es' ? 'Mostrar reportes ocultos' : 'Show hidden reports'}
                  </button>
                  {' '}
                  <button
                    type="button"
                    className="dashboard-security-inline-btn dashboard-report-action-btn--danger"
                    disabled={isPurgingHiddenResearchReports || isPurgingAllResearchReports}
                    onClick={() => {
                      void handlePurgeHiddenResearchReportsFromVantage();
                    }}
                  >
                    {isPurgingHiddenResearchReports
                      ? (language === 'es' ? 'Eliminando…' : 'Deleting…')
                      : (language === 'es' ? 'Eliminar todos los ocultos en Vantage' : 'Delete all hidden in Vantage')}
                  </button>
                </p>
              ) : null}
              {ofacScreeningMessage ? <p className="dashboard-form-success">{ofacScreeningMessage}</p> : null}
              {ofacScreeningError && activeResearchTab === 'completed' ? <p className="dashboard-form-error" role="alert">{ofacScreeningError}</p> : null}
              {completedReports.length > 0 ? (
                <div className="dashboard-subscription-plan-list dashboard-research-report-list">
                  {completedReports.slice(0, 20).map((report) => {
                    const timingLabel = formatResearchSearchDurationLabel(report, language);
                    const hasElapsed = resolveResearchSearchDurationMs(report) != null;
                    return (
                    <div key={report.id} className="dashboard-subscription-plan-item dashboard-research-report-item">
                      <div className="dashboard-research-report-copy">
                        <strong>{resolveResearchReportDisplayTitle(report)}</strong>
                        <p>
                          {report.targetType === 'company' ? (language === 'es' ? 'Compañía' : 'Company') : (language === 'es' ? 'Persona' : 'Person')}
                          {hasCustomResearchReportTitle(report) && report.targetName ? ` · ${report.targetName}` : ''}
                          {timingLabel
                            ? ` · ${timingLabel}`
                            : ` · ${new Date(report.updatedAt).toLocaleString()}`}
                          {!hasElapsed && report.createdAt ? (
                            <>
                              {' · '}
                              <ResearchElapsedTimer
                                startedAt={report.createdAt}
                                endedAt={report.updatedAt}
                                language={language}
                              />
                            </>
                          ) : null}
                        </p>
                      </div>
                      <div className="dashboard-action-buttons dashboard-research-report-actions">
                        <div
                          className="dashboard-report-rerun-group"
                          title={getResearchAffinityLevelHelp(language)}
                        >
                          <label
                            className="dashboard-report-rerun-control"
                            htmlFor={`${report.id}-affinity-level`}
                          >
                            <span className="dashboard-report-rerun-label-text">
                              {getResearchAffinityLevelLabel(language)}
                            </span>
                            <select
                              id={`${report.id}-affinity-level`}
                              className="dashboard-report-rerun-select"
                              value={String(getCompletedReportRerunDepth(report.id))}
                              onChange={(event) => {
                                const parsed = Number(event.target.value);
                                setCompletedReportRerunDepthById((current) => ({
                                  ...current,
                                  [report.id]: Number.isFinite(parsed) ? Math.max(1, Math.min(4, parsed)) : 1,
                                }));
                              }}
                              disabled={isResearchLoading}
                              aria-describedby={`${report.id}-affinity-level-help`}
                            >
                              {RESEARCH_AFFINITY_DEPTH_OPTIONS.map((depth) => (
                                <option key={`${report.id}-affinity-${depth}`} value={depth}>
                                  {depth}
                                </option>
                              ))}
                            </select>
                          </label>
                          <button
                            type="button"
                            className="dashboard-security-inline-btn dashboard-report-action-btn dashboard-report-action-btn--rerun"
                            disabled={isResearchLoading}
                            onClick={() => {
                              void handleResearchReportRerun(report.id, {
                                affinity_depth: getCompletedReportRerunDepth(report.id),
                              });
                            }}
                          >
                            {language === 'es' ? 'Relanzar' : 'Rerun'}
                          </button>
                          <span id={`${report.id}-affinity-level-help`} className="dashboard-report-rerun-help">
                            {getResearchAffinityLevelHelp(language)}
                          </span>
                        </div>
                        {report.isFallback ? (
                          <button
                            type="button"
                            className="dashboard-security-inline-btn dashboard-report-action-btn dashboard-report-action-btn--full"
                            disabled={isResearchLoading}
                            onClick={() => { void handleResearchReportRetry(report.id); }}
                          >
                            {language === 'es' ? 'Reintentar' : 'Retry'}
                          </button>
                        ) : null}
                        <button type="button" className="dashboard-security-inline-btn dashboard-report-action-btn" onClick={() => handleResearchReportView(report.id, { expanded: true })}>
                          {language === 'es' ? 'Ver' : 'View'}
                        </button>
                        {canAccessOfac ? (
                          <button
                            type="button"
                            className="dashboard-btn dashboard-report-action-btn"
                            onClick={() => handleResearchReportView(report.id, { expanded: true })}
                            title={
                              language === 'es'
                                ? 'Abrir el reporte, seleccionar hechos y transferirlos a la base de hechos NextCompli'
                                : 'Open the report, select facts, and transfer them into the NextCompli fact database'
                            }
                          >
                            {language === 'es' ? 'Añadir hechos a NextCompli' : 'Add facts to NextCompli'}
                          </button>
                        ) : null}
                        <button type="button" className="dashboard-security-inline-btn dashboard-report-action-btn" disabled={isResearchPdfGenerating} onClick={() => { void openResearchReportPdf(report); }}>
                          {language === 'es' ? 'PDF' : 'PDF'}
                        </button>
                        <button type="button" className="dashboard-security-inline-btn dashboard-report-action-btn" onClick={() => handleResearchReportEdit(report.id)}>
                          {language === 'es' ? 'Editar' : 'Edit'}
                        </button>
                        <button type="button" className="dashboard-security-inline-btn dashboard-report-action-btn dashboard-report-action-btn--danger" onClick={() => handleResearchReportDelete(report.id)}>
                          {language === 'es' ? 'Eliminar' : 'Delete'}
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <p className="dashboard-panel-subtitle">
                  {language === 'es' ? 'Aún no hay reportes completados.' : 'No completed reports yet.'}
                </p>
              )}
            </div>
            )}

            {activeResearchTab === 'research' && (
            <div className="dashboard-payments-panel" role="region" aria-label={language === 'es' ? 'Formulario de investigación' : 'Research form'}>
              <form
                key={`research-intake-${researchIntakeEpoch}`}
                className="dashboard-password-form"
                {...researchAutofillIgnoreProps}
                onSubmit={async (event) => {
                  event.preventDefault();
                  await enqueueResearchReport();
                }}
              >
                <div id="research-key-data-intake" className="research-intake-main">
                  <ResearchKeyDataIntake
                    language={language}
                    targetType={researchForm.targetType}
                    targetTypeTouched={researchForm.targetTypeTouched}
                    keyData={researchForm.keyData}
                    reportName={researchForm.reportName}
                    affinityDepth={researchForm.affinityDepth}
                    onAffinityDepthChange={(depth) => setResearchForm((current) => ({
                      ...current,
                      affinityDepth: depth,
                    }))}
                    canAccessOfac={canAccessOfac}
                    addToOfacScreening={Boolean(researchForm.addToOfacScreening)}
                    ofacRelationshipType={researchForm.ofacRelationshipType}
                    ofacRelationshipOptions={OFAC_SCREENING_RELATIONSHIP_OPTIONS.filter((option) => (
                      researchForm.targetType === 'person'
                        ? ofacEntityTypeForRelationship(option.value) === 'person'
                        : ofacEntityTypeForRelationship(option.value) === 'company'
                    ))}
                    fieldProps={researchIntakeFieldProps}
                    intakeEpoch={researchIntakeEpoch}
                    uploadFile={researchUploadFile}
                    onChooseType={(kind) => setResearchForm((current) => ({
                      ...current,
                      ...chooseResearchIntakeKind(current, kind),
                    }))}
                    onKeyDataChange={(next) => setResearchForm((current) => ({
                      ...current,
                      keyData: next,
                    }))}
                    onReportNameChange={(value) => setResearchForm((current) => ({
                      ...current,
                      reportName: value,
                      reportNameLocked: true,
                    }))}
                    onOfacChange={(patch) => setResearchForm((current) => ({
                      ...current,
                      ...patch,
                      ofacRelationshipType: (patch.ofacRelationshipType || current.ofacRelationshipType) as OfacScreeningRelationshipType,
                    }))}
                    onUploadFile={(file) => {
                      researchDocumentParseCacheRef.current = null;
                      setResearchUploadFile(file);
                    }}
                  />
                </div>
              {researchError ? <p className="dashboard-form-error" role="alert">{researchError}</p> : null}
              {researchFeedback ? <p className="dashboard-form-success">{researchFeedback}</p> : null}
              {researchParseWarning ? (
                <p
                  role="status"
                  className="research-form-warning"
                >
                  {researchParseWarning}
                </p>
              ) : null}
              <div className="dashboard-action-buttons">
                <button type="submit" className="dashboard-btn dashboard-btn-primary" disabled={isResearchLoading || isResearchPdfGenerating}>
                  {isResearchLoading
                    ? (language === 'es' ? 'Generando...' : 'Generating...')
                    : editingResearchReportId
                      ? (language === 'es' ? 'Actualizar reporte' : 'Update report')
                      : (language === 'es' ? 'Generar reporte completo' : 'Generate full report')}
                </button>
                <button
                  type="button"
                  className="dashboard-btn"
                  disabled={isResearchLoading || isResearchPdfGenerating}
                  onClick={async () => {
                    setIsResearchPdfGenerating(true);
                    await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
                    try {
                      await enqueueResearchReport({ openPdf: true });
                    } finally {
                      setIsResearchPdfGenerating(false);
                    }
                  }}
                >
                  {language === 'es' ? 'Generar y abrir PDF' : 'Generate and open PDF'}
                </button>
              </div>
            </form>
            </div>
            )}
          </article>
        </section>
      );
    }

    if (activeNavItem === 'style') {
      if (!hasSuperUserAccess) {
        return renderHomePane();
      }

      if (location.pathname.startsWith('/console/style/designer')) {
        return <ThemeDesignerPage />;
      }

      return (
        <section className="dashboard-settings-sections dashboard-settings-sections-centered" aria-label={copy.stylePanelLabel}>
          <article className="card settings-card">
            <div className="settings-card-header">
              <h2>{copy.navStyle}</h2>
            </div>
            <p className="dashboard-panel-subtitle">
              {language === 'es'
                ? 'Estandariza tipografía, color y componentes con validaciones de accesibilidad y consistencia visual.'
                : 'Standardize typography, color, and components with accessibility and visual consistency guardrails.'}
            </p>
            <ul className="dashboard-payment-best-practices">
              <li>{language === 'es' ? 'WCAG 1.4.3: mantener contraste mínimo de 4.5:1 en texto normal.' : 'WCAG 1.4.3: keep text contrast at least 4.5:1 for normal text.'}</li>
              <li>{language === 'es' ? 'WCAG 2.4.8: mostrar ubicación del usuario con migas de pan y encabezados claros.' : 'WCAG 2.4.8: expose location context with breadcrumbs and clear headings.'}</li>
              <li>{language === 'es' ? 'Definir tokens de diseño (color, radio, tipografía) como única fuente de verdad.' : 'Define design tokens (color, radius, typography) as the single source of truth.'}</li>
              <li>{language === 'es' ? 'Mantener navegación y nomenclatura consistente en todas las vistas administrativas.' : 'Keep navigation order and naming consistent across all admin experiences.'}</li>
            </ul>
            <div className="dashboard-action-buttons">
              <button
                type="button"
                className="dashboard-btn dashboard-btn-primary"
                onClick={() => navigate('/console/style/designer')}
              >
                {language === 'es' ? 'Abrir diseñador de tokens' : 'Open token designer'}
              </button>
            </div>
          </article>
        </section>
      );
    }

    if (activeNavItem === 'emailInbox') {
      void emailInboxRefreshNonce;
      const buildInboxUrlForAddress = (addr: string): string => {
        const base = adminEmailInboxUrl.trim();
        if (!base) return '';
        const raw = String(addr || '').trim().toLowerCase();
        if (!raw) return base;
        const hasDomain = raw.includes('@');
        const normalizedEmail = hasDomain ? raw : `${raw}@nextcompli.com`;
        const [localPart = '', domainPart = ''] = normalizedEmail.split('@');
        const usernameCandidate = hasDomain ? localPart.trim() : raw;
        if (base.includes('{email}') || base.includes('{username}') || base.includes('{localpart}') || base.includes('{domain}')) {
          return base
            .replaceAll('{email}', encodeURIComponent(normalizedEmail))
            .replaceAll('{username}', encodeURIComponent(usernameCandidate || normalizedEmail))
            .replaceAll('{localpart}', encodeURIComponent(localPart))
            .replaceAll('{domain}', encodeURIComponent(domainPart));
        }
        if (base.includes('mail.google.com')) {
          const query = encodeURIComponent(`{${normalizedEmail}}`);
          const authUser = encodeURIComponent(String(user?.email || accountEmail || normalizedEmail).trim());
          return `https://mail.google.com/mail/u/0/?authuser=${authUser}#search/${query}`;
        }
        try {
          const url = new URL(base);
          url.searchParams.set('login_hint', usernameCandidate || normalizedEmail);
          url.searchParams.set('loginHint', usernameCandidate || normalizedEmail);
          url.searchParams.set('email', normalizedEmail);
          url.searchParams.set('user', usernameCandidate || normalizedEmail);
          url.searchParams.set('username', usernameCandidate || normalizedEmail);
          return url.toString();
        } catch {
          return base;
        }
      };

      const loadManagedDomainAddresses = (): string[] => {
        try {
          const raw = window.localStorage.getItem('nextcompli.accessManagement.users');
          if (!raw) {
            return [];
          }
          const parsed = JSON.parse(raw);
          if (!Array.isArray(parsed)) {
            return [];
          }

          return parsed
            .map((entry) => String((entry && typeof entry === 'object' ? (entry as { email?: string }).email : '') || '').trim().toLowerCase())
            .filter((email) => /@nextcompli\.(com|ai)$/i.test(email));
        } catch {
          return [];
        }
      };

      const discoveredManagedEmails = loadManagedDomainAddresses();
      const tierOneAddressSet = new Set<string>([
        'ceo@nextcompli.com',
        'prod-superuser@nextcompli.com',
        'no-reply@nextcompli.com',
        'social@nextcompli.com',
        'support@nextcompli.com',
        'operations@nextcompli.com',
        'compliance@nextcompli.ai',
      ]);
      const ownManagedMailbox = String(user?.managed_mailbox_email || '').trim().toLowerCase();
      const ownPrimaryEmail = String(user?.email || '').trim().toLowerCase();

      const namedUserMailboxAddresses = new Set<string>();
      if (hasSuperUserAccess) {
        discoveredManagedEmails
          .filter((address) => !tierOneAddressSet.has(address))
          .forEach((address) => namedUserMailboxAddresses.add(address));
      } else {
        if (ownManagedMailbox) {
          namedUserMailboxAddresses.add(ownManagedMailbox);
        }
        if (ownPrimaryEmail) {
          namedUserMailboxAddresses.add(ownPrimaryEmail);
        }
      }

      const tierOneSharedRoleMailboxes = hasSuperUserAccess
        ? [
            { address: 'ceo@nextcompli.com', purpose: 'Executive leadership shared mailbox', source: 'aws', tierKey: 'shared_role_mailboxes' as const },
            { address: 'prod-superuser@nextcompli.com', purpose: 'Platform superuser shared mailbox', source: 'aws', tierKey: 'shared_role_mailboxes' as const },
            { address: 'no-reply@nextcompli.com', purpose: 'Primary transactional sender + WorkMail inbox', source: 'aws', tierKey: 'shared_role_mailboxes' as const },
            { address: 'social@nextcompli.com', purpose: 'Social publishing and integrations mailbox', source: 'config', tierKey: 'shared_role_mailboxes' as const },
            { address: 'vantage@nextcompli.com', purpose: 'Vantage research mailbox (LinkedIn and Facebook OTP)', source: 'config', tierKey: 'shared_role_mailboxes' as const },
            { address: 'support@nextcompli.com', purpose: 'Customer support / reply-to contact', source: 'config', tierKey: 'shared_role_mailboxes' as const },
            { address: 'operations@nextcompli.com', purpose: 'Operations/policy contact in dashboard copy', source: 'config', tierKey: 'shared_role_mailboxes' as const },
            { address: 'compliance@nextcompli.ai', purpose: 'Compliance data service contact user-agent', source: 'config', tierKey: 'shared_role_mailboxes' as const },
          ]
        : [];

      const tierTwoNamedUserMailboxes = [...namedUserMailboxAddresses].map((address) => ({
        address,
        purpose: 'Named user mailbox from Access Management directory',
        source: 'managed-user',
        tierKey: 'named_user_mailboxes' as const,
      }));

      const deduped = new Map<string, { address: string; purpose: string; source: string; tierKey: 'shared_role_mailboxes' | 'named_user_mailboxes' }>();
      [...tierOneSharedRoleMailboxes, ...tierTwoNamedUserMailboxes].forEach((entry) => {
        const key = entry.address.toLowerCase();
        if (!deduped.has(key)) {
          deduped.set(key, entry);
        }
      });

      const emailDirectoryEntries = [...deduped.values()];

      return (
        <DashboardEmailAccounts
          language={language}
          adminEmailInboxUrl={adminEmailInboxUrl}
          accessToken={accessToken}
          gatewayBaseUrl={gatewayBaseUrl}
          buildInboxUrlForAddress={buildInboxUrlForAddress}
          emailDirectoryEntries={emailDirectoryEntries}
          currentUserEmail={user?.email || ''}
          showAdminAudit={hasSuperUserAccess}
          onRefreshAccounts={() => setEmailInboxRefreshNonce((current) => current + 1)}
        />
      );
    }

    if (activeNavItem === 'socials') {
      if (!hasSuperUserAccess) {
        return renderHomePane();
      }

      const fbConfigured = Boolean(facebookPageToken && facebookPageId);

      const loadSocialsData = async () => {
        if (!fbConfigured || socialsPageLoaded) return;
        try {
          const [pageRes, postsRes] = await Promise.all([
            fetch(`https://graph.facebook.com/v20.0/${facebookPageId}?fields=id,name,fan_count&access_token=${facebookPageToken}`),
            fetch(`https://graph.facebook.com/v20.0/${facebookPageId}/posts?fields=id,message,created_time,permalink_url,attachments{media,url}&limit=3&access_token=${facebookPageToken}`),
          ]);
          if (pageRes.ok) {
            const pageData = await pageRes.json() as { id?: string; name?: string; fan_count?: number };
            setSocialsPageName(String(pageData.name || ''));
            setSocialsFanCount(typeof pageData.fan_count === 'number' ? pageData.fan_count : null);
            setSocialsPageConnectionVerified(Boolean(pageData.id || pageData.name));
          } else {
            setSocialsPageConnectionVerified(false);
          }
          if (postsRes.ok) {
            const postsData = await postsRes.json() as { data?: Array<{ id: string; message?: string; created_time?: string; permalink_url?: string; attachments?: { data?: Array<{ media?: { image?: { src?: string } }; url?: string }> } }> };
            setSocialsRecentPosts((postsData.data || []).slice(0, 3));
          }
          setSocialsPageLoaded(true);
        } catch {
          setSocialsPageConnectionVerified(false);
          setSocialsPageLoaded(true);
        }
      };

      const loadInsights = async () => {
        if (!fbConfigured || socialsInsightsLoaded) return;
        try {
          const res = await fetch(
            `https://graph.facebook.com/v20.0/${facebookPageId}/insights?metric=page_impressions_week,page_reach_week,page_post_engagements&period=week&access_token=${facebookPageToken}`
          );
          if (res.ok) {
            const data = await res.json() as { data?: Array<{ name: string; values?: Array<{ value: number }> }> };
            const metricMap: Record<string, number> = {};
            for (const item of (data.data || [])) {
              const latest = item.values?.[item.values.length - 1]?.value ?? 0;
              metricMap[item.name] = Number(latest);
            }
            setSocialsInsights({
              impressions: metricMap['page_impressions_week'],
              reach: metricMap['page_reach_week'],
              engagements: metricMap['page_post_engagements'],
            });
          }
          setSocialsInsightsLoaded(true);
        } catch {
          setSocialsInsightsLoaded(true);
        }
      };

      const loadSocialAccessAudit = async (forceRefresh = false) => {
        if (!accessToken) return;
        if (!forceRefresh && (socialAccessAuditLoaded || socialAccessAuditRunning)) return;

        setSocialAccessAuditRunning(true);
        setSocialAccessAuditError(null);
        try {
          const response = await fetch(socialsAccessAuditUrl, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          if (!response.ok) {
            throw new Error(`Audit failed (${response.status})`);
          }
          const payload = await response.json() as {
            checked_at?: string;
            criteria_version?: string;
            criteria?: SocialAccessAuditCriteria;
            platforms?: Partial<Record<SocialPlatformKey, SocialAccessAuditEntry>>;
          };
          setSocialAccessAuditByPlatform(payload.platforms || {});
          setSocialAccessAuditCheckedAt(typeof payload.checked_at === 'string' ? payload.checked_at : null);
          setSocialAccessAuditCriteria(payload.criteria || null);
          setSocialAccessAuditLoaded(true);
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unable to run access audit';
          setSocialAccessAuditError(message);
          setSocialAccessAuditLoaded(true); // mark attempted so auto-trigger stops; user can retry via button
        } finally {
          setSocialAccessAuditRunning(false);
        }
      };

      const loadSocialResearchSessions = async () => {
        if (!accessToken || !hasSuperUserAccess) return;
        setSocialResearchSessionsLoading(true);
        try {
          const response = await fetch(socialsResearchSessionsUrl, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          if (!response.ok) {
            throw new Error(`Unable to load research sessions (${response.status})`);
          }
          const payload = await response.json() as typeof socialResearchSessions;
          setSocialResearchSessions(payload);
          const connecting = Object.values(payload?.connect || {}).some((row) => row?.status === 'connecting');
          // Clear local Waiting state once the sidecar reports live or logged_in.
          if (socialResearchConnectPlatform) {
            const platformRow = payload?.platforms?.[socialResearchConnectPlatform];
            const connectRow = payload?.connect?.[socialResearchConnectPlatform];
            const done = Boolean(platformRow?.logged_in)
              || connectRow?.status === 'live'
              || connectRow?.status === 'ready'
              || connectRow?.status === 'error';
            if (done) {
              setSocialResearchConnectPlatform(null);
            }
          }
          if (connecting && !socialResearchLiveOpen) {
            setSocialResearchLiveOpen(true);
          }
          if (connecting) {
            window.setTimeout(() => { void loadSocialResearchSessions(); }, 5000);
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unable to load research sessions';
          // eslint-disable-next-line no-console
          console.error('[socials] research-sessions load failed', {
            url: socialsResearchSessionsUrl,
            message,
          });
          setSocialResearchSessions({ ok: false, error: message, platforms: {}, connect: {} });
        } finally {
          setSocialResearchSessionsLoading(false);
        }
      };

      const connectSocialResearchSession = async (platform: 'linkedin' | 'facebook' | 'instagram') => {
        if (!accessToken || !hasSuperUserAccess) return;
        if (socialResearchSessions?.ok === false || socialResearchSessions?.available === false) {
          setSocialResearchSessions((current) => ({
            ...(current || {}),
            ok: false,
            available: false,
            error: current?.operator_hint
              || current?.error
              || 'Research browser is unavailable. Connect is disabled until the AWS sidecar recovers.',
          }));
          return;
        }
        setSocialResearchConnectPlatform(platform);
        setSocialResearchLiveOpen(true);
        try {
          const response = await fetch(`${socialsResearchSessionsUrl}/connect`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ platform }),
          });
          if (!response.ok) {
            let detailMessage = `Connect failed (${response.status})`;
            let operatorHint: string | undefined;
            try {
              const errBody = await response.json() as {
                detail?: string | {
                  error?: string;
                  operator_hint?: string;
                  available?: boolean;
                };
              };
              const detail = errBody?.detail;
              if (typeof detail === 'string' && detail.trim()) {
                detailMessage = detail;
              } else if (detail && typeof detail === 'object') {
                detailMessage = String(detail.error || detailMessage);
                operatorHint = typeof detail.operator_hint === 'string' ? detail.operator_hint : undefined;
              }
            } catch {
              /* keep status-only message */
            }
            setSocialResearchSessions((current) => ({
              ...(current || {}),
              ok: false,
              available: false,
              error: detailMessage,
              operator_hint: operatorHint || current?.operator_hint,
              platforms: current?.platforms || {},
              connect: current?.connect || {},
            }));
            setSocialResearchConnectPlatform(null);
            return;
          }
          await loadSocialResearchSessions();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Connect failed';
          setSocialResearchSessions((current) => ({
            ...(current || {}),
            ok: false,
            available: false,
            error: message,
          }));
          setSocialResearchConnectPlatform(null);
        }
      };

      const loadSocialResearchCredentials = async (reveal = false) => {
        if (!accessToken || !hasSuperUserAccess) return;
        setSocialResearchSecretsLoading(true);
        setSocialResearchSecretsError(null);
        try {
          const url = new URL(socialsResearchCredentialsUrl);
          if (reveal) {
            url.searchParams.set('reveal', 'true');
          }
          const response = await fetch(url.toString(), {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          if (!response.ok) {
            throw new Error(`Unable to load social passwords (${response.status})`);
          }
          const payload = await response.json() as {
            revealed?: boolean;
            secrets?: SocialResearchSecret[];
          };
          setSocialResearchSecrets(Array.isArray(payload.secrets) ? payload.secrets : []);
          setSocialResearchSecretsRevealed(Boolean(payload.revealed));
          setSocialResearchSecretsLoaded(true);
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unable to load social passwords';
          setSocialResearchSecretsError(message);
          setSocialResearchSecretsLoaded(true);
        } finally {
          setSocialResearchSecretsLoading(false);
        }
      };

      const loadVantageSocialPlatforms = async () => {
        if (!accessToken) return;
        setVantageSocialPlatformsLoading(true);
        setVantageSocialPlatformsError(null);
        try {
          const response = await fetch(socialsVantagePlatformsUrl, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          if (!response.ok) {
            throw new Error(`Unable to load Vantage platforms (${response.status})`);
          }
          const payload = await response.json() as typeof vantageSocialPlatforms;
          setVantageSocialPlatforms(payload);
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unable to load Vantage platforms';
          setVantageSocialPlatformsError(message);
        } finally {
          setVantageSocialPlatformsLoading(false);
        }
      };

      const saveVantageSocialPlatformToggle = async (platformId: string, enabled: boolean) => {
        if (!accessToken) return;
        const currentEnabled: Record<string, boolean> = {};
        for (const row of vantageSocialPlatforms?.platforms || []) {
          currentEnabled[row.id] = Boolean(row.enabled);
        }
        currentEnabled[platformId] = enabled;
        setVantageSocialPlatformsSaving(true);
        setVantageSocialPlatformsError(null);
        try {
          const response = await fetch(socialsVantagePlatformsUrl, {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ enabled: currentEnabled }),
          });
          if (!response.ok) {
            throw new Error(`Unable to save Vantage platforms (${response.status})`);
          }
          const payload = await response.json() as typeof vantageSocialPlatforms;
          setVantageSocialPlatforms(payload);
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unable to save Vantage platforms';
          setVantageSocialPlatformsError(message);
        } finally {
          setVantageSocialPlatformsSaving(false);
        }
      };

      const copySocialResearchSecret = async (secret: SocialResearchSecret) => {
        const value = String(secret.password || '').trim();
        if (!value) return;
        try {
          await navigator.clipboard.writeText(value);
          setSocialResearchCopiedId(secret.id);
          window.setTimeout(() => {
            setSocialResearchCopiedId((current) => (current === secret.id ? null : current));
          }, 2000);
        } catch {
          setSocialResearchSecretsError('Clipboard copy failed');
        }
      };

      const handlePost = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!fbConfigured) return;
        const hasText = socialsPostText.trim().length > 0;
        const hasImage = socialsPostImageUrl.trim().length > 0;
        const hasLink = socialsPostLink.trim().length > 0;
        if (!hasText && !hasImage) return;
        setSocialsPostStatus('posting');
        setSocialsPostError(null);
        try {
          let endpoint = `https://graph.facebook.com/v20.0/${facebookPageId}/feed`;
          const params = new URLSearchParams({ access_token: facebookPageToken });
          if (hasImage) {
            // Photo post via /photos
            endpoint = `https://graph.facebook.com/v20.0/${facebookPageId}/photos`;
            params.set('url', socialsPostImageUrl.trim());
            if (hasText) params.set('caption', socialsPostText.trim());
          } else {
            if (hasText) params.set('message', socialsPostText.trim());
            if (hasLink) params.set('link', socialsPostLink.trim());
          }
          const res = await fetch(endpoint, { method: 'POST', body: params });
          if (!res.ok) {
            const errData = await res.json() as { error?: { message?: string } };
            throw new Error(String(errData?.error?.message || 'Post failed'));
          }
          setSocialsPostStatus('success');
          setSocialsPostText('');
          setSocialsPostLink('');
          setSocialsPostImageUrl('');
          setSocialsPageLoaded(false);
          setSocialsRecentPosts([]);
          await loadSocialsData();
        } catch (err) {
          setSocialsPostStatus('error');
          setSocialsPostError(err instanceof Error ? err.message : 'Failed to post');
        }
      };

      if (!socialsPageLoaded && fbConfigured) {
        void loadSocialsData();
      }
      if (!socialAccessAuditLoaded && !socialAccessAuditRunning && accessToken) {
        void loadSocialAccessAudit();
      }
      if (!socialResearchSecretsLoaded && !socialResearchSecretsLoading && accessToken && hasSuperUserAccess) {
        void loadSocialResearchCredentials(false);
      }
      // Research browser card is always visible for platform admins — do not gate
      // the health poll on the Configuration sub-tab (default tab is Analytics).
      if (hasSuperUserAccess && accessToken && !socialResearchSessions && !socialResearchSessionsLoading) {
        void loadSocialResearchSessions();
      }
      if (accessToken && !vantageSocialPlatforms && !vantageSocialPlatformsLoading) {
        void loadVantageSocialPlatforms();
      }
      if (openSocialPlatform === 'facebook' && socialsTab === 'analytics' && !socialsInsightsLoaded && fbConfigured) {
        void loadInsights();
      }

      const getPlatformStatus = (platformKey: SocialPlatformKey): string => {
        const audited = socialAccessAuditByPlatform[platformKey];
        if (audited) {
          const usesEvidenceModel = (
            audited.working != null
            || audited.read_pull_ok != null
            || audited.write_push_ok != null
            || audited.search_ok != null
            || audited.handle_live != null
            || audited.handle_configured != null
            || audited.credential_configured != null
          );

          if (usesEvidenceModel) {
            if (!audited.configured) {
              return 'Not configured';
            }
            if (audited.working === true) {
              return 'Working (pull+push verified)';
            }
            if (audited.handle_configured === true && audited.handle_live === false) {
              return 'Handle configured, profile not live';
            }
            if (audited.read_pull_ok === false) {
              return 'Configured, pull failed';
            }
            if (audited.search_ok === false) {
              return 'Configured, search failed';
            }
            if (audited.read_pull_ok === true && audited.write_push_ok !== true) {
              return 'Read verified, push unverified';
            }
            if (audited.read_pull_ok === true && audited.write_push_ok === true && audited.search_ok !== true) {
              return 'Pull+push verified, search unverified';
            }
            return 'Configured, verification pending';
          }

          if (!audited.configured) {
            return 'Not configured';
          }
          if (audited.can_access) {
            return audited.capability === 'public_profile' ? 'Public access verified' : 'Connected';
          }
          if (audited.capability === 'public_profile') {
            return 'Public profile unreachable';
          }
          return 'Configured, access failed';
        }

        if (platformKey === 'facebook') {
          if (!fbConfigured) {
            return 'Not configured';
          }
          if (!socialsPageLoaded) {
            return 'Verifying connection';
          }
          if (socialsPageConnectionVerified) {
            return `Connected${socialsPageName ? ` • ${socialsPageName}` : ''}`;
          }
          return 'Configured, verification failed';
        }
        const draft = socialConfigDrafts[platformKey];
        const savedStatus = draft.apiStatus.trim();
        const normalizedStatus = savedStatus.toLowerCase();
        const accountSuffix = draft.accountId.trim() ? ` • ${draft.accountId.trim()}` : '';
        const isBlocked =
          normalizedStatus.includes('suspended')
          || normalizedStatus.includes('appeal')
          || normalizedStatus.includes('blocked');
        const isPending =
          normalizedStatus.includes('pending')
          || normalizedStatus.includes('awaiting')
          || normalizedStatus.includes('ticket')
          || normalizedStatus.includes('onboarding');

        if (isBlocked) {
          return `Blocked${accountSuffix}`;
        }
        if (isPending) {
          return `Setup pending${accountSuffix}`;
        }
        if ((platformKey === 'linkedin' && linkedinAccessToken) || (platformKey === 'x' && xAccessToken)) {
          return `Configured${accountSuffix}`;
        }
        if (draft.accountId.trim()) {
          return `Configured${accountSuffix}`;
        }
        return 'Not connected';
      };

      const getPlatformTrafficSignal = (platformKey: SocialPlatformKey, statusText: string): { label: 'Blocked' | 'Doable' | 'Need to' | 'Succeeded' | 'Out of scope'; color: string } => {
        const audited = socialAccessAuditByPlatform[platformKey];
        if (audited) {
          // "Do not expand" is a finished decision. It must not look like open staff work,
          // even if a public URL happens to answer.
          if (audited.cadence === 'skip') {
            return { label: 'Out of scope', color: 'var(--color-text-muted)' };
          }
          if (audited.working === true) {
            return { label: 'Succeeded', color: 'var(--color-success)' };
          }
          // Claimed handles we are not staffing daily.
          if (audited.cadence === 'reserved') {
            return { label: 'Doable', color: 'color-mix(in srgb, #94a3b8 80%, var(--color-text-strong))' };
          }
          if (!audited.configured) {
            return { label: 'Need to', color: 'color-mix(in srgb, #f59e0b 80%, var(--color-text-strong))' };
          }
          if (audited.handle_configured === true && audited.handle_live === false) {
            return { label: 'Blocked', color: 'var(--color-danger)' };
          }
          // API credential failure with a live brand handle is yellow work, not red blocked.
          if (audited.read_pull_ok === false && audited.handle_live === true) {
            return { label: 'Need to', color: 'color-mix(in srgb, #f59e0b 80%, var(--color-text-strong))' };
          }
          if (audited.read_pull_ok === false) {
            return { label: 'Blocked', color: 'var(--color-danger)' };
          }
          // Search engines flake; only treat search failure as Blocked when the profile also failed.
          if (
            audited.search_ok === false
            && audited.handle_live !== true
            && audited.read_pull_ok !== true
          ) {
            return { label: 'Blocked', color: 'var(--color-danger)' };
          }
          if (audited.read_pull_ok === true && audited.write_push_ok !== true) {
            return { label: 'Doable', color: 'color-mix(in srgb, #f59e0b 80%, var(--color-text-strong))' };
          }
          return { label: 'Doable', color: 'color-mix(in srgb, #f59e0b 80%, var(--color-text-strong))' };
        }

        const normalizedStatus = statusText.toLowerCase();
        if (
          normalizedStatus.includes('blocked')
          || normalizedStatus.includes('suspended')
          || normalizedStatus.includes('failed')
          || normalizedStatus.includes('unreachable')
        ) {
          return { label: 'Blocked', color: 'var(--color-danger)' };
        }
        if (
          normalizedStatus.includes('pending')
          || normalizedStatus.includes('verifying')
          || normalizedStatus.includes('unverified')
        ) {
          return { label: 'Doable', color: 'color-mix(in srgb, #f59e0b 80%, var(--color-text-strong))' };
        }
        if (
          normalizedStatus.includes('connected')
          || normalizedStatus.includes('working')
          || normalizedStatus.includes('public access verified')
        ) {
          return { label: 'Succeeded', color: 'var(--color-success)' };
        }
        if (
          normalizedStatus.includes('not configured')
          || normalizedStatus.includes('not connected')
        ) {
          return { label: 'Need to', color: 'color-mix(in srgb, #f59e0b 80%, var(--color-text-strong))' };
        }
        return { label: 'Doable', color: 'color-mix(in srgb, #f59e0b 80%, var(--color-text-strong))' };
      };

      const getPlatformIdentity = (platformKey: SocialPlatformKey): string => {
        const audited = socialAccessAuditByPlatform[platformKey];
        if (audited) {
          if (audited.handle) {
            return audited.handle;
          }
          if (audited.cadence === 'skip') {
            return 'Do not expand';
          }
          if (audited.cadence === 'reserved') {
            return 'Reserved';
          }
          return audited.configured ? '' : 'No confirmed handle';
        }
        const draft = socialConfigDrafts[platformKey];
        if (!draft) {
          return '';
        }
        if (platformKey === 'facebook') {
          return draft.contactEmail || '';
        }
        // Return handle from draft config for non-audited platforms if available
        return draft.handle || '';
      };

      const getTrafficSignalTone = (label: 'Blocked' | 'Doable' | 'Need to' | 'Succeeded' | 'Out of scope'): string => (
        label.toLowerCase().replace(/\s+/g, '-')
      );

      const renderAuditEvidence = (platformKey: SocialPlatformKey) => {
        const audited = socialAccessAuditByPlatform[platformKey];
        if (!audited) {
          return null;
        }

        const evidenceItems: Array<{ label: string; value: string }> = [
          { label: 'Configured', value: audited.configured ? 'Yes' : 'No' },
          { label: 'Access', value: audited.can_access ? 'Yes' : 'No' },
        ];

        if (audited.handle_configured != null) {
          evidenceItems.push({ label: 'Handle configured', value: audited.handle_configured ? 'Yes' : 'No' });
        }
        if (audited.handle_live != null) {
          evidenceItems.push({ label: 'Handle live', value: audited.handle_live ? 'Yes' : 'No' });
        }
        if (audited.read_pull_ok != null) {
          evidenceItems.push({ label: 'Pull verified', value: audited.read_pull_ok ? 'Yes' : 'No' });
        }
        if (audited.write_push_ok != null) {
          evidenceItems.push({ label: 'Push verified', value: audited.write_push_ok ? 'Yes' : 'No' });
        }
        if (audited.search_ok != null) {
          evidenceItems.push({ label: 'Search verified', value: audited.search_ok ? 'Yes' : 'No' });
        }
        if (audited.working != null) {
          evidenceItems.push({ label: 'Working', value: audited.working ? 'Yes' : 'No' });
        }

        return (
          <div style={{ marginTop: '0.75rem', border: '1px solid var(--color-border)', borderRadius: '10px', background: 'var(--color-surface)', padding: '0.75rem 0.85rem' }}>
            <div style={{ display: 'grid', gap: '0.45rem', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
              {evidenceItems.map((item) => (
                <div key={`${platformKey}-${item.label}`} style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                  <strong style={{ color: 'var(--color-text-strong)' }}>{item.label}:</strong> {item.value}
                </div>
              ))}
            </div>
            {(audited.http_status != null || audited.detail) && (
              <p style={{ margin: '0.55rem 0 0', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                {audited.http_status != null ? `HTTP ${audited.http_status}` : 'No HTTP status'}
                {audited.detail ? ` • ${audited.detail}` : ''}
              </p>
            )}
          </div>
        );
      };

      const socialPlatforms = [
        {
          key: 'facebook' as const,
          title: 'Facebook',
          status: getPlatformStatus('facebook'),
          description: 'Publishing, page insights, and recent posts for the connected Facebook page.',
        },
        {
          key: 'linkedin' as const,
          title: 'LinkedIn',
          status: getPlatformStatus('linkedin'),
          description: 'Company-page publishing and analytics settings for LinkedIn.',
        },
        {
          key: 'x' as const,
          title: 'X (Twitter)',
          status: getPlatformStatus('x'),
          description: 'Posting, account access, and engagement settings for X.',
        },
        {
          key: 'instagram' as const,
          title: 'Instagram',
          status: getPlatformStatus('instagram'),
          description: 'Business account publishing and media settings for Instagram.',
        },
        {
          key: 'whatsapp' as const,
          title: 'WhatsApp',
          status: getPlatformStatus('whatsapp'),
          description: 'Business messaging setup and conversation analytics for WhatsApp.',
        },
        {
          key: 'telegram' as const,
          title: 'Telegram',
          status: getPlatformStatus('telegram'),
          description: 'Channel and account presence monitoring for Telegram narrative and community signals.',
        },
        {
          key: 'messenger' as const,
          title: 'Messenger',
          status: getPlatformStatus('messenger'),
          description: 'Facebook Messenger inbox readiness, response ownership, and engagement monitoring.',
        },
        {
          key: 'snapchat' as const,
          title: 'Snapchat',
          status: getPlatformStatus('snapchat'),
          description: 'Public profile presence and audience-signal monitoring for Snapchat.',
        },
        {
          key: 'github' as const,
          title: 'GitHub',
          status: getPlatformStatus('github'),
          description: 'GitHub organization and social presence configuration, pending org creation.',
        },
        {
          key: 'reddit' as const,
          title: 'Reddit',
          status: getPlatformStatus('reddit'),
          description: 'Community footprint and public discussion monitoring for risk and sentiment signals.',
        },
        {
          key: 'youtube' as const,
          title: 'YouTube',
          status: getPlatformStatus('youtube'),
          description: 'Channel presence, content cadence, and media footprint signals.',
        },
        {
          key: 'tiktok' as const,
          title: 'TikTok',
          status: getPlatformStatus('tiktok'),
          description: 'Short-form platform presence and fast-moving narrative monitoring.',
        },
        {
          key: 'threads' as const,
          title: 'Threads',
          status: getPlatformStatus('threads'),
          description: 'Meta-linked text updates and real-time public narrative signals.',
        },
        {
          key: 'twitch' as const,
          title: 'Twitch',
          status: getPlatformStatus('twitch'),
          description: 'Live-stream presence, creator identity, and community engagement signals.',
        },
        {
          key: 'truth_social' as const,
          title: 'Truth Social',
          status: getPlatformStatus('truth_social'),
          description: 'Public-post presence and political narrative monitoring on Truth Social.',
        },
        {
          key: 'bluesky' as const,
          title: 'Bluesky',
          status: getPlatformStatus('bluesky'),
          description: 'Decentralized social profile and public narrative monitoring for Bluesky.',
        },
        {
          key: 'medium' as const,
          title: 'Medium',
          status: getPlatformStatus('medium'),
          description: 'Publication and thought-leadership signal tracking from long-form posts.',
        },
        {
          key: 'substack' as const,
          title: 'Substack',
          status: getPlatformStatus('substack'),
          description: 'Newsletter publication monitoring and narrative consistency checks.',
        },
        {
          key: 'gitlab' as const,
          title: 'GitLab',
          status: getPlatformStatus('gitlab'),
          description: 'Additional open-source/project footprint tracking alongside GitHub.',
        },
        {
          key: 'gofundme' as const,
          title: 'GoFundMe',
          status: getPlatformStatus('gofundme'),
          description: 'Fundraising campaign presence, organizer identity, and donation-trust monitoring.',
        },
        {
          key: 'kickstarter' as const,
          title: 'Kickstarter',
          status: getPlatformStatus('kickstarter'),
          description: 'Project funding profile, campaign trajectory, and backer-engagement monitoring.',
        },
        {
          key: 'patreon' as const,
          title: 'Patreon',
          status: getPlatformStatus('patreon'),
          description: 'Membership and recurring-support presence with creator-page consistency checks.',
        },
        {
          key: 'givebutter' as const,
          title: 'Givebutter',
          status: getPlatformStatus('givebutter'),
          description: 'Nonprofit/fundraising campaign operations and donation-funnel monitoring.',
        },
        {
          key: 'givesendgo' as const,
          title: 'GiveSendGo',
          status: getPlatformStatus('givesendgo'),
          description: 'Campaign presence and public-support narrative monitoring for GiveSendGo.',
        },
        {
          key: 'gab' as const,
          title: 'Gab',
          status: getPlatformStatus('gab'),
          description: 'Public-post presence and narrative monitoring on Gab.',
        },
      ].sort((left, right) => left.title.localeCompare(right.title));

      const handleSocialPlatformToggle = (platformKey: SocialPlatformKey) => {
        const nextIsOpen = openSocialPlatform !== platformKey;
        setOpenSocialPlatform(nextIsOpen ? platformKey : null);
        if (nextIsOpen) {
          setSocialsTab('analytics');
        }
      };

      const renderSocialSecretCard = (secret: SocialResearchSecret | undefined) => {
        if (!hasSuperUserAccess || !secret) {
          return null;
        }
        const isBrowserSessionPlatform = secret.kind === 'website_login'
          && (secret.platform === 'linkedin' || secret.platform === 'facebook' || secret.platform === 'instagram');
        const isWorkspaceMailbox = secret.kind === 'mailbox' || secret.id === 'workmail_social';
        const hidePasswordUi = isBrowserSessionPlatform || isWorkspaceMailbox;
        const revealedValue = socialResearchSecretsRevealed ? String(secret.password || '') : '';
        return (
          <div style={{ border: '1px solid var(--color-border)', borderRadius: '10px', background: 'var(--color-surface)', padding: '0.9rem' }}>
            <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.03em' }}>
              {secret.label}
            </div>
            <p style={{ margin: '0.4rem 0 0', fontSize: '0.86rem', color: 'var(--color-text-strong)' }}>
              {secret.username || secret.email}
            </p>
            {isBrowserSessionPlatform ? (
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                Research session uses Connect above. Passwords are not stored in Settings.
              </p>
            ) : isWorkspaceMailbox ? (
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                No password stored · Google Workspace via NextCompliMCP (not WorkMail IMAP)
              </p>
            ) : (
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                {secret.password_configured ? 'Password stored' : 'No password stored'}
                {' · '}
                {secret.source}
              </p>
            )}
            {secret.note ? (
              <p style={{ margin: '0.45rem 0 0', fontSize: '0.8rem', color: 'var(--color-text-muted)', lineHeight: 1.4 }}>
                {secret.note}
              </p>
            ) : null}
            {!hidePasswordUi ? (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '0.7rem' }}>
              <input
                type={socialResearchSecretsRevealed ? 'text' : 'password'}
                readOnly
                value={socialResearchSecretsRevealed ? (revealedValue || '') : (secret.password_configured ? '••••••••••••' : '')}
                placeholder={secret.password_configured ? '' : 'Not stored'}
                aria-label={`${secret.label} password`}
                style={{ flex: '1 1 180px', minWidth: '160px', padding: '0.55rem 0.7rem', fontSize: '0.9rem', border: '1px solid var(--color-border-strong)', borderRadius: '8px', boxSizing: 'border-box', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
              />
              <button
                type="button"
                disabled={!accessToken || socialResearchSecretsLoading || (!secret.password_configured && !socialResearchSecretsRevealed)}
                onClick={() => {
                  if (socialResearchSecretsRevealed) {
                    void loadSocialResearchCredentials(false);
                    return;
                  }
                  void loadSocialResearchCredentials(true);
                }}
                style={{
                  padding: '0.45rem 0.8rem',
                  borderRadius: '18px',
                  border: '1px solid var(--color-border-strong)',
                  background: 'var(--color-surface-muted)',
                  color: 'var(--color-text-strong)',
                  fontSize: '0.82rem',
                  cursor: accessToken && !socialResearchSecretsLoading ? 'pointer' : 'not-allowed',
                }}
              >
                {socialResearchSecretsLoading ? 'Loading...' : socialResearchSecretsRevealed ? 'Hide password' : 'Reveal password'}
              </button>
              {socialResearchSecretsRevealed && revealedValue ? (
                <button
                  type="button"
                  onClick={() => { void copySocialResearchSecret(secret); }}
                  style={{
                    padding: '0.45rem 0.8rem',
                    borderRadius: '18px',
                    border: '1px solid var(--color-border-strong)',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text-strong)',
                    fontSize: '0.82rem',
                    cursor: 'pointer',
                  }}
                >
                  {socialResearchCopiedId === secret.id ? 'Copied' : 'Copy'}
                </button>
              ) : null}
            </div>
            ) : null}
          </div>
        );
      };

      const renderSocialConfigurationTab = (platformKey: SocialPlatformKey) => {
        const configTitles: Record<SocialPlatformKey, string> = {
          facebook: 'Facebook Configuration',
          linkedin: 'LinkedIn Configuration',
          x: 'X Configuration',
          instagram: 'Instagram Configuration',
          whatsapp: 'WhatsApp Configuration',
          telegram: 'Telegram Configuration',
          messenger: 'Messenger Configuration',
          snapchat: 'Snapchat Configuration',
          github: 'GitHub Configuration',
          reddit: 'Reddit Configuration',
          youtube: 'YouTube Configuration',
          tiktok: 'TikTok Configuration',
          threads: 'Threads Configuration',
          twitch: 'Twitch Configuration',
          truth_social: 'Truth Social Configuration',
          bluesky: 'Bluesky Configuration',
          medium: 'Medium Configuration',
          substack: 'Substack Configuration',
          gitlab: 'GitLab Configuration',
          gofundme: 'GoFundMe Configuration',
          kickstarter: 'Kickstarter Configuration',
          patreon: 'Patreon Configuration',
          givebutter: 'Givebutter Configuration',
          givesendgo: 'GiveSendGo Configuration',
          gab: 'Gab Configuration',
        };
        const configHints: Record<SocialPlatformKey, string> = {
          facebook: 'Manage the settings used to connect the Facebook publishing and insights workflow.',
          linkedin: 'Store the LinkedIn company profile settings you want ready before wiring the integration.',
          x: 'Store the X account settings and operational notes before wiring the integration.',
          instagram: 'Store Instagram setup details and use Meta Business Settings to link this account to the same business portfolio as the Facebook page.',
          whatsapp: 'Store the WhatsApp Business account details and onboarding state before wiring the integration.',
          telegram: 'Store Telegram channel/account details and admin ownership notes for signal monitoring.',
          messenger: 'Store Messenger inbox ownership and routing settings for customer-response readiness.',
          snapchat: 'Store Snapchat profile settings and campaign-operational notes for channel monitoring.',
          github: 'Track the GitHub organization setup details while org creation for nextcompli is still blocked.',
          reddit: 'Store subreddit and moderator account settings for community monitoring and signal validation.',
          youtube: 'Store channel metadata and operational ownership for media footprint checks.',
          tiktok: 'Store official TikTok handle details and setup notes for account validation.',
          threads: 'Store Threads handle and profile settings for Meta-linked narrative monitoring.',
          twitch: 'Store Twitch channel identity and creator settings for live-stream monitoring.',
          truth_social: 'Store Truth Social handle and profile settings for public-post monitoring.',
          bluesky: 'Store Bluesky handle and DID details for decentralized identity monitoring.',
          medium: 'Store Medium publication/account details for long-form communication signals.',
          substack: 'Store Substack publication host details for newsletter footprint checks.',
          gitlab: 'Store GitLab namespace and project visibility settings for engineering footprint checks.',
          gofundme: 'Store GoFundMe organizer and campaign settings for fundraising authenticity checks.',
          kickstarter: 'Store Kickstarter creator profile and campaign details for fundraising due diligence.',
          patreon: 'Store Patreon creator/member settings for recurring-support monitoring.',
          givebutter: 'Store Givebutter organization and campaign settings for donation-operations monitoring.',
          givesendgo: 'Store GiveSendGo campaign profile and governance notes for platform-risk tracking.',
          gab: 'Store Gab handle and profile settings for public-post monitoring.',
        };
        const setupSteps: Record<SocialPlatformKey, string[]> = {
          facebook: [
            'Confirm page token + page ID are available to the website runtime.',
            'Use Meta Business Suite for publishing and insight checks.',
          ],
          linkedin: [
            'Connect a company page admin account and store org/page identifier.',
            'Verify the token includes organization admin analytics scopes before enabling health checks.',
          ],
          x: [
            'Use a dedicated brand account and enroll in the X developer portal.',
            'Generate user-context credentials and store token status for API checks.',
          ],
          instagram: [
            'Open Meta Business Settings and link Instagram to the same business portfolio as the NextCompli Facebook page.',
            'Retrieve Instagram Business Account ID via Graph API: GET /{page-id}?fields=instagram_business_account,connected_instagram_account.',
          ],
          whatsapp: [
            'Start in Meta App Dashboard with the WhatsApp use case, then open API Setup.',
            'Connect a WhatsApp Business Account in API Setup and copy the WhatsApp Business Account ID and Phone Number ID.',
          ],
          telegram: [
            'Create or claim the official Telegram account/channel and lock admin ownership.',
            'Store canonical handle and channel URL for post and engagement monitoring.',
          ],
          messenger: [
            'Enable Messenger on the official Facebook Page and assign inbox ownership.',
            'Store routing owner/contact and escalation workflow notes for response governance.',
          ],
          snapchat: [
            'Create or claim the official Snapchat Public Profile and verify brand ownership.',
            'Store canonical profile URL and campaign ownership notes for monitoring readiness.',
          ],
          github: [
            'Finalize organization creation and admin ownership for the canonical org.',
            'Store org slug and connection notes before enabling API checks.',
          ],
          reddit: [
            'Create the official subreddit and reserve moderator/admin ownership.',
            'Store canonical subreddit and moderation notes for ongoing monitoring.',
          ],
          youtube: [
            'Claim/create the official brand channel in YouTube Studio.',
            'Store channel ID and verify About/links match the official company website.',
          ],
          tiktok: [
            'Claim official handle and complete business verification setup.',
            'Store canonical handle and profile URL for monitoring and account consistency checks.',
          ],
          threads: [
            'Create or claim the official Threads account tied to Meta Business identity.',
            'Verify cross-links between Threads, Instagram, and the official website.',
          ],
          twitch: [
            'Create or claim the official Twitch channel and confirm brand ownership.',
            'Store the canonical channel name and public profile URL for future live-stream checks.',
          ],
          truth_social: [
            'Create or claim the official Truth Social profile and confirm brand ownership.',
            'Store the canonical handle and public URL for political narrative monitoring.',
          ],
          bluesky: [
            'Create or claim the official Bluesky handle and verify the account identity.',
            'Store the handle plus DID so future audits can validate the decentralized profile.',
          ],
          medium: [
            'Create official publication and define editor/admin ownership.',
            'Store publication slug and canonical author handles for reference checks.',
          ],
          substack: [
            'Create publication and capture the permanent host slug.',
            'Store newsletter host and operational notes for cadence and authenticity checks.',
          ],
          gitlab: [
            'Create/claim official namespace and lock admin ownership.',
            'Store namespace and key project links for footprint verification.',
          ],
          gofundme: [
            'Create or claim the official organizer profile and verify identity details.',
            'Store canonical campaign URLs and governance notes for donation-trust monitoring.',
          ],
          kickstarter: [
            'Create or claim the official creator profile and complete project verification.',
            'Store canonical project URLs and campaign states for fundraising lifecycle checks.',
          ],
          patreon: [
            'Create or claim the official creator page and lock account ownership controls.',
            'Store membership tiers and page URL details for recurring-support monitoring.',
          ],
          givebutter: [
            'Create or claim the official Givebutter organization account and verify team ownership.',
            'Store campaign URLs and donation settings for transparency and operational checks.',
          ],
          givesendgo: [
            'Create or claim the official GiveSendGo profile and verify identity details.',
            'Store campaign URLs and public-disclosure notes for due diligence monitoring.',
          ],
          gab: [
            'Create or claim the official Gab profile and confirm brand ownership.',
            'Store the canonical handle and public URL for narrative monitoring.',
          ],
        };
        const draft = socialConfigDrafts[platformKey];
        return (
          <div style={{ display: 'grid', gap: '0.85rem', marginTop: '1rem' }}>
            <div style={{ background: 'var(--color-surface-muted)', border: '1px solid var(--color-border)', borderRadius: '10px', padding: '0.95rem 1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.02rem' }}>{configTitles[platformKey]}</h3>
              <p style={{ margin: '0.35rem 0 0', color: 'var(--color-text-muted)', fontSize: '0.88rem' }}>
                {configHints[platformKey]}
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
              <div style={{ border: '1px solid var(--color-border)', borderRadius: '10px', background: 'var(--color-surface)', padding: '0.9rem' }}>
                <label style={{ display: 'grid', gap: '0.35rem' }}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.03em' }}>Account or page</span>
                  <input
                    type="text"
                    value={draft.accountId}
                    onChange={(event) => { updateSocialConfigDraft(platformKey, 'accountId', event.target.value); }}
                    placeholder={platformKey === 'github' ? 'nextcompli' : 'Page ID, handle, or org'}
                    style={{ width: '100%', padding: '0.6rem 0.75rem', fontSize: '0.9rem', border: '1px solid var(--color-border-strong)', borderRadius: '8px', boxSizing: 'border-box' }}
                  />
                </label>
              </div>
              <div style={{ border: '1px solid var(--color-border)', borderRadius: '10px', background: 'var(--color-surface)', padding: '0.9rem' }}>
                <label style={{ display: 'grid', gap: '0.35rem' }}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.03em' }}>Management URL</span>
                  <input
                    type="url"
                    value={draft.managementUrl}
                    onChange={(event) => { updateSocialConfigDraft(platformKey, 'managementUrl', event.target.value); }}
                    placeholder="https://..."
                    style={{ width: '100%', padding: '0.6rem 0.75rem', fontSize: '0.9rem', border: '1px solid var(--color-border-strong)', borderRadius: '8px', boxSizing: 'border-box' }}
                  />
                </label>
              </div>
              <div style={{ border: '1px solid var(--color-border)', borderRadius: '10px', background: 'var(--color-surface)', padding: '0.9rem' }}>
                <label style={{ display: 'grid', gap: '0.35rem' }}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.03em' }}>Contact mailbox</span>
                  <input
                    type="email"
                    value={draft.contactEmail}
                    onChange={(event) => { updateSocialConfigDraft(platformKey, 'contactEmail', event.target.value); }}
                    placeholder="social@nextcompli.com"
                    style={{ width: '100%', padding: '0.6rem 0.75rem', fontSize: '0.9rem', border: '1px solid var(--color-border-strong)', borderRadius: '8px', boxSizing: 'border-box' }}
                  />
                </label>
              </div>
            </div>
            {renderSocialSecretCard(
              socialResearchSecrets.find((item) => item.kind === 'website_login' && item.platform === platformKey)
              || (platformKey === 'linkedin' || platformKey === 'facebook' || platformKey === 'instagram' || platformKey === 'x'
                ? {
                    id: `${platformKey}_website`,
                    kind: 'website_login',
                    platform: platformKey,
                    label: 'Website login',
                    email: draft.contactEmail || 'social@nextcompli.com',
                    username: draft.contactEmail || 'social@nextcompli.com',
                    password_configured: false,
                    source: 'Not loaded',
                    note: 'Platform admin can reveal the stored website password for this social login.',
                  }
                : undefined),
            )}
            <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <div style={{ border: '1px solid var(--color-border)', borderRadius: '10px', background: 'var(--color-surface)', padding: '0.9rem' }}>
                <label style={{ display: 'grid', gap: '0.35rem' }}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.03em' }}>API status</span>
                  <input
                    type="text"
                    value={draft.apiStatus}
                    onChange={(event) => { updateSocialConfigDraft(platformKey, 'apiStatus', event.target.value); }}
                    placeholder="Connection status or blocker"
                    style={{ width: '100%', padding: '0.6rem 0.75rem', fontSize: '0.9rem', border: '1px solid var(--color-border-strong)', borderRadius: '8px', boxSizing: 'border-box' }}
                  />
                </label>
              </div>
              <div style={{ border: '1px solid var(--color-border)', borderRadius: '10px', background: 'var(--color-primary-soft)', padding: '0.95rem 1rem', color: 'var(--color-text-strong)', fontSize: '0.86rem', lineHeight: 1.5 }}>
                <strong>Integration note:</strong>{' '}
                {platformKey === 'facebook'
                  ? 'Facebook is the only live integration today. These saved fields are for operations and future credential rotation notes.'
                  : platformKey === 'instagram'
                    ? 'Instagram profile fields can be updated through SocialsMCP using instagram_account_update once the account is linked to the same Meta Business portfolio as the page.'
                  : platformKey === 'whatsapp'
                    ? 'WhatsApp should be linked through Meta Business Manager after assigning the Facebook Page to the same Business account.'
                  : platformKey === 'github'
                    ? 'GitHub is listed for planning, but org creation for nextcompli is still failing so no live hookup is enabled yet.'
                    : platformKey === 'gitlab'
                      ? 'GitLab is included as an additional engineering footprint signal and is tracked for namespace-level validation.'
                    : 'This platform is tracked here for future hookup. Saving these values stores your operational notes locally in the browser.'}
              </div>
            </div>
            <div style={{ border: '1px solid var(--color-border)', borderRadius: '10px', background: 'var(--color-surface)', padding: '0.9rem' }}>
              <label style={{ display: 'grid', gap: '0.35rem' }}>
                <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.03em' }}>Notes</span>
                <textarea
                  value={draft.notes}
                  onChange={(event) => { updateSocialConfigDraft(platformKey, 'notes', event.target.value); }}
                  rows={4}
                  placeholder="Setup notes, blockers, next actions, or credential rotation reminders"
                  style={{ width: '100%', padding: '0.75rem', fontSize: '0.9rem', fontFamily: 'inherit', border: '1px solid var(--color-border-strong)', borderRadius: '8px', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </label>
            </div>
            <div style={{ border: '1px solid var(--color-border)', borderRadius: '10px', background: 'var(--color-surface)', padding: '0.9rem' }}>
              <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.03em', marginBottom: '0.45rem' }}>First setup steps</div>
              <ul style={{ margin: 0, paddingLeft: '1.1rem', color: 'var(--color-text-muted)', fontSize: '0.86rem', lineHeight: 1.45 }}>
                {setupSteps[platformKey].map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ul>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <button type="button" className="dashboard-btn dashboard-btn-primary" onClick={() => { saveSocialConfigDraft(platformKey); }}>
                Save configuration
              </button>
              {socialConfigSaveState[platformKey] && (
                <span style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{socialConfigSaveState[platformKey]}</span>
              )}
            </div>
          </div>
        );
      };

      const renderSocialAnalyticsTab = (platformKey: SocialPlatformKey) => {
        const metricGuides: Record<SocialPlatformKey, Array<{ label: string; value: string; note: string }>> = {
          facebook: [
            { label: 'Reach', value: socialsInsights?.reach != null ? socialsInsights.reach.toLocaleString() : '—', note: 'Unique people reached by posts.' },
            { label: 'Impressions', value: socialsInsights?.impressions != null ? socialsInsights.impressions.toLocaleString() : '—', note: 'Total post views including repeats.' },
            { label: 'Engagements', value: socialsInsights?.engagements != null ? socialsInsights.engagements.toLocaleString() : '—', note: 'Reactions, comments, shares, clicks.' },
            { label: 'Followers', value: socialsFanCount != null ? socialsFanCount.toLocaleString() : '—', note: 'Current page audience size.' },
          ],
          linkedin: [
            { label: 'Follower Growth', value: 'Track weekly', note: 'Net followers added from organic and paid.' },
            { label: 'Post Engagement Rate', value: 'Track weekly', note: 'Clicks + reactions + comments + shares / impressions.' },
            { label: 'Impressions by Post Type', value: 'Track monthly', note: 'Compare document, video, image, and text formats.' },
            { label: 'Lead Actions', value: 'Track weekly', note: 'CTA clicks, website visits, and conversion assists.' },
          ],
          x: [
            { label: 'Engagement Rate', value: 'Track weekly', note: 'Replies, reposts, likes, profile clicks per impression.' },
            { label: 'Link Click Rate', value: 'Track weekly', note: 'Traffic quality from post links.' },
            { label: 'Audience Growth', value: 'Track weekly', note: 'Followers added and churn trend.' },
            { label: 'Top Performing Threads', value: 'Track monthly', note: 'Identify topics driving saves and reposts.' },
          ],
          instagram: [
            { label: 'Accounts Reached', value: 'Track weekly', note: 'Unique accounts reached by content.' },
            { label: 'Content Interactions', value: 'Track weekly', note: 'Likes, comments, shares, saves across media.' },
            { label: 'Profile Activity', value: 'Track weekly', note: 'Profile visits, website taps, call/email taps.' },
            { label: 'Reels Watch Time', value: 'Track monthly', note: 'Retention and completion indicators.' },
          ],
          whatsapp: [
            { label: 'Conversations Started', value: 'Track daily', note: 'Inbound and outbound conversation sessions opened.' },
            { label: 'Response SLA', value: 'Track daily', note: 'Median first-response time for customer messages.' },
            { label: 'Delivery Read Rate', value: 'Track weekly', note: 'Delivered/read ratios by template and campaign.' },
            { label: 'Conversation Cost', value: 'Track weekly', note: 'Utility, marketing, and support conversation spend.' },
          ],
          telegram: [
            { label: 'Subscriber Growth', value: 'Track weekly', note: 'Channel audience growth and churn trend.' },
            { label: 'Post Reach', value: 'Track weekly', note: 'Views per post and distribution consistency.' },
            { label: 'Engagement Rate', value: 'Track weekly', note: 'Reactions, replies, and forwards per post.' },
            { label: 'Moderation Events', value: 'Track weekly', note: 'Risk and governance signals from moderation actions.' },
          ],
          messenger: [
            { label: 'Conversations Started', value: 'Track daily', note: 'Inbound threads opened with the official page inbox.' },
            { label: 'First Response Time', value: 'Track daily', note: 'SLA performance for initial customer responses.' },
            { label: 'Resolution Rate', value: 'Track weekly', note: 'Closed conversations versus total active conversations.' },
            { label: 'Escalation Volume', value: 'Track weekly', note: 'Threads escalated for support, legal, or compliance review.' },
          ],
          snapchat: [
            { label: 'Profile Views', value: 'Track weekly', note: 'Public profile discovery and visibility trend.' },
            { label: 'Story Completion', value: 'Track weekly', note: 'Retention and narrative completion quality signal.' },
            { label: 'Follower Growth', value: 'Track weekly', note: 'Net audience additions and churn movement.' },
            { label: 'Campaign Engagement', value: 'Track weekly', note: 'Interaction quality from branded stories and content.' },
          ],
          github: [
            { label: 'Profile Views', value: 'Track weekly', note: 'Traffic to org profile and repos.' },
            { label: 'Stars & Forks', value: 'Track weekly', note: 'Community adoption signals.' },
            { label: 'Issue/PR Throughput', value: 'Track weekly', note: 'Operational responsiveness and velocity.' },
            { label: 'Contributor Growth', value: 'Track monthly', note: 'Net new contributors and repeat contributors.' },
          ],
          reddit: [
            { label: 'Subreddit Growth', value: 'Track weekly', note: 'Subscriber trend and net growth over time.' },
            { label: 'Post Volume', value: 'Track weekly', note: 'Relevant mention cadence and topical concentration.' },
            { label: 'Engagement Quality', value: 'Track weekly', note: 'Comment depth, upvote ratio, and moderator interventions.' },
            { label: 'Risk Mentions', value: 'Track weekly', note: 'Escalatable compliance or reputation-related narratives.' },
          ],
          youtube: [
            { label: 'Channel Views', value: 'Track weekly', note: 'Overall audience reach and trend direction.' },
            { label: 'Subscriber Growth', value: 'Track weekly', note: 'Net subscribers and churn signals.' },
            { label: 'Watch Time', value: 'Track weekly', note: 'Retention and content quality indicator.' },
            { label: 'Top Video Themes', value: 'Track monthly', note: 'Topics driving visibility and engagement.' },
          ],
          tiktok: [
            { label: 'Profile Views', value: 'Track weekly', note: 'Audience discovery and top-of-funnel visibility.' },
            { label: 'Engagement Rate', value: 'Track weekly', note: 'Likes, comments, shares, saves per view.' },
            { label: 'Follower Growth', value: 'Track weekly', note: 'Net additions and volatility checks.' },
            { label: 'Narrative Risk Signals', value: 'Track daily', note: 'Fast-moving trends requiring response or escalation.' },
          ],
          threads: [
            { label: 'Mention Volume', value: 'Track weekly', note: 'Frequency of relevant account and topic mentions.' },
            { label: 'Engagement Rate', value: 'Track weekly', note: 'Replies and repost interactions per post.' },
            { label: 'Follower Growth', value: 'Track weekly', note: 'Account traction and audience movement.' },
            { label: 'Topic Velocity', value: 'Track daily', note: 'How quickly narrative themes change over time.' },
          ],
          twitch: [
            { label: 'Followers', value: 'Track weekly', note: 'Channel audience size and growth trend.' },
            { label: 'Live Views', value: 'Track weekly', note: 'Concurrent and replay reach for live sessions.' },
            { label: 'Chat Activity', value: 'Track weekly', note: 'Audience interaction and community intensity.' },
            { label: 'Stream Cadence', value: 'Track monthly', note: 'Publishing consistency and live-event rhythm.' },
          ],
          truth_social: [
            { label: 'Post Reach', value: 'Track weekly', note: 'Visibility and circulation of public posts.' },
            { label: 'Reposts', value: 'Track weekly', note: 'Amplification and secondary distribution signals.' },
            { label: 'Replies', value: 'Track weekly', note: 'Conversation depth and public engagement.' },
            { label: 'Follower Growth', value: 'Track weekly', note: 'Audience trend and account momentum.' },
          ],
          bluesky: [
            { label: 'Post Reach', value: 'Track weekly', note: 'Visibility and distribution of Bluesky posts.' },
            { label: 'Reposts', value: 'Track weekly', note: 'Amplification and network spread.' },
            { label: 'Replies', value: 'Track weekly', note: 'Conversation depth and engagement quality.' },
            { label: 'Follower Growth', value: 'Track weekly', note: 'Audience trend and account momentum.' },
          ],
          medium: [
            { label: 'Reads', value: 'Track weekly', note: 'Article consumption and distribution trend.' },
            { label: 'Read Ratio', value: 'Track weekly', note: 'Quality/retention signal from read completion.' },
            { label: 'Follower Growth', value: 'Track monthly', note: 'Publication and author audience growth.' },
            { label: 'Citation Mentions', value: 'Track monthly', note: 'External references to posted analysis.' },
          ],
          substack: [
            { label: 'Subscriber Growth', value: 'Track weekly', note: 'Newsletter audience expansion and churn.' },
            { label: 'Open Rate', value: 'Track weekly', note: 'Engagement quality of long-form communications.' },
            { label: 'Click Rate', value: 'Track weekly', note: 'Traffic and action quality from issues.' },
            { label: 'Publication Cadence', value: 'Track monthly', note: 'Consistency and reliability of narrative output.' },
          ],
          gitlab: [
            { label: 'Namespace Activity', value: 'Track weekly', note: 'Project updates, commits, and release signals.' },
            { label: 'Stars & Forks', value: 'Track weekly', note: 'Community adoption and external interest.' },
            { label: 'Issue Velocity', value: 'Track weekly', note: 'Operational responsiveness and backlog trend.' },
            { label: 'Contributor Growth', value: 'Track monthly', note: 'Net contributor participation over time.' },
          ],
          gofundme: [
            { label: 'Campaign Visits', value: 'Track weekly', note: 'Top-of-funnel fundraising visibility.' },
            { label: 'Donation Volume', value: 'Track weekly', note: 'Contribution throughput and momentum trend.' },
            { label: 'Goal Completion', value: 'Track daily', note: 'Progress against stated campaign target.' },
            { label: 'Update Cadence', value: 'Track weekly', note: 'Transparency signal based on organizer updates.' },
          ],
          kickstarter: [
            { label: 'Project Followers', value: 'Track weekly', note: 'Pre-launch and live campaign interest level.' },
            { label: 'Backer Growth', value: 'Track daily', note: 'Net backer additions across campaign phases.' },
            { label: 'Funding Progress', value: 'Track daily', note: 'Pledged amount versus campaign goal.' },
            { label: 'Comment Velocity', value: 'Track weekly', note: 'Community engagement and trust signal.' },
          ],
          patreon: [
            { label: 'Member Growth', value: 'Track weekly', note: 'Recurring-support expansion and retention signal.' },
            { label: 'Tier Mix', value: 'Track monthly', note: 'Revenue concentration and support profile balance.' },
            { label: 'Post Engagement', value: 'Track weekly', note: 'Content interaction across supporter base.' },
            { label: 'Churn Trend', value: 'Track monthly', note: 'Membership stability and potential risk signals.' },
          ],
          givebutter: [
            { label: 'Campaign Reach', value: 'Track weekly', note: 'Fundraising page visibility and traffic trend.' },
            { label: 'Donor Conversion', value: 'Track weekly', note: 'Donation conversion rate from visits.' },
            { label: 'Average Donation', value: 'Track weekly', note: 'Contribution quality and donor mix signal.' },
            { label: 'Recurring Gifts', value: 'Track monthly', note: 'Sustained support profile and reliability.' },
          ],
          givesendgo: [
            { label: 'Campaign Views', value: 'Track weekly', note: 'Audience reach for active campaigns.' },
            { label: 'Supporter Growth', value: 'Track weekly', note: 'Trend in supporters over campaign lifecycle.' },
            { label: 'Funds Raised', value: 'Track daily', note: 'Raised amount versus goal progress.' },
            { label: 'Update Cadence', value: 'Track weekly', note: 'Organizer update frequency and transparency.' },
          ],
          gab: [
            { label: 'Followers', value: 'Track weekly', note: 'Audience growth on the Gab brand profile.' },
            { label: 'Post Reach', value: 'Track weekly', note: 'Visibility of public posts.' },
            { label: 'Engagement Rate', value: 'Track weekly', note: 'Replies, reposts, and reactions versus impressions.' },
            { label: 'Profile Integrity', value: 'Track monthly', note: 'Handle and bio consistency versus brand standards.' },
          ],
        };

        const analyticsLinks: Record<SocialPlatformKey, { label: string; url: string }> = {
          facebook: { label: 'Meta Business Suite Insights', url: 'https://business.facebook.com/latest/insights/' },
          linkedin: { label: 'LinkedIn Page Analytics', url: 'https://www.linkedin.com/analytics/' },
          x: { label: 'X Analytics', url: 'https://analytics.x.com/' },
          instagram: { label: 'Meta Business Settings', url: 'https://business.facebook.com/latest/settings' },
          whatsapp: { label: 'WhatsApp Cloud API Quickstart', url: 'https://developers.facebook.com/docs/whatsapp/cloud-api/get-started' },
          telegram: { label: 'Telegram', url: 'https://telegram.org/' },
          messenger: { label: 'Messenger', url: 'https://www.messenger.com/' },
          snapchat: { label: 'Snapchat for Business', url: 'https://business.snapchat.com/' },
          github: { label: 'GitHub Traffic Insights', url: 'https://github.com/organizations/nextcompli' },
          reddit: { label: 'Reddit', url: 'https://www.reddit.com/' },
          youtube: { label: 'YouTube Studio', url: 'https://studio.youtube.com/' },
          tiktok: { label: 'TikTok Business', url: 'https://www.tiktok.com/business/' },
          threads: { label: 'Threads', url: 'https://www.threads.net/' },
          twitch: { label: 'Twitch', url: 'https://dashboard.twitch.tv/' },
          truth_social: { label: 'Truth Social', url: 'https://truthsocial.com/' },
          bluesky: { label: 'Bluesky', url: 'https://bsky.app/' },
          medium: { label: 'Medium', url: 'https://medium.com/' },
          substack: { label: 'Substack', url: 'https://substack.com/' },
          gitlab: { label: 'GitLab', url: 'https://gitlab.com/' },
          gofundme: { label: 'GoFundMe', url: 'https://www.gofundme.com/' },
          kickstarter: { label: 'Kickstarter', url: 'https://www.kickstarter.com/' },
          patreon: { label: 'Patreon', url: 'https://www.patreon.com/' },
          givebutter: { label: 'Givebutter', url: 'https://givebutter.com/' },
          givesendgo: { label: 'GiveSendGo', url: 'https://www.givesendgo.com/' },
          gab: { label: 'Gab', url: 'https://gab.com/nextcompli' },
        };

        return (
          <div style={{ marginTop: '1rem', display: 'grid', gap: '0.9rem' }}>
            <div style={{ border: '1px solid var(--color-border)', borderRadius: '10px', background: 'var(--color-surface-muted)', padding: '0.95rem 1rem' }}>
              <strong style={{ color: 'var(--color-text-strong)' }}>Analytics Focus</strong>
              <p style={{ margin: '0.4rem 0 0', color: 'var(--color-text-muted)', fontSize: '0.88rem' }}>
                Start with reach, engagement, and conversion-oriented actions. Compare trends week-over-week and keep one source of truth for each platform.
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
              {metricGuides[platformKey].map((item) => (
                <div key={item.label} style={{ border: '1px solid var(--color-border)', borderRadius: '10px', background: 'var(--color-surface)', padding: '0.85rem 0.9rem' }}>
                  <div style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.03em' }}>{item.label}</div>
                  <div style={{ marginTop: '0.35rem', fontSize: '1.15rem', fontWeight: 700, color: 'var(--color-text-strong)' }}>{item.value}</div>
                  <div style={{ marginTop: '0.35rem', fontSize: '0.82rem', color: 'var(--color-text-muted)', lineHeight: 1.35 }}>{item.note}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <a
                href={analyticsLinks[platformKey].url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--color-primary)', fontSize: '0.88rem', textDecoration: 'none', fontWeight: 600 }}
              >
                Open {analyticsLinks[platformKey].label} ↗
              </a>
              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem' }}>Recommended cadence: weekly KPI review, monthly strategy review.</span>
            </div>
          </div>
        );
      };

      return (
        <section className="dashboard-settings-sections dashboard-settings-sections-centered" aria-label="Socials">
          <article className="card settings-card">
            <div className="settings-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div>
                <h2 style={{ margin: 0 }}>Socials</h2>
                {socialsPageName && (
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.88rem', color: 'var(--color-text-muted)' }}>
                    <strong>Facebook:</strong> {socialsPageName}
                    {socialsFanCount !== null && (
                      <span style={{ marginLeft: '0.75rem', color: 'var(--color-primary)', fontWeight: 600 }}>
                        {socialsFanCount.toLocaleString()} followers
                      </span>
                    )}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => { void loadSocialAccessAudit(true); }}
                  disabled={!accessToken || socialAccessAuditRunning}
                  style={{
                    padding: '0.35rem 0.8rem',
                    borderRadius: '18px',
                    border: '1px solid var(--color-border-strong)',
                    background: socialAccessAuditRunning ? 'var(--color-surface-muted)' : 'var(--color-surface)',
                    color: 'var(--color-text-strong)',
                    fontSize: '0.82rem',
                    cursor: accessToken && !socialAccessAuditRunning ? 'pointer' : 'not-allowed',
                  }}
                >
                  {socialAccessAuditRunning ? 'Auditing...' : 'Run NextCompli access audit'}
                </button>
                <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
                  {socialAccessAuditCheckedAt
                    ? `Last audit: ${new Date(socialAccessAuditCheckedAt).toLocaleString()}`
                    : 'Choose a platform below'}
                </p>
              </div>
            </div>

            {hasSuperUserAccess && (
              <div style={{ marginTop: '0.9rem', display: 'grid', gap: '0.75rem' }}>
                <div style={{ border: '1px solid var(--color-border)', borderRadius: '10px', background: 'var(--color-surface)', padding: '0.9rem' }}>
                  <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.03em' }}>
                    Research browser (container)
                  </div>
                  <p style={{ margin: '0.4rem 0 0', fontSize: '0.82rem', color: 'var(--color-text-muted)', lineHeight: 1.45 }}>
                    LinkedIn, Facebook, and Instagram research sessions run in Docker (headed Chromium on a virtual display).
                    LinkedIn signs in as vantage@nextcompli.com and pulls the email OTP from that mailbox — not a household phone.
                    Connect opens a live view so you can finish passkey, MFA, or captcha.
                  </p>
                  {socialResearchSessions && socialResearchSessions.ok === false ? (
                    <div
                      role="alert"
                      style={{
                        marginTop: '0.65rem',
                        padding: '0.7rem 0.8rem',
                        borderRadius: '8px',
                        border: '1px solid var(--color-danger)',
                        background: 'color-mix(in srgb, var(--color-danger) 12%, transparent)',
                        color: 'var(--color-text-strong)',
                        fontSize: '0.84rem',
                        lineHeight: 1.45,
                      }}
                    >
                      <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>
                        Research browser unavailable
                      </div>
                      <div>
                        {socialResearchSessions.operator_hint
                          || socialResearchSessions.error
                          || 'The AWS vantage-social-browser service is not reachable. Connect is disabled until it recovers.'}
                      </div>
                    </div>
                  ) : socialResearchSessions?.error ? (
                    <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: 'var(--color-danger)' }}>
                      {socialResearchSessions.error}
                    </p>
                  ) : null}
                  <div style={{ display: 'grid', gap: '0.45rem', marginTop: '0.75rem' }}>
                    {(['linkedin', 'facebook', 'instagram'] as const).map((platform) => {
                      const row = socialResearchSessions?.platforms?.[platform];
                      const live = Boolean(row?.logged_in);
                      const connectStatus = socialResearchSessions?.connect?.[platform]?.status;
                      // Prefer Live when cookies already say logged_in — a stuck connect job
                      // must not keep the row on Connecting forever.
                      const connecting = !live && (
                        socialResearchConnectPlatform === platform
                        || connectStatus === 'connecting'
                      );
                      const browserUnavailable = socialResearchSessions?.ok === false
                        || socialResearchSessions?.available === false;
                      const statusPending = !socialResearchSessions && (socialResearchSessionsLoading || Boolean(accessToken));
                      return (
                        <div key={platform} style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '0.88rem', color: 'var(--color-text-strong)' }}>
                            {platform === 'linkedin' ? 'LinkedIn' : platform === 'facebook' ? 'Facebook' : 'Instagram'}
                            {' · '}
                            {browserUnavailable
                              ? 'Unavailable'
                              : statusPending
                                ? 'Checking…'
                                : connecting
                                  ? 'Connecting…'
                                  : live
                                    ? 'Live'
                                    : 'Needs connect'}
                            {row?.email ? ` · ${row.email}` : ''}
                          </span>
                          <button
                            type="button"
                            disabled={!accessToken || connecting || browserUnavailable}
                            onClick={() => { void connectSocialResearchSession(platform); }}
                            title={browserUnavailable
                              ? 'Research browser service is down in this environment'
                              : undefined}
                            style={{
                              padding: '0.4rem 0.8rem',
                              borderRadius: '18px',
                              border: '1px solid var(--color-border-strong)',
                              background: 'var(--color-surface-muted)',
                              color: 'var(--color-text-strong)',
                              fontSize: '0.82rem',
                              cursor: accessToken && !connecting && !browserUnavailable ? 'pointer' : 'not-allowed',
                              opacity: browserUnavailable ? 0.55 : 1,
                            }}
                          >
                            {browserUnavailable ? 'Unavailable' : connecting ? 'Waiting…' : live ? 'Reconnect' : 'Connect'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                    <button
                      type="button"
                      onClick={() => { void loadSocialResearchSessions(); }}
                      disabled={socialResearchSessionsLoading}
                      style={{
                        padding: '0.4rem 0.8rem',
                        borderRadius: '18px',
                        border: '1px solid var(--color-border-strong)',
                        background: 'var(--color-surface)',
                        color: 'var(--color-text-strong)',
                        fontSize: '0.82rem',
                        cursor: socialResearchSessionsLoading ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {socialResearchSessionsLoading ? 'Refreshing…' : 'Refresh status'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setSocialResearchLiveOpen((open) => !open); }}
                      style={{
                        padding: '0.4rem 0.8rem',
                        borderRadius: '18px',
                        border: '1px solid var(--color-border-strong)',
                        background: 'var(--color-surface)',
                        color: 'var(--color-text-strong)',
                        fontSize: '0.82rem',
                        cursor: 'pointer',
                      }}
                    >
                      {socialResearchLiveOpen ? 'Hide live view' : 'Show live view'}
                    </button>
                  </div>
                  {socialResearchLiveOpen && socialResearchSessions?.live_url ? (
                    <iframe
                      title="Research browser live view"
                      src={socialResearchSessions.live_url}
                      style={{ width: '100%', height: '520px', marginTop: '0.75rem', border: '1px solid var(--color-border)', borderRadius: '8px', background: '#111' }}
                    />
                  ) : null}
                </div>
                {renderSocialSecretCard(
                  socialResearchSecrets.find((item) => item.id === 'workmail_social')
                  || {
                    id: 'workmail_social',
                    kind: 'mailbox',
                    label: 'Research mailbox (Google Workspace)',
                    email: 'social@nextcompli.com',
                    username: 'social',
                    password_configured: false,
                    source: 'Google Workspace via NextCompliMCP',
                    note: 'OTP and research mail for social@nextcompli.com go through Google Workspace. An IMAP password is not required for Socials Connect.',
                  },
                )}
                <div style={{ border: '1px solid var(--color-border)', borderRadius: '10px', background: 'var(--color-surface)', padding: '0.55rem 0.9rem' }}>
                  <button
                    type="button"
                    aria-expanded={vantageResearchSectionOpen}
                    aria-controls="vantage-research-platforms-panel"
                    onClick={() => { setVantageResearchSectionOpen((open) => !open); }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '0.75rem',
                      padding: '0.35rem 0',
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--color-text-strong)',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.03em' }}>
                      Vantage research platforms
                    </span>
                    <span aria-hidden="true" style={{ fontSize: '1.1rem', fontWeight: 700 }}>
                      {vantageResearchSectionOpen ? '−' : '+'}
                    </span>
                  </button>
                  {vantageResearchSectionOpen ? (
                    <div id="vantage-research-platforms-panel" style={{ paddingBottom: '0.35rem' }}>
                      <p style={{ margin: '0.15rem 0 0', fontSize: '0.82rem', color: 'var(--color-text-muted)', lineHeight: 1.45 }}>
                        Use the checkbox on each account-status row below to choose which platforms Vantage queries for this company.
                        Off platforms are skipped during person and company research.
                      </p>
                      {vantageSocialPlatformsError ? (
                        <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: 'var(--color-danger)' }}>
                          {vantageSocialPlatformsError}
                        </p>
                      ) : null}
                      {!vantageSocialPlatforms && vantageSocialPlatformsLoading ? (
                        <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>Loading platforms…</p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {socialResearchSecretsError && (
              <p style={{ margin: '0.75rem 0 0', fontSize: '0.82rem', color: 'var(--color-danger)' }}>
                Social password error: {socialResearchSecretsError}
              </p>
            )}

            {socialAccessAuditError && (
              <p style={{ margin: '0.75rem 0 0', fontSize: '0.82rem', color: 'var(--color-danger)' }}>
                Access audit error: {socialAccessAuditError}
              </p>
            )}

            {socialAccessAuditRunning && (
              <div
                className="dashboard-social-audit-overlay"
                role="status"
                aria-live="polite"
                aria-busy="true"
              >
                <div className="dashboard-social-audit-loader-card">
                  <span className="dashboard-social-audit-loader-spinner" aria-hidden="true" />
                  <div className="dashboard-social-audit-loader-copy">
                    <strong>Auditing social access</strong>
                    <span>Refreshing platform status and access verification.</span>
                  </div>
                </div>
              </div>
            )}

            <p style={{ margin: '0.6rem 0 0', fontSize: '0.8rem', color: 'var(--color-text-muted)', lineHeight: 1.45 }}>
              Staff this means we post there (LinkedIn, Facebook, Instagram, YouTube, and X).
              Support means we keep the account and check it, without a daily posting desk.
              Reserved means the handle is claimed and we do not post there daily.
              Do not expand means we do not open a new campaign or publication.
              The checkbox is Vantage search. It stays on for every account, including reserved and do-not-expand rows.
            </p>
            {socialAccessAuditCriteria && (
              <p style={{ margin: '0.6rem 0 0', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                {socialAccessAuditCriteria.notes || socialAccessAuditCriteria.working || socialAccessAuditCriteria.configured || ''}
              </p>
            )}

            <div style={{ marginTop: '1rem', display: 'grid', gap: '0.65rem' }}>
              {socialPlatforms.map((platform) => {
                const isOpen = openSocialPlatform === platform.key;
                const trafficSignal = getPlatformTrafficSignal(platform.key, platform.status);
                const trafficSignalTone = getTrafficSignalTone(trafficSignal.label);
                const platformIdentity = getPlatformIdentity(platform.key);
                const vantageRow = (vantageSocialPlatforms?.platforms || []).find((row) => row.id === platform.key);
                const vantageEnabled = vantageRow ? Boolean(vantageRow.enabled) : true;
                return (
                  <div key={platform.key} style={{ display: 'grid', gap: '0.65rem' }}>
                    <div className={`dashboard-social-platform-row${isOpen ? ' is-open' : ''}`}>
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={`social-platform-panel-${platform.key}`}
                        onClick={() => { handleSocialPlatformToggle(platform.key); }}
                        className="dashboard-social-platform-row-main"
                      >
                        <span className="dashboard-social-platform-title">
                          {platform.title}
                          {socialAccessAuditByPlatform[platform.key]?.cadence === 'core' ? ' · staff this' : ''}
                          {socialAccessAuditByPlatform[platform.key]?.cadence === 'support' ? ' · support' : ''}
                          {socialAccessAuditByPlatform[platform.key]?.cadence === 'reserved' ? ' · reserved' : ''}
                          {socialAccessAuditByPlatform[platform.key]?.cadence === 'skip' ? ' · do not expand' : ''}
                        </span>
                        <span className={`dashboard-social-platform-identity${platformIdentity ? '' : ' is-empty'}`}>
                          {platformIdentity || ' '}
                        </span>
                        <span className="dashboard-social-platform-meta">
                          <span
                            className={`dashboard-social-platform-status tone-${trafficSignalTone}`}
                            title={platform.status}
                            aria-label={`${platform.title} status ${trafficSignal.label}. Detail: ${platform.status}`}
                          >
                            <span className="dashboard-social-platform-status-dot" aria-hidden="true" />
                            {trafficSignal.label}
                          </span>
                          <span className="dashboard-social-platform-toggle">{isOpen ? '−' : '+'}</span>
                        </span>
                      </button>
                      <label
                        className="dashboard-social-platform-vantage"
                        title="Include this platform in Vantage research"
                        onClick={(event) => { event.stopPropagation(); }}
                        onKeyDown={(event) => { event.stopPropagation(); }}
                      >
                        <input
                          type="checkbox"
                          checked={vantageEnabled}
                          disabled={!accessToken || vantageSocialPlatformsLoading || vantageSocialPlatformsSaving}
                          onChange={(event) => {
                            void saveVantageSocialPlatformToggle(platform.key, event.target.checked);
                          }}
                          aria-label={`Use ${platform.title} in Vantage research`}
                        />
                      </label>
                    </div>

                    {isOpen && (
                      <div
                        id={`social-platform-panel-${platform.key}`}
                        role="region"
                        aria-label={`${platform.title} settings`}
                        style={{
                          border: '1px solid var(--color-border)',
                          borderRadius: '14px',
                          background: 'var(--color-surface-muted)',
                          padding: '1rem',
                        }}
                      >
                        <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: '0.88rem' }}>{platform.description}</p>
                        {renderAuditEvidence(platform.key)}

                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                          {platform.key === 'facebook' && (
                            <a
                              href={facebookPublicPageUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                padding: '0.35rem 0.9rem',
                                borderRadius: '20px',
                                border: '1px solid var(--color-border-strong)',
                                color: 'var(--color-text-strong)',
                                fontSize: '0.88rem',
                                textDecoration: 'none',
                              }}
                            >
                              Open Page ↗
                            </a>
                          )}
                          {(platform.key === 'facebook'
                            ? (['analytics', 'post', 'configuration'] as const)
                            : (['analytics', 'configuration'] as const)
                          ).map((tab) => (
                            <button
                              key={tab}
                              type="button"
                              onClick={() => { setSocialsTab(tab); }}
                              style={{
                                padding: '0.35rem 0.9rem',
                                borderRadius: '20px',
                                border: '1px solid',
                                borderColor: socialsTab === tab ? 'var(--color-primary)' : 'var(--color-border-strong)',
                                background: socialsTab === tab ? 'var(--color-primary)' : 'var(--color-surface)',
                                color: socialsTab === tab ? 'var(--color-surface)' : 'var(--color-text-strong)',
                                fontWeight: socialsTab === tab ? 600 : 400,
                                cursor: 'pointer',
                                fontSize: '0.88rem',
                                textTransform: 'capitalize',
                              }}
                            >
                              {tab === 'post' ? 'Compose & Posts' : tab === 'analytics' ? 'Analytics' : 'Configuration'}
                            </button>
                          ))}
                        </div>

                        {platform.key === 'facebook' ? (
                          !fbConfigured ? (
                            renderSocialConfigurationTab('facebook')
                          ) : socialsTab === 'post' ? (
                            <>
                              <form onSubmit={(e) => { void handlePost(e); }} style={{ margin: '1.5rem 0' }}>
                                <h3 style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '1.05rem' }}>Compose Post</h3>
                                <textarea
                                  value={socialsPostText}
                                  onChange={(e) => { setSocialsPostText(e.target.value); setSocialsPostStatus('idle'); }}
                                  placeholder="Write your post content..."
                                  disabled={socialsPostStatus === 'posting'}
                                  maxLength={63206}
                                  rows={4}
                                  style={{ width: '100%', padding: '0.75rem', fontSize: '0.95rem', fontFamily: 'inherit', border: '1px solid var(--color-border)', borderRadius: '6px', resize: 'vertical', boxSizing: 'border-box' }}
                                />
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
                                  <div>
                                    <label style={{ display: 'block', fontSize: '0.82rem', color: 'var(--color-text-muted)', marginBottom: '0.3rem' }}>Link URL (creates link preview)</label>
                                    <input
                                      type="url"
                                      value={socialsPostLink}
                                      onChange={(e) => { setSocialsPostLink(e.target.value); setSocialsPostStatus('idle'); }}
                                      placeholder="https://example.com"
                                      disabled={socialsPostStatus === 'posting' || Boolean(socialsPostImageUrl)}
                                      style={{ width: '100%', padding: '0.5rem 0.75rem', fontSize: '0.9rem', border: '1px solid var(--color-border)', borderRadius: '6px', boxSizing: 'border-box' }}
                                    />
                                  </div>
                                  <div>
                                    <label style={{ display: 'block', fontSize: '0.82rem', color: 'var(--color-text-muted)', marginBottom: '0.3rem' }}>Image URL (posts as photo)</label>
                                    <input
                                      type="url"
                                      value={socialsPostImageUrl}
                                      onChange={(e) => { setSocialsPostImageUrl(e.target.value); setSocialsPostStatus('idle'); }}
                                      placeholder="https://example.com/image.jpg"
                                      disabled={socialsPostStatus === 'posting' || Boolean(socialsPostLink)}
                                      style={{ width: '100%', padding: '0.5rem 0.75rem', fontSize: '0.9rem', border: '1px solid var(--color-border)', borderRadius: '6px', boxSizing: 'border-box' }}
                                    />
                                  </div>
                                </div>
                                {socialsPostImageUrl && (
                                  <div style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: 'var(--color-text-muted)', background: 'var(--color-primary-soft)', padding: '0.4rem 0.75rem', borderRadius: '4px' }}>
                                    Photo post mode — image URL will be fetched by Facebook. Text above becomes the caption.
                                  </div>
                                )}
                                {socialsPostLink && !socialsPostImageUrl && (
                                  <div style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: 'var(--color-text-muted)', background: 'color-mix(in srgb, var(--color-success) 14%, var(--color-surface))', padding: '0.4rem 0.75rem', borderRadius: '4px' }}>
                                    Link post mode — Facebook will generate a preview card for the URL.
                                  </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem', gap: '1rem' }}>
                                  <button
                                    type="submit"
                                    disabled={socialsPostStatus === 'posting' || (!socialsPostText.trim() && !socialsPostImageUrl.trim())}
                                    className="dashboard-btn dashboard-btn-primary"
                                  >
                                    {socialsPostStatus === 'posting' ? 'Posting...' : 'Post to Facebook'}
                                  </button>
                                  <span style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{socialsPostText.length.toLocaleString()} / 63,206</span>
                                </div>
                                {socialsPostStatus === 'success' && (
                                  <p style={{ color: 'var(--color-success)', marginTop: '0.5rem', fontSize: '0.9rem' }}>✓ Posted successfully</p>
                                )}
                                {socialsPostStatus === 'error' && socialsPostError && (
                                  <p style={{ color: 'var(--color-danger)', marginTop: '0.5rem', fontSize: '0.9rem' }}>Error: {socialsPostError}</p>
                                )}
                              </form>

                              <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1.5rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                  <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Recent Posts</h3>
                                  <button
                                    type="button"
                                    onClick={() => { setSocialsPageLoaded(false); setSocialsRecentPosts([]); }}
                                    style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: '6px', padding: '0.3rem 0.7rem', fontSize: '0.82rem', cursor: 'pointer', color: 'var(--color-text-muted)' }}
                                  >
                                    Refresh
                                  </button>
                                </div>
                                {!socialsPageLoaded ? (
                                  <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Loading posts...</p>
                                ) : socialsRecentPosts.length === 0 ? (
                                  <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>No recent posts found.</p>
                                ) : (
                                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    {socialsRecentPosts.map((post) => {
                                      const thumb = post.attachments?.data?.[0]?.media?.image?.src;
                                      return (
                                        <li key={post.id} style={{ padding: '0.75rem 1rem', background: 'var(--color-surface-muted)', border: '1px solid var(--color-border)', borderRadius: '8px', display: 'flex', gap: '0.75rem' }}>
                                          {thumb && (
                                            <img src={thumb} alt="" style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
                                          )}
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <p style={{ margin: '0 0 0.4rem', fontSize: '0.95rem', wordBreak: 'break-word' }}>{post.message || '[No text]'}</p>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: 'var(--color-text-muted)', flexWrap: 'wrap', gap: '0.25rem' }}>
                                              <time>{post.created_time ? new Date(post.created_time).toLocaleString() : ''}</time>
                                              {post.permalink_url && (
                                                <a href={post.permalink_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>View on Facebook ↗</a>
                                              )}
                                            </div>
                                          </div>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                              </div>
                            </>
                          ) : socialsTab === 'analytics' ? (
                            !socialsInsightsLoaded ? (
                              <p style={{ marginTop: '1rem', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Loading analytics...</p>
                            ) : renderSocialAnalyticsTab('facebook')
                          ) : (
                            renderSocialConfigurationTab('facebook')
                          )
                        ) : (
                          socialsTab === 'analytics' ? renderSocialAnalyticsTab(platform.key) : renderSocialConfigurationTab(platform.key)
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </article>
        </section>
      );
    }

    if (activeNavItem === 'crm') {
      if (!canAccessCrm) {
        return renderHomePane();
      }
      return (
        <PursuitWorkspace
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          language={language}
          onAccessDenied={denyStaffNav}
          onSignIn={() => {
            logout();
            navigate('/login');
          }}
        />
      );
    }

    if (activeNavItem === 'inlet') {
      if (!canAccessInlet) {
        return renderHomePane();
      }
      return (
        <InletWorkspace
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          language={language}
          onAccessDenied={denyStaffNav}
          onSignIn={() => {
            logout();
            navigate('/login');
          }}
        />
      );
    }

    if (activeNavItem === 'razzmatazz') {
      if (!canAccessRazzmatazz) {
        return renderHomePane();
      }
      return (
        <RazzmatazzWorkspace
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={accessToken}
          language={language}
          scopeChrome="minimal"
          filmDesk={hasSuperUserAccess}
          onAccessDenied={denyStaffNav}
          onSignIn={() => {
            logout();
            navigate('/login');
          }}
        />
      );
    }

    if (activeNavItem === 'admin' || activeNavItem === 'costOptimization') {
      if (!hasSuperUserAccess) {
        return renderHomePane();
      }

      return <AdminConsolePage key={`${location.pathname}:${location.search}:${String((location.state as { adminResetNonce?: number } | null)?.adminResetNonce || '')}`} />;
    }

    return renderHomePane();
  };

  return (
    <div className="dashboard dashboard-account-style">
      {researchIdentifierConfirmation ? (
        <div
          className="dashboard-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="research-identifier-confirmation-title"
          onClick={() => setResearchIdentifierConfirmation(null)}
        >
          <div className="dashboard-report-preview-modal" onClick={(event) => event.stopPropagation()}>
            <div className="dashboard-report-preview-header">
              <h3 id="research-identifier-confirmation-title">
                {language === 'es' ? 'Confirmar identificadores' : 'Confirm identifiers'}
              </h3>
              <div className="dashboard-action-buttons">
                <button type="button" className="dashboard-security-inline-btn" onClick={() => setResearchIdentifierConfirmation(null)}>
                  {language === 'es' ? 'Cerrar' : 'Close'}
                </button>
              </div>
            </div>
            <div className="dashboard-report-preview-content dashboard-report-preview-content-medium">
              <p>
                {researchIdentifierConfirmation.guidance
                  || (language === 'es'
                    ? 'Se requieren al menos dos identificadores (nombre legal completo más un dato secundario) antes de ejecutar verificaciones completas.'
                    : 'At least two identifiers (full legal name plus one secondary item) are required before running full background checks.')}
              </p>
              {Array.isArray(researchIdentifierConfirmation.warnings) && researchIdentifierConfirmation.warnings.length > 0 ? (
                <ul style={{ margin: '12px 0', paddingLeft: '20px', color: 'color-mix(in srgb, #f59e0b 75%, var(--color-text-strong))' }}>
                  {researchIdentifierConfirmation.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              ) : null}
              {researchIdentifierConfirmation.document_attestation?.document_type ? (
                <p style={{ marginTop: '12px' }}>
                  <strong>{language === 'es' ? 'Documento:' : 'Document:'}</strong>{' '}
                  {researchIdentifierConfirmation.document_attestation.document_type}
                  {researchIdentifierConfirmation.document_attestation.treat_parsed_vitals_as_attested
                    ? (language === 'es'
                      ? ' — los datos vitales están atestiguados por el solicitante (solicitud inmobiliaria firmada).'
                      : ' — applicant-attested vitals (signed realtor application). Treat as intake truth; still corroborate independently.')
                    : ''}
                </p>
              ) : null}
              {researchIdentifierConfirmation.primary_identifier?.value ? (
                <p>
                  <strong>{language === 'es' ? 'Nombre principal:' : 'Primary name:'}</strong>{' '}
                  {researchIdentifierConfirmation.primary_identifier.value}
                </p>
              ) : null}
              {Array.isArray(researchIdentifierConfirmation.confirmable_facts) && researchIdentifierConfirmation.confirmable_facts.length > 0 ? (
                <div style={{ marginTop: '12px' }}>
                  <strong>{language === 'es' ? 'Hechos detectados para confirmar' : 'Parsed facts to confirm'}</strong>
                  <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                    {researchIdentifierConfirmation.confirmable_facts.map((fact) => (
                      <li key={`${fact.key}-${fact.label}`}>
                        {fact.label}: {maskIdentifierValue(fact.value, fact.sensitive)}
                        {fact.attested
                          ? (language === 'es' ? ' (atestiguado)' : ' (attested)')
                          : ''}
                        {typeof fact.confidence === 'number'
                          ? ` (${Math.round(fact.confidence * 100)}% ${language === 'es' ? 'confianza' : 'confidence'})`
                          : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {Array.isArray(researchIdentifierConfirmation.missing_secondary_identifiers)
                && researchIdentifierConfirmation.missing_secondary_identifiers.length > 0 ? (
                <div style={{ marginTop: '12px' }}>
                  <strong>{language === 'es' ? 'Identificadores secundarios faltantes' : 'Missing secondary identifiers'}</strong>
                  <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                    {researchIdentifierConfirmation.missing_secondary_identifiers.map((item) => (
                      <li key={item.id}>
                        <strong>{item.label}</strong>
                        {item.description ? ` — ${item.description}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {researchIdentifierConfirmation.standards_reference ? (
                <p style={{ marginTop: '12px', color: 'var(--color-text-muted)', fontSize: '0.86rem' }}>
                  {researchIdentifierConfirmation.standards_reference}
                </p>
              ) : null}
              <div className="dashboard-action-buttons" style={{ marginTop: '16px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="dashboard-btn"
                  onClick={() => {
                    setResearchIdentifierConfirmation(null);
                    setActiveResearchTab('research');
                    focusResearchKeyDataIntake();
                  }}
                >
                  {language === 'es' ? 'Agregar identificadores' : 'Add identifiers'}
                </button>
                <button
                  type="button"
                  className="dashboard-btn"
                  onClick={async () => {
                    setResearchIdentifierConfirmation(null);
                    await enqueueResearchReport({ identifierConfirmed: true });
                  }}
                >
                  {language === 'es' ? 'Confirmar y ejecutar' : 'Confirm and run'}
                </button>
                <button
                  type="button"
                  className="dashboard-security-inline-btn"
                  onClick={async () => {
                    setResearchIdentifierConfirmation(null);
                    await enqueueResearchReport({ proceedWithoutIdentifiers: true });
                  }}
                >
                  {language === 'es' ? 'Continuar de todos modos' : 'Proceed anyway'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {researchQueueAck ? (
        <div className="dashboard-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="research-queue-ack-title" onClick={() => setResearchQueueAck(null)}>
          <div className="dashboard-report-preview-modal" onClick={(event) => event.stopPropagation()}>
            <div className="dashboard-report-preview-header">
              <h3 id="research-queue-ack-title">{language === 'es' ? 'Reporte agregado a la cola' : 'Report added to queue'}</h3>
              <div className="dashboard-action-buttons">
                <button type="button" className="dashboard-security-inline-btn" onClick={() => setResearchQueueAck(null)}>
                  {language === 'es' ? 'Cerrar' : 'Close'}
                </button>
              </div>
            </div>
            <div className="dashboard-report-preview-content dashboard-report-preview-content-medium">
              <p>{researchQueueAck}</p>
              <p>
                {language === 'es'
                  ? 'Puedes seguir el progreso en la pestaña Cola de reportes Vantage.'
                  : 'You can track progress in the Vantage Report Queue tab.'}
              </p>
            </div>
          </div>
        </div>
      ) : null}
      {(isResearchPreviewExpanded && selectedResearchReport) ? (
        <div
          className="dashboard-report-preview-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="research-preview-dialog-title"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closeResearchPreviewExpanded();
            }
          }}
        >
          <div className="dashboard-report-preview-dialog">
            <div className="dashboard-report-preview-header">
              <h3 id="research-preview-dialog-title">
                {language === 'es' ? 'Vista completa del reporte' : 'Full Report View'}: {resolveResearchReportDisplayTitle(selectedResearchReport)}
                {formatResearchSearchDurationLabel(selectedResearchReport, language)
                  ? ` · ${formatResearchSearchDurationLabel(selectedResearchReport, language)}`
                  : ''}
              </h3>
              <div className="dashboard-action-buttons">
                <div className="dashboard-report-preview-font-controls" role="group" aria-label={language === 'es' ? 'Tamaño de texto' : 'Text size'}>
                  <button
                    type="button"
                    className={`dashboard-security-inline-btn${researchPreviewFontSize === 'small' ? ' active' : ''}`}
                    onClick={() => setResearchPreviewFontSize('small')}
                  >
                    {language === 'es' ? 'Pequeño' : 'Small'}
                  </button>
                  <button
                    type="button"
                    className={`dashboard-security-inline-btn${researchPreviewFontSize === 'medium' ? ' active' : ''}`}
                    onClick={() => setResearchPreviewFontSize('medium')}
                  >
                    {language === 'es' ? 'Mediano' : 'Medium'}
                  </button>
                  <button
                    type="button"
                    className={`dashboard-security-inline-btn${researchPreviewFontSize === 'large' ? ' active' : ''}`}
                    onClick={() => setResearchPreviewFontSize('large')}
                  >
                    {language === 'es' ? 'Grande' : 'Large'}
                  </button>
                </div>
                {selectedResearchReport.isFallback ? (
                  <button
                    type="button"
                    className="dashboard-security-inline-btn"
                    disabled={isResearchLoading}
                    onClick={() => { void handleResearchReportRetry(selectedResearchReport.id); }}
                  >
                    {language === 'es' ? 'Reintentar' : 'Retry'}
                  </button>
                ) : null}
                <div className="dashboard-report-rerun-group dashboard-report-rerun-group--preview">
                  <label
                    className="dashboard-report-rerun-control"
                    htmlFor={`${selectedResearchReport.id}-preview-affinity-level`}
                  >
                    <span className="dashboard-report-rerun-label-text">
                      {getResearchAffinityLevelLabel(language)}
                    </span>
                    <select
                      id={`${selectedResearchReport.id}-preview-affinity-level`}
                      className="dashboard-report-rerun-select"
                      value={String(researchPreviewSelectedRerunDepth)}
                      onChange={(event) => {
                        const parsed = Number(event.target.value);
                        setResearchPreviewSelectedRerunDepth(Number.isFinite(parsed) ? Math.max(1, Math.min(4, parsed)) : 1);
                      }}
                      disabled={isResearchLoading}
                      aria-describedby={`${selectedResearchReport.id}-preview-affinity-level-help`}
                    >
                      {RESEARCH_AFFINITY_DEPTH_OPTIONS.map((depth) => (
                        <option key={`${selectedResearchReport.id}-preview-affinity-${depth}`} value={depth}>
                          {depth}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="dashboard-security-inline-btn dashboard-report-action-btn dashboard-report-action-btn--rerun"
                    disabled={isResearchLoading}
                    onClick={() => {
                      void handleResearchReportRerun(selectedResearchReport.id, {
                        affinity_depth: researchPreviewSelectedRerunDepth,
                      });
                    }}
                  >
                    {language === 'es' ? 'Relanzar' : 'Rerun'}
                  </button>
                  <p
                    id={`${selectedResearchReport.id}-preview-affinity-level-help`}
                    className="dashboard-report-rerun-help dashboard-report-rerun-help--visible"
                  >
                    {getResearchAffinityLevelHelp(language)}
                  </p>
                </div>
                {selectedResearchReport.factDatabase ? (
                  isResearchPreviewFactEditing ? (
                    <>
                      <label
                        className="dashboard-report-rerun-control"
                        htmlFor={`${selectedResearchReport.id}-save-rerun-affinity-level`}
                        title={getResearchAffinityLevelHelp(language)}
                      >
                        <span className="dashboard-report-rerun-label-text">
                          {getResearchAffinityLevelLabel(language)}
                        </span>
                        <select
                          id={`${selectedResearchReport.id}-save-rerun-affinity-level`}
                          className="dashboard-report-rerun-select"
                          value={String(researchPreviewSelectedRerunDepth)}
                          onChange={(event) => {
                            const parsed = Number(event.target.value);
                            setResearchPreviewSelectedRerunDepth(Number.isFinite(parsed) ? Math.max(1, Math.min(4, parsed)) : 1);
                          }}
                          disabled={isResearchLoading}
                        >
                          {RESEARCH_AFFINITY_DEPTH_OPTIONS.map((depth) => (
                            <option key={`${selectedResearchReport.id}-save-rerun-depth-${depth}`} value={depth}>
                              {depth}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        className="dashboard-security-inline-btn"
                        disabled={isResearchLoading}
                        onClick={() => { void handleResearchPreviewFactSaveAndRerun(); }}
                      >
                        {language === 'es' ? 'Guardar y relanzar' : 'Save + Rerun'}
                      </button>
                      <button
                        type="button"
                        className="dashboard-security-inline-btn"
                        onClick={handleResearchPreviewFactSave}
                      >
                        {language === 'es' ? 'Guardar hechos' : 'Save Facts'}
                      </button>
                      <button
                        type="button"
                        className="dashboard-security-inline-btn"
                        onClick={handleResearchPreviewFactEditCancel}
                      >
                        {language === 'es' ? 'Cancelar edición' : 'Cancel Edit'}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="dashboard-security-inline-btn"
                      onClick={handleResearchPreviewFactEditStart}
                    >
                      {language === 'es' ? 'Editar hechos' : 'Edit Facts'}
                    </button>
                  )
                ) : null}
                        <button type="button" className="dashboard-security-inline-btn" disabled={isResearchPdfGenerating} onClick={() => { void openResearchReportPdf(selectedResearchReport); }}>
                          {language === 'es' ? 'PDF' : 'PDF'}
                        </button>
                <button
                  type="button"
                  className="dashboard-security-inline-btn"
                  disabled={isResearchPdfGenerating}
                  onClick={() => { void openResearchReportPdf(selectedResearchReport, { downloadOnly: true }); }}
                >
                  {language === 'es' ? 'Descargar PDF' : 'Download PDF'}
                </button>
                <button
                  ref={researchPreviewCloseButtonRef}
                  type="button"
                  className="dashboard-security-inline-btn"
                  onClick={closeResearchPreviewExpanded}
                >
                  {language === 'es' ? 'Cerrar' : 'Close'}
                </button>
              </div>
            </div>
            <div className={`dashboard-report-preview-content dashboard-report-preview-content-${researchPreviewFontSize}`}>
              {selectedResearchReport.isFallback ? (
                <p className="dashboard-form-error" style={{ marginBottom: '10px' }}>
                  {language === 'es'
                    ? `Borrador local: el servicio de reportes no estuvo disponible${selectedResearchReport.fallbackReason ? ` (${selectedResearchReport.fallbackReason})` : ''}.`
                    : `Local draft: the report service was unavailable${selectedResearchReport.fallbackReason ? ` (${selectedResearchReport.fallbackReason})` : ''}.`}
                </p>
              ) : null}
              {(selectedResearchReport.riskScore != null || selectedResearchReport.matchCount != null) ? (
                <div className="dashboard-research-risk-banner">
                  {selectedResearchReport.riskScore != null ? (
                    <span>
                      <strong>{language === 'es' ? 'Puntuación de riesgo:' : 'Risk score:'}</strong>{' '}
                      {Number(selectedResearchReport.riskScore).toFixed(1)}
                    </span>
                  ) : null}
                  {selectedResearchReport.matchCount != null ? (
                    <span>
                      <strong>{language === 'es' ? 'Coincidencias:' : 'Matches:'}</strong>{' '}
                      {selectedResearchReport.matchCount}
                    </span>
                  ) : null}
                  <span className="dashboard-research-risk-banner-note">
                    {language === 'es'
                      ? 'Basado en señales de screening y coincidencias de listas; use Findings para el detalle.'
                      : 'Based on screening signals and list matches; see Findings for detail.'}
                  </span>
                </div>
              ) : null}
              {(() => {
                const reportPhotos = collectIdentityPhotosFromReport(selectedResearchReport, {
                  mediaBaseUrl: gatewayBaseUrl,
                  accessToken: accessToken || undefined,
                });
                const photoNotes = collectReportPhotoNotes(selectedResearchReport);
                const personReport = selectedResearchReport.targetType === 'person';
                if (reportPhotos.length === 0 && photoNotes.length === 0 && !personReport) return null;
                const photoEmpty = personReport && reportPhotos.length > 0 && photoNotes.length === 0
                  ? (language === 'es'
                    ? 'Este reporte no adjuntó características físicas ni comparaciones faciales.'
                    : 'This report did not attach physical characteristics or facial matches.')
                  : '';
                if (!reportPhotos.length && !photoNotes.length && !photoEmpty) return null;
                return (
                  <>
                    {reportPhotos.length ? (
                      <ResearchReportPhotoStrip photos={reportPhotos} language={language} />
                    ) : null}
                    {photoNotes.length || photoEmpty ? (
                      <details data-report-section open className="dashboard-report-section" style={{ marginBottom: '12px' }}>
                        <summary className="dashboard-report-section-summary">
                          {language === 'es' ? 'Análisis de fotos' : 'Photo analysis'}
                        </summary>
                        <div className="dashboard-report-section-body">
                          {photoNotes.map((note) => (
                            <p key={`${selectedResearchReport.id}-photo-${note}`} className="dashboard-bio-para">{note}</p>
                          ))}
                          {photoEmpty ? (
                            <p className="dashboard-bio-para">{photoEmpty}</p>
                          ) : null}
                        </div>
                      </details>
                    ) : null}
                  </>
                );
              })()}
              {(() => {
                const courtRows = collectReportCourtRows(selectedResearchReport);
                const familyRows = collectReportFamilyRows(selectedResearchReport);
                const contactRows = collectReportContactRows(selectedResearchReport);
                const affiliationRows = collectReportAffiliationRows(selectedResearchReport);
                if (!courtRows.length && !familyRows.length && !contactRows.length && !affiliationRows.length) return null;
                return (
                  <div className="dashboard-report-sections" style={{ marginBottom: '12px' }}>
                    {courtRows.length ? (
                      <details data-report-section open className="dashboard-report-section">
                        <summary className="dashboard-report-section-summary">
                          {language === 'es' ? 'Registros judiciales' : 'Court records'}
                        </summary>
                        <div className="dashboard-report-section-body">
                          {courtRows.map((row) => (
                            <p key={`${selectedResearchReport.id}-court-${row.title}`} className="dashboard-bio-para">
                              <strong>{row.title}</strong>
                              {row.detail ? ` — ${row.detail}` : ''}
                            </p>
                          ))}
                        </div>
                      </details>
                    ) : null}
                    {contactRows.length ? (
                      <details data-report-section open className="dashboard-report-section">
                        <summary className="dashboard-report-section-summary">
                          {language === 'es' ? 'Contacto' : 'Contact'}
                        </summary>
                        <div className="dashboard-report-section-body">
                          {contactRows.map((row) => (
                            <p key={`${selectedResearchReport.id}-contact-${row.label}-${row.value}`} className="dashboard-bio-para">
                              <strong>{row.label}:</strong> {row.value}
                            </p>
                          ))}
                        </div>
                      </details>
                    ) : null}
                    {affiliationRows.length ? (
                      <details data-report-section open className="dashboard-report-section">
                        <summary className="dashboard-report-section-summary">
                          {language === 'es' ? 'Iglesia, pasatiempos y grupos' : 'Church, hobbies, and groups'}
                        </summary>
                        <div className="dashboard-report-section-body">
                          {affiliationRows.map((row) => (
                            <p key={`${selectedResearchReport.id}-affil-${row.kind}-${row.value}`} className="dashboard-bio-para">
                              <strong>{row.kind}:</strong> {row.value}
                            </p>
                          ))}
                        </div>
                      </details>
                    ) : null}
                    {familyRows.length ? (
                      <details data-report-section open className="dashboard-report-section">
                        <summary className="dashboard-report-section-summary">
                          {language === 'es' ? 'Familia' : 'Family'}
                        </summary>
                        <div className="dashboard-report-section-body">
                          {familyRows.map((row) => (
                            <p key={`${selectedResearchReport.id}-family-${row.name}`} className="dashboard-bio-para">
                              <strong>{row.relation}:</strong> {row.name}
                            </p>
                          ))}
                        </div>
                      </details>
                    ) : null}
                  </div>
                );
              })()}
              {(() => {
                const rawContent = String(selectedResearchReport.content || '').replace(/\r\n?/g, '\n');
                const sections = splitResearchReportSections(rawContent, language);

                return (
                  <div className="dashboard-report-sections">
                    <div className="dashboard-action-buttons dashboard-report-sections-toolbar">
                      <button
                        type="button"
                        className="dashboard-security-inline-btn"
                        onClick={(event) => {
                          const container = event.currentTarget.closest('.dashboard-report-preview-content');
                          if (!container) return;
                          container.querySelectorAll('details[data-report-section]').forEach((node) => {
                            node.setAttribute('open', '');
                          });
                        }}
                      >
                        {language === 'es' ? 'Expandir todo' : 'Expand all'}
                      </button>
                      <button
                        type="button"
                        className="dashboard-security-inline-btn"
                        onClick={(event) => {
                          const container = event.currentTarget.closest('.dashboard-report-preview-content');
                          if (!container) return;
                          container.querySelectorAll('details[data-report-section]').forEach((node) => {
                            node.removeAttribute('open');
                          });
                        }}
                      >
                        {language === 'es' ? 'Contraer todo' : 'Collapse all'}
                      </button>
                    </div>
                    {sections
                      .filter((section) => !/^(photos|fotos)$/i.test(section.title))
                      .map((section, index) => (
                      <details
                        data-report-section
                        key={`${section.title}-${index}`}
                        className="dashboard-report-section"
                        {...(/follow-up research questions|preguntas de seguimiento|check yield|check results/i.test(section.title) ? { open: true } : {})}
                      >
                        <summary className="dashboard-report-section-summary">
                          {section.title}
                        </summary>
                        {isRichResearchReportSection(section.title) ? (
                          <div
                            className="dashboard-report-section-body dashboard-bio-readable"
                            dangerouslySetInnerHTML={{ __html: renderReportSectionHtml(section.title, section.body, {
                                mediaBaseUrl: gatewayBaseUrl,
                                accessToken: accessToken || undefined,
                                identityNames: [
                                  selectedResearchReport.targetName,
                                  selectedResearchReport.factDatabase?.entity?.canonical_name,
                                ].filter((name): name is string => Boolean(name)),
                              }) }}
                          />
                        ) : (
                          <div
                            className="dashboard-report-section-body dashboard-report-section-linkified"
                            dangerouslySetInnerHTML={{ __html: linkifyPlainTextToHtml(section.body) }}
                          />
                        )}
                      </details>
                    ))}
                  </div>
                );
              })()}
              {selectedResearchReport.factDatabase ? (
                <details className="dashboard-report-section" style={{ marginTop: '10px', marginBottom: '20px' }}>
                  <summary className="dashboard-report-section-summary" style={{ marginBottom: '12px' }}>
                    {language === 'es' ? 'Base de hechos' : 'Fact Database'}
                  </summary>
                  <p style={{ margin: '0 0 10px', color: 'var(--color-text-muted)', fontSize: '0.84rem' }}>
                    {language === 'es'
                      ? 'Esta base de hechos es la fuente de verdad para relanzamientos. Edita directamente aquí y guarda para aplicar cambios.'
                      : 'This fact database is the source of truth for reruns. Edit directly here, then save to apply changes.'}
                  </p>
                  <div style={{ fontSize: '0.9rem', lineHeight: 1.6, color: 'var(--color-text-muted)' }}>
                    {isResearchPreviewFactEditing ? (
                      <div className="dashboard-factdb-editor">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                          <strong style={{ fontSize: '0.84rem', color: 'var(--color-text-strong)' }}>{language === 'es' ? 'Hechos deduplicados en base de hechos' : 'Deduplicated fact-database facts'}</strong>
                          <button type="button" className="dashboard-security-inline-btn" onClick={handleResearchPreviewSourceCheckFactAdd}>
                            {language === 'es' ? 'Agregar' : 'Add'}
                          </button>
                        </div>
                        {researchPreviewSourceCheckFactDraft.length > 0 ? researchPreviewSourceCheckFactDraft.map((item, index) => (
                          <div key={`fact-list-source-check-facts-${index}`} className="dashboard-factdb-row">
                            <input
                              type="text"
                              placeholder={language === 'es' ? 'Hecho de base de hechos' : 'Fact-database fact'}
                              value={item}
                              onChange={(event) => handleResearchPreviewSourceCheckFactChange(index, event.target.value)}
                            />
                            <button type="button" className="dashboard-security-inline-btn" onClick={() => handleResearchPreviewSourceCheckFactDelete(index)}>
                              {language === 'es' ? 'Eliminar' : 'Delete'}
                            </button>
                          </div>
                        )) : (
                          <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
                            {language === 'es'
                              ? 'No hay hechos deduplicados editables aún. Usa Agregar o guarda desde la vista estructurada.'
                              : 'No editable deduplicated source-check facts yet. Use Add, or save from the structured view.'}
                          </p>
                        )}
                      </div>
                    ) : (
                      <>
                        {selectedResearchReport.factDatabase.entity.canonical_name ? (
                          <div style={{ marginBottom: '8px' }}>
                            <strong>{language === 'es' ? 'Nombre canónico:' : 'Canonical name:'}</strong> {selectedResearchReport.factDatabase.entity.canonical_name}
                          </div>
                        ) : null}
                        {Array.isArray(selectedResearchReport.factDatabase.entity.alt_names) && selectedResearchReport.factDatabase.entity.alt_names.length > 0 ? (
                          <div style={{ marginBottom: '8px' }}>
                            <strong>{language === 'es' ? 'Alias alternos:' : 'Alternate names:'}</strong> {selectedResearchReport.factDatabase.entity.alt_names.join(', ')}
                          </div>
                        ) : null}
                        {harvestDisplayAddresses(selectedResearchReport.factDatabase).length > 0 ? (
                          <div style={{ marginBottom: '8px' }}>
                            <strong>{language === 'es' ? 'Direcciones:' : 'Addresses:'}</strong> {harvestDisplayAddresses(selectedResearchReport.factDatabase).join(', ')}
                          </div>
                        ) : null}
                        {Array.isArray(selectedResearchReport.factDatabase.entity.phones) && selectedResearchReport.factDatabase.entity.phones.length > 0 ? (
                          <div style={{ marginBottom: '8px' }}>
                            <strong>{language === 'es' ? 'Teléfonos:' : 'Phones:'}</strong> {selectedResearchReport.factDatabase.entity.phones.join(', ')}
                          </div>
                        ) : null}
                        {Array.isArray(selectedResearchReport.factDatabase.entity.emails) && selectedResearchReport.factDatabase.entity.emails.length > 0 ? (
                          <div style={{ marginBottom: '8px' }}>
                            <strong>{language === 'es' ? 'Correos:' : 'Emails:'}</strong> {selectedResearchReport.factDatabase.entity.emails.join(', ')}
                          </div>
                        ) : null}
                        {selectedResearchReport.factDatabase.entity.birthdate ? (
                          <div style={{ marginBottom: '8px' }}>
                            <strong>{language === 'es' ? 'Fecha de nacimiento:' : 'Birthdate:'}</strong> {selectedResearchReport.factDatabase.entity.birthdate}
                          </div>
                        ) : null}
                        {selectedResearchReport.factDatabase.entity.age_range ? (
                          <div style={{ marginBottom: '8px' }}>
                            <strong>{language === 'es' ? 'Rango de edad aproximado:' : 'Approximate age range:'}</strong> {selectedResearchReport.factDatabase.entity.age_range}
                          </div>
                        ) : null}
                        {selectedResearchReport.factDatabase.entity.ssn ? (
                          <div style={{ marginBottom: '8px' }}>
                            <strong>{language === 'es' ? 'Número de Seguro Social:' : 'Social Security Number:'}</strong> {selectedResearchReport.factDatabase.entity.ssn}
                          </div>
                        ) : null}
                        {selectedResearchReport.factDatabase.entity.dl_number ? (
                          <div style={{ marginBottom: '8px' }}>
                            <strong>{language === 'es' ? 'DL#:' : 'DL#:'}</strong> {selectedResearchReport.factDatabase.entity.dl_number}
                          </div>
                        ) : null}
                        {Array.isArray(selectedResearchReport.factDatabase.entity.company_affiliations) && selectedResearchReport.factDatabase.entity.company_affiliations.length > 0 ? (
                          <div style={{ marginBottom: '8px' }}>
                            <strong>{language === 'es' ? 'Afiliaciones:' : 'Affiliations:'}</strong> {selectedResearchReport.factDatabase.entity.company_affiliations.join(', ')}
                          </div>
                        ) : null}
                        {selectedResearchReport.factDatabase.entity.social_handles && Object.keys(selectedResearchReport.factDatabase.entity.social_handles).length > 0 ? (
                          <div style={{ marginBottom: '8px' }}>
                            <strong>{language === 'es' ? 'Identificadores sociales:' : 'Social handles:'}</strong> {Object.values(selectedResearchReport.factDatabase.entity.social_handles).join(', ')}
                          </div>
                        ) : null}
                        <div style={{ marginBottom: '8px' }}>
                          <strong>{language === 'es' ? 'Hechos de entidad:' : 'Entity facts:'}</strong>
                          <div style={{ marginTop: '6px', display: 'grid', gap: '4px' }}>
                            {[
                              ['Name', selectedResearchReport.factDatabase.entity?.canonical_name],
                              ['Aliases', (selectedResearchReport.factDatabase.entity?.alt_names || []).join(', ')],
                              ['Addresses', harvestDisplayAddresses(selectedResearchReport.factDatabase).join(', ')],
                              ['Phones', (selectedResearchReport.factDatabase.entity?.phones || []).join(', ')],
                              ['Emails', (selectedResearchReport.factDatabase.entity?.emails || []).join(', ')],
                              ['Birthdate', selectedResearchReport.factDatabase.entity?.birthdate],
                              ['Approximate age range', selectedResearchReport.factDatabase.entity?.age_range],
                              ['Affiliations', (selectedResearchReport.factDatabase.entity?.company_affiliations || []).join(', ')],
                              ['Social handles', Object.entries(selectedResearchReport.factDatabase.entity?.social_handles || {}).map(([k, v]) => `${k}: ${v}`).join(', ')],
                            ].filter(([, value]) => String(value || '').trim()).map(([label, value]) => (
                              <div key={`${selectedResearchReport.id}-entity-${label}`}>- <strong>{label}:</strong> {String(value)}</div>
                            ))}
                            {!(selectedResearchReport.factDatabase.entity?.canonical_name
                              || (selectedResearchReport.factDatabase.entity?.addresses || []).length
                              || (selectedResearchReport.factDatabase.entity?.alt_names || []).length) ? (
                              <div style={{ color: 'var(--color-text-muted)' }}>{language === 'es' ? 'Sin hechos de entidad aún.' : 'No entity facts yet.'}</div>
                            ) : null}
                          </div>
                        </div>
                        {Array.isArray(selectedResearchReport.factDatabase.records?.court_cases)
                          && selectedResearchReport.factDatabase.records.court_cases.length > 0 ? (
                          <div style={{ marginBottom: '8px' }}>
                            <strong>{language === 'es' ? 'Casos judiciales:' : 'Court cases:'}</strong>
                            <div style={{ marginTop: '6px', display: 'grid', gap: '6px' }}>
                              {selectedResearchReport.factDatabase.records.court_cases.map((row, courtIndex) => (
                                <div key={`${selectedResearchReport.id}-court-${row.case_number || courtIndex}`}>
                                  - <strong>{row.case_number || (language === 'es' ? 'Sin causa' : 'No cause number')}</strong>
                                  {row.case_name ? ` — ${row.case_name}` : ''}
                                  <div style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem', marginLeft: '12px' }}>
                                    {[
                                      row.filed_at,
                                      row.court,
                                      row.case_type,
                                      row.subject_role,
                                      row.litigation_direction,
                                      row.case_status,
                                    ].filter((part) => String(part || '').trim()).join(' · ')}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        {Array.isArray(selectedResearchReport.criticalFacts?.items) && selectedResearchReport.criticalFacts.items.length > 0 ? (
                          <div style={{ marginBottom: '8px' }}>
                            <strong>{language === 'es' ? 'Jerarquía de hechos críticos:' : 'Critical facts hierarchy:'}</strong>
                            <div style={{ marginTop: '6px', display: 'grid', gap: '4px' }}>
                              {selectedResearchReport.criticalFacts.items.map((item) => (
                                <div key={`${selectedResearchReport.id}-cf-${item.id}`}>
                                  - <strong>T{item.tier || '?'} · {item.label}:</strong>{' '}
                                  {item.value || (language === 'es' ? '(sin valor)' : '(no value)')}
                                  {' · '}
                                  {item.status || 'missing'}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        <div style={{ marginBottom: '8px' }}>
                          <strong>{language === 'es' ? 'Señales:' : 'Signals:'}</strong>{' '}
                          {selectedResearchReport.factDatabase.signals.length}
                          <label style={{ marginLeft: '12px', fontWeight: 400, fontSize: '0.92rem' }}>
                            <input
                              type="checkbox"
                              checked={showDismissedResearchFacts}
                              onChange={(event) => setShowDismissedResearchFacts(event.target.checked)}
                            />
                            {' '}
                            {language === 'es' ? 'Mostrar descartados' : 'Show dismissed'}
                          </label>
                        </div>
                        {canAccessOfac ? (
                          <div
                            style={{
                              marginBottom: '12px',
                              padding: '10px 12px',
                              border: '1px solid var(--color-border)',
                              borderRadius: '8px',
                              background: 'var(--color-surface-muted, transparent)',
                            }}
                          >
                            <strong style={{ display: 'block', marginBottom: '6px', color: 'var(--color-text-strong)' }}>
                              {language === 'es'
                                ? 'Transferir a base de hechos NextCompli'
                                : 'Transfer to NextCompli fact database'}
                            </strong>
                            <p style={{ margin: '0 0 8px', fontSize: '0.82rem' }}>
                              {language === 'es'
                                ? 'Marca qué señales y campos de entidad fusionar. Los no marcados no se transfieren.'
                                : 'Check which signals and entity fields to merge. Unchecked items are not transferred.'}
                            </p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                              <button
                                type="button"
                                className="dashboard-secondary-btn"
                                onClick={() => {
                                  const next = new Set<number>();
                                  (selectedResearchReport.factDatabase?.signals || []).forEach((signal, index) => {
                                    if (signal && !isFactSignalExcluded(signal)) {
                                      next.add(index);
                                    }
                                  });
                                  setResearchTransferSignalIndexes(next);
                                }}
                              >
                                {language === 'es' ? 'Seleccionar señales' : 'Select signals'}
                              </button>
                              <button
                                type="button"
                                className="dashboard-secondary-btn"
                                onClick={() => setResearchTransferSignalIndexes(new Set())}
                              >
                                {language === 'es' ? 'Ninguna señal' : 'Clear signals'}
                              </button>
                              <span style={{ fontSize: '0.82rem' }}>
                                {researchTransferSignalIndexes.size}{' '}
                                {language === 'es' ? 'señal(es) seleccionada(s)' : 'signal(s) selected'}
                              </span>
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontSize: '0.88rem' }}>
                              <input
                                type="checkbox"
                                checked={researchTransferIncludeEntity}
                                onChange={(event) => setResearchTransferIncludeEntity(event.target.checked)}
                              />
                              {language === 'es' ? 'Incluir campos de entidad' : 'Include entity fields'}
                            </label>
                            {researchTransferIncludeEntity ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 14px', marginBottom: '10px', fontSize: '0.84rem' }}>
                                {([
                                  ['canonical_name', language === 'es' ? 'Nombre' : 'Name'],
                                  ['alt_names', language === 'es' ? 'Alias' : 'Aliases'],
                                  ['addresses', language === 'es' ? 'Direcciones' : 'Addresses'],
                                  ['phones', language === 'es' ? 'Teléfonos' : 'Phones'],
                                  ['emails', language === 'es' ? 'Correos' : 'Emails'],
                                  ['birthdate', language === 'es' ? 'Fecha nac.' : 'Birthdate'],
                                  ['age_range', language === 'es' ? 'Rango de edad' : 'Age range'],
                                  ['company_affiliations', language === 'es' ? 'Afiliaciones' : 'Affiliations'],
                                  ['social_handles', language === 'es' ? 'Social' : 'Social'],
                                ] as const).map(([field, label]) => (
                                  <label key={`transfer-entity-${field}`} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input
                                      type="checkbox"
                                      checked={researchTransferEntityFields.has(field)}
                                      onChange={(event) => {
                                        setResearchTransferEntityFields((current) => {
                                          const next = new Set(current);
                                          if (event.target.checked) {
                                            next.add(field);
                                          } else {
                                            next.delete(field);
                                          }
                                          return next;
                                        });
                                      }}
                                    />
                                    {label}
                                  </label>
                                ))}
                              </div>
                            ) : null}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                              <label style={{ fontSize: '0.84rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {language === 'es' ? 'Sujeto OFAC:' : 'OFAC subject:'}
                                <select
                                  value={researchTransferSubjectId === '' ? '' : String(researchTransferSubjectId)}
                                  onChange={(event) => {
                                    const raw = event.target.value;
                                    setResearchTransferSubjectId(raw === '' ? '' : Number(raw));
                                  }}
                                  style={{ minWidth: '180px' }}
                                >
                                  <option value="">
                                    {language === 'es'
                                      ? 'Crear sujeto desde este reporte'
                                      : 'Create subject from this report'}
                                  </option>
                                  {linkedOfacSubjectsForSelectedReport.map((subject) => (
                                    <option key={`linked-transfer-${subject.id}`} value={subject.id}>
                                      {subject.entity_name} (#{subject.id})
                                    </option>
                                  ))}
                                  {ofacScreeningSubjects
                                    .filter((subject) => !linkedOfacSubjectsForSelectedReport.some((linked) => linked.id === subject.id))
                                    .slice(0, 40)
                                    .map((subject) => (
                                      <option key={`all-transfer-${subject.id}`} value={subject.id}>
                                        {subject.entity_name} (#{subject.id})
                                      </option>
                                    ))}
                                </select>
                              </label>
                              <button
                                type="button"
                                className="dashboard-btn"
                                disabled={researchTransferBusy || isResearchLoading}
                                onClick={() => { void handleResearchFactsTransfer(); }}
                              >
                                {researchTransferBusy
                                  ? (language === 'es' ? 'Transfiriendo…' : 'Transferring…')
                                  : (language === 'es'
                                    ? 'Transferir selección a NextCompli'
                                    : 'Transfer selected to NextCompli')}
                              </button>
                            </div>
                          </div>
                        ) : null}
                        {Array.isArray(selectedResearchReport.factDatabase.signals) && selectedResearchReport.factDatabase.signals.length > 0 ? (
                          <div style={{ marginBottom: '8px' }}>
                            <strong>{language === 'es' ? 'Señales (hecho + confianza):' : 'Signals (fact + confidence):'}</strong>
                            <div style={{ marginTop: '6px', display: 'grid', gap: '8px' }}>
                              {selectedResearchReport.factDatabase.signals.map((signal, signalIndex) => {
                                if (!signal || (isFactSignalExcluded(signal) && !showDismissedResearchFacts)) {
                                  return null;
                                }
                                const valueText = formatFactSignalValue(signal.value);
                                const excluded = isFactSignalExcluded(signal);
                                const transferChecked = researchTransferSignalIndexes.has(signalIndex);
                                return (
                                  <div
                                    key={`${selectedResearchReport.id}-signal-${signalIndex}`}
                                    style={{
                                      border: '1px solid var(--color-border)',
                                      borderRadius: '8px',
                                      padding: '8px 10px',
                                      opacity: excluded ? 0.55 : 1,
                                      textDecoration: excluded ? 'line-through' : 'none',
                                    }}
                                  >
                                    {canAccessOfac ? (
                                      <label
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '6px',
                                          marginBottom: '6px',
                                          fontSize: '0.84rem',
                                          textDecoration: 'none',
                                          opacity: 1,
                                          fontWeight: 500,
                                          color: 'var(--color-text-strong)',
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={transferChecked}
                                          onChange={(event) => {
                                            setResearchTransferSignalIndexes((current) => {
                                              const next = new Set(current);
                                              if (event.target.checked) {
                                                next.add(signalIndex);
                                              } else {
                                                next.delete(signalIndex);
                                              }
                                              return next;
                                            });
                                          }}
                                        />
                                        {language === 'es' ? 'Fusionar en NextCompli' : 'Merge into NextCompli'}
                                      </label>
                                    ) : null}
                                    <div style={{ marginBottom: '6px', fontSize: '0.98rem' }}>
                                      <strong>{valueText || (language === 'es' ? 'Sin valor' : 'No value')}</strong>
                                    </div>
                                    <div style={{ marginBottom: '6px', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
                                      {signal.fact_type || 'fact'}
                                      {' · '}
                                      {signal.source || 'unknown'}
                                      {' · '}
                                      {language === 'es' ? 'confianza' : 'confidence'}: {formatFactMatchProbability(signal)}
                                      {signal.prominence_noise ? ` · ${language === 'es' ? 'ruido probable' : 'likely noise'}` : ''}
                                    </div>
                                    {formatFactProvenance(signal, language) ? (
                                      <div style={{ marginBottom: '6px', color: 'var(--color-text-muted)', fontSize: '0.82rem' }}>
                                        {formatFactProvenance(signal, language)}
                                      </div>
                                    ) : null}
                                    {!excluded ? (
                                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        <button
                                          type="button"
                                          className="dashboard-secondary-btn"
                                          onClick={() => void handleFactSignalDecision(selectedResearchReport.id, signalIndex, 'excluded')}
                                        >
                                          {language === 'es' ? 'Descartar' : 'Dismiss'}
                                        </button>
                                        <button
                                          type="button"
                                          className="dashboard-secondary-btn"
                                          onClick={() => void handleFactSignalDecision(selectedResearchReport.id, signalIndex, 'included')}
                                        >
                                          {language === 'es' ? 'Confirmar' : 'Confirm'}
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        className="dashboard-secondary-btn"
                                        onClick={() => void handleFactSignalDecision(selectedResearchReport.id, signalIndex, 'review')}
                                      >
                                        {language === 'es' ? 'Restaurar' : 'Restore'}
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : null}
                        {Array.isArray(selectedResearchReport.factDatabase.factRecords) && selectedResearchReport.factDatabase.factRecords.length > 0 ? (
                          <div style={{ marginBottom: '8px' }}>
                            <strong>{language === 'es' ? 'Hechos con procedencia (verificación + ejecución):' : 'Facts with provenance (check + run):'}</strong>
                            <div style={{ marginTop: '6px', display: 'grid', gap: '6px' }}>
                              {selectedResearchReport.factDatabase.factRecords.map((record, recordIndex) => {
                                const provenance = formatFactProvenance(record, language);
                                const alsoCount = Array.isArray(record.also_found_in) ? record.also_found_in.length : 0;
                                return (
                                  <div key={`${selectedResearchReport.id}-fact-record-${recordIndex}`}>
                                    - <strong>{record.field || record.fact_type || 'fact'}:</strong> {record.value}
                                    {provenance ? (
                                      <div style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', marginLeft: '12px' }}>
                                        {provenance}
                                        {alsoCount > 0
                                          ? ` · ${language === 'es' ? `también en ${alsoCount} verificación(es)` : `also in ${alsoCount} other check(s)`}`
                                          : ''}
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : null}
                        {selectedResearchReport.factDatabase.metadata?.last_run_id || selectedResearchReport.factDatabase.metadata?.last_screening_id ? (
                          <div style={{ marginBottom: '8px', color: 'var(--color-text-muted)', fontSize: '0.84rem' }}>
                            <strong>{language === 'es' ? 'Última ejecución:' : 'Latest run:'}</strong>{' '}
                            {[
                              selectedResearchReport.factDatabase.metadata.last_run_id
                                ? `${language === 'es' ? 'ejecución' : 'run'} ${selectedResearchReport.factDatabase.metadata.last_run_id}`
                                : null,
                              selectedResearchReport.factDatabase.metadata.last_screening_id
                                ? `screening ${selectedResearchReport.factDatabase.metadata.last_screening_id}`
                                : null,
                            ].filter(Boolean).join(' · ')}
                          </div>
                        ) : null}
                        {Array.isArray(selectedResearchReport.factDatabase.sourceCheckFacts) && selectedResearchReport.factDatabase.sourceCheckFacts.length > 0 ? (
                          <div style={{ marginBottom: '8px' }}>
                            <strong>{language === 'es' ? 'Hechos deduplicados en base de hechos:' : 'Deduplicated fact-database facts:'}</strong>
                            <div style={{ marginTop: '6px', display: 'grid', gap: '4px' }}>
                              {normalizeFactListItems(selectedResearchReport.factDatabase.sourceCheckFacts).map((fact, factIndex) => (
                                <div key={`${selectedResearchReport.id}-source-check-fact-${factIndex}`}>- {fact}</div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        {selectedResearchReportIntakeFacts.length > 0 ? (
                          <div style={{ marginBottom: '8px' }}>
                            <strong>{language === 'es' ? 'Hechos de Intake y Parsing:' : 'Fact Intake and Parsing facts:'}</strong>
                            <div style={{ marginTop: '6px', display: 'grid', gap: '4px' }}>
                              {selectedResearchReportIntakeFacts.map((fact, factIndex) => (
                                <div key={`${selectedResearchReport.id}-intake-fact-${factIndex}`}>- {fact}</div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        <div>
                          <strong>{language === 'es' ? 'Fuente:' : 'Source:'}</strong> {selectedResearchReport.factDatabase.metadata.source_checks?.join(', ') || 'research-form'}
                        </div>
                      </>
                    )}
                  </div>
                </details>
              ) : null}
              {hasPlatformAdminAccess ? (
              <ResearchLogPanel
                language={language}
                defaultOpen={false}
                entries={buildResearchLogEntries({
                  researchLog: selectedResearchReport.researchLog,
                  checkTimings: resolveReportCheckTimings(selectedResearchReport),
                  sourceChecksRuns: selectedResearchReport.sourceChecksRuns,
                  checkResults: selectedResearchReport.checkResults,
                  criticalFacts: selectedResearchReport.criticalFacts,
                  factDatabase: selectedResearchReport.factDatabase as Record<string, unknown> | undefined,
                  status: selectedResearchReport.isFallback ? 'fallback' : 'completed',
                })}
              />
              ) : null}
              {(resolveReportCheckTimings(selectedResearchReport).length > 0 || selectedResearchReport.timeEfficiencyAnalysis) ? (
                <details className="dashboard-report-section" style={{ marginTop: '10px', marginBottom: '20px' }}>
                  <summary className="dashboard-report-section-summary" style={{ marginBottom: '12px' }}>
                    {language === 'es' ? 'Análisis de tiempo' : 'Time analysis'}
                  </summary>
                  <p style={{ margin: '0 0 10px', color: 'var(--color-text-muted)', fontSize: '0.84rem' }}>
                    {language === 'es'
                      ? 'Duración por verificación y recomendaciones de eficiencia. Separado de la base de hechos.'
                      : 'Per-check duration and efficiency recommendations. Kept separate from the fact database.'}
                  </p>
                  {resolveReportCheckTimings(selectedResearchReport).length > 0 ? (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>{language === 'es' ? 'Tiempo por verificación:' : 'Time per check:'}</strong>
                      <div style={{ marginTop: '6px', display: 'grid', gap: '4px' }}>
                        {[...resolveReportCheckTimings(selectedResearchReport)]
                          .sort((left, right) => {
                            const leftStart = Date.parse(String(left.started_at || left.start_at || '')) || 0;
                            const rightStart = Date.parse(String(right.started_at || right.start_at || '')) || 0;
                            if (leftStart && rightStart) return leftStart - rightStart;
                            return (Number(right.duration_ms) || 0) - (Number(left.duration_ms) || 0);
                          })
                          .map((timing, index) => {
                            const startedAt = timing.started_at || timing.start_at;
                            const endedAt = timing.ended_at || timing.end_at;
                            const startedLabel = startedAt ? new Date(String(startedAt)).toLocaleString() : null;
                            const endedLabel = endedAt ? new Date(String(endedAt)).toLocaleString() : null;
                            return (
                              <div
                                key={`${selectedResearchReport.id}-timing-${timing.check || index}`}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  gap: '12px',
                                  borderBottom: '1px solid var(--color-border)',
                                  padding: '4px 0',
                                  fontSize: '0.92rem',
                                }}
                              >
                                <span>
                                  {timing.check || timing.phase || 'check'}
                                  {startedLabel || endedLabel ? (
                                    <span style={{ display: 'block', color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>
                                      {[
                                        startedLabel
                                          ? `${language === 'es' ? 'Inicio' : 'Started'}: ${startedLabel}`
                                          : null,
                                        endedLabel
                                          ? `${language === 'es' ? 'Fin' : 'Ended'}: ${endedLabel}`
                                          : null,
                                      ].filter(Boolean).join(' · ')}
                                    </span>
                                  ) : null}
                                </span>
                                <span>{formatCheckTimingDuration(timing.duration_ms, language)}</span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  ) : null}
                  {selectedResearchReport.timeEfficiencyAnalysis ? (
                    <div style={{ marginBottom: '4px', padding: '10px 12px', background: 'var(--color-surface-muted)', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
                      <strong>{language === 'es' ? 'Análisis de eficiencia:' : 'Efficiency analysis:'}</strong>
                      <p style={{ margin: '6px 0', fontSize: '0.92rem' }}>
                        {selectedResearchReport.timeEfficiencyAnalysis.summary
                          || (selectedResearchReport.timeEfficiencyAnalysis.efficiency_score != null
                            ? `Score ${selectedResearchReport.timeEfficiencyAnalysis.efficiency_score}/100`
                            : '')}
                      </p>
                      {(selectedResearchReport.timeEfficiencyAnalysis.recommendations || []).slice(0, 4).map((rec) => (
                        <div key={rec.id || rec.title} style={{ marginTop: '8px', fontSize: '0.9rem' }}>
                          <div><strong>{rec.title}</strong> {rec.severity ? `(${rec.severity})` : ''}</div>
                          <div style={{ color: 'var(--color-text-muted)' }}>{rec.detail}</div>
                          {rec.cloud_hint ? (
                            <div style={{ color: 'var(--color-text-muted)', marginTop: '2px' }}>
                              {language === 'es' ? 'Nube:' : 'Cloud:'} {rec.cloud_hint}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </details>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      {(isResearchPdfGenerating) ? (
        <div className="dashboard-report-loader-overlay" role="status" aria-live="polite" aria-label={language === 'es' ? 'Generando PDF' : 'Generating PDF'}>
          <div className="dashboard-report-loader-card">
            <span className="dashboard-report-loader-spinner" aria-hidden="true" />
            <h3>{language === 'es' ? 'Generando PDF' : 'Generating PDF'}</h3>
            <p>
              {language === 'es'
                ? 'Preparando el archivo PDF...'
                : 'Preparing PDF file...'}
            </p>
          </div>
        </div>
      ) : null}
      <section
        className={`dashboard-account-layout${isSideNavCollapsed && !isCompactNav ? ' is-nav-collapsed' : ''}${isCompactNav ? ' is-compact-nav' : ''}${isMobileNavSheetOpen ? ' is-nav-sheet-open' : ''}`}
        aria-label={copy.sectionLabel}
      >
        {isCompactNav ? (
          <div className="dashboard-mobile-nav-bar">
            <button
              type="button"
              className="dashboard-mobile-nav-jump"
              aria-expanded={isMobileNavSheetOpen}
              aria-controls="dashboard-mobile-nav-sheet"
              aria-label={copy.navOpenMenu}
              onClick={() => openMobileNavSheet(openNavGroupId)}
            >
              <span className="dashboard-mobile-nav-jump-kicker">{copy.navJumpTo}</span>
              <span className="dashboard-mobile-nav-jump-current">{activeNavLabel}</span>
            </button>
          </div>
        ) : (
          <aside className="dashboard-account-nav" aria-label="Account navigation">
            <button
              type="button"
              className={`dashboard-sidenav-toggle${isSideNavCollapsed ? ' is-collapsed' : ''}`}
              aria-expanded={!isSideNavCollapsed}
              aria-controls="dashboard-side-nav"
              aria-label={
                isSideNavCollapsed
                  ? (language === 'es' ? 'Mostrar menú' : 'Show menu')
                  : (language === 'es' ? 'Ocultar menú' : 'Hide menu')
              }
              onClick={() => setIsSideNavCollapsed((open) => !open)}
            >
              <CollapseLeftIcon />
            </button>
            <div className="dashboard-nav-search-wrap">
              <label className="visually-hidden" htmlFor="dashboard-side-nav-search">{copy.navFindPlaceholder}</label>
              <input
                id="dashboard-side-nav-search"
                type="search"
                className="dashboard-nav-search"
                value={navQuery}
                onChange={(event) => setNavQuery(event.target.value)}
                placeholder={copy.navFindPlaceholder}
                autoComplete="off"
              />
            </div>
            {!isSideNavCollapsed ? renderConsoleNavList('dashboard-side-nav') : null}
          </aside>
        )}

        {isCompactNav && isMobileNavSheetOpen ? (
          <div className="dashboard-nav-sheet-backdrop" role="presentation" onClick={closeMobileNavSheet}>
            <div
              className="dashboard-nav-sheet"
              id="dashboard-mobile-nav-sheet"
              role="dialog"
              aria-modal="true"
              aria-label={copy.navOpenMenu}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="dashboard-nav-sheet-header">
                <label className="visually-hidden" htmlFor="dashboard-mobile-nav-search">{copy.navFindPlaceholder}</label>
                <input
                  ref={navSearchInputRef}
                  id="dashboard-mobile-nav-search"
                  type="search"
                  className="dashboard-nav-search"
                  value={navQuery}
                  onChange={(event) => setNavQuery(event.target.value)}
                  placeholder={copy.navFindPlaceholder}
                  autoComplete="off"
                />
                <button type="button" className="dashboard-nav-sheet-close" onClick={closeMobileNavSheet} aria-label={copy.navCloseMenu}>
                  {copy.navCloseMenuDone}
                </button>
              </div>
              {renderConsoleNavList('dashboard-mobile-side-nav')}
            </div>
          </div>
        ) : null}

        <main className="dashboard-account-main">
          {isSideNavCollapsed && !isCompactNav ? (
            <button
              type="button"
              className="dashboard-sidenav-expand-edge"
              aria-label={language === 'es' ? 'Mostrar menú' : 'Show menu'}
              onClick={() => setIsSideNavCollapsed(false)}
            >
              <CollapseLeftIcon />
            </button>
          ) : null}
          {activeNavItem !== 'admin' ? (
            <nav className="dashboard-breadcrumbs" aria-label="Breadcrumb">
              {breadcrumbTrail.map((crumb, index) => (
                <React.Fragment key={`${crumb}-${index}`}>
                  {index > 0 ? <span className="dashboard-breadcrumb-separator">/</span> : null}
                  <span className={index === breadcrumbTrail.length - 1 ? 'dashboard-breadcrumb-current' : ''}>{crumb}</span>
                </React.Fragment>
              ))}
            </nav>
          ) : null}
          {isOfacNavItem(activeNavItem) ? (
            <div className="ofac-workspace">
              <OfacWorkspaceBar
                language={language === 'es' ? 'es' : 'en'}
                active={activeNavItem}
                items={ofacSubmenuItems}
                onOpen={(id) => handleNavSelection(id as DashboardNavItemId)}
              />
              {renderRightPane()}
            </div>
          ) : renderRightPane()}
        </main>
        {isCompactNav ? (
          <nav className="dashboard-mobile-tabbar" aria-label={copy.sectionLabel}>
            <button
              type="button"
              className={`dashboard-mobile-tab${activeNavItem === 'home' || activeNavItem === 'dashboards' ? ' is-active' : ''}`}
              onClick={() => handleNavSelection(hasSuperUserAccess ? 'dashboards' : 'home')}
            >
              {copy.navTabHome}
            </button>
            <button
              type="button"
              className={`dashboard-mobile-tab${consoleNavGroupForItem(activeNavItem) === 'work' ? ' is-active' : ''}`}
              onClick={() => openMobileNavSheet('work')}
            >
              {copy.navTabWork}
            </button>
            <button
              type="button"
              className={`dashboard-mobile-tab${consoleNavGroupForItem(activeNavItem) === 'account' ? ' is-active' : ''}`}
              onClick={() => openMobileNavSheet('account')}
            >
              {copy.navTabAccount}
            </button>
            <button
              type="button"
              className={`dashboard-mobile-tab${isMobileNavSheetOpen ? ' is-active' : ''}`}
              onClick={() => openMobileNavSheet(null, true)}
            >
              {copy.navMore}
            </button>
          </nav>
        ) : null}
        {accessToken ? (
          <ConsoleAssistant
            apiBase={gatewayBaseUrl}
            accessToken={accessToken}
            language={language === 'es' ? 'es' : 'en'}
            activePage={activeNavLabel || activeNavItem}
            helpSignals={consoleHelpSignals}
          />
        ) : null}
      </section>
    </div>
  );
};

export default Dashboard;
